/**
 * English.
 *
 * Keys are the Italian sentences: see `lingua.ts` for why. A missing entry
 * falls back to the Italian text, which is readable — not to a raw identifier,
 * which is not.
 *
 * Keep entries in the order they appear in the interface, grouped by area: a
 * dictionary sorted alphabetically is a dictionary nobody can review, because
 * the sentences that belong to the same screen end up pages apart.
 */
export const en: Record<string, string> = {
  // Inventario cloud/virtuale: titoli foglio e intestazioni colonna, via t().
  'Avviata': 'Started',
  'Bilanciatori': 'Load balancers',
  'Cifrata': 'Encrypted',
  'Condiviso': 'Shared',
  'DRS': 'DRS',
  'Firmware': 'Firmware',
  'Genere': 'Kind',
  'HA': 'HA',
  'HW': 'HW',
  'IP gestione': 'Management IP',
  'Libero (GB)': 'Free (GB)',
  'NIC': 'NIC',
  'Provisioned (GB)': 'Provisioned (GB)',
  'Pubblica': 'Public',
  'Pubblico': 'Public',
  'Security group': 'Security group',
  'Stato connessione': 'Connection state',
  'Uplinks': 'Uplinks',
  'Vendor': 'Vendor',
  'Versioning': 'Versioning',
  // Catalogo report (reports.py): titoli e descrizioni, via t().
  'Inventario per tipo e classe': 'Inventory by type and class',
  "Contenuto della CMDB, raggruppato per tipo e classe, con data dell'ultima rilevazione. Include gli host non gestiti, conteggiati separatamente.": 'CMDB contents, grouped by type and class, with the date of the last detection. Includes unmanaged hosts, counted separately.',
  'Configuration item non registrati come beni': 'Configuration items not registered as assets',
  "Asset individuati dalla discovery e non ancora censiti nel registro dei beni. Gli host non gestiti sono esclusi perché classificati come rilevamenti. Rappresenta il backlog di riconciliazione dell'inventario.": 'Assets found by discovery and not yet recorded in the asset register. Unmanaged hosts are excluded because they are classified as detections. It represents the inventory reconciliation backlog.',
  'Registro dei beni': 'Asset register',
  'Beni censiti con proprietà, centro di costo, valore e stato della copertura contrattuale alla data corrente.': 'Recorded assets with owner, cost center, value and contract coverage status as of the current date.',
  'Beni senza contratto': 'Assets without a contract',
  'Beni attivi o in manutenzione senza un contratto valido alla data corrente. Esclude i beni dismessi e alienati, non soggetti a copertura contrattuale.': 'Assets in use or under maintenance without a valid contract as of the current date. Excludes retired and disposed assets, not subject to contract coverage.',
  'Contratti in scadenza': 'Expiring contracts',
  'Contratti in scadenza entro la finestra selezionata, inclusi quelli già scaduti. In caso di rinnovo tacito, considera come data rilevante il termine ultimo per la disdetta, non la scadenza contrattuale.': 'Contracts expiring within the selected window, including those already expired. For tacit renewal, the relevant date is the cancellation deadline, not the contract expiry.',
  'Garanzie in scadenza': 'Expiring warranties',
  'Beni con garanzia scaduta o in scadenza entro la finestra selezionata, esclusi quelli dismessi e alienati.': 'Assets with warranty expired or expiring within the selected window, excluding retired and disposed ones.',
  'Valore per centro di costo': 'Value by cost center',
  'Distribuzione del valore di acquisto per centro di costo, esclusi i beni alienati. I beni privi di centro di costo sono aggregati separatamente per mantenere la quadratura del totale.': 'Distribution of purchase value by cost center, excluding disposed assets. Assets without a cost center are aggregated separately to keep the total balanced.',
  'Esito delle discovery': 'Discovery outcome',
  'Scansioni eseguite nella finestra selezionata, raggruppate per tipo, con esiti riusciti e falliti. Il tasso di successo è calcolato esclusivamente sui job conclusi.': 'Scans run in the selected window, grouped by type, with succeeded and failed outcomes. The success rate is computed only on completed jobs.',
  // Catalogo connettori (registry.catalog): etichette, help, reachability, placeholder, via t(variabile).
  'API Token': 'API token',
  'API token (Cloud)': 'API token (Cloud)',
  'Access token OAuth': 'OAuth access token',
  'Chiave tecnico': 'Technician key',
  'Client ID OAuth': 'OAuth client ID',
  'Client secret OAuth': 'OAuth client secret',
  "Email dell'utenza (Cloud)": 'User email (Cloud)',
  'Installazione': 'Installation',
  'Modo di autenticazione': 'Authentication mode',
  'Percorso del servizio': 'Service path',
  'Personal Access Token (Data Center)': 'Personal access token (Data Center)',
  'Schema di Assets gestito da CENSO': 'Assets schema managed by CENSO',
  'Stringa di autenticazione': 'Authentication string',
  'Tipo di oggetto da creare': 'Object type to create',
  'Workspace di Assets (Cloud)': 'Assets workspace (Cloud)',
  'SaaS: raggiungibile dal Control Plane senza aperture.': 'SaaS: reachable from the Control Plane with no firewall openings.',
  'Esiste in versione cloud e in versione installata: nel secondo caso il Control Plane deve poterlo raggiungere.': 'Available as cloud and on-premises: in the latter case the Control Plane must be able to reach it.',
  'Spesso installato in azienda: il Control Plane deve poterlo raggiungere in HTTPS, oppure va pubblicato su un indirizzo esposto.': 'Often installed on-premises: the Control Plane must reach it over HTTPS, or it must be published on an exposed address.',
  'Spesso installato in azienda: vale quanto detto per Matrix42.': 'Often installed on-premises: same as noted for Matrix42.',
  'Jira Cloud è SaaS: raggiungibile dal Control Plane (anche api.atlassian.com, dove sta Assets). Jira Data Center è installato in azienda: il Control Plane deve poterlo raggiungere, oppure si passa da una sonda.': 'Jira Cloud is SaaS: reachable from the Control Plane (including api.atlassian.com, where Assets lives). Jira Data Center is installed on-premises: the Control Plane must be able to reach it, or go through a probe.',
  'Da cambiare solo se in installazione è stato rinominato.': 'Change it only if it was renamed during installation.',
  'Facoltativo: se vuoto si ricava dal sito. Serve solo se il sito ha più workspace.': 'Optional: if empty it is derived from the site. Needed only if the site has multiple workspaces.',
  'I permessi della chiave sono quelli del tecnico che la possiede: una chiave di sola lettura fa fallire le scritture con un messaggio che parla di licenza, non di permessi.': 'The key’s permissions are those of the technician who owns it: a read-only key makes writes fail with a message about licensing, not permissions.',
  'Il JWT generato in Amministrazione → Integrazioni → Web Services. Non è il token con cui si chiamano i dati: serve a ottenerlo.': 'The JWT generated in Administration → Integrations → Web Services. It is not the token used to call the data: it is used to obtain that token.',
  'Il campo `authString` del login: serve solo con autenticazione esterna (LDAP/AD via AREA). Vuoto negli altri casi.': 'The login’s `authString` field: needed only with external authentication (LDAP/AD via AREA). Empty otherwise.',
  "Lo schema in cui CENSO scrive i CI: se non esiste lo crea (chiave uguale al nome, in maiuscolo), e dentro crea un tipo di oggetto per classe (Server, Switch, Virtual Machine…) con i suoi attributi. L'utenza deve poter amministrare gli schemi di Assets.": 'The schema where CENSO writes the CIs: if it does not exist it is created (key equal to the name, uppercase), and inside it an object type per class (Server, Switch, Virtual Machine…) with its attributes. The account must be able to administer the Assets schemas.',
  "Serve solo per **creare**: in Matrix42 un asset è un oggetto di un tipo, composto da più frammenti, e i frammenti come `SPSAssetClassBase` da soli non si creano. Qui va il tipo, che dipende dall'istanza: CENSO crea l'oggetto di quel tipo con dentro il frammento a cui la corrispondenza punta. Lasciandolo vuoto si aggiornano gli oggetti esistenti e la creazione dice che cosa manca, invece di crearne del tipo sbagliato.": 'Needed only to **create**: in Matrix42 an asset is an object of a type, made of several fragments, and fragments like `SPSAssetClassBase` cannot be created on their own. Here goes the type, which depends on the instance: CENSO creates the object of that type containing the fragment the mapping points to. Left empty, existing objects are updated and creation reports what is missing, instead of creating ones of the wrong type.',
  "Solo Cloud: l'email dell'utenza Atlassian che possiede il token. Deve avere accesso agli schemi di Assets da sincronizzare.": 'Cloud only: the email of the Atlassian account that owns the token. It must have access to the Assets schemas to synchronise.',
  'Solo Cloud: si crea su id.atlassian.com → Sicurezza → Crea e gestisci i token API.': 'Cloud only: created at id.atlassian.com → Security → Create and manage API tokens.',
  "Solo Data Center: Profilo → Personal Access Tokens. I permessi sono quelli dell'utenza che lo crea.": 'Data Center only: Profile → Personal Access Tokens. The permissions are those of the account that creates it.',
  'Solo per il modo `basic`.': 'Only for `basic` mode.',
  'Solo per la versione cloud (`Zoho-oauthtoken`).': 'Only for the cloud version (`Zoho-oauthtoken`).',
  "Solo se l'istanza impone OAuth. Lasciato vuoto, si usa l'autenticazione di base.": 'Only if the instance requires OAuth. Left empty, basic authentication is used.',
  'Utenza AR System. Le serve il permesso di lettura sui moduli da sincronizzare; per scrivere, anche quello di modifica — che in Remedy si concede per **campo**, non per modulo.': 'AR System account. It needs read permission on the forms to synchronise; to write, also modify permission — which in Remedy is granted per **field**, not per form.',
  'Utenza di integrazione. Le serve il ruolo di lettura sulle tabelle da sincronizzare, più `personalize_dictionary` o `admin` per leggere i metadati in `sys_dictionary`: senza, la scansione trova le tabelle e non i campi.': 'Integration account. It needs the read role on the tables to synchronise, plus `personalize_dictionary` or `admin` to read the metadata in `sys_dictionary`: without it, the scan finds the tables but not the fields.',
  "`api_token` (consigliato dal produttore) oppure `basic`. Con `api_token` si incolla il token generato dall'amministrazione: CENSO lo scambia da sé con l'access token a ogni sessione.": '`api_token` (recommended by the vendor) or `basic`. With `api_token` you paste the token generated in the admin console: CENSO exchanges it for the access token itself on each session.',
  '`cloud` per Jira Cloud (sito `…atlassian.net`), `datacenter` per Jira Data Center / Server installato in azienda.': '`cloud` for Jira Cloud (site `…atlassian.net`), `datacenter` for Jira Data Center / Server installed on-premises.',
  "`technician_key` per l'installazione on-premise (la chiave si genera dal profilo del tecnico), `oauth` per la versione cloud.": '`technician_key` for the on-premises installation (the key is generated from the technician’s profile), `oauth` for the cloud version.',
  'CENSO': 'CENSO',
  'M42Services': 'M42Services',
  'SPSAssetTypeNetworkDevice': 'SPSAssetTypeNetworkDevice',
  'api_token': 'api_token',
  'technician_key': 'technician_key',
  // Metodi/parametri dei piani di discovery (discovery/steps.py), via t(label).
  'Ping (ICMP)': 'Ping (ICMP)',
  'Risoluzione DNS': 'DNS resolution',
  'Scansione porte TCP': 'TCP port scan',
  'Interrogazione SNMP': 'SNMP query',
  'SNMP approfondito': 'Deep SNMP',
  'Adiacenze CDP/LLDP': 'CDP/LLDP adjacencies',
  'Interrogazione SSH': 'SSH query',
  'Interrogazione Windows (WinRM)': 'Windows query (WinRM)',
  'Identità SMB': 'SMB identity',
  'Scansione personalizzata': 'Custom scan',
  'Registro di sistema remoto': 'Remote system registry',
  'Interrogazione Windows (WMI/DCOM)': 'Windows query (WMI/DCOM)',
  'Attesa per porta (ms)': 'Per-port timeout (ms)',
  'Porte in parallelo': 'Ports in parallel',
  'Cifratura TLS': 'TLS encryption',
  'Identità del sistema': 'System identity',
  'Aggiornamenti installati': 'Installed updates',
  'Stampanti collegate': 'Connected printers',
  'Utenti connessi': 'Logged-in users',
  // Etichette della tassonomia CI (classi e categorie), mostrate via t(label).
  'Calcolo': 'Compute',
  'Archiviazione': 'Storage',
  'Basi dati': 'Databases',
  'Cloud — calcolo': 'Cloud — compute',
  'Cloud — archiviazione': 'Cloud — storage',
  'Cloud — rete': 'Cloud — network',
  'Cloud — dati': 'Cloud — data',
  'Cloud — integrazione': 'Cloud — integration',
  'Cloud — account': 'Cloud — account',
  'Periferiche': 'Peripherals',
  'Parti': 'Parts',
  'Switch': 'Switch',
  'Router': 'Router',
  'Controller wireless': 'Wireless controller',
  'Access point': 'Access point',
  'Trasporto ottico': 'Optical transport',
  'Sistema di storage': 'Storage system',
  'Subnet pubblica': 'Public subnet',
  'Host hypervisor': 'Hypervisor host',
  'Host non gestito': 'Unmanaged host',
  'Macchina virtuale': 'Virtual machine',
  'Port group': 'Port group',
  'Workload Kubernetes': 'Kubernetes workload',
  'Servizio Kubernetes': 'Kubernetes service',
  'Ingress Kubernetes': 'Kubernetes ingress',
  'Commutatore virtuale': 'Virtual switch',
  'Servizio container': 'Container service',
  'Gruppo di scalabilità': 'Scaling group',
  'Archivio oggetti': 'Object storage',
  'Volume a blocchi': 'Block volume',
  'Condivisione file': 'File share',
  'Rete virtuale': 'Virtual network',
  'Bilanciatore gestito': 'Managed load balancer',
  'Gateway': 'Gateway',
  'Zona DNS': 'DNS zone',
  'Istanza di base dati': 'Database instance',
  'Base dati relazionale': 'Relational database',
  'Base dati NoSQL': 'NoSQL database',
  'Cache': 'Cache',
  'Coda': 'Queue',
  'Topic di notifica': 'Notification topic',
  'API gateway': 'API gateway',
  'Account cloud': 'Cloud account',
  'Stampante': 'Printer',
  'Telecamera IP': 'IP camera',
  'Telefono VoIP': 'VoIP phone',
  'Apparecchio elettromedicale': 'Medical device',
  'Dominio Active Directory': 'Active Directory domain',
  'Monitor': 'Monitor',
  'Non classificato': 'Unclassified',
  'Temperature dai sensori dell’appliance': 'Temperatures from the appliance sensors',
  'Temperature': 'Temperatures',
  'Appliance CensoOS: la sonda di cattura dal filo': 'CensoOS appliance: the wire-capture probe',
  'Censo Appliance': 'Censo Appliance',
  'Il riepilogo compare quando c’è almeno un agente.': 'The summary appears when there is at least one agent.',
  'Percorsi software': 'Software paths',
  'La pagina apre con un riepilogo (agenti per stato e quante macchine hanno ciascuna feature) e con le personalizzazioni dei percorsi software in alto; l’elenco ha colonne filtrabili — OS e «Features attive» comprese — a pagine da 25 o 100. Selezionando più agenti (anche tutti i risultati filtrati) una barra applica in blocco le features e la manutenzione. Un agente nasce senza feature attive: SBOM, Runtime, eBPF e Ascolto DHCP si accendono quando servono.': 'The page opens with a summary (agents by state and how many machines have each feature) and the software-path customizations at the top; the list has filterable columns — including OS and «Features attive» — in pages of 25 or 100. Selecting several agents (even all filtered results) a bar applies features and maintenance in bulk. An agent is born with no active features: SBOM, Runtime, eBPF and DHCP listening are turned on when needed.',
  '{n} agenti selezionati': '{n} agents selected',
  'disattiva': 'disable',
  'agenti': 'agents',
  'Ultima consegna rifiutata': 'Last delivery rejected',
  'Seleziona tutti i risultati filtrati': 'Select all filtered results',
  'Seleziona': 'Select',
  'Revocati': 'Revoked',
  'Installati': 'Installed',
  'Features attive': 'Active features',
  'Ambito di raccolta': 'Collection scope',
  'Filtri per colonna': 'Per-column filters',
  // Rete · componenti e IP
  'Indirizzi IP': 'IP addresses',
  'IP pubblico': 'Public IP',
  'Indirizzo aggiuntivo': 'Additional address',
  'Nessun indirizzo': 'No address',
  'Gli indirizzi aggiuntivi e pubblici compaiono qui man mano che le scansioni li rilevano sugli apparati e sugli host.':
    'Additional and public addresses appear here as scans detect them on devices and hosts.',
  'Transceiver, schede, alimentatori e ventole compaiono qui quando una scansione SNMP legge le parti inserite negli apparati.':
    'Transceivers, cards, power supplies and fans appear here when an SNMP scan reads the parts installed in the devices.',
  // Lista CI · cruscotto
  'CI totali': 'Total CIs',
  'nessun nuovo negli ultimi 7 giorni': 'none new in the last 7 days',
  'Non classificati': 'Unclassified',
  'Filtra i non classificati': 'Filter the unclassified',
  'un connettore produce qualcosa di nuovo': 'a connector is producing something new',
  'CI con rischi aperti': 'CIs with open findings',
  'Vai alla sicurezza': 'Go to security',
  'nessun rischio aperto': 'no open findings',
  'Non visti da 7 giorni': 'Not seen in 7 days',
  'candidati a una verifica: spenti, o non più raggiunti':
    'candidates for a check: powered off, or no longer reached',
  'Per categoria': 'By category',
  'Come sono stati scoperti': 'How they were discovered',
  'Nessun dato': 'No data',
  // Job · ritenta
  'Ritenta': 'Retry',
  'Rilancio…': 'Relaunching…',
  'Job rilanciato: riparte con la stessa configurazione.':
    'Job relaunched: it restarts with the same configuration.',
  // Discovery · analizza e ottimizza
  'Ottimizza discovery': 'Optimize discovery',
  'Analizza e ottimizza discovery': 'Analyze and optimize discovery',
  'Analisi…': 'Analyzing…',
  'Ancora niente da ottimizzare': 'Nothing to optimize yet',
  'Servono asset già in CMDB con un indirizzo di gestione: fai una prima scansione, poi torna qui.':
    'It needs assets already in the CMDB with a management address: run a first scan, then come back.',
  'Analizza la provenienza dei dati in CMDB per creare job puntuali sugli indirizzi già rilevati come attivi, raggruppati per origine. Evidenzia inoltre gli asset acquisiti da più scansioni, così da individuare sovrapposizioni nel perimetro di discovery.':
    'From provenance already in the CMDB: instead of scanning a whole subnet, a targeted job on just the addresses that respond, grouped by how they were discovered. And the assets seen by more than one scan, which is duplicated work.',
  'Nessun gruppo puntuale da proporre: gli asset non provengono da metodi di rete indirizzabili.':
    'No targeted group to propose: the assets do not come from addressable network methods.',
  'Copia indirizzi': 'Copy addresses',
  'Copiati': 'Copied',
  // Discovery · sweep intelligente
  'Salta gli indirizzi già coperti da altre discovery':
    'Skip addresses already covered by other discoveries',
  'Gli indirizzi già in CMDB o già bersaglio di un altro job vengono verificati vivi ma non ri-scansionati a fondo, e il rapporto dice da dove sono coperti. Utile su una subnet larga che si sovrappone ad altri job.':
    'Addresses already in the CMDB or already targeted by another job are checked for liveness but not re-scanned in depth, and the report says where they are covered from. Useful on a broad subnet that overlaps other jobs.',
  // CI · tab Provenienza
  'Provenienza': 'Provenance',
  'Ultima sonda': 'Last probe',
  'Ultimo job': 'Last job',
  'Fonti': 'Sources',
  'Fonte': 'Source',
  'Job': 'Job',
  'primaria': 'primary',
  'Nessuna fonte registrata': 'No source recorded',
  'CI creato a mano o prima che si tracciasse la provenienza.':
    'CI created by hand, or before provenance was tracked.',
  'Job che l’hanno toccato': 'Jobs that touched it',
  'Provenienza per attributo': 'Provenance by attribute',
  'Mostra tutti': 'Show all',
  'in attesa': 'waiting',
  'in cattura': 'capturing',
  'non partita': 'not started',
  'Ciò che la raccolta non ha potuto leggere, dichiarato invece che taciuto: un campo assente qui è «non letto», non «non esiste». È l’ultima fotografia, non un registro — lo stesso dato non si accumula a ogni scansione.': 'What the collection could not read, declared rather than hidden: a missing field here is “not read”, not “does not exist”. It is the latest snapshot, not a log — the same datum does not pile up scan after scan.',
  'Dati non letti': 'Unread data',
  'Sezione': 'Section',
  '1 ogni 10': '1 in 10',
  '1 ogni 100': '1 in 100',
  '1 ogni 2': '1 in 2',
  '1 ogni 30': '1 in 30',
  'Campionamento di {ind}': 'Sampling for {ind}',
  'Non salvato': 'Not saved',
  'tieni tutto': 'keep all',
  '30 giorni': '30 days',
  '7 giorni': '7 days',
  '90 giorni': '90 days',
  'Attivi': 'Active',
  'Comparsi (7g)': 'Appeared (7d)',
  'Comparsi di recente': 'Recently appeared',
  'Comparsi e scomparsi per giorno': 'Appeared and gone per day',
  'Cosa compare e cosa non si trova più, nel tempo': 'What appears and what is no longer found, over time',
  'Nessun CI nuovo nel periodo.': 'No new CI in the period.',
  'Nessun cambiamento nel periodo.': 'No change in the period.',
  'Niente è sparito: buon segno.': 'Nothing has gone: good sign.',
  'Non più trovati': 'No longer found',
  'Non più trovati (7g)': 'No longer found (7d)',
  'Periodo': 'Period',
  'Silenti (stale)': 'Silent (stale)',
  'Statistiche scansioni': 'Scan statistics',
  'dismesso': 'retired',
  'non trovato': 'not found',
  'Cerca ovunque': 'Search everywhere',
  'Cerca ovunque (⌘K)': 'Search everywhere (⌘K)',
  'Componenti (schede, moduli, seriali)': 'Components (cards, modules, serials)',
  'Nessun risultato per «{q}».': 'No results for “{q}”.',
  'Rete (IP, MAC, porte)': 'Network (IP, MAC, ports)',
  'Ricerca globale': 'Global search',
  'Software': 'Software',
  'altri…': 'more…',
  'Configura una sonda nella rete da monitorare.': 'Set up a probe in the network to monitor.',
  'Avvia il primo job di discovery: i Configuration Item rilevati vengono registrati nell’Inventario.': 'Start the first discovery job: the Configuration Items found are recorded in the Inventory.',
  'Benvenuto in CENSO': 'Welcome to CENSO',
  'CENSO rileva automaticamente gli asset presenti in rete, individua le funzionalità e mantiene aggiornate le informazioni.': 'CENSO automatically detects the assets on the network, identifies their functions and keeps the information up to date.',
  'Comunicazioni tra asset, servizi esposti e dati scambiati (con NetFlow attivo).': 'Communications between assets, exposed services and data exchanged (with NetFlow enabled).',
  'Configura un piano di discovery definendo target e modalità di scansione.': 'Set up a discovery plan, defining targets and scan methods.',
  'Centralizza Configuration Item, Asset e Contratti, consolida i dati tecnici acquisiti e li aggiorna mediante discovery.': 'Centralises Configuration Items, Assets and Contracts, consolidates the technical data collected and keeps it updated through discovery.',
  'Da dove si comincia': 'Where to start',
  'Configurazione di sonde, credenziali, piani di discovery, job e relative pianificazioni.': 'Configuration of probes, credentials, discovery plans, jobs and their schedules.',
  'Visualizza asset, interfacce di rete e topologia delle connessioni.': 'Shows assets, network interfaces and the topology of connections.',
  'Mappa i servizi, le dipendenze degli asset e ne evidenzia gli impatti in caso di indisponibilità.': 'Maps the services, the assets they depend on and highlights the impact if they become unavailable.',
  'Configura le credenziali di accesso: SNMP, SSH e WinRM.': 'Set up the access credentials: SNMP, SSH and WinRM.',
  'Integrazione e scambio dati con piattaforme ITSM come ServiceNow, Matrix42, Remedy e ManageEngine.': 'Integration and data exchange with ITSM platforms such as ServiceNow, Matrix42, Remedy and ManageEngine.',
  'Postura di sicurezza degli host, conformità NIS2 e DORA, monitoraggio tramite Dobermann e Honeypot.': 'Security posture of hosts, NIS2 and DORA compliance, monitoring via Dobermann and Honeypot.',
  'Rete e topologia': 'Network and topology',
  'Salta': 'Skip',
  'Gestione utenti, impostazioni, riconoscimento degli asset e audit di accessi e modifiche.': 'User management, settings, asset recognition and audit of access and changes.',
  'es. esca DMZ': 'e.g. DMZ decoy',
  'Dove l’agent consegna gli hit: il Control Plane (cloud) o una sonda che li inoltra. Il ritorno al Control Plane è automatico se la sonda non risponde.': 'Where the agent delivers the hits: the Control Plane (cloud) or a probe that forwards them. Fallback to the Control Plane is automatic if the probe does not respond.',
  'chi è entrato e chi ha cambiato cosa. Gli accessi (login riusciti e falliti, cambio tenant) e le operazioni (creazioni, modifiche, cancellazioni) in un elenco solo, filtrabile per tipo e testo. È la prima cosa da guardare dopo un sospetto, e la vista che un auditor chiede per prima.': 'who logged in and who changed what. Access (successful and failed logins, tenant switch) and operations (creations, edits, deletions) in a single list, filterable by type and text. It is the first thing to look at after a suspicion, and the view an auditor asks for first.',
  'Accessi': 'Access',
  'Audit / Log': 'Audit / Log',
  'Cerca negli eventi': 'Search events',
  'Cerca per email, IP, oggetto…': 'Search by email, IP, object…',
  'Chi': 'Who',
  'Registro attività: traccia gli accessi e le operazioni eseguite nel tenant.': 'Activity log: tracks the access and the operations performed in the tenant.',
  'Eventi': 'Events',
  'Gli accessi e le operazioni compaiono qui man mano che avvengono.': 'Access and operations show up here as they happen.',
  'Operazioni': 'Operations',
  'Per pagina': 'Per page',
  'Precedenti': 'Previous',
  'Registro accessi': 'Access log',
  'Successivi': 'Next',
  'accesso': 'access',
  'accesso fallito': 'failed access',
  'cambio tenant': 'tenant switch',
  'codice MFA errato': 'wrong MFA code',
  'errore': 'error',
  'ok': 'ok',
  'operazione': 'operation',
  'respinto': 'rejected',
  'sconosciuto': 'unknown',
  '{da}–{a} di {total}': '{da}–{a} of {total}',
  'Honeypot': 'Honeypot',
  'il guardiano di Censo: rileva gli oggetti non censiti nella tua rete correlando inventario, scansioni e netflow. Dichiari le subnet da proteggere e un giro periodico incrocia il traffico con la CMDB — un indirizzo attivo mai censito diventa un avvistamento, con «Ispeziona» per capire con chi parla. Quando l’indirizzo compare in inventario, l’avvistamento passa da solo a «censito». Se togli o restringi una subnet, i suoi avvistamenti escono dall’elenco; gli amministratori possono anche azzerare tutto l’elenco con «Azzera avvistamenti».':
    'Censo’s watchdog: it spots uninventoried objects on your network by correlating inventory, scans and netflow. You declare the subnets to protect and a periodic pass crosses the traffic against the CMDB — an active address never inventoried becomes a sighting, with “Inspect” to see who it talks to. When the address appears in inventory, the sighting turns to “inventoried” on its own. If you remove or narrow a subnet, its sightings leave the list; administrators can also clear the whole list with «Reset sightings».',
  'agent esca autonomi. Registri un agent, scegli i servizi da fingere (SSH, RDP, SMB…) e dove consegna; ne esce un file di configurazione col segreto, da mettere accanto al binario su una macchina dedicata. È isolato dalla discovery di proposito: chi compromette l’esca non trova le credenziali del parco. Ogni connessione a un servizio esca è, per costruzione, sospetta — e finisce negli hit, con il payload catturato.': 'autonomous decoy agents. You register an agent, choose the services to fake (SSH, RDP, SMB…) and where it delivers; out comes a configuration file with the secret, to place next to the binary on a dedicated machine. It is isolated from discovery on purpose: whoever compromises the decoy does not find the estate credentials. Every connection to a decoy service is, by construction, suspicious — and ends up in the hits, with the captured payload.',
  'Agent': 'Agent',
  'Agent attivi': 'Active agents',
  'Agent esca autonomi espongono servizi esca per rilevare tentativi di connessione inattesi o potenzialmente ostili.': 'Autonomous decoy agents expose decoy services to detect unexpected or potentially hostile connection attempts.',
  'Agent honeypot': 'Honeypot agent',
  'Agent «{nome}» registrato': 'Agent “{nome}” registered',
  'Attiva {servizio}': 'Enable {servizio}',
  'Banner di {servizio}': 'Banner for {servizio}',
  'Consegna ogni (minuti)': 'Deliver every (minutes)',
  'IP da ignorare (opzionale)': 'IPs to ignore (optional)',
  'sì': 'yes',
  'no': 'no',
  'Campione della sessione (per l’analisi)': 'Session sample (for analysis)',
  'Scadenza predefinita salvata per i prossimi inviti.': 'Default expiry saved for future invites.',
  'Durata predefinita per il tenant: {ore} ore. Trascorso questo intervallo, il collegamento scade e non è più utilizzabile.':
    'Tenant default: {ore} hours. After that the link stops working.',
  'Salva come predefinito': 'Save as default',
  'Come dare accesso': 'How to grant access',
  'Invita via email (la password la sceglie lui)': 'Invite by email (they choose the password)',
  'Imposta una password': 'Set a password',
  'Scadenza invito (ore)': 'Invite expiry (hours)',
  'Dopo, il collegamento non vale più e serve un nuovo invito.': 'After that the link stops working and a new invite is needed.',
  'Invita utente': 'Invite user',
  'Crea utente': 'Create user',
  'Attendere…': 'Please wait…',
  'Utenza creata.': 'Account created.',
  'Invito inviato a {email}.': 'Invite sent to {email}.',
  'Eseguito da': 'Run from',
  'Architettura CPU': 'CPU architecture',
  'Raccolto da': 'Collected by',
  'Raccolto via': 'Collected via',
  'Virtualizzazione rilevata da': 'Virtualization detected by',
  'Pacchetti gestiti': 'Managed packages',
  'Edizione': 'Edition',
  'Livello di patch': 'Patch level',
  'Nome del database': 'Database name',
  'Riferimento database': 'Database reference',
  'Info': 'Info',
  'Avviso': 'Warning',
  'Errore': 'Error',
  'CI ricevuti': 'CIs received',
  'CI salvati': 'CIs saved',
  'Scartati senza chiave': 'Discarded without a key',
  'Rifiutati per quota': 'Rejected for quota',
  'Relazioni ricevute': 'Relations received',
  'Relazioni risolte': 'Relations resolved',
  'Batch ricevuti': 'Batches received',
  'IP': 'IP',
  'passo': 'step',
  'tipo': 'type',
  'indirizzi nell’ambito': 'addresses in scope',
  'eseguito su': 'run on',
  'riusciti': 'succeeded',
  'falliti': 'failed',
  'saltati per condizione': 'skipped by condition',
  'passo sconosciuto alla sonda': 'step unknown to the probe',
  'attributi raccolti': 'attributes collected',
  'interfacce raccolte': 'interfaces collected',
  'adiacenze raccolte': 'adjacencies collected',
  'indirizzi arricchiti': 'addresses enriched',
  'oggetti raccolti': 'objects collected',
  'nuovi': 'new',
  'già noti': 'already known',
  'per classe': 'by class',
  'prodotti software': 'software products',
  'schede macchina': 'machine cards',
  'adiacenze': 'adjacencies',
  'risponde al ping': 'responds to ping',
  'esito': 'outcome',
  'causa probabile': 'likely cause',
  'proprietà': 'properties',
  'proprietà per risorsa': 'properties per resource',
  'interfacce': 'interfaces',
  'indirizzi IP': 'IP addresses',
  'vicinati': 'neighbors',
  'identificativi': 'identifiers',
  'non leggibili': 'unreadable',
  'credenziale': 'credential',
  'credenziali provate': 'credentials tried',
  'durata': 'duration',
  'approfondita': 'deep',
  'per tipo': 'by type',
  'senza nome': 'unnamed',
  'numero': 'number',
  'omessi': 'omitted',
  'interrogazioni parallele': 'parallel queries',
  'core della sonda': 'probe cores',
  'deciso da': 'decided by',
  'pacchetti/s richiesti': 'packets/s requested',
  'pacchetti/s effettivi': 'packets/s actual',
  'attesa per interrogazione': 'wait per query',
  'massimo per questa sonda': 'max for this probe',
  'servirebbero': 'would need',
  'dimensionamento automatico': 'automatic sizing',
  'impostato nel job': 'set in the job',
  'tetto della sonda': 'probe cap',
  'Indirizzo mai censito': 'Never-inventoried address',
  "Connessione a un'esca": 'Connection to a decoy',
  'es. 10.0.0.5, 192.168.1.0/24': 'e.g. 10.0.0.5, 192.168.1.0/24',
  'Le connessioni da questi indirizzi non diventano hit: la sonda stessa, i sistemi di monitoraggio, gli scanner autorizzati. IP singoli o CIDR, separati da virgola o a capo.':
    'Connections from these addresses never become hits: the probe itself, monitoring systems, authorized scanners. Single IPs or CIDRs, separated by comma or newline.',
  'Copia il file': 'Copy the file',
  'Copiato': 'Copied',
  'Eliminare questo agent honeypot? Il segreto smette di valere e non consegnerà più.': 'Delete this honeypot agent? Its secret stops working and it will no longer deliver.',
  'Esche in ascolto': 'Decoys listening',
  'Impostazioni e notifiche': 'Settings and notifications',
  'Notifiche e marchio delle email': 'Email notifications and branding',
  'Come si presentano le email che la piattaforma manda — avvisi honeypot e Dobermann, inviti, messaggi di prova. Il canale d’invio si configura sopra, in «Posta in uscita».': 'How the emails the platform sends look — honeypot and Dobermann alerts, invites, test messages. The sending channel is configured above, in “Outbound mail”.',
  'Nome del prodotto': 'Product name',
  'Colore del marchio': 'Brand colour',
  'Colore scuro (gradiente)': 'Dark colour (gradient)',
  'Payoff (sotto la firma)': 'Payoff (below the signature)',
  'Email di supporto (facoltativa)': 'Support email (optional)',
  'Lingua delle email': 'Email language',
  'supporto@azienda.it': 'support@company.com',
  'Per vedere il risultato, manda un messaggio di prova da «Posta in uscita».': 'To see the result, send a test message from “Outbound mail”.',
  'Marchio salvato: vale per tutte le email.': 'Branding saved: it applies to all emails.',
  'Statistiche': 'Statistics',
  'LUN': 'LUNs',
  'Dischi, pool e LUN con le capacità arrivano dall’inventario «Huawei OceanStor» (REST): lancia quella discovery su questo array.':
    'Disks, pools and LUNs with their capacities come from the “Huawei OceanStor” (REST) inventory: run that discovery on this array.',
  'Usata': 'Used',
  'Uso': 'Usage',
  'Mappature (LUN → host)': 'Mappings (LUN → host)',
  'Gruppo LUN': 'LUN group',
  'Gruppo host': 'Host group',
  'Gruppo porte': 'Port group',
  'Certificato PDF': 'PDF certificate',
  'Campionamento SNMP': 'SNMP sampling',
  'La cattura dell’albero MIB serve a generare un driver di arricchimento.': 'The MIB-tree capture is used to generate an enrichment driver.',
  'Scarico…': 'Downloading…',
  'Scarica cattura (JSON)': 'Download capture (JSON)',
  'Cattura non ancora disponibile: il job deve completare il campionamento.': 'Capture not available yet: the job must finish sampling.',
  'Pool di storage': 'Storage pools',
  'Elenco dischi': 'Disks',
  'Elenco pool': 'Pools',
  'Elenco LUN': 'LUNs',
  'Un host (o IP) per istanza, separati da virgola. Aggiungi «:porta» per più istanze sullo stesso host — senza porta si usa quella standard del motore.': 'One host (or IP) per instance, comma-separated. Add “:port” for several instances on the same host — without a port the engine’s default is used.',
  'hit': 'hits',
  'Hit nel periodo': 'Hits in the period',
  'Hit (7g)': 'Hits (7d)',
  'Con credenziali': 'With credentials',
  'Hit per giorno': 'Hits per day',
  'Per servizio esca': 'By decoy service',
  'Sorgenti più attive': 'Most active sources',
  'Nessun hit nel periodo.': 'No hits in the period.',
  'Nessuna sorgente nel periodo.': 'No sources in the period.',
  'Avvisami via email quando qualcuno si connette a un’esca': 'Email me when someone connects to a decoy',
  'Avvisa solo per gli hit che hanno catturato credenziali': 'Alert only for hits that captured credentials',
  'Taglia il rumore dei port scan che si connettono e non inviano niente. L’hit resta comunque registrato: cambia solo se parte la mail.': 'Cuts the noise of port scans that connect and send nothing. The hit is recorded either way: this only changes whether the email is sent.',
  'Nessun destinatario configurato: aggiungilo in Impostazioni → Posta in uscita, o l’avviso non arriva a nessuno.': 'No recipient configured: add one in Settings → Outbound mail, or the alert reaches no one.',
  'Nuovi nel periodo': 'New in the period',
  'Nuovi (7g)': 'New (7d)',
  'Censiti': 'Inventoried',
  'Nuovi avvistamenti per giorno': 'New sightings per day',
  'Nessun avvistamento nel periodo.': 'No sightings in the period.',
  'Per subnet sorvegliata': 'By watched subnet',
  'Nessun avvistamento.': 'No sightings.',
  'I più parlanti': 'Top talkers',
  'Nessun avvistamento attivo.': 'No active sightings.',
  'Avvisami via email sui nuovi avvistamenti': 'Email me about new sightings',
  'Avvisa solo sopra questa soglia di flussi': 'Alert only above this flow threshold',
  '0 = avvisa per tutti. Alzala per ignorare il pacchetto vagante singolo (un device mal configurato che sonda una volta). L’avvistamento resta comunque registrato.': '0 = alert for all. Raise it to ignore a single stray packet (a misconfigured device that probes once). The sighting is recorded either way.',
  'cattura la connessione, non le credenziali (cifrate)': 'captures the connection, not the credentials (encrypted)',
  'Payload catturato': 'Captured payload',
  'Clic per il payload completo': 'Click for the full payload',
  '(vuoto: connessione senza dati inviati — spesso uno scanner di porte)': '(empty: connection with no data sent — often a port scanner)',
  'Su Linux (root)': 'On Linux (root)',
  'Scarica il binario, scrive la configurazione a 0600 e installa il servizio (systemd) che tiene l’esca in ascolto e la riavvia se cade.': 'Downloads the binary, writes the configuration at 0600 and installs the service (systemd) that keeps the decoy listening and restarts it if it falls over.',
  'Copia il comando': 'Copy the command',
  'Le modifiche valgono al prossimo file scaricato: rigenera il segreto per prendere il file aggiornato, o modifica il file sulla macchina a mano.': 'Changes take effect on the next downloaded file: regenerate the secret to get the updated file, or edit the file on the machine by hand.',
  'Nessun agent honeypot': 'No honeypot agent',
  'Nessuna esca in ascolto': 'No decoy listening',
  'Nuovo agent honeypot': 'New honeypot agent',
  'Nuovo segreto per «{nome}»': 'New secret for “{nome}”',
  'Porta di {servizio}': 'Port for {servizio}',
  'Registra e genera la configurazione': 'Register and generate the configuration',
  'Registra un agent honeypot': 'Register a honeypot agent',
  'Registra un agent, comparirà nell’elenco e inizierà a inviare i rilevamenti non appena installato.': 'Register an agent: it will appear in the list and start sending detections as soon as it is installed.',
  'Registra un altro agent': 'Register another agent',
  'Rigenera segreto': 'Regenerate secret',
  'Tienilo acceso con un servizio di sistema (systemd, un servizio Windows): un’esca ha senso solo se è in ascolto.': 'Keep it running with a system service (systemd, a Windows service): a decoy only makes sense while it is listening.',
  'Un agent Honeypot espone servizi esca (SSH, RDP, SMB…) su una macchina dedicata, isolata dalla discovery e priva delle credenziali degli asset gestiti. Registra i tentativi di connessione come Alert e li trasmette a una sonda per inoltro o direttamente al Control Plane.': 'A Honeypot agent exposes decoy services (SSH, RDP, SMB…) on a dedicated machine, isolated from discovery and without the credentials of the managed assets. It records connection attempts as Alerts and transmits them to a probe for forwarding or directly to the Control Plane.',
  'consegna: {dove}': 'delivers to: {dove}',
  'mai visto': 'never seen',
  'silenzioso': 'silent',
  'ultimo contatto {quando}': 'last seen {quando}',
  '{listening}/{active} esche in ascolto': '{listening}/{active} decoys listening',
  'Porte non aperte (occupate?):': 'Ports not open (in use?):',
  'L’agent non si fa più sentire: servizio fermo, o non raggiunge più il Control Plane.': 'The agent has gone quiet: the service is stopped, or it can no longer reach the Control Plane.',
  'revocato': 'revoked',
  'scaduto': 'expired',
  '{n} consegne': '{n} deliveries',
  '{n} esche': '{n} decoys',
  '{n} esche in ascolto': '{n} decoys listening',
  'Capacity': 'Capacity',
  'Capacity plan': 'Capacity plan',
  'mostra capacità, spazio utilizzato e disponibilità delle risorse di storage — e di compute e porte — rilevate nell’inventario, per individuare saturazioni e capacità residua.': 'Shows the capacity, used space and availability of the storage — and compute and ports — resources detected in the inventory, to spot saturation and remaining capacity.',
  'Storage': 'Storage',
  'Nessun datastore': 'No datastore',
  'Compaiono qui i datastore raccolti dai connettori di virtualizzazione.': 'Datastores collected by the virtualization connectors appear here.',
  'Capacità totale': 'Total capacity',
  'Usato': 'Used',
  'Datastore da tenere d’occhio': 'Datastores to watch',
  'Ambiente': 'Environment',
  'Compute (overcommit)': 'Compute (overcommit)',
  'Nessun host': 'No host',
  'Compaiono qui gli host raccolti dai connettori di virtualizzazione.': 'Hosts collected by the virtualization connectors appear here.',
  'vCPU allocate': 'Allocated vCPU',
  'Overcommit CPU': 'CPU overcommit',
  'Overcommit RAM': 'RAM overcommit',
  'L’overcommit è il rapporto fra risorse promesse alle VM e risorse fisiche: sopra 1× si conta sul fatto che non tutte tirino insieme.': 'Overcommit is the ratio of resources promised to VMs versus physical resources: above 1× you are counting on not all of them peaking at once.',
  'CPU (vCPU / core)': 'CPU (vCPU / cores)',
  'RAM (alloc. / fisica)': 'RAM (alloc. / physical)',
  'Porte degli apparati': 'Device ports',
  'Nessun apparato con porte note': 'No device with known ports',
  'Le porte si raccolgono con una discovery SNMP approfondita (deep).': 'Ports are collected with a deep SNMP discovery.',
  'Porte totali': 'Total ports',
  'In uso': 'In use',
  'Libere': 'Free',
  'In uso / totali': 'In use / total',
  'n/d': 'n/a',
  'VM': 'VMs',
  'Mappa delle correlazioni dell’host': 'Host correlation map',
  'Guest OS': 'Guest OS',
  'Nessun datastore usato dalle VM di questo host.': 'No datastore used by this host’s VMs.',
  'Nessuna VM su questo host.': 'No VM on this host.',
  'Nessuna rete usata dalle VM di questo host.': 'No network used by this host’s VMs.',
  'RAM': 'RAM',
  'RAM allocata': 'Allocated RAM',
  'colore = riempimento': 'color = fill level',
  'piena = accesa': 'solid = powered on',
  'Scegli un host per vederne VM, datastore e reti.': 'Pick a host to see its VMs, datastores and networks.',
  'Storage usato': 'Storage used',
  'VM accese': 'Running VMs',
  'accesa': 'on',
  'spenta': 'off',
  'vCPU / core': 'vCPU / cores',
  '{env} · cluster e host': '{env} · clusters and hosts',
  '{n} cluster': '{n} clusters',
  'Aggiornata': 'Updated',
  'Asset coinvolti': 'Assets involved',
  'Asset considerati': 'Assets considered',
  'Autovalutazione': 'Self-assessment',
  'Autovalutazione dell’organizzazione (non misurato dalla piattaforma)':
    'Organization self-assessment (not measured by the platform)',
  'Autovalutazione: cosa questa pagina non misura': 'Self-assessment: what this page does not measure',
  'Controlli falliti': 'Failed checks',
  'Due righe di contesto: come è coperto, da quando, con quali limiti.':
    'Two lines of context: how it is covered, since when, with what limits.',
  'Esempi': 'Examples',
  'Esporta CSV': 'Export CSV',
  'Generato il': 'Generated on',
  'Azioni derivate dagli esiti dei controlli, raggruppate per causa e ordinate per priorità: prima gli interventi di remediation per i controlli falliti, in seguito le attività di misurazione per i dati mancanti.': 'Actions derived from the control outcomes, grouped by cause and ordered by priority: first the remediation for the failed controls, then the measurement activities for the missing data.',
  'Le fasce nascono dai soli controlli misurati; «da verificare» segnala asset per cui mancano i dati, non asset a posto.':
    'Bands come from measured checks only; “to verify” flags assets lacking data, not assets in good shape.',
  'Nessuna dichiarazione compilata.': 'No declaration filled in.',
  'Niente da fare: tutti i controlli misurabili passano.': 'Nothing to do: every measurable check passes.',
  'Nota metodologica: la parte misurata nasce dai dati raccolti dalla piattaforma (inventario, rischi di sicurezza, backup delle configurazioni) al momento della generazione. L’autovalutazione è dichiarata dall’organizzazione e non è verificata dalla piattaforma. Questo documento non costituisce una certificazione.':
    'Methodological note: the measured part comes from the data collected by the platform (inventory, security findings, configuration backups) at generation time. The self-assessment is declared by the organization and is not verified by the platform. This document is not a certification.',
  'Organizzazione': 'Organization',
  'Per ogni requisito, indica come viene coperto dall’organizzazione: stato, riferimento al documento o alla procedura e responsabile. Nel report, queste evidenze sono identificate esplicitamente come dichiarate, non misurate.': 'For each requirement, state how the organization covers it: status, reference to the document or procedure and owner. In the report, this evidence is explicitly identified as declared, not measured.',
  'Piano di lavoro': 'Work plan',
  'Quanto segue è dichiarato dall’organizzazione, non misurato dai dati raccolti: le due cose restano separate di proposito.':
    'What follows is declared by the organization, not measured from collected data: the two are kept separate on purpose.',
  'Report da stampare': 'Printable report',
  'Requisiti misurati dalla piattaforma': 'Requirements measured by the platform',
  'Requisiti senza dichiarazione': 'Requirements without a declaration',
  'Requisito': 'Requirement',
  'Riferimento (documento o procedura)': 'Reference (document or procedure)',
  'Salva dichiarazione': 'Save declaration',
  'Salvata.': 'Saved.',
  'Sintesi': 'Summary',
  'Stampa / salva PDF': 'Print / save PDF',
  'Stato dichiarato': 'Declared status',
  'Ultimo aggiornamento: {quando} ({chi})': 'Last update: {quando} ({chi})',
  'da dichiarare': 'to declare',
  'es. CISO, Ufficio compliance': 'e.g. CISO, Compliance office',
  'es. Procedura IR-01, registro fornitori, policy MFA': 'e.g. Procedure IR-01, vendor register, MFA policy',
  'misurare': 'measure',
  'motivo': 'reason',
  'rimediare': 'remediate',
  'scegli…': 'choose…',
  'parzialmente coperto': 'partially covered',
  'non coperto': 'not covered',
  'non applicabile': 'not applicable',
  '(salvata: lascia vuoto per non cambiarla)': '(saved: leave empty to keep it)',
  'Notifica nuove rilevazioni da Dobermann.':
    'Alert on new Dobermann sightings',
  'Definisce il canale di invio degli avvisi Dobermann e i relativi destinatari. Il canale della piattaforma è adatto alla maggior parte degli scenari; un canale personalizzato consente di inviare le e-mail dal proprio dominio, applicando le relative policy.':
    "Where the tenant's alerts (Dobermann) go out from and who receives them. The platform channel suits almost everyone; a custom channel is for those who want mail to leave from their own domain, with their own policies.",
  'Destinatari degli avvisi (separati da virgola)': 'Alert recipients (comma-separated)',
  'Host SMTP': 'SMTP host',
  'noc@azienda.it, sicurezza@azienda.it': 'noc@company.com, security@company.com',
  'Invia prova': 'Send test',
  'Messaggio di prova consegnato al relay.': 'Test message delivered to the relay.',
  'Nome mittente': 'Sender name',
  'Posta in uscita e notifiche': 'Outgoing mail and notifications',
  'Relay della piattaforma': 'Platform relay',
  'SMTP del tenant': 'Tenant SMTP',
  'Viene inviata una sola e-mail per ogni ciclo di controllo, solamente quando viene rilevato un indirizzo mai censito.':
    'One mail per patrol, only when a never-inventoried address shows up.',
  'nessuna (relay interno)': 'none (internal relay)',
  '1 indirizzo mai censito sta facendo traffico.': '1 never-inventoried address is generating traffic.',
  'Ispeziona': 'Inspect',
  'Nessun avvistamento: tutto ciò che parla nelle {n} subnet sorvegliate è censito.':
    'No sighting: everything talking on the {n} watched subnets is inventoried.',
  '{n} indirizzi mai censiti stanno facendo traffico.': '{n} never-inventoried addresses are generating traffic.',
  'Dobermann confronta il traffico con l’inventario e segnala gli asset non censiti. Con «Ispeziona» ne esplori comunicazioni e porte; quando l’indirizzo entra nel CMDB, viene automaticamente considerato censito. In alternativa, puoi avviare una discovery, censirlo manualmente oppure «Ignora» specificandone il motivo.': 'Dobermann compares the traffic with the inventory and flags the unregistered assets. With «Ispeziona» you explore their communications and ports; when the address enters the CMDB it is automatically considered registered. Alternatively, you can start a discovery, register it manually or «Ignora» it by stating the reason.',
  'Con chi parla, su che porte, con che volumi — dai flussi raccolti.':
    'Who it talks to, on which ports, with what volumes — from the collected flows.',
  'Finestra temporale': 'Time window',
  'Interlocutori diretti (un livello). Per spingerti oltre:':
    'Direct peers (one level). To go further:',
  'Ispezione dell’indirizzo': 'Address inspection',
  'Mappa degli interlocutori': 'Peer map',
  'Nessuna conversazione nella finestra scelta: l’indirizzo ha parlato prima, o il traffico non passa dagli esportatori.':
    'No conversation in the chosen window: the address talked earlier, or its traffic does not pass through the exporters.',
  'Ultime 24 ore': 'Last 24 hours',
  'apri l’esplorazione completa': 'open the full exploration',
  'da': 'from',
  'il sospetto': 'the suspect',
  'interlocutore censito': 'inventoried peer',
  'non censito': 'not inventoried',
  'spessore = volume': 'thickness = volume',
  'verso': 'to',
  'Attiva la guardia': 'Activate the guard',
  'Censiti (lieti fini)': 'Inventoried (happy endings)',
  'Chiudi import': 'Close import',
  'Disattiva la guardia': 'Deactivate the guard',
  'Dobermann attivo': 'Dobermann active',
  'Ignorati': 'Ignored',
  'Importa': 'Import',
  'Importa elenco': 'Import list',
  'Importate {a} subnet nuove, {m} aggiornate — {tot} sorvegliate in tutto.':
    'Imported {a} new subnets, {m} updated — {tot} watched in total.',
  'Nuovi avvistamenti': 'New sightings',
  'Ogni quanto fa il giro': 'How often it patrols',
  'Tutte o nessuna: le righe storte vengono rifiutate col numero di riga.':
    'All or nothing: bad lines are refused with their line number.',
  'Ultimo giro': 'Last patrol',
  'Una rete per riga: 10.20.0.0/16, VLAN server — anche ; o TAB come separatore. Le righe con # sono commenti.':
    'One network per line: 10.20.0.0/16, server VLAN — ; or TAB work as separators too. Lines starting with # are comments.',
  'guardia disattivata': 'guard deactivated',
  'ogni {n} h': 'every {n} h',
  'ogni {n} min': 'every {n} min',
  'Dipendenze': 'Dependencies',
  'Archivia': 'Archive',
  'Notifica ogni connessione a un honeypot.':
    'Alert when someone connects to a decoy (honeypot)',
  'Banner (opzionale)': 'Banner (optional)',
  'Credenziali provate': 'Attempted credentials',
  'Esca': 'Decoy',
  'Hit': 'Hits',
  'Honeypot attivo': 'Honeypot active',
  'Indirizzi distinti': 'Distinct addresses',
  'Nessun hit': 'No hit',
  'Nessuno si è ancora connesso alle esche. Quando succede, comparirà qui con il payload catturato — e, se hai configurato le notifiche, arriverà una mail.': 'No one has connected to the decoys yet. When it happens, it will show up here with the captured payload — and, if you set up notifications, a mail will arrive.',
  'Nuovi hit': 'New hits',
  'Segna visto': 'Mark seen',
  'Tentativi': 'Attempts',
  'Ultimo tentativo': 'Last attempt',
  'Un rilevamento indica una connessione a un servizio esca che per definizione è un evento sospetto. Utilizzare «Ispeziona» per analizzarne l’origine in Dobermann o ricercarla nei flussi per ricostruirne la provenienza.': 'A detection indicates a connection to a decoy service, which by definition is a suspicious event. Use «Ispeziona» to analyse its origin in Dobermann or search for it in the flows to reconstruct where it came from.',
  'Non è previsto traffico legittimo verso questi sistemi. Ogni connessione viene considerata un allarme.':
    'A decoy receives no legitimate visitor: every connection is an alarm.',
  'archiviato': 'archived',
  'es. OpenSSH_8.2p1 Ubuntu': 'e.g. OpenSSH_8.2p1 Ubuntu',
  'visto': 'seen',
  'A riposo': 'At rest',
  'Dobermann in servizio': 'Dobermann on duty',
  'In servizio': 'On duty',
  '{n} nuovi avvistamenti': '{n} new sightings',
  'Aggiungi': 'Add',
  'FQDN': 'FQDN',
  'vip.azienda.it': 'vip.company.com',
  'Indirizzi aggiuntivi': 'Additional addresses',
  'FQDN aggiuntivi': 'Additional FQDNs',
  'alias.azienda.it': 'alias.company.com',
  'es. nome pubblico del servizio': 'e.g. the service’s public name',
  'VIP, alias di servizio o indirizzi secondari di questo oggetto. Ognuno diventa un CI a sé, legato a questo: l’indirizzo e l’FQDN entrano nell’identità, così il traffico verso il VIP si riconcilia con l’oggetto giusto.':
    "VIPs, service aliases or secondary addresses of this object. Each becomes a CI of its own, linked to this one: the address and FQDN enter identity, so traffic to the VIP reconciles with the right object.",
  'es. VIP del bilanciatore': 'e.g. load balancer VIP',
  'Honeypot in ascolto': 'Honeypot listening',
  'Niente che chieda attenzione.': 'Nothing needs your attention.',
  'Notifiche': 'Notifications',
  '{n} nuovi hit sulle esche': '{n} new hits on the decoys',
  'Avvistamenti': 'Sightings',
  'Dobermann': 'Dobermann',
  'Vai al CI': 'Go to the CI',
  'individua gli asset non censiti correlando le evidenze raccolte da inventory, discovery e flussi NetFlow.': 'finds the unregistered assets by correlating the evidence collected from inventory, discovery and NetFlow flows.',
  'Cos’è questo indirizzo? (la nota resta accanto alla decisione)':
    'What is this address? (the note stays next to the decision)',
  'Dichiara qui sopra le reti da proteggere: il giro orario confronterà il traffico con l’inventario.':
    'Declare above the networks to protect: the hourly patrol will compare traffic with the inventory.',
  'Fai il giro adesso': 'Patrol now',
  'Interlocutori': 'Peers',
  'reti in cui ogni indirizzo attivo deve corrispondere a un asset censito. Vanno incluse solo le reti sotto controllo, escludendo ambienti come guest e NAT, dove la presenza di indirizzi sconosciuti è prevista e genererebbe segnalazioni non significative.': 'networks where every active address must correspond to a registered asset. Include only the networks under control, excluding environments like guest and NAT, where the presence of unknown addresses is expected and would generate meaningless alerts.',
  'Lo conosco, ignora': 'I know it, ignore',
  'Nessun avvistamento': 'No sighting',
  'Nessuna subnet sorvegliata': 'No watched subnet',
  'Nome della subnet': 'Subnet name',
  'Primo traffico': 'First traffic',
  'Riattiva': 'Reactivate',
  'Rimuovi': 'Remove',
  'Sorveglia': 'Watch',
  'Subnet': 'Subnet',
  'Subnet sorvegliate': 'Watched subnets',
  'Tutto ciò che parla nelle subnet sorvegliate è censito. Il giro passa ogni ora sui flussi delle ultime 24.':
    'Everything talking on the watched subnets is inventoried. The patrol runs hourly over the last 24 hours of flows.',
  'Ultimo traffico': 'Last traffic',
  'Volume (24h)': 'Volume (24h)',
  'censito': 'inventoried',
  'ignorato': 'ignored',
  'nuovo': 'new',
  'es. VLAN server': 'e.g. server VLAN',
  'Ambienti virtuali': 'Virtual environments',
  'Visualizza cluster, host e macchine virtuali rilevati dai connettori di virtualizzazione ed evidenzia le relazioni e la distribuzione all’interno degli ambienti gestiti.':
    'Shows the clusters, hosts and virtual machines found by the virtualisation connectors and highlights the relations and distribution within the managed environments.',
  'Nessun ambiente virtuale': 'No virtual environment',
  'Compaiono qui dopo una discovery con un connettore di virtualizzazione (VMware, Nutanix, Proxmox…).': 'They appear here after a discovery with a virtualization connector (VMware, Nutanix, Proxmox…).',
  'Ambienti': 'Environments',
  '{n} host': '{n} hosts',
  'Cluster e host': 'Clusters and hosts',
  'Nessun host in questo ambiente.': 'No host in this environment.',
  'Senza cluster': 'No cluster',
  '{n} VM': '{n} VMs',
  'Posizione': 'Position',
  'La sonda è un servizio systemd che si chiama {servizio}. I comandi vanno eseguiti sulla macchina che la ospita, come root o con sudo.': 'The probe is a systemd service called {servizio}. Run the commands on the host machine, as root or with sudo.',
  'Un riavvio non perde l’identità né i dati (stanno in /var/lib/{servizio}):': 'A restart loses neither identity nor data (they live in /var/lib/{servizio}):',
  'L’installer lascia sulla macchina lo script di aggiornamento e un timer che lo esegue da sé. Per forzarlo adesso, scarica il binario nuovo dal Control Plane (con l’identità della sonda, nessun token) e riavvia il servizio:': 'The installer leaves the update script and a timer that runs it on its own. To force it now, download the new binary from the Control Plane (with the probe’s identity, no token) and restart the service:',
  'Se è già aggiornata non fa niente e lo dice; se il servizio non risale, rimette il binario precedente e riavvia. L’identità non si tocca: la sonda non si riarruola. Dopo, qui la «Versione della sonda» dice se l’aggiornamento è arrivato.': 'If it is already up to date it does nothing and says so; if the service does not come back up, it restores the previous binary and restarts. The identity is untouched: the probe does not re-enroll. Afterwards the “Probe version” here tells you whether the update landed.',
  'Guarda i log (journalctl) per l’errore di arruolamento o di certificato; verifica che la macchina raggiunga il canale delle sonde (porta 443) e che l’ora di sistema sia corretta — un orologio sfasato fa fallire il TLS con un errore che parla di certificato. Il servizio è /etc/systemd/system/{servizio}.service, l’identità e lo stato stanno in /var/lib/{servizio}.': 'Check the logs (journalctl) for an enrollment or certificate error; make sure the machine can reach the probe channel (port 443) and that the system clock is correct — a skewed clock makes TLS fail with an error that talks about the certificate. The service is /etc/systemd/system/{servizio}.service; identity and state live in /var/lib/{servizio}.',
  'Facoltativo, per l’inventario dei banchi di memoria: dmidecode si legge solo da root. Concedi a questo utente il permesso, senza password, per quel solo comando — e nient’altro:': 'Optional, for memory-bank inventory: dmidecode can only be read as root. Grant this user permission, without a password, for that one command only — and nothing else:',
  'Senza questa riga la discovery funziona lo stesso: i banchi di memoria restano vuoti e il rapporto lo dichiara. La sonda usa sudo solo per questo comando e solo se non entra già come root. Altri pochi dati (conteggio account senza password, config effettiva di sshd) richiedono root pieno e non li concediamo a un utente di sola lettura: se ti servono, usa una credenziale con privilegi di root.': 'Without this line discovery still works: the memory banks stay empty and the report says so. The probe uses sudo only for this command and only if it does not already log in as root. A few other data points (count of passwordless accounts, sshd’s effective config) need full root and we do not grant them to a read-only user: if you need them, use a credential with root privileges.',
  'Una chiave è una coppia di file: uno privato, che si incolla qui, e uno pubblico (.pub) che va sull’apparato da scansionare. Non riusare la chiave con cui entri a mano: creane una dedicata alla discovery. Ci sono due strade, a seconda di dove preferisci generarla.': 'A key is a pair of files: a private one, pasted here, and a public one (.pub) that goes on the device to scan. Do not reuse the key you log in with by hand: create one dedicated to discovery. There are two ways, depending on where you prefer to generate it.',
  'A) Genero la chiave sul mio computer e installo la pubblica': 'A) I generate the key on my computer and install the public one',
  'B) Creo utente e chiave sul server, poi copio la privata qui': 'B) I create the user and key on the server, then copy the private one here',
  'Utile quando preferisci preparare tutto sull’apparato Linux da scansionare. I comandi vanno eseguiti sul server, come root o con sudo.': 'Handy when you prefer to prepare everything on the Linux device to scan. The commands run on the server, as root or with sudo.',
  'Crea un utente dedicato, di sola lettura, senza password:': 'Create a dedicated, read-only user with no password:',
  'Genera la coppia di chiavi nella home dell’utente:': 'Generate the key pair in the user’s home:',
  'L’opzione -N con virgolette vuote crea la chiave senza passphrase; se ne metti una, riportala poi nel campo qui sotto.': 'The -N option with empty quotes creates the key without a passphrase; if you set one, enter it in the field below.',
  'Autorizza la chiave appena creata su questo stesso server:': 'Authorize the newly created key on this same server:',
  'Mostra la chiave PRIVATA per copiarla — e incollala nel campo qui sotto:': 'Show the PRIVATE key to copy it — and paste it in the field below:',
  'Copia tutto, dalla riga -----BEGIN fino a -----END compresa. Su un parco di più server, la stessa chiave pubblica va messa in authorized_keys di censo-discovery su ognuno.': 'Copy all of it, from the -----BEGIN line through -----END. Across several servers, the same public key must go into censo-discovery’s authorized_keys on each one.',
  'In entrambi i casi, verifica prima che la chiave funzioni: ssh -i censo-discovery censo-discovery@apparato deve entrare senza chiedere la password.': 'Either way, first check that the key works: ssh -i censo-discovery censo-discovery@device should log in without asking for a password.',
  'Manutenzione della sonda': 'Probe maintenance',
  'Vedere i log': 'View the logs',
  'In coda, solo le ultime righe (utile dopo un errore):': 'Tail, just the last lines (handy after an error):',
  'Stato e riavvio': 'Status and restart',
  'Forzare l’aggiornamento': 'Force an update',
  'Versione attuale:': 'Current version:',
  'Se non torna online': 'If it does not come back online',
  'Come creo una chiave SSH?': 'How do I create an SSH key?',
  'Generala (su Linux o Mac, dal terminale):': 'Generate it (on Linux or Mac, from the terminal):',
  'Ottieni due file nella cartella corrente: censo-discovery (privato) e censo-discovery.pub (pubblico). Se imposti una passphrase, mettila poi nel campo qui sotto.': 'You get two files in the current folder: censo-discovery (private) and censo-discovery.pub (public). If you set a passphrase, enter it in the field below.',
  'Installa la chiave pubblica sugli apparati da scansionare:': 'Install the public key on the devices to scan:',
  'In alternativa, aggiungi il contenuto del file .pub in coda a ~/.ssh/authorized_keys sull’host.': 'Alternatively, append the contents of the .pub file to ~/.ssh/authorized_keys on the host.',
  'Incolla qui il contenuto del file privato (censo-discovery, quello senza .pub): tutto, dalla riga -----BEGIN fino a -----END compresa.': 'Paste here the contents of the private file (censo-discovery, the one without .pub): all of it, from the -----BEGIN line through -----END.',
  // --- Navigazione ---
  Panoramica: 'Overview',
  Inventario: 'Inventory',
  Asset: 'Assets',
  Topologia: 'Topology',
  Traffico: 'Traffic',
  Raccolta: 'Collection',
  Amministrazione: 'Administration',
  Servizi: 'Services',
  'Pool di asset': 'Asset pools',
  'Piani di discovery': 'Discovery plans',
  'Job scansioni': 'Scan jobs',
  Scheduler: 'Scheduler',
  Sonde: 'Probes',
  'Gruppi di sonde': 'Probe groups',
  Credenziali: 'Credentials',
  'Agenti d’inventario': 'Inventory agents',
  'Configurazioni apparati': 'Device configurations',
  Utenti: 'Users',
  Impostazioni: 'Settings',
  'Utilizzo': 'Usage',
  Connettori: 'Connectors',
  Contratti: 'Contracts',
  'Lingua della console': 'Console language',
  Rete: 'Network',
  Analisi: 'Analytics',
  Report: 'Reports',
  Integrazioni: 'Integrations',
  'Business service': 'Business services',
  'Configuration Item': 'Configuration items',
  'La sincronizzazione verso l’ITSM richiede il modulo «ITSM Connector», che si aggiunge a qualunque piano. La prova a vuoto resta disponibile.':
    'Synchronising to the ITSM requires the «ITSM Connector» module, which can be added to any plan. The dry run remains available.',
  'Mappa correlazione': 'Correlation map',
  Riconoscimento: 'Recognition rules',
  Esportatori: 'Exporters',
  'Entry point': 'Entry points',

  // --- Azioni ricorrenti ---
  Salva: 'Save',
  'Salvataggio…': 'Saving…',
  Chiudi: 'Close',
  Elimina: 'Delete',
  Copia: 'Copy',
  Copiata: 'Copied',
  Scarica: 'Download',
  Apri: 'Open',
  Storia: 'History',

  // --- Configurazioni degli apparati ---
  'Apparati con configurazione salvata': 'Devices with a saved configuration',
  'Cosa è cambiato': 'What changed',
  'Configurazione intera': 'Full configuration',
  'Politica di Retention dello storico':
    'How long history is kept',
  'Versioni per apparato': 'Versions per device',
  Giorni: 'Days',
  'Apparato': 'Device',
  Indirizzo: 'Address',
  'Letto come': 'Read as',
  'Letta come': 'Read as',
  Versioni: 'Versions',
  'Ultima modifica': 'Last change',
  Quando: 'When',
  Pulizia: 'Cleanup',
  Dimensione: 'Size',
  tutte: 'all',
  'per sempre': 'forever',
  'Storia di {nome}': 'History of {nome}',

  // --- Eventi del rapporto: backup delle configurazioni ---
  //
  // Le chiavi sono i **modelli** che la sonda scrive, segnaposto compresi: i
  // valori arrivano in `details` e si sostituiscono dopo la traduzione, così
  // l'ordine delle parole può cambiare con la lingua.
  'configurazioni salvate: {salvate} su {totali}, di cui cambiate {cambiate}':
    'configurations saved: {salvate} of {totali}, {cambiate} changed',
  "{falliti} apparati non hanno risposto: la configurazione precedente resta l'ultima conosciuta":
    '{falliti} devices did not answer: the previous configuration remains the last known one',
  'famiglia non riconosciuta: nessun comando da mandare a {apparato}':
    'unrecognised device family: no command to send to {apparato}',
  'nessuna credenziale ha aperto la sessione su {apparato}':
    'no credential opened a session on {apparato}',
  'configurazione di {apparato} non letta': 'configuration of {apparato} not read',
  'la lettura di {apparato} è tornata vuota: non si salva':
    'the read of {apparato} came back empty: not saved',
  'configurazione di {apparato} letta ma non consegnata':
    'configuration of {apparato} read but not delivered',
  'configurazione di {apparato} cambiata': 'configuration of {apparato} changed',
  'nessun apparato da salvare: il job ha girato a vuoto':
    'no device to save: the job ran with nothing to do',
  'nessuna credenziale SSH utilizzabile': 'no usable SSH credential',
  'credenziale non risolta': 'credential not resolved',

  // --- Accesso ---
  'Su quale tenant vuoi operare?': 'Which tenant do you want to work on?',
  Accedi: 'Sign in',
  'Accesso in corso…': 'Signing in…',
  'Entra con {provider}': 'Sign in with {provider}',

  // --- Stati, che compaiono in ogni elenco ---
  'Assegnato': 'Assigned',
  'Attiva': 'Active',
  'Criticità alta': 'High criticality',
  'Criticità bassa': 'Low criticality',
  'Criticità media': 'Medium criticality',
  'Critico': 'Critical',
  'Degradata': 'Degraded',
  'Fallito': 'Failed',
  'In attesa': 'Pending',
  'In esecuzione': 'Running',
  'Offline': 'Offline',
  'Online': 'Online',
  'Parziale': 'Partial',
  'Preso in carico': 'Accepted',
  'Revocata': 'Revoked',
  'Riuscito': 'Succeeded',
  'Sospesa': 'Suspended',

  // --- Scheda dell'apparato: stato del backup ---
  'Backup della configurazione': 'Configuration backup',
  'Backup attivo': 'Backup active',
  'Backup in ritardo': 'Backup overdue',
  'Backup fermo': 'Backup stalled',
  'Mai salvata': 'Never saved',
  '{n} versioni': '{n} versions',
  'controllato {quando}': 'checked {quando}',
  'mai cambiata da quando è sotto backup': 'unchanged since backup began',
  'Letto con successo di recente.': 'Read successfully recently.',
  'Nessuna lettura riuscita da più di una settimana: il job potrebbe non essere più in pianificazione, o non riuscire ad accedere all’apparato.':
    'No successful read for over a week: the job may no longer be scheduled, or may be unable to reach the device.',
  'Nessuna lettura riuscita da oltre un mese: la copia che c’è non descrive più l’apparato con abbastanza fiducia da ripristinarla senza guardarla.':
    'No successful read for over a month: the copy on file no longer describes the device reliably enough to restore it unchecked.',
  'Questo apparato non è mai stato letto con successo.': 'This device has never been read successfully.',
  'Nessuna copia della configurazione. Di solito perché non c’è ancora un job di tipo «Backup configurazioni» che comprenda questo apparato — non perché il salvataggio sia fallito: in quel caso il rapporto del job lo direbbe.':
    'No copy of the configuration. Usually because there is no “Configuration backup” job covering this device yet — not because the save failed: the job report would say so.',
  'a (ora)': 'at (time)',
  'A che domanda risponde': 'What question it answers',
  'A cosa corrisponde': 'What it corresponds to',
  'A cosa corrisponde, per chi lo troverà fra sei mesi':
    'What it corresponds to, for whoever finds it six months from now',
  'A cosa risponde': 'What it answers',
  'A quale macchina corrisponde, per chi lo troverà fra sei mesi':
    'Which machine it corresponds to, for whoever finds it six months from now',
  'Abilita la capability NetFlow su una sonda e configura gli apparati per esportare i flussi verso il suo indirizzo. La porta si sceglie nella scheda della sonda, sotto «Sorgenti dei flussi»: per difetto è la 2055.':
    'Enable the NetFlow capability on a probe and configure the devices to export flows to its address. The port is chosen on the probe’s page, under “Flow sources”: it defaults to 2055.',
  'Abilitato il': 'Enabled on',
  'Accesso': 'Sign in',
  'Accesso con il provider aziendale (OpenID Connect)':
    'Sign in with the company provider (OpenID Connect)',
  "Accorciare la finestra non libera spazio all'istante: ClickHouse elimina i dati scaduti alla merge successiva della parte che li contiene, quindi nel giro di ore. Se il disco non cala subito non è un guasto.":
    'Shortening the window does not free space immediately: ClickHouse drops expired data at the next merge of the part holding it, so within hours. If the disk does not shrink right away, nothing is broken.',
  'Acquisto': 'Purchase',
  'censo-amministratori': 'censo-administrators',
  "Agenti d'inventario": 'Inventory agents',
  'Aggiorna quelli che ci sono già': 'Update the ones already there',
  'Aggiornamento': 'Update',
  'Aggiornate': 'Updated',
  'Aggiornati': 'Updated',
  'Aggiornato': 'Updated',
  'Aggiunge il nome DNS ai CI trovati.': 'Adds the DNS name to the CIs found.',
  'Aggiungi…': 'Add…',
  'Aggiungi credenziale': 'Add credential',
  'Aggiungi un passo': 'Add a step',
  'Alcune sonde non hanno completato l’aggiornamento.': 'Some probes did not complete the update.',
  'Alcuni provider lo richiedono': 'Some providers require it',
  'almeno una': 'at least one',
  'Alta': 'High',
  'Altri attributi': 'Other attributes',
  'Altri parametri (JSON)': 'Other parameters (JSON)',
  'Ambiti': 'Scopes',
  'Ambiti predefiniti': 'Default scopes',
  'Ambito': 'Scope',
  'Ambito da scansionare': 'Scope to scan',
  'Amministrazione della piattaforma': 'Platform administration',
  'Anagrafica': 'Identity',
  'Anche ciò che sta dietro i vicini': 'Also what lies beyond the neighbours',
  'Apparati': 'Devices',
  'Apparati che inviano flussi alle sonde': 'Devices sending flows to the probes',
  'Apri la scheda del CI →': 'Open the CI page →',
  'Archi mostrati': 'Edges shown',
  "Arrivano alla macchina con la prossima consegna: nessuno deve tornarci sopra, e il segreto non cambia — rigenerarlo per cambiare un'impostazione invaliderebbe l'agente che sta funzionando.":
    'They reach the machine with the next delivery: nobody has to go back to it, and the secret does not change — regenerating it just to change a setting would invalidate an agent that is working.',
  'Arrivata con il prodotto; modificabile come le altre':
    'Shipped with the product; editable like the others',
  'Arruolata il': 'Enrolled on',
  'Ascolto…': 'Listening…',
  'Assegna': 'Assign',
  'Assegnazione': 'Assignment',
  'Assegnazione dei job, recupero dei lease scaduti e controllo di salute delle sonde. Gira da sé; qui si forza un giro e si vede che cosa ha fatto.':
    'Job assignment, recovery of expired leases and probe health checks. It runs on its own; here you force a pass and see what it did.',
  'Asset Discovery & Baseline System': 'Asset Discovery & Baseline System',
  'Attesa fra i tentativi (min)': 'Wait between attempts (min)',
  'attivo': 'active',
  'attributo': 'attribute',
  'Attributo': 'Attribute',
  'Autenticazione': 'Authentication',
  'automatico': 'automatic',
  'Avvia comunque': 'Start anyway',
  'Avvia la prima scansione dai riquadri qui sopra.':
    'Start the first scan from the panels above.',
  'Avvia una discovery dalla sezione Discovery per popolare la CMDB.':
    'Start a discovery from the Discovery section to populate the CMDB.',
  'Avvia la discovery associando al job un piano e un Pool di asset. La sonda esegue la sequenza configurata sui target del pool, acquisisce i risultati e aggiorna i Configuration Item. Titolo e descrizione identificano finalità e contesto.':
    "Start discovery by associating a plan and an asset Pool with the job: the probe runs the planned sequence on the pool's targets, collects the results and updates the Configuration Items. Title and description identify the job's purpose and context and can be changed later.",
  'Avviato': 'Started',
  'azienda.it, azienda.com': 'company.com, company.net',
  'Azione': 'Action',
  'Azioni': 'Actions',
  'Bassa': 'Low',
  'Bene': 'Asset',
  'Beni': 'Assets',
  'Beni registrati': 'Registered assets',
  'Beni scoperti': 'Discovered assets',
  'Byte minimi': 'Minimum bytes',
  'Cambiandolo servono i campi del nuovo metodo: quelli del precedente restano salvati ma non vengono più usati.':
    'Changing it requires the new method’s fields: the previous ones stay saved but are no longer used.',
  'Campi osservati per questa classe: nessuno li ha dichiarati in anticipo.':
    'Fields observed for this class: nobody declared them in advance.',
  'Campionamento della cattura': 'Capture sampling',
  'Campo': 'Field',
  'Campo CENSO': 'CENSO field',
  'Campo di destinazione': 'Target field',
  'Canale delle sonde': 'Probe channel',
  'Capability operative': 'Operational capabilities',
  'Capacità': 'Capacity',
  'Cartellino': 'Tag',
  'cartellino, nome o matricola': 'tag, name or staff number',
  'Catalogo': 'Catalogue',
  'Catalogo prodotti': 'Product catalogue',
  'Catalogo vuoto': 'Empty catalogue',
  'Categoria': 'Category',
  'Cattura diretta dal filo': 'Direct capture from the wire',
  'Cattura di prova (10s)': 'Test capture (10s)',
  'Porte (opzionale)': 'Ports (optional)',
  'es. 2055, 6343': 'e.g. 2055, 6343',
  'Con le porte indicate tiene solo quei pacchetti — utile per vedere se su una porta arriva NetFlow/sFlow.':
    'With the given ports it keeps only those packets — useful to see whether NetFlow/sFlow arrives on a port.',
  'Usa le porte dei flussi': 'Use the flow ports',
  'Diagnosi flussi': 'Flow diagnosis',
  'Diagnosi in corso…': 'Diagnosing…',
  'Ascolta qualche secondo la porta dei flussi e riferisce se e a chi arrivano — la risposta a «sembra arrivi NetFlow ma la sonda non lo rileva».':
    'Listens on the flow port for a few seconds and reports whether — and to whom — flows arrive: the answer to “NetFlow seems to arrive but the probe doesn’t detect it”.',
  'Cattura in corso…': 'Capturing…',
  'Interfaccia da campionare': 'Interface to sample',
  'Predefinita della sonda': "Probe's default",
  'La cattura non è riuscita.': 'The capture failed.',
  'Cattura scaricata. Aprila in Wireshark. Puoi rilanciarla quando vuoi.':
    'Capture downloaded. Open it in Wireshark. You can run it again whenever you like.',
  'Registra circa 10 secondi di traffico sull’interfaccia e scarica un file .pcap da aprire in Wireshark. Non modifica la raccolta continua.':
    'Records about 10 seconds of traffic on the interface and downloads a .pcap file to open in Wireshark. It does not affect continuous collection.',
  'Cattura non avviata.': 'Capture not started.',
  'Cattura su': 'Capture on',
  'Centro di costo': 'Cost centre',
  'Cerca': 'Search',
  'Cerca in tutte le colonne…': 'Search across all columns…',
  'Cerca nome o indirizzo…': 'Search name or address…',
  'Cerca un apparato nella mappa': 'Search for a device on the map',
  'Certificato': 'Certificate',
  'Certificato (PEM)': 'Certificate (PEM)',
  'Certificato applicato.': 'Certificate applied.',
  'Certificato autofirmato.': 'Self-signed certificate.',
  'Certificato client': 'Client certificate',
  'Certificato scritto, ma non ancora servito.': 'Certificate written, but not served yet.',
  'Che cosa contiene': 'What it contains',
  'Che cosa fa': 'What it does',
  "Che fare se il sistema era fermo all'orario previsto. Di un inventario interessa quasi sempre lo stato attuale.":
    'What to do if the system was down at the scheduled time. For an inventory, what matters is almost always the current state.',
  'Chi accede lo fa con indirizzo e password. Collegando un IdP, gli utenti già abilitati entrano con le credenziali aziendali.':
    'People sign in with an address and a password. Connect an IdP and already-enabled users sign in with their company credentials.',
  'Analizza le comunicazioni tra asset, evidenziando sorgenti, destinazioni, porte, protocolli e volumi di traffico.':
    'Analyses the communications between assets, highlighting sources, destinations, ports, protocols and traffic volumes.',
  'Chi può entrare in questa console, con quale ruolo e da dove':
    'Who can enter this console, with which role and from where',
  'Chi vince sui campi senza direzione': 'Which side wins on fields with no direction',
  'Chiave': 'Key',
  'Chiave CENSO': 'CENSO key',
  'Chiave di destinazione': 'Target key',
  'Chiave naturale': 'Natural key',
  'Chiave privata (PEM)': 'Private key (PEM)',
  'Chiedi a un amministratore del tenant di modificare gli accessi.':
    'Ask a tenant administrator to change the access rights.',
  "Chiedi un certificato a Let's Encrypt": 'Request a certificate from Let’s Encrypt',
  'CI agganciati': 'CIs attached',
  'CI compresi': 'CIs included',
  'CI da registrare': 'CIs to register',
  'CI gestiti': 'CIs managed',
  'CI inventariati': 'CIs inventoried',
  'Ciò che i passi precedenti hanno scoperto': 'What the previous steps discovered',
  'Classe': 'Class',
  'Classificazione': 'Classification',
  'Client ID': 'Client ID',
  'Client secret': 'Client secret',
  'Cliente': 'Customer',
  'cloud': 'cloud',
  'CMDB, asset, contratti, diagnostica dei job': 'CMDB, assets, contracts, job diagnostics',
  'Codice': 'Code',
  'Genera un token di registrazione e installa una sonda nella rete da sottoporre a discovery. In ambienti multisede, configura una sonda per sede e raggruppale per consentire l’assegnazione automatica dei job alla sonda disponibile. Verifica periodicamente versione, capability operative e stato; dopo ogni aggiornamento della piattaforma, controlla l’allineamento delle versioni, perché le nuove funzionalità di raccolta richiedono una versione compatibile della sonda.': 'Generate an enrolment token and install a probe in the network to be discovered. In multi-site environments, set up one probe per site and group them so jobs are assigned automatically to an available probe. Check version, operating capabilities and status periodically; after every platform upgrade, verify that versions are aligned, because new collection features require a compatible probe version.',
  'Collegamenti configurati': 'Connections configured',
  'Collegamenti verso i sistemi di destinazione. Il flusso segue una sequenza controllata: configurazione dell’accesso, scansione del sistema, definizione del mapping, dry-run, sincronizzazione e pianificazione.': 'Links to the destination systems. The flow follows a controlled sequence: access configuration, system scan, mapping definition, dry-run, synchronization and scheduling.',
  'Collegamento': 'Connection',
  'Collegamento selezionato': 'Selected connection',
  'Collegato a': 'Connected to',
  'Collocazione': 'Location',
  'Classificazione degli oggetti e associazione ai prodotti':
    'How the platform works out what an object is, and which product it matches',
  'Come lo chiamano le due parti: è quello che si cita in una richiesta di intervento, e senza il fornitore non trova nulla.':
    'What both sides call it: it is what you quote in a support request, and without it the vendor finds nothing.',
  'Come si chiama davvero: è ciò che vedrà il cliente.':
    'What it is really called: this is what the customer will see.',
  'come si rinnova, chi lo rinnova': 'how it renews, who renews it',
  'Completato': 'Completed',
  'Completo': 'Full',
  'Completo + software': 'Full + software',
  'Completo + software installato': 'Full + installed software',
  'Componenti': 'Components',
  'Comprese:': 'Included:',
  'Con una sola credenziale per tipo non serve: creane uno quando gli apparati non rispondono tutti alla stessa.':
    'With a single credential per type it is not needed: create one when the devices do not all answer to the same one.',
  'Concluso': 'Finished',
  'Condizioni': 'Conditions',
  'Conferma': 'Confirm',
  'Configura un collegamento qui sopra: dopo averne verificato la connettività, potrai eseguire la scansione del sistema di destinazione.': 'Configure a link above: after verifying its connectivity, you will be able to scan the destination system.',
  'Configuration item': 'Configuration item',
  'Mostra gli asset nuovi, non più rilevati, attivi e silenti.':
    'Configuration Items collected in the CMDB',
  'Configurazione': 'Configuration',
  'Connessioni': 'Connections',
  'Consegna a': 'Deliver to',
  'Consegne': 'Deliveries',
  'Conservazione del traffico': 'Traffic retention',
  'Console server': 'Console server',
  'Console server Lantronix': 'Lantronix console server',
  'console_server': 'console_server',
  'console.esempio.it, api.esempio.it': 'console.example.com, api.example.com',
  'Consumo': 'Usage',
  'Conta nei gruppi in failover: le riserve ricevono lavoro solo se le attive mancano.':
    'It matters in failover groups: standbys only receive work if the active ones are missing.',
  'Contatto del fornitore': 'Vendor contact',
  'Contenuto': 'Content',
  'Contratto': 'Contract',
  'Control Plane': 'Control Plane',
  'Conversazioni': 'Conversations',
  'coperto': 'covered',
  'Coperto da': 'Covered by',
  'Coperto fino a': 'Covered until',
  'Copertura contrattuale': 'Contract coverage',
  'Cosa non torna': 'What does not add up',
  'Cosa sincronizzare': 'What to synchronise',
  "Così com'è, una scansione che usa questo pool non avrebbe niente da guardare — e riuscirebbe senza fare niente.":
    'As it stands, a scan using this pool would have nothing to look at — and would succeed without doing anything.',
  'Costruttore': 'Manufacturer',
  'Crea gli oggetti che nella destinazione non ci sono':
    'Create objects that are missing in the target',
  'Crea prima una credenziale nella sezione Credenziali.':
    'Create a credential first, in the Credentials section.',
  'creane una': 'create one',
  'Creane una per poter avviare una discovery.': 'Create one so you can start a discovery.',
  'Creane uno': 'Create one',
  'Creata': 'Created',
  'Creati': 'Created',
  'Creato': 'Created',
  'Credenziale': 'Credential',
  'Credenziale singola': 'Single credential',
  'Credenziali configurate': 'Credentials configured',
  'Criticità': 'Criticality',
  'Da dove': 'From where',
  'Da dove partire': 'Where to start',
  "Da dove si entra nel servizio: il VIP del bilanciatore, il front-end, l'indirizzo pubblicato. È l'unica cosa da dichiarare a mano — dai flussi si vede che due macchine parlano, non quale delle due è l'ingresso. Il resto della mappa si ricava seguendo il traffico da qui.":
    'Where the service is entered: the load balancer VIP, the front end, the published address. It is the only thing to declare by hand — the flows show that two machines talk, not which of the two is the way in. The rest of the map is derived by following the traffic from here.',
  'Da dove si parla': 'Where it is spoken from',
  'Da guardare': 'To look at',
  "Da qui deriva l'indirizzo del cliente e non si potrà più cambiare: l'indirizzo sì, l'identificativo no.":
    'The customer’s address derives from this and cannot be changed afterwards: the address can, the identifier cannot.',
  'Da un indirizzo: chi gli ha parlato, con chi ha parlato, e il vicinato dei suoi vicini':
    'From one address: who talked to it, who it talked to, and the neighbourhood of its neighbours',
  'Da una sonda': 'From a probe',
  'da verificare': 'to be checked',
  'Dal cloud': 'From the cloud',
  'Dashboard': 'Dashboard',
  'Data che conta': 'Effective date',
  'Data di acquisto': 'Purchase date',
  'Database': 'Database',
  'Database (PostgreSQL)': 'Database (PostgreSQL)',
  'Dati grezzi': 'Raw data',
  'Decorrenza': 'Start date',
  'Dedotto dalle relazioni, non dichiarato: sparisce da solo quando sparisce il fatto.':
    'Inferred from the relations, not declared: it disappears on its own when the fact does.',
  'Servizi erogati dall’infrastruttura, identificati e ricostruiti tramite l’analisi dei flussi di rete osservati. La composizione dei servizi include componenti applicativi, dipendenze e infrastruttura sottostante, con evidenza delle relazioni tra i sistemi che contribuiscono alla loro erogazione.':
    'Services delivered by the infrastructure, identified and rebuilt from the analysis of the observed network flows. The composition of the services includes application components, dependencies and underlying infrastructure, showing the relations between the systems that contribute to delivering them.',
  'derivato dal nome': 'derived from the name',
  'Descrizione': 'Description',
  'Descrizione breve': 'Short description',
  'Destinazione': 'Target',
  'Distance': 'Distance',
  'VRF/VPN-INSTANCE': 'VRF/VPN-INSTANCE',
  'Nell’elenco delle subnet, accanto a descrizione, sezione e VLAN, c’è anche la VRF/VPN-INSTANCE di phpIPAM.': 'In the subnet list, next to description, section and VLAN, there is also the phpIPAM VRF/VPN-INSTANCE.',
  'globale': 'global',
  'Nelle interfacce del tab Rete, accanto all’IP compaiono l’FQDN e la VRF/VPN-INSTANCE: l’FQDN lo risolve la sonda (reverse DNS) quando la scansione SNMP ha l’opzione «Risoluzione DNS inversa» accesa; la VRF si deduce dalle rotte connesse (l’IP d’interfaccia è la rotta /32 locale nella tabella della sua VRF, es. 10.21.170.10 → management). Le due colonne appaiono solo quando c’è il dato.': 'In the Rete tab interfaces, next to the IP you see the FQDN and the VRF/VPN-INSTANCE: the probe resolves the FQDN (reverse DNS) when the SNMP scan has the «Risoluzione DNS inversa» option on; the VRF is derived from connected routes (the interface IP is the local /32 route in its VRF table, e.g. 10.21.170.10 → management). The two columns appear only when the data is there.',
  'Negli elenchi di rete, IPAM e routing non si mostrano le reti di servizio 127.0.0.0/8 (loopback) e 255.0.0.0/8 (broadcast/riservato): sono rumore, non apparati né rotte utili.': 'In the network, IPAM and routing lists the service networks 127.0.0.0/8 (loopback) and 255.0.0.0/8 (broadcast/reserved) are not shown: they are noise, neither devices nor useful routes.',
  'Sui dispositivi con più tabelle, l’inventario SSH legge anche le rotte per VRF (Cisco: show ip vrf → show ip route vrf …) e vpn-instance (Huawei: display ip routing-table all-routes, o per i 5732 una lettura per vpn-instance): la colonna «VRF/VPN-INSTANCE» appare quando c’è più di una tabella, con «globale» per quella pubblica.': 'On devices with several tables, the SSH inventory also reads routes per VRF (Cisco: show ip vrf → show ip route vrf …) and vpn-instance (Huawei: display ip routing-table all-routes, or per vpn-instance on the 5732): the «VRF/VPN-INSTANCE» column appears when there is more than one table, with «globale» for the public one.',
  'dettaglio': 'detail',
  'Dettaglio (giorni)': 'Detail (days)',
  "Deve essere raggiungibile dalle macchine che consegnano, e da nessun altro: è l'unica porta che una sonda apre in ingresso.":
    'It must be reachable from the machines that deliver, and from nobody else: it is the only inbound port a probe opens.',
  'Direzione': 'Direction',
  'Disattivandola smette di ricevere job ma resta arruolata e continua a mandare heartbeat: serve a metterla da parte durante una manutenzione senza doverla rimuovere e riarruolare.':
    'Disabling it stops job assignment but it stays enrolled and keeps sending heartbeats: it is how you set a probe aside during maintenance without removing and re-enrolling it.',
  "Disattivarlo ferma l'accesso con il provider senza perdere la configurazione: è il gesto giusto mentre l'IdP è in manutenzione.":
    'Disabling it stops sign-in through the provider without losing the configuration: it is the right move while the IdP is under maintenance.',
  'Disattivata': 'Disabled',
  'Discovery approfondita': 'In-depth discovery',
  'Dispositivo': 'Device',
  'Dispositivo collegato': 'Attached device',
  'Distanza dal seme': 'Distance from the seed',
  "Distribuisce l'avvio quando più pianificazioni hanno lo stesso orario.":
    'Spreads the start when several schedules share the same time.',
  'Diversa dalla garanzia: quella dice fino a quando qualcuno lo ripara, questa fino a quando lo si vuole tenere.':
    'Different from the warranty: that says until when somebody repairs it, this says until when you intend to keep it.',
  'Dizionario indirizzo → CI': 'Address → CI dictionary',
  'Report predefiniti su inventario, asset, contratti e discovery, con ambito ed esclusioni espliciti per evitare risultati fuorvianti.': 'Predefined reports on inventory, assets, contracts and discovery, with explicit scope and exclusions to avoid misleading results.',
  'Domini': 'Domains',
  'Domini di posta': 'Mail domains',
  'Dominio di base': 'Base domain',
  'Dominio e hostname di servizio': 'Domain and service hostnames',
  'dopo la compressione': 'after compression',
  'Due bastano per «front-end → applicativo → database». Su una rete piatta alzarla include mezza infrastruttura.':
    'Two is enough for “front end → application → database”. On a flat network, raising it takes in half the infrastructure.',
  'Due oggetti in CMDB per lo stesso indirizzo.': 'Two CMDB objects for the same address.',
  'Durata': 'Duration',
  'Inizio': 'Start',
  'è ciò che cresce': 'is what grows',
  'è fallito': 'failed',
  'e il campo': 'and the field',
  'È il primo passo: non c’è ancora niente su cui condizionare, quindi parte sempre.':
    'It is the first step: there is nothing to condition on yet, so it always runs.',
  "È il volume che si riempie per primo: il traffico cresce di continuo, l'inventario no. Se si avvicina al pieno, le leve sono due — accorciare la retention dei flussi o allargare il volume.":
    'It is the volume that fills first: traffic grows continuously, inventory does not. If it approaches full, there are two levers — shorten flow retention or grow the volume.',
  'È quella che il fornitore usa per decidere se un apparato è in garanzia: meglio vuota che sbagliata.':
    'It is what the vendor uses to decide whether a device is under warranty: better empty than wrong.',
  'È quella che verrà provata su ogni apparato':
    'It is the one that will be tried on every device',
  'È quello che compare sul pulsante: «Entra con…».':
    'It is what appears on the button: “Sign in with…”.',
  'è riuscito': 'succeeded',
  'è stato saltato': 'was skipped',
  'Definisce il perimetro di discovery raggruppando subnet, sedi e ambienti. Ogni scansione utilizza il pool associato per determinare segmenti e target da acquisire; le modifiche al pool si applicano automaticamente alle pianificazioni collegate.':
    'Groups subnets, sites and environments to define the network perimeter on which to run discovery. Each scan uses the associated pool to determine which segments and targets to include; changes to the pool apply automatically to the schedules that use it.',
  'Elimina questa vista': 'Delete this view',
  'Eliminazione irreversibile.': 'Irreversible deletion.',
  'Email': 'Email',
  'Emesso da': 'Issued by',
  'es. Portale clienti': 'e.g. Customer portal',
  'es. Sede di Milano': 'e.g. Milan office',
  'es. sonda-milano-dc1': 'e.g. probe-milan-dc1',
  'es. Team Digital': 'e.g. Digital Team',
  'es. VIP portale': 'e.g. portal VIP',
  'Esaminati': 'Examined',
  'esclusi gli alienati': 'disposed assets excluded',
  'Esecuzioni perse': 'Missed runs',
  'Esegui questo comando sulla macchina, dentro la rete da inventariare. Scarica la sonda, la installa come servizio e la arruola.':
    'Run this command on the machine, inside the network to inventory. It downloads the probe, installs it as a service and enrols it.',
  'Esegui questo passo': 'Run this step',
  'Esegui su': 'Run on',
  'Eseguito su': 'Run on',
  'Esito': 'Outcome',
  'Esito di un passo precedente': 'Outcome of an earlier step',
  'Espressione cron': 'Cron expression',
  'Etichetta': 'Label',
  'Evento': 'Event',
  'fallita': 'failed',
  'Fallite': 'Failed',
  'Falliti': 'Failed',
  'falso': 'false',
  'Famiglia': 'Family',
  'Fase': 'Phase',
  'Filtra': 'Filter',
  'Filtra per fase': 'Filter by phase',
  'Filtra per porta': 'Filter by port',
  'Filtri': 'Filters',
  'Fine vita prevista': 'Expected end of life',
  'Finestra': 'Window',
  'Finestra da (ora)': 'Window from (time)',
  'Flussi': 'Flows',
  'Flussi prodotti': 'Flows produced',
  'Fornitore': 'Vendor',
  'Frequenza': 'Frequency',
  'Frequenza (cron)': 'Frequency (cron)',
  'Funzione': 'Function',
  'Fuori banda': 'Out of band',
  'Fuso orario': 'Time zone',
  'Garanzia': 'Warranty',
  'Garanzia fino al': 'Warranty until',
  'Genera un token di arruolamento e avvia un collector nella rete da inventariare.':
    'Generate an enrolment token and start a collector in the network to inventory.',
  'Gestione riservata agli amministratori': 'Managed by administrators only',
  'Già scaduti': 'Already expired',
  'giallo': 'amber',
  'Giorni di preavviso': 'Days of notice',
  'Gli apparati inviano i flussi alla sonda: nessuno li ha ancora inviati.':
    'Devices send flows to the probe: nobody has sent any yet.',
  'Google Workspace:': 'Google Workspace:',
  'Gruppi': 'Groups',
  'Gruppi (provati in ordine)': 'Groups (tried in order)',
  'Gruppi configurati': 'Groups configured',
  'Gruppi del provider → ruolo (facoltativo)': 'Provider groups → role (optional)',
  'Gruppo': 'Group',
  'host': 'host',
  'Host non gestiti': 'Unmanaged hosts',
  'I campi contrassegnati arrivano dalla discovery: correggili solo se sono sbagliati. Proprietario e centro di costo li sa solo una persona.':
    'The marked fields come from discovery: correct them only if they are wrong. Owner and cost centre are known only to a person.',
  'I nomi dei campi non si somigliano abbastanza. Non è un guasto: significa che il mapping va costruito a mano.':
    'The field names are not similar enough. This is not a fault: it means the mapping has to be built by hand.',
  'I più voluminosi. Massimo 50.': 'The largest ones. At most 50.',
  'I tipi di CI si nascondono e si rimettono cliccandoli. Un clic su un nodo mette a fuoco il suo intorno; un altro clic lo rilascia.':
    'CI types are hidden and restored by clicking them. One click on a node focuses its surroundings; another click releases it.',
  'I volumi sono riportati in scala: sono stime.':
    'Volumes are shown to scale: they are estimates.',
  'ID interno': 'Internal ID',
  'Identificativo': 'Identifier',
  'Identificativo (facoltativo)': 'Identifier (optional)',
  'Il certificato è scaduto.': 'The certificate has expired.',
  'Il certificato non è ancora valido.': 'The certificate is not valid yet.',
  'Il Control Plane (cloud)': 'The Control Plane (cloud)',
  'Il database non risponde.': 'The database is not responding.',
  'Il dizionario indirizzo → CI non è caricato': 'The address → CI dictionary is not loaded',
  'Il job è terminato con errore:': 'The job ended with an error:',
  'il limite che si raggiunge per primo aggiungendo repliche':
    'the limit reached first when adding replicas',
  "Il piano non è stato caricato: non è stato modificato niente. Ricarica la pagina; se l'errore resta, il piano potrebbe essere stato eliminato.":
    'The plan was not loaded: nothing was changed. Reload the page; if the error persists, the plan may have been deleted.',
  'Il rinnovo automatico non sta riuscendo.': 'Automatic renewal is not succeeding.',
  'Filtra il traffico non rilevante per discovery, identificazione degli entry point e correlazione degli asset. I dati esclusi non vengono trasmessi né archiviati, riducendo traffico di rete e spazio occupato.':
    'Traffic is the data that grows without end, and most of it serves none of the three purposes it is collected for — finding unknown assets, recognising entry points, correlating assets with each other. Whatever is discarded here never crosses the network and takes no space.',
  'illimitate': 'unlimited',
  'illimitati': 'unlimited',
  'Immagina un host con questi fatti e guarda quali passi partirebbero. Non tocca la rete: usa la stessa valutazione che userà la sonda.':
    'Imagine a host with these facts and see which steps would run. It does not touch the network: it uses the same evaluation the probe will use.',
  'Impronta SHA-256': 'SHA-256 fingerprint',
  'In aggiornamento': 'Updating',
  'In esecuzione da': 'Running for',
  'In scadenza (90 g)': 'Expiring (90 d)',
  'Incolla un elenco': 'Paste a list',
  'Indietro': 'Back',
  'Indirizzi': 'Addresses',
  'Indirizzi da escludere': 'Addresses to exclude',
  'Indirizzo per gli avvisi di scadenza': 'Address for expiry notices',
  'Installa la sonda': 'Install the probe',
  'Integrazione': 'Integration',
  'Interfaccia': 'Interface',
  'Interlocutore': 'Peer',
  'Interrogazioni parallele': 'Parallel queries',
  'Intestatario': 'Account holder',
  'Invariati': 'Unchanged',
  'inventario + traffico': 'inventory + traffic',
  'Inventario e traffico utilizzano volumi distinti: lo spazio totale occupato è la somma di entrambi.': 'Inventory and traffic use separate volumes: the total space used is the sum of both.',
  'Issuer': 'Issuer',
  'ITSM di produzione': 'Production ITSM',
  'Pianifica l’esecuzione ricorrente dei job di discovery, definendo frequenza e periodicità, per mantenere automaticamente aggiornati i Configuration Item.': 'Schedule the recurring execution of discovery jobs, defining frequency and periodicity, to keep the Configuration Items updated automatically.',
  'Job eseguiti': 'Jobs run',
  'Job falliti': 'Failed jobs',
  'Job recenti': 'Recent jobs',
  'Keycloak:': 'Keycloak:',
  "L'interfaccia collegata alla porta mirror (SPAN) dello switch. Vede solo il traffico che arriva lì: senza un mirror configurato, vedrà quasi soltanto il traffico della sonda stessa.":
    'The interface connected to the switch’s mirror (SPAN) port. It sees only the traffic that arrives there: without a mirror configured, it will see almost nothing but the probe’s own traffic.',
  "L'inventario copre l'intero account o piattaforma: l'endpoint e l'ambito provengono dalla credenziale.":
    'The inventory covers the whole account or platform: the endpoint and the scope come from the credential.',
  "L'orario è interpretato in questo fuso: «alle 02:00» sono le 02:00 qui, anche quando cambia l'ora legale.":
    'The time is read in this zone: “at 02:00” means 02:00 here, daylight saving included.',
  "L'unica cosa che non sta nel certificato, e quindi l'unica che ha senso scrivere a mano.":
    'The only thing not in the certificate, and therefore the only one worth typing by hand.',
  'L’utenza continua ad accedere con le proprie credenziali e può ora selezionare anche questo tenant.':
    'The user keeps signing in with their own credentials: from now on they can pick this tenant too.',
  'La condizione non era soddisfatta su questi indirizzi.':
    'The condition was not met on these addresses.',
  "La finestra limita l'orario in cui le scansioni possono partire: utile per evitare gli orari di lavoro. Lasciala vuota per non porre limiti.":
    'The window limits when scans may start: useful to stay clear of working hours. Leave it empty for no limit.',
  'La mia password': 'My password',
  'La piattaforma': 'The platform',
  'La scansione chiede al sistema quali tipi di oggetto esistono davvero su questa istanza.':
    'The scan asks the system which object types actually exist on this instance.',
  'La sequenza di passi che la sonda esegue su ogni indirizzo dell’ambito. Ogni passo può dipendere da ciò che i precedenti hanno scoperto.':
    'The sequence of steps the probe runs on every address in the scope. Each step may depend on what the previous ones discovered.',
  'La sonda non riesce a leggere la configurazione dei flussi.':
    'The probe cannot read the flow configuration.',
  'Lantronix SLC 8000, 16 porte': 'Lantronix SLC 8000, 16 ports',
  'Le adiacenze si popolano con una discovery che raccoglie CDP/LLDP, con i connettori di virtualizzazione o con NetFlow.':
    'Adjacencies are populated by a discovery that collects CDP/LLDP, by the virtualisation connectors, or by NetFlow.',
  'Le chiamate partono dal cloud.': 'The calls originate from the cloud.',
  'Le classi predefinite della piattaforma non possono essere ridefinite. Classi con la stessa chiave e definizioni diverse comprometterebbero la coerenza di classificazione e filtraggio.':
    'Platform classes cannot be redefined: two classes with the same key and two different labels would make the filters inconsistent.',
  'Le condizioni degli altri passi lo citano con questo nome.':
    'The other steps’ conditions refer to it by this name.',
  'Le discovery vanno avviate a mano finché non ne crei una.':
    'Discoveries have to be started by hand until you create one.',
  'Le interfacce si popolano con una discovery approfondita (deep).':
    'Interfaces are populated by an in-depth (deep) discovery.',
  'Le licenze si assegnano ai clienti: creane uno nella sezione Tenant.':
    'Licences are assigned to customers: create one in the Tenants section.',
  'Le porte alte con un solo mittente sono quasi sempre la direzione di ritorno di una connessione in uscita, non un servizio. Sono nascoste perché in un elenco di servizi esposti sarebbero la maggioranza delle righe.':
    'High ports with a single sender are almost always the return direction of an outbound connection, not a service. They are hidden because in a list of exposed services they would be most of the rows.',
  'Le porte arrivano dalla IF-MIB: uno sweep SNMP non le legge, serve una discovery approfondita.':
    'Ports come from the IF-MIB: an SNMP sweep does not read them, an in-depth discovery is needed.',
  'Le relazioni compaiono con una discovery che raccoglie CDP/LLDP, con i connettori di virtualizzazione o con NetFlow.':
    'Relations appear with a discovery that collects CDP/LLDP, with the virtualisation connectors, or with NetFlow.',
  "Le sonde marcate «non disponibile» non hanno il ricevitore acceso: si accende dalla scheda della sonda, sezione «Consegne degli agenti d'inventario». È spento di default perché è l'unica porta che una sonda apre in ingresso.":
    'Probes marked “unavailable” do not have the receiver switched on: turn it on from the probe’s page, section “Inventory agent deliveries”. It is off by default because it is the only inbound port a probe opens.',
  'Le sonde precedenti alla raccolta della telemetria non le inviano.':
    'Probes older than telemetry collection do not send it.',
  'Legge gli indirizzi fisici imparati dalle porte dello switch e li correla con i CI già in inventario: vede anche ciò che non parla CDP/LLDP.':
    'Reads the physical addresses learned on the switch ports and correlates them with CIs already in inventory: it sees things that do not speak CDP/LLDP too.',
  'Licenza': 'Licence',
  'Licenza scaduta.': 'Licence expired.',
  'Licenze': 'Licences',
  'Limiti del piano': 'Plan limits',
  'Listino': 'Price list',
  'Livelli': 'Levels',
  'Livello': 'Level',
  'Livello di servizio': 'Service level',
  "Lo pretende Let's Encrypt.": 'Let’s Encrypt requires it.',
  'Lo spazio è una stima.': 'The space figure is an estimate.',
  "Lo stesso parco, a colpo d'occhio: un punto per CI, i rossi verso il centro. Nessuna scena da guardare — si legge e si chiude.":
    'The same estate at a glance: one dot per CI, the red ones towards the centre. Nothing to watch — you read it and close it.',
  'Lo store dei flussi non risponde.': 'The flow store is not responding.',
  'Lo store del traffico non risponde.': 'The traffic store is not responding.',
  'Macchina': 'Machine',
  'mai': 'never',
  'mai eseguita': 'never run',
  'mai provato': 'never tried',
  'Mai provato': 'Never tried',
  'Manca': 'Missing',
  'Manca dal registro degli enti: importalo': 'Missing from the enterprise registry: import it',
  'mantenuto dal Control Plane': 'maintained by the Control Plane',
  'mantenuto dal database (materialized view)':
    'maintained by the database (materialized view)',
  'Mappa delle relazioni fra configuration item':
    'Map of the relations between configuration items',
  'Mappa di correlazione': 'Correlation map',
  'Mappatura': 'Mapping',
  'mappatura di partenza pronta': 'starting mapping ready',
  'Mappature': 'Mappings',
  'Media': 'Medium',
  'Memoria': 'Memory',
  'Memoria in uso dal processo': 'Memory used by the process',
  'Menu di navigazione': 'Navigation menu',
  'Metodo di autenticazione': 'Authentication method',
  'Mette la sonda in ascolto sulla porta UDP indicata qui sotto. Si può chiudere solo se la cattura diretta è attiva: senza nessuna delle due, la sonda sembrerebbe raccogliere senza raccogliere nulla.':
    'Puts the probe in listening mode on the UDP port set below. It can only be switched off if direct capture is on: with neither, the probe would look like it is collecting while collecting nothing.',
  'Cataloga i modelli utilizzati nel parco asset. Le informazioni associate, come codice di ordinazione e costo, vengono applicate automaticamente quando un CI diventa un asset.':
    'Catalogue the models that recur in your estate: when a CI becomes an asset, order code and cost are already there instead of being typed for the fortieth identical device.',
  'Microsoft Entra ID': 'Microsoft Entra ID',
  'Microsoft Entra ID:': 'Microsoft Entra ID:',
  'minuto ora giorno mese giorno-settimana': 'minute hour day month day-of-week',
  'Modalità': 'Mode',
  'Modello': 'Model',
  'Modifiche salvate.': 'Changes saved.',
  'Modo': 'Mode',
  'Molto più rapida su ambiti ampi: interroga solo gli host che rispondono.':
    'Much faster on wide scopes: it only queries hosts that answer.',
  'Motivo': 'Reason',
  'name, open_tcp_ports, os_family…': 'name, open_tcp_ports, os_family…',
  'Navigazione principale': 'Main navigation',
  'NBD, 4h lavorative…': 'NBD, 4 business hours…',
  'Negli ultimi 7 giorni': 'In the last 7 days',
  'Nei MIB': 'In the MIBs',
  'Nessun accostamento proposto': 'No match proposed',
  'Nessun agente': 'No agent',
  'Nessun apparato di rete': 'No network device',
  'Nessun bene trovato': 'No asset found',
  "Nessun calcolo lo rimette attivo e nessuna scansione lo annulla. Se l'apparato risponde ancora, il motivo qui sopra lo dice.":
    'No computation puts it back to active and no scan undoes it. If the device still answers, the reason above says so.',
  'Nessun collegamento': 'No connection',
  'Nessun collegamento noto': 'No known connection',
  'Nessun configuration item trovato': 'No configuration item found',
  'Nessun connettore': 'No connector',
  'Nessun contratto': 'No contract',
  'Nessun dato in questa finestra.': 'No data in this window.',
  'Nessun evento': 'No event',
  'Nessun evento da segnalare.': 'No event to report.',
  'Nessun exporter': 'No exporter',
  'Nessun gruppo': 'No group',
  "Nessun ingresso corrisponde a un CI noto: non c'è un punto da cui misurare.":
    'No entry point matches a known CI: there is no place to measure from.',
  'Nessun job': 'No job',
  'Nessun job eseguito': 'No job run',
  'Nessun job eseguito.': 'No job run.',
  'nessun limite di CPU sul container': 'no CPU limit on the container',
  'Nessun oggetto con questo nome o indirizzo partecipa a una relazione. I CI senza collegamenti non compaiono in mappa: cercali in Inventario.':
    'No object with this name or address takes part in a relation. CIs without connections do not appear on the map: look for them in Inventory.',
  'Nessun piano': 'No plan',
  'Nessun pool': 'No pool',
  'Nessun provider collegato': 'No provider connected',
  'Nessun servizio definito': 'No service defined',
  'Nessun servizio osservato': 'No service observed',
  'Nessun tenant': 'No tenant',
  'Nessun traffico osservato': 'No traffic observed',
  'Nessun traffico osservato in questa finestra.': 'No traffic observed in this window.',
  'Nessun utente': 'No user',
  'nessuna': 'none',
  'Nessuna': 'None',
  "Nessuna azione: non c'era niente da assegnare né da recuperare. È l'esito normale su un sistema in pari, non un fallimento.":
    'No action: there was nothing to assign and nothing to recover. This is the normal outcome on a system that is up to date, not a failure.',
  'Nessuna classe in questa categoria': 'No class in this category',
  'Nessuna configurazione salvata. Si comincia con un job di tipo «Backup configurazioni» sugli apparati di rete.':
    'No configuration saved. You start with a “Configuration backup” job on the network devices.',
  'Nessuna conversazione in questa finestra': 'No conversation in this window',
  "Nessuna corrispondenza: controlla il nome dell'attributo. Lo trovi in fondo alla scheda di un CI, sotto «Altri attributi».":
    'No match: check the attribute name. You will find it at the bottom of a CI’s page, under “Other attributes”.',
  'Nessuna credenziale': 'No credential',
  'Nessuna credenziale configurata': 'No credential configured',
  'Nessuna esecuzione': 'No run',
  'Nessuna interfaccia': 'No interface',
  'Nessuna interfaccia riportata': 'No interface reported',
  'nessuna licenza': 'no licence',
  'Nessuna licenza registrata.': 'No licence recorded.',
  'Nessuna mappatura': 'No mapping',
  'Nessuna modifica registrata': 'No change recorded',
  'Nessuna pianificazione': 'No schedule',
  'Nessuna porta o protocollo su questo collegamento. Le adiacenze di rete (CDP, LLDP) e le relazioni di virtualizzazione non ne hanno: non è un dato mancante, è un tipo di collegamento che non ne prevede.':
    'No port or protocol on this connection. Network adjacencies (CDP, LLDP) and virtualisation relations have none: this is not missing data, it is a kind of connection that has none.',
  'Nessuna porta raccolta': 'No port collected',
  'Nessuna regola': 'No rule',
  'Nessuna relazione della famiglia scelta collega due tipi di CI visibili.':
    'No relation of the chosen family connects two visible CI types.',
  'Nessuna relazione rilevata': 'No relation detected',
  'Nessuna riga': 'No row',
  'nessuna sonda': 'no probe',
  'Nessuna sonda arruolata': 'No probe enrolled',
  'Nessuna sonda arruolata.': 'No probe enrolled.',
  'Nessuno': 'None',
  'nessuno: la mappa sarà vuota': 'none: the map will be empty',
  'Niente da mostrare con questi filtri': 'Nothing to show with these filters',
  'Nome': 'Name',
  'Nome Cognome': 'First name Last name',
  'Nome del cliente': 'Customer name',
  'Nome del gruppo': 'Group name',
  'Nome della mappatura': 'Mapping name',
  'Nome della pianificazione': 'Schedule name',
  'Nome della vista…': 'View name…',
  'Nome della vista da salvare': 'Name of the view to save',
  'Nome o chiave naturale': 'Name or natural key',
  'Nomi coperti': 'Names covered',
  'Nomi da coprire': 'Names to cover',
  'Non ancora scandito': 'Not scanned yet',
  'Non comprese:': 'Not included:',
  'non dichiarata': 'not declared',
  'non disponibile': 'unavailable',
  "non esporta da oltre un'ora": 'has not exported for over an hour',
  'non hanno ancora dichiarato una versione': 'have not declared a version yet',
  'Non inventariato': 'Not inventoried',
  'non lette': 'not read',
  'non misurato': 'not measured',
  'non pianificata': 'not scheduled',
  'non raccolto': 'not collected',
  'non riconosciuto': 'not recognised',
  'Non riportata: questa sonda è precedente alla raccolta dei dati sulla macchina. Aggiornala per vedere host e interfacce.':
    'Not reported: this probe predates host data collection. Update it to see host and interfaces.',
  'Non risponde.': 'Not responding.',
  'Non riuscita.': 'Unsuccessful.',
  "Non viene salvata nel database e non è più leggibile da nessuna pagina: finisce solo nel file che il bilanciatore usa. Se è protetta da passphrase, toglila prima — il bilanciatore non può chiederla a nessuno all'avvio.":
    'It is not stored in the database and cannot be read back from any page: it only goes into the file the load balancer uses. If it is passphrase-protected, remove the passphrase first — the load balancer has nobody to ask at start-up.',
  'Nota': 'Note',
  'Note': 'Notes',
  'Nuova categoria': 'New category',
  'Nuova classe': 'New class',
  'Nuova credenziale': 'New credential',
  'nuova etichetta': 'new label',
  'Nuova password': 'New password',
  'Nuova sonda': 'New probe',
  'Nuovo agente': 'New agent',
  'Nuovo connettore': 'New connector',
  'Nuovo contratto': 'New contract',
  'Nuovo gruppo': 'New group',
  'Nuovo gruppo di credenziali': 'New credential group',
  'Nuovo pool': 'New pool',
  'Nuovo tenant': 'New tenant',
  'Nuovo utente': 'New user',
  'Occorrenze': 'Occurrences',
  'Oggetti trovati': 'Objects found',
  'Oggetto': 'Object',
  '«Ogni notte alle 2:00» significa le 2:00 qui, non le 2:00 UTC.':
    '“Every night at 2:00” means 2:00 here, not 2:00 UTC.',
  'Ogni quanti minuti': 'How many minutes apart',
  'Ogni quanto consegna (minuti)': 'How often it delivers (minutes)',
  'OID costruttore': 'Manufacturer OID',
  'Okta:': 'Okta:',
  "Oltre il terzo è quasi sempre l'intera rete.":
    'Beyond the third it is almost always the whole network.',
  'oob': 'oob',
  'Operatore': 'Operator',
  'Opererà su tutte le capability che dichiara.':
    'It will work on every capability it declares.',
  'oppure indirizzo': 'or address',
  'Orchestratore': 'Orchestrator',
  'Ordine': 'Order',
  'Origine': 'Source',
  'Pacchetti minimi': 'Minimum packets',
  'Pacchetti osservati': 'Packets observed',
  'Parametri': 'Parameters',
  'Parte?': 'Does it run?',
  'Partizioni del dettaglio': 'Detail partitions',
  'parziale': 'partial',
  'Passi': 'Steps',
  'Passo': 'Step',
  'Passo di 1 ora. Predefinito: 400.': 'One-hour step. Default: 400.',
  'Passo di 5 minuti. Predefinito: 7.': 'Five-minute step. Default: 7.',
  'Password': 'Password',
  'Password aggiornata.': 'Password updated.',
  'Password attuale': 'Current password',
  'Password iniziale': 'Initial password',
  'Payload': 'Payload',
  'Per gli asset non raggiungibili da alcuna sonda, l’inventario viene acquisito localmente e inviato in modo sicuro':
    'For machines no probe can reach: they collect their own inventory and deliver it sealed',
  'Per riferimento: una subnet aggiunta al pool entra nella scansione successiva.':
    'For reference: a subnet added to the pool enters the next scan.',
  'Perché': 'Why',
  'Perché è stato dismesso': 'Why it was decommissioned',
  'Percorso': 'Path',
  'Periodicità': 'Recurrence',
  'Piani a listino': 'Catalogue plans',
  'Piani di discovery di rete': 'Network discovery plans',
  'pianificata': 'scheduled',
  'Pianificazioni attive': 'Active schedules',
  'Piano': 'Plan',
  'Piano da eseguire': 'Plan to run',
  'Politica di assegnazione': 'Assignment policy',
  'Porta': 'Port',
  'Porta…': 'Port…',
  'Porta di ascolto (UDP)': 'Listening port (UDP)',
  'In ascolto sulla porta {port}.': 'Listening on port {port}.',
  '{n} pacchetti ricevuti.': '{n} packets received.',
  '{n} flussi decodificati.': '{n} flows decoded.',
  'Arrivano pacchetti ma nessun flusso viene decodificato: probabile un template non ancora ricevuto o un formato che la sonda non interpreta.':
    'Packets are arriving but no flow is being decoded: likely a template not yet received or a format the probe cannot interpret.',
  'Nessun flusso ricevuto ancora: verifica che un apparato esporti su questa porta.':
    'No flows received yet: check that a device is exporting to this port.',
  'Ricezione dei flussi esportati (collector)': 'Receiving exported flows (collector)',
  'Ricevi i flussi esportati dagli apparati': 'Receive flows exported by devices',
  'Soglie (valgono per entrambe le sorgenti)': 'Thresholds (apply to both sources)',
  'Non in ascolto: {err}': 'Not listening: {err}',
  'Non ancora in ascolto: la sonda applica la scelta al prossimo giro di configurazione, entro un minuto.':
    'Not listening yet: the probe applies the choice on the next configuration cycle, within a minute.',
  'Porta dichiarata': 'Declared port',
  'Porta locale': 'Local port',
  'Porta remota': 'Remote port',
  'Porte': 'Ports',
  'Porte e protocolli': 'Ports and protocols',
  'Porte, timeout, cartelle in cui cercare, comandi della scansione personalizzata. Vuoto significa «usa i valori predefiniti».':
    'Ports, timeouts, folders to search in, commands for the custom scan. Empty means “use the defaults”.',
  'PostgreSQL': 'PostgreSQL',
  'predefinito': 'default',
  'Prima serve un collegamento riuscito.': 'A successful connection is needed first.',
  'Prima serve una scansione: il mapping si costruisce sui campi che il sistema di destinazione ha davvero, non su un elenco teorico.':
    'A scan is needed first: the mapping is built on the fields the target system actually has, not on a theoretical list.',
  'Primo passo, sul tuo provider.': 'First step, on your provider.',
  'Primo rilevamento': 'First seen',
  'Priorità': 'Priority',
  'Processore': 'Processor',
  'Prodotto': 'Product',
  'Prodotto a catalogo': 'Catalogued product',
  'Produttore': 'Manufacturer',
  'Profondità': 'Depth',
  'Profondità della mappa': 'Map depth',
  'pronto': 'ready',
  'Proprietario': 'Owner',
  'Prossima': 'Next',
  'Protocollo': 'Protocol',
  'Prova a secco': 'Dry run',
  'prova a vuoto': 'dry run',
  'Prova a vuoto: sul sistema di destinazione non è stato scritto niente. I payload qui sotto sono quelli che sarebbero stati inviati.':
    'Dry run: nothing was written to the target system. The payloads below are the ones that would have been sent.',
  'Provala dopo': 'Try it later',
  'Provala prima': 'Try it first',
  'Prove a vuoto comprese.': 'Dry runs included.',
  'Pulizia dello store del traffico': 'Traffic store cleanup',
  'Punti di ingresso': 'Entry points',
  'Quale insieme di sonde può eseguire una discovery, e con quale criterio':
    'Which set of probes may run a discovery, and by what rule',
  'Qualsiasi': 'Any',
  'Qualunque sonda idonea': 'Any eligible probe',
  'Quando una conversazione è interna': 'When a conversation counts as internal',
  'Quanto raccogliere': 'How much to collect',
  'Spazio occupato dai dati del tenant e capacità residua rispetto ai limiti del piano.': 'Space used by the tenant’s data and the remaining capacity against the plan limits.',
  'Quello che la piattaforma ha sotto di sé, e ogni pesce è un CI. In verde ciò che ha riconosciuto e catalogato; in rosso, lampeggiante, ciò che non ha ancora riconosciuto — o che è comparso adesso.':
    'What the platform has beneath it, where every fish is a CI. In green what it has recognised and catalogued; in red, blinking, what it has not recognised yet — or what has just appeared.',
  'Questa console non è aggiornata.': 'This console is out of date.',
  'Questa sonda non ha ancora ricevuto lavoro: verifica che sia online e che abbia una capability abilitata.':
    'This probe has not received any work yet: check that it is online and has a capability enabled.',
  'Questa sonda non riceve job:': 'This probe receives no jobs:',
  'Questa sonda non sa eseguire il passo.': 'This probe cannot run the step.',
  'Questi vicini sono stati raccolti, ma la porta che dichiarano non corrisponde a nessuna interfaccia letta: succede quando CDP o LLDP usano una convenzione di nomi che non si riconosce, o quando la IF-MIB non è stata raccolta.':
    'These neighbours were collected, but the port they declare matches no interface that was read: this happens when CDP or LLDP use a naming convention that is not recognised, or when the IF-MIB was not collected.',
  "Questo backend non riporta lo spazio libero del proprio volume. Con ClickHouse il dato c'è, perché ClickHouse lo espone.":
    'This backend does not report the free space on its own volume. With ClickHouse the figure is there, because ClickHouse exposes it.',
  'Questo Control Plane non conosce la propria versione.':
    'This Control Plane does not know its own version.',
  'Questo tenant': 'This tenant',
  'Quota CI': 'CI quota',
  'Raccoglie i vicinati CDP e LLDP per la mappa delle adiacenze. Richiede la discovery approfondita.':
    'Collects CDP and LLDP neighbourhoods for the adjacency map. Requires the in-depth discovery.',
  'Raccoglie interfacce, seriali e numeri di modello oltre ai dati di base.':
    'Collects interfaces, serial numbers and model numbers on top of the basic data.',
  'Raccolto': 'Collected',
  'Radar': 'Radar',
  'Raddoppia a ogni tentativo successivo.': 'It doubles with every further attempt.',
  'Raggiungibile': 'Reachable',
  'Rapido': 'Quick',
  'Rapido — identità, sistema, rete': 'Quick — identity, system, network',
  'Rapido + software': 'Quick + software',
  'Rapido + software installato': 'Quick + installed software',
  'Record': 'Records',
  'Recupera quelle saltate': 'Catch up on the missed ones',
  'Registra anche i dispositivi senza SNMP': 'Also register devices without SNMP',
  'Registra come bene': 'Register as an asset',
  'Registrato il': 'Recorded on',
  'Registro dei costruttori': 'Manufacturer registry',
  'Regole di riconoscimento': 'Recognition rules',
  'Stampanti Kyocera': 'Kyocera printers',
  'Stampanti Brother': 'Brother printers',
  'Stampanti Ricoh': 'Ricoh printers',
  'Stampanti Lexmark': 'Lexmark printers',
  "L'ente 1.3.6.1.4.1.244 è Lantronix, che fa console server. Senza questa regola risultano switch, perché commutano fra porte seriali e sysServices non distingue le due cose.":
    "Enterprise 1.3.6.1.4.1.244 is Lantronix, which makes console servers. Without this rule they come out as switches, because they switch between serial ports and sysServices doesn't tell the two apart.",
  "L'ente 1.3.6.1.4.1.1347 è Kyocera, che fa solo stampanti. Senza questa regola le multifunzione risultano router: dichiarano il bit di instradamento e la descrizione non nomina la stampa.":
    "Enterprise 1.3.6.1.4.1.1347 is Kyocera, which makes printers only. Without this rule the multifunction devices come out as routers: they declare the routing bit and the description does not name printing.",
  "L'ente 1.3.6.1.4.1.2435 è Brother, che fa solo stampanti. Senza questa regola le multifunzione risultano router: dichiarano il bit di instradamento e la descrizione non nomina la stampa.":
    "Enterprise 1.3.6.1.4.1.2435 is Brother, which makes printers only. Without this rule the multifunction devices come out as routers: they declare the routing bit and the description does not name printing.",
  "L'ente 1.3.6.1.4.1.367 è Ricoh, che fa solo stampanti. Senza questa regola le multifunzione risultano router: dichiarano il bit di instradamento e la descrizione non nomina la stampa.":
    "Enterprise 1.3.6.1.4.1.367 is Ricoh, which makes printers only. Without this rule the multifunction devices come out as routers: they declare the routing bit and the description does not name printing.",
  "L'ente 1.3.6.1.4.1.641 è Lexmark, che fa solo stampanti. Senza questa regola le multifunzione risultano router: dichiarano il bit di instradamento e la descrizione non nomina la stampa.":
    "Enterprise 1.3.6.1.4.1.641 is Lexmark, which makes printers only. Without this rule the multifunction devices come out as routers: they declare the routing bit and the description does not name printing.",
  "L'ente 1.3.6.1.4.1.7745 è Aethra Telecommunications, che fa router e gateway d'accesso. Senza questa regola un SV6044 resta non classificato e va assegnato a mano, perché la sua descrizione nomina «Aethra», non «router».":
    "Enterprise 1.3.6.1.4.1.7745 is Aethra Telecommunications, which makes routers and access gateways. Without this rule an SV6044 stays unclassified and must be assigned by hand, because its description names «Aethra», not «router».",
  'Report disponibili': 'Available reports',
  'Responsabile': 'Responsible',
  'Reti interne': 'Internal networks',
  '«Riapplica» serve dopo un rinnovo fatto fuori da qui — certbot da cron, per esempio — che aggiorna il file senza avvisare il bilanciatore: altrimenti continuerebbe a servire il precedente fino al riavvio, e il browser mostrerebbe ancora quello scaduto.':
    '“Reapply” is for a renewal done outside this page — certbot from cron, say — that updates the file without telling the load balancer: otherwise it would keep serving the previous one until restart, and the browser would still show the expired certificate.',
  'Ricerca': 'Search',
  'Ricevono solo le discovery che non hanno scelto un gruppo. Si assegnano a un gruppo dalla scheda della singola sonda.':
    'They only receive discoveries that did not pick a group. They are assigned to a group from the individual probe’s page.',
  'Ricevuti': 'Received',
  'Uso CPU': 'CPU usage',
  'Uso memoria': 'Memory usage',
  'Uso disco': 'Disk usage',
  'Riferimento': 'Reference',
  'Righe': 'Rows',
  'Righe non valide:': 'Invalid rows:',
  'Rileva i collegamenti tra apparati (CDP/LLDP)':
    'Detect the links between devices (CDP/LLDP)',
  'Rilevato tramite': 'Detected via',
  'Rimuovi il traffico più vecchio di (giorni)': 'Remove traffic older than (days)',
  'Ripartizione per servizio': 'Breakdown by service',
  'Riserva': 'Standby',
  'Risoluzione DNS inversa': 'Reverse DNS resolution',
  'Risorse': 'Resources',
  'Ritardo casuale (s)': 'Random delay (s)',
  'Ritentativi': 'Retries',
  'Riusciti': 'Succeeded',
  'Rivedi i piani': 'Review the plans',
  'Rollup (giorni)': 'Rollup (days)',
  'Rollup orario': 'Hourly rollup',
  'Ruolo': 'Role',
  'Ruolo nel gruppo': 'Role in the group',
  'Ruolo su questo tenant': 'Role on this tenant',
  'Salta e riparti dalla prossima': 'Skip and resume from the next one',
  'Salta questo turno': 'Skip this turn',
  'Saltati': 'Skipped',
  'Salva in CMDB gli host raggiungibili ma non inventariabili. Richiede la verifica di raggiungibilità.':
    'Saves to the CMDB the hosts that are reachable but cannot be inventoried. Requires the reachability check.',
  'Scadenza': 'Expiry',
  'Scansione': 'Scan',
  'Scansione notturna sede': 'Nightly office scan',
  'Esclude sempre il traffico indicato, in entrambe le direzioni, anche tra asset interni.':
    'Always discarded, in both directions, even when internal — it is the only way to exclude the nightly backup, which is internal by definition.',
  'Scegli…': 'Choose…',
  'scegli o scrivi': 'choose or type',
  'scegli o scrivi il nome': 'choose or type the name',
  "Scegliere la sonda è scegliere la rete da cui guardare: da un'altra rete la stessa scansione non fallisce, restituisce un inventario vuoto.":
    'Choosing the probe is choosing the network to look from: from another network the same scan does not fail, it returns an empty inventory.',
  'Scelta da una persona: le regole non la toccano':
    'Chosen by a person: the rules do not touch it',
  'Scheda del bene': 'Asset page',
  'scheda di un CI': 'a CI’s page',
  'Sconosciute': 'Unknown',
  'Sconsigliata.': 'Not recommended.',
  'scoperto': 'discovered',
  'Scrivi anche il software installato': 'Also write the installed software',
  'Se la precedente è ancora in corso': 'If the previous one is still running',
  'Se lo lasci vuoto la sonda userà il proprio hostname.':
    'Leave it empty and the probe will use its own hostname.',
  "Se quella sonda non risponde, l'agente consegna al Control Plane e lo dichiara nel proprio log: l'inventario non si ferma, e la sonda da riparare resta visibile.":
    'If that probe does not answer, the agent delivers to the Control Plane and says so in its own log: the inventory does not stop, and the probe that needs fixing stays visible.',
  'Sede di Bergamo': 'Bergamo office',
  'Seleziona…': 'Select…',
  "Seleziona una riga per vedere le porte dell'apparato.":
    'Select a row to see the device’s ports.',
  'Sembra un dettaglio da rubrica, ed è la prima cosa che si cerca alle tre di notte.':
    'It looks like an address-book detail, and it is the first thing you look for at three in the morning.',
  'sempre': 'always',
  'Senza contratto': 'No contract',
  'Senza limiti la sonda esegue tutto ciò che il suo binario sa fare. Limitarle serve a dedicarne una a una funzione sola — per esempio una sonda al solo NetFlow.':
    'With no limits the probe runs everything its binary can do. Limiting them is how you dedicate one to a single function — a NetFlow-only probe, for instance.',
  'Senza questo dato la data mostrata coincide con la scadenza, e si crede di avere tempo fino a quel giorno.':
    'Without this figure the date shown is the expiry date itself, and you believe you have until that day.',
  'senza scadenza': 'no expiry',
  "Senza, un valore di 12.000 non dice se è il canone dell'anno o del mese.":
    'Without it, a value of 12,000 does not say whether it is the yearly or the monthly fee.',
  'Senza, una sola riga sbagliata annulla tutto: un pool a metà sembra completo.':
    'Without it, a single bad row cancels everything: a half-filled pool looks complete.',
  'separati da virgola': 'comma separated',
  'Serial Number': 'Serial Number',
  'Serie temporali del traffico': 'Traffic time series',
  'Serve il MIB di prodotto di questo costruttore': 'This manufacturer’s product MIB is needed',
  'Serve per le macchine che la sonda non riesce a interrogare: un server in cloud, un host dietro un firewall.':
    'It is for machines the probe cannot query: a cloud server, a host behind a firewall.',
  'Server ERP in cloud': 'Cloud ERP server',
  'Servizi definiti': 'Services defined',
  'Servizi osservati': 'Services observed',
  'Servizio': 'Service',
  'Servono quando le sonde sono più di una e non vedono la stessa rete: un gruppo dice «una qualunque di queste va bene». Con una sonda sola non serve a niente.':
    'They matter when there is more than one probe and they do not see the same network: a group says “any one of these will do”. With a single probe they are pointless.',
  'Sezioni': 'Sections',
  "Si applica alla cattura diretta: scegli prima un'interfaccia.":
    'It applies to direct capture: choose an interface first.',
  'Si eseguono nell’ordine, su ogni indirizzo separatamente.':
    'They run in order, on each address separately.',
  'Si popola con una discovery SNMP. Compaiono anche gli apparati non ancora classificati: sono quelli che più spesso si vogliono guardare.':
    'It fills up with an SNMP discovery. Devices not yet classified show up too: they are the ones you most often want to look at.',
  'Si registrano solo i cambiamenti di valore: un CI appena scoperto non ne ha, e una discovery che riscrive gli stessi dati non ne produce.':
    'Only value changes are recorded: a freshly discovered CI has none, and a discovery rewriting the same data produces none.',
  'Sistema': 'System',
  'Sistema operativo della sonda': 'Probe operating system',
  'SLC8016': 'SLC8016',
  'SNMP standard': 'Standard SNMP',
  'snmp-readonly-sede': 'snmp-readonly-office',
  'Solo ciò che è collegato direttamente': 'Only what is directly connected',
  'solo dove il prodotto lo distingue': 'only where the product tells them apart',
  'Sonar': 'Sonar',
  'Sonda': 'Probe',
  'sonde': 'probes',
  'Sonde comprese': 'Probes included',
  'Sonde registrate': 'Probes registered',
  'Sorgenti dei flussi': 'Flow sources',
  'Sospendi dopo N fallimenti': 'Suspend after N failures',
  'sospesa': 'suspended',
  'sostituito, restituito al noleggio, rottamato…':
    'replaced, returned to the lessor, scrapped…',
  'Spazio': 'Space',
  'Spazio occupato': 'Space used',
  'Spazio totale': 'Total space',
  'Spente da un amministratore': 'Switched off by an administrator',
  'Sposta la messa a fuoco su questo nodo': 'Move the focus to this node',
  'Stato': 'Status',
  'Stato nel tempo': 'State over time',
  'Store attivo': 'Active store',
  'Su Linux o macOS': 'On Linux or macOS',
  'Su Windows (PowerShell come amministratore)': 'On Windows (PowerShell as administrator)',
  'Tabella': 'Table',
  'tecnici@esempio.it': 'support@example.com',
  'Telemetria': 'Telemetry',
  'Tema della console': 'Console theme',
  'Tenant': 'Tenant',
  'Tenant su cui operare': 'Tenant to work on',
  'Tipo': 'Type',
  'Tipo dell’oggetto da creare': 'Type of the object to create',
  'Tipo di scansione': 'Scan type',
  'Togli la condizione': 'Remove the condition',
  'Togliere dalla CMDB': 'Remove from the CMDB',
  'Totale': 'Total',
  'Tracciabilità': 'Traceability',
  'Traffico da conservare': 'Traffic to keep',
  'Trasmessi': 'Sent',
  'Tutte': 'All',
  'tutte le condizioni': 'all the conditions',
  'Tutte le fasi': 'All phases',
  'Tutte le relazioni insieme': 'All relations together',
  'tutti': 'all',
  'Tutti': 'All',
  'tutti i connettori': 'all connectors',
  'Tutti i pacchetti': 'All packets',
  'Ultima': 'Last',
  'Ultima consegna': 'Last delivery',
  'Ultima esecuzione': 'Last run',
  'Ultime discovery': 'Latest discoveries',
  'Ultimi 30 giorni': 'Last 30 days',
  'Ultimi 7 giorni': 'Last 7 days',
  'Ultimi 90 giorni': 'Last 90 days',
  'Ultimo anno': 'Last year',
  'Ultimo esito': 'Last outcome',
  'Ultimo heartbeat': 'Last heartbeat',
  'Ultimo rilevamento': 'Last seen',
  'Un blocco senza condizioni corrisponderebbe a ogni apparato: aggiungine almeno una, o togli il blocco.':
    'A block with no conditions would match every device: add at least one, or remove the block.',
  'Un gruppo vuoto non servirebbe a niente: una discovery che lo usasse non avrebbe nulla da provare, e ogni apparato risulterebbe irraggiungibile — che si legge come un problema di rete.':
    'An empty group would be useless: a discovery using it would have nothing to try, and every device would come out unreachable — which reads as a network problem.',
  'Un indirizzo, non un nome: nei flussi i nomi non compaiono, e cercarli darebbe sempre un risultato vuoto — che si legge come «non parla con nessuno».':
    'An address, not a name: names do not appear in flows, and searching for one would always give an empty result — which reads as “talks to nobody”.',
  'Configura il piano di discovery definendo metodi di scansione, credenziali e sequenza di esecuzione. Il job associa il piano al Pool di asset e avvia la discovery sui target compresi nel perimetro configurato.':
    'Define how to run discovery on the perimeter configured in the asset Pool: choose methods, credentials and execution order; then associate the plan and pool with the job to start scanning the included targets.',
  'Un piano senza passi non raccoglierebbe niente. Aggiungine uno qui sotto.':
    'A plan with no steps would collect nothing. Add one below.',
  'Un pool raccoglie le subnet di una sede o di un ambiente: si scrive una volta e lo richiamano tutte le scansioni.':
    'A pool gathers the subnets of a site or an environment: you write it once and every scan reuses it.',
  'Una mappatura dice cosa sincronizzare, in quale direzione e ogni quanto.':
    'A mapping says what to synchronise, in which direction and how often.',
  'Una sonda del gruppo, scelta con la politica di assegnazione del gruppo.':
    'One probe from the group, picked by the group’s assignment policy.',
  'Uptime': 'Uptime',
  'Utenti abilitati su questo tenant': 'Users enabled on this tenant',
  'Configura le credenziali SNMP, SSH e WinRM utilizzate dalle sonde per interrogare gli asset. Associa a ogni rete o zona il relativo gruppo di credenziali e definisci l’ordine di verifica: la sonda le prova in sequenza fino alla prima autenticazione riuscita e il report indica quella utilizzata. Le credenziali delle piattaforme infrastrutturali, come vCenter, NexaVM e OceanStor, sono gestite in questa sezione; quelle dei sistemi interrogati in tempo reale nelle rispettive Connessioni, mentre ntopng è configurato in Integrazioni → Sorgenti dati.': 'Set up the SNMP, SSH and WinRM credentials the probes use to query the assets. Assign each network or zone its credential group and define the order of attempts: the probe tries them in sequence until the first successful authentication, and the report shows which one was used. Credentials for infrastructure platforms, such as vCenter, NexaVM and OceanStor, are managed in this section; those of systems queried in real time are in their respective Connections, while ntopng is configured in Integrations → Data sources.',
  'Vai al registro dei beni →': 'Go to the asset register →',
  'Vale per i report che guardano un periodo; gli altri la ignorano.':
    'It applies to reports that look at a period; the others ignore it.',
  'Validità del segreto (giorni)': 'Secret validity (days)',
  'valore': 'value',
  'Valore': 'Value',
  'Valore di acquisto': 'Purchase value',
  'Velocità': 'Speed',
  'Verifica raggiungibilità prima di interrogare': 'Check reachability before querying',
  'Verifica sessione…': 'Checking session…',
  'vero': 'true',
  'Versione': 'Version',
  'API': 'API',
  'Build': 'Build',
  'Gestore': 'Manager',
  '{env} · versioni': '{env} · versions',
  'Versione della sonda': 'Probe version',
  'Versione delle sonde': 'Probe versions',
  'Verso': 'To',
  'Verso quale oggetto': 'Towards which object',
  'Vicini per nodo': 'Neighbours per node',
  'Vista': 'View',
  'Viste salvate': 'Saved views',
  'Visto': 'Seen',
  'VLAN stampanti': 'Printer VLAN',
  'Volume': 'Volume',
  'volume del Control Plane': 'Control Plane volume',
  'Volume totale': 'Total volume',
  'Vuoto: la sonda si dimensiona sui propri core.':
    'Empty: the probe sizes itself on its own cores.',
  'Annulla': 'Cancel',
  'Annulla la scelta': 'Undo the choice',
  'assegnata a mano': 'assigned by hand',
  'Nome con cui è identificato il collegamento, utile per distinguere ambienti diversi, ad esempio produzione e collaudo.': 'Name that identifies the link, useful to distinguish different environments, for example production and staging.',
  'Completo — dischi, servizi, account, sicurezza':
    'Full — disks, services, accounts, security',
  'Configurato. Non torna indietro dal Control Plane: per sostituirlo premi «Cambia», altrimenti resta quello.':
    'Configured. It never comes back from the Control Plane: to replace it press “Change”, otherwise it stays as it is.',
  'Elimina il pool': 'Delete the pool',
  'Elimina registrazione': 'Delete registration',
  'È il campo con cui si riconosce «lo stesso oggetto». Sbagliarlo non dà un errore: dà un doppione a ogni esecuzione, e il conteggio resta plausibile.':
    'It is the field by which “the same object” is recognised. Getting it wrong raises no error: it produces a duplicate on every run, and the count stays plausible.',
  'Esci': 'Sign out',
  'Ho finito': 'Done',
  'Ho finito, chiudi': 'Done, close',
  'Firma del pacchetto (GPG)': 'Package signature (GPG)',
  'Gli RPM sono firmati: importa la chiave pubblica e verifica la firma prima di installare. Per un repository, metti la chiave e gpgcheck=1.':
    'The RPMs are signed: import the public key and verify the signature before installing. For a repository, add the key and set gpgcheck=1.',
  'Scarica la chiave pubblica': 'Download the public key',
  'Opzioni della riga di comando (censo-inventory)': 'Command-line options (censo-inventory)',
  'Gli stessi flag su Windows, Linux e macOS. Di norma non servono — il comando qui sopra li imposta già.':
    'The same flags on Windows, Linux and macOS. You usually don’t need them — the command above already sets them.',
  'Stampa la versione ed esce.': 'Prints the version and exits.',
  'File di configurazione da usare (predefinito: accanto all’eseguibile).':
    'Configuration file to use (default: next to the executable).',
  'Quanto raccogliere (vuoto = quello scelto qui).': 'How much to collect (empty = the choice made here).',
  'Resta in esecuzione: keep-alive ogni minuto, inventario all’intervallo scelto.':
    'Stays running: keep-alive every minute, inventory at the chosen interval.',
  'Non registra il sorvegliante (su Windows l’attività pianificata): lo avvia un supervisore esterno. Come la variabile CENSO_NO_WATCHDOG.':
    'Does not register the watchdog (on Windows the scheduled task): an external supervisor starts it. Same as the CENSO_NO_WATCHDOG variable.',
  'Raccoglie e mostra l’inventario senza sigillarlo né consegnarlo.':
    'Collects and shows the inventory without sealing or delivering it.',
  'Scrive la busta sigillata su file invece di consegnarla (per reti senza uscita).':
    'Writes the sealed envelope to a file instead of delivering it (for networks with no outbound access).',
  'Quanto attendere la consegna (predefinito 30 secondi).': 'How long to wait for delivery (default 30 seconds).',
  'Elenca a video ogni dato non letto.': 'Lists on screen every datum it could not read.',
  'L’agente si installa da solo (accetta -config, -force, -no-watchdog).':
    'The agent installs itself (accepts -config, -force, -no-watchdog).',
  'Disinstalla l’agente (lo usa anche l’MSI di Windows in rimozione).':
    'Uninstalls the agent (also used by the Windows MSI on removal).',
  'ignoto': 'unknown',
  'Il numero è quanti ne ha questo cliente adesso: è ciò che dice se il filtro sta per prendere l’insieme che hai in mente o un decimo.':
    'The number is how many this customer has right now: it is what tells you whether the filter is about to take the set you have in mind or a tenth of it.',
  'Il piano non è eseguibile: gli errori qui sopra vanno risolti. Gli avvisi no — un ordine costoso è una scelta.':
    'The plan cannot run: the errors above have to be fixed. The warnings do not — an expensive order is a choice.',
  'La credenziale del sistema di destinazione passerà da questa sonda: cifrata in transito, mai su disco, mai nei log.':
    'The target system’s credential will pass through this probe: encrypted in transit, never on disk, never in the logs.',
  'Le azioni di questa pagina valgono per tutti i clienti':
    'The actions on this page apply to every customer',
  'manca': 'missing',
  'Mostra il JSON completo — serve quando un attributo nuovo non ha ancora una resa dedicata':
    'Shows the full JSON — useful when a new attribute has no dedicated rendering yet',
  'Nessuna vista salvata.': 'No saved view.',
  'Non dichiarata da questa sonda: il suo binario non la implementa. Aggiornala per renderla disponibile.':
    'Not declared by this probe: its binary does not implement it. Update the probe to make it available.',
  'Andamento dello storage usato con proiezione': 'Storage usage trend with projection',
  'Crescita al giorno': 'Daily growth',
  'Crescita/giorno': 'Growth/day',
  'Giorni al pieno': 'Days to full',
  'La serie storica si sta riempiendo: un campione al giorno per datastore. Il grafico compare con i primi punti, la proiezione dopo una settimana.':
    'The history is filling up: one sample per datastore per day. The chart appears with the first points, the projection after a week.',
  'Pieno fra': 'Full in',
  'Proiezione': 'Projection',
  'pieno · {d}': 'full · {d}',
  '~{n} giorni': '~{n} days',
  'ospite': 'guest',
  'Prova': 'Test',
  'provata per prima': 'tried first',
  'Riapplica quello sul disco': 'Reapply the one on disk',
  'Rimuovi sonda': 'Remove probe',
  'Salva le condizioni': 'Save the conditions',
  'Salva nota': 'Save note',
  'sconsigliata': 'not recommended',
  'senza segreto': 'no secret',
  'Sono un suggerimento: chi avvia la scansione può sostituirli, ed è il motivo per cui lo stesso piano si riusa su reti diverse.':
    'They are a suggestion: whoever starts the scan can replace them, which is why the same plan is reused on different networks.',
  'spegni il radar': 'switch off the radar',
  'spegni il sonar': 'switch off the sonar',
  'togli': 'remove',
  'Un fatto lasciato vuoto è «non raccolto», e non soddisfa nessuna condizione.':
    'A fact left empty means “not collected”, and satisfies no condition.',
  'Per i sistemi raggiungibili solo dalla rete interna, le chiamate devono essere eseguite tramite una sonda con accesso alla rete di destinazione.': 'For systems reachable only from the internal network, the calls must be made through a probe with access to the destination network.',
  'Vale per quel bersaglio soltanto: su una scansione di rete il fallimento su un indirizzo è il caso normale.':
    'It applies to that target only: on a network scan, failing on one address is the normal case.',
  'Vale solo dopo la prima sincronizzazione riuscita: la pianificazione si accende in un secondo momento.':
    'It only applies after the first successful synchronisation: scheduling is switched on later.',
  'Verifica il certificato TLS del sistema. Disabilitala solo se il certificato interno non può essere validato tramite una CA attendibile.': 'Verifies the system’s TLS certificate. Disable it only if the internal certificate cannot be validated through a trusted CA.',
  '(centra)': '(centre)',
  '+ condizione': '+ condition',
  'Aggiorna': 'Refresh',
  'Aggiungi al pool': 'Add to the pool',
  'Aggiungi ambito': 'Add scope',
  'Aggiungi categoria': 'Add category',
  'Aggiungi classe': 'Add class',
  'Aggiungi condizione': 'Add condition',
  'Aggiungi ingresso': 'Add entry point',
  'Aggiungi la coppia': 'Add the pair',
  'Aggiungi un blocco (oppure)': 'Add a block (or)',
  'Aggiungi un gruppo': 'Add a group',
  'Aggiungi una condizione (e)': 'Add a condition (and)',
  'Applica': 'Apply',
  'Avvia ora': 'Start now',
  'Azzera': 'Reset',
  'Azzera filtri': 'Clear filters',
  'filtra…': 'filter…',
  'Wazuh': 'Wazuh',
  'Stato dell’agente e conformità, chiesti a Wazuh al momento tramite la sonda.':
    'Agent status and compliance, asked to Wazuh right now through the probe.',
  'Wazuh non è interrogabile.': 'Wazuh cannot be queried.',
  'Nessun agente Wazuh su questo host': 'No Wazuh agent on this host',
  'Ultimo log ricevuto da Wazuh: {quando}': 'Last log received by Wazuh: {quando}',
  'Clicca una CVE per gli host colpiti e i pacchetti da aggiornare.':
    'Click a CVE for the affected hosts and the packages to update.',
  'Nessun host trovato per questa CVE.': 'No host found for this CVE.',
  'Pacchetti da aggiornare': 'Packages to update',
  'Wazuh non ha un agente per questo host, né allarmi da log inviati via syslog a suo nome.':
    'Wazuh has no agent for this host, nor alerts from logs forwarded via syslog under its name.',
  'Questo apparato non ha un agente Wazuh, ma invia i suoi log a Wazuh (syslog): qui sotto gli allarmi generati da quei log. Conformità (SCA) e vulnerabilità richiedono un agente installato e non sono disponibili.':
    'This device has no Wazuh agent, but forwards its logs to Wazuh (syslog): below are the alerts those logs raised. Compliance (SCA) and vulnerabilities need an installed agent and are not available.',
  'Agente': 'Agent',
  'Ultimo keep-alive': 'Last keep-alive',
  'Conformità (SCA / CIS)': 'Compliance (SCA / CIS)',
  'Nessuna policy SCA valutata su questo agente.': 'No SCA policy assessed on this agent.',
  'Policy': 'Policy',
  'Superati': 'Passed',
  'Ultima scansione': 'Last scan',
  'disconnesso': 'disconnected',
  'mai connesso': 'never connected',
  'Allarmi rilevanti': 'Relevant alerts',
  'Allarmi e vulnerabilità richiedono l’Indexer di Wazuh, non configurato in questa credenziale.':
    'Alerts and vulnerabilities require the Wazuh Indexer, not configured in this credential.',
  'Nessun allarme di livello ≥ 7 negli ultimi 7 giorni.': 'No level ≥ 7 alerts in the last 7 days.',
  'Ultimo': 'Last',
  'Vulnerabilità': 'Vulnerabilities',
  '(+{n} pacchetti)': '(+{n} packages)',
  'La prima idonea': 'The first eligible',
  '{n} configurati': '{n} configured',
  'Firewall': 'Firewall',
  'Firewall non interrogabile.': 'Firewall not queryable.',
  'Traffico che il firewall ha bloccato da/verso questo host, dai log in Wazuh (ultimi 7 giorni).':
    'Traffic the firewall blocked to/from this host, from the logs in Wazuh (last 7 days).',
  'Niente bloccato dal firewall': 'Nothing blocked by the firewall',
  'Nessun traffico da/verso questo host risulta bloccato dal firewall negli ultimi 7 giorni.':
    'No traffic to/from this host was blocked by the firewall in the last 7 days.',
  'Bloccati (7g)': 'Blocked (7d)',
  'Traffico dal firewall': 'Firewall traffic',
  'Bloccato dal firewall': 'Blocked by the firewall',
  'Ultimo traffico che il firewall ha visto da/verso questo host, letto in tempo reale via sonda.':
    'Latest traffic the firewall saw to/from this host, read in real time via the probe.',
  'Scegli un firewall…': 'Choose a firewall…',
  'Solo bloccato': 'Blocked only',
  'Scegli quale firewall': 'Choose which firewall',
  'Sono configurati più firewall: scegline uno qui sopra per vedere il traffico di questo host.':
    'Several firewalls are configured: pick one above to see this host’s traffic.',
  'Nessun traffico sul firewall': 'No traffic on the firewall',
  'Il firewall non ha righe recenti per gli indirizzi di questo host.':
    'The firewall has no recent entries for this host’s addresses.',
  'Ora': 'Time',
  'Cruscotto firewall': 'Firewall dashboard',
  'Attivo da': 'Uptime',
  'Client GlobalProtect': 'GlobalProtect client',
  'Sessioni e traffico': 'Sessions and traffic',
  'Sessioni attive': 'Active sessions',
  'Nuove sessioni/s': 'New sessions/s',
  'Utenti VPN connessi': 'Connected VPN users',
  'Nessun utente VPN connesso.': 'No VPN users connected.',
  'IP virtuale': 'Virtual IP',
  'Dal': 'Since',
  'Amministratori collegati': 'Connected administrators',
  'Nessun amministratore collegato.': 'No administrators connected.',
  'Amministratore': 'Administrator',
  'Inattivo da': 'Idle for',
  'tu': 'you',
  'Applicazioni più usate': 'Top applications',
  'Le applicazioni con più sessioni nelle ultime 24 ore.':
    'The applications with the most sessions in the last 24 hours.',
  'Nessun dato applicativo.': 'No application data.',
  'Sessioni': 'Sessions',
  'Byte': 'Bytes',
  'Minacce recenti': 'Recent threats',
  'Le ultime minacce rilevate dal firewall (antivirus, anti-spyware, IPS).':
    'The latest threats detected by the firewall (antivirus, anti-spyware, IPS).',
  'Nessuna minaccia recente': 'No recent threats',
  'Il firewall non ha minacce recenti da mostrare.':
    'The firewall has no recent threats to show.',
  'Minaccia': 'Threat',
  'URL filtering': 'URL filtering',
  'Firewall configurati': 'Configured firewalls',
  'Firewall configurati e interrogazione in tempo reale dei relativi log tramite sonda.': 'Configured firewalls and real-time querying of their logs through the probe.',
  'Nessun firewall configurato': 'No firewalls configured',
  'Log Query': 'Log Query',
  'Interroga un firewall per IP sorgente e/o destinazione e mostra il traffico rilevato, consentito o bloccato. L’opzione «Solo bloccato» limita i risultati al traffico negato.': 'Queries a firewall by source and/or destination IP and shows the traffic it saw, allowed or blocked. The «Solo bloccato» option limits the results to denied traffic.',
  'IP sorgente': 'Source IP',
  'IP destinazione': 'Destination IP',
  'Interroga': 'Query',
  'Scegli quale firewall interrogare.': 'Choose which firewall to query.',
  'Nessun traffico': 'No traffic',
  'Il firewall non ha righe recenti per gli IP indicati.':
    'The firewall has no recent entries for the given IPs.',
  'Accessi VPN falliti': 'Failed VPN logins',
  'Accessi al sistema falliti': 'Failed system logins',
  'Gateway/Portale': 'Gateway/Portal',
  'Messaggio': 'Message',
  'Gli ultimi tentativi di accesso VPN non riusciti (GlobalProtect, SSL VPN, IPsec).':
    'The latest failed VPN login attempts (GlobalProtect, SSL VPN, IPsec).',
  'Gli ultimi accessi amministrativi al firewall non riusciti (autenticazione fallita).':
    'The latest failed administrative logins to the firewall (authentication failed).',
  'Nessun accesso VPN fallito': 'No failed VPN logins',
  'Nessun tentativo VPN fallito di recente.': 'No failed VPN attempts recently.',
  'Nessun accesso al sistema fallito': 'No failed system logins',
  'Nessuna autenticazione amministrativa fallita di recente.':
    'No failed administrative authentication recently.',
  'Inventario virtuale': 'Virtual inventory',
  'Acquisisce VM, host, cluster, datastore e reti rilevati dai connettori di virtualizzazione, per una vista dettagliata dell’ambiente virtuale, consultabile a video o esportabile in Excel e PDF, con un livello di dettaglio analogo a RVTools.':
    'Collects the VMs, hosts, clusters, datastores and networks found by the virtualisation connectors, for a detailed view of the virtual environment, on screen or exported to Excel and PDF, with a level of detail like RVTools.',
  'Tutto l’ambiente virtuale': 'The whole virtual environment',
  'Esporta Excel': 'Export Excel',
  'Esporta PDF': 'Export PDF',
  'Elenco dei software, Scadenze, Licenze e Application Maintenance si esportano con i pulsanti «Esporta Excel» e «Esporta PDF» in alto: scaricano la tabella corrente rispettando il filtro in uso (il PDF è il report, con intestazione CENSO; Application Maintenance produce un file con un foglio per Applicazioni, Componenti e Vulnerabilità).':
    'The Software list, Deadlines, Licences and Application Maintenance export with the “Export Excel” and “Export PDF” buttons at the top: they download the current table honouring the active filter (the PDF is the report, with the CENSO header; Application Maintenance produces a file with one sheet each for Applications, Components and Vulnerabilities).',
  'Esporto…': 'Exporting…',
  'Nessun oggetto virtuale': 'No virtual objects',
  'Configura un connettore di virtualizzazione (VMware, Nutanix, Proxmox…) e lancia una raccolta.':
    'Configure a virtualization connector (VMware, Nutanix, Proxmox…) and run a collection.',
  'Nessuna riga in questo foglio.': 'No rows in this sheet.',
  'Zone': 'Zones',
  'Interrogo il DNS…': 'Querying the DNS…',
  'DNS non interrogabile.': 'DNS not queryable.',
  'Nessuna zona': 'No zones',
  'Il DNS non ha zone, oppure la chiave API non ha i permessi.':
    'The DNS has no zones, or the API key lacks permissions.',
  'Nessun record': 'No records',
  'disabilitato': 'disabled',
  'Cruscotto': 'Dashboard',
  'Il cruscotto del firewall selezionato, in tempo reale via sonda.': 'The selected firewall’s dashboard, in real time via the probe.',
  'Scegli un firewall qui sopra per vederne il cruscotto.': 'Choose a firewall above to see its dashboard.',
  'Backup non interrogabile.': 'Backup not available.',
  'Interrogo il backup…':
    'Querying the backup…',
  'Job di backup': 'Backup jobs',
  'Libero': 'Free',
  'Nessun oggetto protetto': 'No protected objects',
  'Nessuna repository': 'No repositories',
  'Oggetti protetti': 'Protected objects',
  'Punti di ripristino': 'Restore points',
  'Repository': 'Repository',
  'Successo': 'Success',
  'Il sistema di backup non ha job, oppure l’utenza non ha i permessi.':
    'The backup system has no jobs, or the account lacks permissions.',
  'Il sistema di backup non ha oggetti protetti da mostrare.':
    'The backup system has no protected objects to show.',
  'Job con problemi': 'Jobs with issues',
  'La copertura di backup di questo oggetto, chiesta al sistema di backup al momento tramite la sonda e abbinata per nome.':
    'This object’s backup coverage, asked of the backup system right now through the probe and matched by name.',
  'Nessun backup per questo oggetto':
    'No backup for this object',
  'Oggetti di backup': 'Backup objects',
  'Protetto': 'Protected',
  'Protezione dati': 'Data protection',
  'Nessun sistema di backup ha un oggetto che corrisponde al nome di questo CI: potrebbe non essere protetto, o avere nel backup un nome diverso.':
    'No backup system has an object matching this CI’s name: it may be unprotected, or have a different name in the backup.',
  '{n} falliti': '{n} failed',
  'Ultimo backup': 'Last backup',
  'Analizzatore di percorso': 'Path analyzer',
  'Analizzo il percorso… (aggiorno le rotte dei firewall)':
    'Analyzing the path… (refreshing the firewalls’ routes)',
  'Interrogo il firewall…': 'Querying the firewall…',
  'Grafo del percorso': 'Path graph',
  'esterno': 'outside',
  'anello': 'loop',
  'nessuna rotta': 'no route',
  'ricostruisce il percorso che un pacchetto effettua tra un IP sorgente e uno di destinazione, utilizzando le tabelle di routing acquisite dagli asset di rete.': 'reconstructs the path a packet takes between a source and a destination IP, using the routing tables collected from the network assets.',
  'Ricostruzione del percorso di routing: calcola il percorso previsto tra sorgente e destinazione dalle tabelle di routing presenti nell’inventario. Riflette l’ultima scansione, non il traffico in tempo reale, e non considera ECMP, policy-based routing, VRF o NAT.': 'Routing path reconstruction: computes the expected path between source and destination from the routing tables in the inventory. It reflects the last scan, not real-time traffic, and does not consider ECMP, policy-based routing, VRF or NAT.',
  'Analizza': 'Analyze',
  'Analizzo…': 'Analyzing…',
  'Riproduci': 'Replay',
  'Destinazione raggiunta': 'Destination reached',
  'Esce verso l’esterno': 'Exits to the outside',
  'Stessa rete': 'Same network',
  'Nessuna rotta': 'No route',
  'Anello di routing': 'Routing loop',
  'Sorgente sconosciuta': 'Unknown source',
  'Nessun salto da mostrare': 'No hops to show',
  'rotta': 'route',
  'uscita': 'egress',
  'connessa': 'connected',
  'Stato sessione': 'Session state',
  'Byte inviati': 'Bytes sent',
  'Byte ricevuti': 'Bytes received',
  'Pacchetti': 'Packets',
  'ID sessione': 'Session ID',
  'Comprimi': 'Collapse',
  'Espandi': 'Expand',
  'Cosa significano gli stati di sessione': 'What the session states mean',
  'Routing': 'Routing',
  'Sincronizza dal firewall': 'Sync from the firewall',
  'Sincronizzo…': 'Syncing…',
  '{n} rotte sincronizzate dal firewall.': '{n} routes synced from the firewall.',
  'Metrica': 'Metric',
  'Next hop': 'Next hop',
  'Aggiorno…': 'Refreshing…',
  'Nessuna rotta raccolta': 'No routes collected',
  '{n} rotte': '{n} routes',
  'Tabelle di routing (L3)': 'Routing tables (L3)',
  'Nessun risultato per i filtri impostati.': 'No results for the applied filters.',
  'Lancia una discovery SNMP approfondita con l’opzione «Tabelle di routing» su questo apparato.':
    'Run a deep SNMP discovery with the “Routing tables” option on this device.',
  'Raccoglie la tabella di routing dell’apparato via SNMP (inetCidrRoute/ipCidrRoute/ipRoute), mostrata nella sezione «Routing» del CI. Richiede la discovery approfondita. Su un router con molte rotte è voluminosa: è troncata a un tetto.':
    'Collects the device’s routing table via SNMP (inetCidrRoute/ipCidrRoute/ipRoute), shown in the CI’s “Routing” section. Requires deep discovery. On a router with many routes it is large: it is capped.',
  'Applicazioni più bloccate': 'Most blocked applications',
  'Le applicazioni più negate dalle regole nelle ultime 24 ore.':
    'The applications most denied by rules in the last 24 hours.',
  'Minacce più frequenti': 'Most frequent threats',
  'Le minacce con più occorrenze nelle ultime 24 ore.':
    'The threats with the most occurrences in the last 24 hours.',
  'Nessuna minaccia nel periodo.': 'No threats in the period.',
  'Genera la chiave dalla password…': 'Generate the key from the password…',
  'Inserisci un amministratore del firewall: la sonda scelta genera la chiave API e la incolla qui sopra. Servono prima l’endpoint e la sonda; la password non viene salvata.':
    'Enter a firewall administrator: the chosen probe generates the API key and fills it in above. The endpoint and probe are needed first; the password is not saved.',
  'Chiave generata e inserita nel campo «Chiave API».':
    'Key generated and filled into the “API key” field.',
  'Genero…': 'Generating…',
  'Genera chiave': 'Generate key',
  'Compila prima l’endpoint del firewall qui sopra.':
    'Fill in the firewall endpoint above first.',
  'Le ultime richieste web con categoria ed esito (permesse o bloccate).':
    'The latest web requests with category and outcome (allowed or blocked).',
  'Nessuna richiesta web recente': 'No recent web requests',
  'Il firewall non ha righe di URL filtering da mostrare.':
    'The firewall has no URL filtering entries to show.',
  'Bloccato in ingresso': 'Blocked inbound',
  'Bloccato in uscita': 'Blocked outbound',
  'Sorgente': 'Source',
  'Applicazione': 'Application',
  'Bloccati': 'Blocked',
  'Inventario componenti (SFP, schede, alimentatori)': 'Component inventory (SFP, cards, power supplies)',
  'Legge dalla ENTITY-MIB i componenti fisici con il loro seriale. Richiede la discovery approfondita. Le SFP di terze parti spesso non hanno seriale in SNMP: per quelle è più completo l’inventario avanzato via SSH.':
    'Reads the physical components with their serial from ENTITY-MIB. Requires deep discovery. Third-party SFPs often have no serial over SNMP: for those the SSH advanced inventory is more complete.',
  'Sorgenti dati': 'Data sources',
  'Nessuna sorgente disponibile.': 'No source available.',
  'Le sorgenti live (Wazuh, ntopng…) compaiono qui appena il catalogo le prevede.':
    'Live sources (Wazuh, ntopng…) appear here as soon as the catalogue includes them.',
  'configurato': 'configured',
  'da configurare': 'to configure',
  'Configura': 'Set up',
  'Agenti per stato': 'Agents by status',
  'CVE': 'CVE',
  'Nome del cluster': 'Cluster name',
  'FQDN del cluster': 'Cluster FQDN',
  'Tipo di cluster': 'Cluster type',
  'Nodi del cluster': 'Cluster nodes',
  'Indirizzi VIP': 'VIP addresses',
  'Testimone': 'Witness',
  'Quorum': 'Quorum',
  'Nessun agente registrato.': 'No agent registered.',
  'totali': 'total',
  'vulnerabilità': 'vulnerabilities',
  'Sonda utilizzata come punto di origine della query in tempo reale. L’elenco include solo le sonde con capability abilitata. La sonda deve essere raggiungibile dal sistema.': 'Probe used as the origin of the real-time query. The list includes only probes with the capability enabled. The probe must be able to reach the system.',
  'Nessuna sonda ha questa capability abilitata: abilitala dalla scheda della sonda che raggiunge il servizio.':
    'No probe has this capability enabled: enable it from the page of the probe that reaches the service.',
  'Come creo le credenziali Wazuh, e dove?': 'How do I create the Wazuh credentials, and where?',
  'Wazuh usa due accessi distinti, entrambi da creare dedicati e in sola lettura: l’utente della API del manager (porta 55000: agenti e conformità SCA) e — se vuoi anche allarmi e vulnerabilità — l’utente dell’Indexer (porta 9200), che è un’utenza separata. Le credenziali di default wazuh/wazuh vanno cambiate, non usate qui.':
    'Wazuh uses two separate logins, both to be created dedicated and read-only: the manager API user (port 55000: agents and SCA compliance) and — if you also want alerts and vulnerabilities — the Indexer user (port 9200), which is a separate account. The default wazuh/wazuh credentials must be changed, not used here.',
  'A) Utente della Wazuh API (manager, porta 55000)': 'A) Wazuh API user (manager, port 55000)',
  'Dà accesso ad agenti e SCA. Si crea nella sicurezza della Wazuh API (RBAC), che è separata da quella dell’Indexer.':
    "Grants access to agents and SCA. It is created in the Wazuh API security (RBAC), which is separate from the Indexer's.",
  'Dal Wazuh dashboard: menu → Server management → Security (la sicurezza della Wazuh API). Il nome esatto della voce cambia con la versione.':
    'From the Wazuh dashboard: menu → Server management → Security (the Wazuh API security). The exact menu name changes with the version.',
  'Crea un utente dedicato e assegnagli un ruolo di sola lettura (va bene la policy di lettura predefinita): deve poter leggere agenti e SCA, niente di più.':
    'Create a dedicated user and assign it a read-only role (the default read policy is fine): it must be able to read agents and SCA, nothing more.',
  'In alternativa, dalla riga di comando (cambia password amministrativa e valori del nuovo utente):':
    'Alternatively, from the command line (change the admin password and the new user values):',
  'Metti quell’utente e la sua password nei campi «Utente API» e «Password API», e l’URL del manager (di norma https://indirizzo:55000) in «Endpoint API». La dashboard sta su un’altra porta: qui va l’API.':
    'Put that user and its password in the «API user» and «API password» fields, and the manager URL (usually https://address:55000) in «API endpoint». The dashboard is on a different port: this is the API.',
  'B) Utente dell’Indexer (porta 9200) — per allarmi e vulnerabilità':
    'B) Indexer user (port 9200) — for alerts and vulnerabilities',
  'Opzionale: senza, la scheda mostra agente e SCA ma non allarmi né vulnerabilità (dalla 4.8 stanno nell’Indexer, non nella API). È un’utenza separata da quella della API.':
    'Optional: without it, the card shows the agent and SCA but neither alerts nor vulnerabilities (since 4.8 they live in the Indexer, not the API). It is an account separate from the API one.',
  'Dal Wazuh dashboard: menu → Indexer management → Security → Internal users (la sicurezza di OpenSearch). Crea un utente interno dedicato.':
    'From the Wazuh dashboard: menu → Indexer management → Security → Internal users (the OpenSearch security). Create a dedicated internal user.',
  'Assegnagli un ruolo con permesso di LETTURA sugli indici wazuh-alerts-* e wazuh-states-vulnerabilities-*. Non serve il monitoraggio del cluster: la sola lettura dei dati basta.':
    'Assign it a role with READ permission on the wazuh-alerts-* and wazuh-states-vulnerabilities-* indices. Cluster monitoring is not needed: read on the data alone is enough.',
  'Metti quell’utente e la sua password nei campi «Utente Indexer» e «Password Indexer», e l’URL dell’Indexer (di norma https://indirizzo:9200) in «Endpoint Indexer». Lascia vuoti questi campi se non ti servono allarmi e vulnerabilità.':
    'Put that user and its password in the «Indexer user» and «Indexer password» fields, and the Indexer URL (usually https://address:9200) in «Indexer endpoint». Leave these fields empty if you do not need alerts and vulnerabilities.',
  'C) La sonda che raggiunge Wazuh': 'C) The probe that reaches Wazuh',
  'La query esce in tempo reale da una sonda, non dal cloud: Wazuh vive sulla rete del cliente. La sonda deve raggiungere sia la 55000 sia la 9200 e avere la capability «Wazuh (SIEM/XDR)» abilitata (dalla sua scheda in Sonde). Poi scegli proprio quella nel campo «Sonda»: se ne indichi una che non la raggiunge, ottieni un errore chiaro invece di una risposta sbagliata.':
    'The query runs in real time from a probe, not from the cloud: Wazuh lives on the customer network. The probe must reach both 55000 and 9200 and have the «Wazuh (SIEM/XDR)» capability enabled (from its page under Probes). Then pick exactly that one in the «Probe» field: if you point to one that does not reach it, you get a clear error instead of a wrong answer.',
  'Il certificato di Wazuh è spesso autofirmato: usa il certificato della CA o l’impronta SHA-256; «accetta qualunque certificato» solo se non hai alternative. Un endpoint scritto senza schema riceve https.':
    'Wazuh’s certificate is often self-signed: use the CA certificate or the SHA-256 fingerprint; «accept any certificate» only if you have no alternative. An endpoint written without a scheme receives https.',
  'Pacchetto': 'Package',
  'Nessuna vulnerabilità rilevata da Wazuh su questo host.': 'No vulnerabilities detected by Wazuh on this host.',
  'Agenti, allarmi e vulnerabilità degli asset, sincronizzati in tempo reale con Wazuh.': 'Agents, alerts and vulnerabilities of the assets, synchronized in real time with Wazuh.',
  'Agenti con allarmi': 'Agents with alerts',
  'Allarmi (ultimi {giorni} giorni)': 'Alerts (last {giorni} days)',
  'Allarmi ({giorni}g)': 'Alerts ({giorni}d)',
  'Allarmi al giorno': 'Alerts per day',
  'Andamento': 'Trend',
  'CVE più diffuse': 'Most widespread CVEs',
  'Critiche e alte': 'Critical and high',
  'Host colpiti': 'Affected hosts',
  'Host più colpiti': 'Most affected hosts',
  'Nessun allarme di livello ≥ 7 nella finestra scelta.': 'No level ≥ 7 alerts in the selected window.',
  'Per gravità': 'By severity',
  'Per livello': 'By level',
  'Regole più frequenti': 'Most frequent rules',
  '{n}g': '{n}d',
  'Nessuna vulnerabilità rilevata da Wazuh sul parco.': 'No vulnerabilities detected by Wazuh across the fleet.',
  'Dati in tempo reale da ntopng, chiesti alla sonda al momento: non sono salvati.':
    'Live data from ntopng, requested from the probe right now: not stored.',
  'Interrogo…': 'Querying…',
  'ntopng non è interrogabile.': 'ntopng cannot be queried.',
  'ntopng non conosce questo host': 'ntopng does not know this host',
  'Nessun traffico osservato di recente per {ip}.': 'No traffic seen recently for {ip}.',
  'Score': 'Score',
  'Allarmi': 'Alerts',
  'Throughput': 'Throughput',
  'In blacklist': 'Blacklisted',
  'Con chi parla fuori': 'Who it talks to outside',
  'Nessun interlocutore critico (verso l’esterno o in blacklist) al momento.':
    'No critical peer (external or blacklisted) right now.',
  '{n} conversazioni interne non mostrate.': '{n} internal conversations not shown.',
  'Dove': 'Where',
  'dentro': 'inside',
  'fuori': 'outside',
  'entrante': 'inbound',
  'uscente': 'outbound',
  'blacklist': 'blacklist',
  'Interrogato: {quando}': 'Queried: {quando}',
  'È un indirizzo di…': 'It is an address of…',
  'Scegli il CI di cui questo indirizzo fa parte: lo aggiungo ai suoi indirizzi aggiuntivi e l’avvistamento diventa «censito».':
    'Choose the CI this address belongs to: it is added to its extra addresses and the sighting becomes “inventoried”.',
  'Cerca un CI per nome o chiave naturale…': 'Search a CI by name or natural key…',
  'Scrivi almeno due caratteri per cercare.': 'Type at least two characters to search.',
  'Nessun CI trovato.': 'No CI found.',
  'Associo…': 'Linking…',
  'Campi personalizzati': 'Custom fields',
  'Campi definiti': 'Defined fields',
  'Definisce campi personalizzati per l’inventario, oltre a quelli raccolti dalla discovery. Possono essere associati a una specifica classe di CI, a una categoria di asset oppure resi disponibili in tutte le schede.':
    'Fields of your own inventory, beyond those collected by discovery. You can limit them to a class (CI) or category (Asset), or leave them valid for all records.',
  'Nuovo campo': 'New field',
  'Modifica campo': 'Edit field',
  'Crea campo': 'Create field',
  'Salva modifiche': 'Save changes',
  'Valori ammessi (uno per riga)': 'Allowed values (one per line)',
  'Tutte le classi': 'All classes',
  'Tutte le categorie': 'All categories',
  'Obbligatorio': 'Required',
  'Aiuto (facoltativo)': 'Help (optional)',
  'Nessun campo personalizzato': 'No custom fields',
  'Crea un nuovo campo dal pannello di configurazione: sarà immediatamente disponibile nelle schede a cui è associato.':
    'Create one with the form beside: it will appear on this record ready to fill in.',
  'Eliminare il campo «{label}»? I valori già scritti restano nei dati.':
    'Delete the field “{label}”? Values already written stay in the data.',
  'Salvo…': 'Saving…',
  'Salvato': 'Saved',
  'Sì': 'Yes',
  'No': 'No',
  'Cambia classe': 'Change class',
  'Cambia comunque': 'Change anyway',
  'cambia indirizzo': 'change address',
  'Carica': 'Load',
  'Collega un provider': 'Connect a provider',
  "Condizioni sull'inventario": 'Conditions on the inventory',
  'dalla baseline': 'from the baseline',
  'Di solito significa che la porta 80 non è più raggiungibile da internet, o che il DNS di uno dei nomi è stato spostato. Finché non si rimedia, il certificato scade e i clienti non accedono più.':
    'It usually means port 80 is no longer reachable from the internet, or that the DNS for one of the names has been moved. Until this is fixed, the certificate expires and customers can no longer sign in.',
  'Dichiara dismesso': 'Declare decommissioned',
  "Dov'è l'issuer?": 'Where is the issuer?',
  "È il primo salvataggio di questo apparato: non c'è un «prima», e tutto risulta aggiunto.":
    'This is the first save for this device: there is no “before”, so everything shows as added.',
  "È normale in sviluppo, dove non c'è un bilanciatore. Su un'installazione reale significa che la cartella dei certificati non è montata su questo processo, e da qui non si potrà sostituirlo.":
    'This is normal in development, where there is no load balancer. On a real installation it means the certificate folder is not mounted on this process, and the certificate cannot be replaced from here.',
  'elimina': 'delete',
  'Elimina definitivamente': 'Delete permanently',
  'Elimina gruppo': 'Delete group',
  'Esegui ora': 'Run now',
  'Esplora': 'Explore',
  "Fra gli ingressi scelti c'è del traffico di ritorno: è la porta effimera di una connessione in uscita, non un servizio esposto. Se è voluto va bene, ma la mappa del servizio cambierà a ogni finestra.":
    'Among the chosen entry points there is return traffic: it is the ephemeral port of an outbound connection, not an exposed service. If that is intended it is fine, but the service map will change with every window.',
  'I segreti sono cifrati e non sono mai più visualizzabili. Vengono consegnati a una sonda solo quando le è stato assegnato un job che li utilizza.':
    'Secrets are encrypted and can never be displayed again. They are delivered to a probe only when it has been assigned a job that uses them.',
  "Il confronto è troppo lungo ed è stato tagliato: quello che vedi è l'inizio. La configurazione intera è nell'altra scheda.":
    'The comparison is too long and has been cut: what you see is the beginning. The whole configuration is on the other tab.',
  'La macchina non raggiunge il Control Plane: metto il file a mano':
    'The machine cannot reach the Control Plane: I will place the file by hand',
  "La sonda è in contatto ma non ha ancora letto la configurazione dei flussi: succede nei primi secondi dopo l'avvio. Se resta così, l'errore comparirà qui al prossimo tentativo.":
    'The probe is in contact but has not read the flow configuration yet: this happens in the first seconds after start-up. If it stays this way, the error will appear here at the next attempt.',
  'La verifica è passata: togli la spunta a «Prima una prova» e rilancia per il certificato vero.':
    'The check passed: clear “Test first” and run again for the real certificate.',
  'Le sonde continuano a raccogliere e a consegnare, ma il traffico consegnato adesso è perso: non si accoda in attesa che il servizio torni.':
    'The probes keep collecting and delivering, but the traffic delivered right now is lost: it is not queued waiting for the service to come back.',
  'Linux': 'Linux',
  'Modifica': 'Edit',
  'Mostra tutto il traffico': 'Show all traffic',
  "Nessun costruttore caricato: il produttore si ricava solo per le marche più diffuse. Di norma il registro arriva con l'immagine — se manca, questa installazione è stata costruita senza uscita verso Internet.":
    'No manufacturer loaded: the maker is only derived for the most common brands. The registry normally ships with the image — if it is missing, this installation was built without internet access.',
  'Nessuna capability abilitata: la sonda resterà ferma.':
    'No capability enabled: the probe will sit idle.',
  'Nessuna capability selezionata: la sonda si arruolerà ma non riceverà alcun job.':
    'No capability selected: the probe will enrol but will receive no job.',
  "Nessuna credenziale configurata: creane una nella sezione Credenziali, altrimenti non c'è modo di interrogare nulla.":
    'No credential configured: create one in the Credentials section, otherwise there is no way to query anything.',
  'nessuna in linea: i job resteranno in attesa': 'none online: jobs will stay pending',
  'Nuova regola': 'New rule',
  'Nuovo piano': 'New plan',
  'Nuovo prodotto': 'New product',
  'passo costoso senza un filtro davanti: verrebbe provato su ogni indirizzo dell’ambito, compresi quelli spenti.':
    'expensive step with no filter in front: it would be tried on every address in the scope, switched-off ones included.',
  'Pianifica': 'Schedule',
  'Proponi questo piano per primo': 'Offer this plan first',
  'Questa non è la console di un cliente: da qui si creano ed eliminano tenant e si assegnano licenze.':
    'This is not a customer’s console: from here tenants are created and deleted and licences are assigned.',
  'Questa sonda non dichiara interfacce adatte alla cattura: servono una scheda attiva e non di loopback, di norma collegata alla porta mirror. Verifica che sia aggiornata e che la scheda sia su.':
    'This probe declares no interface suitable for capture: an interface that is up and not loopback is needed, normally connected to the mirror port. Check that the probe is up to date and the interface is up.',
  'Questo bene non è collegato ad alcun configuration item: la rete non lo conferma. È normale per un ricambio, un bene in magazzino o una licenza; per un apparato in servizio significa che la discovery non lo vede.':
    'This asset is not linked to any configuration item: the network does not confirm it. That is normal for a spare part, an item in stock or a licence; for a device in service it means discovery cannot see it.',
  'Questo gruppo non ha più credenziali: probabilmente sono state eliminate. Una discovery che lo usasse verrebbe rifiutata.':
    'This group has no credentials left: they were probably deleted. A discovery using it would be rejected.',
  'Quota esaurita: i nuovi CI non verranno registrati finché il piano non viene ampliato. Quelli già noti continuano ad aggiornarsi.':
    'Quota exhausted: new CIs will not be recorded until the plan is extended. The ones already known keep updating.',
  'Reti e indirizzi che scrivo io': 'Networks and addresses I type myself',
  'Riapplica ai clienti': 'Reapply to the customers',
  'Valori di serie': 'Factory defaults',
  'Riporta il piano ai valori di serie del prodotto': 'Reset the plan to the product\u2019s factory defaults',
  'Ricarica': 'Reload',
  'Rimuovi i dati più vecchi': 'Remove the oldest data',
  'rinnovo tacito': 'automatic renewal',
  'Scollega': 'Unlink',
  'Scrivo io reti e indirizzi': 'I type the networks and addresses',
  'Sincronizza': 'Synchronise',
  "Stai modificando una pianificazione che gira già. Il tipo non si cambia; le credenziali restano quelle attuali se non ne scegli un'altra. Lo storico delle esecuzioni resta.":
    'You are editing a schedule that is already running. The type cannot be changed; the credentials stay as they are unless you pick another. The run history is kept.',
  'su un periodo': 'over a period',
  'Togli': 'Remove',
  'Togli il blocco': 'Remove the block',
  'Torna all’elenco': 'Back to the list',
  'Tutto': 'Everything',
  'Uso un pool': 'Use a pool',
  'vuoto = illimitati': 'empty = unlimited',
  'Windows': 'Windows',
  'Prova a vuoto': 'Dry run',
  "Abilita un'utenza esistente": 'Enable an existing user',
  "Che cosa contiene l'inventario": 'What the inventory contains',
  "Consegne degli agenti d'inventario": 'Inventory agent deliveries',
  "Creane uno qui sopra: riceverà il proprio indirizzo derivato dall'identificativo.":
    'Create one above: it will get its own address derived from the identifier.',
  "Definisci un servizio indicando il suo punto di ingresso — il VIP del bilanciatore, il front-end, l'indirizzo pubblicato — e la sua mappa si costruirà dal traffico osservato.":
    'Define a service by naming its entry point — the load balancer VIP, the front end, the published address — and its map will build itself from the observed traffic.',
  "L'indirizzo non compare nel traffico osservato nel periodo scelto. Prova una finestra più ampia, o verifica che il filtro del traffico non lo stia scartando: in Impostazioni → Traffico da conservare.":
    'The address does not appear in the traffic observed over the chosen period. Try a wider window, or check that the traffic filter is not discarding it: under Settings → Traffic to keep.',
  "L'utenza appartiene a un altro tenant": 'The user belongs to another tenant',
  "La prova dice anche chi risulti essere dall'altra parte: utenza e istanza. Un connettore puntato sull'ambiente di collaudo funziona benissimo, ed è proprio quello il problema.":
    'The test also says who you turn out to be on the other side: user and instance. A connector pointed at the staging environment works perfectly, and that is precisely the problem.',
  "Le regole servono quando la piattaforma classifica male un apparato che tu riconosci a colpo d'occhio — per esempio dal sysObjectID del costruttore.":
    'Rules are for when the platform misclassifies a device you recognise at a glance — from the manufacturer’s sysObjectID, for instance.',
  "Nessuno spazio attribuito: l'inventario è vuoto.":
    'No space attributed: the inventory is empty.',
  "Non è un errore: con questi criteri non c'è nulla da riportare. È spesso la risposta che si spera.":
    'This is not an error: with these criteria there is nothing to report. It is often the answer you hope for.',
  "Non è un errore: in questa finestra non c'è nulla da riportare.":
    'This is not an error: there is nothing to report in this window.',
  "Ripartizione del traffico all'ingresso": 'Breakdown of traffic at the entry point',
  "Servono flussi nella finestra scelta. Se il traffico c'è ma qui non compare nulla, allarga la finestra: un servizio si riconosce anche dal fatto che ricorre.":
    'Flows are needed within the chosen window. If there is traffic but nothing shows here, widen the window: a service is recognised partly by the fact that it recurs.',
  "Traffico all'ingresso": 'Traffic at the entry point',
  "Traffico verso l'ingresso del servizio": 'Traffic towards the service entry point',
  'esegui': 'run',
  'modifica': 'edit',
  'Rigenera': 'Regenerate',
  'Verifica': 'Check',
  '{quanti} costruttori caricati.': '{quanti} manufacturers loaded.',
  '{persi} CI raccolti dalla sonda non sono finiti in CMDB. Il motivo è negli eventi di fase «ingest» qui sotto.':
    '{persi} CIs collected by the probe did not reach the CMDB. The reason is in the “ingest” phase events below.',
  'da riconoscere': 'to be recognised',
  'catalogati': 'catalogued',
  ' e ': ' and ',
  ' oppure ': ' or ',
  'Accessibile': 'Accessible',
  'Accessibile pubblicamente': 'Publicly accessible',
  'Aggiorn. hardware': 'HW upgrade',
  'Aggiornamento hardware': 'Hardware upgrade',
  'Aggiunta CPU a caldo': 'CPU hot-add',
  'Aggiunta memoria a caldo': 'Memory hot-add',
  'Architettura': 'Architecture',
  'Blocco CIDR': 'CIDR block',
  'Build ESXi': 'ESXi build',
  'CD-ROM collegati': 'Attached CD-ROMs',
  'Capacità (GB)': 'Capacity (GB)',
  'Capacità GB': 'Capacity GB',
  'Capacità fisica': 'Physical capacity',
  'Capacità fisica (GB)': 'Physical capacity (GB)',
  'Chassis ID': 'Chassis ID',
  'Chiaro': 'Light',
  'Cifrato': 'Encrypted',
  'Classe di istanza': 'Instance class',
  'Cluster': 'Cluster',
  'Cluster (riferimento)': 'Cluster (reference)',
  'Cluster collegati': 'Attached clusters',
  'Collegata': 'Connected',
  'Commutatore': 'Switch',
  'Computer': 'Computer',
  'Condiviso fra host': 'Shared between hosts',
  'Connessione': 'Connection',
  'Contatto': 'Contact',
  'Core fisici': 'Physical cores',
  'Core per socket': 'Cores per socket',
  'DRS attivo': 'DRS enabled',
  'Datastore': 'Datastore',
  'Dimensione (GB)': 'Size (GB)',
  'Dischi': 'Disks',
  'Disco': 'Disk',
  'Dominio DNS': 'DNS domain',
  'Endpoint': 'Endpoint',
  'Export NFS': 'NFS export',
  'Famiglia del sistema': 'OS family',
  'Famiglia di API': 'API family',
  'File': 'File',
  'File system': 'File system',
  'Firmware di avvio': 'Boot firmware',
  'Foresta': 'Forest',
  'Formato': 'Format',
  'HA attivo': 'HA enabled',
  'Hardware': 'Hardware',
  'Host': 'Host',
  'Hypervisor': 'Hypervisor',
  'Indirizzo IP': 'IP address',
  'Indirizzo del controller di gestione': 'Management controller address',
  'Indirizzo di gestione': 'Management address',
  'Libero GB': 'Free GB',
  'MAC': 'MAC',
  'MTU': 'MTU',
  'Macchine virtuali ospitate': 'Hosted virtual machines',
  'Memoria (GB)': 'Memory (GB)',
  'Memoria (MB)': 'Memory (MB)',
  'Memoria (MiB)': 'Memory (MiB)',
  'Memoria MB': 'Memory MB',
  'Memoria MiB': 'Memory MiB',
  'Modello CPU': 'CPU model',
  'Modo VLAN': 'VLAN mode',
  'Motore': 'Engine',
  'Nome DNS': 'DNS name',
  'Nome host': 'Host name',
  'Numero di uplink': 'Uplink count',
  'Piattaforma': 'Platform',
  'Piattaforma ospite': 'Guest platform',
  'Porte TCP aperte': 'Open TCP ports',
  'Porte in ascolto': 'Listening ports',
  'Prefisso': 'Prefix',
  'Presente su host': 'On host',
  'Produttore scheda': 'NIC manufacturer',
  'Provider': 'Provider',
  'Provisionato GB': 'Provisioned GB',
  'Provisioning': 'Provisioning',
  'Punto di mount': 'Mount point',
  'Raccolto tramite': 'Collected via',
  'Regione': 'Region',
  'Regole': 'Rules',
  'Reti': 'Networks',
  'Riferimento vCenter': 'vCenter reference',
  'Rilevato il': 'Detected on',
  'Runtime': 'Runtime',
  'Runtime container': 'Container runtime',
  'Schede di rete': 'Network adapters',
  'Schema': 'Schema',
  'Scuro': 'Dark',
  'Seriale del telaio': 'Chassis serial',
  'Server NFS': 'NFS server',
  'Sistema operativo': 'Operating system',
  'Socket': 'Sockets',
  'Spazio fisico libero': 'Free physical space',
  'Spazio fisico libero (GB)': 'Free physical space (GB)',
  'Spazio fisico usato (%)': 'Used physical space (%)',
  'Spazio libero': 'Free space',
  'Spazio libero (GB)': 'Free space (GB)',
  'Spazio occupato (GB)': 'Used space (GB)',
  'Spazio provisionato': 'Provisioned space',
  'Spazio provisionato (GB)': 'Provisioned space (GB)',
  'Spazio usato (%)': 'Used space (%)',
  'Stato di alimentazione': 'Power state',
  'Stato di connessione': 'Connection state',
  'Stato osservato': 'Observed state',
  'Supporto': 'Media',
  'Thread': 'Threads',
  'Timeout (s)': 'Timeout (s)',
  'Tipo di MAC': 'MAC type',
  'Tipo di archiviazione': 'Storage type',
  'Tipo di commutatore': 'Switch type',
  'Tipo di disco': 'Disk type',
  'Tipo di istanza': 'Instance type',
  'Tipo di rete': 'Network type',
  'Tipo di risorsa': 'Resource type',
  'Tipo di scheda': 'Adapter type',
  'Tipo rete': 'Network type',
  'Tipo switch': 'Switch type',
  'UUID BIOS': 'BIOS UUID',
  'Ubicazione': 'Location',
  'Uplink': 'Uplinks',
  'Usato %': 'Used %',
  'VLAN': 'VLAN',
  'VLAN del trunk': 'Trunk VLANs',
  'VMFS': 'VMFS',
  'Versionamento': 'Versioning',
  'Versione ESXi': 'ESXi version',
  'Versione OS': 'OS version',
  'Versione VMFS': 'VMFS version',
  'Versione firmware': 'Firmware version',
  'Versione hardware virtuale': 'Virtual hardware version',
  'Versione motore': 'Engine version',
  'Versione software': 'Software version',
  'WinRM attivo': 'WinRM enabled',
  'Zona': 'Zone',
  'Zona di disponibilità': 'Availability zone',
  'comparso nelle ultime 24 ore': 'appeared in the last 24 hours',
  'condizione non leggibile': 'condition not readable',
  'host non gestito: vivo in rete ma non interrogabile':
    'unmanaged host: alive on the network but cannot be queried',
  'la condizione guarda l’esito di se stesso, che non esiste ancora':
    'the condition looks at its own outcome, which does not exist yet',
  'la condizione guarda l’esito di «{passo}», che viene dopo o non c’è':
    'the condition looks at the outcome of “{passo}”, which comes later or is missing',
  'nessun oggetto': 'no object',
  'non classificato: la piattaforma non sa cosa sia':
    'unclassified: the platform does not know what it is',
  'non è': 'is not',
  'parte sempre': 'always runs',
  'riconosciuto e classificato': 'recognised and classified',
  'serve un numero intero': 'a whole number is required',
  'vCPU': 'vCPUs',
  '{campo} contiene {valore}': '{campo} contains {valore}',
  '{campo} non è presente': '{campo} is not present',
  '{campo} è maggiore di {valore}': '{campo} is greater than {valore}',
  '{campo} è minore di {valore}': '{campo} is less than {valore}',
  '{campo} è presente': '{campo} is present',
  'è': 'is',
  'è diverso da': 'is not equal to',
  'è maggiore di': 'is greater than',
  'è minore di': 'is less than',
  'è presente': 'is present',
  'è uguale a': 'is equal to',
  'Alimentazione in uso': 'Power source in use',
  'Stato della batteria': 'Battery status',
  'Carica della batteria': 'Battery charge',
  'Autonomia stimata': 'Estimated runtime',
  'Tensione della batteria': 'Battery voltage',
  'Temperatura della batteria': 'Battery temperature',
  'Da quanto è a batteria': 'Time on battery',
  'Tensione in ingresso': 'Input voltage',
  'Frequenza in ingresso': 'Input frequency',
  'Mancanze di rete': 'Mains failures',
  'Tensione in uscita': 'Output voltage',
  'Frequenza in uscita': 'Output frequency',
  'Potenza in uscita': 'Output power',
  'Carico in uscita': 'Output load',
  'Potenza nominale': 'Rated power',
  'Allarmi attivi': 'Active alarms',
  'Apparati collegati': 'Attached devices',
  'Batterie sostituite il': 'Batteries replaced on',
  'Età delle batterie': 'Battery age',
  'Data di fabbricazione': 'Manufacture date',
  'Ultimo autotest': 'Last self-test',
  'Pacchi batteria': 'Battery packs',
  'Pacchi batteria guasti': 'Failed battery packs',
  'Tensione nominale della batteria': 'Nominal battery voltage',
  'Corrente della batteria': 'Battery current',
  'Temperatura ambiente': 'Ambient temperature',
  'Punteggio ntopng': 'ntopng score',
  'Allarmi su ntopng': 'Alerts on ntopng',
  'In blacklist secondo ntopng': 'Blacklisted per ntopng',
  'Segnalato da ntopng': 'Flagged by ntopng',
  'Perché è segnalato': 'Why it is flagged',
  'Visto da ntopng': 'Seen by ntopng',
  'Protocolli applicativi': 'Application protocols',
  'Come è stato rilevato': 'How it was detected',
  'ntopng': 'ntopng',
  'Rilevazione e classificazione degli host effettuata mediante la sola analisi del traffico di rete.': 'Detection and classification of hosts performed through network traffic analysis alone.',
  'Active Directory': 'Active Directory',
  'Adiacenze CDP': 'CDP adjacencies',
  'Adiacenze LLDP': 'LLDP adjacencies',
  'Amazon Web Services': 'Amazon Web Services',
  'Backup configurazioni': 'Configuration backup',
  'Inventario avanzato apparati': 'Advanced device inventory',
  'Wireless': 'Wireless',
  'Nessun access point censito': 'No access point recorded',
  'Lancia una discovery «Inventario avanzato apparati» su questo controller.':
    'Run an “Advanced device inventory” discovery on this controller.',
  '{ap} access point, {ssid} SSID, {client} client': '{ap} access points, {ssid} SSIDs, {client} clients',
  'Host registrati': 'Registered hosts',
  'Porte ethernet': 'Ethernet ports',
  'Initiator': 'Initiators',
  'Host serviti': 'Hosts served',
  'array': 'array',
  'Wireless (client, SSID)': 'Wireless (clients, SSIDs)',
  'Controller': 'Controller',
  'Postazione': 'Computer',
  'Collegato da': 'Connected since',
  'Utente collegato': 'Logged-on user',
  'Presenza': 'Presence',
  'Wi-Fi': 'Wi-Fi',
  'VPN':
    'VPN',
  'PC': 'PCs',
  'Dove risulta collegata questa persona, per sorgente: Wi-Fi e PC dall’ultimo inventario, VPN chiesta al firewall adesso.':
    'Where this person shows up as connected, by source: Wi-Fi and PCs from the latest inventory, VPN asked to the firewall right now.',
  'Nessun client Wi-Fi con questa login': 'No Wi-Fi client with this login',
  'Le login arrivano dalle reti a 802.1X/Portal, con l’inventario avanzato dei controller wireless.':
    'Logins come from 802.1X/Portal networks, via the advanced inventory of wireless controllers.',
  'Nessuna sessione VPN attiva per questa login': 'No active VPN session for this login',
  'Nessun firewall interrogabile': 'No firewall to query',
  'Firewall non raggiunti: {elenco}': 'Firewalls not reached: {elenco}',
  'Il firewall non ha risposto adesso: sessioni dall’ultima sincronizzazione (al più 5 minuti fa).':
    'The firewall did not answer just now: sessions from the last sync (at most 5 minutes ago).',
  'Nessun PC dichiara questa login': 'No PC reports this login',
  'Serve l’attributo «Utente collegato» dall’inventario degli host: arriverà con l’agent e con WinRM.':
    'It needs the “Logged-on user” attribute from host inventory: it will come with the agent and WinRM.',
  'MAC casuale': 'Random MAC',
  'MAC randomizzato dal dispositivo (privacy)': 'MAC randomised by the device (privacy)',
  'Reti diffuse': 'Broadcast networks',
  'Client connessi': 'Connected clients',
  'Client per banda': 'Clients by band',
  'Qualità del segnale': 'Signal quality',
  'Client per SSID': 'Clients by SSID',
  'Client per access point': 'Clients by access point',
  'client': 'clients',
  'ottimo': 'excellent',
  'buono': 'good',
  'debole': 'weak',
  'Dal RSSI dei client: ottimo ≥ −55 dBm, buono ≥ −65, debole ≥ −75, critico sotto.':
    'From client RSSI: excellent ≥ −55 dBm, good ≥ −65, weak ≥ −75, critical below.',
  'normale': 'normal',
  'Acceso da': 'Up for',
  'Radio': 'Radio',
  'Banda': 'Band',
  'Client': 'Clients',
  'Transceiver (SFP)': 'Transceiver (SFP)',
  'Transceiver': 'Transceiver',
  'Vai al dettaglio del transceiver': 'Go to the transceiver detail',
  'Scheda': 'Card',
  'Alimentatore': 'Power supply',
  'Ventola': 'Fan',
  'Modulo': 'Module',
  'Telaio': 'Chassis',
  'Membro dello stack': 'Stack member',
  'Cloud': 'Cloud',
  'Directory': 'Directory',
  'Google Cloud': 'Google Cloud',
  'Host ESXi e macchine virtuali del vCenter indicato nella credenziale.':
    'ESXi hosts and virtual machines of the vCenter named in the credential.',
  'Host e macchine virtuali del management node ZStack.':
    'Hosts and virtual machines of the ZStack management node.',
  'Host e macchine virtuali della piattaforma Sangfor.':
    'Hosts and virtual machines of the Sangfor platform.',
  'Host e macchine virtuali di NexaSphere (API ZStack).':
    'Hosts and virtual machines of NexaSphere (ZStack API).',
  'Host e macchine virtuali di NexaVM Cloud (API ZStack).':
    'Hosts and virtual machines of NexaVM Cloud (ZStack API).',
  'Host e macchine virtuali di Prism Central.': 'Hosts and virtual machines of Prism Central.',
  'I computer del dominio letti dal Domain Controller via LDAP: nome, sistema operativo, collocazione nelle OU e ultimo accesso. Non tocca le workstation, quindi vede anche quelle spente.':
    'The domain computers read from the Domain Controller over LDAP: name, operating system, place in the OUs and last logon. It never touches the workstations, so it sees the switched-off ones too.',
  'Interroga solo i vicinati CDP degli apparati indicati.':
    'Queries only the CDP neighbourhoods of the given devices.',
  'Interroga solo i vicinati LLDP degli apparati indicati.':
    'Queries only the LLDP neighbourhoods of the given devices.',
  'Inventario della regione indicata nella credenziale: EC2 e VPC, S3, Lambda, RDS, load balancer, volumi EBS, security group e cluster/servizi ECS.':
    'Inventory of the region named in the credential: EC2 and VPC, S3, Lambda, RDS, load balancers, EBS volumes, security groups and ECS clusters/services.',
  'Istanze e reti del progetto indicato nella credenziale.':
    'Instances and networks of the project named in the credential.',
  'Istanze, VCN e subnet del compartimento indicato nella credenziale.':
    'Instances, VCNs and subnets of the compartment named in the credential.',
  'Macchine virtuali e reti della sottoscrizione indicata nella credenziale.':
    'Virtual machines and networks of the subscription named in the credential.',
  'Microsoft Azure': 'Microsoft Azure',
  'NexaSphere': 'NexaSphere',
  'NexaVM Cloud': 'NexaVM Cloud',
  "Nodi, macchine virtuali e container del cluster. Gli indirizzi IP degli ospiti non sono esposti dall'API: li conosce il sistema ospite e richiedono il QEMU guest agent.":
    'Cluster nodes, virtual machines and containers. Guest IP addresses are not exposed by the API: only the guest OS knows them, and reading them requires the QEMU guest agent.',
  'Nutanix': 'Nutanix',
  'Oracle Cloud': 'Oracle Cloud',
  'Proxmox VE': 'Proxmox VE',
  'Rete (SNMP)': 'Network (SNMP)',
  "Legge da switch e router i componenti che l'SNMP non vede — SFP, schede, alimentatori — con i loro seriali; sui controller wireless censisce anche gli access point (che diventano CI collegati al controller), gli SSID diffusi e i client associati. Huawei (compresi gli AirEngine), Cisco, Juniper, Fortinet ed Extreme; gli altri vendor si aggiungono nel tempo.":
    'Reads from switches and routers the components SNMP does not see — SFPs, cards, power supplies — with their serial numbers; on wireless controllers it also takes stock of the access points (which become CIs linked to the controller), the broadcast SSIDs and the associated clients. Huawei (including AirEngine), Cisco, Juniper, Fortinet and Extreme; other vendors are added over time.',
  'Salva la configurazione di switch, router e firewall e ne conserva la storia: Cisco, Huawei (compresi i NetEngine), Aruba, HP Comware, Extreme, Juniper, Fortinet, MikroTik, Palo Alto, Lantronix. Le configurazioni identiche alla precedente non creano una revisione.':
    'Saves the configuration of switches, routers and firewalls and keeps its history: Cisco, Huawei (including NetEngine), Aruba, HP Comware, Extreme, Juniper, Fortinet, MikroTik, Palo Alto, Lantronix. A configuration identical to the previous one creates no revision.',
  'Sangfor aCloud': 'Sangfor aCloud',
  'Scansiona subnet e indirizzi via SNMP; con le opzioni dedicate raccoglie anche le adiacenze CDP/LLDP e gli indirizzi fisici imparati dalle porte.':
    'Scans subnets and addresses over SNMP; with the dedicated options it also collects CDP/LLDP adjacencies and the physical addresses learned on the ports.',
  "Sequenza di passi disegnata da chi amministra la rete: ping, DNS, porte, SNMP, adiacenze, SSH, Windows. Ogni passo può dipendere dall'esito dei precedenti.":
    'A sequence of steps designed by whoever runs the network: ping, DNS, ports, SNMP, adjacencies, SSH, Windows. Each step may depend on the outcome of the previous ones.',
  'VMware vSphere': 'VMware vSphere',
  'Virtualizzazione': 'Virtualisation',
  'ZStack': 'ZStack',
  'Security health': 'Security health',
  "Sta impiegando più del solito: il Control Plane potrebbe essere in riavvio. Riprovo da solo…": "It's taking longer than usual: the Control Plane may be restarting. Retrying by itself…",
  "quali servizi AI esterni (ChatGPT, Claude, Copilot…) vengono usati in rete e da quali asset, per la governance (shadow AI). Il dato viene dal nome TLS (SNI) che la sonda vede in ascolto passivo, confrontato con un catalogo di servizi AI: esce solo l’uso delle AI, mai l’intera navigazione. Il rapporto per asset può essere anonimizzato (PC e utente come pseudonimi) e due chiavi di disoffuscamento rivelano la macchina (livello 1) o anche l’utente (livello 2), dalla sezione Privacy. Add-on «Utilizzo AI».":
    "which external AI services (ChatGPT, Claude, Copilot…) are used on the network and by which assets, for governance (shadow AI). The data comes from the TLS name (SNI) the probe sees in passive listening, matched against a catalogue of AI services: only AI use leaves the probe, never the whole browsing history. The per-asset report can be anonymised (PC and user as pseudonyms) and two de-anonymisation keys reveal the machine (level 1) or the user too (level 2), from the Privacy section. «AI Usage» add-on.",
  'Aggiungi un servizio qui sopra.': "Add a service above.",
  'Anonimizza il rapporto (PC e utente come pseudonimi)': "Anonymise the report (PC and user as pseudonyms)",
  'Assistente di codice': "Coding assistant",
  'Carico…': "Loading…",
  'Catalogo AI': "AI catalogue",
  'Chat generativa': "Generative chat",
  'Chi usa le AI': "Who uses AI",
  'Chiave PC (facoltativa)': "PC key (optional)",
  'Chiave PC (livello 1)': "PC key (level 1)",
  'Chiave utente (facoltativa)': "User key (optional)",
  'Chiave utente (livello 2)': "User key (level 2)",
  'Generazione immagini': "Image generation",
  'I nomi (SNI) con cui le sonde riconoscono i servizi AI esterni — per il modulo Utilizzo AI': "The names (SNI) the probes use to recognise external AI services — for the AI Usage module",
  'I nomi (SNI) con cui le sonde riconoscono i servizi AI esterni. Vale per tutti i clienti. Il confronto è per dominio: «openai.com» riconosce anche «chat.openai.com».': "The names (SNI) the probes use to recognise external AI services. Applies to every customer. The match is by domain: «openai.com» also recognises «chat.openai.com».",
  'Nessun asset': "No asset",
  'Nessun traffico verso i servizi AI del catalogo nel periodo scelto.': "No traffic to the catalogue's AI services in the chosen period.",
  'Nessun uso di AI nel periodo scelto.': "No AI use in the chosen period.",
  'Nessun uso di AI rilevato': "No AI use detected",
  'PC in chiaro, utente anonimizzato: serve la chiave utente per il livello 2.': "PC shown, user anonymised: the user key is needed for level 2.",
  'Pattern (dominio)': "Pattern (domain)",
  'Per asset. Se il rapporto è anonimizzato, PC e utente sono pseudonimi: inserisci la chiave di disoffuscamento per rivelare la macchina (livello 1) o anche l’utente (livello 2).': "By asset. If the report is anonymised, PC and user are pseudonyms: enter the de-anonymisation key to reveal the machine (level 1) or the user too (level 2).",
  'Privacy': "Privacy",
  'Quali servizi AI esterni vengono usati in rete e da quali asset, da ciò che vedono sonde, agenti e flussi. Solo metadati: nessun contenuto, nessuna decrittazione.':
    'Which external AI services are used on the network and by which assets, from what probes, agents and flows see. Metadata only: no content, no decryption.',
  'Rapporto anonimizzato: PC e utente sono pseudonimi. Inserisci una chiave per rivelarli.': "Anonymised report: PC and user are pseudonyms. Enter a key to reveal them.",
  'Ricerca / risposte': "Search / answers",
  'Salva le chiavi': "Save the keys",
  'Servizi AI usati': "AI services used",
  'Servizi nel catalogo ({n})': "Services in the catalogue ({n})",
  'Solo un amministratore del tenant può cambiare le chiavi.': "Only a tenant administrator can change the keys.",
  'Togli le chiavi': "Remove the keys",
  'Usi': "Uses",
  'Utilizzo AI': "AI usage",
  'aggiunto': "added",
  'di serie': "built-in",
  'impostata — lascia vuoto per non cambiarla': "set — leave empty to keep it",
  '«Chi usa le AI» sfiora la sorveglianza dei lavoratori: puoi anonimizzare il rapporto e tenere due chiavi per rivelare chi serve. La chiave PC (livello 1) svela la macchina, la chiave utente (livello 2) anche l’utente. Le chiavi non si rileggono mai in chiaro.': "«Who uses AI» borders on employee monitoring: you can anonymise the report and keep two keys to reveal who you need. The PC key (level 1) reveals the machine, the user key (level 2) the user too. The keys are never read back in clear.",
  'Sicurezza': 'Security',
  'Sorgenti': 'Sources',
  'Asset segnalati': 'Flagged assets',
  'Nessun asset segnalato': 'No flagged asset',
  'Nessuna sorgente di sicurezza': 'No security source',
  'aggrega gli asset segnalati dalle sorgenti di sicurezza e le evidenze che ne determinano la segnalazione.': 'aggregates the assets flagged by the security sources and the evidence that triggers the flag.',
  'Il piano in uso non comprende nessuna sorgente di questo tipo.':
    'The current plan includes no source of this kind.',
  'Nessuna evidenza rilevata dalle sorgenti di sicurezza, l’assenza di segnalazioni non indica assenza di rischi.': 'No evidence detected by the security sources; the absence of reports does not mean the absence of risks.',
  'motivo non riportato': 'reason not reported',
  'indirizzo in blacklist secondo ntopng': 'address blacklisted per ntopng',
  'ha allarmi aperti su ntopng': 'has open alerts on ntopng',
  'Nessun rischio': 'No finding',
  'Nessuna raccolta ha ancora prodotto rischi. Il rapporto di ogni job dice se la valutazione è stata eseguita e con quale conoscenza: un elenco vuoto vale quanto vale quel rapporto.':
    'No collection has produced findings yet. Each job report says whether the assessment ran and with what knowledge: an empty list is worth exactly what that report is worth.',
  'Gravità': 'Severity',
  'Cosa': 'What',
  'Aperto dal': 'Open since',
  'Nascondi': 'Hide',
  'Da quale fatto': 'From which fact',
  'Regola': 'Rule',
  'Rimedio': 'Remedy',
  'Mostra anche i rischi accettati': 'Also show accepted risks',
  'rischio accettato': 'risk accepted',
  'Rischio accettato: {motivo}': 'Risk accepted: {motivo}',
  'torna visibile il {quando}': 'reappears on {quando}',
  'Revoca': 'Revoke',
  'Accetta': 'Accept',
  'Accetta il rischio, perché': 'Accept the risk, because',
  'Per quanti giorni': 'For how many days',
  'es. apparato del fornitore, sostituzione già pianificata':
    'e.g. vendor-owned device, replacement already scheduled',
  'critica': 'critical',
  'alta': 'high',
  'media': 'medium',
  'bassa': 'low',
  'telnet in ascolto': 'telnet listening',
  'FTP in ascolto': 'FTP listening',
  'rlogin in ascolto': 'rlogin listening',
  'rsh in ascolto': 'rsh listening',
  'VNC in ascolto': 'VNC listening',
  'Amministrazione web senza cifratura': 'Unencrypted web administration',
  '{valutati} asset valutati, {reperti} reperti':
    '{valutati} assets assessed, {reperti} findings',
  'Esegui valutazione': 'Run assessment',
  'Valutazione in corso…': 'Assessing…',
  'La valutazione è stata affidata a una sonda: i rischi compariranno qui appena la prende in carico.':
    'The assessment has been handed to a probe: findings will appear here as soon as it picks the job up.',
  'Valutato su {assessed} asset: {findings} rischi aperti, {closed} non più validi.':
    'Assessed {assessed} assets: {findings} findings open, {closed} no longer valid.',
  'Tutti i rischi del parco': 'All findings across the estate',
  'Catalogo delle vulnerabilità': 'Vulnerability catalogue',
  'Le vulnerabilità note con cui si confronta il software inventariato. Le scarica il Control Plane una volta per tutte le sonde: una sonda in una rete chiusa non ha uscita verso Internet, e proprio lì questa funzione serve di più.':
    'The known vulnerabilities the inventoried software is compared against. The Control Plane downloads them once for every probe: a probe on a closed network has no way out to the internet, and that is exactly where this matters most.',
  'Il catalogo non è mai stato importato: il confronto con i CVE non produce niente, e «nessuna vulnerabilità» non vorrebbe dire «nessun problema».':
    'The catalogue has never been imported: the CVE comparison produces nothing, and “no vulnerabilities” would not mean “no problems”.',
  'Aggiornato al': 'Updated on',
  'Voci nel catalogo': 'Entries in the catalogue',
  'L’ultima importazione è fallita: {errore}': 'The last import failed: {errore}',
  'Importazione in corso. Sono centinaia di megabyte da scaricare: dura qualche minuto, e questa pagina si aggiorna da sola.':
    'Import running. There are hundreds of megabytes to download: it takes a few minutes, and this page refreshes itself.',
  'Aggiorna il catalogo': 'Update the catalogue',
  'Importa il catalogo': 'Import the catalogue',
  'Avviata.': 'Started.',
  'pacchetto senza nome': 'package without a name',
  'senza una versione che corregge (non ancora corretta nella distribuzione)':
    'no fixing version (not yet fixed in the distribution)',
  'senza la release della distribuzione': 'no distribution release',
  'Scartate': 'Discarded',
  'avviso di manutenzione, non di sicurezza': 'maintenance advisory, not a security one',
  '{cve} — {pacchetto}': '{cve} — {pacchetto}',
  '{cve} — {pacchetto} e altri {altri_pacchetti}':
    '{cve} — {pacchetto} and {altri_pacchetti} more',
  'Mostrati i {mostrati} più gravi su {totale}.':
    'Showing the {mostrati} most severe of {totale}.',
  'Vedili tutti': 'See them all',
  'su {asset} asset': 'across {asset} assets',
  'Cosa fare': 'What to do',
  'Dettagli': 'Details',
  '— chiude {chiude} rischi': '— closes {chiude} findings',
  'Aggiorna {n} pacchetti': 'Update {n} packages',
  'Da valutare a mano: non c’è un rimedio unico':
    'To be assessed by hand: there is no single remedy',
  'I rischi, per chi vuole controllare': 'The findings, for anyone who wants to check',
  '{cve} — {pacchetto} · sfruttata attivamente (CISA KEV)': '{cve} — {pacchetto} · actively exploited (CISA KEV)',
  '{cve} — {pacchetto} e altri {altri_pacchetti} · sfruttata attivamente (CISA KEV)':
    '{cve} — {pacchetto} and {altri_pacchetti} more · actively exploited (CISA KEV)',
  'Aggiorna {n} pacchetti con vulnerabilità già sfruttate':
    'Update {n} packages with already-exploited vulnerabilities',
  'catalogo delle vulnerabilità sfruttate non scaricato':
    'catalogue of exploited vulnerabilities not downloaded',
  'sfruttate (KEV)': 'exploited (KEV)',
  'Per ecosistema': 'By ecosystem',
  'debian': 'debian',
  'ubuntu': 'ubuntu',
  'alma': 'alma',
  'rocky': 'rocky',
  'alpine': 'alpine',
  'Rischio': 'Risk',
  'Fascia': 'Band',
  'Valutato': 'Assessed',
  'critico': 'critical',
  'alto': 'high',
  'medio': 'medium',
  'basso': 'low',
  'nessun rilievo': 'nothing found',
  '{n} vulnerabilità già sfruttate attivamente (CISA KEV)':
    '{n} vulnerabilities already actively exploited (CISA KEV)',
  'sistema fuori supporto con servizi in ascolto': 'unsupported system with services listening',
  '{c} vulnerabilità critiche': '{c} critical vulnerabilities',
  'sistema fuori supporto': 'unsupported system',
  '{a} vulnerabilità gravi': '{a} high-severity vulnerabilities',
  '{p} servizi con credenziali in chiaro': '{p} services with cleartext credentials',
  '{x} rischi di gravità minore': '{x} lower-severity findings',
  'Fuori supporto': 'Out of support',
  'Prodotti senza più disponibilità di aggiornamenti di sicurezza, da sostituire.': 'Products with no security updates available any more, to be replaced.',
  'Ciclo': 'Cycle',
  'Fuori supporto dal': 'Out of support since',
  'Su quanti asset': 'On how many assets',
  'sistema': 'operating system',
  'e altri {altri}': 'and {altri} more',
  '{pacchetto} {ciclo} è fuori supporto': '{pacchetto} {ciclo} is out of support',
  'Il ciclo {ciclo} non riceve più correzioni dal {fine_leggibile}: le vulnerabilità scoperte da allora restano aperte, e non arriverà un aggiornamento che le chiuda':
    'Cycle {ciclo} has received no fixes since {fine_leggibile}: vulnerabilities found after that stay open, and no update will close them',
  'Passare a un ciclo ancora supportato': 'Move to a still-supported cycle',
  'cicli di vita del software': 'software lifecycles',
  'ciclo di vita non scaricato': 'lifecycle not downloaded',
  'cicli di vita del software non scaricati': 'software lifecycles not downloaded',
  'Correzioni di Windows':
    'Windows updates',
  'Il bollettino mensile di Microsoft: per ogni versione di Windows, a che livello va portata e con quale KB. Su Windows il sistema non è un pacchetto, quindi il confronto sui pacchetti non lo raggiunge — senza questo catalogo, di un Windows non si può dire se è aggiornato.':
    'Microsoft\'s monthly bulletin: for each version of Windows, which level it must be brought to and with which KB. On Windows the operating system is not a package, so the package comparison never reaches it — without this catalogue, there is no way to say whether a Windows machine is up to date.',
  'Mai importato: di ogni macchina Windows il rapporto dichiara la lacuna invece di dire che è aggiornata.':
    'Never imported: for every Windows machine the report declares the gap instead of claiming the machine is up to date.',
  'Livelli di build':
    'Build levels',
  'dal bollettino {mese}':
    'from the {mese} bulletin',
  'Importazione in corso: due anni di bollettini sono qualche centinaio di megabyte. Questa pagina si aggiorna da sola.':
    'Import running: two years of bulletins are a few hundred megabytes. This page refreshes on its own.',
  'Aggiorna le correzioni di Windows':
    'Update the Windows fixes',
  'Importa le correzioni di Windows':
    'Import the Windows fixes',
  'Valuta e controlla i servizi':
    'Assess and check the services',
  'Controllo dei servizi in corso…':
    'Checking the services…',
  'Apre una connessione TLS verso le porte cifrate di questo asset: certificati scaduti, TLS obsoleto, cifrari deboli. Nessuna credenziale provata, nessun dato inviato.':
    'Opens a TLS connection to this asset’s encrypted ports: expired certificates, obsolete TLS, weak ciphers. No credentials tried, no data sent.',
  'Valutazione con i controlli sui servizi affidata a una sonda — è l’unica che arriva su quella rete. I rischi compariranno qui appena la prende in carico.':
    'Assessment with the service checks handed to a probe — it is the only one that reaches that network. The findings will appear here as soon as it picks the job up.',
  '{pacchetto} è fuori supporto':
    '{pacchetto} is out of support',
  '{pacchetto} è indietro di {indietro} versioni':
    '{pacchetto} is {indietro} versions behind',
  'Prodotti senza voci':
    'Products with no entries',
  'Un prodotto a zero voci non vuol dire «nessuna vulnerabilità»: può voler dire che l’identificativo con cui lo cerchiamo non è più quello giusto.':
    'A product with zero entries does not mean “no vulnerabilities”: it may mean the identifier we search it by is no longer the right one.',
  'Importazione in corso. Il NVD limita le richieste, quindi ci vuole qualche minuto: questa pagina si aggiorna da sola.':
    'Import running. The NVD rate-limits requests, so it takes a few minutes: this page refreshes on its own.',
  'Vulnerabilità dal NVD: software di terzi e apparati':
    'NVD vulnerabilities: third-party software and devices',
  'Due cose in una fonte sola: i difetti noti dei programmi di terzi che girano su Windows — 7-Zip, VLC, PuTTY, Adobe Reader — e il firmware degli apparati di rete, FortiOS, PAN-OS, Fireware. Né il catalogo delle distribuzioni Linux né il bollettino Microsoft li coprono.':
    'Two things from one source: the known flaws of third-party programs running on Windows — 7-Zip, VLC, PuTTY, Adobe Reader — and the firmware of network devices, FortiOS, PAN-OS, Fireware. Neither the Linux distribution catalogue nor the Microsoft bulletin covers them.',
  'Mai importato: né dei programmi di terzi su Windows né del firmware degli apparati si può dire se hanno vulnerabilità note.':
    'Never imported: nothing can be said about known vulnerabilities in third-party Windows programs or in device firmware.',
  '{software} per il software, {apparati} per il firmware degli apparati':
    '{software} for software, {apparati} for device firmware',
  'Aggiorna le vulnerabilità dal NVD':
    'Update the NVD vulnerabilities',
  'Importa le vulnerabilità dal NVD':
    'Import the NVD vulnerabilities',
  'Filtra il menu':
    'Filter the menu',
  'Nessuna voce del menu corrisponde.':
    'No menu entry matches.',
  'Sezioni della pagina':
    'Page sections',
  'Backup':
    'Backup',
  'Ciclo di vita':
    'Lifecycle',
  'Attributi':
    'Attributes',
  'Collegamenti':
    'Relations',
  'Attività':
    'Activity',
  'Agenti':
    'Agents',
  'Utenti abilitati':
    'Enabled users',
  'Abilita utenza':
    'Enable an account',
  'Accesso aziendale':
    'Corporate sign-in',
  'Oggetti':
    'Objects',
  'Esecuzioni':
    'Runs',
  'Discovery':
    'Discovery',
  'Esclusi dalla mappa:':
    'Excluded from the map:',
  'Riporta sulla mappa':
    'Bring back to the map',
  'Ripristina tutti':
    'Restore all',
  'Escludi dalla mappa':
    'Exclude from the map',
  'Crea un business service da questa mappa':
    'Create a business service from this map',
  'Gli ingressi saranno i {quanti} nodi visibili, ognuno con il suo indirizzo e la porta su cui riceve più traffico.':
    'The entry points will be the {quanti} visible nodes, each with its address and the port receiving the most traffic.',
  'I {esclusi} nodi esclusi restano fuori.':
    'The {esclusi} excluded nodes stay out.',
  'Il servizio resta modificabile in ogni momento dalla pagina Business service.':
    'The service stays editable at any time from the Business service page.',
  'Creato dalla mappa di correlazione di {seed} (finestra {finestra})':
    'Created from the correlation map of {seed} (window {finestra})',
  'Crea il business service':
    'Create the business service',
  'Creazione…':
    'Creating…',
  'Business service creato':
    'Business service created',
  'Aprilo in Business service per modificarlo →':
    'Open it in Business service to edit it →',
  'Critica':
    'Critical',
  'La mappa ha {quanti} nodi e un servizio ne accetta al massimo {massimo}: escludi quelli che non c’entrano (Maiusc+clic), o riduci livelli e vicini per nodo.':
    'The map has {quanti} nodes and a service accepts at most {massimo}: exclude the ones that do not belong (Shift+click), or reduce levels and neighbours per node.',
  'Personalizza':
    'Customise',
  'Fine':
    'Done',
  'Nessun apparato di rete in CMDB.':
    'No network device in the CMDB.',
  'Backup recente (≤{giorni} gg)':
    'Recent backup (≤{giorni} days)',
  'Senza backup':
    'No backup',
  'Nessuna valutazione di sicurezza ancora eseguita.':
    'No security assessment run yet.',
  'Coperti da contratto':
    'Covered by contract',
  'Senza copertura':
    'Not covered',
  '{quanti} CI osservati non sono ancora registrati come beni.':
    '{quanti} observed CIs are not yet registered as assets.',
  'Comunicazione scelta:':
    'Selected communication:',
  'Escludi la comunicazione':
    'Exclude the communication',
  'Escludi':
    'Exclude',
  'Deseleziona':
    'Deselect',
  'Da':
    'From',
  'A':
    'To',
  'Mappa':
    'Map',
  'Apri la mappa di correlazione partendo da questo indirizzo':
    'Open the correlation map starting from this address',
  'Dettaglio':
    'Detail',
  'Esplora da qui':
    'Explore from here',
  '{quante} comunicazioni selezionate.':
    '{quante} communications selected.',
  'Escludi le selezionate':
    'Exclude the selected ones',
  'Annulla la selezione':
    'Clear the selection',
  'Mostra in mappa:':
    'Show on the map:',
  'Una mappa per indirizzo: la correlazione parte da un seme solo.':
    'One map per address: the correlation starts from a single seed.',
  'Guida':
    'Guide',
  'Come funziona questa pagina':
    'How this page works',
  'Che cosa fa ogni area della console, e in che ordine si usa':
    'What each area of the console does, and in what order to use it',
  'Primi passi':
    'First steps',
  'CENSO esegue discovery e aggiornamento automatico dell’inventario tramite sonde distribuite nella rete; il cambio tenant mantiene la sezione corrente, azzera i filtri e chiude l’eventuale oggetto aperto oppure, se la sezione non è disponibile, reindirizza alla Home.': 'CENSO runs discovery and keeps the inventory updated automatically through probes distributed across the network; switching tenant keeps the current section, clears the filters and closes any open object, or, if the section is not available, redirects to the Home.',
  'genera un token di arruolamento e installa la prima sonda nella rete da osservare.':
    'generate an enrolment token and install the first probe in the network to observe.',
  'inserisci le credenziali SNMP, SSH o WinRM con cui le sonde possono interrogare gli apparati.':
    'enter the SNMP, SSH or WinRM credentials the probes can use to query the devices.',
  'avvia la prima discovery su una subnet. I risultati compaiono in Configuration Item.':
    'start the first discovery on a subnet. The results appear under Configuration Items.',
  'pianifica le scansioni ricorrenti: l’inventario resta aggiornato senza che nessuno lo rilanci.':
    'schedule the recurring scans: the inventory stays current without anyone re-running it.',
  'Ogni cosa scoperta diventa un Configuration Item: server, apparato, stampante, macchina virtuale. La scheda di un CI raccoglie tutto quello che se ne sa — anagrafica, interfacce, software, sicurezza, storico — con un indice in alto per saltare alla sezione.':
    'Everything discovered becomes a Configuration Item: server, device, printer, virtual machine. A CI page gathers everything known about it — identity, interfaces, software, security, history — with an index at the top to jump to a section.',
  'l’elenco completo, filtrabile. È la fotografia tecnica: la scrivono le scansioni. Seleziona più righe per agire in blocco: tag, «Dichiara dismesso», classe, «Togli dalla CMDB». Gli amministratori hanno «Trova doppioni»: l’anteprima dei CI che sono lo stesso oggetto, con la prova, e l’unione dei gruppi scelti. Con «Colonne» scegli quali colonne vedere e ne aggiungi altre (modello, seriale, sistema operativo…); quando la tabella non entra, frecce e ombre ai bordi dicono che continua.':
    'the full, filterable list. It is the technical snapshot: the scans write it. Select several rows to act in bulk: tags, «Declare retired», class, «Remove from the CMDB». Administrators have «Find duplicates»: a preview of the CIs that are the same object, with the evidence, and merging of the chosen groups. With «Columns» you choose which columns to see and add others (model, serial, operating system…); when the table does not fit, arrows and shadows at the edges show that it continues.',
  'il registro dei beni: valore, stato, centro di costo. Si popola dai CI («Registra come bene») e vive anche quando la macchina viene spenta.':
    'the asset register: value, status, cost centre. It is populated from CIs (“Register as asset”) and lives on even when the machine is switched off.',
  'la mappa dei collegamenti fisici e logici. Centrata su un nodo, ogni linea dice le porte su cui si parlano; i nodi si trascinano, la rotella ingrandisce.':
    'the map of physical and logical links. Centred on a node, every line says the ports they talk on; nodes can be dragged, the wheel zooms.',
  'Le sonde raccolgono i flussi di rete (NetFlow/IPFIX): chi parla con chi, su quale porta, con quanto volume. Tre viste, per tre domande diverse.':
    'Probes collect network flows (NetFlow/IPFIX): who talks to whom, on which port, with how much volume. Three views, for three different questions.',
  'il registro del traffico, filtrabile. La domanda di tutti i giorni.':
    'the traffic log, filterable. The everyday question.',
  'gli indirizzi e le porte che ricevono connessioni — i servizi esposti della rete.':
    'the addresses and ports receiving connections — the exposed services of the network.',
  'Un business service è un gruppo di macchine e porte che insieme erogano qualcosa: «il gestionale», «la posta». Il flusso consigliato parte dal traffico:':
    'A business service is a group of machines and ports that together deliver something: “the ERP”, “the mail”. The recommended flow starts from the traffic:',
  'La valutazione rilegge quello che è già in inventario — porte, sistema, software — e lo confronta con i cataloghi pubblici: vulnerabilità note, aggiornamenti Windows mancanti, software a fine vita, firmware degli apparati.':
    'The assessment re-reads what is already in the inventory — ports, system, software — and compares it with the public catalogues: known vulnerabilities, missing Windows updates, end-of-life software, device firmware.',
  'Sulla scheda di un CI: «Esegui valutazione» rilegge l’inventario; «Valuta e controlla i servizi» apre anche connessioni TLS verso l’asset — va a una sonda, perché dalla cloud la rete non risponde.':
    'On a CI page: “Run assessment” re-reads the inventory; “Assess and check the services” also opens TLS connections to the asset — it goes to a probe, because the network does not answer from the cloud.',
  'I cataloghi si importano una volta e per tutti i clienti, dalla console di amministrazione: OSV per Linux, il bollettino Microsoft per Windows, il NVD per software di terzi e firmware.':
    'The catalogues are imported once, for every customer, from the administration console: OSV for Linux, the Microsoft bulletin for Windows, the NVD for third-party software and firmware.',
  'la copia delle configurazioni di switch e firewall, salvata a ogni scansione quando cambia. La domanda a cui risponde è «cosa è cambiato stanotte» — e il widget in dashboard dice chi non ha nessun backup.':
    'the copy of switch and firewall configurations, saved at each scan when it changes. The question it answers is “what changed last night” — and the dashboard widget says who has no backup at all.',
  'per le macchine che nessuna scansione raggiunge — un server in una cloud altrui, un portatile. L’agente si installa con un comando, consegna l’inventario a intervalli e si aggiorna da solo.':
    'for the machines no scan reaches — a server in someone else’s cloud, a laptop. The agent installs with one command, delivers the inventory at intervals and updates itself.',
  'chi entra, con quale ruolo, e l’accesso con il provider aziendale.':
    'who can sign in, with which role, and corporate-provider sign-in.',
  'le regole con cui la piattaforma classifica gli oggetti. Una correzione fatta a mano su un CI resiste alle scansioni successive.':
    'the rules the platform classifies objects with. A manual correction on a CI survives later scans.',
  'licenza, filtri del traffico, password.':
    'licence, traffic filters, password.',
  'Quadro':
    'Overview',
  'Rischio del parco':
    'Estate risk',
  'Sfruttate attivamente (CISA KEV)':
    'Actively exploited (CISA KEV)',
  'Vulnerabilità che qualcuno sta già usando: prima di tutto il resto':
    'Vulnerabilities someone is already using: before everything else',
  'Rischi critici e alti':
    'Critical and high findings',
  'Su {totale} rischi aperti':
    'Out of {totale} open findings',
  'Ultima valutazione':
    'Last assessment',
  'Il silenzio vale quanto è fresco':
    'Silence is worth only as much as it is fresh',
  'Da dove cominciare':
    'Where to start',
  'Azioni correttive, da applicare per risolvere i rischi rilevati per singolo asset, ordinate per priorità.': 'Corrective actions to apply to resolve the risks found, per asset, ordered by priority.',
  'Nessun rischio corrisponde al filtro.':
    'No finding matches the filter.',
  'Con più sedi conviene una sonda per sede, riunite in un gruppo (Gruppi di sonde): i job si assegnano al gruppo, e la sonda giusta se li prende da sola.':
    'With several sites, one probe per site works best, gathered in a group (Probe groups): jobs are assigned to the group, and the right probe picks them up on its own.',
  'Se le credenziali cambiano da zona a zona, componi un gruppo di credenziali: la sonda le prova in ordine finché una risponde, e il rapporto dice quale ha funzionato.':
    'If credentials differ from zone to zone, compose a credential group: the probe tries them in order until one answers, and the report says which one worked.',
  'definisci le zone su cui lavorare — subnet, sedi, ambienti. Sono il «dove», e si preparano prima di decidere il «come».':
    'define the zones to work on — subnets, sites, environments. They are the “where”, and they come before deciding the “how”.',
  'componi i passi di una scansione: cosa provare, in che ordine, con quali credenziali. Un job si avvia da un piano.':
    'compose the steps of a scan: what to try, in what order, with which credentials. A job starts from a plan.',
  'Il rapporto di ogni job è la prima cosa da leggere quando qualcosa non torna: dice cosa ha trovato, cosa ha scartato e perché — comprese le cose che non ha potuto guardare. Un risultato vuoto senza leggere il rapporto non è un risultato: è un’assenza di risposta.':
    'The report of each job is the first thing to read when something looks wrong: it says what was found, what was discarded and why — including what it could not look at. An empty result without reading the report is not a result: it is an absence of an answer.',
  'Un CI non si «chiude» a mano: se il fatto sparisce dalla rete, sparisce dalla scheda alla scansione successiva. Quello che invece si decide a mano — la classe, per esempio — resiste alle scansioni.':
    'A CI is not “closed” by hand: if the fact disappears from the network, it disappears from the page at the next scan. What is decided by hand — the class, for instance — survives the scans.',
  'Sulla scheda di un CI: «Cambia classe» corregge una classificazione sbagliata, e la correzione dichiara chi l’ha fatta e perché; lo «Storico delle modifiche» dice cosa è cambiato scansione dopo scansione — è la risposta a «da quando è così?».':
    'On a CI page: “Change class” corrects a wrong classification, and the correction declares who made it and why; the “Change history” says what changed scan after scan — it answers “since when has it been like this?”.',
  'La differenza è il ciclo di vita: il CI è ciò che si osserva, l’asset è ciò che si possiede. Un portatile rubato non è più un CI, ma resta un asset con uno stato.':
    'The difference is the lifecycle: the CI is what is observed, the asset is what is owned. A stolen laptop is no longer a CI, but it remains an asset with a status.',
  'gli estratti pronti da consegnare — attraversano inventario, beni, contratti e discovery.':
    'ready-to-hand-over extracts — they cut across inventory, assets, contracts and discovery.',
  'La tabella MAC dice chi è collegato dove: è la domanda che la mappa non sa raccontare.':
    'The MAC table says who is attached where: the question the map cannot tell.',
  'I gesti sulla mappa della topologia:':
    'The gestures on the topology map:',
  'clic su un nodo: lo mette al centro e mostra solo il suo intorno, con la profondità scelta;':
    'click a node: it becomes the centre and only its neighbourhood is shown, at the chosen depth;',
  'trascinare un nodo lo fissa dove l’hai messo — il contorno tratteggiato lo dichiara — e la disposizione automatica non lo sposta più;':
    'dragging a node pins it where you put it — the dashed outline declares it — and the automatic layout no longer moves it;',
  'clic su una linea: la scheda del collegamento, con porte, protocolli e volumi;':
    'click a line: the link’s detail, with ports, protocols and volumes;',
  'i filtri sopra la mappa restringono per famiglia di relazione, tipo di CI, porta e protocollo: «chi parla su 445» è un filtro, non una ricerca a occhio.':
    'the filters above the map narrow by relation family, CI type, port and protocol: “who talks on 445” is a filter, not an eyeball search.',
  'Le porte alte con un solo mittente sono nascoste: sono quasi sempre il ritorno di una connessione in uscita, non un servizio.':
    'High ports with a single sender are hidden: they are almost always the return leg of an outgoing connection, not a service.',
  'chi sta mandando i flussi e quanto: la pagina da aprire quando i numeri sembrano sbagliati. Nella stessa tabella ci sono anche le sonde che catturano dal filo, con protocollo «Sonda».':
    'who is sending the flows and how much: the page to open when the numbers look wrong. The same table also lists the probes capturing from the wire, with protocol «Probe».',
  'Sulla mappa di correlazione, tre parametri decidono cosa vedi:':
    'On the correlation map, three parameters decide what you see:',
  'Livelli: quanti salti dal seme. Uno sono i vicini, due i vicini dei vicini; oltre il terzo è quasi sempre l’intera rete.':
    'Levels: how many hops from the seed. One is the neighbours, two the neighbours’ neighbours; beyond the third it is almost always the whole network.',
  'Vicini per nodo: il tetto per non affogare — si mostrano i più voluminosi, e il segno ⁺ dichiara che c’è dell’altro. Una mappa che tronca in silenzio non è incompleta: è falsa.':
    'Neighbours per node: the cap that keeps you afloat — the most voluminous are shown, and the ⁺ mark declares there is more. A map that truncates silently is not incomplete: it is false.',
  'Verso: chi gli ha parlato (i suoi client), con chi ha parlato (le sue dipendenze), o entrambi.':
    'Direction: who talked to it (its clients), whom it talked to (its dependencies), or both.',
  'In giallo gli indirizzi che la CMDB non conosce. Non è un difetto della mappa: trovarli è uno dei motivi per cui il traffico si raccoglie.':
    'In yellow, the addresses the CMDB does not know. It is not a flaw of the map: finding them is one of the reasons the traffic is collected.',
  'Il servizio si definisce dai suoi punti di ingresso; la mappa si ricava dal traffico osservato, seguendo i salti fino alla profondità scelta (max depth). Due salti bastano per «front-end → applicativo → database»; su una rete piatta alzarlo include mezza infrastruttura.':
    'A service is defined by its entry points; its map is derived from the observed traffic, following hops up to the chosen depth (max depth). Two hops cover “front end → application → database”; on a flat network raising it pulls in half the infrastructure.',
  'La criticità serve a ordinare le cure: quando un asset compare in più servizi, vale il più critico dei due.':
    'Criticality orders the care: when an asset appears in several services, the most critical one wins.',
  'si apre con il quadro — la ruota del rischio e le vulnerabilità già sfruttate attivamente (CISA KEV) — poi «Cosa fare», gli asset da cui cominciare, e i rischi uno per uno con la prova.':
    'opens with the overview — the risk wheel and the vulnerabilities already actively exploited (CISA KEV) — then “What to do”, the assets to start from, and the findings one by one with their evidence.',
  'I controlli sui servizi sono una stretta di mano e basta: nessuna credenziale provata, nessun dato inviato.':
    'The service checks are a handshake and nothing more: no credentials tried, no data sent.',
  'Come leggere quello che vedi:':
    'How to read what you see:',
  '«Sfruttata attivamente (CISA KEV)» batte qualunque punteggio: non è una previsione di gravità, è che qualcuno la sta usando adesso. È il rischio di stanotte, non di prima o poi.':
    '“Actively exploited (CISA KEV)” beats any score: it is not a severity forecast, it means someone is using it right now. It is tonight’s finding, not sooner-or-later’s.',
  'Ogni rischio porta la prova: da quale fatto viene, con quali numeri. Un rischio che non si può verificare è un’opinione — e si può controllare in un minuto invece di crederci.':
    'Every finding carries its evidence: which fact it comes from, with which numbers. A finding that cannot be verified is an opinion — and it can be checked in a minute instead of being believed.',
  'Un rischio si può accettare, con un motivo e una scadenza obbligatoria: senza scadenza sarebbe un modo di nascondere le cose per sempre. Alla scadenza il rischio ricompare.':
    'A risk can be accepted, with a reason and a mandatory expiry: without an expiry it would be a way of hiding things forever. At expiry the finding comes back.',
  'Le lacune si dichiarano: «di questa macchina non conosco il livello di aggiornamento» vale più di un silenzio che sembra un via libera. Un elenco vuoto vale quanto il rapporto che lo accompagna.':
    'Gaps are declared: “I do not know this machine’s patch level” is worth more than a silence that looks like a clean bill. An empty list is worth as much as the report that comes with it.',
  'Ogni apparato ha la sua storia di revisioni: si vede cosa è cambiato fra due salvataggi, e quando. Una configurazione che cambia senza che nessuno l’abbia toccata è la prima cosa da guardare.':
    'Each device has its revision history: you see what changed between two snapshots, and when. A configuration that changes with nobody touching it is the first thing to look at.',
  'Si salva solo quando cambia: cento notti uguali sono una revisione, non cento.':
    'It is saved only when it changes: a hundred identical nights are one revision, not a hundred.',
  'Sulla scheda del CI di un apparato, il riquadro del backup dice subito se è recente e quante versioni esistono: verde se fresco.':
    'On a device’s CI page, the backup box says at once whether it is recent and how many versions exist: green when fresh.',
  'L’installazione è un comando da copiare, valido per una finestra di tempo: scaduta quella, se ne genera un altro. Il comando contiene già l’identità dell’agente.':
    'Installation is a command to copy, valid for a window of time: once expired, another one is generated. The command already carries the agent’s identity.',
  'L’aggiornamento è automatico e avviene a fine consegna: se fallisce, l’inventario è già arrivato. Si può spegnere per agente, dalla console.':
    'Updating is automatic and happens after delivery: if it fails, the inventory has already arrived. It can be switched off per agent, from the console.',
  'Su Windows l’agente legge anche il livello di aggiornamento del sistema dal registro: è il dato che nessuna CMDB esterna può portare, e che serve alla valutazione di sicurezza.':
    'On Windows the agent also reads the system patch level from the registry: the one piece of data no external CMDB can bring, and the one the security assessment needs.',
  'Il widget dei backup ha la fetta che conta: gli apparati senza nessun backup, che nella pagina delle configurazioni sono invisibili per definizione.':
    'The backup widget has the slice that matters: the devices with no backup at all, which are invisible by definition on the configurations page.',
  'Sonar e radar sono due modi di guardare il parco a colpo d’occhio: si accendono a mano, perché una cosa che lampeggia non deve essere il primo saluto della giornata.':
    'Sonar and radar are two ways of glancing at the estate: they are switched on by hand, because a blinking thing must not be the first greeting of the day.',
  'Su ogni pagina, accanto al percorso in alto, «Come funziona questa pagina» apre questa guida alla sezione giusta.':
    'On every page, next to the path at the top, “How this page works” opens this guide at the right section.',
  'Tre ruoli: l’amministratore gestisce utenti, credenziali e sonde; l’operatore avvia le discovery e lavora sui dati; la sola lettura consulta e basta.':
    'Three roles: the administrator manages users, credentials and probes; the operator starts discoveries and works on the data; read-only just reads.',
  'Prima di salvare una regola, l’anteprima dice quanti CI cambierebbero classe: una regola scritta male riscrive l’inventario di tutti.':
    'Before saving a rule, the preview says how many CIs would change class: a badly written rule rewrites everyone’s inventory.',
  'lo scambio con gli altri sistemi — l’ITSM, una CMDB esistente: cosa sincronizzare, in che direzione, ogni quanto. Ogni esecuzione lascia il suo rapporto.':
    'the exchange with other systems — the ITSM, an existing CMDB: what to sync, in which direction, how often. Every run leaves its report.',
  'quanto si sta consumando rispetto alla licenza, prima che lo dica un limite.':
    'how much is being consumed against the licence, before a limit says it.',
  'I filtri del traffico decidono cosa si conserva dei flussi: si impostano una volta e valgono per tutta l’organizzazione.':
    'The traffic filters decide what is kept of the flows: set once, they apply to the whole organisation.',
  'CENSO è un prodotto DiscoveryGuard':
    'CENSO is a DiscoveryGuard product',
  'DORA Compliance':
    'DORA Compliance',
  'Conformità al Regolamento UE 2022/2554 (DORA), valutata sui dati raccolti, con evidenza esplicita dei requisiti non misurabili.': 'Compliance with EU Regulation 2022/2554 (DORA), assessed on the collected data, with explicit evidence of the non-measurable requirements.',
  'I requisiti DORA non verificabili tramite l’inventario richiedono evidenze organizzative, tra cui gestione degli incidenti ICT, test di resilienza e registro dei fornitori. Il report li distingue dai controlli misurati, evitando di attribuire conformità in assenza di evidenze.': 'DORA requirements that cannot be verified through the inventory require organizational evidence, including ICT incident management, resilience testing and the register of providers. The report distinguishes them from the measured controls, avoiding attributing compliance in the absence of evidence.',
  'lo stesso motore, sul regolamento UE 2022/2554 per le entità finanziarie. Cambiano gli articoli citati e pesa di più il «non misurato»: incidenti, test di resilienza e registro dei fornitori vanno coperti altrove, e la pagina lo dichiara.':
    'the same engine, on EU Regulation 2022/2554 for financial entities. The cited articles change and the “not measured” weighs more: incidents, resilience testing and the register of providers must be covered elsewhere, and the page says so.',
  'Governance e organizzazione interna':
    'Governance and internal organisation',
  'Quadro documentato di gestione del rischio ICT':
    'Documented ICT risk management framework',
  'Continuità operativa ICT e piani di risposta':
    'ICT business continuity and response plans',
  'Apprendimento dagli incidenti e revisioni':
    'Learning from incidents and reviews',
  'Gestione e segnalazione degli incidenti ICT':
    'Management and reporting of ICT incidents',
  'Test di resilienza operativa digitale (incluso il TLPT)':
    'Digital operational resilience testing (including TLPT)',
  'Rischio ICT da terze parti e registro dei contratti':
    'Third-party ICT risk and the register of contracts',
  'Condivisione delle informazioni sulle minacce':
    'Sharing of threat information',
  'Dettaglio reputazione':
    'Reputation detail',
  'Reputazione controllata al':
    'Reputation checked at',
  '«Nuovo CI» crea a mano la macchina che nessuna scansione raggiunge. Dichiara seriale, indirizzo o nome host: sono le chiavi con cui la discovery, quando arriverà, riconoscerà l’oggetto invece di duplicarlo.':
    '“New CI” manually creates the machine no scan reaches. Declare a serial, address or hostname: they are the keys discovery will use, when it arrives, to recognise the object instead of duplicating it.',
  'Indirizzo MAC':
    'MAC address',
  'Indizio di virtualizzazione':
    'Virtualisation hint',
  'Account':
    'Account',
  'Costo del mese':
    'Month-to-date cost',
  'Valuta dei costi':
    'Cost currency',
  'Costi per servizio':
    'Costs by service',
  'Costi: inizio periodo':
    'Costs: period start',
  'Costi: fine periodo':
    'Costs: period end',
  'indirizzo della piattaforma':
    'platform address',
  'assegna un indirizzo dedicato':
    'assign a dedicated address',
  'Prerequisiti: DNS wildcard del dominio base verso il control plane, e certificato da riemettere includendo il nome nuovo (Amministrazione → Piattaforma).':
    'Prerequisites: wildcard DNS for the base domain pointing to the control plane, and the certificate reissued to include the new name (Administration → Platform).',
  'Primo utente':
    'First user',
  'Il primo amministratore del tenant.':
    'The tenant’s first administrator.',
  'email':
    'email',
  'nome (facoltativo)':
    'name (optional)',
  'Crea amministratore':
    'Create administrator',
  'Posta in uscita (SMTP)':
    'Outgoing mail (SMTP)',
  'Da qui escono inviti e avvisi. Si usa il relay dell’organizzazione: la posta parte dal vostro dominio, con le vostre policy.':
    'Invitations and notices leave from here. It uses your organisation’s relay: mail goes out from your domain, with your policies.',
  'Relay SMTP':
    'SMTP relay',
  'Cifratura':
    'Encryption',
  'nessuna — solo relay interni':
    'none — internal relays only',
  'Utente':
    'User',
  'salvata — lascia vuoto per non cambiarla':
    'saved — leave empty to keep it',
  'Mittente':
    'Sender',
  'Nome del mittente':
    'Sender name',
  'indirizzo per la prova':
    'address for the test',
  'Invia messaggio di prova':
    'Send test message',
  'Prima salva la configurazione':
    'Save the configuration first',
  'Configurazione salvata.':
    'Configuration saved.',
  'Messaggio di prova consegnato al relay: controlla la casella.':
    'Test message handed to the relay: check the mailbox.',
  'smtp.azienda.it':
    'smtp.company.com',
  'censo@azienda.it':
    'censo@company.com',
  'Indirizzi o subnet (CIDR), separati da virgola':
    'Addresses or subnets (CIDR), comma-separated',
  '{creati} indirizzi creati, {esistenti} già presenti.':
    '{creati} addresses created, {esistenti} already present.',
  'Una subnet diventa UN CI, con la tabella dei suoi indirizzi sulla scheda (fino a una /24): la verifica di reputazione la riempie indirizzo per indirizzo. Un indirizzo scritto da solo diventa un CI suo.':
    'A subnet becomes ONE CI, with the table of its addresses on its sheet (up to a /24): the reputation check fills it address by address. An address written on its own becomes its own CI.',
  'Dichiara':
    'Declare',
  'Togli dalla CMDB':
    'Remove from the CMDB',
  '{tolti} voci tolte dalla CMDB.':
    '{tolti} entries removed from the CMDB.',
  'Indirizzi della subnet':
    'Subnet addresses',
  'La reputazione si verifica indirizzo per indirizzo, su più fonti; il rischio — se qualcosa è segnalato — è uno, su questa subnet.':
    'Reputation is checked address by address, across several sources; the finding — if anything is flagged — is one, on this subnet.',
  'Mai verificata: premi «Verifica reputazione» per riempire la tabella.':
    'Never checked: press “Check reputation” to fill the table.',
  'Subnet (CIDR)':
    'Subnet (CIDR)',
  'Indirizzi contenuti':
    'Addresses contained',
  'Reputazione per indirizzo':
    'Reputation by address',
  'Reputazione IP (AbuseIPDB)':
    'IP reputation (AbuseIPDB)',
  'chiave API di AbuseIPDB':
    'AbuseIPDB API key',
  'configurata — incolla una chiave per sostituirla':
    'configured — paste a key to replace it',
  'Togli la chiave':
    'Remove the key',
  'es. Uffici Milano':
    'e.g. Milan offices',
  'a cosa serve, chi la gestisce…':
    'what it is for, who manages it…',
  'Gli IP pubblici: dalla scheda di un CI si dichiarano gli indirizzi che lo espongono; da «Nuovo CI» → «IP pubblico» si dichiarano indirizzi e subnet dell’organizzazione — una subnet è un CI solo, con la tabella dei suoi indirizzi sulla scheda. «Verifica reputazione» interroga più fonti (tre DNSBL, più AbuseIPDB con la chiave): segnalato se almeno una segnala, e una query senza risposta è «non verificabile», non un pulito.':
    'Public IPs: from a CI sheet you declare the addresses exposing it; from “New CI” → “Public IP” you declare the organisation’s addresses and subnets — a subnet is a single CI, with the table of its addresses on its sheet. “Check reputation” queries several sources (three DNSBLs, plus AbuseIPDB with a key): flagged if at least one flags, and a query with no answer is “not verifiable”, not a clean.',
  'Amministratori della piattaforma':
    'Platform administrators',
  'reimposta password':
    'reset password',
  'Reimposta':
    'Reset',
  'nuova password (min 10)':
    'new password (min 10)',
  'password attuale':
    'current password',
  'Cambia la mia password':
    'Change my password',
  'La tua password. Serve quella attuale: un token rubato non deve bastare a sostituirla.':
    'Your password. The current one is required: a stolen token must not be enough to replace it.',
  'Password cambiata.':
    'Password changed.',
  'Password reimpostata: comunicala di persona, non per iscritto dove resta.':
    'Password reset: hand it over in person, not in writing where it lingers.',
  'rinomina':
    'rename',
  'Abilitazioni':
    'Enablements',
  'abilita su un tenant':
    'enable on a tenant',
  'Abilita':
    'Enable',
  'Abilita subito su':
    'Enable right away on',
  'nessun tenant (per ora)':
    'no tenant (for now)',
  'nessun tenant: non può entrare':
    'no tenant: cannot log in',
  'piattaforma':
    'platform',
  'tenant…':
    'tenant…',
  'Con una chiave API, la verifica della reputazione degli IP pubblici utilizza anche AbuseIPDB, oltre alle tre DNSBL. La chiave e la relativa quota sono associate al contratto. Il piano gratuito prevede 1000 verifiche giornaliere.':
    'With an API key, the public-IP reputation check also queries AbuseIPDB alongside the three DNSBLs. The key is yours: your quota, your contract — the free plan is enough (a thousand checks a day).',
  'Scegli la tua password':
    'Choose your password',
  'Nuova password (almeno 10 caratteri)':
    'New password (at least 10 characters)',
  'Ripetila':
    'Repeat it',
  'Imposta la password':
    'Set the password',
  'Le due password non coincidono.':
    'The two passwords do not match.',
  'Password impostata: puoi entrare.':
    'Password set: you can log in.',
  'Vai all’accesso':
    'Go to sign-in',
  'Questo collegamento è incompleto: apri quello ricevuto per email.':
    'This link is incomplete: open the one you received by email.',
  'Nessuna password da scegliere: la persona riceve un’email con un collegamento a scadenza (72 ore) e la imposta da sola. Serve la posta in uscita configurata.':
    'No password to choose: the person receives an email with an expiring link (72 hours) and sets it themselves. Outgoing mail must be configured.',
  'Nessuna password da scegliere: la persona riceve un’email con un collegamento a scadenza e la imposta da sola. Un’email già esistente viene abilitata senza toccarne le credenziali.':
    'No password to choose: the person receives an email with an expiring link and sets it themselves. An already-existing email is enabled without touching its credentials.',
  'Attiva la verifica':
    'Enable verification',
  'Verifica in due passaggi':
    'Two-step verification',
  'Protegge l’accesso con un ulteriore fattore di autenticazione (Google Authenticator, Aegis, 1Password…)':
    'A code from your authenticator app (Google Authenticator, Aegis, 1Password…) on top of the password. The code lives on your phone, offline: nothing is sent to anyone.',
  'attiva':
    'enabled',
  'Disattiva':
    'Disable',
  'codice per disattivare':
    'code to disable',
  '1. Aggiungi la chiave all’app di autenticazione (inserimento manuale, o apri il collegamento dal telefono):':
    '1. Add the key to your authenticator app (manual entry, or open the link from your phone):',
  'apri nell’app di autenticazione':
    'open in the authenticator app',
  '2. Conferma con il codice che l’app mostra adesso:':
    '2. Confirm with the code the app shows right now:',
  'Conferma e attiva':
    'Confirm and enable',
  'Verifica in due passaggi attiva: dal prossimo accesso servirà il codice.':
    'Two-step verification enabled: the code will be required from the next sign-in.',
  'Verifica in due passaggi disattivata.':
    'Two-step verification disabled.',
  'Il codice dell’app di autenticazione':
    'The code from your authenticator app',
  'Codice a 6 cifre':
    '6-digit code',
  'Verifica…':
    'Verifying…',
  'eliminare definitivamente?':
    'delete permanently?',
  'Chiavi per il riconoscimento':
    'Keys for recognition',
  'Indirizzi da dichiarare':
    'Addresses to declare',
  'es. srv-sede-nord':
    'e.g. srv-north-site',
  'e altre {quante} voci':
    'and {quante} more entries',
  'srv-nord-01':
    'srv-north-01',
  'Registro':
    'Log',
  'Per problema':
    'By problem',
  'Nessun problema':
    'No problems',
  'Nessun errore o avviso in questa scansione.':
    'No errors or warnings in this scan.',
  'Salva anche le configurazioni degli apparati (backup)':
    'Also save the devices’ configurations (backup)',
  'Alla conferma parte un secondo job di backup sugli stessi bersagli, con la credenziale SSH scelta qui sotto: due job e due rapporti, così il backup può fallire senza sporcare la scansione.':
    'On confirm a second backup job starts on the same targets, with the SSH credential chosen below: two jobs and two reports, so the backup can fail without muddying the scan.',
  'Credenziale SSH per il backup':
    'SSH credential for the backup',
  'Imposta backup':
    'Set up backup',
  'credenziale SSH…':
    'SSH credential…',
  'Salva ora':
    'Save now',
  'Avvio…':
    'Starting…',
  'Backup avviato (job {id}…): l’esito compare qui sopra e nel rapporto del job.':
    'Backup started (job {id}…): the outcome shows above and in the job report.',
  'Per avviare un backup da qui serve l’indirizzo di gestione, che questo CI non ha.':
    'Starting a backup from here needs the management address, which this CI does not have.',
  'Nessun componente':
    'No components',
  'Nessun traffico osservato dagli ingressi nella finestra scelta.':
    'No traffic observed from the entry points in the chosen window.',
  'Vale per OGNI apparato di rete — switch, router, firewall, bilanciatori, controller wireless: se la scheda ha il riquadro Backup, si può salvare.':
    'It applies to EVERY network device — switches, routers, firewalls, load balancers, wireless controllers: if the sheet has the Backup box, it can be saved.',
  'Due modi per avviarlo: il flag «Salva anche le configurazioni» sulla scansione o sulla pianificazione di rete (parte un secondo job, con la sua credenziale SSH e il suo rapporto); oppure «Imposta backup» sulla scheda dell’apparato — credenziale SSH, «Salva ora», e vale solo per lui.':
    'Two ways to start it: the “Also save the configurations” flag on the network scan or schedule (a second job starts, with its own SSH credential and its own report); or “Set up backup” on the device sheet — SSH credential, “Save now”, for that device only.',
  'Quanto raccogliere si sceglie una volta, dalla console, e vale per tutte le esecuzioni: «fast» (identità, sistema, rete), «full» (dischi, servizi, account, sicurezza — ma NON il software), «software» (rapida più software), «all» (tutto: è il predefinito). Se il modo esclude il software, l’agente lo dichiara fra i non letti.':
    'How much to collect is chosen once, from the console, and applies to every run: “fast” (identity, system, network), “full” (disks, services, accounts, security — but NOT software), “software” (quick plus software), “all” (everything: the default). If the mode excludes software, the agent declares it among the not-read items.',
  'Oltre al comando c’è il download diretto del binario — su Windows il .exe — per chi non può eseguire script scaricati: il file va accompagnato dalla configurazione, che lo script scriverebbe da solo.':
    'Besides the command there is the direct binary download — the .exe on Windows — for those who cannot run downloaded scripts: the file must be paired with the configuration, which the script would write by itself.',
  'Il rapporto di una scansione ha due viste: il registro cronologico e «Per problema», che raggruppa errori e avvisi per causa e dice quali host colpisce — su una /24 è la vista con cui si capisce cosa non è andato.':
    'A scan report has two views: the chronological log and “By problem”, which groups errors and warnings by cause and says which hosts they hit — on a /24 it is the view that tells you what went wrong.',
  'Pacchetto RPM (Linux: RHEL, Rocky, AlmaLinux, Oracle, SUSE…)':
    'RPM package (Linux: RHEL, Rocky, AlmaLinux, Oracle, SUSE…)',
  'L’RPM contiene il binario e il servizio systemd «censo-inventory», ma non la configurazione: lo stesso pacchetto va su molti host, mentre il file con il segreto è diverso per ciascuno. Lo installa la tua piattaforma (Ansible) e lo avvia systemd, non il cron.':
    'The RPM ships the binary and the “censo-inventory” systemd service, but not the configuration: the same package goes to many hosts, while the file with the secret differs for each one. Your platform (Ansible) installs it and systemd runs it — not cron.',
  'Da Ansible: ansible.builtin.dnf per il pacchetto, un template per /etc/censo/inventory.json (0600), ansible.builtin.systemd con enabled e state=started. La configurazione di questo host è il file qui sotto (oppure rigenera il comando per averne una nuova).':
    'From Ansible: ansible.builtin.dnf for the package, a template for /etc/censo/inventory.json (0600), ansible.builtin.systemd with enabled and state=started. This host’s configuration is the file below (or regenerate the command to get a fresh one).',
  'Oppure scarica il binario e configura a mano':
    'Or download the binary and configure by hand',
  'Il file scaricato va accompagnato dal file di configurazione: senza, l’agente non sa a chi consegnare. Lo script lo scrive da solo; a mano, chiedi la configurazione rigenerando il comando.':
    'The downloaded file must be paired with the configuration file: without it, the agent does not know where to deliver. The script writes it by itself; by hand, get the configuration by regenerating the command.',
  'Come si salva':
    'How it is saved',
  'Prova NETCONF prima della CLI':
    'Try NETCONF before the CLI',
  'Quando disponibile, acquisisce la configurazione in formato strutturato e senza paginazione; in caso contrario, utilizza automaticamente la CLI. Disabilita questa modalità solo se il dispositivo presenta problemi durante il tentativo di acquisizione.':
    'Where available, the configuration arrives structured and unpaginated. Where it is not, the CLI takes over by itself: turning it off is only useful if some device reacts badly to the attempt.',
  'Escludi le credenziali dall’archiviazione':
    'Mask secrets before saving',
  'Password, chiavi e community vengono escluse dall’archiviazione per evitare la conservazione permanente delle credenziali. Una configurazione salvata potrà essere ripristinata unicamente reinserendo manualmente le credenziali.':
    'Passwords, keys and communities do not end up in the archive — which would keep them forever. The price is that the saved configuration cannot be restored without re-entering the credentials by hand: choose knowingly.',
  'Vale per tutti i salvataggi, dei job e delle pianificazioni: una scelta sola, un archivio coerente.':
    'It applies to every save, from jobs and schedules alike: one choice, a coherent archive.',
  'Gestisci utenti':
    'Manage users',
  'Abilitati':
    'Enabled',
  'Non abilitati':
    'Not enabled',
  'Rimuovi selezionati':
    'Remove selected',
  'Abilita selezionati':
    'Enable selected',
  'Rimossi.':
    'Removed.',
  'Abilitati.':
    'Enabled.',
  'Rifiutati:':
    'Refused:',
  'amministratore':
    'administrator',
  'operatore':
    'operator',
  'sola lettura':
    'read only',
  'togli (torna all’indirizzo della piattaforma)':
    'remove (back to the platform address)',
  'Salva i nomi di servizio':
    'Save the service names',
  'Un nome nuovo va prima pubblicato nel DNS e incluso nel certificato. Il canale delle sonde deve restare fuori da qualunque proxy TLS (le sonde si autenticano in mTLS): dietro un proxy prendono 403.':
    'A new name must first be published in DNS and included in the certificate. The probe channel must stay out of any TLS proxy (probes authenticate with mTLS): behind a proxy they get 403.',
  'Avanti':
    'Next',
  'Controllo…':
    'Checking…',
  'oppure con la password':
    'or with the password',
  'Ti sto portando su {provider}…':
    'Taking you to {provider}…',
  'Nuovo CI':
    'New CI',
  'Crea':
    'Create',
  'Numero di serie':
    'Serial number',
  'Per la macchina che nessuna scansione raggiunge: la sede staccata, l’apparato ordinato e non ancora in rete. Le chiavi qui sotto permettono alla discovery, quando arriverà, di riconoscerlo invece di duplicarlo.':
    'For the machine no scan reaches: the remote site, the device ordered and not yet on the network. The keys below let discovery, when it arrives, recognise it instead of duplicating it.',
  'Senza seriale, indirizzo o nome host il CI si crea lo stesso, ma la discovery non avrà modo di riconoscerlo: se la macchina verrà scandita, diventerà un doppione.':
    'Without a serial, address or hostname the CI is created anyway, but discovery will have no way to recognise it: if the machine ever gets scanned, it will become a duplicate.',
  'Nessun contratto collegato a questo bene.':
    'No contract is linked to this asset.',
  'Valido oggi':
    'Valid today',
  'valido':
    'valid',
  'non valido':
    'not valid',
  'Copertura':
    'Coverage',
  'Aggancia a un contratto…':
    'Attach to a contract…',
  'Aggancia':
    'Attach',
  'Aggancio…':
    'Attaching…',
  'Sgancia':
    'Detach',
  'IP pubblici':
    'Public IPs',
  'Indirizzi, separati da virgola':
    'Addresses, comma-separated',
  'Prima dichiara e salva almeno un indirizzo':
    'Declare and save at least one address first',
  'Verifica reputazione':
    'Check reputation',
  'Verifica in corso…':
    'Checking…',
  'Reputazione':
    'Reputation',
  'Controllato':
    'Checked',
  'mai verificata':
    'never checked',
  'pulito':
    'clean',
  'segnalato':
    'flagged',
  'non verificabile':
    'not verifiable',
  'NIS2 Compliance':
    'NIS2 Compliance',
  'Conformità alla Direttiva UE 2022/2555 (NIS2), valutata sui dati raccolti, con evidenza esplicita dei requisiti non misurabili.': 'Compliance with EU Directive 2022/2555 (NIS2), assessed on the collected data, with explicit evidence of the non-measurable requirements.',
  'Requisiti':
    'Requirements',
  'Nessun asset in inventario':
    'No asset in the inventory',
  'La conformità si misura su quello che c’è: avvia una discovery.':
    'Compliance is measured on what is there: start a discovery.',
  'Conformità del parco':
    'Estate compliance',
  'Asset critici':
    'Critical assets',
  'Vulnerabilità sfruttate attivamente (CISA KEV) o sistemi fuori supporto':
    'Vulnerabilities actively exploited (CISA KEV), or unsupported systems',
  'Da verificare':
    'To verify',
  'Mancano dati per giudicare: agente o controlli sui servizi':
    'Data missing to judge: agent or service checks',
  'Generato':
    'Generated',
  'Sui rischi aperti e sull’inventario di adesso':
    'On the open findings and the inventory of right now',
  'Requisiti misurati':
    'Measured requirements',
  'Ogni controllo è associato all’articolo della direttiva di riferimento. Le barre mostrano gli asset conformi, non conformi e quelli per cui mancano dati sufficienti alla valutazione.': 'Each control is linked to the article of the reference directive. The bars show the compliant assets, the non-compliant ones and those for which there is not enough data to assess.',
  'Controllo':
    'Check',
  'Articolo':
    'Article',
  'Esiti':
    'Outcomes',
  'Superato da':
    'Passed by',
  'Punteggio':
    'Score',
  'Passa sopra un pallino per il motivo: ● superato o fallito, ◐ non valutabile (il motivo dice come procurarsi il dato), — non applicabile.':
    'Hover a dot for the reason: ● passed or failed, ◐ not assessable (the reason says how to get the data), — not applicable.',
  'La NIS2 include requisiti non verificabili attraverso i soli dati tecnici. Questi devono essere coperti da processi, procedure o contratti e dichiarati esplicitamente, per evitare che l’assenza di evidenze venga interpretata come conformità.': 'NIS2 includes requirements that cannot be verified through technical data alone. These must be covered by processes, procedures or contracts and declared explicitly, to avoid the absence of evidence being interpreted as compliance.',
  'Superato':
    'Passed',
  'Non valutabile':
    'Not assessable',
  'Non applicabile':
    'Not applicable',
  'conforme':
    'compliant',
  'Sistema operativo supportato':
    'Supported operating system',
  'Vulnerabilità critiche gestite':
    'Critical vulnerabilities managed',
  'Aggiornamenti applicati':
    'Updates applied',
  'Software nel ciclo di vita':
    'Software within its lifecycle',
  'Cifratura dei servizi esposti':
    'Encryption of exposed services',
  "Protezione dell'endpoint":
    'Endpoint protection',
  'Inventario aggiornato':
    'Inventory up to date',
  'Gestione degli incidenti':
    'Incident handling',
  'Piani di continuità e gestione delle crisi':
    'Continuity plans and crisis management',
  'Sicurezza della catena di fornitura':
    'Supply chain security',
  'Verifica dell’efficacia delle misure':
    'Verification of the effectiveness of measures',
  'Formazione e consapevolezza':
    'Training and awareness',
  'Cifratura dei dati a riposo':
    'Encryption of data at rest',
  'Autenticazione a più fattori':
    'Multi-factor authentication',
  'la direttiva misurata sui dati raccolti, asset per asset. Ogni controllo cita l’articolo, ha quattro esiti — e i «non valutabili» dicono come procurarsi il dato. La pagina dichiara anche gli obblighi che la piattaforma non misura.':
    'the directive measured on the collected data, asset by asset. Each check cites its article, has four outcomes — and the “not assessable” ones say how to get the data. The page also declares the obligations the platform does not measure.',

  // Amministrazione · Regole di riconoscimento del prodotto
  'Sono le regole del prodotto, valide per tutti i clienti. Una regola guarda gli attributi di un CI (l’ente SNMP, la descrizione) e gli assegna una classe. Valgono dopo le regole scritte dal cliente e prima dell’euristica.':
    'These are the product rules, valid for all customers. A rule looks at a CI’s attributes (the SNMP enterprise, the description) and assigns it a class. They apply after the rules written by the customer and before the heuristics.',
  'Aggiungine una con «Nuova regola».': 'Add one with “New rule”.',
  'Modifica regola': 'Edit rule',
  'disattivata': 'disabled',
  'eliminare?': 'delete?',
  'La chiave identifica la regola e non si cambia.':
    'The key identifies the rule and cannot be changed.',
  'Un identificativo stabile, minuscolo, es. «aethra-router».':
    'A stable, lowercase identifier, e.g. “aethra-router”.',
  'Più bassa vince prima.': 'Lower wins first.',
  'Router Aethra': 'Aethra router',
  'Devono essere vere tutte. Il confronto non distingue maiuscole e minuscole.':
    'All must be true. The comparison is case-insensitive.',
  'La chiave di una classe (es. «router», «switch», «console_server»). La categoria si deduce dalla classe se la lasci vuota.':
    'The key of a class (e.g. “router”, “switch”, “console_server”). The category is derived from the class if you leave it empty.',
  '(dedotta dalla classe)': '(derived from the class)',
  'Serve almeno una condizione: una regola senza condizioni corrisponderebbe a ogni CI.':
    'At least one condition is required: a rule with no conditions would match every CI.',
  // --- Guida ---
  'Cosa fare, in ordine':
    "What to do, in order",
  'La scheda di ogni sonda dice versione, capability e salute: dopo un aggiornamento della piattaforma controlla che le sonde siano alla stessa versione — molte funzioni nuove vivono nel binario della sonda, e una sonda vecchia semplicemente non le esegue.':
    "Each probe’s page shows version, capabilities and health: after a platform upgrade check that the probes are on the same version — many new features live in the probe binary, and an old probe simply does not run them.",
  'il catalogo delle sorgenti dati per categoria — rete, virtualizzazione, storage, sicurezza, backup, DNS. Dice cosa è collegato, cosa manca e con quale credenziale.':
    "the catalogue of data sources by category — network, virtualisation, storage, security, backup, DNS. It shows what is connected, what is missing and with which credential.",
  'Leggere il rapporto di un job':
    "Reading a job report",
  'Una riga «sezione non raccolta» non è un fallimento della scansione: dice che una parte (un permesso mancante, una tabella che quella versione non ha) non è arrivata, e che il resto è arrivato lo stesso.':
    "A “section not collected” line is not a failed scan: it says one part (a missing permission, a table that version does not have) did not arrive, and that the rest arrived anyway.",
  '«Ritenta» nell’elenco dei job rilancia la stessa configurazione in un job nuovo: il vecchio resta come verbale di ciò che è successo.':
    "“Retry” in the job list runs the same configuration again as a new job: the old one stays as the record of what happened.",
  'Dopo un aggiornamento della piattaforma':
    "After a platform upgrade",
  'Durante l’aggiornamento la console mostra la pagina «Platform upgrade in progress», che si ricarica da sola: quando la piattaforma torna, si rientra senza fare niente.':
    "During the upgrade the console shows the “Platform upgrade in progress” page, which reloads by itself: when the platform is back you are in again without doing anything.",
  'Le novità che dipendono dalla raccolta (nuovi comandi, nuovi campi) compaiono al primo giro di scansione successivo, e solo con le sonde aggiornate: una scheda che non mostra ancora un dato nuovo di solito aspetta la scansione, non è rotta.':
    "New features that depend on collection (new commands, new fields) appear on the next scan run, and only with updated probes: a page that does not show a new value yet is usually waiting for the scan, not broken.",
  'La lente in alto (o ⌘K / Ctrl+K) cerca ovunque e porta dove sta la risposta: i risultati sono raggruppati per tipo, e ogni riga apre il posto giusto.':
    "The magnifier at the top (or ⌘K / Ctrl+K) searches everywhere and takes you where the answer is: results are grouped by type, and each row opens the right place.",
  'Cosa trovi':
    "What you find",
  'Configuration Item: per nome, identificativo, produttore e per tutte le chiavi con cui il CI è stato riconosciuto — indirizzi, MAC, seriali, nomi — non solo quella principale.':
    "Configuration Items: by name, identifier, vendor and by every key the CI has been recognised by — addresses, MACs, serials, names — not just the main one.",
  'Rete: indirizzi IP, MAC, nomi e descrizioni delle porte degli apparati.':
    "Network: IP addresses, MACs, names and descriptions of device ports.",
  'Wireless: un client Wi-Fi per MAC, indirizzo o login, e un SSID o un BSSID. La riga apre la sezione Wireless del controller che lo serve.':
    "Wireless: a Wi-Fi client by MAC, address or login, and an SSID or BSSID. The row opens the Wireless section of the controller that serves it.",
  'Utenti: una persona, per login — vista sul Wi-Fi, in VPN o collegata a un PC. La riga apre la sua pagina di presenza.':
    "Users: a person, by login — seen on Wi-Fi, on VPN or logged on to a PC. The row opens their presence page.",
  'Software, componenti (seriali di schede e SFP), asset, business service e contratti.':
    "Software, components (card and SFP serials), assets, business services and contracts.",
  'Da sapere':
    "Good to know",
  'Un MAC si incolla come lo si ha davanti: DC-EF-80-03-2B-40 (Windows), dcef.8003.2b40 (Cisco), dcef-8003-2b40 (Huawei) e dc:ef:80:03:2b:40 trovano la stessa cosa.':
    "Paste a MAC as you have it: DC-EF-80-03-2B-40 (Windows), dcef.8003.2b40 (Cisco), dcef-8003-2b40 (Huawei) and dc:ef:80:03:2b:40 all find the same thing.",
  'Servono almeno due caratteri, e ogni gruppo mostra i primi risultati: il segno che ce ne sono altri rimanda alla pagina dedicata, che ha i filtri.':
    "At least two characters are needed, and each group shows the first results: the sign that there are more points to the dedicated page, which has the filters.",
  'Chi è collegato solo in VPN si trova con qualche minuto di ritardo: le sessioni si leggono dai firewall ogni cinque minuti.':
    "Someone connected only via VPN is found with a few minutes’ delay: sessions are read from the firewalls every five minutes.",
  'Anche l’elenco dei Configuration Item ha la sua casella di ricerca, con le stesse regole per MAC e chiavi, più i filtri per colonna.':
    "The Configuration Item list has its own search box too, with the same rules for MACs and keys, plus per-column filters.",
  'La pagina di presenza risponde a «dov’è collegata questa persona, adesso?». Ci si arriva dalla ricerca: si digita la login e si apre il risultato nel gruppo Utenti.':
    "The presence page answers “where is this person connected, right now?”. You get there from search: type the login and open the result in the Users group.",
  'Wi-Fi: i dispositivi associati con quella login (reti 802.1X), con access point, SSID, banda, segnale e controller. Il MAC porta al CI del dispositivo quando è in inventario.':
    "Wi-Fi: the devices associated with that login (802.1X networks), with access point, SSID, band, signal and controller. The MAC links to the device’s CI when it is in the inventory.",
  'PC: le macchine su cui la persona risulta collegata all’ultimo inventario.':
    "PCs: the machines the person was logged on to at the last inventory.",
  'La stessa persona si riconosce anche se ogni sorgente la scrive a modo suo: il nome con il dominio davanti, con il dominio dopo la chiocciola o senza dominio è la stessa login.':
    "The same person is recognised even if each source writes them differently: the name with the domain in front, with the domain after the @ or with no domain is the same login.",
  'Se i firewall non rispondono, la VPN si mostra dall’ultima sincronizzazione (al più cinque minuti fa), e la pagina lo dice.':
    "If the firewalls do not answer, the VPN is shown from the last sync (at most five minutes ago), and the page says so.",
  'Sulle reti a chiave condivisa una login non esiste: quei dispositivi si vedono nella sezione Wireless del controller, non qui.':
    "On shared-key networks there is no login: those devices are visible in the controller’s Wireless section, not here.",
  'In cima all’elenco il cruscotto: CI totali e nuovi, non classificati, con rischi di sicurezza, non visti da una settimana, e le ruote per categoria e per origine. I riquadri numerici filtrano l’elenco con un clic.':
    "At the top of the list, the dashboard: total and new CIs, unclassified ones, those with security findings, those not seen for a week, and the donuts by category and by source. The number tiles filter the list with one click.",
  'I filtri per colonna si sommano: «nome contiene srv» e «produttore è Dell» restringono insieme, come in un foglio di calcolo.':
    "Column filters add up: “name contains srv” and “vendor is Dell” narrow down together, as in a spreadsheet.",
  'cosa compare e cosa non si trova più, giorno per giorno: la risposta a «è cambiato qualcosa nel parco?».':
    "what appears and what can no longer be found, day by day: the answer to “has anything changed in the estate?”.",
  'La scheda di un CI':
    "A CI’s page",
  'Le sezioni in alto portano a ciò che quel CI ha: anagrafica, attributi, classificazione, sicurezza, backup, rete, collegamenti, ciclo di vita e tracciabilità — e, a seconda di cosa è, routing, wireless, storage, firewall o Wazuh.':
    "The sections at the top lead to what that CI has: overview, attributes, classification, security, backup, network, links, lifecycle and traceability — and, depending on what it is, routing, wireless, storage, firewall or Wazuh.",
  'Collegamenti: le relazioni con gli altri oggetti — a quale switch è attaccato, in quale cluster sta, su quale storage tiene i dati, chi lo usa.':
    "Links: the relationships with other objects — which switch it is plugged into, which cluster it belongs to, which storage holds its data, who uses it.",
  'Tracciabilità: per ogni attributo, chi l’ha scritto e quando, col valore di prima e quello di dopo. È la risposta a «questo valore da dove viene?».':
    "Traceability: for each attribute, who wrote it and when, with the old and new value. It answers “where does this value come from?”.",
  'Duplicati e identità':
    "Duplicates and identity",
  'Lo stesso oggetto visto da due strade (una scansione SNMP e un connettore, per esempio) si riconosce dalle chiavi che hanno in comune — seriale, MAC, indirizzo — e diventa un CI solo. Quando due CI si scoprono lo stesso, si fondono alla scansione successiva e il rapporto lo scrive.':
    "The same object seen through two paths (an SNMP scan and a connector, for instance) is recognised by the keys they share — serial, MAC, address — and becomes a single CI. When two CIs turn out to be the same, they merge at the next scan and the report says so.",
  'Se vedi due CI che dovrebbero essere uno, confronta la chiave sotto il nome: se non hanno niente in comune, nessuna scansione può sapere che sono lo stesso oggetto. Segnalalo — di solito basta insegnare alla piattaforma una chiave in più.':
    "If you see two CIs that should be one, compare the key under the name: if they share nothing, no scan can know they are the same object. Report it — usually teaching the platform one more key is enough.",
  'Beni, contratti e report':
    "Assets, contracts and reports",
  'Gli apparati di rete si raccontano in tre modi: l’SNMP dice porte, vicini e rotte; l’inventario avanzato via SSH dice schede, ottiche e seriali; il traffico dice chi parla con chi.':
    "Network devices describe themselves in three ways: SNMP gives ports, neighbours and routes; the SSH advanced inventory gives cards, optics and serials; traffic tells who talks to whom.",
  'tre viste: Apparati (colonne filtrabili — produttore, modello, indirizzo, stato delle porte), Componenti (schede, SFP, alimentatori e ventole di tutti gli apparati, cercabili per seriale) e IP (gli indirizzi aggiuntivi e pubblici).':
    "three views: Devices (filterable columns — vendor, model, address, port status), Components (cards, SFPs, power supplies and fans of every device, searchable by serial) and IPs (additional and public addresses).",
  'Per ottiche, schede e seriali lancia l’«Inventario avanzato apparati» con una credenziale SSH. Su Cisco IOS le ottiche arrivano da «show inventory», sui Nexus da «show interface transceiver», su Huawei da «display transceiver» e dall’etichetta elettronica.':
    "For optics, cards and serials run the “Advanced device inventory” with an SSH credential. On Cisco IOS optics come from “show inventory”, on Nexus from “show interface transceiver”, on Huawei from “display transceiver” and the electronic label.",
  'La stessa scansione, quando incontra un controller wireless, legge anche access point, SSID e client: non serve un job a parte.':
    "The same scan, when it meets a wireless controller, also reads access points, SSIDs and clients: no separate job is needed.",
  'La sezione Wireless compare sulla scheda dei controller wireless: raccoglie gli access point che il controller gestisce, le reti che diffonde e i dispositivi collegati.':
    "The Wireless section appears on wireless controllers’ pages: it gathers the access points the controller manages, the networks it broadcasts and the connected devices.",
  'Verifica che il controller sia classificato «Controller wireless»: se risulta uno switch, correggi la classe dalla scheda.':
    "Check that the controller is classified as “Wireless controller”: if it shows as a switch, fix the class from its page.",
  'Dagli una credenziale SSH e includilo nell’Inventario avanzato apparati: al primo giro la sezione si popola, ai giri successivi si aggiorna.':
    "Give it an SSH credential and include it in the Advanced device inventory: the section fills in at the first run and updates at the next ones.",
  'Grafici: client per banda (2.4/5/6 GHz), qualità del segnale (dal RSSI: ottimo, buono, debole, critico), client per SSID e per access point.':
    "Charts: clients by band (2.4/5/6 GHz), signal quality (from RSSI: excellent, good, weak, critical), clients by SSID and by access point.",
  'Access point: ogni AP è un CI a sé, collegato al controller, con modello, indirizzo, MAC, gruppo, stato e da quanto è acceso. Il nome apre la sua scheda.':
    "Access points: each AP is a CI of its own, linked to the controller, with model, address, MAC, group, state and uptime. The name opens its page.",
  'Reti diffuse: ogni SSID su ogni radio, con BSSID, autenticazione e quanti client ha.':
    "Broadcast networks: every SSID on every radio, with BSSID, authentication and client count.",
  'Client connessi: MAC, dispositivo (il costruttore ricavato dal MAC), utente (la login sulle reti 802.1X), indirizzo, SSID, AP, banda, protocollo, velocità, RSSI e VLAN.':
    "Connected clients: MAC, device (the vendor derived from the MAC), user (the login on 802.1X networks), address, SSID, AP, band, protocol, speed, RSSI and VLAN.",
  '«MAC casuale» nella colonna Dispositivo è la privacy dei telefoni: il MAC l’ha inventato il dispositivo, e un costruttore non esiste.':
    "“Random MAC” in the Device column is phone privacy: the device made the MAC up, and there is no vendor.",
  'Il MAC di un client diventa un link quando lo stesso dispositivo è già in inventario (un portatile, un telefono visto da una scansione).':
    "A client’s MAC becomes a link when the same device is already in the inventory (a laptop, a phone seen by a scan).",
  'Client e SSID sono la fotografia dell’ultima lettura: un telefono che cambia stanza cambia AP. Una lettura fallita non cancella la fotografia precedente.':
    "Clients and SSIDs are the snapshot of the last reading: a phone that changes room changes AP. A failed reading does not erase the previous snapshot.",
  'Oggi il driver legge i controller Huawei AirEngine; gli altri costruttori si aggiungono nel tempo.':
    "Today the driver reads Huawei AirEngine controllers; other vendors will be added over time.",
  'Crea la credenziale della piattaforma e lancia il job del connettore: gli oggetti compaiono in Configuration Item già legati fra loro — VM sull’host, host nel cluster, dischi sul datastore.':
    "Create the platform credential and run the connector job: objects appear in Configuration Items already linked to each other — VMs on hosts, hosts in clusters, disks on datastores.",
  'Pianificalo: le macchine virtuali nascono e muoiono più in fretta di tutto il resto, e un inventario virtuale vecchio di un mese racconta un altro ambiente.':
    "Schedule it: virtual machines come and go faster than anything else, and a month-old virtual inventory describes a different environment.",
  'Mappa virtuale':
    "Virtual map",
  'cluster, host e macchine virtuali in un colpo d’occhio, per ambiente.':
    "clusters, hosts and virtual machines at a glance, by environment.",
  'l’inventario dell’ambiente a fogli, come RVTools — VM, host, cluster, datastore, reti — a video, in Excel o in PDF.':
    "the environment inventory in sheets, like RVTools — VMs, hosts, clusters, datastores, networks — on screen, in Excel or in PDF.",
  'Un host si lega al suo cluster da solo quando il cluster esiste come CI. Se un cluster non compare, il rapporto del job del connettore dice perché.':
    "A host links to its cluster by itself once the cluster exists as a CI. If a cluster does not appear, the connector job’s report says why.",
  'Un array di storage si inventaria da due strade che diventano un CI solo: l’SNMP dà anagrafica e mappature, il connettore REST del costruttore (Huawei OceanStor) dà pool, LUN, dischi, host e porte.':
    "A storage array is inventoried through two paths that become a single CI: SNMP gives the overview and mappings, the vendor’s REST connector (Huawei OceanStor) gives pools, LUNs, disks, hosts and ports.",
  'Crea una credenziale DeviceManager e lancia l’inventario «Huawei OceanStor» sull’array; pianificalo, perché ogni giro riporta capacità e mappature aggiornate e alimenta il capacity plan.':
    "Create a DeviceManager credential and run the “Huawei OceanStor” inventory on the array; schedule it, because each run brings updated capacity and mappings and feeds the capacity plan.",
  'Cosa trovi nella sezione Storage':
    "What you find in the Storage section",
  'In cima: mappature, dischi, pool, LUN e host registrati, con la capacità totale e quanta è usata.':
    "At the top: mappings, disks, pools, LUNs and registered hosts, with total capacity and how much is used.",
  'Pool di storage: capacità, usata e percentuale d’uso — il buffer reale dell’array.':
    "Storage pools: capacity, used and usage percentage — the array’s real headroom.",
  'Host registrati e initiator: chi monta le LUN, con il sistema operativo e gli IQN o i WWPN.':
    "Registered hosts and initiators: who mounts the LUNs, with the operating system and the IQNs or WWPNs.",
  'Dischi: modello, seriale e capacità di ogni disco fisico. Le porte dell’array stanno fra le interfacce di rete del CI.':
    "Disks: model, serial and capacity of every physical disk. The array’s ports are among the CI’s network interfaces.",
  'Le correlazioni':
    "Correlations",
  'Con gli switch: i MAC delle porte dell’array risolvono le tabelle MAC degli switch, e nei Collegamenti si vede a quale switch e porta è attaccato. Serve una scansione SNMP degli switch dopo quella dell’array.':
    "With switches: the array ports’ MACs resolve the switches’ MAC tables, and Links shows which switch and port it is plugged into. It needs an SNMP scan of the switches after the array’s.",
  'Il capacity plan dice quanto spazio c’è ancora, quanto cresce e quando finisce — su datastore e array insieme, dai dati in inventario.':
    "The capacity plan tells how much space is left, how fast it grows and when it runs out — for datastores and arrays together, from inventory data.",
  'in cima i totali — capacità, usato, libero, crescita al giorno e fra quanto è pieno — e sotto l’andamento con la proiezione.':
    "at the top the totals — capacity, used, free, daily growth and time to full — and below the trend with the projection.",
  'La tabella: per ogni datastore e array (con l’etichetta «array») l’ambiente, gli host serviti, la capacità, lo spazio libero, l’utilizzo, la crescita al giorno e i giorni al pieno.':
    "The table: for every datastore and array (labelled “array”) the environment, hosts served, capacity, free space, usage, daily growth and days to full.",
  '«Host serviti» sugli array è il numero che trasforma «si riempie» in «chi si ferma»: quanti host hanno i dati lì.':
    "“Hosts served” on arrays is the number that turns “it fills up” into “who stops”: how many hosts keep their data there.",
  'Compute e porte: l’occupazione degli host di virtualizzazione e delle porte degli apparati.':
    "Compute and ports: the load of virtualisation hosts and the usage of device ports.",
  'Crescita e giorni al pieno si calcolano sulla storia: servono almeno sette giorni di campioni, e fino ad allora la colonna mostra un trattino.':
    "Growth and days to full are computed from history: at least seven days of samples are needed, and until then the column shows a dash.",
  'Il campione si prende una volta al giorno. Una crescita negativa è spazio liberato, non un errore.':
    "A sample is taken once a day. Negative growth is freed space, not an error.",
  'Configura router e firewall perché mandino NetFlow/IPFIX alla sonda: la pagina Esportatori conferma che arrivano. Se i numeri non tornano, la diagnosi dei flussi dalla scheda della sonda dice se e da chi arrivano i pacchetti.':
    "Configure routers and firewalls to send NetFlow/IPFIX to the probe: the Exporters page confirms they arrive. If the numbers look wrong, the flow diagnosis on the probe’s page tells whether and from whom packets arrive.",
  'Sorgenti in tempo reale':
    "Real-time sources",
  'Alcuni sistemi del cliente non si inventariano: si interrogano quando serve, attraverso la sonda che li raggiunge. Queste pagine leggono adesso, non da una copia.':
    "Some customer systems are not inventoried: they are queried when needed, through the probe that reaches them. These pages read now, not from a copy.",
  'agenti, allarmi e vulnerabilità del parco; sulla scheda di un CI, lo stato del suo agente.':
    "agents, alerts and vulnerabilities of the estate; on a CI’s page, the state of its agent.",
  'DNS':
    "DNS",
  'job, repository e oggetti protetti del backup (Veeam, Vinchin, Commvault, Rubrik); sulla scheda di una VM o di un server, la sua protezione.':
    'backup jobs, repositories and protected objects (Veeam, Vinchin, Commvault, Rubrik); on a VM’s or server’s page, its protection.',
  'Crea la connessione nel modulo del sistema (sezione «Connessioni» di Backup, IPAM, Wazuh o Firewall; per il DNS in Sorgenti dati) scegliendo la sonda che lo raggiunge, e assicurati che quella sonda sia aggiornata: è lei che parla col sistema.':
    'Create the connection in the system’s module («Connections» section of Backup, IPAM, Wazuh or Firewall; for the DNS in Data sources) choosing the probe that reaches it, and make sure that probe is up to date: it is the one talking to the system.',
  '«Non interrogabile» vuol dire che la sonda non ha raggiunto il sistema: il messaggio dice perché — credenziale, rete o certificato.':
    "“Not queryable” means the probe did not reach the system: the message says why — credential, network or certificate.",
  'Nei Collegamenti di un datastore: gli host che lo montano («collegato a»), le VM che ci tengono i dischi («archiviato su») e l’array che lo serve.':
    "In a datastore’s Links: the hosts that mount it (“connected to”), the VMs keeping their disks there (“stored on”) and the array that serves it.",
  'Layer storage':
    "Storage layer",
  'Layer network':
    "Network layer",
  'Layer fisico':
    "Physical layer",
  'Altre relazioni':
    "Other relationships",
  'Sulla mappa ogni relazione ha il colore e il tratto del suo layer: blu pieno la rete, verde a puntini il fisico (VM sull’host, host nel cluster), arancio tratteggiato lo storage (archiviato su, host che monta un datastore). Più relazioni fra gli stessi due oggetti si aprono a ventaglio e restano distinguibili.':
    "On the map every relationship has the colour and stroke of its layer: solid blue for network, dotted green for physical (VM on host, host in cluster), dashed orange for storage (stored on, host mounting a datastore). Several relationships between the same two objects fan out and stay distinguishable.",
  '1 salto':
    '1 hop',
  '{n} salti':
    '{n} hops',
  'Adatta alla finestra (0)':
    'Fit to window (0)',
  'Anelli attorno al centro: la distanza in salti':
    'Rings around the centre: the distance in hops',
  'Ciò che è collegato si avvicina, restando nella sua fascia':
    'Connected objects move closer, staying in their band',
  'Comunicazione (spessore = volume)':
    'Communication (thickness = volume)',
  'Disposizione':
    'Layout',
  'Fasce per livello, dall’alto in basso: il criterio della pagina':
    'Bands by level, top to bottom: the page\'s criterion',
  'Gerarchica':
    'Hierarchical',
  'Radiale':
    'Radial',
  'Organica':
    'Organic',
  'Host e dispositivi':
    'Hosts and devices',
  'Macchine virtuali':
    'Virtual machines',
  'Perimetro':
    'Perimeter',
  'Switching':
    'Switching',
  'Ingrandisci (+)':
    'Zoom in (+)',
  'Riduci (−)':
    'Zoom out (−)',
  'Schermo intero':
    'Full screen',
  'Livello {n}':
    'Level {n}',
  'Nella CMDB':
    'In the CMDB',
  'Non censito':
    'Not inventoried',
  'Seme':
    'Seed',
  'Rotella o pizzico per ingrandire, trascinamento della tela per spostare, trascinamento di un nodo per fissarlo (contorno tratteggiato). Passando su un nodo si accendono i suoi collegamenti; un clic lo mette a fuoco. Tasti: + − e 0 per adattare.':
    'Wheel or pinch to zoom, drag the canvas to pan, drag a node to pin it (dashed outline). Hovering a node lights up its links; a click focuses it. Keys: + − and 0 to fit.',
  'La mappa di correlazione e quella del servizio sono la stessa della topologia, con le stesse tre viste e gli stessi comandi: qui il livello è la distanza dal seme (Seme, Livello 1, Livello 2…) e lo spessore della linea è il volume scambiato.':
    'The correlation map and the service map are the same as the topology one, with the same three views and the same controls: here the level is the distance from the seed (Seed, Level 1, Level 2…) and the line thickness is the volume exchanged.',
  'passando sopra un nodo si accendono le sue linee e i suoi vicini, il resto si spegne: è il modo più veloce per leggere un nodo affollato senza cliccarlo. I nomi lunghi si accorciano per non sovrapporsi e si allungano ingrandendo;':
    'hovering over a node lights up its lines and its neighbours and dims the rest: the quickest way to read a crowded node without clicking it. Long names shorten so they never overlap, and grow back as you zoom in;',
  'rotella o pizzico ingrandiscono verso il puntatore senza far scorrere la pagina; trascinando lo sfondo ti sposti. In alto a destra: + e − per lo zoom, «Adatta» per rivedere tutto, «Schermo intero» per le reti grandi. Con la tastiera: + − e 0. La minimappa in basso a destra dice dove sei quando hai ingrandito;':
    'wheel or pinch zoom towards the pointer without scrolling the page; dragging the background pans. Top right: + and − to zoom, «Fit» to see everything again, «Full screen» for large networks. On the keyboard: + − and 0. The minimap at the bottom right shows where you are once you have zoomed in;',
  'tre viste, in alto a sinistra, e la scelta si ricorda: Gerarchica (fasce per ruolo, dal perimetro in alto allo storage in basso, con il nome della fascia a sinistra), Radiale (anelli per ruolo; con un nodo scelto, anelli per distanza in salti da lui, al centro — ogni cluster ha il suo spicchio) e Organica (ciò che è collegato si avvicina, restando vicino alla sua fascia). Cambiando vista i nodi scivolano al posto nuovo: segui con l’occhio dove va ciascuno;':
    'three views, top left, and the choice is remembered: Hierarchical (bands by role, from the perimeter at the top to storage at the bottom, with the band name on the left), Radial (rings by role; with a node selected, rings by hop distance from it, in the centre — each cluster gets its own slice) and Organic (connected objects move closer while staying near their band). When you switch view the nodes glide to their new place: follow each one with your eye;',
  'Sulla scheda di un datastore: capacità, spazio libero e i WWN dei dischi sotto — dai pool SharedBlock di NexaVM/ZStack e dagli extent dei VMFS di VMware (il disco naa.) — che lo legano alla LUN dell’array che lo serve. Su VMware servono utente e password nella credenziale: gli extent si leggono solo dalla SOAP di vCenter, non con un token.':
    'On a datastore\'s page: capacity, free space and the WWNs of the disks underneath — from NexaVM/ZStack SharedBlock pools and from VMware VMFS extents (the naa. disk) — which tie it to the array LUN serving it. On VMware the credential needs a username and password: extents can only be read from the vCenter SOAP API, not with a token.',
  'Con i datastore: un datastore si lega all’array per WWN quando la piattaforma di virtualizzazione dichiara i dischi che ha sotto (NexaVM/ZStack e VMware); altrimenti per nome, quando si chiama come la LUN.':
    'With datastores: a datastore is linked to the array by WWN when the virtualisation platform declares the disks underneath (NexaVM/ZStack and VMware); otherwise by name, when it is named like the LUN.',
  'Aggiungi il salto':
    'Add the hop',
  'Apri il servizio →':
    'Open the service →',
  'Archiviato su':
    'Stored on',
  'Chiama':
    'Calls',
  'Chiama altri {n} oggetti oltre quelli mostrati (i più voluminosi sono in mappa).':
    'It calls {n} more objects beyond those shown (the busiest are on the map).',
  'Chiamata (spessore = volume)':
    'Call (thickness = volume)',
  'Chiamate per nodo':
    'Calls per node',
  'Chiamate · livello {n}':
    'Calls · level {n}',
  'Chiamato da':
    'Called by',
  'Collegato a (rete)':
    'Connected to (network)',
  'Componente':
    'Component',
  'Componi':
    'Compose',
  'Composizione del servizio':
    'Service composition',
  'Composizione del servizio dietro {seed}':
    'Composition of the service behind {seed}',
  'Crea un business service da questa composizione':
    'Create a business service from this composition',
  'Creato dalla composizione di {seed}':
    'Created from the composition of {seed}',
  'Da quale ingresso':
    'From which entry point',
  'Costruisce la catena delle comunicazioni tra i componenti del servizio, mostra le dipendenze infrastrutturali ed evidenzia l’origine dell’esecuzione (host, cluster, datastore, storage e rete).':
    'Builds the chain of communications between the service components, shows the infrastructure dependencies and highlights where each runs (host, cluster, datastore, storage and network).',
  'Dall’ingresso non esce nessuna chiamata nel traffico osservato. Se è il VIP di un bilanciatore che verso i server esce da indirizzi non inventariati (o non esporta flussi), dichiara il salto a mano qui sotto: da lì la catena riprende da sola.':
    'No call leaves the entry point in the observed traffic. If it is a load balancer VIP that reaches the servers from addresses not in the inventory (or exports no flows), declare the hop manually below: from there the chain continues on its own.',
  'Esplora il vicinato':
    'Explore the neighbourhood',
  'Gira su / membro di':
    'Runs on / member of',
  'Il VIP del bilanciatore, il front-end, l’indirizzo pubblicato: da qui si seguono le chiamate verso ciò che il servizio usa.':
    'The load balancer VIP, the front end, the published address: from here the calls are followed towards what the service uses.',
  'Il salto che il traffico non vede — il VIP verso i server del pool, un’applicazione verso un sistema che non esporta flussi — si dichiara qui. La catena lo segue come un salto osservato e da lì riprende a seguire il traffico.':
    'The hop that traffic does not show — the VIP to the pool servers, an application to a system that exports no flows — is declared here. The chain follows it like an observed hop and from there goes back to following the traffic.',
  'Indirizzo dell’ingresso':
    'Entry point address',
  'Indirizzo non inventariato in CMDB.':
    'Address not inventoried in the CMDB.',
  'Infrastruttura (host, cluster, datastore, rete)':
    'Infrastructure (hosts, clusters, datastores, network)',
  'Ingresso':
    'Entry point',
  'La catena del servizio':
    'The service chain',
  'Le più voluminose. Massimo 40.':
    'The busiest ones. Maximum 40.',
  'Lo usano anche {n} oggetti fuori da questo servizio: se lo tocchi, se ne accorgono anche loro.':
    '{n} objects outside this service also use it: if you touch it, they will notice too.',
  'L’indirizzo di partenza non compare né in CMDB né nel traffico della finestra scelta.':
    'The starting address appears neither in the CMDB nor in the traffic of the selected window.',
  'Nascosti perché parlano con tutti:':
    'Hidden because they talk to everyone:',
  'Nessun ingresso riconosciuto':
    'No entry point recognised',
  'Salti a mano':
    'Manual hops',
  'Salti di chiamata dall’ingresso.':
    'Call hops from the entry point.',
  'Salto a mano':
    'Manual hop',
  'Scegli un oggetto dall’elenco o scrivi un indirizzo IP.':
    'Pick an object from the list or type an IP address.',
  'Servizi comuni (DNS, NTP, AD, monitoraggio, backup)':
    'Common services (DNS, NTP, AD, monitoring, backup)',
  'Spunta «Servizi comuni» per vederli.':
    'Tick «Common services» to see them.',
  'Ultima ora':
    'Last hour',
  'Verso (oggetto o indirizzo IP)':
    'To (object or IP address)',
  'a mano':
    'manual',
  'entro {n} salti':
    'within {n} hops',
  'es. pool web del VIP':
    'e.g. VIP web pool',
  'es. web-01 oppure 10.0.2.11':
    'e.g. web-01 or 10.0.2.11',
  'salto a mano':
    'manual hop',
  '{n} client':
    '{n} clients',
  '{n} client chiamano l’ingresso; i principali per volume:':
    '{n} clients call the entry point; the main ones by volume:',
  '{n} componenti nella catena':
    '{n} components in the chain',
  'due viste. «Composizione del servizio» (la predefinita, quella che si apre da Entry point): dall’ingresso la catena delle chiamate — il balancer verso i web, i web verso l’app, l’app verso il database, con le porte — e sotto dove sta ogni pezzo: host, cluster, datastore, array, rete. «Esplora il vicinato»: da un indirizzo, chi gli parla e con chi parla; tasto destro per escludere un nodo o una comunicazione, spunte in tabella per escludere in blocco.':
    'two views. «Service composition» (the default, the one opened from Entry points): from the entry point, the chain of calls — the load balancer to the web servers, the web servers to the app, the app to the database, with ports — and below it where each piece lives: hosts, clusters, datastores, arrays, network. «Explore the neighbourhood»: from an address, who talks to it and who it talks to; right-click to exclude a node or a communication, table checkboxes to exclude in bulk.',
  'individua il servizio in Entry point, selezionalo e premi «Mostra in mappa»: si apre la composizione del servizio dietro quell’ingresso;':
    'find the service in Entry points, select it and press «Show on map»: the composition of the service behind that entry point opens;',
  'controlla la catena nella tabella e, se manca il salto dopo il bilanciatore, aggiungilo a mano;':
    'check the chain in the table and, if the hop after the load balancer is missing, add it manually;',
  'premi «Crea il business service»: l’ingresso, la profondità e i salti a mano passano nel servizio, e la catena si ricalcola dal traffico a ogni apertura.':
    'press «Create the business service»: the entry point, the depth and the manual hops go into the service, and the chain is recomputed from traffic every time it is opened.',
  'l’elenco, la composizione di ciascun servizio ricostruita dal traffico (la stessa vista della mappa di correlazione, con i salti a mano salvati sul servizio), e la modifica in ogni momento.':
    'the list, the composition of each service rebuilt from traffic (the same view as the correlation map, with manual hops saved on the service), and editing at any time.',
  'Come si legge la composizione di un servizio:':
    'How to read a service composition:',
  'i nodi sono oggetti della CMDB, con tutti i loro indirizzi: il VIP e gli indirizzi interni del bilanciatore sono lo stesso nodo, così la catena non si spezza al primo salto. Un indirizzo che la CMDB non conosce resta un nodo a sé, in giallo;':
    'nodes are CMDB objects with all their addresses: the VIP and the load balancer\'s internal addresses are the same node, so the chain does not break at the first hop. An address the CMDB does not know stays a node of its own, in yellow;',
  'si seguono solo le chiamate, non le risposte: il verso di una conversazione è quello verso la porta più bassa, la porta del servizio. I client dell’ingresso stanno in un nodo solo («214 client»), con i principali nel dettaglio;':
    'only calls are followed, not replies: the direction of a conversation is the one towards the lower port, the service port. The entry point\'s clients sit in a single node («214 clients»), with the main ones in the detail;',
  'DNS, NTP, SNMP, syslog, monitoraggio, backup e controller di dominio parlano con tutti: sono nascosti, e sopra la mappa trovi cosa è stato nascosto e quante chiamate. «Servizi comuni» li rimette; «Infrastruttura» accende o spegne le fasce sotto la catena;':
    'DNS, NTP, SNMP, syslog, monitoring, backup and domain controllers talk to everyone: they are hidden, and above the map you find what was hidden and how many calls. «Common services» brings them back; «Infrastructure» turns the bands below the chain on or off;',
  'la tabella «La catena del servizio» dice la stessa cosa a parole, una riga per componente: chi lo chiama e su che porta, cosa chiama, su quale host, cluster, datastore (con l’array sotto) e rete sta. Un clic su un nodo apre il dettaglio, compreso quanti oggetti fuori dal servizio lo usano;':
    'the «The service chain» table says the same thing in words, one row per component: who calls it and on which port, what it calls, and which host, cluster, datastore (with the array underneath) and network it sits on. Clicking a node opens the detail, including how many objects outside the service use it;',
  'se dall’ingresso non esce niente — un bilanciatore che verso i server esce da indirizzi non inventariati, o che non esporta flussi — aggiungi un «salto a mano»: da un nodo a un oggetto o a un indirizzo, con la porta. È l’arco viola tratteggiato, e da lì la catena riprende a seguire il traffico.':
    'if nothing leaves the entry point — a load balancer that reaches the servers from addresses not in the inventory, or that exports no flows — add a «manual hop»: from a node to an object or an address, with the port. It is the dashed purple edge, and from there the chain goes back to following the traffic.',
  'per pulire il risultato: tasto destro su un nodo («Togli dalla composizione») o su una chiamata («Togli questa chiamata», oppure «Togli la porta … ovunque»), Maiusc+clic su un nodo, o «Togli» nella tabella. Togliendo un oggetto sparisce anche ciò che si raggiungeva solo passando da lui. Quello che hai tolto resta elencato sopra la mappa («Tolti da te»): un clic sulla ✕ lo rimette. Nel business service le esclusioni si salvano sul servizio e valgono anche alle aperture successive; dalla mappa di correlazione passano nel servizio quando lo crei.':
    'to clean up the result: right-click a node («Remove from the composition») or a call («Remove this call», or «Remove port … everywhere»), Shift+click a node, or «Remove» in the table. Removing an object also removes whatever was reached only through it. What you removed stays listed above the map («Removed by you»): clicking the ✕ brings it back. In a business service the exclusions are saved on the service and apply on later openings too; from the correlation map they go into the service when you create it.',
  'Per pulire: tasto destro su un nodo o su una chiamata (anche «togli la porta ovunque»), Maiusc+clic su un nodo, o «Togli» nella tabella. Togliendo un oggetto sparisce anche ciò che si raggiungeva solo passando da lui.':
    'To clean up: right-click a node or a call (including «remove the port everywhere»), Shift+click a node, or «Remove» in the table. Removing an object also removes whatever was reached only through it.',
  'Tolti da te:':
    'Removed by you:',
  'Rimetti':
    'Put back',
  'Rimetti tutto':
    'Put everything back',
  'porta {n}':
    'port {n}',
  'Togli dalla composizione':
    'Remove from the composition',
  'Togli {nome}':
    'Remove {nome}',
  'Togli questa chiamata':
    'Remove this call',
  'Togli la porta {porta} ovunque':
    'Remove port {porta} everywhere',
  'togli la chiamata':
    'remove the call',
  'L’ingresso sarà {seed}, con la profondità scelta, i {n} salti a mano e ciò che hai tolto. La catena non si copia: il servizio la ricalcola dal traffico a ogni apertura, con le stesse esclusioni.':
    'The entry point will be {seed}, with the chosen depth, the {n} manual hops and what you removed. The chain is not copied: the service recomputes it from traffic every time it is opened, with the same exclusions.',
  "Cattura l'albero MIB del costruttore di un apparato (OID, tipo, valore) per generare un driver di arricchimento. Non modifica l'inventario; la cattura si scarica dal rapporto del job. Indica un solo apparato.":
    'Captures a device\'s vendor MIB tree (OID, type, value) to generate an enrichment driver. It does not change the inventory; the capture is downloaded from the job report. Specify a single device.',
  'Censisce i database (PostgreSQL, MySQL/MariaDB, Microsoft SQL Server, Oracle) come CI: versione, istanza, elenco e dimensione dei database. Un utente di sola lettura per motore; niente dati dentro le tabelle.':
    'Inventories databases (PostgreSQL, MySQL/MariaDB, Microsoft SQL Server, Oracle) as CIs: version, instance, list and size of the databases. One read-only user per engine; no data from inside the tables.',
  'Il server non ha zone, oppure l’utente non ha i permessi di lettura sul DNS.':
    'The server has no zones, or the user has no read permission on DNS.',
  'Inventario database':
    'Database inventory',
  "Inventario via REST DeviceManager: dischi, pool, LUN e capacità di un array OceanStor/Dorado. L'array si riconosce da uno sweep SNMP; qui si indica il suo indirizzo di gestione e una credenziale DeviceManager.":
    'Inventory via the DeviceManager REST API: disks, pools, LUNs and capacity of an OceanStor/Dorado array. The array is recognised by an SNMP sweep; here you give its management address and a DeviceManager credential.',
  'La zona è troppo grande per una lettura sola: gli ultimi record sono stati tagliati. Quelli mostrati sono corretti, ma non sono tutti.':
    'The zone is too large for a single read: the last records were cut off. Those shown are correct, but they are not all of them.',
  "Rivaluta la postura di sicurezza degli asset utilizzando i dati inventariati nell'ultima scansione effettuata.": 'Re-assess the security posture of assets using the data inventoried in the last scan performed.',
  'Server DNS':
    'DNS server',
  'Storage Huawei OceanStor (REST)':
    'Huawei OceanStor storage (REST)',
  'Valutazione di sicurezza':
    'Security assessment',
  'Zone ({n})':
    'Zones ({n})',
  'statico':
    'static',
  'le zone e i record dei DNS del cliente: PowerDNS (via la sua REST API) e Microsoft DNS (via WinRM sul domain controller). Con più server configurati si sceglie quale guardare. Di Microsoft DNS vedi anche se la zona è integrata in Active Directory, l’aggiornamento dinamico e, sui record registrati dai client, la data di registrazione: un record vecchio è un client che non si registra più.':
    'the zones and records of the customer\'s DNS servers: PowerDNS (via its REST API) and Microsoft DNS (via WinRM on the domain controller). With several servers configured you choose which one to look at. For Microsoft DNS you also see whether the zone is Active Directory-integrated, the dynamic update setting and, on records registered by clients, the registration date: an old record is a client that no longer registers.',
  'Qui si inseriscono anche le credenziali delle piattaforme (vCenter, NexaVM, OceanStor…). Quelle dei sistemi letti in tempo reale (backup, IPAM, DNS, Wazuh, firewall) stanno nel loro modulo, sezione «Connessioni»; ntopng in Integrazioni → Sorgenti dati.':
    'Platform credentials (vCenter, NexaVM, OceanStor…) are entered here too. Those of systems read in real time (backup, IPAM, DNS, Wazuh, firewall) live in their module, «Connections» section; ntopng in Integrations → Data sources.',
  'Per Microsoft DNS crea una credenziale «Microsoft DNS (WinRM)» con il nome del server DNS e un utente che possa leggere il DNS (per esempio del gruppo DnsAdmins): non serve un amministratore di dominio. La sonda esegue sul server solo due letture predefinite — l’elenco delle zone e i record di una zona — e nient’altro. Serve la sonda aggiornata.':
    'For Microsoft DNS create a «Microsoft DNS (WinRM)» credential with the DNS server name and a user that can read DNS (for example a member of DnsAdmins): no domain administrator is needed. The probe runs only two predefined reads on the server — the zone list and the records of a zone — and nothing else. An up-to-date probe is required.',
  'Il riquadro PC si popola dall’inventario dei Windows: la scansione (passo WMI o WinRM del piano) e l’agente d’inventario leggono chi siede alla console. È l’utente davanti al PC — le sessioni RDP non contano — e quando nessuno è collegato il PC si svuota, così una persona non resta su una macchina che ha lasciato. Il dato è quello dell’ultimo inventario: per averlo fresco, pianifica la scansione dei PC di frequente.':
    'The PC box is filled from the Windows inventory: the scan (WMI or WinRM step of the plan) and the inventory agent read who is sitting at the console. It is the user in front of the PC — RDP sessions do not count — and when nobody is logged on the PC is cleared, so a person does not stay on a machine they have left. The data is from the last inventory: to keep it fresh, schedule the PC scan frequently.',
  '5986 (HTTPS) se lasci vuoto; 5985 se usi WinRM in chiaro.':
    '5986 (HTTPS) if left empty; 5985 if you use plain-text WinRM.',
  'A scadenza breve, ma va rinnovato a mano: poco adatto a discovery pianificate.':
    'Short-lived, but must be renewed by hand: not well suited to scheduled discoveries.',
  'ARN del ruolo':
    'Role ARN',
  'AccessKey ID e Secret':
    'AccessKey ID and Secret',
  'Accetta qualunque certificato':
    'Accept any certificate',
  'Active Directory (LDAP)':
    'Active Directory (LDAP)',
  'Algoritmo di autenticazione':
    'Authentication algorithm',
  'Algoritmo di cifratura':
    'Encryption algorithm',
  "Consente il certificate pinning in alternativa alla CA: viene accettato solo il certificato con l'impronta SHA-256 configurata.": 'Enables certificate pinning as an alternative to the CA: only the certificate with the configured SHA-256 fingerprint is accepted.',
  'Assunzione di ruolo (STS)':
    'Role assumption (STS)',
  'Autenticata':
    'Authenticated',
  'Autenticata e cifrata':
    'Authenticated and encrypted',
  'Backup (Veeam, Vinchin, Commvault, Rubrik)':
    'Backup (Veeam, Vinchin, Commvault, Rubrik)',
  'Basic (utente locale)':
    'Basic (local user)',
  'Bind semplice (UPN o DN)':
    'Simple bind (UPN or DN)',
  'CA interna che ha firmato il certificato del sistema: mantiene la verifica TLS completa utilizzando la CA specificata come radice di attendibilità.': "Internal CA that signed the system's certificate: keeps full TLS verification using the specified CA as the trust root.",
  'Certificato del service principal':
    'Service principal certificate',
  'Certificato della CA':
    'CA certificate',
  'Chiave **non cifrata**: una chiave protetta da passphrase non è utilizzabile da un processo non presidiato.':
    '**Unencrypted** key: a passphrase-protected key cannot be used by an unattended process.',
  'Chiave API':
    'API key',
  'Chiave del service account':
    'Service account key',
  'Chiave privata':
    'Private key',
  'Chiave privata di sessione (PEM)':
    'Session private key (PEM)',
  'Chiavi statiche':
    'Static keys',
  'Chiavi temporanee (STS)':
    'Temporary keys (STS)',
  'Community':
    'Community',
  "Consigliato: impedisce che il ruolo venga assunto da chi ne conosce l'ARN per conto di un altro cliente.":
    'Recommended: prevents the role from being assumed on behalf of another customer by anyone who knows its ARN.',
  'Credenziali già temporanee, da rinnovare alla scadenza.':
    'Already temporary credentials, to be renewed when they expire.',
  'DNS (PowerDNS, Microsoft DNS)':
    'DNS (PowerDNS, Microsoft DNS)',
  'Database di connessione':
    'Connection database',
  'Endpoint API':
    'API endpoint',
  'Cynet (EDR/XDR)':
    'Cynet (EDR/XDR)',
  'Client ID, Access Key e Secret Key': 'Client ID, Access Key and Secret Key',
  'Client ID (Site ID)': 'Client ID (Site ID)',
  'Access Key': 'Access Key',
  'Secret Key': 'Secret Key',
  "Le credenziali dell'utente API di Cynet (Settings → API Management → Create API User): Access Key e Secret Key, più il Client ID (Site ID) del sito. CENSO le scambia con un token a scadenza e non le riusa a ogni chiamata. Restano cifrate con la chiave del tenant.":
    'The credentials of the Cynet API user (Settings → API Management → Create API User): Access Key and Secret Key, plus the site Client ID (Site ID). CENSO exchanges them for an expiring token and does not reuse them on every call. They stay encrypted with the tenant key.',
  'https://<istanza>.api.cynet.com':
    'https://<instance>.api.cynet.com',
  "L'indirizzo dell'API di Cynet, nella forma https://<istanza>.api.cynet.com (l'istanza si ricava dall'indirizzo della console). In assenza di schema si usa HTTPS. Se è in cloud scegli «Control Plane» in «Da dove si parla»; se è on-premise nella rete del cliente, scegli una sonda.":
    'The Cynet API address, in the form https://<instance>.api.cynet.com (the instance is derived from the console address). Without a scheme HTTPS is used. If it is in the cloud choose «Control Plane» under «Where from»; if it is on-premise in the customer network, choose a probe.',
  'Cynet': 'Cynet',
  'online': 'online',
  'Chiavi API': 'API keys',
  'Chiave creata': 'Key created',
  'Chiavi esistenti': 'Existing keys',
  'Nuova chiave API': 'New API key',
  'Crea chiave': 'Create key',
  'Creo…': 'Creating…',
  'Nessuna chiave API.': 'No API keys.',
  'Creane una qui sopra per far accedere un sistema esterno.': 'Create one above to let an external system in.',
  'Chiavi per far leggere e scrivere i dati di questo tenant a un sistema esterno (SIEM, SOC, automazione), con il profilo per modulo che decidi tu. Il segreto si mostra una volta sola.':
    'Keys for an external system (SIEM, SOC, automation) to read and write this tenant’s data via API, with the per-module profile you decide. The secret is shown only once.',
  'Copiala adesso: non sarà più visibile dopo aver lasciato questa pagina.':
    'Copy it now: it will not be visible again after you leave this page.',
  'Il profilo può solo restringere rispetto al ruolo di partenza: non concede mai più di quello.':
    'The profile can only narrow the base role: it never grants more than it.',
  'Profilo: cosa può vedere e fare la chiave': 'Profile: what the key can see and do',
  'Revocare questa chiave? Chi la usa perde subito l’accesso.':
    'Revoke this key? Whoever uses it loses access immediately.',
  'Scadenza (facoltativa)': 'Expiry (optional)',
  'Tutti i moduli (predefinito)': 'All modules (default)',
  'es. SIEM, export notturno': 'e.g. SIEM, nightly export',
  'chiavi con cui un sistema esterno (SIEM, SOC, automazione) legge e scrive i dati del tenant via API, con il profilo per modulo che scegli — come un ruolo, e mai più potente di chi la crea. Il segreto si vede una volta sola. Alcune superfici restano off-limits per qualunque chiave: segreti delle credenziali, utenti e ruoli, altre chiavi, amministrazione di piattaforma. È una funzione del piano enterprise, con un freno sulla frequenza delle chiamate; ogni chiave si revoca con un clic e può avere una scadenza.':
    'keys with which an external system (SIEM, SOC, automation) reads and writes the tenant’s data via API, with the per-module profile you choose — like a role, and never more powerful than whoever creates it. The secret is shown only once. Some surfaces stay off-limits to any key: credential secrets, users and roles, other keys, platform administration. It is an enterprise-plan feature, with a brake on the call rate; each key is revoked with a click and can have an expiry.',
  'La copertura dell’EDR Cynet sul parco: quali asset sono protetti, quali no, più allarmi e vulnerabilità sui CI.':
    'Cynet EDR coverage across the estate: which assets are protected, which are not, plus alerts and vulnerabilities on the CIs.',
  'Cynet non è interrogabile.': 'Cynet cannot be queried.',
  'Sincronizzati {h} host, {f} reperti scritti, {c} chiusi.':
    'Synced {h} hosts, {f} findings written, {c} closed.',
  'Host con l’agente': 'Hosts with the agent',
  'Copertura EDR': 'EDR coverage',
  'Degli asset di CENSO che possono portare un agente (PC, server), quanti Cynet li protegge. Gli apparecchi che non possono averlo (medicali, rete, stampanti) sono «non applicabili».':
    'Of CENSO’s assets that can carry an agent (PCs, servers), how many Cynet protects. Devices that cannot have one (medical, network, printers) are «not applicable».',
  'Protetti': 'Protected',
  'Non protetti': 'Not protected',
  'Non applicabili': 'Not applicable',
  'Solo in Cynet': 'Only in Cynet',
  'Asset non protetti dall’EDR': 'Assets not protected by the EDR',
  'Endpoint che CENSO conosce ma su cui Cynet non vede l’agente: da coprire o da verificare.':
    'Endpoints CENSO knows but where Cynet does not see the agent: to cover or to check.',
  'Host solo in Cynet': 'Hosts only in Cynet',
  'Host che Cynet protegge ma CENSO non ha ancora in inventario: la sincronizzazione li aggiunge.':
    'Hosts Cynet protects but CENSO does not have in inventory yet: the sync adds them.',
  'Interrogato': 'Queried',
  'la copertura dell’EDR Cynet: quali asset dell’inventario non hanno l’agente (un controllo NIS2/DORA), più host, allarmi e vulnerabilità. La console è spesso in cloud, quindi la si interroga dal Control Plane; gli allarmi e le vulnerabilità diventano reperti sui CI, gli host con l’agente diventano CI che si fondono con quelli già noti.':
    'Cynet EDR coverage: which inventory assets lack the agent (a NIS2/DORA control), plus hosts, alerts and vulnerabilities. The console is often in the cloud, so it is queried from the Control Plane; alerts and vulnerabilities become findings on the CIs, hosts with the agent become CIs that merge with those already known.',
  'Endpoint Indexer':
    'Indexer endpoint',
  'Equivale a una community in chiaro.':
    'Equivalent to a plain-text community.',
  'Firewall (Palo Alto)':
    'Firewall (Palo Alto)',
  'Firma asimmetrica su ogni richiesta: nessun segreto condiviso viaggia sulla rete. La chiave è revocabile singolarmente dalla console Oracle.':
    'Asymmetric signature on every request: no shared secret travels over the network. The key can be revoked individually from the Oracle console.',
  'Identificativo della regione, non il nome esteso.':
    'Region identifier, not the full name.',
  "Il bind LDAP classico: l'utente è un UPN («utente@dominio») o un DN completo. Da usare **solo su TLS** — in chiaro manda la password leggibile in rete.":
    'The classic LDAP bind: the user is a UPN («user@domain») or a full DN. Use **only over TLS** — in plain text it sends the password readable on the network.',
  'Il campo private_key del file JSON del service account.':
    'The private_key field of the service account JSON file.',
  "Il segreto non lascia mai la sonda: si invia un'asserzione firmata, valida pochi minuti e solo per l'endpoint del token.":
    'The secret never leaves the probe: a signed assertion is sent, valid for a few minutes and only for the token endpoint.',
  'Il segreto viene inviato a ogni autenticazione e vale fino alla sua scadenza.':
    'The secret is sent at every authentication and is valid until it expires.',
  'Il server DNS Windows (di norma un domain controller), per nome o indirizzo, raggiungibile in WinRM dalla sonda scelta.':
    'The Windows DNS server (usually a domain controller), by name or address, reachable over WinRM from the chosen probe.',
  'Impersonificazione di un service account':
    'Service account impersonation',
  'Impronta SHA-256 del certificato':
    'Certificate SHA-256 fingerprint',
  'Impronta della chiave':
    'Key fingerprint',
  "In ntopng si genera da Impostazioni → Utenti, sull'utente che si vuole usare. Revocabile da solo, senza toccare la password.":
    'In ntopng it is generated under Settings → Users, on the user you want to use. It can be revoked on its own, without touching the password.',
  'Interfaccia (ifid)':
    'Interface (ifid)',
  'Interrogazione in tempo reale del backup del cliente via REST API — Veeam Backup & Replication, Vinchin Backup & Recovery, Commvault, Rubrik: stato dei job, repository (capacità e spazio) e oggetti protetti. Non apre porte e non avvia job — la query esce da questa sonda attraverso il relay. Abilitala dove la sonda raggiunge il server di backup.':
    'Real-time querying of the customer’s backup via REST API — Veeam Backup & Replication, Vinchin Backup & Recovery, Commvault, Rubrik: job status, repositories (capacity and space) and protected objects. It opens no ports and starts no jobs — the query goes out from this probe through the relay. Enable it where the probe reaches the backup server.',
  "Interrogazione in tempo reale di un Wazuh sulla rete del cliente: stato degli agenti, conformità SCA, allarmi e vulnerabilità, letti al momento dalla API del manager e dall'Indexer. Non apre porte e non avvia job — la query esce da questa sonda attraverso il relay. Abilitala solo dove la sonda raggiunge davvero Wazuh: è la sonda che il connettore userà.":
    'Real-time querying of a Wazuh instance on the customer\'s network: agent status, SCA compliance, alerts and vulnerabilities, read on demand from the manager API and the Indexer. It opens no ports and starts no jobs — the query leaves from this probe through the relay. Enable it only where the probe can actually reach Wazuh: this is the probe the connector will use.',
  "Interrogazione in tempo reale di un firewall Palo Alto via XML API: il traffico visto da/verso un host (anche «solo bloccato») e il cruscotto dell'apparato (sistema, sessioni, utenti VPN, amministratori). Non apre porte e non avvia job — la query esce da questa sonda attraverso il relay. Abilitala dove la sonda raggiunge la gestione del firewall.":
    'Real-time querying of a Palo Alto firewall via XML API: traffic seen from/to a host (including «blocked only») and the device dashboard (system, sessions, VPN users, administrators). It opens no ports and starts no jobs — the query leaves from this probe through the relay. Enable it where the probe can reach the firewall management.',
  'La revisione dell’header `x-api-version`, legata alla build di VBR. «Automatica» la sceglie CENSO dalla build del server: scegline una solo per forzarla.':
    'The revision of the `x-api-version` header, tied to the VBR build. «Automatic» lets CENSO pick it from the server build: choose one only to force it.',
  "Identificativo dell'istanza PowerDNS usato nell'endpoint /servers/<id>. Se non specificato, viene utilizzato localhost.": 'Identifier of the PowerDNS instance used in the /servers/<id> endpoint. If not specified, localhost is used.',
  "Vuota: l'inventario copre tutte le regioni abilitate dell'account (serve il permesso ec2:DescribeRegions). Un valore: solo quella regione.":
    'Empty: the inventory covers every enabled region of the account (needs the ec2:DescribeRegions permission). A value: only that region.',
  "L'unico livello che protegge anche il contenuto delle risposte.":
    'The only level that also protects the content of responses.',
  "L'utente può essere di dominio («DOMINIO\\\\utente») o locale. L'autenticazione è Basic: su HTTPS è protetta dal TLS, in chiaro no — per questo il passo usa la 5986 se non gli si dice altro.":
    'The user can be a domain user («DOMAIN\\\\user») or a local one. Authentication is Basic: over HTTPS it is protected by TLS, in plain text it is not — which is why this step uses 5986 unless told otherwise.',
  "L'utente si scrive «DOMINIO\\\\utente» o «utente@dominio». Basta un account di sola lettura del dominio: leggere gli oggetti non richiede privilegi. Su ldaps (default) il bind è protetto dal TLS.":
    'Write the user as «DOMAIN\\\\user» or «user@domain». A read-only domain account is enough: reading objects requires no privileges. Over ldaps (default) the bind is protected by TLS.',
  "La `api-key` configurata in PowerDNS (`api-key=` nel config). Viaggia nell'header `X-API-Key`. Revocabile cambiandola nel config. Vale anche per scrivere: CENSO scrive solo se accendi «Consenti la scrittura».":
    'The `api-key` configured in PowerDNS (`api-key=` in the config). It travels in the `X-API-Key` header. Revocable by changing it in the config. It is also valid for writing: CENSO writes only if you turn on «Allow writing».',
  'La chiave API di PAN-OS, generata per un amministratore dedicato in **sola lettura** (Device → Admin Roles, con la scheda XML API abilitata). Si ottiene una volta da «/api/?type=keygen&user=…&password=…» e si incolla qui: è revocabile da sola e non coincide con la password interattiva.':
    'The PAN-OS API key, generated for a dedicated **read-only** administrator (Device → Admin Roles, with the XML API tab enabled). Obtain it once from «/api/?type=keygen&user=…&password=…» and paste it here: it can be revoked on its own and is not the interactive password.',
  "La chiave distribuita appartiene a un'identità che può solo impersonare: revocare l'accesso non richiede di ruotarla sulle sonde.":
    'The distributed key belongs to an identity that can only impersonate: revoking access does not require rotating it on the probes.',
  'La chiave vale finché non viene eliminata dal progetto.':
    'The key is valid until it is deleted from the project.',
  'La chiave è preferibile: una password riusata su molti host è un solo segreto che apre tutto il parco.':
    'A key is preferable: a password reused across many hosts is a single secret that opens the whole estate.',
  'La community viaggia in chiaro sulla rete: preferire SNMP v3 dove gli apparati lo supportano.':
    'The community travels in plain text over the network: prefer SNMP v3 where devices support it.',
  'La coppia che NexaSphere/NexaVM mostrano in «AccessKey». Il segreto non viene mai inviato: firma ogni richiesta. Revocabile da sola e indipendente dalle credenziali interattive di un amministratore.':
    'The pair shown by NexaSphere/NexaVM under «AccessKey». The secret is never sent: it signs each request. It can be revoked on its own and is independent of an administrator\'s interactive credentials.',
  "La sonda ottiene la chiave API dal firewall a ogni interrogazione (keygen). Usare un'utenza **locale** dedicata in sola lettura con la XML API abilitata: un account RADIUS/TACACS+ non genera la chiave.":
    'The probe obtains the API key from the firewall at every query (keygen). Use a dedicated **local** read-only account with the XML API enabled: a RADIUS/TACACS+ account does not generate the key.',
  "Le chiavi salvate servono solo a chiedere credenziali temporanee, che scadono da sole dopo un'ora.":
    'The stored keys are only used to request temporary credentials, which expire on their own after an hour.',
  'Le risposte viaggiano in chiaro.':
    'Responses travel in plain text.',
  'Lettura di zone e record dei DNS del cliente in tempo reale attraverso la sonda: PowerDNS via REST API, Microsoft DNS via WinRM (solo gli script di lettura del catalogo della sonda). Non apre porte e non avvia job. Abilitala dove la sonda raggiunge il server DNS.':
    'Real-time reading of the customer\'s DNS zones and records through the probe: PowerDNS via REST API, Microsoft DNS via WinRM (only the read scripts in the probe\'s catalogue). It opens no ports and starts no jobs. Enable it where the probe can reach the DNS server.',
  'Microsoft DNS (WinRM)':
    'Microsoft DNS (WinRM)',
  'NTLM (utente e password)':
    'NTLM (username and password)',
  'Nessuna autenticazione':
    'No authentication',
  'Nome sessione':
    'Session name',
  'OCID del compartimento':
    'Compartment OCID',
  "OCID dell'utente":
    'User OCID',
  'OCID della tenancy':
    'Tenancy OCID',
  'Osservazione del traffico, in due modi che questa sola funzione abilita entrambi: ricevere i flussi esportati dagli apparati (NetFlow/IPFIX/sFlow — apre una porta in ascolto), oppure catturarli da una porta mirror (nessuna porta in ingresso). Una sonda che fa solo cattura abilita comunque questa: quale sorgente usare si sceglie nella sua configurazione dei flussi, non con una capability diversa.':
    'Traffic observation, in two ways that this single capability enables together: receiving flows exported by devices (NetFlow/IPFIX/sFlow — opens a listening port), or capturing them from a mirror port (no inbound port). A probe that only captures still enables this one: which source to use is chosen in its flow configuration, not with a different capability.',
  'Palo Alto (firewall)':
    'Palo Alto (firewall)',
  'Passphrase della chiave':
    'Key passphrase',
  'Passphrase di autenticazione':
    'Authentication passphrase',
  'Passphrase di cifratura':
    'Encryption passphrase',
  'Password API':
    'API password',
  'Password Indexer':
    'Indexer password',
  'Porta DeviceManager':
    'DeviceManager port',
  'Porta REST del DeviceManager. Predefinita 8088.':
    'DeviceManager REST port. Default 8088.',
  'Porta WinRM':
    'WinRM port',
  'Project ID':
    'Project ID',
  'Protocollo obsoleto: usarlo solo con apparati che non offrono altro.':
    'Obsolete protocol: use it only with devices that offer nothing else.',
  "Quale interfaccia di ntopng leggere. Vuoto significa la prima, che su quasi tutte le installazioni è l'unica. Si legge una sola interfaccia per volta: sommarne due che vedono lo stesso segmento produrrebbe lo stesso host due volte.":
    'Which ntopng interface to read. Empty means the first one, which on almost every installation is the only one. Only one interface is read at a time: adding up two that see the same segment would produce the same host twice.',
  'Quella mostrata da Oracle accanto alla chiave API caricata.':
    'The one Oracle shows next to the uploaded API key.',
  'Restano valide finché non vengono revocate: usare un utente IAM dedicato con i soli permessi di lettura.':
    'They remain valid until revoked: use a dedicated IAM user with read-only permissions.',
  'Revocabile da solo e spesso a scadenza, senza coincidere con le credenziali interattive di un amministratore.':
    'Revocable on its own and often time-limited, without being the same as an administrator\'s interactive credentials.',
  'SERVER\\utente':
    'SERVER\\user',
  'SSH con chiave':
    'SSH with key',
  'SSH con password':
    'SSH with password',
  "Sconsigliato: disabilita la verifica dell'identità del server ed espone ad attacchi. Consigliata una CA attendibile o l'impronta SHA-256 del certificato.": "Not recommended: it disables verification of the server's identity and exposes to attacks. A trusted CA or the certificate's SHA-256 fingerprint is advised.",
  'Scope pieno (serve a Cloud Functions e Cloud SQL)':
    'Full scope (needed for Cloud Functions and Cloud SQL)',
  "Senza, il token legge solo Compute e Storage — che è il grosso dell'inventario — e i due servizi vengono saltati con la ragione scritta nel rapporto. Con, il token porta anche cloud-platform: da quel momento la sola lettura la garantisce il ruolo IAM del service account, che va tenuto a roles/viewer.":
    'Without it, the token reads only Compute and Storage — the bulk of the inventory — and the two services are skipped with the reason stated in the report. With it, the token also carries cloud-platform: from then on read-only access is guaranteed only by the service account\'s IAM role, which must be kept at roles/viewer.',
  'Server':
    'Server',
  'Service account':
    'Service account',
  'Service account da impersonare':
    'Service account to impersonate',
  'Service account della sonda':
    'Probe service account',
  'Service name':
    'Service name',
  'Session token':
    'Session token',
  "Solo se sul server è abilitata l'autenticazione Basic di WinRM, e solo su HTTPS: in chiaro manderebbe la password leggibile.":
    'Only if Basic authentication is enabled for WinRM on the server, and only over HTTPS: in plain text it would send the password readable.',
  'Solo su un segmento fidato: con NTLM la password non viaggia, ma il contenuto sì. Il default è HTTPS sulla 5986.':
    'Only on a trusted segment: with NTLM the password does not travel, but the content does. The default is HTTPS on 5986.',
  'Spento di default: WinRM usa quasi sempre il certificato autofirmato di `winrm quickconfig`. Accendilo se il server ha un certificato della tua PKI.':
    'Off by default: WinRM almost always uses the self-signed certificate from `winrm quickconfig`. Turn it on if the server has a certificate from your PKI.',
  'Subscription ID':
    'Subscription ID',
  'Tenant ID':
    'Tenant ID',
  'Token':
    'Token',
  'Token API':
    'API token',
  'Token di sessione':
    'Session token',
  'Traffico (NetFlow e cattura)':
    'Traffic (NetFlow and capture)',
  "URL del Wazuh Indexer (OpenSearch), porta predefinita 9200. Da Wazuh 4.8 è necessario per acquisire vulnerabilità e allarmi, non più disponibili tramite l'API sulla porta 55000. Se non configurato, vulnerabilità e allarmi non vengono acquisiti.": 'URL of the Wazuh Indexer (OpenSearch), default port 9200. From Wazuh 4.8 it is required to collect vulnerabilities and alerts, no longer available through the API on port 55000. If not configured, vulnerabilities and alerts are not collected.',
  'URL del vCenter.':
    'vCenter URL.',
  'Endpoint della REST API di PowerDNS Authoritative Server, porta predefinita 8081 e schema HTTP se omesso. Richiede api=yes e webserver=yes nella configurazione del server.': 'Endpoint of the PowerDNS Authoritative Server REST API, default port 8081 and HTTP scheme if omitted. Requires api=yes and webserver=yes in the server configuration.',
  'URL della REST API di Veeam Backup & Replication (VBR), porta predefinita 9419. Se lo schema è omesso, viene usato HTTPS. La documentazione Swagger è disponibile in /swagger/index.html.': 'URL of the Veeam Backup & Replication (VBR) REST API, default port 9419. If the scheme is omitted, HTTPS is used. The Swagger documentation is available at /swagger/index.html.',
  "URL della Wazuh API, porta predefinita 55000. In assenza di schema viene utilizzato HTTPS. La sonda interroga questo endpoint per acquisire agenti e risultati SCA; per l'utenza API dedicata, consulta la guida qui sopra.": 'URL of the Wazuh API, default port 55000. HTTPS is used when the scheme is omitted. The probe queries this endpoint to collect agents and SCA results; for the dedicated API user, see the guide above.',
  'URL della console aCloud.':
    'aCloud console URL.',
  'URL della console di ntopng. La porta predefinita è la 3000. Se manca lo schema si assume «https».':
    'URL of the ntopng console. The default port is 3000. If the scheme is missing, «https» is assumed.',
  'URL della gestione del firewall (o di Panorama): la XML API risponde sulla stessa porta della console, di norma la 443. Senza schema si assume «https».':
    'URL of the firewall management (or Panorama): the XML API answers on the same port as the console, usually 443. Without a scheme, «https» is assumed.',
  "URL delle **API** del management node, con lo schema per esteso: di norma «http://indirizzo:8080», spesso in chiaro — non l'indirizzo della console web, che risponde in HTTPS su un'altra porta. Stesse API di ZStack: l'utente può essere un account o un sub-account («utente@account»).":
    'URL of the management node **API**, with the full scheme: usually «http://address:8080», often in plain text — not the web console address, which answers over HTTPS on another port. Same API as ZStack: the user can be an account or a sub-account («user@account»).',
  "URL delle **API** del management node, con lo schema per esteso: di norma «http://indirizzo:8080», spesso in chiaro. La console web risponde in HTTPS su un'altra porta e alle API restituisce la propria pagina, quindi puntarla non dà un errore ma una risposta HTML. L'utente può essere un account («admin») o un sub-account («utente@account»).":
    'URL of the management node **API**, with the full scheme: usually «http://address:8080», often in plain text. The web console answers over HTTPS on another port and returns its own page to API calls, so pointing at it gives not an error but an HTML response. The user can be an account («admin») or a sub-account («user@account»).',
  'URL di Prism Central (porta 9440 se non standard).':
    'Prism Central URL (port 9440 if non-standard).',
  "URL di un nodo o del cluster, di norma sulla porta 8006. Con il token API serve la forma completa «utente@realm!nome=segreto»; con utente e password il realm è obbligatorio («root@pam»). Serve il ruolo **PVEAuditor su «/» con propagazione**: senza, Proxmox non dà errore — risponde con meno dati, e l'inventario esce con i nomi e senza le misure. Attenzione ai token API: nascono con la separazione dei privilegi attiva, quindi hanno solo i ruoli assegnati **al token**, non quelli dell'utente che lo possiede.":
    'URL of a node or of the cluster, usually on port 8006. With an API token the full form «user@realm!name=secret» is required; with username and password the realm is mandatory («root@pam»). The **PVEAuditor role on «/» with propagation** is required: without it Proxmox raises no error — it returns less data, and the inventory comes out with names but no measurements. Beware of API tokens: they are created with privilege separation enabled, so they only have the roles assigned **to the token**, not those of the user who owns it.',
  "Un login di sola lettura basta: l'inventario legge solo le viste di sistema (versione, elenco e dimensione dei database).":
    'A read-only login is enough: the inventory only reads system views (version, list and size of databases).',
  "Un ruolo di sola lettura basta: l'inventario interroga solo i cataloghi di sistema (versione, elenco e dimensione dei database). Il database a cui connettersi di default è «postgres».":
    'A read-only role is enough: the inventory only queries system catalogues (version, list and size of databases). The default database to connect to is «postgres».',
  "Un utente di sola lettura basta: l'inventario legge solo `information_schema` (versione, schemi e loro dimensione).":
    'A read-only user is enough: the inventory only reads `information_schema` (version, schemas and their size).',
  "Un utente di sola lettura basta: l'inventario legge solo le viste di sistema (versione, PDB e loro dimensione). Il «service name» è il servizio a cui connettersi (default «XE»).":
    'A read-only user is enough: the inventory only reads system views (version, PDBs and their size). The «service name» is the service to connect to (default «XE»).',
  'Utenza API Wazuh dedicata e in sola lettura, gestita tramite RBAC. La sonda si autentica via API e utilizza un token JWT a scadenza. Le credenziali predefinite devono essere cambiate.': 'Dedicated read-only Wazuh API user, managed through RBAC. The probe authenticates via the API and uses an expiring JWT token. The default credentials must be changed.',
  'Una chiave dedicata alla discovery, con `command=` o una shell ristretta sul lato host. Non riusare la chiave con cui gli amministratori entrano a mano.':
    'A key dedicated to discovery, with `command=` or a restricted shell on the host side. Do not reuse the key administrators log in with manually.',
  "Usare un'utenza dedicata in sola lettura, non quella di un amministratore.":
    'Use a dedicated read-only account, not an administrator\'s.',
  "Usare un'utenza dedicata in sola lettura. ntopng risponde alla chiamata REST con una pagina di login HTML quando l'utenza non è accettata, e la sonda lo dice invece di leggerla come «nessun host».":
    'Use a dedicated read-only account. ntopng answers the REST call with an HTML login page when the account is not accepted, and the probe reports this instead of reading it as «no hosts».',
  'Utente API':
    'API user',
  'Utente Indexer':
    'Indexer user',
  'Utente e password':
    'Username and password',
  "Utente interno del Wazuh Indexer, separato dall'utenza API, con permessi di sola lettura sugli indici wazuh-alerts-* e wazuh-states-vulnerabilities-*. Si configura nella dashboard in Indexer management → Security → Internal users.": 'Internal Wazuh Indexer user, separate from the API user, with read-only permissions on the wazuh-alerts-* and wazuh-states-vulnerabilities-* indices. Configured in the dashboard under Indexer management → Security → Internal users.',
  'Verifica il certificato':
    'Verify the certificate',
  'Versione API':
    'API version',
  "Vuoto: si interroga l'intera tenancy, che ne è la radice.":
    'Empty: the whole tenancy, which is its root, is queried.',
  'Wazuh (SIEM/XDR)':
    'Wazuh (SIEM/XDR)',
  'WinRM in chiaro (HTTP)':
    'Plain-text WinRM (HTTP)',
  'Windows (WinRM)':
    'Windows (WinRM)',
  'censo@progetto.iam.gserviceaccount.com':
    'censo@project.iam.gserviceaccount.com',
  'dc01.dominio.local':
    'dc01.domain.local',
  'inventario@progetto.iam.gserviceaccount.com':
    'inventory@project.iam.gserviceaccount.com',
  '«DOMINIO\\utente». Per leggere basta un utente con lettura sul server DNS; per scrivere (se accendi «Consenti la scrittura») serve la scrittura sulle zone, per esempio un membro di «DnsAdmins»: non serve un amministratore di dominio.':
    '«DOMAIN\\user». To read, a user with read access to the DNS server is enough; to write (if you turn on «Allow writing») it needs write access to the zones, for example a member of «DnsAdmins»: no domain administrator needed.',
  'Esportazione non riuscita (HTTP {status})':
    'Export failed (HTTP {status})',
  '180 giorni':
    '180 days',
  '1 anno':
    '1 year',
  '{n} riga · generato il {quando}':
    '{n} row · generated on {quando}',
  '{n} righe · generato il {quando}':
    '{n} rows · generated on {quando}',
  'CPU (ultimi {s}s)':
    'CPU (last {s}s)',
  '{n} core':
    '{n} cores',
  'nessun limite impostato':
    'no limit set',
  'Memoria letta da {fonte}: dentro un container /proc/meminfo mostra la memoria dell’host, e un servizio con 512 MB di limite su una macchina da 64 GB risulterebbe al 3% un istante prima di essere ucciso.':
    'Memory read from {fonte}: inside a container /proc/meminfo shows the host’s memory, and a service with a 512 MB limit on a 64 GB machine would show 3% a moment before being killed.',
  'Carico della macchina (non del servizio): {carico} su {cpu} CPU visibili.':
    'Machine load (not the service’s): {carico} across {cpu} visible CPUs.',
  'Disco {percorso}':
    'Disk {percorso}',
  'Store del traffico ({backend})':
    'Traffic store ({backend})',
  'Store del traffico':
    'Traffic store',
  'Non è possibile sapere quanto spazio occupa il traffico né quanto ne resta sul suo volume.':
    'There is no way to know how much space the traffic takes up or how much is left on its volume.',
  'Volume {nome}':
    'Volume {nome}',
  'Misurato il {quando}. I numeri non si aggiornano da soli: la pagina resta ferma finché non si preme «Aggiorna».':
    'Measured on {quando}. The figures do not update on their own: the page stays as it is until you press “Refresh”.',
  'sonda mancante':
    'probe missing',
  'riuscito':
    'succeeded',
  'fallito':
    'failed',
  'Contiene':
    'Contains',
  'Non contiene':
    'Does not contain',
  'È uguale a':
    'Equals',
  'Inizia con':
    'Starts with',
  '{n} righe':
    '{n} rows',
  '{n} di {tot} righe':
    '{n} of {tot} rows',
  'Copia {cosa}':
    'Copy {cosa}',
  'copiato':
    'copied',
  'copia':
    'copy',
  'Ospita macchine virtuali':
    'Hosts virtual machines',
  'Gestito fuori banda':
    'Managed out of band',
  'Membro di un cluster':
    'Cluster member',
  'Attivo':
    'Active',
  'Silente':
    'Silent',
  'Scomparso':
    'Missing',
  'Dismesso':
    'Retired',
  'Caricamento…':
    'Loading…',
  '{n}s fa':
    '{n}s ago',
  '{n} min fa':
    '{n} min ago',
  '{n} h fa':
    '{n} h ago',
  '{n} g fa':
    '{n} d ago',
  'Creare, sospendere, eliminare, assegnare indirizzi':
    'Create, suspend, delete, assign addresses',
  'Creare utenze e abilitarle sui tenant, con i ruoli':
    'Create accounts and grant them access to tenants, with roles',
  'Piano assegnato, consumo rispetto alle quote, scadenze':
    'Assigned plan, usage against quotas, expiry dates',
  'Spazio e oggetti per tenant, dal più ingombrante':
    'Space and objects per tenant, largest first',
  'CPU, memoria, dischi: se e quando scalare':
    'CPU, memory, disks: whether and when to scale',
  'Dominio, hostname di servizio, certificato':
    'Domain, service hostname, certificate',
  'Riconoscimento del prodotto: un ente SNMP → una classe, per tutti i clienti':
    'Product recognition: one SNMP enterprise → one class, for all customers',
  'Numeri di tutti i clienti insieme, esportabili':
    'Figures for all customers together, exportable',
  'Chi è entrato, su quale tenant, con che esito — e i login falliti':
    'Who signed in, on which tenant, with what outcome — and the failed logins',
  'Accesso non riuscito: riprova':
    'Sign-in failed: please try again',
  'Questa utenza non ha il ruolo provider_admin: per l’amministrazione serve un’utenza di piattaforma':
    'This account does not have the provider_admin role: administration requires a platform account',
  'Accesso…':
    'Signing in…',
  'Entra':
    'Sign in',
  'Job di discovery':
    'Discovery jobs',
  'Eventi dei job':
    'Job events',
  'L’identificativo corrisponde al nome breve del tenant presente nell’URL; l’ID interno è l’identificativo univoco utilizzato dal supporto per individuare il tenant interessato.': 'The identifier is the tenant’s short name present in the URL; the internal ID is the unique identifier used by support to locate the tenant concerned.',
  'Si applicano i valori di prova del piano «{piano}»: i limiti qui sotto sono quelli, e le funzioni oltre la rete — cloud, virtualizzazione, inventario degli host — restano negate. Le discovery di rete funzionano.':
    'The trial values of the “{piano}” plan apply: the limits below are those, and features beyond the network — cloud, virtualisation, host inventory — remain unavailable. Network discovery works.',
  'Era valida fino al {data}.':
    'It was valid until {data}.',
  'Fino al rinnovo valgono i limiti di prova, che sono quelli mostrati qui sotto.':
    'Until renewal the trial limits apply, which are the ones shown below.',
  'scade il {data}':
    'expires on {data}',
  'il piano non pone un limite':
    'the plan sets no limit',
  'nessun piano assegnato':
    'no plan assigned',
  'Questi conteggi sono esatti. Sono anche quelli confrontati con le quote della licenza.':
    'These counts are exact. They are also the ones compared against the licence quotas.',
  'lo store del traffico non risponde':
    'the traffic store is not responding',
  'Viene ripartito fra i tenant in proporzione alle righe: né PostgreSQL né ClickHouse sanno attribuire i byte a un tenant senza rileggere tutti i dati, e farlo a ogni apertura di questa pagina rallenterebbe l’inventario di tutti. Serve a capire dove cresce, non a fatturare a byte — per quello ci sono i conteggi, che sono esatti.':
    'It is split across tenants in proportion to their rows: neither PostgreSQL nor ClickHouse can attribute bytes to a tenant without rereading all the data, and doing so every time this page opens would slow down everyone’s inventory. It helps you see where things grow, not bill by the byte — the counts, which are exact, are for that.',
  'Tenant ({n})':
    'Tenants ({n})',
  'Crea tenant':
    'Create tenant',
  '+ {n} alias ancora valido':
    '+ {n} alias still valid',
  '+ {n} alias ancora validi':
    '+ {n} aliases still valid',
  'sospeso':
    'suspended',
  '{n} sonde':
    '{n} probes',
  'Sospendi':
    'Suspend',
  'Verranno cancellati CMDB, flussi, credenziali, sonde, integrazioni, OpenID, licenza e ordini di «{nome}» ({ci} CI, {sonde} sonde). Gli utenti che appartengono anche ad altri tenant restano. Per procedere scrivi {slug}.':
    'CMDB, flows, credentials, probes, integrations, OpenID, licence and orders of «{nome}» ({ci} CIs, {sonde} probes) will be deleted. Users who also belong to other tenants remain. To proceed, type {slug}.',
  'Utenti di {tenant}':
    'Users of {tenant}',
  'Licenze ({n})':
    'Licences ({n})',
  'Fra 1 e 3650 giorni.':
    'Between 1 and 3650 days.',
  'listino: illimitato':
    'list: unlimited',
  'listino: {valore}':
    'list: {valore}',
  'Riapplicare «{piano}» a tutti i clienti che ce l’hanno?':
    'Reapply “{piano}” to all the customers who have it?',
  '«{piano}» riapplicato a {n} clienti.':
    '“{piano}” reapplied to {n} customers.',
  'Riportare «{piano}» ai valori di serie del prodotto?':
    'Reset “{piano}” to the product’s factory defaults?',
  'Rimette le funzioni e le quote come nascono di serie. Le licenze già emesse non cambiano: per allinearle usa «Riapplica ai clienti».':
    'It puts the features and quotas back as they ship by default. Licences already issued do not change: to bring them in line, use “Reapply to the customers”.',
  '«{piano}» riportato ai valori di serie.':
    '“{piano}” reset to factory defaults.',
  '{funzione} in {piano}':
    '{funzione} in {piano}',
  'Il job non è ancora stato eseguito da una sonda.':
    'The job has not yet been run by a probe.',
  'La sonda che ha eseguito questo job non produce ancora un rapporto: aggiornala.':
    'The probe that ran this job does not produce a report yet: update it.',
  'Mostrati {n} eventi su {tot}: usa i filtri per restringere.':
    'Showing {n} of {tot} events: use the filters to narrow down.',
  'Eseguito, con dati raccolti.':
    'Run, with data collected.',
  'A vuoto':
    'Empty run',
  'Eseguito e non ha raccolto niente: gli host hanno risposto e non avevano quel dato, oppure il passo cerca qualcosa che lì non c’è.':
    'Run but collected nothing: the hosts responded and did not have that data, or the step looks for something that is not there.',
  'Mai partito':
    'Never started',
  'Non è partito su nessun indirizzo: la condizione non è mai stata soddisfatta. La scansione risulta riuscita e i dati di questo passo non ci sono.':
    'It did not start on any address: the condition was never met. The scan shows as successful and the data from this step is missing.',
  'Sconosciuto alla sonda':
    'Unknown to the probe',
  'Questa sonda non sa eseguire il passo: va aggiornata.':
    'This probe cannot run the step: it needs updating.',
  '{n} oggetti raccolti ({tipi})':
    '{n} objects collected ({tipi})',
  '{n} interfacce':
    '{n} interfaces',
  '{n} adiacenze':
    '{n} adjacencies',
  '{n} prodotti software':
    '{n} software products',
  '{n} schede macchina':
    '{n} machine records',
  'in CMDB: {nuovi} nuovi, {noti} già noti':
    'in the CMDB: {nuovi} new, {noti} already known',
  '{n} non salvati, vedi la fase «ingest»':
    '{n} not saved, see the “ingest” stage',
  '{n} per condizione':
    '{n} by condition',
  '{n} passo ignoto':
    '{n} unknown step',
  '{n} attributi su {m} indirizzi':
    '{n} attributes on {m} addresses',
  'Il comando vale un’ora (fino alle {ora}), e contiene il segreto di questo agent: è l’unica volta in cui compare. Passata la finestra si rigenera — ma rigenerare invalida il segreto vecchio, quindi se l’esca era già installata smette di consegnare finché non la reinstalli.':
    'The command is valid for one hour (until {ora}) and contains this agent’s secret: this is the only time it is shown. Once the window has passed it can be regenerated — but regenerating invalidates the old secret, so if the decoy was already installed it stops delivering until you reinstall it.',
  'Il comando vale un’ora, e contiene il segreto di questo agent: è l’unica volta in cui compare. Passata la finestra si rigenera — ma rigenerare invalida il segreto vecchio, quindi se l’esca era già installata smette di consegnare finché non la reinstalli.':
    'The command is valid for one hour and contains this agent’s secret: this is the only time it is shown. Once the window has passed it can be regenerated — but regenerating invalidates the old secret, so if the decoy was already installed it stops delivering until you reinstall it.',
  'Copia il binario {binario} sulla macchina dedicata, e questo file in {percorso} con permessi {permessi}.':
    'Copy the {binario} binary to the dedicated machine, and this file to {percorso} with permissions {permessi}.',
  '{spazio} liberi':
    '{spazio} free',
  'risposta di accesso senza token':
    'sign-in response without a token',
  'risposta di cambio tenant senza token':
    'tenant switch response without a token',
  'Il tenant più grande è {nome} con {spazio}, il {percento}% del totale.':
    'The largest tenant is {nome} with {spazio}, {percento}% of the total.',
  'Sopra la metà del totale conviene agire su questo cliente — retention del traffico, o piano — prima di allargare il sistema.':
    'Above half of the total it is worth acting on this customer — traffic retention, or plan — before expanding the system.',
  'Il consumo è distribuito: se lo spazio scarseggia, va allargato il sistema, non un singolo cliente.':
    'Usage is spread out: if space runs short, expand the system rather than acting on a single customer.',
  'Le colonne del traffico sono vuote perché non è stato possibile interrogarlo, non perché non ci sia traffico.':
    'The traffic columns are empty because it could not be queried, not because there is no traffic.',
  'Entrambi interni':
    'Both internal',
  'Conserva solo il traffico tra indirizzi appartenenti al perimetro, riducendo al minimo i dati raccolti. Il traffico da e verso Internet viene escluso, quindi i servizi utilizzati esclusivamente dall’esterno non vengono rilevati come entry point.':
    'Only traffic between two addresses inside the perimeter is kept. It saves the most space, and also discards traffic to and from the Internet: a service used only from outside will not appear among the entry points.',
  'Almeno uno interno':
    'At least one internal',
  'Include il traffico da e verso Internet, mantenendo visibilità sui mittenti che accedono ai servizi pubblicati. Il volume di dati raccolti è maggiore.':
    'Traffic to and from the Internet is kept as well. This is the choice for those with published services who want to see who connects to them. It saves much less.',
  'Nessun perimetro':
    'No perimeter',
  'Acquisisce tutto il traffico, mantenendo attive le esclusioni e le soglie definite sulle sonde.':
    'Everything is kept. Exclusions and probe thresholds still apply.',
  'Filtro salvato.':
    'Filter saved.',
  'Salva filtro':
    'Save filter',
  'Inserisci un indirizzo per riga; per gli indirizzi singoli /32 è facoltativo. Se l’elenco è vuoto, il filtro viene disabilitato e tutto il traffico resta acquisito, evitando esclusioni involontarie.':
    'One per line. A single address is fine without {suffisso}.',
  "Se svuoti l'elenco il filtro si spegne invece di scartare tutto: un elenco vuoto che buttasse via ogni cosa farebbe sparire il traffico senza alcun errore.":
    'If you empty the list the filter switches off instead of discarding everything: an empty list that threw everything away would make traffic vanish without any error.',
  'Vale dal prossimo giro di configurazione delle sonde e non tocca i dati già raccolti: filtrare a posteriori sarebbe una cancellazione di massa innescata da una modifica di configurazione. Per lo spazio già occupato c’è la conservazione, che è una decisione separata.':
    'It applies from the next probe configuration round and does not touch data already collected: filtering after the fact would be a mass deletion triggered by a configuration change. For space already in use there is retention, which is a separate decision.',
  '{n} di {tot} già registrati':
    '{n} of {tot} already registered',
  '{n} coperti oggi':
    '{n} covered today',
  'Solo i beni senza contratto':
    'Only assets without a contract',
  'Beni ({n})':
    'Assets ({n})',
  '(senza nome)':
    '(unnamed)',
  'CI collegato':
    'Linked CI',
  'scaduta da {n} g':
    'expired {n} d ago',
  'fra {n} g':
    'in {n} d',
  'Nuovo servizio':
    'New service',
  'Modifica servizio':
    'Edit service',
  '— indica un indirizzo —':
    '— enter an address —',
  'es. {esempio}':
    'e.g. {esempio}',
  'Crea servizio':
    'Create service',
  'Eliminare il piano «{nome}»? Le scansioni già eseguite restano.':
    'Delete the plan “{nome}”? Scans already run are kept.',
  'Creandone uno si parte da una sequenza già sensata: ping, poi il nome, poi le porte, e i passi cari solo dove il servizio c’è.':
    'Creating one starts from a sequence that already makes sense: ping, then the name, then the ports, and the expensive steps only where the service is present.',
  '{n} avvisi':
    '{n} warnings',
  'Il piano non ha passi: la scansione riuscirebbe senza guardare niente.':
    'The plan has no steps: the scan would succeed without looking at anything.',
  '«{nome}»: job {job}… avviato su {ambito}.':
    '“{nome}”: job {job}… started on {ambito}.',
  'Vuoto: si usano gli ambiti predefiniti del piano — {ambiti}.':
    'Empty: the plan’s default scopes are used — {ambiti}.',
  'Il piano non ha ambiti predefiniti: senza un ambito qui la scansione non avrebbe indirizzi su cui girare.':
    'The plan has no default scopes: without a scope here the scan would have no addresses to run on.',
  'non in linea':
    'offline',
  'Avvia adesso':
    'Start now',
  '1 errore':
    '1 error',
  '{n} errori':
    '{n} errors',
  '1 avviso':
    '1 warning',
  'Avvia: {tipo}':
    'Start: {tipo}',
  'Pianifica: {tipo}':
    'Schedule: {tipo}',
  'Rapporto':
    'Report',
  'Pianifica come questa':
    'Schedule like this one',
  'Il tipo {tipo} non è più fra quelli disponibili: questa scansione non si può ripianificare così com’è.':
    'The type {tipo} is no longer available: this scan cannot be rescheduled as it is.',
  'Pianifica come: {nome}':
    'Schedule like: {nome}',
  'Questa scansione è nata dalla pianificazione «{nome}». Crearne un’altra uguale raddoppia il carico sulle sonde: ha senso solo se vuoi una ricorrenza diversa.':
    'This scan came from the schedule “{nome}”. Creating an identical one doubles the load on the probes: it only makes sense if you want a different recurrence.',
  'Ripresi da questa scansione: ambito, credenziali, sonda, opzioni e ritmo di interrogazione ({pps} pacchetti al secondo). Resta da decidere quando.':
    'Carried over from this scan: scope, credentials, probe, options and query rate ({pps} packets per second). What is left is deciding when.',
  'I pool si riprendono per riferimento: la pianificazione seguirà le reti che verranno aggiunte.':
    'Pools are carried over by reference: the schedule will follow the networks added later.',
  '{n} asset indirizzabili in CMDB.':
    '{n} addressable assets in the CMDB.',
  '{n} visti da più fonti (lavoro doppio).':
    '{n} seen by multiple sources (duplicated work).',
  '(primi {n})':
    '(first {n})',
  'Valida fino al {data}.':
    'Valid until {data}.',
  'Il piano {piano} comprende {funzioni}, ma la licenza è stata emessa prima che questa funzione esistesse e non la concede. Chiedi al fornitore di riassegnare lo stesso piano per attivarla.':
    'The {piano} plan includes {funzioni}, but the licence was issued before this feature existed and does not grant it. Ask your supplier to reassign the same plan to enable it.',
  'La nuova password e la conferma non coincidono':
    'The new password and the confirmation do not match',
  'Almeno {n} caratteri.':
    'At least {n} characters.',
  'Aggiornamento…':
    'Updating…',
  'Cambia password':
    'Change password',
  'Classi e categorie':
    'Classes and categories',
  'Catalogo prodotti (SKU)':
    'Product catalogue (SKU)',
  'Hanno precedenza sulle regole della piattaforma e vengono applicate in ordine di priorità. La prima regola corrispondente viene utilizzata; le classificazioni manuali non vengono sovrascritte.':
    'Evaluated before the platform’s rules, in order of priority: the first match wins. A class chosen by hand on a single CI is still left untouched.',
  'Il prodotto ha {n} voci di riconoscimento che questo tenant non ha ancora. Applicarle non modifica né cancella niente di ciò che hai già.':
    'The product has {n} recognition entries that this tenant does not have yet. Applying them neither changes nor deletes anything you already have.',
  '— nessuna —':
    '— none —',
  '— nessuno —':
    '— none —',
  '— scegli —':
    '— choose —',
  'Corrisponde a {n} CI, di cui {cambi} cambierebbero classe — {manuali} hanno la classe scelta a mano e non verrebbero toccati.':
    'Matches {n} CIs, of which {cambi} would change class — {manuali} have a hand-picked class and would not be touched.',
  'Corrisponde a {n} CI, di cui {cambi} cambierebbero classe.':
    'Matches {n} CIs, of which {cambi} would change class.',
  'non classificato':
    'unclassified',
  'scelta a mano':
    'chosen by hand',
  '…e altri.':
    '…and more.',
  'Provo…':
    'Testing…',
  'Prova senza salvare':
    'Test without saving',
  '{categoria} · personalizzata':
    '{categoria} · custom',
  'personalizzata':
    'custom',
  'della piattaforma':
    'platform',
  'Lo SKU identifica il prodotto associato a un oggetto. Viene assegnato ai CI tramite regole di riconoscimento e ne accompagna la trasformazione in asset.':
    'The SKU says which product an object is: the order code, the list price, the end of support. It is attached to CIs through a recognition rule, and when a CI becomes an asset that data is already there.',
  'nessuna regola lo aggancia?':
    'no rule matches it?',
  'Testo':
    'Text',
  'Numero':
    'Number',
  'Data':
    'Date',
  'Sì/No':
    'Yes/No',
  'Elenco a scelta':
    'Pick list',
  'Entrambi i versi':
    'Both directions',
  'Tutto ciò che tocca il nodo.':
    'Everything that touches the node.',
  'Chi ha parlato al nodo':
    'Who talked to the node',
  'I suoi client: chi apre verso di lui.':
    'Its clients: who opens connections to it.',
  'Con chi ha parlato il nodo':
    'Who the node talked to',
  'Le sue dipendenze: verso chi apre lui.':
    'Its dependencies: who it opens connections to.',
  '1 — i vicini':
    '1 — the neighbours',
  '2 — e i loro':
    '2 — and theirs',
  '3 — oltre':
    '3 — beyond',
  '1 nodo ha altri interlocutori oltre il tetto di {tetto} per nodo (segnati con ⁺). Quello che vedi è la parte più voluminosa del vicinato, non tutto il vicinato.':
    '1 node has other peers beyond the cap of {tetto} per node (marked with ⁺). What you see is the busiest part of the neighbourhood, not the whole neighbourhood.',
  '{n} nodi hanno altri interlocutori oltre il tetto di {tetto} per nodo (segnati con ⁺). Quello che vedi è la parte più voluminosa del vicinato, non tutto il vicinato.':
    '{n} nodes have other peers beyond the cap of {tetto} per node (marked with ⁺). What you see is the busiest part of the neighbourhood, not the whole neighbourhood.',
  'Attorno a {seed}':
    'Around {seed}',
  'Mappa di correlazione di {seed}: {n} nodi':
    'Correlation map of {seed}: {n} nodes',
  'Un clic su un nodo mostra il dettaglio, un doppio clic riparte da lì, e un nodo si trascina dove serve. Un clic su una linea sceglie la comunicazione — qui e nella tabella sotto. Maiusc+clic esclude un nodo; rotella per ingrandire, trascinamento della tela per spostare.':
    'Click a node to see its details, double-click to restart from there, and drag a node wherever you need it. Click a line to select the communication — here and in the table below. Shift+click excludes a node; scroll wheel to zoom, drag the canvas to pan.',
  'In {giallo} gli indirizzi che la CMDB non conosce: non è un difetto della mappa, è la risposta — e trovarli è uno dei motivi per cui questo traffico si raccoglie.':
    'In {giallo}, the addresses the CMDB does not know: this is not a flaw in the map, it is the answer — and finding them is one of the reasons this traffic is collected.',
  'Comunicazioni ({n})':
    'Communications ({n})',
  'I vicini di questo nodo non sono stati cercati: è al bordo dei livelli richiesti. Doppio clic per ripartire da qui.':
    'This node’s neighbours were not searched: it sits at the edge of the requested levels. Double-click to restart from here.',
  '→ verso':
    '→ to',
  '← da':
    '← from',
  '{n} ingressi dalla mappa.':
    '{n} entries from the map.',
  'es. Servizio attorno a {seed}':
    'e.g. Service around {seed}',
  'Cancellare tutta la storia di «{nome}»?':
    'Delete the entire history of “{nome}”?',
  'Sono {n} versioni, e non si recuperano. Serve quando un apparato è stato dismesso: la sua configurazione non interessa più, e tenerla in elenco fa cercare fra apparati che non esistono.':
    'That is {n} versions, and they cannot be recovered. Use this when a device has been decommissioned: its configuration no longer matters, and keeping it listed means searching among devices that do not exist.',
  'È lo stesso apparato visto due volte: la configurazione si salva una volta sola, ma finché restano separati la scheda mostra due storie.':
    'It is the same device seen twice: the configuration is saved only once, but as long as they stay separate the record shows two histories.',
  'Le regole di retention sono cumulative: una versione viene eliminata solo quando non soddisfa più alcun criterio di conservazione. L’ultima versione della configurazione di ogni asset è sempre esclusa dalla cancellazione, indipendentemente dalle regole configurate.':
    'Empty fields = keep everything, which is the default: deleting history cannot be undone. The two rules add up — a version is kept if at least one says to keep it — so enabling the second does not delete what the first was keeping.',
  'L’ultima versione di ogni apparato non si cancella mai, qualunque cosa dicano le regole: un archivio senza la configurazione corrente è un backup senza backup.':
    'The latest version of each device is never deleted, whatever the rules say: an archive without the current configuration is a backup without a backup.',
  'Con questa politica verranno tolte {n} versioni al prossimo giro dell’orchestratore. Non succede al salvataggio: c’è il tempo di accorgersi di aver scritto 3 dove si voleva 30.':
    'With this policy {n} versions will be removed at the next orchestrator run. It does not happen on save: there is time to notice you typed 3 where you meant 30.',
  'Cancellare la versione del {data}?':
    'Delete the version from {data}?',
  'Non si recupera. Il confronto delle versioni rimaste continuerà a funzionare, ma salterà quello che è successo in mezzo.':
    'It cannot be recovered. Comparing the remaining versions will still work, but will skip what happened in between.',
  '{righe} righe volatili · {segreti} segreti':
    '{righe} volatile lines · {segreti} secrets',
  '+{aggiunte} righe aggiunte, −{tolte} tolte':
    '+{aggiunte} lines added, −{tolte} removed',
  'Le credenziali sono state mascherate prima di conservarla: per ripristinare da qui vanno rimesse a mano.':
    'Credentials were masked before it was stored: to restore from here they must be put back by hand.',
  'Fuori supporto ({n})':
    'Out of support ({n})',
  '(ultima: {versione})':
    '(latest: {versione})',
  'Asset segnalati ({n})':
    'Flagged assets ({n})',
  '(facoltativo)':
    '(optional)',
  '(stato: {stato}).':
    '(status: {stato}).',
  '+{n} negli ultimi 7 giorni':
    '+{n} in the last 7 days',
  '+{n} senza indirizzo':
    '+{n} without an address',
  '0':
    '0',
  '0 = mai.':
    '0 = never.',
  '0 = nessuna soglia.':
    '0 = no threshold.',
  '0 = nessuna soglia. 5120 = 5 KB.':
    '0 = no threshold. 5120 = 5 KB.',
  '0 = senza scadenza. Con la scadenza il caso peggiore è un inventario che manca — e si vede — invece di un permesso che sopravvive alla macchina.':
    '0 = no expiry. With an expiry, the worst case is a missing inventory — which shows — rather than a permission that outlives the machine.',
  '0 nodi corrispondono a «{q}» — potrebbero essere nascosti da un filtro.':
    '0 nodes match “{q}” — they may be hidden by a filter.',
  '1 pacchetto su {n}':
    '1 packet in {n}',
  '1 su 10 (predefinito)':
    '1 in 10 (default)',
  '1 su {n}':
    '1 in {n}',
  '1. Collegamento':
    '1. Connection',
  '12:34:ab:cd:…':
    '12:34:ab:cd:…',
  '2 salti':
    '2 hops',
  '2. Cosa contiene il sistema di destinazione':
    '2. What the target system contains',
  '2055 è la più diffusa. NetFlow v9 di Cisco usa spesso 9995 o 9996, IPFIX 4739, sFlow 6343. Deve corrispondere a dove gli apparati esportano già.':
    '2055 is the most common. Cisco NetFlow v9 often uses 9995 or 9996, IPFIX 4739, sFlow 6343. It must match where the devices already export to.',
  '24 ore':
    '24 hours',
  '3. Mappature, direzione e frequenza':
    '3. Mappings, direction and frequency',
  '4. Cosa è successo':
    '4. What happened',
  '5986':
    '5986',
  '6 ore':
    '6 hours',
  '8088':
    '8088',
  'A rotazione':
    'Round robin',
  'A turno, senza guardare il carico.':
    'In turn, regardless of load.',
  'AB:12:CD:34:…':
    'AB:12:CD:34:…',
  'ABCDEFGHIJKLMNOPQRST':
    'ABCDEFGHIJKLMNOPQRST',
  'Abilitazione…':
    'Enabling…',
  'Access key ID':
    'Access key ID',
  'AccessKey ID':
    'AccessKey ID',
  'AccessKey Secret':
    'AccessKey Secret',
  'Adiacenze fisiche annunciate da CDP e LLDP':
    'Physical adjacencies advertised by CDP and LLDP',
  'Aggiorna le sonde indietro':
    'Update lagging probes',
  'Aggiorna l’agente da sé':
    'Updates itself',
  'Alcuni CI hanno una classe non prevista dal catalogo: {classi}. È il segnale che un connettore produce qualcosa di nuovo.':
    'Some CIs have a class not in the catalogue: {classi}. It is a sign that a connector is producing something new.',
  'Alcuni provider lo richiedono, altri no.':
    'Some providers require it, others do not.',
  'Allegato: asset non conformi ({n})':
    'Attachment: non-compliant assets ({n})',
  'Alleggerisci ingest':
    'Lighten ingest',
  'Altri {n} eventi non sono mostrati: questo elenco è parziale.':
    '{n} more events are not shown: this list is partial.',
  'Ambiti predefiniti — subnet, intervalli o indirizzi, uno per riga':
    'Default scopes — subnets, ranges or addresses, one per line',
  'Andamento del traffico complessivo':
    'Overall traffic trend',
  'Andamento della conversazione':
    'Conversation trend',
  'Apparati ({n})':
    'Devices ({n})',
  'Apri il registro dei contratti →':
    'Open the contract register →',
  'Arruola nuova sonda':
    'Enrol new probe',
  'Avrebbe dovuto rileggerla entro un minuto: se non cambia, la sonda non sta più riuscendo a interrogare il Control Plane.':
    'It should have re-read it within a minute: if it does not change, the probe is no longer managing to query the Control Plane.',
  'Avvia discovery e consulta i dati':
    'Run discovery and browse the data',
  'Avviate {n} sonde ({elenco}).':
    'Started {n} probes ({elenco}).',
  'Avviate {n} sonde ({elenco}). Altre {coda} in coda: partiranno man mano che queste tornano con la versione nuova.':
    'Started {n} probes ({elenco}). {coda} more queued: they will start as these come back on the new version.',
  'Avvistamenti ({n})':
    'Sightings ({n})',
  'Basta superarne una: «20 pacchetti o 5 KB» descrive due modi di essere interessante, non due condizioni insieme. In AND si butterebbero via proprio le conversazioni grosse e brevi — pochi pacchetti, molti byte, cioè un trasferimento. Con entrambe a zero non si scarta nulla.':
    'Exceeding either one is enough: “20 packets or 5 KB” describes two ways of being interesting, not two conditions together. With AND you would throw away precisely the large, short conversations — few packets, many bytes, i.e. a transfer. With both at zero, nothing is discarded.',
  'Bilanciato':
    'Balanced',
  'Bloccata da una regola di sicurezza.':
    'Blocked by a security rule.',
  'Build della console: {sha}':
    'Console build: {sha}',
  'CENSO → destinazione':
    'CENSO → target',
  'Cambia':
    'Change',
  'Cambia il significato della scadenza: con il rinnovo automatico quella data è l’ultimo giorno per disdire, non quello in cui si resta scoperti.':
    'Changes the meaning of the expiry: with automatic renewal that date is the last day to cancel, not the day you are left uncovered.',
  'Cambiare i campi, la chiave o l’oggetto azzera la prova a vuoto e la pianificazione: la prova è ciò che autorizza a scrivere, e vale per la mappatura che è stata provata — non per quella che diventa dopo.':
    'Changing the fields, the key or the object resets the dry run and the schedule: the dry run is what authorises writing, and it applies to the mapping that was tested — not to what it becomes afterwards.',
  'Campionamento':
    'Sampling',
  'Capability abilitate all’arruolamento:':
    'Capabilities enabled at enrolment:',
  'Centra su {nome}':
    'Centre on {nome}',
  'Cerca «{q}» in tutta la topologia':
    'Search “{q}” across the whole topology',
  'Cerco…':
    'Searching…',
  'Chiave proposta: {motivo}':
    'Proposed key: {motivo}',
  'Chiedi e installa':
    'Ask and install',
  'Chiusa in fase di decrypt SSL: validazione del certificato non riuscita.':
    'Closed during SSL decryption: certificate validation failed.',
  'Chiusa per esaurimento di risorse sul firewall.':
    'Closed due to resource exhaustion on the firewall.',
  'Chiusa per un errore durante il decrypt SSL.':
    'Closed due to an error during SSL decryption.',
  'Chiusa perché è stata rilevata una minaccia.':
    'Closed because a threat was detected.',
  'Chiusura regolare TCP: entrambe le parti hanno terminato con un FIN.':
    'Normal TCP close: both sides finished with a FIN.',
  'Clicca per nasconderlo':
    'Click to hide it',
  'Collega':
    'Connect',
  'Collegamenti di {nome}':
    'Links of {nome}',
  'Componenti ({n})':
    'Components ({n})',
  'Comunicazioni osservate sul filo o via NetFlow':
    'Communications observed on the wire or via NetFlow',
  'Configuration Item ({n})':
    'Configuration Items ({n})',
  'Consegna rifiutata':
    'Delivery rejected',
  'Contratti ({n})':
    'Contracts ({n})',
  'Control Plane non raggiungibile':
    'Control Plane unreachable',
  'Conversazioni ({n})':
    'Conversations ({n})',
  'Conviene che dica dove guardano queste sonde: è la cosa che si deve sapere quando si sceglie un gruppo per una scansione.':
    'It is worth saying where these probes look: it is what you need to know when choosing a group for a scan.',
  'Copia il binario censo-inventory sulla macchina, e questo file in /etc/censo/inventory.json con permessi 600. Se non c’è rete in uscita affatto, l’agente scrive la busta su file con -out: è sigillata, quindi può viaggiare su una chiavetta.':
    'Copy the censo-inventory binary to the machine, and this file to /etc/censo/inventory.json with permissions 600. If there is no outbound network at all, the agent writes the envelope to a file with -out: it is sealed, so it can travel on a USB stick.',
  'Copia l’indirizzo di ritorno':
    'Copy the return address',
  'Copiati nel job: resta il verbale di ciò che è stato guardato oggi.':
    'Copied into the job: a record remains of what was looked at today.',
  'Corrisponde se…':
    'Matches if…',
  'Crea business service':
    'Create business service',
  'Crea connettore':
    'Create connector',
  'Crea credenziale':
    'Create credential',
  'Crea e genera la configurazione':
    'Create and generate the configuration',
  'Crea gruppo':
    'Create group',
  'Crea il piano':
    'Create the plan',
  'Crea pianificazione':
    'Create schedule',
  'C’è già. Lascialo vuoto per non cambiarlo.':
    'Already set. Leave it empty to keep it unchanged.',
  'Da qui si leggono da soli gli indirizzi del provider — non serve copiarli. Viene verificato al salvataggio: se non risponde, non si salva.':
    'From here the provider’s addresses are read automatically — no need to copy them. It is checked on save: if it does not respond, it is not saved.',
  'Da riconoscere ({n})':
    'To be identified ({n})',
  'Da usare solo per una registrazione sbagliata. Un bene venduto o rottamato va messo in stato «alienato»: la storia di cosa si è posseduto e per quanto serve all’inventario e a chi lo verifica.':
    'Use only for a wrong entry. An asset that has been sold or scrapped should be set to “disposed”: the history of what was owned and for how long matters to the inventory and to whoever audits it.',
  'Dedotto dalla descrizione di sistema o dai bit di sysServices':
    'Inferred from the system description or the sysServices bits',
  'Degradate':
    'Degraded',
  'Deve corrispondere esattamente, anche nello slash finale: uno diverso fa fallire l’accesso con «redirect_uri mismatch». Poi torna qui con issuer, client ID e client secret.':
    'It must match exactly, including the trailing slash: a different one makes sign-in fail with “redirect_uri mismatch”. Then come back here with the issuer, client ID and client secret.',
  'Deve essere un numero fra 1 e 65535.':
    'Must be a number between 1 and 65535.',
  'Di norma significa che l’orologio di questa macchina è indietro rispetto a chi lo ha emesso.':
    'It usually means this machine’s clock is behind that of the issuer.',
  'Di {totale} tipi dichiarati dal sistema sono elencati i {n} che CENSO sa riempire: per questi la mappatura arriva proposta e verificata. Gli altri esistono ancora — se ti serve un tipo personalizzato, scrivine il nome a mano nella mappatura.':
    'Of the {totale} types declared by the system, the {n} that CENSO can populate are listed: for these the mapping comes proposed and verified. The others still exist — if you need a custom type, type its name manually in the mapping.',
  'Dice dove cercare, e funziona anche la prima volta: è l’unico tipo utile quando in CMDB non c’è ancora niente.':
    'It says where to look, and it works the first time too: it is the only useful type when the CMDB is still empty.',
  'Dice quali macchine già note riguardare — «quelle con la 22 aperta», «quelle che si chiamano SRV-*». Non può decidere la prima scansione: una porta aperta si sa dopo averla guardata.':
    'It says which already-known machines to revisit — “those with port 22 open”, “those named SRV-*”. It cannot drive the first scan: you only know a port is open after looking at it.',
  'Dichiarata dalla sonda ma non a catalogo: la sonda è più recente del Control Plane.':
    'Declared by the probe but not in the catalogue: the probe is newer than the Control Plane.',
  'Dinamico':
    'Dynamic',
  'Disabilita':
    'Disable',
  'Disattivato':
    'Disabled',
  'Discovery di rete':
    'Network discovery',
  'Dopo il':
    'After',
  'Dopo una consegna riuscita, se il Control Plane distribuisce una versione più recente. Il binario scaricato viene eseguito prima di essere installato, e il precedente resta accanto: un aggiornamento difettoso non spegne la macchina. Toglilo dove esistono finestre di manutenzione.':
    'After a successful delivery, if the Control Plane distributes a newer version. The downloaded binary is run before being installed, and the previous one is kept alongside it: a faulty update does not take the machine down. Turn it off where maintenance windows exist.',
  'Dove gira una VM, su quale storage vive, a quale cluster appartiene':
    'Where a VM runs, which storage it lives on, which cluster it belongs to',
  'Elimina la vista {nome}':
    'Delete view {nome}',
  'Eliminare il pool «{nome}»?':
    'Delete pool “{nome}”?',
  'Eliminare il servizio «{nome}»?':
    'Delete service “{nome}”?',
  'Eliminare la registrazione di {tag}?':
    'Delete the record for {tag}?',
  'Eliminare l’agente «{nome}»?\n\nSparisce anche la sua storia: quante consegne ha fatto e quando ha parlato l’ultima volta. Per fermarlo senza perderla, usa Revoca.':
    'Delete agent “{nome}”?\n\nIts history goes too: how many deliveries it made and when it last reported. To stop it without losing that, use Revoke.',
  'Eliminare «{nome}»?':
    'Delete “{nome}”?',
  'Eliminare «{nome}»? Le {n} sonde restano arruolate e tornano assegnabili a qualunque discovery del tenant. Le pianificazioni che nominavano il gruppo perdono il vincolo.':
    'Delete “{nome}”? The {n} probes remain enrolled and become assignable to any discovery in the tenant again. Schedules that named the group lose the constraint.',
  'Entrambe':
    'Both',
  'Errore {stato}':
    'Error {stato}',
  'Errore {stato}: {dettaglio}':
    'Error {stato}: {dettaglio}',
  'Esegui un giro adesso':
    'Run a pass now',
  'Espressione cron personalizzata':
    'Custom cron expression',
  'Evidenzia questo collegamento sulla mappa e portacisi sopra':
    'Highlight this link on the map and move to it',
  'External ID':
    'External ID',
  'Fai prima una prova a vuoto: mostra cosa verrebbe scritto, e nessuna scrittura si annulla':
    'Do a dry run first: it shows what would be written, and no write can be undone',
  'Failover':
    'Failover',
  'Filtrato su {sorgente} → {destinazione} ({servizio}/{protocollo}).':
    'Filtered on {sorgente} → {destinazione} ({servizio}/{protocollo}).',
  'Filtri e ordinamento agiscono sui {n} servizi caricati per questa finestra, ordinati dal server per numero di mittenti.':
    'Filters and sorting apply to the {n} services loaded for this window, ordered by the server by number of senders.',
  'Filtri e ordinamento agiscono sulle {n} conversazioni caricate per questa finestra, che il server sceglie per volume.':
    'Filters and sorting apply to the {n} conversations loaded for this window, which the server selects by volume.',
  'Finché tace non riceve nemmeno la configurazione, quindi la cattura non può partire e quello che si vede qui non è aggiornato. Verifica che il processo sia in esecuzione e che raggiunga il Control Plane.':
    'While it stays silent it does not even receive its configuration, so capture cannot start and what you see here is not up to date. Check that the process is running and can reach the Control Plane.',
  'Flussi ricevuti':
    'Flows received',
  'Formati: subnet {subnet} (fino a /16), indirizzo singolo {indirizzo}, intervallo {intervallo}. Solo IPv4, come la scansione di rete.':
    'Formats: subnet {subnet} (up to /16), single address {indirizzo}, range {intervallo}. IPv4 only, like the network scan.',
  'Funzionale':
    'Functional',
  'Genera token di arruolamento':
    'Generate enrolment token',
  'Generazione…':
    'Generating…',
  'Gestisce utenti, credenziali e sonde':
    'Manages users, credentials and probes',
  'Gli ambiti vengono copiati nel job: resta il verbale di ciò che è stato guardato oggi.':
    'Scopes are copied into the job: a record remains of what was looked at today.',
  'Gli indirizzi dei clienti nascono qui sotto: {esempio} diventa {indirizzo}. Serve un record DNS wildcard e un certificato per {wildcard}.':
    'Customer addresses are created under here: {esempio} becomes {indirizzo}. A wildcard DNS record and a certificate for {wildcard} are required.',
  'Gli utenti restano: sono abilitati per invito, e il provider diceva solo chi fossero. Chi ha una password continua a entrare; chi non ce l’ha dovrà farsene dare una.':
    'Users remain: they are enabled by invitation, and the provider only said who they were. Those with a password can still sign in; those without one will need to be given one.',
  'Gratuito e automatico, senza credenziali del DNS. Serve che la porta 80 sia raggiungibile da internet: l’autorità ci manda una richiesta per verificare che il dominio sia davvero tuo.':
    'Free and automatic, with no DNS credentials. Port 80 must be reachable from the internet: the authority sends a request to verify that the domain really is yours.',
  'Gruppi ({n})':
    'Groups ({n})',
  'Ha consegnato, e l’ultima consegna è andata a buon fine.':
    'It has delivered, and the last delivery succeeded.',
  'Ha parlato e non è stato accettato. Il motivo è qui sotto: è un guasto diverso dal silenzio.':
    'It reported in and was not accepted. The reason is below: this is a different fault from silence.',
  'Hash costante':
    'Constant hash',
  'Host ({n})':
    'Hosts ({n})',
  'Huawei OceanStor (DeviceManager)':
    'Huawei OceanStor (DeviceManager)',
  'I campi {campi} partono vuoti: i segreti non sono leggibili. Lasciandoli vuoti restano quelli salvati; si cambiano solo scrivendoci sopra.':
    'The fields {campi} start empty: secrets cannot be read back. Leaving them empty keeps the saved ones; they only change if you type over them.',
  'I job si distribuiscono fra tutte le sonde del gruppo.':
    'Jobs are distributed across all probes in the group.',
  'I passi vengono copiati nel job: il piano è il modello, il job è il verbale di ciò che è stato fatto. Le credenziali stanno sui singoli passi del piano. {link}.':
    'Steps are copied into the job: the plan is the template, the job is the record of what was done. Credentials sit on the individual steps of the plan. {link}.',
  'I primi {n} sono i tipi che CENSO sa già riempire: per questi la mappatura arriva proposta e verificata.':
    'The first {n} are the types CENSO can already populate: for these the mapping comes proposed and verified.',
  'I punti più vicini al centro sono quelli da riconoscere: è dove va l’occhio per primo. Il quadro mostra fino a 300 CI.':
    'The points closest to the centre are the ones to identify: that is where the eye goes first. The chart shows up to 300 CIs.',
  'I tipi di oggetto sono ciò che si crea e si aggiorna: un oggetto è composto da più definizioni di dato, e i suoi campi si indirizzano come {forma}.':
    'Object types are what gets created and updated: an object is made up of several data definitions, and its fields are addressed as {forma}.',
  'I volumi del traffico restano corretti, ma senza attribuzione: il traffico di un asset inventariato compare come traffico verso un indirizzo sconosciuto. Sembra una CMDB incompleta, e non lo è.':
    'Traffic volumes stay correct, but unattributed: the traffic of an inventoried asset shows up as traffic to an unknown address. It looks like an incomplete CMDB, and it is not.',
  'I volumi vengono riportati in scala, quindi restano confrontabili — a differenza dei pacchetti che il kernel scarta, dove il fattore non si conosce. Su reti tranquille scendi a 1.':
    'Volumes are scaled back up, so they remain comparable — unlike packets dropped by the kernel, where the factor is unknown. On quiet networks, go down to 1.',
  'Il Dobermann non è di guardia: dichiara le subnet da proteggere nella sua pagina.':
    'The Dobermann is not on guard: declare the subnets to protect on its page.',
  'Il bilanciatore lo serve da adesso, senza riavvio: nessuna connessione è caduta, comprese quelle delle sonde.':
    'The load balancer serves it from now on, without a restart: no connection was dropped, including the probes’ ones.',
  'Il campo {campi} parte vuoto: i segreti non sono leggibili. Lasciandolo vuoto resta quello salvato; si cambia solo scrivendoci sopra.':
    'The field {campi} starts empty: secrets cannot be read back. Leaving it empty keeps the saved one; it only changes if you type over it.',
  'Il certificato scade fra {n} giorni. Un wildcard scaduto rende inaccessibili tutti i clienti nello stesso istante.':
    'The certificate expires in {n} days. An expired wildcard makes every customer unreachable at the same moment.',
  'Il client ha inviato un reset (RST): sessione chiusa dal suo lato.':
    'The client sent a reset (RST): session closed from its side.',
  'Il comando contiene l’impronta del certificato di questo server, perché il certificato è ancora autofirmato: è una verifica esatta, non un «fidati di chiunque». Sostituendo il certificato con uno vero il comando tornerà a essere un download normale, e le sonde già installate continueranno a funzionare.':
    'The command contains the fingerprint of this server’s certificate, because the certificate is still self-signed: it is an exact check, not a “trust anyone”. Once the certificate is replaced with a real one, the command will go back to being a normal download, and probes already installed will keep working.',
  'Il comando contiene l’impronta della chiave pubblica di questo server, perché il certificato è ancora autofirmato: è una verifica esatta, non un «fidati di chiunque». Sostituendo il certificato con uno vero il comando tornerà a essere un download normale, e le sonde già installate continueranno a funzionare.':
    'The command contains the fingerprint of this server’s public key, because the certificate is still self-signed: it is an exact check, not a “trust anyone”. Once the certificate is replaced with a real one, the command will go back to being a normal download, and probes already installed will keep working.',
  'Il comando vale un’ora (fino alle {ora}), e contiene il segreto di questo agente: è l’unica volta in cui compare. Passata la finestra si rigenera — ma rigenerare invalida il segreto vecchio, quindi se l’agente era già installato smette di consegnare finché non lo reinstalli.':
    'The command is valid for one hour (until {ora}) and contains this agent’s secret: this is the only time it is shown. Once the window has passed it can be regenerated — but regenerating invalidates the old secret, so if the agent was already installed it stops delivering until you reinstall it.',
  'Il comando vale un’ora, e contiene il segreto di questo agente: è l’unica volta in cui compare. Passata la finestra si rigenera — ma rigenerare invalida il segreto vecchio, quindi se l’agente era già installato smette di consegnare finché non lo reinstalli.':
    'The command is valid for one hour and contains this agent’s secret: this is the only time it is shown. Once the window has passed it can be regenerated — but regenerating invalidates the old secret, so if the agent was already installed it stops delivering until you reinstall it.',
  'Il dizionario è caricato ma vuoto: la CMDB non contiene ancora interfacce né indirizzi di gestione, quindi nessun flusso risulterà attribuito a un configuration item. Non è un guasto dello store — è una discovery che non è ancora passata.':
    'The dictionary is loaded but empty: the CMDB does not yet contain any interfaces or management addresses, so no flow will be attributed to a configuration item. It is not a store fault — it is a discovery that has not run yet.',
  'Il piano ha raggiunto il massimo di {n} passi. Oltre quel numero nessuno lo rilegge prima di lanciarlo su una rete di produzione.':
    'The plan has reached the maximum of {n} steps. Beyond that number nobody re-reads it before launching it on a production network.',
  'Il primo del mese all’1:00':
    'The first of the month at 1:00',
  'Il provider verifica chi è chi entra, non se può entrare: chi si autentica accede solo se il suo indirizzo è già fra gli utenti qui sopra. Collegare l’IdP non apre la console a tutta l’azienda.':
    'The provider verifies who is signing in, not whether they may: whoever authenticates gets in only if their address is already among the users above. Connecting the IdP does not open the console to the whole company.',
  'Il segreto non vale più. Rigeneralo e rimetti la configurazione sulla macchina.':
    'The secret is no longer valid. Regenerate it and put the configuration back on the machine.',
  'Il server ha inviato un reset (RST): sessione chiusa dal suo lato.':
    'The server sent a reset (RST): session closed from its side.',
  'Il server ne serve una versione più recente, ma il browser sta ancora eseguendo la build {sha}. Quello che vedi può non riflettere lo stato attuale del sistema.':
    'The server is serving a newer version, but the browser is still running build {sha}. What you see may not reflect the current state of the system.',
  'Importa comunque le righe valide':
    'Import the valid rows anyway',
  'Importati {n} ambiti.':
    'Imported {n} scopes.',
  'In ascolto sulla porta {port} ({indirizzi}).':
    'Listening on port {port} ({indirizzi}).',
  'In corso':
    'In progress',
  'In corso…':
    'In progress…',
  'In entrata':
    'Inbound',
  'In generale è l’indirizzo a cui, aggiungendo {percorso}, si ottiene un documento JSON.':
    'In general it is the address to which, by appending {percorso}, you get a JSON document.',
  'In linea':
    'Online',
  'In tutto {n} indirizzi.':
    '{n} addresses in all.',
  'In uscita':
    'Outbound',
  'Incolla il certificato (con la catena, se ce l’hai: prima il tuo, poi gli intermedi) e la sua chiave privata. La coppia viene verificata prima di essere scritta — una chiave che non corrisponde impedirebbe al bilanciatore di ripartire, e l’installazione resterebbe irraggiungibile, questa pagina compresa.':
    'Paste the certificate (with the chain, if you have it: yours first, then the intermediates) and its private key. The pair is checked before being written — a key that does not match would stop the load balancer from restarting, and the installation would become unreachable, this page included.',
  'Indirizzi IP ({n})':
    'IP addresses ({n})',
  'Identifica gli endpoint di rete, rileva indirizzi IP, porte e servizi esposti, le sorgenti che vi si connettono e le relative dipendenze applicative.':
    'Identifies the network endpoints and finds the exposed IP addresses, ports and services, the sources connecting to them and the related application dependencies.',
  'Installa certificato':
    'Install certificate',
  'Installa «{nome}»':
    'Install “{nome}”',
  'Interfacce di rete ({n})':
    'Network interfaces ({n})',
  'JSON non ancora valido':
    'JSON not yet valid',
  'La capability {nome} è spenta su questa sonda: la cattura non partirà, per quanto si aspetti. Riattivala fra le capability qui sopra.':
    'The {nome} capability is off on this probe: capture will not start, however long you wait. Re-enable it among the capabilities above.',
  'La configurazione ricevuta dalla sonda {tempo} fa non abilita la raccolta di flussi, pur essendo la capability attiva in console. Di norma significa che la licenza del tenant non comprende i flussi: controlla gli entitlement.':
    'The configuration received by the probe {tempo} ago does not enable flow collection, even though the capability is active in the console. This usually means the tenant’s licence does not include flows: check the entitlements.',
  'La discovery verrà eseguita solo da questa sonda. Se non è disponibile resta in attesa e non passa a un’altra: scegliere la sonda è scegliere la rete da cui guardare, e un’altra rete darebbe un inventario vuoto che sembra completo.':
    'The discovery will be run only by this probe. If it is unavailable, it waits and does not move to another: choosing the probe means choosing the network to look from, and another network would give an empty inventory that looks complete.',
  'La freccia è il verso: {sorgente} apre la connessione verso {destinazione}. Le porte qui sotto sono quelle di destinazione.':
    'The arrow shows the direction: {sorgente} opens the connection to {destinazione}. The ports below are the destination ones.',
  'La mappa mostra 1 oggetto che corrisponde a «{q}» e ciò a cui è collegato. Svuota la ricerca per rivedere tutto.':
    'The map shows 1 object matching “{q}” and what it is linked to. Clear the search to see everything again.',
  'La mappa mostra {n} oggetti che corrispondono a «{q}» e ciò a cui sono collegati. Svuota la ricerca per rivedere tutto.':
    'The map shows {n} objects matching “{q}” and what they are linked to. Clear the search to see everything again.',
  'La richiesta è stata bloccata prima di arrivare all’applicazione (Errore {stato}).':
    'The request was blocked before reaching the application (Error {stato}).',
  'La richiesta è stata bloccata prima di arrivare all’applicazione (WAF). Se l’operazione è legittima, cerca l’id della regola nei log del servizio waf e aggiungi un’eccezione mirata.':
    'The request was blocked before reaching the application (WAF). If the operation is legitimate, look up the rule ID in the waf service logs and add a targeted exception.',
  'La richiesta è stata bloccata prima di arrivare all’applicazione (bilanciatore o WAF): il servizio a valle non ha risposto.':
    'The request was blocked before reaching the application (load balancer or WAF): the downstream service did not respond.',
  'La rilegge ogni minuto: la cattura parte al prossimo giro.':
    'It re-reads it every minute: capture starts on the next pass.',
  'La sceglie il Control Plane fra quelle online che dichiarano la capability necessaria. Va bene quando le sonde vedono la stessa rete.':
    'The Control Plane picks it from the online probes that declare the required capability. Fine when the probes see the same network.',
  'La sonda ha applicato la cattura su {interfaccia} ({tempo} fa) ma non riporta alcuno stato di cattura. È una combinazione che non dovrebbe verificarsi: raccogli i log della sonda («cattura diretta») prima di riavviarla, altrimenti la causa si perde.':
    'The probe applied capture on {interfaccia} ({tempo} ago) but reports no capture status. This combination should not occur: collect the probe logs («direct capture») before restarting it, otherwise the cause is lost.',
  'La sonda ha riferito {tempo} fa ma non riporta nulla sulla raccolta di flussi: è una versione precedente alla cattura diretta. Aggiornala — le versioni che sanno catturare riportano questo stato anche quando non stanno catturando.':
    'The probe reported {tempo} ago but says nothing about flow collection: it is a version older than direct capture. Update it — versions that can capture report this status even when they are not capturing.',
  'La sonda ha una configurazione di {tempo} fa che chiede la cattura su {ha}, non su {voluta}.':
    'The probe has a configuration from {tempo} ago that requests capture on {ha}, not on {voluta}.',
  'La sonda ha una configurazione di {tempo} fa che non chiede alcuna cattura.':
    'The probe has a configuration from {tempo} ago that requests no capture.',
  'La sonda le prova in ordine e si ferma alla prima che risponde. La sequenza conta: ogni tentativo fallito costa un timeout per ogni apparato, e con SNMPv3 un’autenticazione fallita resta tale — alcuni apparati bloccano l’utenza dopo qualche tentativo.':
    'The probe tries them in order and stops at the first one that responds. The order matters: each failed attempt costs a timeout per device, and with SNMPv3 a failed authentication stays failed — some devices lock the account after a few attempts.',
  'La sonda le prova in ordine e si ferma alla prima che risponde. Su una pianificazione il costo si ripete a ogni esecuzione: una credenziale sbagliata in cima aggiunge un timeout per ogni apparato, ogni notte.':
    'The probe tries them in order and stops at the first one that responds. On a schedule the cost repeats at every run: a wrong credential at the top adds a timeout per device, every night.',
  'La sonda non si fa sentire da {tempo}.':
    'The probe has not been heard from for {tempo}.',
  'La sonda parla col Control Plane solo in uscita, su 443: non aprire niente in ingresso.':
    'The probe talks to the Control Plane outbound only, on 443: do not open anything inbound.',
  'La sonda prova le credenziali in ordine e si ferma alla prima che risponde. Serve quando gli apparati non hanno tutti la stessa community o lo stesso utente — tipicamente dopo una migrazione, o fra sedi acquisite in momenti diversi.':
    'The probe tries the credentials in order and stops at the first one that responds. Useful when devices do not all share the same community or user — typically after a migration, or across sites acquired at different times.',
  'La sonda è avviata con {variabile}, ma le capability effettive sono {effettive}. Vale quest’ultima: la variabile imposta solo la politica iniziale all’arruolamento, così una scelta fatta da qui non viene riscritta dal prossimo heartbeat. Puoi rimuoverla dalla riga di comando della sonda senza cambiare nulla.':
    "The probe is started with {variabile}, but the effective capabilities are {effettive}. The latter applies: the variable only sets the initial policy at enrolment, so a choice made here is not overwritten by the next heartbeat. You can remove it from the probe's command line without changing anything.",
  'La stessa coppia porta/indirizzi è stata riusata per una nuova sessione.':
    'The same port/address pair was reused for a new session.',
  'Lascia quelle attuali':
    'Keep the current ones',
  'Le capability abilitate qui possono solo restringere ciò che la sonda dichiara di saper fare: non aggiungono funzioni che il suo binario non ha.':
    'The capabilities enabled here can only restrict what the probe declares it can do: they do not add functions its binary lacks.',
  'Le capability {elenco} sono abilitate ma il binario della sonda non le implementa: aggiorna la sonda o avviala senza CENSO_CAPABILITIES che le esclude.':
    'The capabilities {elenco} are enabled but the probe binary does not implement them: update the probe or start it without the CENSO_CAPABILITIES that excludes them.',
  'Le chiamate escono da una sonda che non esiste più — riassegnala qui sotto, altrimenti non parte niente.':
    'Calls go out from a probe that no longer exists — reassign it below, otherwise nothing runs.',
  'Le chiamate escono da {sonda}.':
    'Calls go out from {sonda}.',
  'Le macchine che nessuna sonda raggiunge raccolgono il proprio inventario e lo consegnano da sé. Quelle che nemmeno raggiungono il Control Plane — una DMZ senza uscita — possono consegnarlo a questa sonda, che lo inoltra senza aprirlo: il carico è sigillato.':
    'Machines that no probe can reach collect their own inventory and deliver it themselves. Those that cannot even reach the Control Plane — a DMZ with no outbound access — can deliver it to this probe, which forwards it without opening it: the payload is sealed.',
  'L’aggiornamento potrebbe non iniziare subito. Le sonde verificano periodicamente la disponibilità della nuova versione. Se l’aggiornamento impedisce alla sonda di ripartire correttamente, viene ripristinata automaticamente la versione precedente.': 'The update may not start immediately. The probes periodically check for the availability of the new version. If the update prevents a probe from restarting correctly, the previous version is restored automatically.',
  'Legge gli indirizzi fisici imparati dalle porte dello switch e li correla con i CI già in inventario. Vede anche ciò che non parla CDP/LLDP — stampanti, PC, macchine virtuali. Le porte con troppi indirizzi sono dorsali e vengono escluse: «raggiungibile attraverso» non è «attaccato a».':
    'Reads the physical addresses learned by the switch ports and correlates them with the CIs already in the inventory. It also sees what does not speak CDP/LLDP — printers, PCs, virtual machines. Ports with too many addresses are backbones and are excluded: «reachable through» is not «attached to».',
  'Letti da {colonna} su un campione di {n} record (ce ne sono altri): l’elenco dice quali tipi esistono, non quanti oggetti hanno.':
    'Read from {colonna} on a sample of {n} records (there are more): the list shows which types exist, not how many objects they have.',
  'Letti da {colonna} su un campione di {n} record: l’elenco dice quali tipi esistono, non quanti oggetti hanno.':
    'Read from {colonna} on a sample of {n} records: the list shows which types exist, not how many objects they have.',
  'Libera {n} nodi fissati':
    'Release {n} pinned nodes',
  'Limita le capability':
    'Limit capabilities',
  'Limita le capability di questa sonda':
    "Limit this probe's capabilities",
  'Lo stesso ambito finisce sempre sulla stessa sonda: utile per confronti nel tempo.':
    'The same scope always ends up on the same probe: useful for comparisons over time.',
  'Lo stesso documento che la sonda produce entrando via SSH o WinRM, così i due inventari della stessa macchina si riconciliano invece di affiancarsi.':
    'The same document the probe produces when connecting via SSH or WinRM, so the two inventories of the same machine are reconciled instead of sitting side by side.',
  'L’elenco dei prodotti interroga il gestore di pacchetti o il registro: secondi, non millisecondi.':
    'The product list queries the package manager or the registry: seconds, not milliseconds.',
  'Mai consegnato':
    'Never delivered',
  'Massimo {n}':
    'Maximum {n}',
  'Meno carica':
    'Least loaded',
  'Microsoft SQL Server':
    'Microsoft SQL Server',
  'Mittenti':
    'Senders',
  'Modifica «{nome}»':
    'Edit «{nome}»',
  'Mostra anche il traffico di ritorno':
    'Also show return traffic',
  'Mostra anche le {n} porte spente e senza descrizione':
    'Also show the {n} ports that are down and have no description',
  'MySQL / MariaDB':
    'MySQL / MariaDB',
  'Nasce spenta: il passo successivo è la prova a vuoto, che calcola tutto e non scrive niente.':
    'It starts disabled: the next step is the dry run, which calculates everything and writes nothing.',
  'Nascosto: clicca per rimetterlo':
    'Hidden: click to restore it',
  'Nella scena ne nuotano {n}: gli altri {nascosti} ci sono, ma disegnarli tutti farebbe muovere l’immagine a scatti. I nuovi non vengono mai nascosti per primi.':
    '{n} are swimming in the scene: the other {nascosti} are there, but drawing them all would make the image stutter. New ones are never the first to be hidden.',
  'Nessun CI. Avvia una discovery per popolare la CMDB.':
    'No CIs. Start a discovery to populate the CMDB.',
  'Nessun cambiamento: le regole attuali danno lo stesso esito di prima.':
    'No change: the current rules give the same outcome as before.',
  'Nessun certificato leggibile da qui.':
    'No certificate readable from here.',
  'Nessun cliente può accedere: ogni browser rifiuta la connessione prima ancora di mostrare la pagina.':
    'No customer can access: every browser rejects the connection before even showing the page.',
  'Nessun contratto valido copre questo bene oggi. Se dovrebbe essere coperto, verifica dal registro dei contratti: un contratto cessato, o una copertura con data di fine passata, non conta.':
    'No valid contract covers this asset today. If it should be covered, check the contract register: a terminated contract, or a coverage whose end date has passed, does not count.',
  'Nessun dato da mostrare.':
    'No data to show.',
  'Nessun dato nel periodo.':
    'No data in the period.',
  'Nessun flusso da {n} pacchetti: sull’interfaccia arriva traffico ma non è IP. Verifica che la porta mirror stia replicando le VLAN giuste.':
    'No flows from {n} packets: traffic is arriving on the interface but it is not IP. Check that the mirror port is replicating the right VLANs.',
  'Nessun piano. Una discovery di rete è la sequenza di passi che qualcuno ha disegnato — ping, DNS, porte, SNMP, SSH, Windows — e senza non c’è niente da eseguire. {link}.':
    'No plan. A network discovery is the sequence of steps someone has designed — ping, DNS, ports, SNMP, SSH, Windows — and without one there is nothing to run. {link}.',
  'Nessun pool ancora. {link}: le reti si scrivono una volta e le richiamano tutte le scansioni.':
    'No pools yet. {link}: networks are written once and every scan reuses them.',
  'Nessun pool. {link} per scrivere gli ambiti una volta sola.':
    'No pools. {link} to write the scopes once only.',
  'Nessuna capability abilitata: si arruolerà e resterà ferma finché non ne abiliti almeno una dalla sua scheda.':
    'No capabilities enabled: it will enrol and stay idle until you enable at least one from its page.',
  'Nessuna corrispondenza fra i collegamenti caricati, che sono solo una parte.':
    'No matches among the loaded links, which are only a subset.',
  'Nessuna corrispondenza fra i collegamenti caricati.':
    'No matches among the loaded links.',
  'Nessuna credenziale da scegliere qui: questo tipo di discovery usa quelle dichiarate sui singoli passi del piano, perché ne servono di tipi diversi — SNMP per gli apparati, SSH per i server Unix, Windows per gli altri — e un elenco unico non saprebbe quale dare a chi.':
    'No credential to choose here: this discovery type uses the ones declared on the individual plan steps, because different kinds are needed — SNMP for network devices, SSH for Unix servers, Windows for the rest — and a single list would not know which to give to whom.',
  'Nessuna credenziale di tipo {tipi}.':
    'No credential of type {tipi}.',
  'Nessuna credenziale di tipo {tipi}: creala nella sezione Credenziali.':
    'No credential of type {tipi}: create one in the Credentials section.',
  'Nessuna partizione pronta per il dettaglio: il prossimo batch di flussi fallirà. Le crea la manutenzione periodica, e l’ingest ne crea una al volo — se restano zero, la manutenzione non sta girando.':
    'No partition ready for detail data: the next batch of flows will fail. They are created by periodic maintenance, and ingest creates one on the fly — if they stay at zero, maintenance is not running.',
  'Nessuna regola ha riconosciuto questo CI':
    'No rule recognised this CI',
  'Nessuna sonda avviata: ce ne sono già {n} in aggiornamento. Il lotto successivo parte quando queste tornano.':
    'No probe started: {n} are already updating. The next batch starts when these come back.',
  'Nessuna sonda da aggiornare.':
    'No probes to update.',
  'Nessuna sonda online ha la capability {capability} abilitata: il job resterebbe in attesa. Verifica la scheda della sonda.':
    "No online probe has the {capability} capability enabled: the job would stay pending. Check the probe's page.",
  'Niente da mostrare.':
    'Nothing to show.',
  'Non applicabile: sessione ancora aperta o protocollo senza stato (UDP/ICMP).':
    'Not applicable: session still open or stateless protocol (UDP/ICMP).',
  'Non concede l’accesso — quello resta l’invito — ma propone un ruolo quando abiliti un’utenza che il provider conosce già.':
    'It does not grant access — that is still done by invitation — but it suggests a role when you enable a user the provider already knows.',
  'Non in ascolto.':
    'Not listening.',
  'Non raggiungibili':
    'Unreachable',
  'Non si rilegge mai: se lo perdi, ne generi un altro sul provider.':
    'It is never shown again: if you lose it, generate a new one on the provider.',
  'Per evitare tentativi ripetuti senza esito, dopo due fallimenti il sistema sospende l’aggiornamento. Se una sonda completa l’operazione in seguito, il suo stato viene aggiornato automaticamente.': 'To avoid repeated fruitless attempts, after two failures the system suspends the update. If a probe completes the operation later, its status is updated automatically.',
  'Non valutabile: {motivo}':
    'Cannot be assessed: {motivo}',
  'Non verificabile da qui, si vede solo a un accesso vero: {elenco}.':
    'Cannot be verified from here, only visible on an actual sign-in: {elenco}.',
  'Nuova mappatura':
    'New mapping',
  'Nuova pianificazione':
    'New schedule',
  'Nuovo business service da {n} ingressi':
    'New business service from {n} entry points',
  'Nuovo piano di discovery':
    'New discovery plan',
  'OPZIONI':
    'OPTIONS',
  'Ogni 15 minuti':
    'Every 15 minutes',
  'Ogni 6 ore':
    'Every 6 hours',
  'Ogni credenziale sbagliata prima di quella giusta costa un timeout per ogni apparato dell’ambito: metti per prima quella che copre più apparati.':
    'Every wrong credential before the right one costs a timeout per device in scope: put first the one that covers the most devices.',
  'Ogni domenica alle 03:00':
    'Every Sunday at 03:00',
  'Ogni domenica alle 3:00':
    'Every Sunday at 3:00',
  'Ogni giorno alle 02:00':
    'Every day at 02:00',
  'Ogni notte alle 2:00':
    'Every night at 2:00',
  'Ogni ora':
    'Hourly',
  'Oracle Database':
    'Oracle Database',
  'Passi ({n} su {max})':
    'Steps ({n} of {max})',
  'Per creare in Matrix42 serve il tipo di oggetto, non il frammento: un asset è un oggetto di un tipo, composto da più frammenti. Qui sotto i tipi trovati su questa istanza, da incollare nella configurazione del connettore.':
    'To create in Matrix42 you need the object type, not the fragment: an asset is an object of a type, made up of several fragments. Below are the types found on this instance, to paste into the connector configuration.',
  'La {guida} descrive funzioni e sequenza operativa di ogni area. Il tab «Come funziona questa pagina» fornisce indicazioni contestuali.': 'The {guida} describes the functions and operating sequence of each area. The «How this page works» tab gives contextual guidance.',
  'Per riferimento: una subnet aggiunta al pool entra nella scansione della notte successiva.':
    "For reference: a subnet added to the pool is included in the following night's scan.",
  'Pianificazione «{nome}» aggiornata.':
    'Schedule «{nome}» updated.',
  'Pianificazione «{nome}» creata.':
    'Schedule «{nome}» created.',
  'Pianificazioni «{nome}» e «{backup}» create.':
    'Schedules «{nome}» and «{backup}» created.',
  'Piano di lavoro ({n})':
    'Work plan ({n})',
  'Piano predefinito':
    'Default plan',
  'Piano «{nome}»':
    'Plan «{nome}»',
  'Pool: {nome}':
    'Pool: {nome}',
  'PowerDNS':
    'PowerDNS',
  'Prezzo ({valuta})':
    'Price ({valuta})',
  'Prima del':
    'Before',
  'Prima le attive':
    'Active first',
  'Prima una prova':
    'Test first',
  'Primo flusso ricevuto il {data}.':
    'First flow received on {data}.',
  'Privilegia le sonde in ruolo attivo rispetto alle passive.':
    'Prefers probes in the active role over passive ones.',
  'Proponi il mapping':
    'Suggest the mapping',
  'Prova il collegamento':
    'Test the connection',
  'Prova la richiesta':
    'Test the request',
  'Prova le condizioni':
    'Test the conditions',
  'Provate in ordine: la sonda si ferma alla prima che risponde. Ogni tentativo fallito costa un timeout per ogni bersaglio.':
    'Tried in order: the probe stops at the first one that responds. Each failed attempt costs a timeout per target.',
  'Può solo consultare':
    'View only',
  'Qualcuno gli ha tolto il permesso di consegnare.':
    'Someone has revoked its permission to deliver.',
  'Questa sonda non ha ancora inviato un heartbeat.':
    'This probe has not sent a heartbeat yet.',
  'Questo certificato non copre {wildcard}: senza wildcard ogni nuovo cliente richiede un certificato nuovo, e finché non c’è il suo indirizzo si apre con un avviso.':
    'This certificate does not cover {wildcard}: without a wildcard every new customer needs a new certificate, and until it exists their address opens with a warning.',
  'Raggruppamento per ruolo o competenza, senza distribuzione.':
    'Grouping by role or skill, without distribution.',
  'Registra contratto':
    'Register contract',
  'Registra un’applicazione web e incolla questo come indirizzo di ritorno (redirect URI, callback URL):':
    'Register a web application and paste this as the return address (redirect URI, callback URL):',
  'Registrato ma non ha ancora parlato: la configurazione non è ancora sulla macchina, o l’esecuzione periodica non è stata impostata.':
    'Registered but has not reported yet: the configuration is not on the machine yet, or the periodic run has not been set up.',
  'Registro dei costruttori non caricato: il produttore si ricava solo per le marche più diffuse. Di norma arriva con l’immagine; se manca, si carica con un pulsante dalla console di amministrazione, in «Piattaforma → Registro dei costruttori». Da lì il produttore c’è per qualunque apparato SNMP, anche di marche mai viste.':
    'Vendor registry not loaded: the manufacturer is derived only for the most common brands. It normally ships with the image; if it is missing, load it with a button from the administration console, under «Platform → Vendor registry». From then on the manufacturer is available for any SNMP device, even of brands never seen before.',
  'Regole del prodotto ({n})':
    'Product rules ({n})',
  'Restano scritti a mano {ambiti}, e con il pool non vengono inclusi. Torna su «Scrivo io» per usarli.':
    "{ambiti} are still written by hand, and are not included with the pool. Go back to «I'll write them» to use them.",
  'Revoca accesso':
    'Revoke access',
  'Revocato':
    'Revoked',
  'Ricevi le consegne su questa sonda':
    'Receive deliveries on this probe',
  'Richiesta in corso…':
    'Request in progress…',
  'Riclassifica':
    'Reclassify',
  'Riclassificati {n} CI, {invariati} invariati.':
    '{n} CIs reclassified, {invariati} unchanged.',
  'Riclassifico…':
    'Reclassifying…',
  'Rileva cosa è attaccato a ogni porta':
    'Detect what is attached to each port',
  'Rileva i collegamenti tra apparati':
    'Detect links between devices',
  'Rimuovere la sonda «{nome}»? I CI che ha inventariato restano; le discovery in corso vanno concluse prima.':
    'Remove the probe «{nome}»? The CIs it inventoried are kept; running discoveries must be completed first.',
  'Rinnovo tacito':
    'Automatic renewal',
  'Rosso è ciò che la piattaforma non ha ancora riconosciuto — non classificato, non gestito, o comparso nelle ultime 24 ore. La scena legge fino a 300 CI.':
    'Red is what the platform has not yet recognised — unclassified, unmanaged, or appeared in the last 24 hours. The scene reads up to 300 CIs.',
  'Ruolo e accesso valgono su questo tenant: la stessa utenza può averne altri altrove. L’ultimo amministratore attivo non può essere rimosso o disabilitato, perché il tenant resterebbe senza nessuno in grado di gestirlo.':
    'Role and access apply to this tenant: the same user may have others elsewhere. The last active administrator cannot be removed or disabled, because the tenant would be left with no one able to manage it.',
  'SNMP v1':
    'SNMP v1',
  'SNMP v2c':
    'SNMP v2c',
  'SNMP v3':
    'SNMP v3',
  'Salva dominio':
    'Save domain',
  'Salva gli ambiti':
    'Save scopes',
  'Salva la mappatura':
    'Save mapping',
  'Salva le modifiche':
    'Save changes',
  'Salva sorgenti':
    'Save sources',
  'Salva vista':
    'Save view',
  'Salvato: {ambiti} ambiti, {indirizzi} indirizzi.':
    'Saved: {ambiti} scopes, {indirizzi} addresses.',
  'Salvato: {n} apparati corrispondono, {conIndirizzo} con un indirizzo da scansionare.':
    'Saved: {n} devices match, {conIndirizzo} with an address to scan.',
  'Scade il {quando} ed è per una sonda sola. Finché non è stato usato vale quanto una credenziale: non inoltrarlo su canali che non controlli.':
    'It expires on {quando} and is for a single probe. Until it is used it is as sensitive as a credential: do not forward it over channels you do not control.',
  'Scandisci':
    'Scan',
  'Scansione del {quando}: {n} tipi di oggetto trovati. Rilanciala per rivederne l’elenco.':
    'Scan of {quando}: {n} object types found. Run it again to refresh the list.',
  'Scansione…':
    'Scanning…',
  'Scarica il binario, scrive la configurazione, prova a raccogliere e mette l’esecuzione periodica. Quello che stampa dice anche cosa non è riuscito a leggere: senza privilegi mancano numero di serie e modello.':
    'Downloads the binary, writes the configuration, tries a collection and sets up the periodic run. Its output also says what it could not read: without privileges, serial number and model are missing.',
  'Scegli {indirizzo} {servizio}':
    'Choose {indirizzo} {servizio}',
  'Scheda di {nome}':
    '{nome} details',
  'Scollegare «{nome}»?':
    'Disconnect «{nome}»?',
  'Sconosciuto: spesso una sessione ancora attiva o non TCP.':
    'Unknown: often a session still active or non-TCP.',
  'Se fallisce, prosegui con i passi successivi':
    'If it fails, continue with the next steps',
  'Se il tipo che cerchi non è nell’elenco, scrivine il nome: su installazioni personalizzate è la norma.':
    'If the type you are looking for is not in the list, type its name: on customised installations this is the norm.',
  'Se manca {cap}: in Docker servono {opzione} e la rete dell’host. La ricezione dei flussi esportati non dipende da questo e continua a funzionare.':
    'If {cap} is missing: in Docker you need {opzione} and host networking. Receiving exported flows does not depend on this and keeps working.',
  'Secret access key':
    'Secret access key',
  'Segnalato {tempo} fa. La cattura non partirà finché non riesce a leggerla: la configurazione dell’ascolto vive nel cloud, non sulla macchina.':
    'Reported {tempo} ago. Capture will not start until it can read it: the listening configuration lives in the cloud, not on the machine.',
  'Segreto scaduto':
    'Secret expired',
  'Senza limiti la sonda esegue tutto ciò che dichiara ({elenco}).':
    'Without limits the probe runs everything it declares ({elenco}).',
  'Separati da virgola, senza la chiocciola. Chi scrive un indirizzo con uno di questi domini vede comparire il pulsante nella pagina di accesso. Un dominio può appartenere a un solo provider.':
    'Comma-separated, without the @ sign. Anyone who types an address with one of these domains sees the button appear on the sign-in page. A domain can belong to only one provider.',
  'Separati da virgola. Il certificato copre questi e nient’altro: quando aggiungi un cliente con un nome nuovo va riemesso, altrimenti quel cliente apre la propria console e trova un avviso.':
    'Comma-separated. The certificate covers these and nothing else: when you add a customer with a new name it must be reissued, otherwise that customer opens their console and gets a warning.',
  'Serie per giorno':
    'Series by day',
  'Serve Linux, curl e i permessi di root.':
    'Requires Linux, curl and root permissions.',
  'Serve Windows x64 e i privilegi di amministratore. La sonda si registra come servizio, riparte da sola dopo un riavvio, e non fa la cattura diretta dal filo: quella usa un’interfaccia del kernel Linux. Tutto il resto — SNMP, porte, SSH, WinRM, NetFlow ricevuto, connettori — funziona uguale.':
    'Requires Windows x64 and administrator privileges. The probe registers as a service, restarts by itself after a reboot, and does not perform direct capture from the wire: that uses a Linux kernel interface. Everything else — SNMP, ports, SSH, WinRM, received NetFlow, connectors — works the same.',
  'Serve prima mettere a fuoco un oggetto: la direzione è rispetto a lui':
    'First focus on an object: the direction is relative to it',
  'Sessione scaduta per inattività (timeout), non chiusa esplicitamente.':
    'Session expired due to inactivity (timeout), not closed explicitly.',
  'Sessione scaduta: effettua di nuovo l’accesso':
    'Session expired: please sign in again',
  'Si aggiunge ai pool scelti.':
    'Added to the selected pools.',
  'Sistema: {sistema} — non modificabile. Per un altro sistema serve una credenziale nuova.':
    'System: {sistema} — cannot be changed. For another system you need a new credential.',
  'Sola lettura':
    'Read-only',
  'Solo {cosa}':
    'Only {cosa}',
  'Sonda attiva':
    'Active probe',
  'Sonde fuori da ogni gruppo ({n})':
    'Probes outside any group ({n})',
  'Sono {n} indirizzi e una scansione ne espande al massimo {tetto}: la sonda rifiuterebbe il job intero. Scegli meno pool, o dividili.':
    'That is {n} addresses and a scan expands at most {tetto}: the probe would reject the whole job. Choose fewer pools, or split them.',
  'Sotto 1024 servono privilegi che la sonda non ha: scegli una porta più alta.':
    'Below 1024 privileges are needed that the probe does not have: choose a higher port.',
  'Sposta {nome} più in alto':
    'Move {nome} up',
  'Sposta {nome} più in basso':
    'Move {nome} down',
  'Statico':
    'Static',
  'Su {n} CI sono stati ricavati modello o versione dalla descrizione di sistema.':
    'Model or version was derived from the system description on {n} CIs.',
  'Subnet CIDR (max /16), indirizzi singoli o intervalli, separati da virgola.':
    'CIDR subnets (max /16), single addresses or ranges, comma-separated.',
  'Subnet, indirizzi o intervalli, separati da virgola.':
    'Subnets, addresses or ranges, comma-separated.',
  'Sul sistema di destinazione il software installato alimenta il riconoscimento dei prodotti e il fabbisogno di licenze: accendilo solo se l’inventario software di questo parco è completo. Due cose da sapere: le righe si scrivono alla creazione dell’oggetto — su uno che esiste già si possono aggiornare e togliere, non aggiungere — e ciò che non si riesce ad aggiungere compare nel rapporto della corsa invece di essere taciuto.':
    'On the target system, installed software feeds product recognition and licence requirements: turn it on only if the software inventory of this estate is complete. Two things to know: rows are written when the object is created — on an existing one they can be updated and removed, not added — and anything that cannot be added appears in the run report instead of being silently dropped.',
  'Tipi ammessi: {tipi}. Senza, il passo fallirebbe su ogni bersaglio — e il rapporto direbbe «accesso negato», che manda a controllare le credenziali invece di aggiungerne una.':
    'Allowed types: {tipi}. Without one, the step would fail on every target — and the report would say «access denied», which sends you to check the credentials instead of adding one.',
  'Tipi di {tipo}':
    'Types of {tipo}',
  'Tipi non ancora classificati in una famiglia':
    'Types not yet classified into a family',
  'Tipo dichiarato dalla sorgente o riconoscimento inequivocabile':
    'Type declared by the source or unambiguous recognition',
  'Togli l’evidenza dalla mappa':
    'Clear the highlight from the map',
  'Togli {nome} dal gruppo':
    'Remove {nome} from the group',
  'Topologia (CDP/LLDP)':
    'Topology (CDP/LLDP)',
  'Traffico osservato: {n} intervalli, massimo {max}':
    'Observed traffic: {n} intervals, maximum {max}',
  'Troppi tentativi: riprova fra qualche minuto.':
    'Too many attempts: try again in a few minutes.',
  'Ultima osservazione':
    'Last seen',
  'Ultimo invio':
    'Last sent',
  'Un ambito per riga. Dopo il separatore — «;», tabulazione o «,» — la nota, se la vuoi.':
    'One scope per line. After the separator — «;», tab or «,» — the note, if you want one.',
  'Un apparato esporta un campione del traffico. I volumi sono riportati in scala, quindi restano confrontabili — ma sono stime, e su volumi bassi la stima è grossolana.':
    'A device exports a sample of the traffic. Volumes are reported scaled up, so they remain comparable — but they are estimates, and at low volumes the estimate is rough.',
  'Un apparato non esporta da oltre un’ora. Il traffico che passava di lì non compare più nei grafici: è un buco nei dati, non una rete diventata silenziosa.':
    'One device has not exported for over an hour. Traffic that went through it no longer appears in the charts: this is a gap in the data, not a network gone quiet.',
  'Aggiornamento in corso: le sonde vengono aggiornate gradualmente in funzione del gruppo di appartenenza. Tra un gruppo e il successivo possono trascorrere alcuni minuti.': 'Update in progress: the probes are updated gradually according to their group. A few minutes may pass between one group and the next.',
  'Un secondo di lavoro. Le sezioni non raccolte vengono dichiarate: un inventario magro non si legge come una macchina povera.':
    'A second of work. Sections that were not collected are stated as such: a thin inventory is not mistaken for a bare machine.',
  'Un servizio accetta al massimo {max} ingressi, e ne hai scelti {n}. Toglierne qualcuno non è una limitazione tecnica da aggirare: un servizio con quaranta porte di ingresso non è un servizio, è una rete — e la sua mappa lo mostrerebbe.':
    'A service accepts at most {max} entry points, and you have chosen {n}. Removing some is not a technical limitation to work around: a service with forty entry points is not a service, it is a network — and its map would show it.',
  'Una sonda lavora, le altre subentrano se cade.':
    'One probe does the work; the others take over if it goes down.',
  'Una sonda serve solo se la macchina non raggiunge il Control Plane ma raggiunge lei. Chi inoltra non legge: il carico è sigillato.':
    'A probe is only needed if the machine cannot reach the Control Plane but can reach the probe. Whoever relays cannot read: the payload is sealed.',
  'Utenti ({n})':
    'Users ({n})',
  'Va alla sonda con meno job in corso. È il default e va bene quasi sempre.':
    'Goes to the probe with the fewest running jobs. This is the default and suits almost every case.',
  'Veeam Backup & Replication':
    'Veeam Backup & Replication',
  'Verifica che la richiesta arrivi, senza consumare uno dei cinque tentativi a settimana che l’autorità concede. Il certificato di prova non è valido e non viene installato.':
    'Checks that the request gets through, without using up one of the five attempts per week the authority allows. The test certificate is not valid and is not installed.',
  'Verifica del provider…':
    'Checking the provider…',
  'Verifica e installazione…':
    'Verifying and installing…',
  'Verrà scelto in fase di discovery: un nome che dica dove o quando si applica («Filiali», «Apparati pre-2020») si riconosce fra sei mesi.':
    'It will be chosen during discovery: a name that says where or when it applies («Branches», «Pre-2020 devices») is still recognisable six months from now.',
  'Versione distribuita da questa installazione:':
    'Version distributed by this installation:',
  'Versione {v}':
    'Version {v}',
  'Vicini':
    'Neighbours',
  'Vista «{nome}» salvata con {n} nodi fissati.':
    'View «{nome}» saved with {n} pinned nodes.',
  'Visto anche da: {fonti}':
    'Also seen by: {fonti}',
  'Vuoto: si usano gli ambiti predefiniti del piano.':
    'Empty: the plan’s default scopes are used.',
  'XE':
    'XE',
  'abilitate ma non disponibili: {elenco}':
    'enabled but unavailable: {elenco}',
  'accettabile':
    'acceptable',
  'altri {n}':
    '{n} more',
  'archiviato su':
    'stored on',
  'arn:aws:iam::123456789012:role/CensoDiscovery':
    'arn:aws:iam::123456789012:role/CensoDiscovery',
  'catena di {n}':
    'chain of {n}',
  'chiave':
    'key',
  'chiudi':
    'close',
  'collegato a':
    'connected to',
  'configurata':
    'configured',
  'configuration item collegato':
    'linked configuration item',
  'connettore inesistente':
    'connector does not exist',
  'consigliata':
    'recommended',
  'contiene':
    'contains',
  'corrisponde all’espressione':
    'matches the expression',
  'costo {c}':
    'cost {c}',
  'da confermare':
    'to be confirmed',
  'dall’inizio':
    'from the start',
  'destinazione → CENSO':
    'destination → CENSO',
  'dipende da':
    'depends on',
  'disabilitata':
    'disabled',
  'disabilitate: {elenco}':
    'disabled: {elenco}',
  'dominio':
    'domain',
  'doppio senso':
    'both directions',
  'e altri {n}':
    'and {n} more',
  'es. 10.0.0.10':
    'e.g. 10.0.0.10',
  'eu-frankfurt-1':
    'eu-frankfurt-1',
  'eu-west-1':
    'eu-west-1',
  'fermata':
    'stopped',
  'fermo':
    'silent',
  'finisce con':
    'ends with',
  'fino alla scadenza':
    'until expiry',
  'fra {n} giorni':
    'in {n} days',
  'fra {n} giorni · {motivo}':
    'in {n} days · {motivo}',
  'gira su':
    'runs on',
  'già nella mappatura':
    'already in the mapping',
  'giù (livello inferiore)':
    'down (lower layer)',
  'gruppo {nome}':
    'group {nome}',
  'http://dns.example.com:8081':
    'http://dns.example.com:8081',
  'https://192.0.2.1':
    'https://192.0.2.1',
  'https://ntopng.example.com:3000':
    'https://ntopng.example.com:3000',
  'https://vcenter.example.com':
    'https://vcenter.example.com',
  'https://veeam.example.com:9419':
    'https://veeam.example.com:9419',
  'https://wazuh.example.com:55000':
    'https://wazuh.example.com:55000',
  'https://wazuh.example.com:9200':
    'https://wazuh.example.com:9200',
  'il passo «{id}»':
    'step «{id}»',
  'il primo':
    'the first',
  'il secondo':
    'the second',
  'in corso':
    'in progress',
  'in ritentativo':
    'retrying',
  'indietro':
    'behind',
  'inizia per':
    'starts with',
  'instrada verso':
    'routes to',
  'kernel {v}':
    'kernel {v}',
  'la condizione usa «{fatto}», che a questo punto del piano non esiste ancora: il passo non partirebbe mai':
    'the condition uses «{fatto}», which does not exist yet at this point in the plan: the step would never start',
  'localhost':
    'localhost',
  'l’elenco contiene':
    'the list contains',
  'membro di':
    'member of',
  'meno di 1 MB':
    'less than 1 MB',
  'modello':
    'model',
  'mostra in mappa':
    'show on map',
  'nascondi':
    'hide',
  'nelle ultime {n} ore':
    'in the last {n} hours',
  'nessun fallimento':
    'no failures',
  'nessun ruolo':
    'no role',
  'non ({condizione})':
    'not ({condizione})',
  'non contiene':
    'does not contain',
  'non ha capability abilitate':
    'has no capabilities enabled',
  'non ha dichiarato alcuna capability':
    'has not declared any capabilities',
  'non serve':
    'not needed',
  'non si riprova da sé':
    'will not retry on its own',
  'non è online (stato: {stato})':
    'is not online (status: {stato})',
  'non è online (stato: {stato}, {motivo})':
    'is not online (status: {stato}, {motivo})',
  'noto':
    'known',
  'nulla':
    'nothing',
  'ogni flusso':
    'every flow',
  'ogni {n} g':
    'every {n} d',
  'parla con':
    'talks to',
  'passata da {n} giorni':
    '{n} days ago',
  'postgres':
    'postgres',
  'predefinito: {v}':
    'default: {v}',
  'prossima: {quando}':
    'next: {quando}',
  'protocollo {protocollo}':
    'protocol {protocollo}',
  'provider non disponibile':
    'provider unavailable',
  'richiesta':
    'request',
  'riga {n}':
    'row {n}',
  'rilevato tramite {fonte}':
    'detected via {fonte}',
  'riuscita':
    'succeeded',
  'scelto a mano':
    'chosen manually',
  'sconosciuta':
    'unknown',
  'se':
    'if',
  'senza indirizzo':
    'no address',
  'servizio':
    'service',
  'snmp':
    'snmp',
  'su':
    'up',
  'traffico di ritorno':
    'return traffic',
  'ultime {n} ore':
    'last {n} hours',
  'valore non valido':
    'invalid value',
  'versione':
    'version',
  'versione {v}':
    'version {v}',
  '{a} su {v} fisici':
    '{a} on {v} physical',
  '{campo} non è falso':
    '{campo} is not false',
  '{campo} non è vero':
    '{campo} is not true',
  '{campo} non è {valore}':
    '{campo} is not {valore}',
  '{campo} è falso':
    '{campo} is false',
  '{campo} è vero':
    '{campo} is true',
  '{campo} è {valore}':
    '{campo} is {valore}',
  '{d} g {h} h':
    '{d} d {h} h',
  '{id}… — non esiste più':
    '{id}… — no longer exists',
  '{nome} (1 sonda)':
    '{nome} (1 probe)',
  '{nome} (non disponibile)':
    '{nome} (unavailable)',
  '{nome} ({n} passi)':
    '{nome} ({n} steps)',
  '{nome} ({n} sonde)':
    '{nome} ({n} probes)',
  '{nome} e altri {n}':
    '{nome} and {n} more',
  '{nome} · {n} ambiti':
    '{nome} · {n} scopes',
  '{nome} — backup configurazioni':
    '{nome} — configuration backup',
  '{nome} — gruppo':
    '{nome} — group',
  '{nome} — non in linea':
    '{nome} — offline',
  '{nome} — predefinito ({n} passi)':
    '{nome} — default ({n} steps)',
  '{nome} — {n} credenziali':
    '{nome} — {n} credentials',
  '{nomi} è un wildcard: non si può convalidare con questa strada, perché dimostrare il controllo di {esempio} richiede di scrivere un record DNS. Per il wildcard servono le credenziali del provider e lo script {script}.':
    '{nomi} is a wildcard: it cannot be validated this way, because proving control of {esempio} requires writing a DNS record. For the wildcard you need the provider’s credentials and the {script} script.',
  '{n} CI non sono stati riconosciuti da alcuna regola. Restano consultabili e classificati come «non classificati»: attribuirli a caso sarebbe peggio, perché l’errore si noterebbe solo a valle.':
    '{n} CIs were not recognised by any rule. They remain available and are marked as «unclassified»: assigning them at random would be worse, because the error would only be noticed downstream.',
  '{n} ambiti · {indirizzi} indirizzi':
    '{n} scopes · {indirizzi} addresses',
  '{n} apparati adesso — dalle condizioni':
    '{n} devices now — from the conditions',
  '{n} apparati corrispondono, {conIndirizzo} con un indirizzo da scansionare.':
    '{n} devices match, {conIndirizzo} with an address to scan.',
  '{n} apparati esportano un campione del traffico. I volumi sono riportati in scala, quindi restano confrontabili — ma sono stime, e su volumi bassi la stima è grossolana.':
    '{n} devices export a sample of the traffic. Volumes are scaled up, so they remain comparable — but they are estimates, and on low volumes the estimate is rough.',
  '{n} apparati non esportano da oltre un’ora. Il traffico che passava di lì non compare più nei grafici: è un buco nei dati, non una rete diventata silenziosa.':
    '{n} devices have not exported for over an hour. Traffic that went through them no longer appears in the charts: this is a gap in the data, not a network gone quiet.',
  '{n} campi':
    '{n} fields',
  '{n} campi non proposti, di proposito — vedi perché':
    '{n} fields deliberately not suggested — see why',
  '{n} corrispondono alle condizioni ma non hanno un indirizzo di gestione: non si possono scansionare.':
    '{n} match the conditions but have no management address: they cannot be scanned.',
  '{n} corrispondono ma non hanno un indirizzo di gestione, quindi non si possono scansionare':
    '{n} match but have no management address, so they cannot be scanned',
  '{n} corrispondono ma non hanno un indirizzo di gestione, quindi non si possono scansionare: {esempi}…':
    '{n} match but have no management address, so they cannot be scanned: {esempi}…',
  '{n} credenziale':
    '{n} credential',
  '{n} credenziali':
    '{n} credentials',
  '{n} di fila':
    '{n} in a row',
  '{n} di {tot} coperti':
    '{n} of {tot} covered',
  '{n} disabilitate':
    '{n} disabled',
  '{n} flussi':
    '{n} flows',
  '{n} g':
    '{n} d',
  '{n} già presenti nel file.':
    '{n} already in the file.',
  '{n} in corso':
    '{n} in progress',
  '{n} nodi corrispondono a «{q}».':
    '{n} nodes match «{q}».',
  '{n} nodi fissati.':
    '{n} pinned nodes.',
  '{n} nodi fuori dall’intorno non sono mostrati.':
    '{n} nodes outside the neighbourhood are not shown.',
  '{n} nodi, {m} collegamenti':
    '{n} nodes, {m} links',
  '{n} non IP (ARP, STP, LLDP)':
    '{n} non-IP (ARP, STP, LLDP)',
  '{n} oggetti':
    '{n} objects',
  '{n} pacchetti':
    '{n} packets',
  '{n} record':
    '{n} records',
  '{n} registrazioni di prodotto caricate dai MIB. Le righe qui sotto con un OID e senza corrispondenza sono quelle per cui manca il MIB di quel costruttore.':
    '{n} product registrations loaded from the MIBs. The rows below with an OID and no match are those for which that vendor’s MIB is missing.',
  '{n} righe lasciate indietro.':
    '{n} rows left behind.',
  '{n} su {campione} letti':
    '{n} of {campione} read',
  '{n} volte':
    '{n} times',
  '{n} volte, l’ultima {quando}':
    '{n} times, most recently {quando}',
  '{ok} ok, {ko} ko, {nv} non valutabili':
    '{ok} ok, {ko} failed, {nv} not assessable',
  '{rate} medi su {min} min':
    '{rate} average over {min} min',
  '{stato} · {n} indirizzi noti':
    '{stato} · {n} known addresses',
  '{tipo} — ricorrente':
    '{tipo} — recurring',
  '{tipo}: job {id}… avviato, con il backup configurazioni a seguire.':
    '{tipo}: job {id}… started, with the configuration backup to follow.',
  '{tipo}: job {id}… avviato. Verrà assegnato a una sonda online.':
    '{tipo}: job {id}… started. It will be assigned to an online probe.',
  '{vcpu} vCPU · {n} core':
    '{vcpu} vCPU · {n} cores',
  '{volume} in totale':
    '{volume} in total',
  '{volume} in {n} intervalli da {risoluzione}.':
    '{volume} in {n} intervals of {risoluzione}.',
  '{v} di RAM':
    '{v} of RAM',
  '«{fatto}» non è disponibile a questo punto del piano':
    '«{fatto}» is not available at this point in the plan',
  '«{nome}» avviata ora (job {job}…). La prossima esecuzione programmata resta invariata.':
    '«{nome}» started now (job {job}…). The next scheduled run is unchanged.',
  '«{nome}»: la configurazione risponde.':
    '«{nome}»: the configuration responds.',
  '«{nome}»: qualcosa non torna.':
    '«{nome}»: something is not right.',
  '«{passo}» è fallito':
    '«{passo}» failed',
  '«{passo}» è riuscito':
    '«{passo}» succeeded',
  '«{passo}» è stato saltato':
    '«{passo}» was skipped',
  '«{valore}» non è un numero':
    '«{valore}» is not a number',
  '«{valore}» non è una porta (1-65535)':
    '«{valore}» is not a port (1-65535)',
  'È quello generato all’installazione: il servizio funziona, ma ogni browser mostrerà un avviso di sicurezza e i clienti dovranno accettarlo a mano. Sostituiscilo qui sotto quando ne hai uno emesso da una CA pubblica.':
    'This is the one generated at installation: the service works, but every browser will show a security warning and customers will have to accept it manually. Replace it below once you have one issued by a public CA.',
  'È stato costruito senza {variabile}, quindi non c’è nulla con cui confrontare le sonde e nessun aggiornamento può essere richiesto.':
    'It was built without {variabile}, so there is nothing to compare the probes against and no update can be requested.',
  'è assente':
    'is missing',
  'è disattivata dalla console':
    'is disabled from the console',
  'è uno di':
    'is one of',
  '•••••• (invariato)':
    '•••••• (unchanged)',
  '…oppure carica un CSV':
    '…or upload a CSV',
  '…oppure incolla un certificato che hai già':
    '…or paste a certificate you already have',
  '…oppure se…':
    '…or if…',
  '← Torna ai beni':
    '← Back to assets',
  '← Torna ai servizi':
    '← Back to services',
  '← Torna alle sonde':
    '← Back to probes',
  'Annulla la dismissione':
    'Undo decommissioning',
  'Annullamento…':
    'Undoing…',
  'Collegamenti ({n})':
    'Links ({n})',
  'Comunica con':
    'Talks to',
  'Conferma registrazione':
    'Confirm registration',
  'Dipende da':
    'Depends on',
  'Gira su':
    'Runs on',
  'Membri dello stack':
    'Stack members',
  'Membro di':
    'Member of',
  'Ospita':
    'Hosts',
  'Parte di':
    'Part of',
  'Preparazione…':
    'Preparing…',
  'Questo configuration item è già registrato come bene: {bene}.':
    'This configuration item is already registered as an asset: {bene}.',
  'Questo oggetto era registrato più di una volta. L’identificativo {id} è stato unito a questo e continua a portare qui: i collegamenti salvati restano validi.':
    'This object was registered more than once. The identifier {id} has been merged into this one and still leads here: saved links remain valid.',
  'Registrazione…':
    'Registering…',
  'Rimozione…':
    'Removing…',
  'Sicurezza ({n})':
    'Security ({n})',
  'Storico delle modifiche ({n})':
    'Change history ({n})',
  'Togliere {nome} dalla CMDB?\n\nSe questo apparato esiste ancora in rete, la prossima scansione lo riporta come oggetto nuovo, senza la sua storia: per una macchina dismessa usa «Dichiara dismesso», più in alto in questa pagina, non la cancellazione.\n\nSpariscono interfacce, software, relazioni e storico. Il bene eventualmente registrato resta, e perde solo il collegamento a questo CI.':
    'Remove {nome} from the CMDB?\n\nIf this device still exists on the network, the next scan will bring it back as a new object, without its history: for a decommissioned machine use «Declare decommissioned», higher up on this page, not deletion.\n\nInterfaces, software, relationships and history are removed. Any registered asset remains, and only loses its link to this CI.',
  'Torna all’automatico':
    'Back to automatic',
  'Ultima verifica: {quando}.':
    'Last check: {quando}.',
  'da questo CI':
    'from this CI',
  'dalla discovery':
    'from discovery',
  'nessun job: conclusione del Control Plane':
    'no job: a Control Plane conclusion',
  'verso questo CI':
    'towards this CI',
  'Exporter':
    'Exporter',
  'IP VPN':
    'VPN IP',
  'Accorcia la storia una tantum, senza cambiare la regola. Non si annulla.':
    'Shortens the history as a one-off, without changing the rule. It cannot be undone.',
  'Aggiorna dalla IANA':
    'Update from IANA',
  'Applica la conservazione adesso':
    'Apply retention now',
  'Applicata alle tabelle esistenti. {nota}':
    'Applied to the existing tables. {nota}',
  'Applicazione…':
    'Applying…',
  'Carica un file':
    'Upload a file',
  'ClickHouse elimina i dati scaduti alla merge successiva della parte che li contiene: dopo aver accorciato la conservazione lo spazio si libera nel giro di ore, non subito. Questo comando non aspetta.':
    'ClickHouse deletes expired data at the next merge of the part that holds it: after shortening retention, space is freed within hours, not straight away. This command does not wait.',
  'Il dettaglio a cinque minuti risponde a «cos’è successo l’altro ieri alle 15» ed è la tabella che cresce: ogni giorno in più costa circa quanto dodici giorni di rollup. Il rollup orario risponde a «come è cambiato l’anno», e tredici mesi coprono il confronto con lo stesso periodo dell’anno prima.':
    'The five-minute detail answers “what happened the day before yesterday at 3 pm” and is the table that grows: each extra day costs about as much as twelve days of rollup. The hourly rollup answers “how has the year changed”, and thirteen months cover the comparison with the same period of the previous year.',
  'Incolla il contenuto di {file}, scaricato da una macchina che ha rete':
    'Paste the contents of {file}, downloaded from a machine with network access',
  'Non applicata alle tabelle: {motivo}':
    'Not applied to the tables: {motivo}',
  'Ogni sysObjectID contiene il numero che la IANA ha assegnato a un’organizzazione: è da lì che si ricava il produttore di un apparato, anche di una marca mai vista. L’elenco è pubblico e conta circa sessantamila voci.':
    'Every sysObjectID contains the number IANA assigned to an organisation: that is where a device’s manufacturer is derived from, even for a brand never seen before. The list is public and has about sixty thousand entries.',
  'Retention salvata: {dettaglio} giorni di dettaglio, {rollup} di rollup.':
    'Retention saved: {dettaglio} days of detail, {rollup} of rollup.',
  'Rimuovere tutto il traffico precedente a {giorni} giorni fa? L’operazione non si annulla: quei dati non sono altrove.':
    'Remove all traffic older than {giorni} days? This cannot be undone: that data is not stored anywhere else.',
  'Salva e applica':
    'Save and apply',
  'Scarica il registro':
    'Download the registry',
  'Tabelle interessate: {tabelle}.':
    'Tables affected: {tabelle}.',
  'Misurare: {motivo}':
    'Measure: {motivo}',
  'Rimediare: {motivo}':
    'Remediate: {motivo}',
  'Pool':
    'Pool',
  'Allocazione':
    'Allocation',
  'Allocata':
    'Allocated',
  'LUN: nome, WWN, pool, thin o thick, capacità e spazio allocato. Su una LUN thin la capacità è una promessa: lo spazio che prende davvero dal pool è l’«Allocata», con la percentuale. Il WWN è l’identità con cui gli host vedono la LUN (il dispositivo naa seguito dal WWN).':
    'LUNs: name, WWN, pool, thin or thick, capacity and allocated space. On a thin LUN the capacity is a promise: the space it really takes from the pool is «Allocated», with the percentage. The WWN is the identity under which hosts see the LUN (the naa device followed by the WWN).',
  '{n} aggiunti':
    '{n} added',
  '{n} tolti':
    '{n} removed',
  'aggiunto {campo}':
    '{campo} added',
  'tolto {campo}':
    '{campo} removed',
  'cambiato {campo}':
    '{campo} changed',
  '{n} elementi: {come}':
    '{n} items: {come}',
  'stesso contenuto, in altro ordine':
    'same content, different order',
  'Aggiornamento automatico':
    'Automatic update',
  'aggiornamento automatico':
    'automatic update',
  'Quando il Control Plane passa a una versione nuova, la sonda si aggiorna da sola in un momento a caso (entro 3 minuti fino a cinque sonde, entro 10 fino a venti, entro 20 oltre), senza premere «Aggiorna»: così una fleet non si riavvia tutta insieme. Se è l’unica sonda del suo gruppo pesca nella seconda metà della finestra. Il freno resta: se gli aggiornamenti falliscono, le sonde non ancora partite si fermano, e non c’è mai un nuovo tentativo automatico su una sonda che ha già rifiutato quella versione. La sonda si riavvia: per qualche istante la sua rete resta senza raccolta.':
    'When the Control Plane moves to a new version, the probe updates by itself at a random moment (within 3 minutes up to five probes, within 10 up to twenty, within 20 beyond), without pressing «Update»: so a fleet does not restart all at once. If it is the only probe of its group it draws from the second half of the window. The brake stays: if updates fail, the probes not yet started stop, and there is never a new automatic attempt on a probe that already refused that version. The probe restarts: for a moment its network goes without collection.',
  'Le sonde nuove nascono con «Aggiornamento automatico» acceso (per quelle già arruolate si accende nella scheda): quando la piattaforma cambia versione, ognuna si aggiorna da sola in un momento a caso, così la fleet non si riavvia tutta insieme: entro 3 minuti fino a cinque sonde, entro 10 fino a venti, entro 20 oltre. Nell’elenco delle sonde, sotto la versione, compare «aggiornamento automatico» dove è acceso.':
    'New probes are born with «Automatic update» on (for probes already enrolled it is turned on in their page): when the platform changes version, each updates by itself at a random moment, so the fleet does not restart all at once: within 3 minutes up to five probes, within 10 up to twenty, within 20 beyond. In the probe list, under the version, «automatic update» appears where it is on.',
  'IQN (initiator iSCSI)':
    'IQN (iSCSI initiator)',
  'WWPN (porte FC)':
    'WWPN (FC ports)',
  'Con gli host: dagli host registrati e dagli initiator nasce la relazione «archiviato su» host → array. La prova più forte è l’IQN o il WWPN: gli host li dichiarano da sé (ESXi dalla vCenter, Linux e Windows dall’inventario SSH/WinRM e dall’agente) e l’initiator dell’array che ha lo stesso valore aggancia l’host giusto anche senza nome, in qualunque ordine arrivino le scansioni. In mancanza, il nome registrato sull’array o gli indizi nell’IQN (VMware, Microsoft, open-iscsi).':
    'With hosts: the registered hosts and the initiators produce the «stored on» relationship host → array. The strongest evidence is the IQN or WWPN: hosts declare them themselves (ESXi from vCenter, Linux and Windows from the SSH/WinRM inventory and the agent), and the array initiator with the same value hooks the right host even without a name, whatever order the scans arrive in. Failing that, the name registered on the array or the hints inside the IQN (VMware, Microsoft, open-iscsi).',
  'Ruotare la chiave dei segreti di «{nome}»? I segreti esistenti si ricifrano da soli nei minuti successivi; fino ad allora restano leggibili con la chiave precedente.':
    'Rotate the secrets key of «{nome}»? Existing secrets are re-encrypted automatically over the next few minutes; until then they remain readable with the previous key.',
  'Chiavi dei segreti per cliente':
    'Secrets keys per customer',
  'Ogni cliente cifra le proprie credenziali e i segreti dei connettori con una chiave sua, conservata cifrata dalla chiave master della piattaforma: una chiave esposta riguarda un cliente solo, e cancellare un cliente rende illeggibili i suoi segreti anche in un vecchio backup. I segreti di prima migrano da soli, a lotti, a ogni giro dell’orchestratore.':
    'Each customer encrypts its credentials and connector secrets with its own key, stored encrypted by the platform master key: an exposed key affects one customer only, and deleting a customer makes its secrets unreadable even in an old backup. Older secrets migrate on their own, in batches, at every orchestrator cycle.',
  'Versione della chiave':
    'Key version',
  'Segreti':
    'Secrets',
  'Da migrare':
    'To migrate',
  'non ancora creata':
    'not created yet',
  'Rotazione…':
    'Rotating…',
  'Ruota la chiave':
    'Rotate the key',
  'Altri indirizzi di gestione':
    'Other management addresses',
  'Il Control Plane ha rifiutato i risultati della sonda (vedi l’errore sopra), e il rapporto viaggiava con loro. Non è la sonda: ritenta il job quando il problema del Control Plane è risolto.':
    "The Control Plane rejected the probe's results (see the error above), and the report travelled with them. It is not the probe: retry the job once the Control Plane problem is fixed.",
  'Tabella di routing mancante':
    'Routing table missing',
  'tabella di routing non raccolta: il percorso si conosce fin qui':
    'routing table not collected: the path is known up to here',
  'senza tabella':
    'no table',
  'da un IP sorgente a uno di destinazione, il giro che il pacchetto farebbe attraverso le tabelle di routing raccolte — salto per salto, con l’interfaccia di uscita. Il next-hop si riconosce anche quando è un indirizzo secondario, un IP di gestione o il VIP VRRP/HSRP di una coppia di core (il salto dedotto lo dice). Se il percorso si ferma su «Tabella di routing mancante», il next-hop è un apparato noto di cui non hai raccolto le rotte: accendi l’opzione «Tabelle di routing» nella sua scansione e riprova.':
    'from a source IP to a destination IP, the route a packet would take through the collected routing tables — hop by hop, with the outgoing interface. The next hop is recognised even when it is a secondary address, a management IP or the VRRP/HSRP VIP of a core pair (the inferred hop says so). If the path stops on «Routing table missing», the next hop is a known device whose routes you have not collected: turn on the «Routing tables» option in its scan and try again.',
  'Un controller di gestione (iDRAC, iLO) e il suo server restano due CI, legati da «Parte di»: il legame si fa dal service tag che il controller dichiara (Seriale del telaio) e che il server porta come seriale, in qualunque ordine arrivino le scansioni. Il server prende il ruolo «Gestito fuori banda», e in testa alla sua scheda compare in evidenza «IP di Management»: l’indirizzo del controller (si apre la sua console) e il nome, che porta alla scheda del controller; sulla scheda del controller, viceversa, «Controller di gestione di» porta al server. Se il legame manca, guarda nella scheda del controller il Seriale del telaio: se è vuoto, la sonda non l’ha letto via SNMP (credenziale o versione della sonda).':
    'A management controller (iDRAC, iLO) and its server remain two CIs, linked by «Part of»: the link comes from the service tag the controller declares (Chassis serial) and the server carries as its serial, whatever order the scans arrive in. The server gets the «Managed out of band» role, and «Management IP» appears highlighted at the top of its page: the controller\'s address (opens its console) and its name, which leads to the controller\'s page; on the controller\'s page, «Management controller of» leads back to the server. If the link is missing, check the Chassis serial on the controller\'s page: if it is empty, the probe did not read it via SNMP (credential or probe version).',
  'IP di Management': 'Management IP',
  'Controller di gestione': 'Management controller',
  'Controller di gestione di': 'Management controller of',
  'Apri la console del controller di gestione': 'Open the management controller\'s console',
  'Canale attuale: {come}':
    'Current channel: {come}',
  "Come all'installazione (CENSO_PROXY, altrimenti diretta)":
    'As at installation (CENSO_PROXY, otherwise direct)',
  'Comunicazione con il Control Plane':
    'Communication with the Control Plane',
  'Destinazioni che escono dirette':
    'Destinations that go out directly',
  'Diretta, senza proxy':
    'Direct, without proxy',
  'Il proxy HTTP che questa sonda usa per uscire. Serve per la comunicazione con il Control Plane, se lo scegli qui sotto, e per i connettori che nella loro scheda scelgono «proxy della sonda».':
    'The HTTP proxy this probe uses to go out. It is used for communication with the Control Plane, if you choose it below, and by connectors that choose «probe proxy» on their page.',
  'Il proxy si configura nella scheda della sonda (sezione Proxy).':
    "The proxy is configured on the probe's page (Proxy section).",
  'Indirizzo del proxy':
    'Proxy address',
  'La sonda prova il nuovo modo prima di adottarlo: se il Control Plane non risponde così, resta sul modo che funziona e l’errore compare qui. Un proxy che ispeziona il TLS rompe l’identità mTLS della sonda: va escluso dall’ispezione.':
    "The probe tests the new mode before adopting it: if the Control Plane does not answer that way, it stays on the mode that works and the error shows here. A proxy that inspects TLS breaks the probe's mTLS identity: exclude it from inspection.",
  'La sonda scelta non ha un proxy configurato: le chiamate falliranno finché non lo imposti nella sua scheda.':
    'The chosen probe has no proxy configured: calls will fail until you set it on its page.',
  'Nessun proxy':
    'No proxy',
  'Applicato alle connessioni originate dalla sonda verso questo sistema.': 'Applied to the connections originating from the probe towards this system.',
  'Proxy':
    'Proxy',
  'Proxy della sonda':
    'Probe proxy',
  'Proxy specificato':
    'Specified proxy',
  'Togli la password salvata':
    'Remove the saved password',
  'Tramite questo proxy':
    'Through this proxy',
  'Una per riga: host, domini (.azienda.local), IP o reti (10.0.0.0/8). Proposte le reti private: vCenter, iDRAC e storage in LAN non passano dal proxy.':
    'One per line: hosts, domains (.company.local), IPs or networks (10.0.0.0/8). Private networks are suggested: vCenter, iDRAC and storage on the LAN do not go through the proxy.',
  'Userà {url}, configurato nella scheda della sonda.':
    "It will use {url}, configured on the probe's page.",
  'Utente (facoltativo)':
    'User (optional)',
  "come all'installazione":
    'as at installation',
  'diretto':
    'direct',
  'http, https o socks5. Vuoto = la sonda non ha un proxy.':
    'http, https or socks5. Empty = the probe has no proxy.',
  'in applicazione: {come}':
    'being applied: {come}',
  'proxy di installazione: {p}':
    'installation proxy: {p}',
  'tramite il proxy':
    'through the proxy',
  'Una sonda dietro un proxy':
    'A probe behind a proxy',
  'Se la sonda esce solo da un proxy, installala con CENSO_PROXY=http://proxy:porta davanti al comando: lo script scarica attraverso il proxy e lo lascia nel servizio. Poi, nella scheda della sonda (sezione Proxy), il proxy si governa dalla console: indirizzo, utente, password, destinazioni dirette e come parlare con il Control Plane (come all’installazione, diretta, tramite il proxy).':
    "If the probe can only go out through a proxy, install it with CENSO_PROXY=http://proxy:port in front of the command: the script downloads through the proxy and leaves it in the service. Then, on the probe's page (Proxy section), the proxy is managed from the console: address, user, password, direct destinations and how to talk to the Control Plane (as at installation, direct, through the proxy).",
  'Un cambio del canale verso il Control Plane la sonda lo prova prima di adottarlo: se con il nuovo proxy il Control Plane non risponde, resta su quello che funziona e la scheda mostra l’errore. Un proxy sbagliato non stacca la sonda.':
    'The probe tests a change of the channel to the Control Plane before adopting it: if the Control Plane does not answer through the new proxy, it stays on the one that works and the page shows the error. A wrong proxy does not disconnect the probe.',
  'Ogni connettore che esce da una sonda (ITSM «da una sonda», Wazuh, firewall, DNS, Veeam, Vinchin, ntopng) sceglie accanto alla sonda il proprio proxy: nessuno, uno specificato lì, o il proxy della sonda. Se sceglie quello della sonda e la sonda non ne ha uno, la chiamata fallisce dicendolo invece di uscire diretta.':
    "Every connector that goes out from a probe (ITSM «from a probe», Wazuh, firewall, DNS, Veeam, Vinchin, ntopng) chooses its own proxy next to the probe: none, one specified there, or the probe proxy. If it chooses the probe's and the probe has none, the call fails saying so instead of going out directly.",
  'Anche la sonda è una macchina della rete: una volta installata fa l’inventario di sé stessa, come un agente (sistema, dischi, software, porte), e ne nasce un CI. Dalla scheda della sonda «CI della macchina» porta al CI; nella scheda del CI la targhetta «Sonda CENSO» porta alla sonda. Si ripete ogni 12 ore. In un container non si fa: descriverebbe il container, non la macchina.':
    "The probe is a machine of the network too: once installed it inventories itself, like an agent (system, disks, software, ports), and a CI is created. On the probe's page «Machine CI» leads to the CI; on the CI's page the «CENSO probe» tag leads to the probe. It repeats every 12 hours. Not in a container: it would describe the container, not the machine.",
  'Sonda CENSO':
    'CENSO probe',
  'CI della macchina':
    'Machine CI',
  'Autoinventario non eseguito: la sonda gira in un container, e l’inventario descriverebbe il container invece della macchina.':
    'Self-inventory not run: the probe runs in a container, and the inventory would describe the container instead of the machine.',
  'Autoinventario non consegnato: {errore}':
    'Self-inventory not delivered: {errore}',
  'Autoinventario della macchina: {quando}':
    'Machine self-inventory: {quando}',
  'Le sonde, gli agenti e gli honeypot installati quando il prodotto si chiamava ADABS continuano a funzionare con i nomi di allora (per esempio il servizio adabs-agent) e si aggiornano normalmente; la scheda della sonda mostra i comandi con il nome vero di quella macchina. Reinstallandoli prendono i nomi nuovi (censo-agent) e quelli vecchi vengono tolti.':
    "Probes, agents and honeypots installed when the product was called ADABS keep working with their old names (for example the adabs-agent service) and update normally; the probe's page shows the commands with that machine's actual name. Reinstalling them gives them the new names (censo-agent) and removes the old ones.",
  'Allineati':
    'Aligned',
  'App ID':
    'App ID',
  'CI trovato':
    'CI found',
  'Che cosa è documentato in phpIPAM per gli indirizzi di questo oggetto, chiesto al momento.':
    "What phpIPAM documents for this object's addresses, asked on the spot.",
  "Codice dell'app":
    'App code',
  'Confronto':
    'Comparison',
  'Confronto: {cidr}':
    'Comparison: {cidr}',
  'Da una sonda se il sistema sta nella rete del cliente; dal Control Plane se è raggiungibile da Internet (con un certificato valido).':
    "From a probe if the system is in the customer's network; from the Control Plane if it is reachable from the Internet (with a valid certificate).",
  'Dal Control Plane':
    'From the Control Plane',
  'Documentati':
    'Documented',
  'Documentati e trovati. «nome diverso» segnala che phpIPAM e la CMDB chiamano la macchina in due modi.':
    'Documented and found. «different name» means phpIPAM and the CMDB call the machine in two ways.',
  'Documentati ma non trovati':
    'Documented but not found',
  'IPAM (phpIPAM)':
    'IPAM (phpIPAM)',
  "Identificativo dell'API app di phpIPAM, configurata in Administration → API, con privilegi di lettura o di lettura/scrittura in base all'opzione «Consenti la scrittura».": 'Identifier of the phpIPAM API app, configured under Administration → API, with read or read/write privileges depending on the «Consenti la scrittura» option.',
  'In uso ma non documentati':
    'In use but not documented',
  'Indirizzi che la discovery ha trovato in questa subnet e che phpIPAM non conosce: documentali, o sono il prossimo conflitto di indirizzo.':
    'Addresses the discovery found in this subnet that phpIPAM does not know: document them, or they are the next address conflict.',
  'Indirizzi che phpIPAM dà in uso e che nessuna scansione ha visto: da liberare, oppure apparati spenti o fuori dal perimetro delle scansioni.':
    'Addresses phpIPAM marks as in use that no scan has seen: to be released, or devices that are off or outside the scan scope.',
  'Indirizzi documentati':
    'Documented addresses',
  'Indirizzi riservati o del DHCP in phpIPAM: non assegnati a una macchina, non ci si aspetta di trovarli.':
    'Reserved or DHCP addresses in phpIPAM: not assigned to a machine, they are not expected to be found.',
  'Indirizzo di phpIPAM':
    'phpIPAM address',
  'Interrogo phpIPAM…':
    'Querying phpIPAM…',
  "L'app con sicurezza «SSL with User token»: CENSO fa il login (`POST /api/<App ID>/user/`) e usa il token a scadenza che ne riceve. Meglio un utente dedicato, in sola lettura sulle sezioni da vedere (o con scrittura, se accendi «Consenti la scrittura»).":
    'The app with «SSL with User token» security: CENSO logs in (`POST /api/<App ID>/user/`) and uses the expiring token it receives. Better a dedicated user, read-only on the sections to see (or with write access, if you turn on «Allow writing»).',
  "URL base dell'istanza phpIPAM, senza /api. L'endpoint API è /api/<App ID>/; in assenza di schema viene utilizzato HTTPS, richiesto per l'autenticazione tramite token.": 'Base URL of the phpIPAM instance, without /api. The API endpoint is /api/<App ID>/; HTTPS is used when the scheme is omitted, required for token authentication.',
  "Lettura in tempo reale dell'IPAM del cliente (phpIPAM via REST API): subnet, indirizzi e VLAN documentati, da confrontare con ciò che la discovery ha trovato. Non apre porte e non avvia job — la query esce da questa sonda attraverso il relay. Abilitala dove la sonda raggiunge phpIPAM.":
    "Real-time reading of the customer's IPAM (phpIPAM via REST API): documented subnets, addresses and VLANs, to compare with what the discovery found. It opens no ports and starts no jobs — the query goes out from this probe through the relay. Enable it where the probe reaches phpIPAM.",
  'Spazio di indirizzamento IP rilevato da phpIPAM e confrontato con i dati correnti della CMDB. La pagina mostra l’ultima sincronizzazione disponibile e si aggiorna automaticamente in sottofondo, confrontando le allocazioni IP con l’inventario più recente.':
    'IP address space read from phpIPAM and compared with the current CMDB data. The page shows the last available sync and updates itself in the background, comparing the IP allocations with the latest inventory.',
  'Nessun indirizzo documentato':
    'No documented address',
  'Nessun indirizzo in questo gruppo.':
    'No address in this group.',
  'Nessuna subnet':
    'No subnets',
  'Nessuno: tutto ciò che è in uso è documentato.':
    'None: everything in use is documented.',
  'Nome in phpIPAM':
    'Name in phpIPAM',
  'Nomi diversi':
    'Different names',
  'Non documentati':
    'Not documented',
  'Non trovati':
    'Not found',
  'Occupazione':
    'Usage',
  'Per le chiamate verso questo sistema, fatte dal Control Plane.':
    'For calls to this system, made by the Control Plane.',
  'Riservati e DHCP':
    'Reserved and DHCP',
  'Seleziona una subnet per confrontare ogni indirizzo IP documentato in phpIPAM con i rilevamenti della discovery.': 'Select a subnet to compare each IP address documented in phpIPAM with the discovery findings.',
  'Stato in phpIPAM':
    'Status in phpIPAM',
  'Subnet documentate':
    'Documented subnets',
  'Ultima vista (phpIPAM)':
    'Last seen (phpIPAM)',
  'censo':
    'censo',
  'https://ipam.example.com':
    'https://ipam.example.com',
  'nome diverso':
    'different name',
  'phpIPAM':
    'phpIPAM',
  'phpIPAM non conosce nessuno degli indirizzi di questo oggetto.':
    "phpIPAM knows none of this object's addresses.",
  'phpIPAM non ha subnet, oppure l’app non ha i permessi di lettura sulle sezioni.':
    'phpIPAM has no subnets, or the app lacks read permission on the sections.',
  'il confronto fra lo spazio IP documentato in phpIPAM e quello trovato dalla discovery, subnet per subnet: indirizzi allineati, in uso ma non documentati (il prossimo conflitto di indirizzo), documentati ma non trovati (da liberare o spenti), e dove phpIPAM e la CMDB chiamano la stessa macchina con nomi diversi. Sulla scheda di un CI, che cosa phpIPAM documenta per i suoi indirizzi.':
    "the comparison between the IP space documented in phpIPAM and the one found by the discovery, subnet by subnet: aligned addresses, in use but not documented (the next address conflict), documented but not found (to be released or switched off), and where phpIPAM and the CMDB call the same machine by different names. On a CI's page, what phpIPAM documents for its addresses.",
  'Per phpIPAM crea in phpIPAM un’«API app» in sola lettura (Administration → API) e poi una credenziale «phpIPAM» con l’indirizzo e l’App ID: codice dell’app (sicurezza «SSL with App code token») oppure utente e password. Scegli da dove si parla: da una sonda, se phpIPAM sta nella rete del cliente, o dal Control Plane, se è raggiungibile da Internet con un certificato valido. Gli indirizzi riservati o del DHCP non contano come «non trovati».':
    "For phpIPAM create a read-only «API app» in phpIPAM (Administration → API) and then a «phpIPAM» credential with the address and the App ID: app code («SSL with App code token» security) or user and password. Choose where to talk from: from a probe, if phpIPAM is in the customer's network, or from the Control Plane, if it is reachable from the Internet with a valid certificate. Reserved or DHCP addresses do not count as «not found».",
  'inclusa nel piano':
    'included in the plan',
  'I loro add-on si conservano. Chi ha una licenza più vecchia si allinea a quello che il piano comprende adesso.':
    'Their add-ons are kept. Anyone with an older licence is aligned with what the plan includes now.',
  'Modificare un piano cambia le assegnazioni future. Le licenze già emesse restano com’erano — un cliente non si vede togliere una funzione perché qualcuno ha corretto il listino — e per allinearle c’è «Riapplica ai clienti», che conserva i loro add-on.':
    'Changing a plan changes future assignments. Licences already issued stay as they were — a customer does not lose a feature because someone corrected the price list — and to align them there is «Reapply to customers», which keeps their add-ons.',
  'CI massimi':
    'Maximum CIs',
  'La fine deve seguire l’inizio.':
    'The end must follow the start.',
  'Sonde massime':
    'Maximum probes',
  'con una data':
    'with a date',
  'in giorni':
    'in days',
  'Accesso al tenant interrotto finché la licenza non viene estesa.':
    'Access to the tenant is suspended until the licence is extended.',
  'Da eliminare dal {data}':
    'To delete since {data}',
  'Estendi +{n} gg':
    'Extend +{n} d',
  'Il periodo di prova è terminato':
    'The trial period has ended',
  'La licenza è scaduta':
    'The licence has expired',
  'Licenza scaduta da oltre 15 giorni senza estensioni. Estendendola, il segno sparisce.':
    'Licence expired more than 15 days ago with no extension. Extending it removes the mark.',
  'Per continuare a usare CENSO contatta il tuo fornitore: i dati sono conservati e tornano disponibili appena la licenza viene estesa.':
    'To keep using CENSO contact your provider: the data is kept and becomes available again as soon as the licence is extended.',
  'Per continuare a usare CENSO contatta il tuo fornitore: i dati sono conservati e tornano disponibili appena la licenza viene rinnovata.':
    'To keep using CENSO contact your provider: the data is kept and becomes available again as soon as the licence is renewed.',
  'Scaduta il {data}.':
    'Expired on {data}.',
  'Sposta la scadenza della prova di {n} giorni (da oggi se è già scaduta).':
    'Moves the trial expiry by {n} days (from today if it has already expired).',
  'Vai a un altro tenant':
    'Go to another tenant',
  'prova terminata':
    'trial ended',
  'scaduta':
    'expired',
  '+{n} giorni':
    '+{n} days',
  'Approva':
    'Approve',
  'Estendi la prova di {n} giorni':
    'Extend the trial by {n} days',
  'La prima estensione è immediata.':
    'The first extension is immediate.',
  'Le estensioni successive le approva il fornitore: riceverai l’accesso appena approvata.':
    'Further extensions are approved by the provider: you will get access as soon as it is approved.',
  'L’ultima richiesta di estensione non è stata approvata.':
    'The last extension request was not approved.',
  'Puoi farne un’altra dal {data}.':
    'You can make another one from {data}.',
  'Richiedi altri {n} giorni':
    'Request {n} more days',
  'Richiesta di {n} giorni inviata il {data}: in attesa di approvazione del fornitore.':
    "Request for {n} days sent on {data}: awaiting the provider's approval.",
  'Richieste di estensione della prova ({n})':
    'Trial extension requests ({n})',
  'Rifiuta':
    'Reject',
  'A questo tenant non è assegnata una licenza: contatta il tuo fornitore.':
    'No licence is assigned to this tenant: contact your provider.',
  'Nessuna licenza assegnata':
    'No licence assigned',
  'Ogni tenant nasce con una licenza. La trial comprende tutti i moduli fino alla scadenza.':
    'Every tenant is created with a licence. The trial includes all modules until it expires.',
  'Un tenant non ha una licenza. Ogni tenant deve averne una: entro pochi secondi riceve da solo una trial di 30 giorni, che puoi cambiare qui.':
    'One tenant has no licence. Every tenant must have one: within a few seconds it automatically receives a 30-day trial, which you can change here.',
  'Vuota = 30 giorni. Alla scadenza l’accesso al tenant si interrompe.':
    'Empty = 30 days. On expiry, access to the tenant is suspended.',
  'Vuota = senza scadenza.':
    'Empty = no expiry.',
  '{n} tenant non hanno una licenza. Ogni tenant deve averne una: entro pochi secondi ricevono da soli una trial di 30 giorni, che puoi cambiare qui.':
    '{n} tenants have no licence. Every tenant must have one: within a few seconds they automatically receive a 30-day trial, which you can change here.',
  'Nessuna VM in questo gruppo.':
    'No VM in this group.',
  'Nessuna macchina virtuale in CMDB.':
    'No virtual machine in the CMDB.',
  'Oggetto di backup':
    'Backup object',
  'Protette':
    'Protected',
  'VM protette da backup':
    'VMs protected by backup',
  'VM senza backup':
    'VMs without backup',
  '«VM protette da backup» confronta le macchine virtuali della CMDB con gli oggetti protetti letti dal backup (Veeam, Vinchin, Commvault, Rubrik) in tempo reale: un clic su una fetta elenca quali VM sono protette (con l’oggetto di backup che le copre) e quali no, ognuna con il collegamento alla scheda.':
    '«VMs protected by backup» compares the virtual machines in the CMDB with the protected objects read from the backup (Veeam, Vinchin, Commvault, Rubrik) in real time: a click on a slice lists which VMs are protected (with the backup object covering them) and which are not, each linked to its page.',
  'Add-on':
    'Add-ons',
  'CI':
    'CIs',
  'Funzioni che il piano scelto non comprende, concesse in più a questo cliente.':
    'Features the chosen plan does not include, granted in addition to this customer.',
  'Il numero di asset venduto al cliente: fissalo e resta così a prescindere dal piano. Non fissato = segue il listino del piano.':
    'The number of assets sold to the customer: set it and it stays regardless of the plan. Not set = follows the plan’s list.',
  'Le quote sono numeri interi non negativi (vuoto = illimitati).':
    'Quotas are non-negative integers (empty = unlimited).',
  'Nessun ordine registrato. Una licenza a pagamento si attiva con «Nuovo ordine» sulla riga del cliente.':
    'No order recorded. A paid licence is activated with «New order» on the customer’s row.',
  'Nessun ordine registrato: la licenza di questo cliente è una prova o è stata assegnata a mano.':
    'No order recorded: this customer’s licence is a trial or was assigned by hand.',
  'Nessun piano a pagamento nel listino.':
    'No paid plan in the catalogue.',
  'Nessuna modifica registrata.':
    'No change recorded.',
  'Nuovo ordine':
    'New order',
  'Ordini':
    'Orders',
  'Ordini ({n})':
    'Orders ({n})',
  'Ordini e storico':
    'Orders and history',
  'Quote':
    'Quotas',
  'Registra ciò che il cliente ha comprato e attiva la licenza a pagamento. L’ordine resta in archivio, legato al cliente; quello precedente viene segnato come sostituito. Add-on e quote scritti qui restano fissi a prescindere dal piano.':
    'Record what the customer bought and activate the paid licence. The order stays on file, tied to the customer; the previous one is marked as superseded. Add-ons and quotas written here stay fixed regardless of the plan.',
  'Registra l’ordine e attiva la licenza':
    'Record the order and activate the licence',
  'Registra una scheda ordine: piano, add-on, quote e validità così come venduti. Attiva la licenza a pagamento.':
    'Record an order form: plan, add-ons, quotas and validity as sold. Activates the paid licence.',
  'Registrato da':
    'Recorded by',
  'Registro delle modifiche':
    'Change log',
  'Registro…':
    'Recording…',
  'Riferimento cliente':
    'Customer reference',
  'Riferimento cliente: {rif}':
    'Customer reference: {rif}',
  'Scheda ordine — {cliente}':
    'Order form — {cliente}',
  'Ultimo giorno':
    'Last day',
  'numero d’ordine, contratto, offerta':
    'order number, contract, quote',
  'Validità':
    'Validity',
  'dal {data}':
    'from {data}',
  'sostituito':
    'superseded',
  'annullato':
    'cancelled',
  'ordine':
    'order',
  'modifica a mano':
    'manual change',
  'estensione della prova':
    'trial extension',
  'trial automatica':
    'automatic trial',
  'creazione del tenant':
    'tenant creation',
  'riapplica ai clienti':
    'reapply to customers',
  'revoca':
    'revocation',
  'di listino':
    'per list',
  'La prima estensione (7 giorni) si approva da sola; queste sono le successive, di 15 giorni ciascuna. Approvando, la scadenza si sposta di 15 giorni (da oggi se è già passata).':
    "The first extension (7 days) is approved automatically; these are the later ones, 15 days each. Approving moves the expiry by 15 days (from today if it has already passed).",
  'Ordini e storico — {cliente}':
    'Orders and history — {cliente}',
  'Scegli il cliente e compila la scheda: piano, add-on, quote e validità così come venduti. Registrarla attiva la licenza a pagamento e sostituisce l’ordine precedente, che resta in archivio.':
    'Choose the customer and fill in the form: plan, add-ons, quotas and validity as sold. Recording it activates the paid licence and supersedes the previous order, which stays on file.',
  'Scheda ordine che attiva la licenza a pagamento; ordini e storico per cliente':
    'Order form that activates the paid licence; orders and history per customer',
  'Aggiungi amministratore':
    'Add administrator',
  'Chiave «{nome}» generata. Copiala ora: non si rivede.':
    'Key «{nome}» generated. Copy it now: it will not be shown again.',
  'Chiavi API della piattaforma':
    'Platform API keys',
  'L’ho copiata':
    'I have copied it',
  'Nessuna chiave.':
    'No keys.',
  'Per collegare plugin e sistemi esterni (il plugin WordPress, un CRM) alle API di amministrazione: licenze, ordini, clienti. Chi presenta la chiave opera come amministratore di piattaforma, solo sulle API di amministrazione. Si manda come «Authorization: Bearer <chiave>».':
    'To connect plugins and external systems (the WordPress plugin, a CRM) to the administration APIs: licences, orders, customers. Whoever presents the key acts as a platform administrator, on the administration APIs only. Send it as «Authorization: Bearer <key>».',
  'Revocare la chiave «{nome}»? Da questo istante non apre più niente.':
    'Revoke key «{nome}»? From this moment it no longer opens anything.',
  'Scade':
    'Expires',
  'Togliere a {email} il ruolo di amministratore di piattaforma? L’utenza resta.':
    'Remove the platform administrator role from {email}? The account remains.',
  'Ultimo uso':
    'Last used',
  'Utenza esistente promossa ad amministratore di piattaforma.':
    'Existing account promoted to platform administrator.',
  'mai usata':
    'never used',
  'nome (es. plugin WordPress)':
    'name (e.g. WordPress plugin)',
  'nota (facoltativa)':
    'note (optional)',
  'password (vuota = invito via email)':
    'password (empty = email invitation)',
  'revocata':
    'revoked',
  'scade fra N giorni (vuoto = mai)':
    'expires in N days (empty = never)',
  'togli il ruolo':
    'remove the role',
  'Aggiungi un amministratore di piattaforma. Un’email nuova crea l’utenza: senza password parte l’invito via posta con il collegamento alla console di amministrazione; con la password scritta qui parte un benvenuto con l’indirizzo della console (la password comunicala di persona). Un’email esistente viene promossa. «Reinvia invito» rimanda il collegamento.':
    'Add a platform administrator. A new email creates the account: without a password an invitation goes out by email with the link to the administration console; with the password typed here a welcome goes out with the console address (share the password in person). An existing email is promoted. «Resend invitation» sends the link again.',
  'Amministratore creato: gli è partita un’email con l’indirizzo della console; la password comunicagliela di persona.':
    'Administrator created: an email with the console address went out; share the password in person.',
  'Amministratore creato: riceverà un invito via email con il collegamento alla console di amministrazione per scegliere la password.':
    'Administrator created: they will receive an email invitation with the link to the administration console to choose a password.',
  'Invito inviato di nuovo a {email}: collegamento alla console di amministrazione, valido per la durata degli inviti.':
    'Invitation sent again to {email}: link to the administration console, valid for the invitation period.',
  'Manda di nuovo l’email con il collegamento per impostare la password.':
    'Send the email with the password link again.',
  'reinvia invito':
    'resend invitation',
  'Email di benvenuto':
    'Welcome email',
  'Manda «Il tuo ambiente è pronto» agli amministratori del tenant: indirizzo, piano e date, primi passi, certificato di licenza allegato.':
    'Send «Your environment is ready» to the tenant administrators: address, plan and dates, first steps, licence certificate attached.',
  'Email di benvenuto inviata a: {chi}':
    'Welcome email sent to: {chi}',
  'Legge dal DNS SPF, DKIM e DMARC del dominio del mittente: sono ciò che decide se la posta finisce nella posta indesiderata.':
    'Reads SPF, DKIM and DMARC for the sender domain from DNS: they decide whether mail ends up in spam.',
  'Verifica recapito (SPF/DKIM/DMARC)':
    'Check deliverability (SPF/DKIM/DMARC)',
  'Recapito per {dominio}':
    'Deliverability for {dominio}',
  'da migliorare':
    'to improve',
  'Conta anche che il mittente della busta (envelope-from) sia sullo stesso dominio del mittente visibile: con il relay autenticato della piattaforma lo è; con mail() del server web non lo era (SPF none).':
    'The envelope sender (envelope-from) must also be on the same domain as the visible sender: with the platform\'s authenticated relay it is; with the web server\'s mail() it was not (SPF none).',
  'Banco di memoria':
    'Memory module',
  'Scheda di rete':
    'Network adapter',
  'Controller RAID':
    'RAID controller',
  'Nella scansione SNMP approfondita l’iDRAC descrive anche il server in cui sta, come un controller wireless descrive i suoi access point: modello, BIOS, sistema operativo, salute, e i componenti — processori, banchi di memoria, dischi, schede di rete, alimentatori, controller RAID — con i loro seriali, nella sezione Componenti della scheda del server. Il server nasce in CMDB anche se spento o senza agente, e quando poi lo si vede via SSH o da vCenter è lo stesso CI.':
    "In the deep SNMP scan the iDRAC also describes the server it sits in, as a wireless controller describes its access points: model, BIOS, operating system, health, and the components — processors, memory modules, disks, network adapters, power supplies, RAID controllers — with their serial numbers, in the Components section of the server's card. The server is born in the CMDB even if powered off or without an agent, and when it is later seen via SSH or from vCenter it is the same CI.",
  "L'indirizzo della console Vinchin (la stessa porta 443 del web): l'API sta sotto `/api/`. Senza schema si assume https.":
    'The address of the Vinchin console (the same port 443 as the web UI): the API lives under `/api/`. Without a scheme, https is assumed.',
  "Un'utenza della console Vinchin che **veda tutte le risorse** (Vinchin mostra a ogni utente solo ciò che gli è assegnato: un'utenza senza risorse legge zero job senza errori), meglio se di sola lettura. La sonda entra a ogni interrogazione e chiude la sessione alla fine; il primo accesso e il cambio password vanno fatti prima dalla console.":
    'A Vinchin console account that **sees all resources** (Vinchin shows each user only what is assigned to them: an account with no resources reads zero jobs without any error), preferably read-only. The probe signs in at every query and closes the session at the end; the first sign-in and the password change must be done from the console first.',
  'Chiave API (non verificata)':
    'API key (not verified)',
  'Una chiave di Sistema → Impostazioni di sistema → API Key Management, mandata al posto del token di sessione. Come Vinchin la accetti non è documentato: se la pagina Backup dà errore, usa utente e password.':
    'A key from System → System Settings → API Key Management, sent in place of the session token. How Vinchin accepts it is not documented: if the Backup page shows an error, use username and password.',
  'Per Vinchin crea una credenziale «Vinchin Backup & Recovery» con l’indirizzo della console e un’utenza che veda tutte le risorse (per esempio un amministratore in sola lettura): Vinchin mostra a ogni utente solo ciò che gli è assegnato, e un’utenza senza risorse legge zero job senza errori. Il primo accesso e il cambio password falli prima dalla console; dopo tre login sbagliati Vinchin chiede il captcha e il collegamento si ferma finché non entri a mano.':
    'For Vinchin create a «Vinchin Backup & Recovery» credential with the console address and an account that sees all resources (for example a read-only administrator): Vinchin shows each user only what is assigned to them, and an account with no resources reads zero jobs without any error. Do the first sign-in and the password change from the console first; after three wrong logins Vinchin asks for a captcha and the connection stops until you sign in by hand.',
  'Annullato':
    'Canceled',
  'Collo di bottiglia':
    'Bottleneck',
  'Dati dell’ultima esecuzione':
    'Data in the last run',
  'Durata media':
    'Average duration',
  'Elaborati':
    'Processed',
  'Il grafico compare con almeno due esecuzioni di cui il prodotto dà dati elaborati e durata.':
    'The chart appears with at least two runs for which the product reports processed data and duration.',
  'Il prodotto non ha esecuzioni recenti di questo job, oppure l’utenza non le vede.':
    'The product has no recent runs of this job, or the account cannot see them.',
  'Il prodotto non ha righe di log per questa esecuzione.':
    'The product has no log lines for this run.',
  'Leggo il log…':
    'Reading the log…',
  'Log':
    'Log',
  'Log non disponibile.':
    'Log not available.',
  'Non protette':
    'Unprotected',
  'Oggetti dell’esecuzione':
    'Objects in the run',
  'Riuscite':
    'Successful',
  'Trasferiti':
    'Transferred',
  'Tutte le VM sono protette':
    'All VMs are protected',
  'Una VM risulta protetta quando un oggetto di backup di qualunque fornitore porta il suo nome (intero o corto). Una VM con nomi diversi nel backup e nella CMDB appare qui anche se è protetta.':
    'A VM counts as protected when a backup object from any vendor carries its name (full or short). A VM with different names in the backup and in the CMDB appears here even if it is protected.',
  'VM nella CMDB':
    'VMs in the CMDB',
  'VM non protette':
    'Unprotected VMs',
  'Velocità media':
    'Average speed',
  'Velocità per esecuzione (GB/h)':
    'Speed per run (GB/h)',
  'max {v}':
    'max {v}',
  '{n} su {tot}':
    '{n} of {tot}',
  'Nella pagina Backup il nome di un job apre la sua pagina: le ultime esecuzioni con esito, durata, dati elaborati e trasferiti, velocità in GB/h e il grafico del suo andamento; aprendo un’esecuzione si vedono gli oggetti (su Veeam, VM per VM, con il collo di bottiglia) e il log. La sezione «VM non protette» elenca le macchine virtuali della CMDB che nessun backup copre, ognuna con il collegamento alla scheda.':
    'On the Backup page a job name opens its page: the latest runs with result, duration, processed and transferred data, speed in GB/h and a chart of its trend; opening a run shows its objects (on Veeam, VM by VM, with the bottleneck) and the log. The «Unprotected VMs» section lists the CMDB virtual machines that no backup covers, each linked to its page.',
  'Commvault':
    'Commvault',
  'Rubrik':
    'Rubrik',
  'Indirizzo del Web Server':
    'Web Server address',
  "L'indirizzo dell'API del Web Server o del Command Center. Con il solo host si assume `/commandcenter/api`; va bene anche `…/webconsole/api`. Senza schema si assume https.":
    'The API address of the Web Server or Command Center. With just the host, `/commandcenter/api` is assumed; `…/webconsole/api` works too. Without a scheme, https is assumed.',
  'https://commserve.example.com/commandcenter/api':
    'https://commserve.example.com/commandcenter/api',
  "Un'utenza Commvault che veda client, job e storage, meglio in sola lettura (ruolo View). Utente di dominio nella forma `DOMINIO\\utente`. Si entra a ogni interrogazione e si esce alla fine.":
    "A Commvault account that sees clients, jobs and storage, preferably read-only (View role). Domain users as `DOMAIN\\user`. It signs in at every query and signs out at the end.",
  "Per Rubrik Security Cloud l'indirizzo dell'account (`https://<account>.my.rubrik.com`), da interrogare dal Control Plane; per un cluster CDM l'indirizzo del cluster, da una sonda.":
    'For Rubrik Security Cloud the account address (`https://<account>.my.rubrik.com`), queried from the Control Plane; for a CDM cluster the cluster address, from a probe.',
  'https://account.my.rubrik.com':
    'https://account.my.rubrik.com',
  'Rubrik Security Cloud — service account':
    'Rubrik Security Cloud — service account',
  'Un service account creato in RSC (Impostazioni → Utenti e accessi → Service account) con un ruolo in sola lettura: client ID e secret si leggono dal file JSON scaricato alla creazione.':
    'A service account created in RSC (Settings → Users and access → Service accounts) with a read-only role: client ID and secret are in the JSON file downloaded at creation.',
  'Cluster CDM — service account':
    'CDM cluster — service account',
  'ID del service account':
    'Service account ID',
  'Secret':
    'Secret',
  'Un service account del cluster (Impostazioni → Utenti → Service account) con un ruolo in sola lettura.':
    'A cluster service account (Settings → Users → Service accounts) with a read-only role.',
  'Cluster CDM — utente e password':
    'CDM cluster — username and password',
  "Un'utenza locale o di dominio del cluster, meglio in sola lettura.":
    'A local or domain account of the cluster, preferably read-only.',
  'Per Commvault crea una credenziale «Commvault» con l’indirizzo del Web Server o del Command Center e un’utenza in sola lettura; si interroga da una sonda o, se il Web Server è pubblicato, dal Control Plane. Per Rubrik crea una credenziale «Rubrik»: con Rubrik Security Cloud usa un service account di RSC e scegli di parlare dal Control Plane; con un cluster CDM in sede usa un service account del cluster (o utente e password) e una sonda. In Commvault ogni riga dei job è un subclient; in Rubrik è un oggetto protetto, con il suo SLA Domain.':
    'For Commvault create a «Commvault» credential with the Web Server or Command Center address and a read-only account; it is queried from a probe or, if the Web Server is published, from the Control Plane. For Rubrik create a «Rubrik» credential: with Rubrik Security Cloud use an RSC service account and choose to talk from the Control Plane; with an on-premises CDM cluster use a cluster service account (or username and password) and a probe. In Commvault each job row is a subclient; in Rubrik it is a protected object, with its SLA Domain.',
  'Dettagli tecnici':
    'Technical details',
  '{n} di {tot} {nome}':
    '{n} of {tot} {nome}',
  '{tot} {nome}':
    '{tot} {nome}',
  'oggetti protetti':
    'protected objects',
  'Aggiungi un firewall «Palo Alto (firewall)» o «Fortinet FortiGate (firewall)» qui sotto, in «Connessioni».':
    'Add a «Palo Alto (firewall)» or «Fortinet FortiGate (firewall)» firewall below, in «Connections».',
  'Sistemi interrogati in tempo reale dal modulo. Le credenziali di accesso sono cifrate e non sono più visualizzabili dopo il salvataggio.': 'Systems the module queries in real time. The access credentials are encrypted and can no longer be viewed after saving.',
  'Le credenziali dei sistemi letti in tempo reale (backup, IPAM, DNS, Wazuh, firewall, ntopng) si gestiscono nel loro modulo, sezione «Connessioni».':
    'Credentials of systems read in real time (backup, IPAM, DNS, Wazuh, firewall, ntopng) are managed in their module, «Connections» section.',
  'Catalogo centralizzato delle sorgenti dati, suddivise per rete, virtualizzazione, storage, sicurezza, backup e DNS. Per ogni sorgente mostra lo stato operativo (connessa, non configurata o in errore) e le credenziali associate.':
    'Centralised catalogue of data sources for network, virtualization, storage, security, backup and DNS. For each source it shows the operational status — connected, not configured or in error — and the associated credentials.',
  'Nuova connessione':
    'New connection',
  'Dati elaborati':
    'Processed data',
  'Durata media per job':
    'Average duration per job',
  'Esito dell’ultima esecuzione':
    'Result of the last run',
  'Il sistema di backup non ha esecuzioni negli ultimi giorni, oppure l’utenza non le vede.':
    'The backup system has no runs in the last days, or the account cannot see them.',
  'Nessun dato da confrontare.':
    'No data to compare.',
  'Nessun job con un esito.':
    'No job with a result.',
  'Nessuna VM da confrontare.':
    'No VMs to compare.',
  'Nessuna esecuzione recente':
    'No recent runs',
  'Nessuna repository con la capacità nota.':
    'No repository with a known capacity.',
  'Occupazione delle repository':
    'Repository usage',
  'Prestazioni':
    'Performance',
  'Prestazioni dei job':
    'Job performance',
  'Ultima durata':
    'Last duration',
  'VM della CMDB protette':
    'CMDB VMs protected',
  'Velocità media per job (GB/h)':
    'Average speed per job (GB/h)',
  'job':
    'jobs',
  '{n} job senza un esito (mai eseguiti, in corso o senza storico).':
    '{n} jobs without a result (never run, running or without history).',
  'Il cruscotto del Backup mostra, oltre ai numeri, l’esito dell’ultima esecuzione di ogni job, la ruota delle VM della CMDB protette e l’occupazione di ogni repository. La sezione «Prestazioni» riassume le esecuzioni recenti di tutti i fornitori job per job: quante, quante riuscite, durata media e ultima, velocità media in GB/h e dati elaborati, con il confronto fra job su velocità e durata; il nome di un job apre la sua pagina.':
    'Besides the numbers, the Backup dashboard shows the result of each job’s last run, the wheel of protected CMDB VMs and the usage of each repository. The «Performance» section summarizes the recent runs of all vendors job by job: how many, how many succeeded, average and last duration, average speed in GB/h and processed data, with a comparison between jobs on speed and duration; a job name opens its page.',
  'Prodotti supportati: ServiceNow, Matrix42 ESM, BMC Remedy, ManageEngine ServiceDesk Plus e Jira Service Management Assets (la CMDB di Jira, Cloud o Data Center): per Jira Cloud servono l’email dell’utenza e un API token, per Data Center un Personal Access Token; i tipi di oggetto sono quelli degli schemi di Assets.':
    'Supported products: ServiceNow, Matrix42 ESM, BMC Remedy, ManageEngine ServiceDesk Plus and Jira Service Management Assets (Jira’s CMDB, Cloud or Data Center): Jira Cloud needs the account email and an API token, Data Center a Personal Access Token; the object types are those of the Assets schemas.',
  'A) Jira Cloud':
    'A) Jira Cloud',
  'B) Jira Data Center':
    'B) Jira Data Center',
  'C) Dove trovare le cose in Assets':
    'C) Where to find things in Assets',
  'Come collego Jira, e dove trovo le cose?':
    'How do I connect Jira, and where do I find things?',
  'Il connettore scrive nella CMDB di Jira, cioè Jira Service Management Assets, non nei ticket. Servono Jira Service Management con Assets attivo e un’utenza che veda gli schemi da sincronizzare.':
    'The connector writes into Jira’s CMDB, that is Jira Service Management Assets, not into tickets. It needs Jira Service Management with Assets enabled and an account that sees the schemas to synchronize.',
  'Indirizzo: il sito, per esempio https://azienda.atlassian.net, senza percorsi. Installazione: cloud.':
    'Address: the site, for example https://azienda.atlassian.net, without paths. Installation: cloud.',
  'Utenza: meglio una dedicata, con licenza agente di Jira Service Management. In Assets apri lo schema → Impostazioni dello schema → Ruoli e dalle il ruolo «Object Schema Users» per leggere, o «Object Schema Managers» per scrivere.':
    'Account: preferably a dedicated one, with a Jira Service Management agent license. In Assets open the schema → Schema settings → Roles and give it the «Object Schema Users» role to read, or «Object Schema Managers» to write.',
  'API token: entra con quell’utenza su id.atlassian.com/manage-profile/security/api-tokens → «Crea token API», dagli un nome (per esempio CENSO) e una scadenza, e copialo subito: non si rivede. Nel connettore vanno l’email dell’utenza e il token.':
    'API token: sign in with that account at id.atlassian.com/manage-profile/security/api-tokens → «Create API token», give it a name (for example CENSO) and an expiry, and copy it right away: it is not shown again. The connector needs the account email and the token.',
  'Workspace: di norma lascialo vuoto, CENSO lo ricava dal sito. Se il sito ne ha più d’uno, lo trovi aprendo nel browser https://azienda.atlassian.net/rest/servicedeskapi/assets/workspace (campo workspaceId).':
    'Workspace: normally leave it empty, CENSO derives it from the site. If the site has more than one, open https://azienda.atlassian.net/rest/servicedeskapi/assets/workspace in the browser (workspaceId field).',
  'Indirizzo: quello di Jira, per esempio https://jira.azienda.local. Installazione: datacenter.':
    'Address: the Jira one, for example https://jira.azienda.local. Installation: datacenter.',
  'Utenza: con un ruolo sugli schemi di Assets, come per Cloud («Object Schema Users» o «Object Schema Managers»).':
    'Account: with a role on the Assets schemas, as for Cloud («Object Schema Users» or «Object Schema Managers»).',
  'Personal Access Token: con quell’utenza, avatar in alto a destra → Profilo → Personal Access Tokens → «Create token»; imposta una scadenza e copialo subito. Serve Jira 8.14 o successivo.':
    'Personal Access Token: with that account, avatar at the top right → Profile → Personal Access Tokens → «Create token»; set an expiry and copy it right away. It needs Jira 8.14 or later.',
  'Se Jira non è raggiungibile dal Control Plane, scegli qui sopra di passare da una sonda che lo raggiunge.':
    'If Jira cannot be reached from the Control Plane, choose above to go through a probe that reaches it.',
  'Schemi e tipi di oggetto: menu Assets → Schemi di oggetti; dentro uno schema, a sinistra, l’albero dei tipi (Server, Computer…). Nella scansione del connettore compaiono come «Schema / Tipo».':
    'Schemas and object types: Assets menu → Object schemas; inside a schema, on the left, the tree of types (Server, Computer…). In the connector scan they appear as «Schema / Type».',
  'L’ID di un tipo: aprendo il tipo, nell’indirizzo della pagina compare typeId=23. È la chiave con cui il connettore lo indica.':
    'The ID of a type: opening the type, the page address shows typeId=23. It is the key the connector uses for it.',
  'Gli attributi: nel tipo di oggetto, scheda Attributi. Sono i campi del mapping; «Name» è l’etichetta dell’oggetto, «Key» (per esempio CMDB-12) la assegna Jira e non si scrive.':
    'Attributes: in the object type, Attributes tab. They are the mapping fields; «Name» is the object label, «Key» (for example CMDB-12) is assigned by Jira and is not written.',
  'Chiave di corrispondenza: scegli un attributo univoco e sempre valorizzato, come il numero di serie o l’hostname. Se lo stesso valore compare in due oggetti, il connettore si ferma invece di aggiornarne uno a caso.':
    'Match key: choose a unique, always filled attribute, such as the serial number or the hostname. If the same value appears in two objects, the connector stops instead of updating one at random.',
  'Prima verifica: «Prova il collegamento» mostra l’utenza e quanti schemi vede. Zero schemi vuol dire che all’utenza manca un ruolo in Assets.':
    'First check: «Test the connection» shows the account and how many schemas it sees. Zero schemas means the account lacks a role in Assets.',
  'Per collegare Jira, nel modulo del connettore scegli Jira e apri il riquadro «Come collego Jira, e dove trovo le cose?»: spiega passo passo utenza, ruoli sugli schemi di Assets, API token o Personal Access Token, e dove trovare workspace, schemi, tipi e attributi.':
    'To connect Jira, choose Jira in the connector form and open the «How do I connect Jira, and where do I find things?» panel: it explains step by step the account, the roles on the Assets schemas, the API token or Personal Access Token, and where to find workspace, schemas, types and attributes.',
  'Token con o senza ambiti: vanno bene entrambi. Il più semplice è «Crea token API» (senza ambiti). Se usi «Crea token API con ambiti», scegli l’app Jira e dagli almeno read:jira-user e gli ambiti di Assets read:cmdb-schema:jira, read:cmdb-type:jira, read:cmdb-attribute:jira, read:cmdb-object:jira (più write:cmdb-object:jira per scrivere): CENSO lo usa passando da api.atlassian.com.':
    'Token with or without scopes: both work. The simplest is «Create API token» (without scopes). If you use «Create API token with scopes», choose the Jira app and give it at least read:jira-user and the Assets scopes read:cmdb-schema:jira, read:cmdb-type:jira, read:cmdb-attribute:jira, read:cmdb-object:jira (plus write:cmdb-object:jira to write): CENSO uses it through api.atlassian.com.',
  'Attenzione a non confondere il token personale, che comincia con ATATT e si crea su id.atlassian.com, con le API key dell’organizzazione, che cominciano con ATCTT e si creano in admin.atlassian.com: queste non valgono per Jira né per Assets, e CENSO lo dice subito.':
    'Be careful not to confuse the personal token, which starts with ATATT and is created at id.atlassian.com, with the organization API keys, which start with ATCTT and are created at admin.atlassian.com: those do not work for Jira or Assets, and CENSO says so right away.',
  'I tipi di oggetto sono ciò che si crea e si aggiorna su questa istanza: scegline uno nella mappatura.':
    'Object types are what is created and updated on this instance: choose one in the mapping.',
  'Su {prodotto} il contenitore su cui si scrive e il tipo dell’oggetto che nasce sono due cose diverse: scegli qui il tipo. Il pulsante «Tipi di …» nella scansione qui sopra elenca quelli di questa istanza.':
    'On {prodotto} the container being written to and the type of the object being created are two different things: choose the type here. The «Types of …» button in the scan above lists those of this instance.',
  'Schema gestito da CENSO (automatico): il tipo di oggetto lo decide la classe di ogni CI':
    'Schema managed by CENSO (automatic): the object type is decided by each CI’s class',
  'Un tipo di oggetto a scelta (mappatura personalizzata)':
    'An object type of your choice (custom mapping)',
  'Schema gestito da CENSO (la scelta automatica della mappatura): indica nel connettore il nome dello schema. Se è uno schema che esiste già, CENSO riusa i suoi tipi (Server, Virtual Machines, Network Devices…) e i suoi attributi con nomi equivalenti, e crea solo quelli che mancano; se non esiste, lo crea. Creare uno schema richiede un amministratore di Jira o di Assets; se lo crei tu a mano, a CENSO basta il ruolo «Object Schema Managers» su quello schema.':
    'Schema managed by CENSO (the automatic choice in the mapping): enter the schema name in the connector. If the schema already exists, CENSO reuses its types (Servers, Virtual Machines, Network Devices…) and its attributes with equivalent names, and creates only those that are missing; if it does not exist, it creates it. Creating a schema requires a Jira or Assets administrator; if you create it by hand, CENSO only needs the «Object Schema Managers» role on that schema.',
  'Esecuzione in corso: i contatori si aggiornano da soli, e alla fine se ne apre il dettaglio.':
    'Run in progress: the counters update by themselves, and its detail opens at the end.',
  'CMDB per classe (automatico): ogni CI nella tabella standard della sua classe':
    'CMDB by class (automatic): each CI in the standard table of its class',
  'Per ServiceNow la destinazione di partenza della mappatura è «CMDB per classe»: ogni CI va nella tabella standard della sua classe (cmdb_ci_server, cmdb_ci_ip_switch, cmdb_ci_ip_router…), una VM come server virtuale, e la corrispondenza si fa su correlation_id con l’identificativo permanente di CENSO, così niente duplicati. Resta possibile scegliere una tabella a mano. Per avere il link alla scheda in CENSO crea sulla tabella cmdb_ci un campo u_censo_url di tipo URL.':
    'For ServiceNow the default mapping destination is «CMDB by class»: each CI goes into the standard table of its class (cmdb_ci_server, cmdb_ci_ip_switch, cmdb_ci_ip_router…), a VM as a virtual server, and matching uses correlation_id with CENSO’s permanent identifier, so no duplicates. You can still choose a table by hand. To have the link to the CENSO page, create a u_censo_url field of type URL on the cmdb_ci table.',
  'Per ManageEngine ServiceDesk Plus la destinazione di partenza è «Moduli per classe»: i computer (server, VM, workstation) nel modulo Workstations, gli apparati negli Asset, con il prodotto della loro classe (cercato per produttore e modello, o creato). Prima serve creare una volta, per Workstations e per Asset, un campo aggiuntivo di testo «CENSO ID»: è la chiave che evita i duplicati; un campo «CENSO URL» facoltativo riceve il link alla scheda in CENSO.':
    'For ManageEngine ServiceDesk Plus the default destination is «Modules by class»: computers (servers, VMs, workstations) in the Workstations module, devices in Assets, with the product of their class (looked up by manufacturer and model, or created). First create once, for Workstations and for Assets, an additional text field «CENSO ID»: it is the key that prevents duplicates; an optional «CENSO URL» field receives the link to the CENSO page.',
  'Moduli per classe (automatico): i computer in Workstations, gli apparati negli Asset':
    'Modules by class (automatic): computers in Workstations, devices in Assets',
  'Come preparo AWS per CENSO, compreso il software delle EC2?':
    'How do I prepare AWS for CENSO, including the software on EC2?',
  'CENSO legge AWS con permessi di sola lettura, meglio assumendo un ruolo dedicato: EC2, VPC, volumi, gruppi di sicurezza, S3, Lambda, RDS, bilanciatori e i costi del mese. Il software installato sulle EC2 arriva da AWS Systems Manager Inventory: CENSO interroga AWS, non le macchine.':
    'CENSO reads AWS with read-only permissions, preferably by assuming a dedicated role: EC2, VPCs, volumes, security groups, S3, Lambda, RDS, load balancers and the month’s costs. The software installed on EC2 comes from AWS Systems Manager Inventory: CENSO queries AWS, not the machines.',
  'A) La policy del ruolo':
    'A) The role policy',
  'Crea un ruolo IAM per CENSO con la policy di sola lettura della guida dei permessi AWS: comprende ssm:DescribeInstanceInformation, ssm:ListInventoryEntries e ssm:DescribeInstancePatchStates, le sole tre azioni di Systems Manager, tutte in lettura (niente ssm:SendCommand, nessuna esecuzione sulle istanze).':
    'Create an IAM role for CENSO with the read-only policy from the AWS permissions guide: it includes ssm:DescribeInstanceInformation, ssm:ListInventoryEntries and ssm:DescribeInstancePatchStates, the only three Systems Manager actions, all read-only (no ssm:SendCommand, no execution on the instances).',
  'Nella fiducia del ruolo indica l’account o l’utenza che lo assume e un External ID; qui metti l’ARN del ruolo e lo stesso External ID.':
    'In the role’s trust policy set the account or user that assumes it and an External ID; here enter the role ARN and the same External ID.',
  'B) Il software delle EC2 (Systems Manager Inventory)':
    'B) The software on EC2 (Systems Manager Inventory)',
  'Sulle EC2 serve l’agente SSM (già presente sulle immagini Amazon Linux, Ubuntu e Windows di AWS) e un profilo d’istanza con la policy AmazonSSMManagedInstanceCore.':
    'The EC2 instances need the SSM agent (already present on AWS’s Amazon Linux, Ubuntu and Windows images) and an instance profile with the AmazonSSMManagedInstanceCore policy.',
  'Attiva la raccolta: console AWS → Systems Manager → Inventory → «Setup Inventory» (oppure Quick Setup → Host Management), per tutte le istanze o per tag. Di default raccoglie ogni 30 minuti.':
    'Turn on collection: AWS console → Systems Manager → Inventory → «Setup Inventory» (or Quick Setup → Host Management), for all instances or by tag. By default it collects every 30 minutes.',
  'Al job successivo, sulla scheda di ogni EC2 gestita compaiono il software installato (fonte «ssm»), il sistema operativo reale, gli aggiornamenti Windows e i servizi in esecuzione. Il rapporto del job elenca le EC2 non gestite da SSM o gestite senza Inventory: sono i buchi da chiudere.':
    'At the next job, the page of each managed EC2 shows the installed software (source «ssm»), the real operating system, Windows updates and running services. The job report lists the EC2 instances not managed by SSM or managed without Inventory: those are the gaps to close.',
  'Vinchin Backup & Recovery':
    'Vinchin Backup & Recovery',
  'Risposta parziale: {motivi}':
    'Partial answer: {motivi}',
  'https://vinchin.example.com':
    'https://vinchin.example.com',
  // Import avanzato dei connettori cloud
  'Cloud (AWS, Azure, Google Cloud, Oracle Cloud): con la casella «Attiva import avanzato» nella credenziale, CENSO legge dal servizio di gestione del fornitore il software installato sulle macchine, il sistema operativo reale e gli aggiornamenti, senza collegarsi alle macchine: AWS Systems Manager Inventory (fonte «ssm»), Azure Change Tracking e Update Manager («azure_inventory»), Google Cloud VM Manager («gcp_osconfig»), Oracle OS Management Hub («oci_osmh»). «Cosa fa?» accanto alla casella spiega servizi e permessi da concedere; il rapporto del job elenca le macchine rimaste senza dati e perché.':
    "Cloud (AWS, Azure, Google Cloud, Oracle Cloud): with the «Enable advanced import» box in the credential, CENSO reads the software installed on the machines, the real operating system and the updates from the provider's management service, without connecting to the machines: AWS Systems Manager Inventory (source «ssm»), Azure Change Tracking and Update Manager («azure_inventory»), Google Cloud VM Manager («gcp_osconfig»), Oracle OS Management Hub («oci_osmh»). «What does it do?» next to the box explains the services and the permissions to grant; the job report lists the machines left without data and why.",
  'Nella credenziale AWS accendi «Attiva import avanzato»: senza, CENSO non interroga Systems Manager e il rapporto del job lo ricorda.':
    'In the AWS credential turn on «Enable advanced import»: without it, CENSO does not query Systems Manager and the job report says so.',
  '00000000-0000-0000-0000-000000000000':
    '00000000-0000-0000-0000-000000000000',
  'Attiva import avanzato':
    'Enable advanced import',
  'Cosa fa?':
    'What does it do?',
  'ID del workspace di Log Analytics':
    'Log Analytics workspace ID',
  'Software installato, sistema operativo reale e aggiornamenti delle macchine, letti dal servizio di gestione del fornitore e non dalle macchine. Servono permessi in più: vedi «Cosa fa?».':
    "Installed software, real operating system and updates of the machines, read from the provider's management service and not from the machines. Extra permissions are needed: see «What does it do?».",
  "Solo per l'import avanzato. Vuoto: si interroga tutta la sottoscrizione.":
    'Only for the advanced import. Empty: the whole subscription is queried.',
  "Legge AWS Systems Manager Inventory: per ogni EC2 gestita da SSM il software installato (fonte «ssm»), il sistema operativo vero (per esempio Windows Server 2022 Datacenter invece di «windows»), l'hostname, gli aggiornamenti Windows installati e i servizi in esecuzione.":
    'Reads AWS Systems Manager Inventory: for each EC2 instance managed by SSM, the installed software (source «ssm»), the real operating system (for example Windows Server 2022 Datacenter instead of «windows»), the hostname, the installed Windows updates and the running services.',
  "Serve che sulle EC2 giri l'agente SSM (già presente nelle AMI Amazon) e che esista un'associazione di Inventory (Systems Manager → Inventory → «Setup Inventory»). Alla policy di CENSO vanno aggiunte tre sole letture: ssm:DescribeInstanceInformation, ssm:ListInventoryEntries e ssm:DescribeInstancePatchStates (quest'ultima per gli aggiornamenti mancanti, se Patch Manager fa le scansioni).":
    "The SSM agent must run on the EC2 instances (already present in Amazon AMIs) and an Inventory association must exist (Systems Manager → Inventory → «Setup Inventory»). Only three reads need to be added to the CENSO policy: ssm:DescribeInstanceInformation, ssm:ListInventoryEntries and ssm:DescribeInstancePatchStates (the last one for missing updates, if Patch Manager runs scans).",
  "Legge due servizi di Azure. Da Azure Resource Graph: il sistema operativo visto dall'agente della VM e, se Azure Update Manager valuta le patch, quanti aggiornamenti mancano (critici e di sicurezza a parte) e se serve un riavvio. Da Log Analytics (Change Tracking and Inventory): il software installato (fonte «azure_inventory») e i servizi in esecuzione.":
    'Reads two Azure services. From Azure Resource Graph: the operating system seen by the VM agent and, if Azure Update Manager assesses patches, how many updates are missing (critical and security counted separately) and whether a reboot is needed. From Log Analytics (Change Tracking and Inventory): the installed software (source «azure_inventory») and the running services.',
  "Serve il ruolo Reader sulla sottoscrizione (che già basta all'inventario) e, per il software, Change Tracking and Inventory attivo sulle VM e il ruolo Log Analytics Reader sul workspace. Se indichi l'ID del workspace, CENSO interroga quello; se lo lasci vuoto, tutta la sottoscrizione.":
    'It needs the Reader role on the subscription (already enough for the inventory) and, for software, Change Tracking and Inventory enabled on the VMs and the Log Analytics Reader role on the workspace. If you enter the workspace ID, CENSO queries that one; if you leave it empty, the whole subscription.',
  'Legge Google Cloud VM Manager (OS Config): per ogni VM il software installato (fonte «gcp_osconfig»), il sistema operativo vero, il kernel, gli aggiornamenti Windows installati e quanti pacchetti hanno un aggiornamento disponibile.':
    'Reads Google Cloud VM Manager (OS Config): for each VM the installed software (source «gcp_osconfig»), the real operating system, the kernel, the installed Windows updates and how many packages have an update available.',
  "Servono l'API OS Config attiva nel progetto, VM Manager abilitato (l'agente è già nelle immagini Google recenti) e il ruolo roles/osconfig.inventoryViewer al service account, compreso in roles/viewer. Attenzione: l'API di OS Config accetta solo lo scope cloud-platform, quindi accendendo l'import avanzato il token lo porta, come con lo scope pieno: la sola lettura la garantisce il ruolo IAM, che va tenuto a roles/viewer.":
    'It needs the OS Config API enabled in the project, VM Manager enabled (the agent is already in recent Google images) and the roles/osconfig.inventoryViewer role for the service account, included in roles/viewer. Note: the OS Config API only accepts the cloud-platform scope, so turning on the advanced import makes the token carry it, as with the full scope: read-only access is then guaranteed by the IAM role, which must stay roles/viewer.',
  'Legge Oracle OS Management Hub: per ogni istanza registrata i pacchetti installati (fonte «oci_osmh»), il sistema operativo e il kernel, gli aggiornamenti disponibili (di sicurezza a parte), se serve un riavvio e, su Windows, gli aggiornamenti installati.':
    'Reads Oracle OS Management Hub: for each registered instance the installed packages (source «oci_osmh»), the operating system and kernel, the available updates (security counted separately), whether a reboot is needed and, on Windows, the installed updates.',
  "Servono il plugin OS Management Hub dell'Oracle Cloud Agent attivo sulle istanze e, per il gruppo dell'utente di CENSO, la policy «Allow group <gruppo> to read osmh-family in compartment <compartimento>». Il job fa qualche chiamata in più per istanza: su tenancy molto grandi dura di più.":
    'It needs the OS Management Hub plugin of the Oracle Cloud Agent enabled on the instances and, for the group of the CENSO user, the policy «Allow group <group> to read osmh-family in compartment <compartment>». The job makes a few more calls per instance: on very large tenancies it takes longer.',
  "Solo letture: CENSO non esegue comandi sulle macchine, non installa agenti e non cambia configurazioni. Se un permesso manca o il servizio non è attivo, l'inventario di base arriva lo stesso e il rapporto del job dice che cosa manca e perché, macchina per macchina.":
    'Reads only: CENSO does not run commands on the machines, does not install agents and does not change configurations. If a permission is missing or the service is not active, the basic inventory still arrives and the job report says what is missing and why, machine by machine.',
  'Spenta, il rapporto del job lo ricorda con una riga: così chi si chiede perché non vede il software delle macchine cloud trova la risposta.':
    'When it is off, the job report says so in one line: whoever wonders why they cannot see the software of the cloud machines finds the answer there.',
  // Ruoli personalizzati
  'A chi serve e perché':
    'Who needs it and why',
  'Anteprima del menu':
    'Menu preview',
  'Assegnato a {n} utenti: assegna loro un altro ruolo prima.':
    'Assigned to {n} users: assign them another role first.',
  'Colore':
    'Colour',
  'Con questi permessi il menu è vuoto: chi ha il ruolo non vedrà nulla.':
    'With these permissions the menu is empty: whoever has the role will see nothing.',
  'Crea ruolo':
    'Create role',
  'Disattivato per il tuo ruolo':
    'Disabled for your role',
  'Duplica':
    'Duplicate',
  'Eliminare il ruolo «{nome}»?':
    'Delete the role «{nome}»?',
  'Help desk':
    'Help desk',
  'I ruoli predefiniti costituiscono la base per configurare autorizzazioni specifiche.':
    'The templates on the left are a starting point to fine-tune.',
  'Il livello delle sezioni che arriveranno con i prossimi aggiornamenti.':
    'The level of the sections that will arrive with future updates.',
  'Il ruolo che ti è stato assegnato non dà accesso a questa sezione. Se ti serve, chiedi a chi amministra gli utenti del tenant.':
    "The role assigned to you does not give access to this section. If you need it, ask whoever manages the tenant's users.",
  'Il ruolo di partenza è in sola lettura':
    'The base role is read-only',
  'La voce non compare nel menu e le sue API sono chiuse.':
    'The entry does not appear in the menu and its APIs are closed.',
  'La voce si vede in grigio con il lucchetto, ma non si apre.':
    'The entry is shown greyed out with a padlock, but does not open.',
  'Le modifiche ai permessi valgono entro pochi secondi per chi ha il ruolo; un cambio del ruolo di partenza vale dal prossimo accesso.':
    'Permission changes apply within a few seconds to whoever has the role; a change of base role applies from the next sign-in.',
  'Lettura':
    'Read',
  'Modelli':
    'Templates',
  'Moduli futuri':
    'Future modules',
  'Modulo disattivato per il tuo ruolo':
    'Module disabled for your role',
  'Modulo non disponibile':
    'Module not available',
  'Nascosto':
    'Hidden',
  'Nome del ruolo':
    'Role name',
  'Nuovo ruolo':
    'New role',
  'Permessi per modulo':
    'Permissions per module',
  'Ruoli del prodotto':
    'Product roles',
  'Ruoli e permessi':
    'Roles and permissions',
  'Ruoli personalizzati':
    'Custom roles',
  'Ruolo di partenza':
    'Base role',
  'Ruolo personalizzato':
    'Custom role',
  'Seleziona un ruolo o creane uno personalizzato':
    'Choose a role or create one',
  'Scrittura':
    'Write',
  'Si consulta tutto, senza poter modificare.':
    'Everything can be viewed, nothing can be changed.',
  'Sola lettura: con il tuo ruolo puoi consultare questo modulo ma non modificarlo.':
    'Read-only: with your role you can view this module but not change it.',
  'Team rete':
    'Network team',
  'Team sicurezza':
    'Security team',
  'Tutto quello che il ruolo di partenza consente.':
    'Everything the base role allows.',
  'Tutto: {livello}':
    'All: {livello}',
  'Un ruolo personalizzato deriva da un ruolo predefinito e definisce, per ciascuna voce di menu, il relativo livello di accesso. Le autorizzazioni possono essere esclusivamente limitate rispetto al ruolo predefinito e non possono essere estese.':
    'A custom role starts from a product role and decides, menu entry by menu entry, whether the module is hidden, disabled, read or write. It can only take away: it never gives more than the base role.',
  'Vai a una sezione disponibile':
    'Go to an available section',
  'misto':
    'mixed',
  '{nome} (copia)':
    '{nome} (copy)',
  '{n} utenti':
    '{n} users',
  'Ruoli personalizzati: in Utenti → «Ruoli e permessi» crei un ruolo che parte da uno dei tre e decide, voce per voce del menu, se il modulo è nascosto, disattivato (in grigio con il lucchetto), in lettura o in scrittura; a destra l’anteprima mostra il menu come lo vedrà chi ha quel ruolo. Un ruolo può solo togliere: non dà mai più del ruolo di partenza, e le API dei moduli chiusi rispondono comunque «non consentito». Si assegna dal selettore del ruolo nell’elenco degli utenti; non si può togliere all’ultimo amministratore la gestione degli utenti.':
    'Custom roles: in Users → «Roles and permissions» you create a role that starts from one of the three and decides, menu entry by menu entry, whether the module is hidden, disabled (greyed out with a padlock), read or write; on the right the preview shows the menu as whoever has that role will see it. A role can only take away: it never gives more than the base role, and the APIs of closed modules answer «not allowed» anyway. It is assigned from the role selector in the user list; the last administrator cannot be deprived of user management.',
  'Torna alla porta':
    'Back to the port',
  nessuno: 'none',
  // Diagnosi dei connettori cloud
  'Cosa fare:':
    'What to do:',
  'Diagnosi {fornitore}':
    '{fornitore} diagnosis',
  '{n} a posto':
    '{n} fine',
  '{n} da sistemare':
    '{n} to fix',
  // Cloud Costs
  'Aggiornato: {quando}':
    'Updated: {quando}',
  'Ancora nessun costo':
    'No costs yet',
  'Andamento giornaliero':
    'Daily trend',
  'Costi Cloud':
    'Cloud Costs',
  'Costi effettivi come li riporta il fornitore; i giorni più recenti possono ancora cambiare, e il job successivo li corregge.':
    'Actual costs as reported by the provider; the most recent days may still change, and the next job corrects them.',
  'Costo nel periodo':
    'Cost in the period',
  'Gli account usano più valute: gli importi sono in {valuta}; le altre si scelgono dal filtro «Valuta».':
    'The accounts use more than one currency: amounts are in {valuta}; pick the others from the «Currency» filter.',
  'I costi arrivano con i job di discovery cloud. Su AWS servono il permesso ce:GetCostAndUsage e Cost Explorer abilitato sull’account; su Azure il ruolo Cost Management Reader sulla sottoscrizione. La diagnosi nel rapporto del job dice cosa manca.':
    'Costs arrive with the cloud discovery jobs. On AWS they need the ce:GetCostAndUsage permission and Cost Explorer enabled on the account; on Azure the Cost Management Reader role on the subscription. The diagnosis in the job report says what is missing.',
  'Mese in corso':
    'Current month',
  'Mese scorso':
    'Last month',
  'Mese scorso e mese in corso':
    'Last month and current month',
  'Nessun costo nel periodo.':
    'No costs in the period.',
  'Per account':
    'By account',
  'Per regione':
    'By region',
  'Per servizio':
    'By service',
  'Quanto costano gli account cloud, giorno per giorno: per servizio, regione e account':
    'What the cloud accounts cost, day by day: by service, region and account',
  'Quota':
    'Share',
  'Stima di fine mese':
    'Month-end estimate',
  'Totale del periodo':
    'Period total',
  'Tutti gli account':
    'All accounts',
  'Valuta':
    'Currency',
  'i costi degli account cloud (AWS, Azure) giorno per giorno, per servizio, regione e account: mese in corso, confronto con il mese scorso allo stesso giorno e stima lineare di fine mese. Arrivano con i job di discovery cloud, che rileggono ogni volta dal primo del mese scorso così le correzioni del provider entrano da sole. Servono ce:GetCostAndUsage con Cost Explorer abilitato su AWS, il ruolo Cost Management Reader su Azure.':
    'the costs of the cloud accounts (AWS, Azure) day by day, by service, region and account: current month, comparison with last month at the same day and a linear month-end estimate. They arrive with the cloud discovery jobs, which re-read from the first of last month every time so provider corrections come in by themselves. They need ce:GetCostAndUsage with Cost Explorer enabled on AWS, the Cost Management Reader role on Azure.',
  'massimo {importo}':
    'maximum {importo}',
  'media dei giorni trascorsi × giorni del mese: non una previsione':
    'average of the days so far × days in the month: not a forecast',
  'rispetto al mese scorso allo stesso giorno ({importo})':
    'compared with last month at the same day ({importo})',
  'senza regione':
    'no region',
  // Chiave dei segreti del tenant
  'Chiave dei segreti':
    'Secrets key',
  'Conferma la rotazione':
    'Confirm the rotation',
  'La rotazione precedente è ancora in corso: aspetta che tutti i segreti siano ricifrati.':
    'The previous rotation is still in progress: wait until all secrets have been re-encrypted.',
  'Le credenziali di discovery e dei connettori del tenant sono cifrate con una chiave privata dedicata. Il rinnovo della chiave genera una nuova chiave di cifratura, utilizzata immediatamente per le nuove credenziali. Le credenziali già archiviate vengono ricifrate automaticamente in background, senza interruzioni del servizio o perdita di accesso.':
    "The discovery and connector credentials of this tenant are encrypted with a key of your own, different from other customers'. Rotating it generates a new key: new secrets use it immediately, existing ones are re-encrypted in the background without interrupting anything.",
  'Ricifratura in corso: {fatti} di {totale} segreti sulla chiave nuova':
    'Re-encryption in progress: {fatti} of {totale} secrets on the new key',
  'Segreti cifrati':
    'Encrypted secrets',
  'Storia delle chiavi ({n})':
    'Key history ({n})',
  'Ultima rotazione':
    'Last rotation',
  'Versione attiva':
    'Active version',
  'Versione {n}':
    'Version {n}',
  'Viene creata la versione {n} della chiave. Nessun servizio si ferma e nessuna credenziale va reinserita; la chiave precedente resta finché serve a leggere i segreti non ancora ricifrati. L’operazione resta nell’audit.':
    'Version {n} of the key will be created. No service stops and no credential needs to be re-entered; the previous key stays as long as it is needed to read the secrets not yet re-encrypted. The operation is recorded in the audit log.',
  'Chi amministra il tenant trova qui anche la «Chiave dei segreti»: la chiave, solo vostra, che cifra credenziali e connettori. Si ruota con un clic, senza fermare nulla: i segreti esistenti vengono ricifrati in background e l’avanzamento si vede nella stessa scheda.':
    'Tenant administrators also find the «Secrets key» here: the key, yours alone, that encrypts credentials and connectors. It is rotated with one click, without stopping anything: existing secrets are re-encrypted in the background and the progress shows in the same card.',
  // Scrittura verso DNS e IPAM
  'Allinea nome':
    'Align name',
  'Allineo…':
    'Aligning…',
  'Consenti la scrittura':
    'Allow writing',
  'Documenta':
    'Document',
  'Documenta tutti in phpIPAM ({n})':
    'Document all in phpIPAM ({n})',
  'Documentati in phpIPAM: {n} indirizzi.':
    'Documented in phpIPAM: {n} addresses.',
  'Documento…':
    'Documenting…',
  'Modifica record':
    'Edit record',
  'Nome di {ip} in phpIPAM: «{prima}» → «{dopo}».':
    'Name of {ip} in phpIPAM: «{prima}» → «{dopo}».',
  'Nuovo record in {zona}':
    'New record in {zona}',
  'Scrivo sul DNS…':
    'Writing to DNS…',
  'Togli da phpIPAM':
    'Remove from phpIPAM',
  'Togliere {ip} ({nome}) da phpIPAM?':
    'Remove {ip} ({nome}) from phpIPAM?',
  'Tolgo…':
    'Removing…',
  'Tolto da phpIPAM: {ip}.':
    'Removed from phpIPAM: {ip}.',
  'www, oppure @ per la zona':
    'www, or @ for the zone',
  'Scrittura verso DNS e IPAM: si accende nella credenziale di ciascuna connessione, con «Consenti la scrittura» (spenta di default), dalla sezione «Connessioni» della pagina IPAM e, per il DNS, di Sorgenti dati. Accesa, sul DNS si aggiungono, modificano e tolgono record dalla scheda della zona; in IPAM, dal confronto, si documentano in phpIPAM gli indirizzi trovati (con il nome del CI), si allineano i nomi e si tolgono gli indirizzi non più usati. Ogni scrittura resta nell’audit, e la credenziale deve avere i permessi di scrittura sul sistema.':
    'Writing to DNS and IPAM: it is turned on in the credential of each connection, with «Allow writing» (off by default), from the «Connections» section of the IPAM page and, for the DNS, of Data sources. When on, DNS records can be added, changed and removed from the zone card; in IPAM, from the comparison, you can document in phpIPAM the addresses found (with the CI name), align the names and remove addresses no longer used. Every write stays in the audit log, and the credential must have write permissions on the system.',
  // Tabella a pagine e job
  'Nella finestra ci sono più di {n} job: sono mostrati i {n} più recenti. Restringi la finestra per vedere il resto.':
    'There are more than {n} jobs in the window: the {n} most recent are shown. Narrow the window to see the rest.',
  'Pagina precedente':
    'Previous page',
  'Pagina successiva':
    'Next page',
  'Pagina {n} di {tot}':
    'Page {n} of {tot}',
  'Prima pagina':
    'First page',
  'Ultima pagina':
    'Last page',
  '{da}–{a} di {tot}':
    '{da}–{a} of {tot}',
  // Storia della configurazione nel CI
  'Cosa cambia più spesso':
    'What changes most often',
  'Intervallo medio fra due modifiche':
    'Average interval between changes',
  'Le prime parole delle righe cambiate (interface, ip route, snmp-server…): dicono di cosa si parla.':
    'The first words of the changed lines (interface, ip route, snmp-server…): they say what it is about.',
  'Letta via':
    'Read via',
  'Modifiche negli ultimi 30 giorni':
    'Changes in the last 30 days',
  'Modifiche per mese':
    'Changes per month',
  'Nessuna modifica negli ultimi 12 mesi.':
    'No changes in the last 12 months.',
  'Righe aggiunte e tolte a ogni modifica':
    'Lines added and removed at each change',
  'Righe cambiate':
    'Changed lines',
  'Righe della configurazione attuale':
    'Lines in the current configuration',
  'Salvata il':
    'Saved on',
  'Statistiche e versioni sulle ultime {n} di {tot}.':
    'Statistics and versions over the last {n} of {tot}.',
  'Una sola versione: ancora nessuna modifica da confrontare.':
    'Only one version: no changes to compare yet.',
  'primo salvataggio':
    'first save',
  '{n} giorni':
    '{n} days',
  // Guida: aggiornamenti del 26/09
  'In alto una riga di categorie (Rete, Cloud, Virtualizzazione, Storage…) filtra il catalogo dei tipi di scansione: scegline una per vedere solo i suoi job e scorrere meno; «Tutte» le rimette tutte.': 'At the top, a row of categories (Network, Cloud, Virtualization, Storage…) filters the catalogue of scan types: pick one to see only its jobs and scroll less; «Tutte» brings them all back.',
  'In Job scansioni, «Job recenti» è una tabella con un filtro per ogni colonna (tipo, stato, ambito, esito, date): si sceglie la finestra (24 ore, 7 giorni, 30 giorni) e quante righe per pagina. Da ogni riga si apre il rapporto, si ripianifica la stessa scansione o la si ritenta. La spunta «Mostra solo job scansioni», attiva di default, nasconde le consegne degli agent, l’autoinventario della sonda e i job di servizio.':
    'In Scan jobs, «Recent jobs» is a table with a filter on every column (type, status, scope, outcome, dates): you choose the window (24 hours, 7 days, 30 days) and how many rows per page. From each row you can open the report, schedule the same scan again or retry it. The «Show scan jobs only» tick, on by default, hides agent deliveries, the probe self-inventory and service jobs.',
  'Sulla scheda di un apparato, la sezione Backup mostra la sua storia senza cambiare pagina: quante versioni, quante modifiche nell’ultimo mese, righe aggiunte e tolte, ogni quanto cambia, i grafici delle modifiche per mese e per modifica, che cosa cambia più spesso, e ogni versione con il confronto e il testo intero da copiare o scaricare.':
    'On a device card, the Backup section shows its history without leaving the page: how many versions, how many changes in the last month, lines added and removed, how often it changes, the charts of changes per month and per change, what changes most often, and every version with the comparison and the full text to copy or download.',
  'Diagnosi AWS: in cima al rapporto di ogni job AWS un riquadro elenca i controlli (credenziali, account, EC2, ogni servizio, Systems Manager, Cost Explorer) con ciò che è a posto e, per ciò che non lo è, cosa fare: il permesso esatto da aggiungere, il servizio da abilitare, le EC2 da registrare in Systems Manager.':
    'AWS diagnosis: at the top of every AWS job report a panel lists the checks (credentials, account, EC2, each service, Systems Manager, Cost Explorer) with what is fine and, for what is not, what to do: the exact permission to add, the service to enable, the EC2 instances to register in Systems Manager.',
  'Un ruolo personalizzato vale anche fuori dalle pagine del modulo: le sezioni della scheda di un CI (sicurezza, backup, IPAM, routing, firewall, Wazuh…), i widget della dashboard e i distintivi in alto seguono i permessi del modulo da cui vengono i dati — nascosto o disattivato non si vedono, in lettura si vedono senza i comandi.':
    'A custom role also applies outside the module pages: the sections of a CI card (security, backup, IPAM, routing, firewall, Wazuh…), the dashboard widgets and the badges at the top follow the permissions of the module their data comes from — hidden or disabled they are not shown, read-only they are shown without the commands.',
  // Scrittura nella credenziale
  'Accesa, dalla pagina DNS si aggiungono, modificano e tolgono record (A, AAAA, CNAME, MX, NS, PTR, SRV, TXT) nelle zone di questo server, con il valore controllato per tipo prima di mandarlo.':
    'When on, from the DNS page you can add, change and remove records (A, AAAA, CNAME, MX, NS, PTR, SRV, TXT) in the zones of this server, with the value checked by type before it is sent.',
  'Questa connessione è in sola lettura: la scrittura si accende nella sua credenziale, in «Connessioni» → «Consenti la scrittura».':
    'This connection is read-only: writing is turned on in its credential, under «Connections» → «Allow writing».',
  "Servono i permessi di scrittura sul server: per PowerDNS la chiave API vale già anche per scrivere; per Microsoft DNS l'utente deve poter modificare le zone, per esempio un membro di «DnsAdmins», e la sonda deve essere aggiornata.":
    'Write permissions on the server are needed: for PowerDNS the API key already allows writing; for Microsoft DNS the user must be able to change the zones, for example a member of «DnsAdmins», and the probe must be updated.',
  'Con autorizzazione disabilitata, CENSO opera in sola lettura; se abilitata, consente agli operatori del modulo di eseguire anche operazioni di scrittura: vedi «Cosa fa?».':
    'When off, CENSO only reads. When on, whoever works on the module can also write to this system: see «What does it do?».',
  // Categorie delle credenziali
  'Decide chi la vede: nei ruoli personalizzati ogni categoria si concede a parte.':
    'Decides who sees it: in custom roles each category is granted separately.',
  'Le credenziali, categoria per categoria: chi vede quali.':
    'Credentials, category by category: who sees which.',
  'Le credenziali di discovery hanno una categoria (Rete, Server, Virtualizzazione, Cloud, Storage, Database) che si sceglie creandole: nei ruoli personalizzati ogni categoria si concede a parte sotto «Credenziali», così il team di rete vede solo le credenziali di rete e quello cloud solo le sue. Le credenziali delle connessioni (DNS, IPAM, backup…) seguono invece il loro modulo.':
    'Discovery credentials have a category (Network, Server, Virtualisation, Cloud, Storage, Database) chosen when they are created: in custom roles each category is granted separately under «Credentials», so the network team sees only network credentials and the cloud team only its own. Connection credentials (DNS, IPAM, backup…) follow their module instead.',
  // Tag
  'Aggiungi un tag':
    'Add a tag',
  'Allora metti il tag':
    'Then add the tag',
  'Applica ora':
    'Apply now',
  'Applicata: {matched} oggetti con il tag, {added} aggiunti, {removed} tolti.':
    'Applied: {matched} objects with the tag, {added} added, {removed} removed.',
  'Assegnatario':
    'Assignee',
  'Attributo…':
    'Attribute…',
  'Catalogo dei tag':
    'Tag catalogue',
  'Crea tag':
    'Create tag',
  'Crea un tag qui, oppure scrivilo direttamente su un oggetto («ambiente:produzione») o con una regola.':
    'Create a tag here, or write it directly on an object («environment:production») or with a rule.',
  'Dove è usato':
    'Where it is used',
  'Eliminare il tag «{tag}»? Viene tolto da {n} oggetti.':
    'Delete the tag «{tag}»? It is removed from {n} objects.',
  'Eliminare la regola «{nome}»? I tag che aveva messo vengono tolti.':
    'Delete the rule «{nome}»? The tags it added are removed.',
  'Classificazione di CI, asset, servizi, contratti, credenziali, pool, piani di discovery e sonde tramite attributi strutturati (chiave:valore), assegnati manualmente o automaticamente mediante regole. Consentono di organizzare, filtrare e correlare gli oggetti in base a caratteristiche comuni.':
    'Classification of CIs, assets, services, contracts, credentials, pools, discovery plans and probes through structured attributes (key:value), assigned by hand or automatically through rules. They let you organise, filter and correlate objects by common traits.',
  'Filtra per tag (chiave:valore)':
    'Filter by tag (key:value)',
  'Il valore può prendere un dato dell’oggetto: {vendor}, {ci_class}, {attr:tag_Environment} (i tag cloud). Se il dato manca, il tag non si mette.':
    'The value can take a piece of data from the object: {vendor}, {ci_class}, {attr:tag_Environment} (cloud tags). If the data is missing, the tag is not added.',
  'Messo da una regola: se lo togli, la regola lo rimette finché l’oggetto la soddisfa.':
    'Added by a rule: if you remove it, the rule adds it back as long as the object matches it.',
  'Metti il tag':
    'Add the tag',
  'Modifica i tag':
    'Edit tags',
  'Nessun tag':
    'No tags',
  'Nome della regola':
    'Rule name',
  'Per esempio: i CI nella subnet 10.1.0.0/16 → sede:Milano; classe firewall → criticità:alta; il tag cloud Environment → ambiente:{attr:tag_Environment}.':
    'For example: CIs in the subnet 10.1.0.0/16 → site:Milan; firewall class → criticality:high; the cloud tag Environment → environment:{attr:tag_Environment}.',
  'Piani':
    'Plans',
  'Qualunque indirizzo IP':
    'Any IP address',
  'Quando (tutte le condizioni)':
    'When (all conditions)',
  'Rinominare o ricolorare un tag vale ovunque è assegnato.':
    'Renaming or recolouring a tag applies everywhere it is assigned.',
  'Sede di Milano':
    'Milan site',
  'Seleziona la pagina':
    'Select the page',
  'Seleziona {nome}':
    'Select {nome}',
  'Si applica a':
    'Applies to',
  'Tag':
    'Tags',
  'Tag da mettere o togliere':
    'Tag to add or remove',
  'Togli il tag':
    'Remove the tag',
  'Ultima applicazione: {quando}':
    'Last applied: {quando}',
  'Una regola mette il tag sugli oggetti che soddisfano tutte le condizioni: subito, e sui CI a ogni raccolta. Chi smette di soddisfarla perde il tag della regola; i tag messi a mano restano.':
    "A rule adds the tag to the objects that match all the conditions: immediately, and on CIs at every collection. Objects that stop matching lose the rule's tag; tags added by hand stay.",
  'chiave (es. ambiente)':
    'key (e.g. environment)',
  'chiave:valore':
    'key:value',
  'chiave:valore, poi Invio':
    'key:value, then Enter',
  'espressione regolare':
    'regular expression',
  'inizia con':
    'starts with',
  'nome attributo':
    'attribute name',
  'tag':
    'tags',
  'valore (facoltativo)':
    'value (optional)',
  '{n} CI selezionati':
    '{n} CIs selected',
  '{n} oggetti riceverebbero il tag':
    '{n} objects would receive the tag',
  'è nella subnet':
    'is in the subnet',
  'è valorizzato':
    'has a value',
  'i tag chiave:valore (ambiente:produzione, sede:Milano) su CI, asset, servizi, contratti, credenziali, pool, piani e sonde: si mettono dalla scheda o dalla colonna «Tag» degli elenchi — sui CI anche in blocco sui selezionati — e si filtra per tag. Le regole li mettono da sole, anche sui CI che arrivano con le scansioni: per esempio i CI nella 10.1.0.0/16 → sede:Milano, o il tag cloud Environment → ambiente.':
    'key:value tags (environment:production, site:Milan) on CIs, assets, services, contracts, credentials, pools, plans and probes: they are added from the object card or from the «Tag» column of the lists — on CIs also in bulk on the selected ones — and you can filter by tag. Rules add them automatically, also on CIs that arrive with scans: for example CIs in 10.1.0.0/16 → site:Milan, or the cloud tag Environment → environment.',
  'A) Il profilo in sola lettura':
    'A) The read-only profile',
  'Alta affidabilità':
    'High availability',
  'B) L’utente API e la chiave':
    'B) The API user and key',
  'Bloccata per il limite di connessioni per indirizzo IP.':
    'Blocked by the per-IP connection limit.',
  'C) I log che CENSO legge':
    'C) The logs CENSO reads',
  'CENSO interroga il FortiGate in tempo reale con la REST API di FortiOS, attraverso una sonda che raggiunge la sua interfaccia di gestione. Serve un amministratore «REST API Admin» in sola lettura: CENSO non cambia nulla sul firewall.':
    'CENSO queries the FortiGate in real time with the FortiOS REST API, through a probe that reaches its management interface. It needs a read-only «REST API Admin»: CENSO changes nothing on the firewall.',
  "CENSO legge i log attraverso il FortiGate stesso (la REST API «/api/v2/log/<dispositivo>/…»): anche con FortiAnalyzer non serve una credenziale a parte, è il firewall che interroga l'analyzer.":
    'CENSO reads the logs through the FortiGate itself (the REST API «/api/v2/log/<device>/…»): even with FortiAnalyzer no separate credential is needed, the firewall queries the analyzer.',
  'CPU':
    'CPU',
  'Calcolata sulle ultime {n} righe di log del firewall.':
    'Computed on the firewall’s last {n} log lines.',
  'Chiave API (REST API Admin)':
    'API key (REST API Admin)',
  'Chiusura regolare: la sessione è terminata normalmente.':
    'Regular close: the session ended normally.',
  'Come preparo il FortiGate per CENSO (utente API e log)?':
    'How do I prepare the FortiGate for CENSO (API user and logs)?',
  'D) La sonda':
    'D) The probe',
  "Da dove leggere traffico e minacce. Vuoto: CENSO lo ricava (FortiAnalyzer se attivo, altrimenti il disco se c'è, altrimenti la memoria).":
    'Where to read traffic and threats from. Empty: CENSO works it out (FortiAnalyzer if enabled, otherwise the disk if present, otherwise memory).',
  'Dalla riga di comando è lo stesso: «config system api-user», poi «edit censoapi», «set accprofile censo-readonly», «config trusthost» con l’IP della sonda, e infine «execute api-user generate-key censoapi».':
    'From the command line it is the same: «config system api-user», then «edit censoapi», «set accprofile censo-readonly», «config trusthost» with the probe’s IP, and finally «execute api-user generate-key censoapi».',
  'Disattiva «PKI Group» e, se non serve, «CORS». Conferma: FortiOS mostra la chiave una volta sola. Copiala qui, nel campo «Chiave API».':
    'Turn off «PKI Group» and, if not needed, «CORS». Confirm: FortiOS shows the key only once. Copy it here, into the «API key» field.',
  'Dove sono i log':
    'Where the logs are',
  'Fortinet FortiGate (firewall)':
    'Fortinet FortiGate (firewall)',
  "Il FortiGate conserva i log dove glielo si dice (Log & Report → Log Settings): in memoria (pochi, si perdono al riavvio), sul disco locale se il modello ce l'ha, su un FortiAnalyzer o su FortiCloud.":
    'The FortiGate stores logs where it is told to (Log & Report → Log Settings): in memory (few, lost on reboot), on the local disk if the model has one, on a FortiAnalyzer or on FortiCloud.',
  "Il VDOM da interrogare, se il firewall ne ha più d'uno. Vuoto: quello predefinito dell'amministratore API.":
    'The VDOM to query, if the firewall has more than one. Empty: the API administrator’s default one.',
  'Il certificato della console di solito è autofirmato: incolla la CA o l’impronta SHA-256 nei campi TLS, invece di disattivare la verifica.':
    'The console certificate is usually self-signed: paste the CA or the SHA-256 fingerprint in the TLS fields instead of turning verification off.',
  'In «Trusted Hosts» metti l’IP della sonda che interroga il firewall (o la sua rete, per esempio 10.0.12.5/32): la chiave vale solo da lì.':
    'In «Trusted Hosts» put the IP of the probe that queries the firewall (or its network, for example 10.0.12.5/32): the key only works from there.',
  'Indirizzo della gestione del FortiGate: la REST API risponde sulla porta HTTPS della console (443, o quella impostata in «admin-sport»). Senza schema si assume «https».':
    'Address of the FortiGate management: the REST API answers on the console’s HTTPS port (443, or the one set in «admin-sport»). Without a scheme «https» is assumed.',
  "La chiave di un «REST API Admin» con un profilo in **sola lettura** e, come Trusted Host, l'IP della sonda: si genera in System → Administrators → Create New → REST API Admin. FortiOS la mostra una volta sola.":
    'The key of a «REST API Admin» with a **read-only** profile and the probe’s IP as Trusted Host: generate it in System → Administrators → Create New → REST API Admin. FortiOS shows it only once.',
  'Lascia vuoto per la scelta automatica; indica «memory», «disk», «fortianalyzer» o «forticloud» per forzarla.':
    'Leave empty for automatic choice; enter «memory», «disk», «fortianalyzer» or «forticloud» to force it.',
  'Log & Report → Log Settings: dove tieni i log (memoria, disco, FortiAnalyzer o FortiCloud). CENSO lo ricava da solo; se vuoi forzarlo, compila «Dove sono i log». In memoria i log sono pochi e si perdono al riavvio: per una storia utile usa disco o FortiAnalyzer.':
    'Log & Report → Log Settings: where you keep the logs (memory, disk, FortiAnalyzer or FortiCloud). CENSO works it out on its own; to force it, fill in «Where the logs are». In memory logs are few and lost on reboot: for a useful history use disk or FortiAnalyzer.',
  'Nelle policy di cui vuoi vedere il traffico imposta «Log Allowed Traffic» su «All Sessions» (oppure «Security Events» per i soli eventi UTM). Il traffico negato si registra con «Log Violation Traffic», attivo di default sulla policy implicita.':
    'On the policies whose traffic you want to see, set «Log Allowed Traffic» to «All Sessions» (or «Security Events» for UTM events only). Denied traffic is logged with «Log Violation Traffic», on by default on the implicit policy.',
  'Nessuna voce in «Read/Write»: a CENSO basta leggere.':
    'No entry in «Read/Write»: CENSO only needs to read.',
  'Per minacce e web filter servono i profili di sicurezza (IPS, antivirus, web filter, application control) applicati alle policy: senza, quelle viste restano vuote.':
    'Threats and web filter need security profiles (IPS, antivirus, web filter, application control) applied to the policies: without them those views stay empty.',
  'Permessa dalla policy; la sessione era ancora aperta al momento del log.':
    'Allowed by the policy; the session was still open when logged.',
  'Sulla scheda della sonda che raggiunge la gestione del firewall abilita la capability «Firewall» e scegli quella sonda qui sotto: le richieste escono solo da lì.':
    'On the card of the probe that reaches the firewall management, enable the «Firewall» capability and choose that probe below: requests only leave from there.',
  'System → Admin Profiles → Create New: un nome (per esempio «censo-readonly») e «Read» su tutte le voci; in particolare «Log & Report» (per traffico, minacce e web filter), «System» (stato, sessioni, amministratori), «Network» (rotte) e «VPN».':
    'System → Admin Profiles → Create New: a name (for example «censo-readonly») and «Read» on every entry; in particular «Log & Report» (for traffic, threats and web filter), «System» (status, sessions, administrators), «Network» (routes) and «VPN».',
  'System → Administrators → Create New → REST API Admin: un nome (per esempio «censoapi»), il profilo del passo A e, con più VDOM, il VDOM da leggere.':
    'System → Administrators → Create New → REST API Admin: a name (for example «censoapi»), the profile from step A and, with several VDOMs, the VDOM to read.',
  'VDOM':
    'VDOM',
  'memory | disk | fortianalyzer | forticloud':
    'memory | disk | fortianalyzer | forticloud',
  'nessuna (standalone)':
    'none (standalone)',
  'root':
    'root',
  '{n} peer':
    '{n} peers',
  'Azzera avvistamenti':
    'Reset sightings',
  'Azzero…':
    'Resetting…',
  'Azzerare tutti gli avvistamenti, anche quelli ignorati e censiti con le loro note? Le subnet sorvegliate restano; il prossimo giro ripropone ciò che è ancora non censito.':
    'Reset all sightings, including ignored and inventoried ones with their notes? The watched subnets stay; the next round brings back whatever is still not inventoried.',
  'Autoinventario sonda':
    'Probe self-inventory',
  'Mostra solo job scansioni':
    'Show scan jobs only',
  '{n} job che non sono scansioni sono nascosti: togli la spunta «Mostra solo job scansioni» per vederli.':
    '{n} jobs that are not scans are hidden: untick «Show scan jobs only» to see them.',
  'Gestisce licenza, sicurezza dell’accesso e configurazione del tenant':
    'License, security of your access and tenant settings',
  'Annulla dismissione':
    'Undo retirement',
  'Applica classe':
    'Apply class',
  'Automatica (dalle regole)':
    'Automatic (from the rules)',
  'Cambia classe…':
    'Change class…',
  'Classe da assegnare':
    'Class to assign',
  'Dichiarare dismessi {n} CI? Lo stato resiste alle scansioni. Motivo (facoltativo):':
    'Declare {n} CIs retired? The state survives scans. Reason (optional):',
  'Togliere {n} CI dalla CMDB?\n\nSe questi apparati esistono ancora in rete, la prossima scansione li riporta come oggetti nuovi, senza la loro storia: per macchine dismesse usa «Dichiara dismesso». Spariscono interfacce, software, relazioni e storico.':
    'Remove {n} CIs from the CMDB?\n\nIf these devices still exist on the network, the next scan brings them back as new objects, without their history: for retired machines use «Declare retired». Interfaces, software, relations and history are removed.',
  '{n} CI aggiornati.':
    '{n} CIs updated.',
  'Cosa dice phpIPAM':
    'What phpIPAM says',
  'Di chi è questo indirizzo':
    'Who owns this address',
  'Indirizzo dichiarato a mano sulla scheda del proprietario.':
    'Address declared by hand on the owner’s card.',
  'Indirizzo trovato dalla scansione fra quelli dell’apparato: chi lo raggiunge o ci fa traffico viene attribuito al proprietario.':
    'Address found by the scan among the device’s own: whoever reaches it or sends traffic to it is attributed to the owner.',
  'Legato dal {da}, ultima conferma {quando}':
    'Linked since {da}, last confirmed {quando}',
  'Nessun CI espone più questo indirizzo: il legame è stato tolto o l’oggetto che lo portava non c’è più.':
    'No CI exposes this address any more: the link was removed or the object that carried it is gone.',
  'Nome dal DNS inverso':
    'Name from reverse DNS',
  'Proprietario in IPAM':
    'Owner in IPAM',
  'gateway VLAN 31, VIP del bilanciatore…':
    'VLAN 31 gateway, load balancer VIP…',
  'non indicata':
    'not given',
  'phpIPAM non documenta questo indirizzo: è in uso ma non registrato.':
    'phpIPAM does not document this address: it is in use but not registered.',
  'phpIPAM non interrogabile.':
    'phpIPAM cannot be queried.',
  'Network Security (ntopng)':
    'Network Security (ntopng)',
  'nome.dominio':
    'name.domain',
  'Assorbito':
    'Absorbed',
  'CI che risultano lo stesso oggetto: per ogni gruppo, quello che resta e quelli che verrebbero assorbiti, con la prova (le chiavi in comune). Niente cambia finché non premi «Unisci». Togli la spunta ai gruppi che non ti convincono.':
    'CIs that turn out to be the same object: for each group, the one that stays and those that would be absorbed, with the evidence (the keys in common). Nothing changes until you press «Merge». Untick the groups you are not sure about.',
  'Cerco i doppioni…':
    'Looking for duplicates…',
  'Doppioni':
    'Duplicates',
  'Host visto dallo sweep su un indirizzo aggiuntivo già in CMDB.':
    'Host seen by the sweep on an additional address already in the CMDB.',
  'Nessun doppione':
    'No duplicates',
  'Ogni oggetto compare una volta sola.':
    'Every object appears only once.',
  'Resta':
    'Stays',
  'Trova doppioni':
    'Find duplicates',
  'Unire {g} gruppi? {n} CI verranno assorbiti nel CI che resta di ciascun gruppo; i loro identificativi continueranno a portare lì.':
    'Merge {g} groups? {n} CIs will be absorbed into the CI that stays in each group; their identifiers will keep leading there.',
  'Unisci {n} gruppi':
    'Merge {n} groups',
  'Unisco…':
    'Merging…',
  'Uniti {g} gruppi, {n} CI assorbiti.':
    'Merged {g} groups, {n} CIs absorbed.',
  'visto {quando}':
    'seen {quando}',
  'Condividono i MAC virtuali del cluster ma hanno seriali diversi: sono due apparati in HA, non doppioni, e non vengono uniti.':
    'They share the cluster’s virtual MACs but have different serials: they are two devices in HA, not duplicates, and are not merged.',
  'Coppie in alta affidabilità ({n})':
    'High-availability pairs ({n})',
  'Registra come coppie HA':
    'Register as HA pairs',
  'Registrate {n} coppie in alta affidabilità.':
    'Registered {n} high-availability pairs.',
  'in alta affidabilità con':
    'in high availability with',
  'Colonne a sinistra':
    'Columns to the left',
  'Altre colonne a destra':
    'More columns to the right',
  'Attributi della classe':
    'Class attributes',
  'Colonne':
    'Columns',
  'Colonne mostrate':
    'Shown columns',
  'Colonne in più':
    'Extra columns',
  'Ripristina le colonne predefinite':
    'Restore default columns',
  'Versione del sistema':
    'System version',
  'In HA con':
    'In HA with',
  'Coppia in alta affidabilità: i due apparati condividono i MAC virtuali del cluster.':
    'High-availability pair: the two devices share the cluster’s virtual MACs.',
  'Flussi ricavati dalla sonda osservando il traffico su una porta mirror, non ricevuti da un apparato.':
    'Flows derived by the probe from traffic on a mirror port, not received from a device.',
  'Interfaccia in cattura':
    'Capture interface',
  "L'elenco è completo: gli apparati che hanno esportato almeno un flusso e le sonde che catturano dal filo (protocollo «Sonda»).":
    'The list is complete: the devices that exported at least one flow and the probes capturing from the wire (protocol «Probe»).',
  'Pacchetti buttati dal kernel perché la sonda non teneva il passo: i volumi sono incompleti.':
    'Packets dropped by the kernel because the probe could not keep up: volumes are incomplete.',
  'Sorgenti di flussi ({n})':
    'Flow sources ({n})',
  "non si sente da oltre un'ora":
    'silent for over an hour',
  '{n} scartati':
    '{n} dropped',
  'tra {n}s':
    'in {n}s',
  'tra {n} min':
    'in {n} min',
  'tra {n} h':
    'in {n} h',
  'tra {n} g':
    'in {n} d',
  'interno':
    'internal',
  'origine':
    'source',
  'Altri indirizzi del cluster':
    'Other cluster addresses',
  'Cosa gira sul cluster e su quale nodo adesso: il nodo proprietario cambia a ogni failover.':
    'What runs on the cluster and on which node right now: the owner node changes at every failover.',
  'Dominio':
    'Domain',
  'I nodi compaiono quando l’inventario di almeno un membro (agente o scansione WinRM) arriva al Control Plane.':
    'Nodes appear when the inventory of at least one member (agent or WinRM scan) reaches the Control Plane.',
  'Il nodo non è ancora in CMDB: installa l’agente o scansionalo via WinRM.':
    'The node is not in the CMDB yet: install the agent or scan it via WinRM.',
  'Livello funzionale':
    'Functional level',
  'Nessun nodo noto':
    'No known nodes',
  'Nodi':
    'Nodes',
  'Nodi ({n})':
    'Nodes ({n})',
  'Nodi non attivi: {nodi}. I ruoli girano sugli altri nodi, ma il cluster ha meno margine per un altro guasto.':
    'Nodes not active: {nodi}. Roles run on the other nodes, but the cluster has less headroom for another failure.',
  'Nodo':
    'Node',
  'Nodo attivo':
    'Active node',
  'Ruoli ({n})':
    'Roles ({n})',
  'Ruoli ospitati':
    'Hosted roles',
  'Ultimo inventario':
    'Last inventory',
  'VIP':
    'VIP',
  'VIP del cluster non associati a un ruolo nell’ultimo inventario (dagli inventari precedenti al dettaglio dei ruoli).':
    'Cluster VIPs not tied to a role in the last inventory (from inventories older than role details).',
  'non in CMDB':
    'not in CMDB',
  '{su} attivi su {tot}':
    '{su} active out of {tot}',
  'Si installa dalla sonda «{sonda}» ({indirizzo}), in HTTPS con il certificato della sonda verificato: la macchina non deve raggiungere il Control Plane.':
    'Installs from probe «{sonda}» ({indirizzo}), over HTTPS with the probe’s certificate verified: the machine does not need to reach the Control Plane.',
  'Apri PowerShell come amministratore e incolla le due righe: la prima scarica l’installer in un file ({file}) con curl.exe, che verifica il certificato del server per impronta; la seconda lo esegue. Prima che parta puoi aprire il file e leggerlo: è un normale file su disco, non codice che gira al volo. curl.exe c’è da Windows 10 e da Windows Server 2019; per Windows Server 2016 usa la variante qui sopra.':
    'Open PowerShell as administrator and paste the two lines: the first downloads the installer to a file ({file}) with curl.exe, which verifies the server certificate by fingerprint; the second runs it. Before it starts you can open the file and read it: it is a normal file on disk, not code running on the fly. curl.exe ships with Windows 10 and Windows Server 2019; for Windows Server 2016 use the variant above.',
  'Windows Server 2016 o senza curl.exe':
    'Windows Server 2016 or without curl.exe',
  'Stesso risultato senza curl.exe: il certificato si verifica in PowerShell. Incolla tutte le righe insieme.':
    'Same result without curl.exe: the certificate is verified in PowerShell. Paste all the lines together.',
  'Al riavvio successivo (o con gpupdate /force e un riavvio) ogni macchina installa l’agente come SYSTEM, registra l’attività «CENSO inventory» e fa la prima consegna.':
    'At the next restart (or with gpupdate /force and a restart) each machine installs the agent as SYSTEM, registers the «CENSO inventory» task and makes its first delivery.',
  'Come si distribuisce il pacchetto?':
    'How do I deploy the package?',
  'Con Ansible: un task «unarchive» dell’archivio seguito da un task «command: sh /tmp/censo-inventory/install.sh» con become: true.':
    'With Ansible: an «unarchive» task for the archive followed by a «command: sh /tmp/censo-inventory/install.sh» task with become: true.',
  'Copia l’archivio sulle macchine ed estrailo: tar xzf censo-inventory-*-linux.tar.gz.':
    'Copy the archive to the machines and extract it: tar xzf censo-inventory-*-linux.tar.gz.',
  'Da root: sh censo-inventory/install.sh. Sceglie da solo il binario amd64 o arm64, scrive la configurazione in /etc/censo e il cron in /etc/cron.d/censo-inventory.':
    'As root: sh censo-inventory/install.sh. It picks the amd64 or arm64 binary by itself, writes the configuration to /etc/censo and the cron job to /etc/cron.d/censo-inventory.',
  'Distribuzione di massa':
    'Mass deployment',
  'Estrai lo zip in una cartella condivisa leggibile dai computer del dominio (per esempio \\\\dominio\\NETLOGON\\censo-inventory o una share con permesso di lettura per «Domain Computers»).':
    'Extract the zip to a shared folder readable by the domain computers (for example \\\\domain\\NETLOGON\\censo-inventory or a share with read permission for «Domain Computers»).',
  'Gestione Criteri di gruppo → un GPO collegato alle OU dei server → Configurazione computer → Criteri → Impostazioni di Windows → Script → Avvio → Aggiungi: install.cmd dalla share.':
    'Group Policy Management → a GPO linked to the server OUs → Computer Configuration → Policies → Windows Settings → Scripts → Startup → Add: install.cmd from the share.',
  'In questa pagina, su questo agente, «Consegne» sale e «Ultima consegna» mostra l’IP interno delle macchine; ogni macchina compare fra i Configuration Item con origine «Agente d’inventario».':
    'On this page, for this agent, «Deliveries» goes up and «Last delivery» shows the machines’ internal IP; each machine appears among the Configuration Items with source «Inventory agent».',
  'Intune: impacchetta la cartella come app Win32 (IntuneWinAppUtil) con comando di installazione «install.cmd», contesto di sistema; rilevamento: il file C:\\ProgramData\\CENSO\\censo-inventory.exe.':
    'Intune: package the folder as a Win32 app (IntuneWinAppUtil) with install command «install.cmd», system context; detection: the file C:\\ProgramData\\CENSO\\censo-inventory.exe.',
  'Le macchine consegnano a':
    'The machines deliver to',
  'Linux — Ansible o script':
    'Linux — Ansible or script',
  'Lo stesso pacchetto va bene per tutte le macchine: ognuna si presenta con il proprio seriale e nome e diventa un CI a sé.':
    'The same package works for every machine: each presents its own serial and name and becomes its own CI.',
  'Prima di iniziare':
    'Before you start',
  'SCCM / Configuration Manager: applicazione o pacchetto con programma «install.cmd», eseguito con diritti amministrativi, che l’utente sia connesso o no.':
    'SCCM / Configuration Manager: application or package with program «install.cmd», run with administrative rights, whether or not a user is logged on.',
  'Scarica pacchetto Linux (.tar.gz)':
    'Download Linux package (.tar.gz)',
  'Scarica pacchetto Windows (.zip)':
    'Download Windows package (.zip)',
  'Scegli qui sopra dove consegnano le macchine: il Control Plane se lo raggiungono (HTTPS, porta 443), oppure una sonda della loro rete se il cloud non lo vedono (porta del ricevitore della sonda, di norma 8899). Se la sonda non risponde, l’agente riprova sul Control Plane.':
    'Choose above where the machines deliver: the Control Plane if they can reach it (HTTPS, port 443), or a probe in their network if they cannot see the cloud (the probe receiver port, usually 8899). If the probe does not answer, the agent retries on the Control Plane.',
  'Se l’agente è già installato il pacchetto non fa niente: puoi lanciarlo a ogni avvio senza doppioni. Per reinstallare (per esempio dopo aver rigenerato il segreto) usa /force su Windows, --force su Linux.':
    'If the agent is already installed the package does nothing: you can run it at every startup without duplicates. To reinstall (for example after regenerating the secret) use /force on Windows, --force on Linux.',
  'Windows — Intune o SCCM':
    'Windows — Intune or SCCM',
  'Windows — criterio di gruppo':
    'Windows — Group Policy',
  '1 ora':
    '1 hour',
  'Definisce il periodo massimo di inattività prima della richiesta di una nuova autenticazione. La sessione viene rinnovata automaticamente durante l’utilizzo, è condivisa tra schede e finestre del browser e si applica a tutti gli utenti del tenant dal successivo rinnovo della sessione.':
    'How long without activity before the console asks you to sign in again. While it is in use the session renews itself, and it is shared across the browser’s tabs and windows. It applies to all users of this tenant, from their next session renewal.',
  'Salvato.':
    'Saved.',
  'Scade dopo un’inattività di':
    'Expires after inactivity of',
  'Sessione':
    'Session',
  '{n} minuti':
    '{n} minutes',
  '{n} ore':
    '{n} hours',
  'Togli il filtro':
    'Remove the filter',
  '5986 (HTTPS) se lasci vuoto; 5985 se usi WinRM in chiaro. Ignorata in modalità WMI.':
    '5986 (HTTPS) if left empty; 5985 if you use plain-text WinRM. Ignored in WMI mode.',
  'Di default, se un host è in un cluster di failover, la sonda interroga anche gli altri nodi (ognuno ospita solo le sue VM). Accendilo per limitarti agli host scritti sopra.':
    'By default, if a host is in a failover cluster, the probe also queries the other nodes (each one hosts only its own VMs). Turn this on to stick to the hosts listed above.',
  "Host, cluster di failover, macchine virtuali, commutatori virtuali e volumi (anche CSV). Via WinRM o WMI sugli host, oppure dal server SCVMM. Gli indirizzi delle VM richiedono i servizi d'integrazione nell'ospite.":
    'Hosts, failover clusters, virtual machines, virtual switches and volumes (including CSV). Via WinRM or WMI on the hosts, or from the SCVMM server. VM addresses require the integration services in the guest.',
  'Host, cluster o server SCVMM':
    'Hosts, cluster or SCVMM server',
  'Microsoft Hyper-V':
    'Microsoft Hyper-V',
  "Per host fuori dominio con l'autenticazione Basic di WinRM abilitata, e solo su HTTPS: in chiaro manderebbe la password leggibile, e la sonda lo rifiuta.":
    'For hosts outside the domain with WinRM Basic authentication enabled, and only over HTTPS: in plain text it would send a readable password, and the probe refuses it.',
  'Quando WinRM non è attivo: porta 135 più le porte RPC dinamiche verso ogni host. Solo letture WMI da un elenco chiuso. Non dà le dimensioni dei dischi virtuali (servirebbe invocare metodi).':
    'When WinRM is not enabled: port 135 plus the dynamic RPC ports to each host. Only WMI reads from a closed list. Does not give virtual disk sizes (that would require invoking methods).',
  'Solo gli host indicati':
    'Only the listed hosts',
  'Solo su un segmento fidato e solo con NTLM: la password non viaggia, il contenuto sì. Il default è HTTPS sulla 5986.':
    'Only on a trusted segment and only with NTLM: the password does not travel, the content does. The default is HTTPS on 5986.',
  'Spento di default: WinRM usa quasi sempre il certificato autofirmato di `winrm quickconfig`. Accendilo se gli host hanno un certificato della tua PKI.':
    'Off by default: WinRM almost always uses the self-signed certificate from `winrm quickconfig`. Turn it on if the hosts have a certificate from your PKI.',
  'System Center VMM':
    'System Center VMM',
  'Un solo punto di contatto: WinRM verso il server VMM, che risponde per tutti gli host e i cluster che gestisce. Serve un utente con ruolo VMM di sola lettura («Read-Only Administrator»). Le VM vSphere gestite da VMM si saltano: le legge il connettore VMware.':
    'A single point of contact: WinRM to the VMM server, which answers for all the hosts and clusters it manages. Requires a user with a read-only VMM role (“Read-Only Administrator”). vSphere VMs managed by VMM are skipped: the VMware connector reads them.',
  'Uno o più host Hyper-V (separati da virgola o a capo), oppure il nome del cluster di failover: i nodi del cluster si trovano da soli. In modalità SCVMM: il server VMM.':
    'One or more Hyper-V hosts (comma- or newline-separated), or the failover cluster name: the cluster nodes are found automatically. In SCVMM mode: the VMM server.',
  'WMI (DCOM), senza WinRM':
    'WMI (DCOM), without WinRM',
  'WinRM (PowerShell) con Basic':
    'WinRM (PowerShell) with Basic',
  'WinRM (PowerShell) con NTLM':
    'WinRM (PowerShell) with NTLM',
  'hv01.dominio.local, hv02.dominio.local':
    'hv01.domain.local, hv02.domain.local',
  "«DOMINIO\\utente» membro di «Hyper-V Administrators» e di «Remote Management Users» su ogni host (in cluster: anche lettura sul cluster). Dà l'inventario più completo, dimensioni dei dischi virtuali comprese.":
    '“DOMAIN\\user” member of “Hyper-V Administrators” and “Remote Management Users” on every host (in a cluster: also read access to the cluster). Gives the most complete inventory, virtual disk sizes included.',
  'A) L’utente':
    'A) The user',
  'B) WinRM (consigliata)':
    'B) WinRM (recommended)',
  'C) WMI, senza WinRM':
    'C) WMI, without WinRM',
  'CENSO legge Hyper-V senza installare nulla sugli host: la sonda si collega con la modalità scelta nella credenziale ed esegue soltanto letture. Scegli una modalità: WinRM se è attivo (la più completa), WMI se WinRM è spento, SCVMM se hai System Center Virtual Machine Manager.':
    'CENSO reads Hyper-V without installing anything on the hosts: the probe connects with the mode chosen in the credential and only performs reads. Pick a mode: WinRM if it is enabled (the most complete), WMI if WinRM is off, SCVMM if you have System Center Virtual Machine Manager.',
  'Come preparo Hyper-V per CENSO (WinRM, WMI o SCVMM)?':
    'How do I prepare Hyper-V for CENSO (WinRM, WMI or SCVMM)?',
  'D) System Center VMM':
    'D) System Center VMM',
  'Da un PC Windows: «Enter-PSSession hv01 -Credential DOMINIO\\censo-hyperv», poi «Get-VM». Se risponde, risponde anche a CENSO.':
    'From a Windows PC: “Enter-PSSession hv01 -Credential DOMAIN\\censo-hyperv”, then “Get-VM”. If it answers, it answers CENSO too.',
  'Dopo il job, in Ambienti virtuali trovi l’ambiente Hyper-V: host, cluster, VM (generazione, memoria dinamica, dischi, schede con VLAN e indirizzi), commutatori virtuali e volumi/CSV come datastore.':
    'After the job, Virtual environments shows the Hyper-V environment: hosts, clusters, VMs (generation, dynamic memory, disks, adapters with VLANs and addresses), virtual switches and volumes/CSVs as datastores.',
  'E) La prova e cosa trovi':
    'E) The test and what you get',
  'Firewall: dalla sonda verso ogni host, TCP 135 e le porte RPC dinamiche (49152–65535); sugli host le regole «Windows Management Instrumentation (DCOM-In)» e «(WMI-In)».':
    'Firewall: from the probe to every host, TCP 135 and the dynamic RPC ports (49152–65535); on the hosts the “Windows Management Instrumentation (DCOM-In)” and “(WMI-In)” rules.',
  'Firewall: dalla sonda verso ogni host, TCP 5986 (o 5985 se scegli «WinRM in chiaro»).':
    'Firewall: from the probe to every host, TCP 5986 (or 5985 if you choose “Plain-text WinRM”).',
  'Gli indirizzi IP delle VM li dà il servizio d’integrazione «Scambio dati (KVP)» dentro l’ospite: se è spento, la VM si riconosce solo per MAC, e il rapporto del job lo dice.':
    'VM IP addresses come from the “Data Exchange (KVP)” integration service inside the guest: if it is off, the VM is recognised only by MAC, and the job report says so.',
  'Host e VM vSphere gestiti da VMM vengono saltati: li legge il connettore VMware, così non compaiono due volte.':
    'vSphere hosts and VMs managed by VMM are skipped: the VMware connector reads them, so they do not appear twice.',
  'Hyper-V non ha una API REST: nella credenziale «Microsoft Hyper-V» scegli come collegarti. WinRM con NTLM (consigliata, la più completa), WinRM con Basic (host fuori dominio, solo HTTPS), WMI senza WinRM (porta 135 e RPC dinamiche) oppure il server System Center VMM, che risponde per tutti gli host che gestisce. Con WinRM e WMI basta indicare il cluster o un nodo: la sonda trova gli altri nodi e si collega a ciascuno. La guida «Come preparo Hyper-V per CENSO» accanto alla credenziale spiega utente, gruppi e porte per ogni modalità.':
    'Hyper-V has no REST API: in the “Microsoft Hyper-V” credential choose how to connect. WinRM with NTLM (recommended, the most complete), WinRM with Basic (hosts outside the domain, HTTPS only), WMI without WinRM (port 135 and dynamic RPC) or the System Center VMM server, which answers for all the hosts it manages. With WinRM and WMI it is enough to name the cluster or one node: the probe finds the other nodes and connects to each one. The “How do I prepare Hyper-V for CENSO” guide next to the credential explains user, groups and ports for each mode.',
  'In VMM (Settings → User Roles) dai all’utente il ruolo «Read-Only Administrator», con ambito sui gruppi host da inventariare.':
    'In VMM (Settings → User Roles) give the user the “Read-Only Administrator” role, scoped to the host groups to inventory.',
  'L’utente, oltre ai gruppi del passo A, va in «Distributed COM Users» e deve avere «Enable Account» e «Remote Enable» sui namespace root\\virtualization\\v2, root\\cimv2 e root\\MSCluster (wmimgmt.msc → Proprietà → Sicurezza).':
    'Besides the groups in step A, the user goes into “Distributed COM Users” and needs “Enable Account” and “Remote Enable” on the root\\virtualization\\v2, root\\cimv2 and root\\MSCluster namespaces (wmimgmt.msc → Properties → Security).',
  'Nella credenziale scrivi il nome del cluster o uno qualunque dei nodi: la sonda chiede al cluster l’elenco dei nodi e si collega a ciascuno direttamente (ogni nodo conosce solo le VM che ospita). «Solo gli host indicati» disattiva questo passo.':
    'In the credential write the cluster name or any one of the nodes: the probe asks the cluster for its node list and connects to each directly (each node only knows the VMs it hosts). “Only the listed hosts” turns this step off.',
  'Nella credenziale scrivi il server VMM: la sonda apre WinRM verso di lui (passo B, ma solo verso il server VMM).':
    'In the credential write the VMM server: the probe opens WinRM to it (step B, but only towards the VMM server).',
  'Se gli host sono in cluster: lettura sul cluster, con «Grant-ClusterAccess -User DOMINIO\\censo-hyperv -ReadOnly» su un nodo.':
    'If the hosts are clustered: read access to the cluster, with “Grant-ClusterAccess -User DOMAIN\\censo-hyperv -ReadOnly” on one node.',
  'Su ogni host WinRM deve rispondere: «Enable-PSRemoting» (HTTP 5985) oppure un listener HTTPS sulla 5986 («winrm quickconfig -transport:https» con un certificato del server).':
    'WinRM must answer on every host: “Enable-PSRemoting” (HTTP 5985) or an HTTPS listener on 5986 (“winrm quickconfig -transport:https” with a server certificate).',
  'Su ogni host: membro di «Hyper-V Administrators» (per leggere le VM) e di «Remote Management Users» (per aprire la sessione WinRM).':
    'On every host: member of “Hyper-V Administrators” (to read the VMs) and of “Remote Management Users” (to open the WinRM session).',
  'Un utente di dominio dedicato (per esempio «censo-hyperv»), non un amministratore di dominio.':
    'A dedicated domain user (for example “censo-hyperv”), not a domain administrator.',
  'Via WMI mancano le dimensioni dei dischi virtuali: per leggerle servirebbe invocare metodi, e CENSO si limita a interrogare.':
    'Over WMI the virtual disk sizes are missing: reading them would require invoking methods, and CENSO only queries.',
  'Accessibile da Internet':
    'Reachable from the Internet',
  'Visualizza gli account cloud, le regioni, le reti e le relative risorse rilevate dai connettori e mostra in forma grafica le relazioni e le dipendenze tra componenti infrastrutturali, servizi e workload distribuiti.':
    'Shows the cloud accounts, regions, networks and their resources found by the connectors, and draws the relations and dependencies between infrastructure components, services and distributed workloads.',
  'Alimentazione':
    'Power',
  'Altro':
    'Other',
  'Ambienti cloud':
    'Cloud environments',
  'Ambienti cloud è lo specchio di Ambienti virtuali per AWS, Azure, Google Cloud e Oracle Cloud: un ambiente è un account (sottoscrizione Azure, progetto Google, compartimento Oracle), con le sue regioni, reti e risorse.':
    'Cloud environments mirrors Virtual environments for AWS, Azure, Google Cloud and Oracle Cloud: an environment is an account (Azure subscription, Google project, Oracle compartment) with its regions, networks and resources.',
  'Apri la scheda del CI':
    'Open the CI page',
  'Base dati':
    'Database',
  'Bilanciatore':
    'Load balancer',
  'CIDR':
    'CIDR',
  'Cerca nella mappa…':
    'Search the map…',
  'Ciò che il provider non lega a una rete (i bucket, le VM di Azure, le funzioni fuori da una VPC) sta sotto la sua regione: non sparisce, ma non ha collegamenti di rete.':
    'What the provider does not tie to a network (buckets, Azure VMs, functions outside a VPC) sits under its region: it does not disappear, but it has no network links.',
  'Compaiono qui dopo una discovery con un connettore cloud (AWS, Azure, Google Cloud, Oracle Cloud).':
    'They appear here after a discovery with a cloud connector (AWS, Azure, Google Cloud, Oracle Cloud).',
  'Con molte risorse i volumi non si disegnano: si contano sull’istanza a cui sono collegati, per tenere la mappa leggibile.':
    'With many resources, volumes are not drawn: they are counted on the instance they are attached to, to keep the map readable.',
  'Configura un connettore cloud (AWS, Azure, Google Cloud, Oracle Cloud) e lancia una raccolta.':
    'Configure a cloud connector (AWS, Azure, Google Cloud, Oracle Cloud) and run a collection.',
  'Esposta a Internet':
    'Exposed to the Internet',
  'Esposte a Internet':
    'Exposed to the Internet',
  'Gruppo di risorse':
    'Resource group',
  'Gruppo di sicurezza':
    'Security group',
  'IP privato':
    'Private IP',
  'Inventario cloud':
    'Cloud inventory',
  'Istanza':
    'Instance',
  'Istanze accese':
    'Running instances',
  'La mappa dinamica dell’account scelto: account → regioni → reti (VPC, VNet, VCN) → subnet → istanze, basi dati, bilanciatori, funzioni e gruppi di sicurezza, con i volumi collegati alle loro istanze. Tre viste (gerarchica, radiale, organica), zoom con la rotella, filtro per regione e per genere, ricerca; un clic su un nodo apre i suoi dati e la scheda del CI. Il 🌐 segna ciò che è raggiungibile da Internet; le istanze spente sono sbiadite.':
    'The dynamic map of the chosen account: account → regions → networks (VPC, VNet, VCN) → subnets → instances, databases, load balancers, functions and security groups, with volumes attached to their instances. Three views (hierarchical, radial, organic), zoom with the wheel, filter by region and by kind, search; clicking a node opens its data and the CI page. 🌐 marks what is reachable from the Internet; stopped instances are faded.',
  'Inventario delle risorse rilevate dai connettori cloud: account, istanze, volumi, reti, security group, bilanciatori, database, storage e funzioni serverless. Offre una vista dettagliata dell’ambiente cloud, consultabile a video o esportabile in Excel e PDF, con un livello di dettaglio analogo ai principali strumenti di inventario delle infrastrutture virtuali.':
    'Inventory of the resources found by the cloud connectors: accounts, instances, volumes, networks, security groups, load balancers, databases, storage and serverless functions. A detailed view of the cloud environment, on screen or exported to Excel and PDF, with a level of detail like the main virtual infrastructure inventory tools.',
  'Mappa cloud di {account}: {n} nodi':
    'Cloud map of {account}: {n} nodes',
  'Molte risorse: i volumi sono contati sulla loro istanza invece che disegnati.':
    'Many resources: volumes are counted on their instance instead of being drawn.',
  'Mostra':
    'Show',
  'Nessun ambiente cloud':
    'No cloud environment',
  'Nessuna risorsa cloud':
    'No cloud resources',
  'Nessuna risorsa da mostrare con questi filtri.':
    'No resources to show with these filters.',
  'Regioni':
    'Regions',
  'Regole aperte a Internet':
    'Rules open to the Internet',
  'Rete (VPC/VNet)':
    'Network (VPC/VNet)',
  'Scegli un nodo sulla mappa: qui compaiono i suoi dati. La rotella ingrandisce, il trascinamento sposta, il passaggio sopra un nodo accende i suoi collegamenti.':
    'Pick a node on the map: its data appears here. The wheel zooms, dragging pans, hovering a node highlights its links.',
  'Spenta':
    'Stopped',
  'Taglia':
    'Size',
  'Tutte le regioni':
    'All regions',
  'Tutto il cloud':
    'All of the cloud',
  'Volumi':
    'Volumes',
  'costi non raccolti':
    'costs not collected',
  'in cima i totali (account, istanze accese, risorse esposte a Internet, gruppi di sicurezza aperti, costo del mese); sotto una card per account con i suoi numeri e il costo, che porta al Costi Cloud.':
    'totals at the top (accounts, running instances, resources exposed to the Internet, open security groups, monthly cost); below, one card per account with its numbers and cost, linking to the Cloud Costs.',
  'l’inventario a fogli come RVTools — istanze, volumi, reti, security group, bilanciatori, basi dati, storage, funzioni — per un account o per tutto il cloud, a video, in Excel o in PDF.':
    'the sheet inventory like RVTools — instances, volumes, networks, security groups, load balancers, databases, storage, functions — for one account or the whole cloud, on screen, in Excel or in PDF.',
  'volume collegato':
    'attached volume',
  '{account} · mappa':
    '{account} · map',
  '{c} questo mese':
    '{c} this month',
  '{n} SG aperti':
    '{n} open SGs',
  '{n} basi dati':
    '{n} databases',
  '{n} bilanciatori':
    '{n} load balancers',
  '{n} esposte':
    '{n} exposed',
  '{n} funzioni':
    '{n} functions',
  '{n} provider':
    '{n} providers',
  '{n} risorse':
    '{n} resources',
  '{n} storage':
    '{n} storage',
  '{n} volumi':
    '{n} volumes',
  '{n} volumi ({gb} GB)':
    '{n} volumes ({gb} GB)',
  'Con regole in ingresso da qualunque origine':
    'With inbound rules from any source',
  'Istanze':
    'Instances',
  'Istanze, basi dati e bilanciatori con indirizzo pubblico':
    'Instances, databases and load balancers with a public address',
  'Security group aperti':
    'Open security groups',
  'aperti a tutti':
    'open to everyone',
  'con indirizzo pubblico':
    'with a public address',
  'dal primo del mese':
    'since the first of the month',
  'API {piattaforma}':
    '{piattaforma} API',
  'Agente d’inventario':
    'Inventory agent',
  'Apparato di rete':
    'Network device',
  'Apri un CI in Inventario → Configuration Item, vai alla sezione Eagle e scegli il profilo predefinito o i parametri da monitorare.':
    'Open a CI in Inventory → Configuration Items, go to the Eagle section and choose the default profile or the parameters to monitor.',
  'Attenzione':
    'Warning',
  'Attiva il monitoraggio':
    'Enable monitoring',
  'Attivazione':
    'Activation',
  'Attivo con il profilo «{profilo}»':
    'Active with the “{profilo}” profile',
  'Attivo con una scelta manuale':
    'Active with a manual selection',
  'Attivo dal':
    'Active since',
  'Attivo…':
    'Enabling…',
  'Autonomia':
    'Runtime',
  'Base':
    'Basic',
  'CI monitorati':
    'Monitored CIs',
  'CI monitorati ({n})':
    'Monitored CIs ({n})',
  'CI monitorati in questo tenant: {n}':
    'Monitored CIs in this tenant: {n}',
  'Carico':
    'Load',
  'Come attivare il monitoraggio':
    'How to enable monitoring',
  'Connessione TCP dalla sonda':
    'TCP connection from the probe',
  'Disponibilità':
    'Availability',
  'Eagle · monitoraggio':
    'Eagle · monitoring',
  'Frequenza di {parametro}':
    'Frequency of {parametro}',
  'Gruppo di continuità':
    'UPS',
  'Il monitoraggio si abilita dalla scheda del singolo CI, nella sezione Eagle.':
    "The monitored CIs. Monitoring is enabled from each CI's page, Eagle section.",
  'Il CI non ha un indirizzo da interrogare.':
    'The CI has no address to query.',
  'Il profilo non ha parametri leggibili su questo CI: usa «Scelgo io» per vedere perché.':
    'The profile has no readable parameters on this CI: use “I’ll choose” to see why.',
  'Il tuo ruolo può vedere Eagle ma non attivarlo.':
    'Your role can see Eagle but not enable it.',
  'Interfacce':
    'Interfaces',
  'La lettura via {metodo} arriva con un prossimo aggiornamento: puoi già sceglierlo, la scelta resta salvata.':
    'Reading via {metodo} arrives with an upcoming update: you can already select it, the choice is saved.',
  'Metodo':
    'Method',
  'Monitora questo CI con le credenziali e i dati che CENSO ha già: scegli il profilo predefinito della sua classe oppure spunta tu i parametri e la frequenza.':
    'Monitor this CI with the credentials and data CENSO already has: choose the default profile for its class or tick the parameters and frequency yourself.',
  'Mostra tutti i parametri monitorabili ({n})':
    'Show all monitorable parameters ({n})',
  'Nessun CI monitorato':
    'No monitored CIs',
  'Nessuna fonte dell’inventario legge questo dato su questo CI: aggiungi una credenziale SNMP, SSH o WinRM, oppure installa l’agente d’inventario.':
    'No inventory source reads this data on this CI: add an SNMP, SSH or WinRM credential, or install the inventory agent.',
  'Non più in inventario':
    'No longer in inventory',
  'Non più in inventario: la scelta resta finché non la togli.':
    'No longer in inventory: the choice stays until you remove it.',
  'Pagina non disponibile':
    'Page not available',
  'Parametro':
    'Parameter',
  'Parti dal profilo':
    'Start from the profile',
  'Ping dalla sonda':
    'Ping from the probe',
  'Ping e interfacce attive.':
    'Ping and active interfaces.',
  'Ping, CPU, memoria e le interfacce attive che portano un collegamento documentato o un uplink.':
    'Ping, CPU, memory and the active interfaces carrying a documented link or an uplink.',
  'Ping, CPU, memoria, tutti i volumi, i servizi noti in ascolto e le interfacce attive.':
    'Ping, CPU, memory, all volumes, the well-known listening services and the active interfaces.',
  'Ping, carica della batteria, autonomia e carico.':
    'Ping, battery charge, runtime and load.',
  'Ping.':
    'Ping.',
  'Profilo predefinito':
    'Default profile',
  'Profilo predefinito: {profilo}':
    'Default profile: {profilo}',
  'Quella predefinita di ogni parametro':
    "Each parameter's default",
  'Questa funzione non è attiva per il tuo spazio.':
    'This feature is not enabled for your space.',
  'Raggiungibilità (ping)':
    'Reachability (ping)',
  'Scelgo io':
    'I’ll choose',
  'Scelta manuale':
    'Manual selection',
  'Scelto':
    'Selected',
  'Servizio {porta}/tcp':
    'Service {porta}/tcp',
  'Soglia critica di {parametro}':
    'Critical threshold of {parametro}',
  'Soglia di attenzione di {parametro}':
    'Warning threshold of {parametro}',
  'Specifici della classe':
    'Class-specific',
  'Spunta i parametri da monitorare e imposta la frequenza di ciascuno.':
    "Tick the parameters to monitor and set each one's frequency.",
  'Togli tutto':
    'Clear all',
  'Volume {mount}':
    'Volume {mount}',
  'dal {quando}':
    'since {quando}',
  'in attesa dei primi dati':
    'waiting for the first data',
  'non monitorato':
    'not monitored',
  'perdita di pacchetti':
    'packet loss',
  'sopra {v}':
    'above {v}',
  'sotto {v}':
    'below {v}',
  'spazio usato':
    'space used',
  'tempo di risposta':
    'response time',
  'utilizzo':
    'utilisation',
  'utilizzo di banda, errori e stato':
    'bandwidth use, errors and status',
  '{n} parametri monitorati':
    '{n} parameters monitored',
  '{n} parametri su questo CI':
    '{n} parameters on this CI',
  'Inventario adesso':
    'Inventory now',
  'L’agente raccoglie e consegna subito, entro un minuto, senza aspettare il suo intervallo.':
    'The agent collects and delivers right away, within a minute, without waiting for its interval.',
  'Questo agente non è ancora residente: consegna solo al suo intervallo. Con l’aggiornamento automatico diventa residente da solo.':
    'This agent is not resident yet: it only delivers at its interval. With automatic updates it becomes resident by itself.',
  'Ultimo contatto':
    'Last contact',
  'in linea':
    'online',
  'non residente':
    'not resident',
  'prossimo inventario {quando}':
    'next inventory {quando}',
  'Mostra le ultime 24 ore':
    'Show the last 24 hours',
  'Servono almeno due misure per disegnare l’andamento.':
    'At least two measurements are needed to draw the trend.',
  'attenzione':
    'warning',
  'non risponde':
    'not responding',
  'Se un EDR (per esempio Cynet) blocca il comando, usa il pacchetto Windows qui sotto anche per una macchina sola: si estrae lo zip e si lancia install.cmd da amministratore. Non c’è nessuno script PowerShell e nessuno scaricamento, perché l’agente si installa da solo.':
    'If an EDR (for example Cynet) blocks the command, use the Windows package below even for a single machine: extract the zip and run install.cmd as administrator. There is no PowerShell script and no download, because the agent installs itself.',
  'Un pacchetto con l’agente e la sua configurazione che non scarica niente: su Windows install.cmd lancia l’agente, che si installa da solo senza script PowerShell (la strada che gli EDR non bloccano). Si mette su una share o in Intune/SCCM e si installa su quante macchine vuoi, ognuna diventa il suo CI. Contiene il segreto dell’agente: trattalo come una credenziale. Si scarica solo adesso (finestra d’installazione); una volta scaricato non scade, e lo invalidi rigenerando o revocando l’agente.':
    'A package with the agent and its configuration that downloads nothing: on Windows install.cmd launches the agent, which installs itself without any PowerShell script (the route EDRs do not block). Put it on a share or in Intune/SCCM and install it on as many machines as you like; each becomes its own CI. It contains the agent’s secret: treat it as a credential. It can only be downloaded now (installation window); once downloaded it does not expire, and you invalidate it by regenerating or revoking the agent.',
  '{p} principali · {s} secondari':
    '{p} primary · {s} secondary',
  'Aggiungi una sonda':
    'Add a probe',
  'CI letti':
    'CIs read',
  'dedicata':
    'dedicated',
  'Dedicata al monitoraggio':
    'Dedicated to monitoring',
  'Esclusa da Eagle':
    'Excluded from Eagle',
  'La prima sonda legge tutti i parametri; le altre solo ping e porte, così l’oggetto risulta giù solo se non risponde a nessuna.':
    'The first probe reads every parameter; the others only ping and ports, so the object is down only if it answers none of them.',
  'nessuna sonda attiva può leggerlo adesso':
    'no active probe can read it right now',
  'non attiva':
    'not active',
  'Normale: legge i CI che ha scoperto':
    'Normal: reads the CIs it discovered',
  'Osservato da':
    'Observed from',
  'principale':
    'primary',
  'rendi principale':
    'make primary',
  'Rendi principale {sonda}':
    'Make {sonda} primary',
  'Reti di {sonda}':
    'Networks of {sonda}',
  'Ruolo di {sonda}':
    'Role of {sonda}',
  'scelta automatica':
    'automatic choice',
  'Scelta automatica: una sonda dedicata al monitoraggio che copre l’indirizzo, altrimenti quella che ha scoperto il CI.':
    'Automatic choice: a probe dedicated to monitoring that covers the address, otherwise the one that discovered the CI.',
  'scelta manuale':
    'manual choice',
  'solo raggiungibilità':
    'reachability only',
  'Togli {sonda}':
    'Remove {sonda}',
  'Torna alla scelta automatica':
    'Back to automatic choice',
  'Seleziona la sonda da dedicare al monitoraggio Eagle. Indica le reti da monitorare; se non ne specifichi nessuna, la sonda monitora tutti i CI. La sonda di origine resta disponibile per la discovery. La sonda esclusa da Eagle non acquisisce dati di monitoraggio.':
    'A probe dedicated to monitoring reads the CIs in the listed networks (empty = all) instead of the probe that discovered them, which stays free for discovery. An excluded probe reads nothing for Eagle.',
  'Togli il tetto':
    'Remove the cap',
  '{n}% in ritardo':
    '{n}% late',
  'Tetto: {n} parametri':
    'Cap: {n} parameters',
  'fissato: {n} letture su {m} in ritardo':
    'set: {n} of {m} readings late',
  'allentato: nessun ritardo':
    'loosened: no delays',
  '{n} parametri':
    '{n} parameters',
  'Servizi automatici':
    'Automatic services',
  'Servizio {nome}':
    'Service {nome}',
  'quanti servizi ad avvio automatico sono fermi (su Linux: unità in errore)':
    'how many automatic-start services are stopped (on Linux: failed units)',
  'in esecuzione':
    'running',
  'Aggiungi un gruppo di servizi':
    'Add a service group',
  'Aggiungi un processo':
    'Add a process',
  'Almeno':
    'At least',
  'almeno {n} attivi fra: {regola}':
    'at least {n} running among: {regola}',
  'Almeno un certo numero attivi':
    'At least a given number running',
  'Cerca un servizio':
    'Search a service',
  'Comprende ogni servizio il cui nome corrisponde, anche quelli installati dopo.':
    'Includes every service whose name matches, including those installed later.',
  'es. SQL Server':
    'e.g. SQL Server',
  'Giù se non ce n’è nessuno in esecuzione; il valore è quante istanze girano.':
    'Down if none is running; the value is how many instances are running.',
  'Gruppo di servizi «{nome}»':
    'Service group “{nome}”',
  'istanze in esecuzione':
    'running instances',
  'Modello (facoltativo)':
    'Pattern (optional)',
  'Nome del processo':
    'Process name',
  'Nuovo gruppo di servizi':
    'New service group',
  'Nuovo processo':
    'New process',
  'Processi':
    'Processes',
  'Processo {nome}':
    'Process {nome}',
  'Quando è a posto':
    'When it is fine',
  'Servizi scelti ({n})':
    'Chosen services ({n})',
  'tutti attivi: {regola}':
    'all running: {regola}',
  'Tutti i servizi del gruppo attivi':
    'All services of the group running',
  'Tempo dall’avvio':
    'Time since boot',
  'ore dall’avvio: sotto la soglia la macchina è ripartita da poco':
    'hours since boot: below the threshold the machine restarted recently',
  'Salute del disco {nome}':
    'Disk health {nome}',
  'Aggiornamenti in sospeso':
    'Pending updates',
  'Riavvio richiesto':
    'Reboot required',
  'guasto previsto (SMART); sulle macchine virtuali non è disponibile':
    'predicted failure (SMART); not available on virtual machines',
  'dall’ultima ricerca del sistema, senza andare in rete':
    "from the system's last search, without going online",
  'il sistema chiede un riavvio per completare gli aggiornamenti':
    'the system asks for a reboot to complete updates',
  '{n} critici':
    '{n} critical',
  '{n} in attenzione':
    '{n} warning',
  '{n} non rispondono':
    '{n} not responding',
  '{n} ok':
    '{n} ok',
  '95° pct {v}':
    '95th pct {v}',
  'Andamento di {parametro} nelle ultime {ore} ore':
    'Trend of {parametro} over the last {ore} hours',
  'Cerca un CI':
    'Search a CI',
  'da {quando}':
    'since {quando}',
  'Finestra di tempo':
    'Time window',
  'la fascia è il minimo–massimo di ogni intervallo; la linea la media':
    "the band is each interval's minimum–maximum; the line the average",
  'Le sonde viste da Eagle: chi legge che cosa, e con quale carico.':
    'The probes as Eagle sees them: who reads what, and with what load.',
  'min {min} · max {max}':
    'min {min} · max {max}',
  'nessun parametro in allarme nel periodo':
    'no parameter in alarm in the period',
  'Nessun parametro in attenzione, critico o che non risponde.':
    'No parameter in warning, critical or not responding.',
  'Nessuna misura nel periodo.':
    'No measurement in the period.',
  'Parametro in allarme':
    'Parameter in alarm',
  'Parametri in allarme nel tempo':
    'Parameters in alarm over time',
  'Parametri in allarme per intervallo nelle ultime {ore} ore':
    'Parameters in alarm per interval over the last {ore} hours',
  'solo con problemi':
    'only with problems',
  'stato':
    'status',
  'tutto regolare':
    'all fine',
  'Eagle è il monitoraggio: legge nel tempo i parametri dei CI che scegli — raggiungibilità, porte, CPU, memoria, dischi, servizi, processi, interfacce — con le credenziali e i dati che CENSO ha già. È un modulo a parte: se non lo vedi nel menu, non è attivo per il tuo spazio.':
    'Eagle is monitoring: it reads over time the parameters of the CIs you choose — reachability, ports, CPU, memory, disks, services, processes, interfaces — with the credentials and data CENSO already has. It is a separate module: if you do not see it in the menu, it is not active for your space.',
  'l’elenco di ciò che Eagle legge, con quanti parametri e da quando.':
    'the list of what Eagle reads, with how many parameters and since when.',
  'chi legge che cosa: il ruolo di ogni sonda (normale, dedicata al monitoraggio con le sue reti, esclusa), quanti CI legge e con quale carico.':
    'who reads what: the role of each probe (normal, dedicated to monitoring with its networks, excluded), how many CIs it reads and with what load.',
  'Nella scheda di un CI, la sezione Eagle: l’attivazione (profilo della classe o scelta manuale), i parametri con frequenza, soglie e stato, «Osservato da» per scegliere le sonde, e in fondo i gruppi di servizi e i processi da aggiungere a mano.':
    "In a CI's page, the Eagle section: activation (class profile or manual choice), the parameters with frequency, thresholds and status, “Observed from” to choose the probes, and at the bottom the service groups and processes to add by hand.",
  'Apri un CI, sezione Eagle, e attiva il monitoraggio con il profilo predefinito: accende ciò che si può leggere su quella classe. «Scelgo io» per spuntare i parametri uno a uno.':
    'Open a CI, Eagle section, and enable monitoring with the default profile: it turns on what can be read on that class. “I choose” to tick the parameters one by one.',
  'Sul grafico di un parametro scegli la finestra (1 ora, 6 ore, 24 ore, 7 giorni): la linea è il valore, la fascia il minimo–massimo di ogni intervallo, sotto la striscia di stato minuto per minuto.':
    "On a parameter's chart choose the window (1 hour, 6 hours, 24 hours, 7 days): the line is the value, the band each interval's minimum–maximum, below the status strip minute by minute.",
  'Per vedere un oggetto da più sedi, «Osservato da» → «Cambia»: la prima sonda legge tutto, le altre solo ping e porte, e l’oggetto è giù solo se non risponde a nessuna.':
    'To watch an object from several sites, “Observed from” → “Change”: the first probe reads everything, the others only ping and ports, and the object is down only if it answers none of them.',
  'Un parametro «non disponibile» non è un guasto: l’apparato non espone quel dato (una VM non ha il SMART dei dischi). «In attesa» aspetta la prima lettura.':
    'An “unavailable” parameter is not a failure: the device does not expose that data (a VM has no disk SMART). “Pending” waits for the first reading.',
  'CPU, memoria, dischi, servizi e processi dei server con l’agente li legge l’agente stesso, con il keep-alive di ogni minuto; gli apparati di rete si leggono via SNMP con la credenziale che ha risposto in discovery.':
    'CPU, memory, disks, services and processes of servers with the agent are read by the agent itself, with the keep-alive every minute; network devices are read via SNMP with the credential that answered in discovery.',
  'Le sonde elaborano sul posto e mandano al Control Plane i cambi di stato subito e le medie ogni cinque minuti: se una resta indietro, il cruscotto delle sonde lo mostra e il carico passa a un’altra che vede gli stessi CI.':
    'Probes process locally and send the Control Plane state changes at once and averages every five minutes: if one falls behind, the probes view shows it and the load moves to another that sees the same CIs.',
  '{n} min':
    '{n} min',
  'Aggiungi una fascia':
    'Add a slot',
  'Alle':
    'To',
  'Attesa':
    'Delay',
  'Attesa prima di avvisare (minuti)':
    'Delay before alerting (minutes)',
  'Avvisa':
    'Alert',
  'avvisa comunque':
    'alert anyway',
  'Campanella in console':
    'Console bell',
  'Canali':
    'Channels',
  'Configura destinatari, livelli di gravità, canali e orari delle notifiche. Il gruppo viene associato a ciascun CI monitorato nella sezione Eagle.':
    'Whom to alert for which severities, through which channels, in which hours. Each monitored CI chooses its group in the Eagle section.',
  'Gestione delle notifiche: contatti, orari di reperibilità, gruppi di notifica e SLA.':
    'Whom to alert, in which hours, with what commitment: contacts, working hours, notification groups, SLAs.',
  'Contatti':
    'Contacts',
  'Contatti ({n})':
    'Contacts ({n})',
  'Crea prima un contatto.':
    'Create a contact first.',
  'Dalle':
    'From',
  'Disponibilità attesa (%)':
    'Expected availability (%)',
  'dom':
    'Sun',
  'Eagle → Notifiche':
    'Eagle → Notifications',
  'Fasce':
    'Slots',
  'Festività':
    'Holidays',
  'Festività (una data per riga, AAAA-MM-GG)':
    'Holidays (one date per line, YYYY-MM-DD)',
  'Fuori orario':
    'Outside hours',
  'gio':
    'Thu',
  'Giorno':
    'Day',
  'Gruppi di notifica ({n})':
    'Notification groups ({n})',
  'Gruppi e SLA si definiscono in':
    'Groups and SLAs are defined in',
  'Gruppo di notifica':
    'Notification group',
  'i contatti (le persone da avvisare), gli orari di lavoro (fasce e festività in un fuso), i gruppi di notifica (chi, per quali gravità, con quali canali, cosa fare fuori orario, quanto aspettare prima di avvisare) e gli SLA (disponibilità attesa, presa in carico e risoluzione). Ogni CI sceglie il suo gruppo e il suo SLA nella sezione Eagle.':
    'the contacts (the people to alert), the working hours (slots and holidays in a time zone), the notification groups (whom, for which severities, through which channels, what to do outside hours, how long to wait before alerting) and the SLAs (expected availability, acknowledgement and resolution). Each CI chooses its group and its SLA in the Eagle section.',
  'Definisce i livelli di servizio: disponibilità mensile attesa, tempi di presa in carico e risoluzione e relative fasce di validità. Ogni CI monitorato utilizza lo SLA assegnato nella sezione Eagle; lo stesso SLA è il riferimento per i report di disponibilità.':
    'The commitment: the expected monthly availability, how soon someone acknowledges and resolves, and in which hours it applies. Each monitored CI chooses its SLA in the Eagle section; availability reports are measured against it.',
  'lun':
    'Mon',
  'mar':
    'Tue',
  'mer':
    'Wed',
  'Nessun calendario configurato: le notifiche sono sempre attive.':
    'No calendar: without one, groups alert at any time.',
  'Nessun contatto ancora.':
    'No contact yet.',
  'Nessun gruppo configurato: le notifiche non vengono inviate.':
    'No group yet: without one, nobody is alerted.',
  'Nessuno SLA ancora.':
    'No SLA yet.',
  'niente, si vede al rientro':
    'nothing, it will be seen on return',
  'Nuovo calendario':
    'New calendar',
  'Nuovo contatto':
    'New contact',
  'Nuovo SLA':
    'New SLA',
  'Orari di lavoro':
    'Working hours',
  'Orari di lavoro ({n})':
    'Working hours ({n})',
  'Presa in carico':
    'Acknowledgement',
  'Presa in carico entro (minuti)':
    'Acknowledge within (minutes)',
  'Definisce giorni, fasce orarie e festività in cui il servizio è attivo. Gli SLA si applicano durante l’orario di servizio; fuori orario, il comportamento dipende dal gruppo di notifica.':
    'When on duty: the slots per weekday in the given time zone, and the holidays. Notification groups decide what to do outside these hours; SLAs apply within them.',
  'ripete ogni {n} min':
    'repeats every {n} min',
  'Ripeti ogni (minuti, 0 = mai)':
    'Repeat every (minutes, 0 = never)',
  'Risoluzione':
    'Resolution',
  'Risoluzione entro (minuti)':
    'Resolve within (minutes)',
  'sab':
    'Sat',
  'Sempre (24 ore su 24, 7 giorni su 7)':
    'Always (24 hours a day, 7 days a week)',
  'SLA':
    'SLA',
  'SLA ({n})':
    'SLAs ({n})',
  'solo critico e non risponde':
    'only critical and not responding',
  'spento':
    'off',
  'Telefono':
    'Phone',
  'Togliere questa voce?':
    'Remove this entry?',
  'Un problema che rientra da solo entro l’attesa non avvisa nessuno.':
    'A problem that clears by itself within the delay alerts nobody.',
  'Vale negli orari':
    'Applies in hours',
  'ven':
    'Fri',
  '← Report':
    '← Reports',
  '{h} h {m} min':
    '{h} h {m} min',
  '{n} CI scelti':
    '{n} chosen CIs',
  '{n} s':
    '{n} s',
  'Aggiungi almeno un blocco.':
    'Add at least one block.',
  'Aggiungi un blocco':
    'Add a block',
  'Al massimo (righe)':
    'At most (rows)',
  'Anteprima':
    'Preview',
  'Blocchi':
    'Blocks',
  'Blocchi, nell’ordine del report':
    "Blocks, in the report's order",
  'CI nel report':
    'CIs in the report',
  'CI o parametro non trovato.':
    'CI or parameter not found.',
  'CI scelti':
    'Chosen CIs',
  'CI, disponibilità media, incidenti, tempo giù, SLA rispettati, il peggiore.':
    'CIs, average availability, incidents, downtime, SLAs met, the worst one.',
  'classi: {classi}':
    'classes: {classi}',
  'Crea il primo report: seleziona il periodo, i CI e i blocchi da includere, come riepilogo, disponibilità, incidenti, grafici e SLA; verifica l’anteprima e salva.':
    'Compose the first one: choose the period, the CIs and the blocks (summary, availability, incidents, charts, SLAs), check the preview and save.',
  'Composizione del report':
    'Report composition',
  'disponibilità media':
    'average availability',
  'Disponibilità per CI':
    'Availability per CI',
  'es. Disponibilità mensile':
    'e.g. Monthly availability',
  'Eseguo…':
    'Running…',
  'generato {quando}':
    'generated {quando}',
  'Giù':
    'Down',
  'Grafico di un parametro':
    'Chart of a parameter',
  'I parametri di un tipo con la media più alta: CPU, memoria, volumi, interfacce.':
    'The parameters of a kind with the highest average: CPU, memory, volumes, interfaces.',
  'I tratti in cui un CI non rispondeva: da, a, durata.':
    'The stretches when a CI was not responding: from, to, duration.',
  'Il mese scorso':
    'Last month',
  'il peggiore: {p}':
    'the worst: {p}',
  'incidenti':
    'incidents',
  'Incidenti':
    'Incidents',
  'Incidenti ({n})':
    'Incidents ({n})',
  'La copertura dice quanta parte del periodo era davvero osservata: una disponibilità su poche misure vale poco.':
    'Coverage says how much of the period was actually observed: availability on few measurements is worth little.',
  'La disponibilità di un CI si misura sulla sua raggiungibilità (il ping, o la prima porta monitorata) e, con uno SLA che vale in certi orari, solo dentro quegli orari. La copertura dice quanta parte del periodo era davvero osservata: una disponibilità su poche misure vale poco. Nel PDF i grafici diventano tabelle.':
    "A CI's availability is measured on its reachability (the ping, or the first monitored port) and, with an SLA valid in certain hours, only within those hours. Coverage says how much of the period was actually observed: availability on few measurements is worth little. In the PDF, charts become tables.",
  'L’andamento degli allarmi intervallo per intervallo.':
    'The trend of alarms interval by interval.',
  'L’andamento di un parametro di un CI nel periodo.':
    'The trend of one CI parameter over the period.',
  'Mancati':
    'Missed',
  'mancato':
    'missed',
  'Massimo':
    'Maximum',
  'Misure':
    'Measurements',
  'Nessun CI del report ha uno SLA.':
    'No CI in the report has an SLA.',
  'Nessun incidente nel periodo.':
    'No incident in the period.',
  'Nessun report ancora':
    'No report yet',
  'Nuovo report':
    'New report',
  'Parametri più alti':
    'Highest parameters',
  'Parametri più alti ({tipo})':
    'Highest parameters ({tipo})',
  'Peggiore':
    'Worst',
  'Per classe':
    'By class',
  'Per ogni CI: disponibilità, tempo giù, incidenti, copertura, SLA.':
    'For each CI: availability, downtime, incidents, coverage, SLA.',
  'Per ogni SLA: quanti CI lo rispettano e quanti no.':
    'For each SLA: how many CIs meet it and how many do not.',
  'Periodo: {da} – {a} · {n} CI':
    'Period: {da} – {a} · {n} CIs',
  'Ping':
    'Ping',
  'Porte TCP':
    'TCP ports',
  'Quanti':
    'How many',
  'Questo mese':
    'This month',
  'Report ({n})':
    'Reports ({n})',
  'Crea report configurabili per periodo, CI e contenuti. I report vengono generati sui dati correnti e possono essere esportati in PDF o Excel.':
    'Reports you compose: period, CIs and blocks of your choice; they run on current data and download as PDF or Excel.',
  'report che si compongono: scegli il periodo (ultime 24 ore, 7 o 30 giorni, questo mese, il mese scorso), l’ambito (tutti i CI, per classe, CI scelti) e i blocchi in ordine — riepilogo, disponibilità per CI, incidenti, andamento degli allarmi, parametri più alti, grafico di un parametro, rispetto degli SLA, testo — guarda l’anteprima, salva, scarica in PDF o Excel.':
    'reports you compose: choose the period (last 24 hours, 7 or 30 days, this month, last month), the scope (all CIs, by class, chosen CIs) and the blocks in order — summary, availability per CI, incidents, alarm trend, highest parameters, chart of a parameter, SLA compliance, text — check the preview, save, download as PDF or Excel.',
  'Riepilogo':
    'Summary',
  'Rispettati':
    'Met',
  'rispettato':
    'met',
  'Rispetto degli SLA':
    'SLA compliance',
  'Scarica Excel':
    'Download Excel',
  'Scarica PDF':
    'Download PDF',
  'Senza dati':
    'No data',
  'SLA rispettati':
    'SLAs met',
  'Sposta giù':
    'Move down',
  'Sposta su':
    'Move up',
  'tempo giù {d}':
    'downtime {d}',
  'Tipo di parametro':
    'Parameter kind',
  'Titolo':
    'Title',
  'Togliere questo report?':
    'Remove this report?',
  'tutti i CI monitorati':
    'all monitored CIs',
  'Tutti i CI monitorati':
    'All monitored CIs',
  'Un titolo e un testo libero: una premessa, una conclusione.':
    'A title and free text: a foreword, a conclusion.',
  'Nessuna credenziale SNMP nel tuo spazio: aggiungine una in Credenziali e Eagle la proverà al primo giro.':
    'No SNMP credential in your space: add one under Credentials and Eagle will try it on the first round.',
  'La discovery non ha ancora detto quale credenziale SNMP risponde: Eagle prova quelle del tuo spazio al primo giro e ricorda la buona.':
    'Discovery has not yet said which SNMP credential answers: Eagle tries those of your space on the first round and remembers the good one.',
  '{a} sorgenti · {b} con credenziali':
    '{a} sources · {b} with credentials',
  '{d}% rispetto allo stesso giorno del mese scorso':
    '{d}% vs the same day last month',
  '{libero} liberi su {tot}':
    '{libero} free of {tot}',
  '{n} in attenzione · {m} CI monitorati':
    '{n} warning · {m} monitored CIs',
  '{n} in tutto':
    '{n} in total',
  '{n} silenziosi':
    '{n} silent',
  '{v} nella finestra':
    '{v} in the window',
  '+ sezione':
    '+ section',
  'Aggiungi widget':
    'Add widget',
  'apparati che mandano flussi':
    'devices sending flows',
  'Apparati con backup recente, fermo, o senza.':
    'Devices with a recent backup, a stale one, or none.',
  'apri':
    'open',
  'Asset e contratti':
    'Assets and contracts',
  'Backup delle configurazioni':
    'Configuration backups',
  'Capacità e costi':
    'Capacity and costs',
  'Catalogo dei widget':
    'Widget catalogue',
  'Chi fa più traffico':
    'Top talkers',
  'CI per tipo':
    'CIs by type',
  'Compute':
    'Compute',
  'Conversazioni principali':
    'Top conversations',
  'Coperti da contratto e scoperti.':
    'Covered by contract and uncovered.',
  'Costi cloud del mese':
    'Cloud costs this month',
  'CPU allocata {x}× · memoria {y}×':
    'CPU allocated {x}× · memory {y}×',
  'critici o che non rispondono':
    'critical or not responding',
  'da classificare':
    'to classify',
  'dall’inizio del mese':
    'since the start of the month',
  'Dashboard del tenant':
    'Tenant dashboard',
  'Dashboard predefinita: personalizzala e diventa tua.':
    'Default dashboard: customise it and it becomes yours.',
  'Dati non disponibili al momento.':
    'Data not available right now.',
  'Dove vanno i soldi.':
    'Where the money goes.',
  'Eagle · parametri in allarme nel tempo':
    'Eagle · parameters in alarm over time',
  'Eagle · parametri per stato':
    'Eagle · parameters by status',
  'Eagle · problemi aperti':
    'Eagle · open problems',
  'Eagle · problemi gravi':
    'Eagle · serious problems',
  'Esc per uscire':
    'Esc to exit',
  'Esportatori di flussi':
    'Flow exporters',
  'Ferma':
    'Pause',
  'Gli agenti per stato e le vulnerabilità.':
    'Agents by status and vulnerabilities.',
  'Gli apparati che mandano flussi, e quelli silenziosi.':
    'The devices sending flows, and the silent ones.',
  'Gli asset per fascia di rischio.':
    'Assets by risk band.',
  'Gli avvistamenti di chi parla senza essere censito.':
    'Sightings of who talks without being inventoried.',
  'Gli indirizzi e i CI con più byte, in entrata e in uscita.':
    'The addresses and CIs with the most bytes, in and out.',
  'Gli indirizzi non censiti con più traffico.':
    'The non-inventoried addresses with the most traffic.',
  'I job di discovery terminati con errore.':
    'Discovery jobs that ended with an error.',
  'I parametri in attenzione, critici o giù.':
    'Parameters in warning, critical or down.',
  'I prodotti a fine supporto in inventario.':
    'End-of-support products in the inventory.',
  'I rischi per gravità.':
    'Findings by severity.',
  'I tocchi sulle esche e chi li fa.':
    'Hits on the decoys and who makes them.',
  'Il numero che conta: critici e non risponde.':
    'The number that matters: critical and not responding.',
  'Il traffico per protocollo.':
    'Traffic by protocol.',
  'Il traffico per servizio (porta).':
    'Traffic by service (port).',
  'in linea su totali':
    'online out of total',
  'In linea, degradate, non raggiungibili.':
    'Online, degraded, unreachable.',
  'indirizzi e porte che ricevono connessioni':
    'addresses and ports receiving connections',
  'Indirizzi e porte che ricevono connessioni.':
    'Addresses and ports receiving connections.',
  'Job per stato':
    'Jobs by status',
  'La composizione del parco.':
    'The makeup of the estate.',
  'La mia dashboard':
    'My dashboard',
  'La tua dashboard':
    'Your dashboard',
  'L’andamento degli allarmi.':
    'The trend of alarms.',
  'L’andamento del traffico nella finestra.':
    'The traffic trend in the window.',
  'Le coppie che si parlano di più.':
    'The pairs that talk the most.',
  'Le sonde che rispondono, sul totale.':
    'The probes that answer, out of the total.',
  'Le VM con e senza backup.':
    'VMs with and without backup.',
  'Misura del widget':
    'Widget size',
  'Movimenti dell’inventario':
    'Inventory movements',
  'Nessun CI monitorato.':
    'No CI monitored.',
  'Nessun rischio.':
    'No findings.',
  'Nome della dashboard':
    'Dashboard name',
  'non più visti':
    'no longer seen',
  'Nuova sezione':
    'New section',
  'Nuovi, non più visti, da classificare negli ultimi 7 giorni.':
    'New, no longer seen, to classify in the last 7 days.',
  'Ok, attenzione, critico, non risponde.':
    'Ok, warning, critical, not responding.',
  'Predefinita':
    'Default',
  'prodotti a fine supporto in inventario':
    'end-of-support products in the inventory',
  'Protezione':
    'Protection',
  'Protezione e capacità':
    'Protection and capacity',
  'Protocolli':
    'Protocols',
  'Quanti CI sono riconosciuti da almeno una fonte.':
    'How many CIs are recognised by at least one source.',
  'Quanto si scopre nel tempo: nuovi e scomparsi.':
    'What gets discovered over time: new and gone.',
  'Quanto storage è usato e quanto ne resta.':
    'How much storage is used and how much is left.',
  'Questa sezione è vuota.':
    'This section is empty.',
  'Questo widget non esiste più: toglilo dalla dashboard.':
    'This widget no longer exists: remove it from the dashboard.',
  'registro':
    'log',
  'Rischi sui CI':
    'Findings on CIs',
  'riconosciuti da almeno una fonte':
    'recognised by at least one source',
  'Riprendi':
    'Resume',
  'Rischio degli asset':
    'Asset risk',
  'Riusciti, in corso, in attesa, falliti.':
    'Succeeded, running, pending, failed.',
  'Salva come mia':
    'Save as mine',
  'Salva per il tenant':
    'Save for the tenant',
  'Scansioni':
    'Scans',
  'Sconosciuti che parlano di più':
    'Unknowns that talk the most',
  'Servizi esposti':
    'Exposed services',
  'Servizi più usati':
    'Most used services',
  'Sezione vuota: aggiungi un widget dal catalogo.':
    'Empty section: add a widget from the catalogue.',
  'Sinottico':
    'Wall view',
  'Sinottico: cambia sezione ogni':
    'Wall view: switch section every',
  'Sonde in linea':
    'Probes online',
  'Sonde per stato':
    'Probes by status',
  'Spazio per fermare o riprendere la rotazione':
    'Space to pause or resume the rotation',
  'Speso finora, confronto e stima a fine mese.':
    'Spent so far, comparison and month-end estimate.',
  'stima a fine mese {v}':
    'month-end estimate {v}',
  'su {n} CI':
    'on {n} CIs',
  'su {n} job':
    'out of {n} jobs',
  'Titolo della sezione':
    'Section title',
  'tocchi sulle esche':
    'hits on the decoys',
  'togli sezione':
    'remove section',
  'Togliere la sezione «{s}» con i suoi widget?':
    'Remove the section “{s}” with its widgets?',
  'Tornare alla dashboard predefinita? La tua personalizzazione va persa.':
    'Go back to the default dashboard? Your customisation will be lost.',
  'Trascina per spostare':
    'Drag to move',
  'ultimi {n} giorni':
    'last {n} days',
  'ultimi 7 giorni':
    'last 7 days',
  'usato':
    'used',
  'visti in rete senza accesso':
    'seen on the network without access',
  'Visti in rete senza accesso: la coda di lavoro.':
    'Seen on the network without access: the work queue.',
  'VM su {n} host':
    'VMs on {n} hosts',
  'VM, host e allocazione.':
    'VMs, hosts and allocation.',
  'Volume di traffico':
    'Traffic volume',
  'Nel sinottico le frecce cambiano sezione, spazio ferma la rotazione, Esc esce; «Schermo intero» toglie anche la cornice del browser. Ogni widget si aggiorna da sé senza lampeggiare: si può lasciare acceso.':
    'In the wall view the arrows change section, space pauses the rotation, Esc exits; “Full screen” removes the browser frame too. Every widget refreshes by itself without flashing: it can stay on.',
  'gravi':
    'serious',
  'il monitoraggio su un monitor: la parete dei CI colorati dal loro stato peggiore, i problemi prima e in rosso pieno con il parametro e da quanto, a lato i problemi aperti e l’andamento degli allarmi; si aggiorna ogni 30 secondi, Esc esce.':
    'monitoring on a wall screen: the wall of CIs coloured by their worst status, problems first in solid red with the parameter and since when, on the side the open problems and the alarm trend; it refreshes every 30 seconds, Esc exits.',
  'Sinottico a parete':
    'Wall view',
  'Anche chi li riceve soltanto':
    'Also those that only receive them',
  'Chi conta come «parla»':
    'Who counts as “talking”',
  'Le regole valgono dal prossimo giro; gli avvistamenti già registrati restano finché non li azzeri.':
    'The rules apply from the next round; sightings already recorded stay until you clear them.',
  'Porte di destinazione da non contare':
    'Destination ports not to count',
  'Protocolli da non contare':
    'Protocols not to count',
  'Salva le regole':
    'Save the rules',
  'Separate da virgola. Un flusso verso una di queste porte non fa di nessuno un avvistamento.':
    'Comma-separated. A flow to one of these ports makes nobody a sighting.',
  'Solo chi manda pacchetti (consigliato)':
    'Only those that send packets (recommended)',
  'un IP è considerato attivo quando viene osservato come sorgente di traffico. Se compare solo come destinazione nei flussi non dimostra la presenza di un asset.': 'an IP is considered active when it is observed as a source of traffic. If it appears only as a destination in the flows it does not prove the presence of an asset.',
  'Altri protocolli, per numero':
    'Other protocols, by number',
  'Il numero del protocollo IP come lo mostra la scheda dell’avvistamento (per esempio 249 su certi firewall).':
    "The IP protocol number as shown in the sighting's page (for instance 249 on some firewalls).",
  'Nessun agente è ancora legato a questo CI: l’agente si lega alla prossima consegna d’inventario (Agenti d’inventario → «Inventario adesso»).':
    'No agent is linked to this CI yet: the agent links itself at the next inventory delivery (Inventory agents → “Inventory now”).',
  'L’agente «{nome}» non si fa sentire da {quando}: le letture arrivano con il keep-alive di ogni minuto. Controlla che sia residente e aggiornato.':
    'The agent “{nome}” has been silent since {quando}: readings come with the keep-alive every minute. Check that it is resident and up to date.',
  'L’agente «{nome}» è in linea (versione {v}): le prime letture arrivano entro un paio di minuti. Se non arrivano, l’agente va aggiornato a una versione con Eagle.':
    'The agent “{nome}” is online (version {v}): the first readings arrive within a couple of minutes. If they do not, the agent must be updated to a version with Eagle.',
  '{tipo} più alti':
    'Highest {tipo}',
  'Chi legge, con quale ruolo e quale carico.':
    'Who reads, with which role and which load.',
  'Stato operativo degli oggetti monitorati, configurabile in base alle specifiche esigenze.':
    'How the monitored objects are doing, now and over the last hours. Customise it and it becomes yours.',
  'Come stanno le cose':
    'How things are',
  'Cruscotto del tenant':
    'Tenant dashboard',
  'Cruscotto di Eagle':
    'Eagle dashboard',
  'Disponibilità più bassa':
    'Lowest availability',
  'Eagle · come stanno le cose':
    'Eagle · how things are',
  'esclusa':
    'excluded',
  'Il tuo cruscotto':
    'Your dashboard',
  'Incidenti recenti':
    'Recent incidents',
  'Nessun CI ha uno SLA.':
    'No CI has an SLA.',
  'Nessuna misura ancora.':
    'No measurement yet.',
  'Parete dei CI':
    'CI wall',
  'Per ogni SLA quanti CI lo rispettano, negli ultimi 30 giorni.':
    'For each SLA how many CIs meet it, over the last 30 days.',
  'report':
    'reports',
  'Scegli un CI e un parametro in «Personalizza».':
    'Choose a CI and a parameter in “Customise”.',
  'sinottico':
    'wall view',
  'Sonde di Eagle':
    'Eagle probes',
  'tetto {n}':
    'cap {n}',
  'tutti i CI':
    'all CIs',
  'ultimi 30 giorni':
    'last 30 days',
  'esclusa da Eagle':
    'excluded from Eagle',
  'Definisce i centri di costo a cui imputare le spese di contratti e asset':
    'Who the spend is charged to: contracts and assets pick from here.',
  'Aggiungi la prima voce, o importa l’elenco da testo.':
    'Add the first entry, or import the list from text.',
  'Anagrafiche':
    'Master data',
  'Budget annuo':
    'Annual budget',
  'CAP':
    'Postal code',
  'Centri di costo':
    'Cost centers',
  'Città':
    'City',
  'Definisce direzioni, reparti e team per identificare le unità responsabili e utilizzatrici di contratti e asset':
    'Divisions, departments, teams: who owns and who uses.',
  'Definisce la collocazione fisica degli asset per sede, edificio, piano e sala':
    'Where things are: site, building, floor, room.',
  'Importa da testo':
    'Import from text',
  'Importo…':
    'Importing…',
  'In uso: si disattiva, non si toglie':
    'In use: it is deactivated, not removed',
  'Definisce le anagrafiche di riferimento della piattaforma, come centri di costo, unità organizzative e sedi. Contratti e asset utilizzano questi valori.':
    "The platform's reference master data: cost centers, organizational units, locations. Contracts and assets pick from here instead of typing free text.",
  'Nessuna voce ancora':
    'No entries yet',
  'Nuova voce':
    'New entry',
  'Paese':
    'Country',
  'Sede':
    'Location',
  'Sedi':
    'Locations',
  'Sta sotto':
    'Parent',
  'Togliere «{n}»?':
    'Remove “{n}”?',
  'Una voce per riga: codice; nome; codice del genitore (facoltativo)':
    'One entry per line: code; name; parent code (optional)',
  'Unità organizzativa':
    'Organizational unit',
  'Unità organizzative':
    'Organizational units',
  'i centri di costo, le unità organizzative e le sedi della tua organizzazione, ad albero, con importazione da testo. Contratti e asset le scelgono da un menu invece di scrivere testi liberi; una voce in uso non si toglie, si disattiva.':
    "your organization's cost centers, organizational units and locations, as a tree, with import from text. Contracts and assets pick them from a menu instead of typing free text; an entry in use is not removed, it is deactivated.",
  'nessuna (primo livello)':
    'none (top level)',
  'testo libero precedente: {v}':
    'previous free text: {v}',
  'voce non più in anagrafica':
    'entry no longer in master data',
  'vuoto = il nome':
    'empty = the name',
  '{a} create, {b} aggiornate':
    '{a} created, {b} updated',
  '{n} asset':
    '{n} assets',
  '{n} contratti':
    '{n} contracts',
  '30 gg d.f.f.m., anticipato…':
    '30 days EOM, in advance…',
  'Abbonamento a servizio':
    'Service subscription',
  'Aggiungi beni':
    'Add assets',
  'Aggiungi una voce':
    'Add a line',
  'Allegato SLA':
    'SLA annex',
  'Annuale':
    'Annual',
  'Avvisa sulle scadenze dei contratti':
    'Notify about contract deadlines',
  'Azzera i filtri':
    'Clear filters',
  'Beni coperti':
    'Covered assets',
  'Bozza':
    'Draft',
  'Cerca nei beni':
    'Search assets',
  'Cessa':
    'Terminate',
  'Cessa il contratto':
    'Terminate the contract',
  'Cessato':
    'Terminated',
  'Cessato il':
    'Terminated on',
  'Chiude il cerchio fra ciò che si è comprato e ciò che Eagle misura.':
    'Closes the loop between what was bought and what Eagle measures.',
  'Cloud (IaaS/PaaS)':
    'Cloud (IaaS/PaaS)',
  'Collegamenti, non file: il contratto firmato sta nel sistema documentale dell’organizzazione, e copiarlo qui farebbe due versioni.':
    "Links, not files: the signed contract lives in the organization's document system, and copying it here would make two versions.",
  'Connettività e telecomunicazioni':
    'Connectivity and telecommunications',
  'Consulenza':
    'Consulting',
  'Contratto firmato':
    'Signed contract',
  'Contratto in scadenza':
    'Contract about to expire',
  'Contratto scaduto':
    'Contract expired',
  'Copertura ({n})':
    'Coverage ({n})',
  'Copri {n} beni':
    'Cover {n} assets',
  'Date':
    'Dates',
  'Date che contano nei prossimi 12 mesi':
    'Dates that matter in the next 12 months',
  'Dati':
    'Details',
  'Documenti':
    'Documents',
  'Documenti ({n})':
    'Documents ({n})',
  'Duplica il contratto':
    'Duplicate the contract',
  'Duplicato da':
    'Duplicated from',
  'Durata del rinnovo (mesi)':
    'Renewal term (months)',
  'Eliminare il contratto «{ref}» con la sua copertura e il suo storico? Per un contratto finito è meglio la cessazione.':
    'Delete contract “{ref}” with its coverage and history? For a finished contract, termination is better.',
  'Email del responsabile':
    "Owner's email",
  'Fattura':
    'Invoice',
  'Garanzia estesa':
    'Extended warranty',
  'Giorni residui':
    'Days left',
  'Il pulsante «Rinnova» la usa per proporre la nuova scadenza; con il rinnovo tacito la scadenza avanza da sola.':
    'The “Renew” button uses it to propose the new end date; with tacit renewal the end date moves forward by itself.',
  'Importo':
    'Amount',
  'Imputazione':
    'Charged to',
  'In scadenza (90 g), compresi gli scaduti':
    'Expiring (90 d), including expired',
  'In vigore':
    'In force',
  'La copertura dei beni si chiude a quella data e resta visibile: «quel guasto di marzo era coperto?» avrà ancora una risposta.':
    'Asset coverage closes on that date and stays visible: “was that March failure covered?” will still have an answer.',
  'Le righe del contratto (canoni, giornate, licenze per utente): il totale senza le righe non si verifica contro la fattura.':
    'The contract lines (fees, days, per-user licenses): a total without lines cannot be checked against the invoice.',
  'Licenza software':
    'Software license',
  'Manutenzione hardware':
    'Hardware maintenance',
  'Mensile':
    'Monthly',
  'Modifica del contratto':
    'Edit contract',
  'Modifica le voci':
    'Edit lines',
  'Modificato':
    'Updated',
  'Nasce un contratto nuovo con le stesse condizioni e gli stessi beni (quelli ancora coperti); questo resta «rinnovato», collegato al nuovo. Vuoto = la scadenza si calcola dalla durata del rinnovo, il riferimento diventa «{ref}/R1».':
    'A new contract is created with the same terms and the same assets (those still covered); this one stays “renewed”, linked to the new one. Empty = the end date comes from the renewal term, the reference becomes “{ref}/R1”.',
  'Nessun bene coperto':
    'No covered assets',
  'Nessun contratto con questi filtri':
    'No contracts match these filters',
  'Nessun documento collegato':
    'No linked documents',
  'Nessuna voce':
    'No lines',
  'Noleggio o leasing':
    'Rental or leasing',
  'Nuova nota':
    'New note',
  'Nuovo riferimento':
    'New reference',
  'Offerta':
    'Quote',
  'Per centro di costo':
    'By cost center',
  'Per fornitore':
    'By vendor',
  'Per tipo':
    'By type',
  'Prezzo unitario':
    'Unit price',
  'Promemoria (giorni prima)':
    'Reminders (days before)',
  'Promemoria inviato':
    'Reminder sent',
  'Quantità':
    'Quantity',
  'Riceve i promemoria di scadenza oltre ai destinatari del tenant.':
    "Receives deadline reminders in addition to the tenant's recipients.",
  'Riferimento esterno':
    'External reference',
  'Rinnova':
    'Renew',
  'Rinnova il contratto':
    'Renew the contract',
  'Rinnovato':
    'Renewed',
  'Rinnovo di':
    'Renewal of',
  'Rinnovo tacito: la scadenza è avanzata da {a} a {b}.':
    'Tacit renewal: the end date moved from {a} to {b}.',
  'SLA di Eagle':
    'Eagle SLA',
  'Scaduto':
    'Expired',
  'Servizio gestito':
    'Managed service',
  'Sganciare «{tag}»? Per un bene uscito a una certa data è meglio impostare la fine della copertura.':
    'Detach “{tag}”? For an asset that left on a given date, setting the coverage end is better.',
  'Software come servizio (SaaS)':
    'Software as a service (SaaS)',
  'Sospeso':
    'Suspended',
  'Spesa e scadenze':
    'Spend and deadlines',
  'Storico':
    'History',
  'Supporto e assistenza':
    'Support and assistance',
  'Termini di pagamento':
    'Payment terms',
  'Totale delle voci':
    'Lines total',
  'Trimestrale':
    'Quarterly',
  'Un contratto senza beni non protegge nulla: aggiungili da qui o dalla scheda del bene.':
    "A contract without assets protects nothing: add them here or from the asset's page.",
  'Notifica le scadenze dei contratti alle soglie previste, ad esempio 90, 30 e 7 giorni prima della data di riferimento o secondo le soglie configurate nel contratto. Gli avvisi possono essere inviati anche al responsabile del contratto.':
    "One reminder per threshold (90, 30 and 7 days before the date that matters, or the contract's own), also to the contract owner.",
  'Una bozza con le stesse condizioni, voci e documenti, senza beni: per il contratto gemello con un altro fornitore o un altro reparto.':
    'A draft with the same terms, lines and documents, without assets: for the twin contract with another vendor or another department.',
  'Una tantum':
    'One-off',
  'Valore annuo':
    'Annual value',
  'Valore dichiarato':
    'Declared value',
  'Voci':
    'Lines',
  'Voci e costi':
    'Lines and costs',
  'Vuoto = {giorni}. Un avviso per soglia, prima della data che conta.':
    'Empty = {giorni}. One notice per threshold, before the date that matters.',
  'anno, mese, una tantum':
    'year, month, one-off',
  'cambio fornitore, bene dismesso, recesso…':
    'vendor change, asset retired, withdrawal…',
  'cartellino, nome, matricola':
    'tag, name, serial',
  'contratto inesistente':
    'contract does not exist',
  'cosa ha detto il fornitore, cosa si è deciso…':
    'what the vendor said, what was decided…',
  'date di copertura aggiornate':
    'coverage dates updated',
  'modificato':
    'changed',
  'nessuno definito':
    'none defined',
  'nome, telefono, email del supporto':
    'support name, phone, email',
  'ordine, pratica acquisti, id nel gestionale':
    'order, purchasing file, ERP id',
  'predefiniti':
    'defaults',
  'riferimento, nome, fornitore, responsabile…':
    'reference, name, vendor, owner…',
  'scadenza':
    'end date',
  'scaduto da {n} giorni':
    'expired {n} days ago',
  'scarto {v} rispetto al valore dichiarato':
    '{v} off the declared value',
  'scritto: {s}':
    'recorded: {s}',
  'senza centro di costo':
    'no cost center',
  'si rinnova tacitamente a questa data':
    'renews tacitly on this date',
  'ultimo giorno per disdire ({n} giorni di preavviso)':
    "last day to cancel ({n} days' notice)",
  'un bene sganciato':
    'one asset detached',
  '{n} a rinnovo tacito':
    '{n} with tacit renewal',
  '{n} beni aggiunti':
    '{n} assets added',
  '{n} beni portati':
    '{n} assets carried over',
  '{n} destinatari':
    '{n} recipients',
  '{n} giorni alla data che conta':
    '{n} days to the date that matters',
  '{n} giorni di preavviso':
    "{n} days' notice",
  '{n} in tutto, {b} bozze':
    '{n} in total, {b} drafts',
  '{n} mesi':
    '{n} months',
  '{n} validi oggi':
    '{n} valid today',
  '«Scaduto» lo decidono le date; rinnovo e cessazione hanno i loro pulsanti, che scrivono lo storico.':
    '“Expired” is decided by the dates; renewal and termination have their own buttons, which write the history.',
  '← contratto precedente':
    '← previous contract',
  'il registro dei contratti (manutenzione, supporto, licenze, SaaS, cloud, connettività…) con i beni che coprono, la spesa annualizzata per tipo, centro di costo e fornitore, e la «data che conta»: su un rinnovo tacito è l’ultimo giorno per disdire, non la scadenza. Dalla scheda si rinnova (nasce il contratto successivo, il vecchio resta collegato), si cessa, si duplica; voci, documenti e storico hanno le loro schede. I promemoria di scadenza arrivano per email e in campanella, una volta per soglia.':
    'the contract register (maintenance, support, licenses, SaaS, cloud, connectivity…) with the assets they cover, annualized spend by type, cost center and vendor, and the “date that matters”: on a tacit renewal it is the last day to cancel, not the end date. From the page you renew (the next contract is born, the old one stays linked), terminate, duplicate; lines, documents and history have their own tabs. Deadline reminders arrive by email and in the bell, once per threshold.',
  'Avvistamenti per stato':
    'Sightings by state',
  'Come stanno oggi':
    'Where they stand today',
  'Da decidere':
    'To decide',
  'Gli avvistamenti «nuovi» che hanno mosso più flussi nell’ultimo giro: chi merita un’occhiata per primo.':
    'The “new” sightings that moved the most flows in the last round: who deserves a look first.',
  'Un avvistamento «nuovo» aspetta una decisione: censirlo o ignorarlo.':
    'A “new” sighting awaits a decision: register it or ignore it.',
  'ancora «nuovi»':
    'still “new”',
  '{n} giorni con avvistamenti su {tot}':
    '{n} days with sightings out of {tot}',
  '{n} subnet sorvegliate':
    '{n} watched subnets',
  '{tot} in {g} giorni, in media {m} al giorno; il picco il {data} con {n}.':
    '{tot} in {g} days, {m} a day on average; the peak on {data} with {n}.',
  'Eagle non è attivo per questo tenant':
    'Eagle is not active for this tenant',
  'Metti in monitoraggio':
    'Start monitoring',
  'Seleziona tutti i {n} CI del filtro':
    'Select all {n} CIs matching the filter',
  'Seleziono…':
    'Selecting…',
  'Togli dal monitoraggio':
    'Stop monitoring',
  'Seleziona fino a 1.000 CI alla volta; per selezioni più mirate, restringi il filtro, ad esempio tramite tag.': 'Select up to 1,000 CIs at a time; for more targeted selections, narrow the filter, for example with a tag.',
  '{m} saltati: {elenco}':
    '{m} skipped: {elenco}',
  '{n} CI messi in monitoraggio.':
    '{n} CIs now monitored.',
  '{n} CI tolti dal monitoraggio.':
    '{n} CIs no longer monitored.',
  'Trova anche i tag: «produzione» mostra il tag ambiente:produzione con quanti CI lo portano, e apre l’elenco già filtrato.':
    'It finds tags too: “production” shows the tag environment:production with how many CIs carry it, and opens the list already filtered.',
  'Entrata':
    'Inbound',
  'Errori':
    'Errors',
  'Errori e scarti nell’intervallo':
    'Errors and discards per interval',
  'Scarti':
    'Discards',
  'Traffico (Mbit/s)':
    'Traffic (Mbit/s)',
  'Uscita':
    'Outbound',
  'Utilizzo ({unita}), con le soglie':
    'Utilization ({unita}), with thresholds',
  'picco {v}':
    'peak {v}',
  'Aggiungi un utente della console':
    'Add a console user',
  'Aggiungi un utente della console…':
    'Add a console user…',
  'I contatti da notificare possono essere utenti CENSO o esterni al sistema. Per gli utenti della console, nome ed e-mail vengono acquisiti automaticamente.':
    'The people to notify: anyone, including people who never log into CENSO. A console user is added with one choice, and name and email follow their account.',
  'utente':
    'user',
  'È l’email con cui l’utente entra in CENSO: si cambia dalla pagina Utenti.':
    'It is the email the user logs into CENSO with: change it from the Users page.',
  'Un contatto può essere un utente della console: lo aggiungi con una scelta, e nome ed email seguono la sua utenza.':
    'A contact can be a console user: add them with one choice, and name and email follow their account.',
  'Dati raccolti il {quando}: premi «Aggiorna» per interrogare di nuovo.':
    'Data collected on {quando}: press “Refresh” to query again.',
  'La pagina si apre con i dati dell’ultima lettura, con la data: «Aggiorna» interroga di nuovo i sistemi di backup attraverso la sonda.':
    'The page opens with the data of the last reading, with its date: “Refresh” queries the backup systems again through the probe.',
  'Stato dei backup rilevato dai sistemi integrati (Veeam, Vinchin, Commvault e Rubrik): esito delle esecuzioni, ultimo backup completato, conformità alle policy di protezione e informazioni operative fornite dalla piattaforma. La pagina mostra i dati dell’ultima sincronizzazione; «Aggiorna» esegue una nuova interrogazione e riallinea lo stato con i sistemi di backup.':
    'Backup status read from the integrated systems (Veeam, Vinchin, Commvault and Rubrik): outcome of the runs, last completed backup, compliance with the protection policies and operational information from the platform. The page shows the data of the last sync; «Refresh» queries again and realigns the status with the backup systems.',
  'L’ultimo aggiornamento non è riuscito ({motivo}): questi sono i dati raccolti il {quando}.':
    'The last refresh failed ({motivo}): these are the data collected on {quando}.',
  'piano {p}':
    'plan {p}',
  'Interrogo i sistemi in sottofondo: la pagina si aggiorna da sola appena arrivano i dati nuovi.':
    'Querying the systems in the background: the page refreshes by itself as soon as new data arrives.',
  'Intanto vedi i dati raccolti il {quando}.':
    'Meanwhile you see the data collected on {quando}.',
  'Leggi le ottiche dal firewall':
    'Read optics from the firewall',
  'Leggo…':
    'Reading…',
  'Nella tabella di routing del CI, accanto alla metrica c’è la «Distance»: la distanza amministrativa (Cisco/Palo Alto) o la preference (Huawei), con cui si sceglie fra due rotte per la stessa destinazione.': 'In the CI routing table, next to the metric there is «Distance»: the administrative distance (Cisco/Palo Alto) or the preference (Huawei), used to choose between two routes to the same destination.',
  'Sui router e switch Cisco e Huawei l’inventario SSH legge anche la tabella di routing dalla CLI (show ip route, display ip routing-table): utile dove SNMP non la dà.':
    'On Cisco and Huawei routers and switches the SSH inventory also reads the routing table from the CLI (show ip route, display ip routing-table): useful where SNMP does not provide it.',
  '{n} ottiche lette dal firewall, {c} CI creati.':
    '{n} optics read from the firewall, {c} CIs created.',
  'Pool rinominato in «{nome}».':
    'Pool renamed to “{nome}”.',
  'Salva il nome':
    'Save the name',
  'Rinomina':
    'Rename',
  'Le pianificazioni e i job che usano questo pool lo seguono per identità: il nome nuovo compare dalla prossima esecuzione.':
    'Schedules and jobs that use this pool follow it by identity: the new name shows up from the next run.',
  'Dalla scheda di un pool lo rinomini e ne cambi la descrizione: le pianificazioni che lo usano lo seguono.':
    "From a pool's page you rename it and change its description: the schedules that use it follow.",
  'seriale {s}':
    'serial {s}',
  'Nessun firewall corrisponde a questo CI: scegli quale.':
    'No firewall matches this CI: choose one.',
  'Il CI ha seriale {s} e si presenta come {ids}.':
    'The CI has serial {s} and goes by {ids}.',
  'Il CI non ha un seriale in inventario e si presenta come {ids}.':
    'The CI has no serial in the inventory and goes by {ids}.',
  '— scegli il firewall —':
    '— choose the firewall —',
  'Dai al job un titolo e una descrizione: con decine di job al giorno, tipo e ambito non dicono perché è stato lanciato. Si correggono anche dopo, dal rapporto.':
    'Give the job a title and a description: with dozens of jobs a day, type and scope do not say why it was launched. Both can be corrected later, from the report.',
  'Modifica titolo':
    'Edit title',
  'Senza titolo':
    'Untitled',
  'facoltativo: per riconoscere il job nell’elenco':
    'optional: to recognise the job in the list',
  'facoltativo: perché, per chi, cosa aspettarsi':
    'optional: why, for whom, what to expect',
  'nativa {n}':
    'native {n}',
  'trunk':
    'trunk',
  'Fra le interfacce di un apparato di rete la colonna VLAN dice quali VLAN passano su ogni porta: il numero per una porta d’accesso, «trunk» con l’elenco e la nativa per un trunk. La legge la scansione SNMP approfondita.':
    "Among a network device's interfaces, the VLAN column says which VLANs go through each port: the number for an access port, “trunk” with the list and the native VLAN for a trunk. The deep SNMP scan reads it.",
  'Abilita Cost Explorer sull’account: console AWS → Billing and Cost Management → Cost Explorer. La prima attivazione richiede fino a 24 ore, e ogni lettura costa circa 0,01 $.':
    'Enable Cost Explorer on the account: AWS console → Billing and Cost Management → Cost Explorer. The first activation takes up to 24 hours, and each read costs about $0.01.',
  'Aggiungi il permesso ce:GetCostAndUsage alla policy del ruolo o dell’utenza che CENSO usa (la policy completa è nella scheda della credenziale AWS e in docs/operations/aws-permessi.md).':
    'Add the ce:GetCostAndUsage permission to the policy of the role or user CENSO uses (the full policy is in the AWS credential page and in docs/operations/aws-permessi.md).',
  'Apri il rapporto del job: la «Diagnosi AWS» in cima dice se il permesso o Cost Explorer mancano, e cosa fare.':
    'Open the job report: the “AWS diagnosis” at the top says whether the permission or Cost Explorer is missing, and what to do.',
  'Assegna al service principal di CENSO il ruolo «Cost Management Reader» sulla sottoscrizione (Access control (IAM) → Add role assignment).':
    "Assign the “Cost Management Reader” role to CENSO's service principal on the subscription (Access control (IAM) → Add role assignment).",
  'Come si attiva':
    'How to enable it',
  'Google Cloud e Oracle Cloud non sono ancora coperti: su Google i costi reali passano dall’export della fatturazione in BigQuery, su Oracle dalla Usage API.':
    'Google Cloud and Oracle Cloud are not covered yet: on Google the real costs go through the billing export to BigQuery, on Oracle through the Usage API.',
  'A ogni job rilegge dal primo del mese scorso a oggi e sostituisce i giorni riletti, così le correzioni del provider entrano da sole. Un job senza costi (permesso mancante, servizio spento) non cancella la storia.':
    "Each job re-reads from the first of last month to today and replaces the re-read days, so the provider's corrections come in by themselves. A job without costs (missing permission, service off) does not erase the history.",
  'Accendi «Recupera costi» nella credenziale AWS, poi lancia il job di discovery cloud dell’account o aspetta la pianificazione: rilegge dal primo del mese scorso a oggi, così le correzioni del provider entrano da sole.':
    "Turn on “Collect costs” in the AWS credential, then run the account's cloud discovery job or wait for the schedule: it re-reads from the first of last month to today, so the provider's corrections come in by themselves.",
  'Accendi «Recupera costi» nella credenziale Azure e lancia il job di discovery cloud della sottoscrizione: i costi entrano con l’inventario. Se il ruolo manca, il rapporto del job lo dice.':
    "Turn on “Collect costs” in the Azure credential and run the subscription's cloud discovery job: costs come in with the inventory. If the role is missing, the job report says so.",
  'Crea la credenziale del provider e lancia il job cloud: l’account compare da solo. Per il costo del mese accendi «Recupera costi» nella credenziale e fai in modo che il provider lasci leggere i costi (Cost Explorer abilitato e ce:GetCostAndUsage su AWS, ruolo Cost Management Reader su Azure). La pagina Costi Cloud spiega i passi.':
    "Create the provider credential and run the cloud job: the account shows up by itself. For the month's cost turn on “Collect costs” in the credential and make sure the provider lets the costs be read (Cost Explorer enabled and ce:GetCostAndUsage on AWS, the Cost Management Reader role on Azure). The Cloud Costs page explains the steps.",
  'I costi arrivano con i job di discovery cloud della credenziale che ha la spunta «Recupera costi», e serve che il provider li lasci leggere.':
    'Costs come in with the cloud discovery jobs of a credential that has “Collect costs” ticked, and the provider must let them be read.',
  "I costi dell'account per giorno, servizio e regione, nella pagina Costi Cloud. Serve un permesso in più: vedi «Cosa fa?».":
    "The account's costs per day, service and region, in the Cloud Costs page. One more permission is needed: see “What does it do?”.",
  'Legge AWS Cost Explorer (GetCostAndUsage): il costo non miscelato per giorno, servizio e regione.':
    'Reads AWS Cost Explorer (GetCostAndUsage): the unblended cost per day, service and region.',
  'Legge Azure Cost Management (query dei costi effettivi): il costo per giorno, servizio e regione della sottoscrizione.':
    "Reads Azure Cost Management (actual cost query): the subscription's cost per day, service and region.",
  'Per attivarlo: accendi «Recupera costi» nella credenziale cloud; su AWS abilita Cost Explorer da Billing (la prima volta fino a 24 ore) e aggiungi il permesso alla policy di CENSO, su Azure assegna il ruolo al service principal; poi lancia il job cloud. La scheda «Come si attiva» in cima alla pagina ha i passi, e la Diagnosi AWS nel rapporto del job dice cosa manca.':
    "To enable it: turn on “Collect costs” in the cloud credential; on AWS enable Cost Explorer from Billing (up to 24 hours the first time) and add the permission to CENSO's policy, on Azure assign the role to the service principal; then run the cloud job. The “How to enable it” card at the top of the page has the steps, and the AWS diagnosis in the job report says what is missing.",
  'Recupera costi':
    'Collect costs',
  "Serve Cost Explorer abilitato sull'account (Billing and Cost Management → Cost Explorer; la prima attivazione richiede fino a 24 ore) e il permesso ce:GetCostAndUsage nella policy di CENSO. Ogni lettura costa circa 0,01 $: una o poche per job.":
    "Cost Explorer must be enabled on the account (Billing and Cost Management → Cost Explorer; the first activation takes up to 24 hours) and the ce:GetCostAndUsage permission must be in CENSO's policy. Each read costs about $0.01: one or a few per job.",
  'Serve il ruolo «Cost Management Reader» al service principal sulla sottoscrizione (Access control (IAM) → Add role assignment).':
    'The service principal needs the “Cost Management Reader” role on the subscription (Access control (IAM) → Add role assignment).',
  'Spenta, il rapporto del job lo ricorda con una riga e la pagina Costi Cloud resta senza questo account.':
    'Off, the job report reminds you with one line and the Cloud Costs page stays without this account.',
  'CENSO, l’assistente':
    'CENSO, the assistant',
  'Chiedi a CENSO':
    'Ask CENSO',
  'Ciao, sono CENSO. Cerchi un apparato, un indirizzo o un seriale?':
    "Hi, I'm CENSO. Looking for a device, an address or a serial number?",
  'Ciao, sono CENSO. Cerchi un rischio, una vulnerabilità o una macchina esposta?':
    "Hi, I'm CENSO. Looking for a finding, a vulnerability or an exposed machine?",
  'Ciao, sono CENSO. Cosa vuoi sapere del tuo inventario?':
    "Hi, I'm CENSO. What do you want to know about your inventory?",
  'Ciao, sono CENSO. Vuoi sapere cosa non sta rispondendo?':
    "Hi, I'm CENSO. Want to know what is not responding?",
  'Ho trovato {n} cose. Scegli, o scrivi ancora.':
    'I found {n} things. Pick one, or keep typing.',
  'La tua domanda':
    'Your question',
  'Non trovo niente con «{q}». Prova con un nome, un indirizzo, un MAC o un seriale.':
    'Nothing found for “{q}”. Try a name, an address, a MAC or a serial number.',
  'Per ora rispondo cercando nell’inventario. Con il modulo IA capirò le domande in italiano.':
    'For now I answer by searching the inventory. With the AI module I will understand questions in plain language.',
  'Scrivi qui…':
    'Type here…',
  'Un attimo, guardo nell’inventario…':
    "One moment, I'm looking in the inventory…",
  'firewall':
    'firewall',
  'switch':
    'switch',
  'uplink':
    'uplink',
  'windows server':
    'windows server',
  '… e altri: apri l’elenco per vederli tutti':
    '… and more: open the list to see them all',
  'Chiedi a CENSO (trascina per spostarlo)':
    'Ask CENSO (drag to move it)',
  'Carica l’ambiente di esempio':
    'Load the sample environment',
  'Caricato il {quando}.':
    'Loaded on {quando}.',
  'Dati di esempio':
    'Sample data',
  'I dati di esempio si caricano solo in un tenant senza CI: qui ci sono già dati veri.':
    'Sample data can only be loaded into a tenant without CIs: there is real data here already.',
  'Il tenant è vuoto. Puoi caricare l’ambiente di esempio per vedere la piattaforma popolata: una sede, i suoi server, un account cloud, rischi e contratti.':
    'The tenant is empty. You can load the sample environment to see the platform populated: a site, its servers, a cloud account, findings and contracts.',
  'Passa all’ambiente vuoto':
    'Switch to the empty environment',
  'Quando sei pronto a collegare le tue sonde, passa all’ambiente vuoto: si toglie tutto ciò che è di esempio e solo quello. Non si torna indietro, ma i dati veri arrivano con la prima scansione.':
    'When you are ready to connect your probes, switch to the empty environment: everything that is sample data is removed, and only that. There is no way back, but real data comes with the first scan.',
  'Stai guardando l’ambiente di esempio di CENSO: una sede con la sua rete, due host VMware con le macchine virtuali, un server fisico, stampanti, UPS, storage, un account AWS con i costi, rischi di sicurezza e contratti.':
    "You are looking at CENSO's sample environment: a site with its network, two VMware hosts with virtual machines, a physical server, printers, UPS, storage, an AWS account with costs, security findings and contracts.",
  'Stai guardando l’ambiente di esempio di CENSO: una sede, i suoi server e un account cloud inventati per capire la piattaforma.':
    "You are looking at CENSO's sample environment: a site, its servers and a cloud account made up to understand the platform.",
  'Togliere tutti i dati di esempio? I dati veri, se ci sono, restano.':
    'Remove all sample data? Real data, if any, stays.',
  'Un attimo…':
    'One moment…',
  'Un tenant in prova nasce con l’ambiente di esempio: una sede con rete, server, VM, stampanti, UPS, storage, un account AWS con i costi, rischi e contratti, passati dalla stessa strada dei dati veri. Il banner in cima lo ricorda; da «Dati di esempio» in Impostazioni si passa all’ambiente vuoto quando si collegano le sonde: via tutto ciò che è di esempio, e solo quello.':
    'A trial tenant is born with the sample environment: a site with network, servers, VMs, printers, UPS, storage, an AWS account with costs, findings and contracts, brought in the same way as real data. The banner at the top reminds you; from “Sample data” in Settings you switch to the empty environment when you connect the probes: everything that is sample data goes, and only that.',
  '{n} CI di esempio su {tot}.':
    '{n} sample CIs out of {tot}.',
  'Assistente «Chiedi a CENSO»':
    '“Ask CENSO” assistant',
  "CENSO chiama POST <indirizzo>/v1/chat/completions con gli strumenti (function calling): il modello deve saperli usare. Vanno bene Qwen3 32B e Mistral Small 3.2 su una GPU da 24–48 GB per spiegazioni e domande semplici; gpt-oss-120b o Qwen3 235B su 80 GB per l'assistente completo.":
    'CENSO calls POST <address>/v1/chat/completions with tools (function calling): the model must know how to use them. Qwen3 32B and Mistral Small 3.2 on a 24–48 GB GPU are fine for explanations and simple questions; gpt-oss-120b or Qwen3 235B on 80 GB for the full assistant.',
  'Ci sto guardando…':
    'Let me look…',
  'Claude (Anthropic) — assistente':
    'Claude (Anthropic) — assistant',
  'Claude via API':
    'Claude via API',
  'Con vLLM: --enable-auto-tool-choice e il parser degli strumenti del modello. Con Ollama: un modello che dichiara «tools».':
    "With vLLM: --enable-auto-tool-choice and the model's tool parser. With Ollama: a model that declares “tools”.",
  'Cosa non sta rispondendo adesso?':
    'What is not responding right now?',
  'Cosa stacco se spengo il core?':
    'What do I cut off if I switch off the core?',
  'Cosa è esposto su Internet?':
    'What is exposed to the Internet?',
  'Ecco. Vuoi sapere altro?':
    'Here it is. Anything else?',
  'Fai una domanda sul tuo inventario…':
    'Ask a question about your inventory…',
  'Ho guardato: {cosa}':
    'I looked at: {cosa}',
  'Il modello da usare. Vuoto: claude-sonnet-5, il punto di equilibrio fra qualità e costo per un assistente con gli strumenti. claude-haiku-4-5-20251001 costa meno e basta per le domande semplici; claude-fable-5-1 è il più capace.':
    'The model to use. Empty: claude-sonnet-5, the balance between quality and cost for an assistant with tools. claude-haiku-4-5-20251001 costs less and is enough for simple questions; claude-fable-5-1 is the most capable.',
  'Il modello non ha risposto: {perche}':
    'The model did not answer: {perche}',
  'L’assistente può utilizzare Claude (Anthropic) tramite API oppure Modello interno tramite endpoint compatibile OpenAI, anche ospitato nella rete del cliente e raggiungibile tramite sonda. Accede ai dati in sola lettura e nel rispetto delle autorizzazioni dell’utente. Non può modificare configurazioni, eseguire operazioni amministrative o accedere alle credenziali archiviate.':
    "The model is chosen among the credentials: “Claude (Anthropic)” via API, or “Internal model” for an OpenAI-compatible service (vLLM, Ollama…) even inside the customer's network, through a probe. The assistant only reads: tools are reads with the permissions of whoever asks, never the decryption of credentials.",
  'Il nome del modello come lo conosce il servizio (per esempio «qwen3:32b» su Ollama, il nome del repository su vLLM).':
    'The model name as the service knows it (for example “qwen3:32b” on Ollama, the repository name on vLLM).',
  'Impostazioni salvate.':
    'Settings saved.',
  'In basso a destra c’è la faccina di CENSO: aprila e fa una domanda. Con il modulo «Assistente» e un modello configurato risponde in italiano leggendo il tuo inventario — «cosa stacco se spengo il core?», «quali contratti scadono?» — e ogni risposta porta i link per verificare e dice cosa ha guardato. Senza, cerca nell’inventario: un nome, un indirizzo, un MAC, un seriale. Si trascina dove non dà fastidio.':
    "Bottom right there is CENSO's face: open it and it asks a question. With the “Assistant” module and a configured model it answers in plain language by reading your inventory — “what do I cut off if I switch off the core?”, “which contracts expire?” — and every answer carries the links to verify and says what it looked at. Without, it searches the inventory: a name, an address, a MAC, a serial number. Drag it where it does not get in the way.",
  'Indirizzo del servizio':
    'Service address',
  "Indirizzo dell'API":
    'API address',
  "L'indirizzo di vLLM, Ollama (porta 11434), LM Studio, LocalAI o di un servizio ospitato che parla /v1/chat/completions. Se sta nella rete del cliente, scegli la sonda in «Da dove si parla».":
    "The address of vLLM, Ollama (port 11434), LM Studio, LocalAI or a hosted service that speaks /v1/chat/completions. If it lives in the customer's network, pick the probe in “Where to call from”.",
  "La chiave API di Anthropic (console.anthropic.com → API keys). Resta cifrata con la chiave del tenant e non lascia mai il Control Plane se non verso l'API.":
    "The Anthropic API key (console.anthropic.com → API keys). It stays encrypted with the tenant's key and never leaves the Control Plane except towards the API.",
  "La chiave che il servizio si aspetta nell'header Authorization: Bearer.":
    'The key the service expects in the Authorization: Bearer header.',
  'La quota di oggi è finita: riprova domani, o alza la quota nelle impostazioni.':
    "Today's quota is used up: try again tomorrow, or raise the quota in the settings.",
  'L’assistente si configura fra le credenziali: «Claude (Anthropic)» con la chiave API, oppure «Modello interno» per un servizio OpenAI-compatibile (vLLM, Ollama…), anche nella rete del cliente attraverso una sonda. Nella card «Assistente» di Impostazioni si vede con cosa risponde, quanti token ha usato oggi, la quota e il mascheramento degli indirizzi verso un fornitore esterno. Legge soltanto, con i permessi di chi chiede: mai le credenziali.':
    "The assistant is set up among the credentials: “Claude (Anthropic)” with the API key, or “Internal model” for an OpenAI-compatible service (vLLM, Ollama…), even inside the customer's network through a probe. The “Assistant” card in Settings shows what it answers with, how many tokens it used today, the quota and the masking of addresses towards an external provider. It only reads, with the permissions of whoever asks: never the credentials.",
  'Con modelli esterni, gli indirizzi IP vengono anonimizzati prima dell’elaborazione. Le informazioni originali non lasciano il tenant e vengono ripristinate dal sistema solo al momento della presentazione della risposta.':
    'Mask IP addresses towards an external provider (Claude): the model sees placeholders, the answer comes back with the real addresses.',
  'Modello interno (OpenAI-compatibile) — assistente':
    'Internal model (OpenAI-compatible) — assistant',
  'Nessun modello configurato: l’assistente risponde utilizzando esclusivamente la ricerca nell’inventario.':
    'No model configured: the face answers with the inventory search.',
  'Nessun modello configurato: serve una credenziale «Claude» o «Modello interno».':
    'No model configured: a “Claude” or “Internal model” credential is needed.',
  'Non posso rispondere adesso.':
    'I cannot answer right now.',
  'Per ora rispondo cercando nell’inventario. Le domande in italiano sono nel modulo aggiuntivo «Assistente».':
    'For now I answer by searching the inventory. Plain-language questions are in the “Assistant” add-on module.',
  'Per ora rispondo cercando nell’inventario. Per le domande in italiano aggiungi una credenziale «Claude» o «Modello interno» fra le credenziali.':
    'For now I answer by searching the inventory. For plain-language questions add a “Claude” or “Internal model” credential among the credentials.',
  'Per ora rispondo cercando nell’inventario. Per le domande in italiano serve che chi amministra il tenant configuri un modello.':
    'For now I answer by searching the inventory. For plain-language questions the tenant administrator must configure a model.',
  'Quali CI hanno la CPU in attenzione?':
    'Which CIs have the CPU in warning?',
  'Quali contratti scadono entro 90 giorni?':
    'Which contracts expire within 90 days?',
  'Quali rischi critici ho aperti?':
    'Which critical findings are open?',
  'Quanti server Windows ho e quali?':
    'How many Windows servers do I have, and which?',
  'Quanto mi costa il cloud questo mese?':
    'How much is the cloud costing me this month?',
  'Quota di token al giorno':
    'Token quota per day',
  'Risponde con «{nome}» ({fornitore}). Oggi ha usato {usati} token su {quota}.':
    'It answers with “{nome}” ({fornitore}). Today it used {usati} tokens out of {quota}.',
  'Rispondo con ciò che leggo nel tuo inventario, con i link per verificare. Modello: {m}.':
    'I answer with what I read in your inventory, with links to verify. Model: {m}.',
  'Senza chiave':
    'No key',
  'Spenta solo per un servizio interno con certificato autofirmato.':
    'Off only for an internal service with a self-signed certificate.',
  'Un servizio interno che non chiede chiavi: lo protegge la rete, non CENSO.':
    'An internal service that asks for no key: the network protects it, not CENSO.',
  'Verifica il certificato TLS':
    'Verify the TLS certificate',
  "Vuoto: l'API di Anthropic. Si cambia solo con un gateway aziendale che la rilancia con lo stesso dialetto.":
    "Empty: Anthropic's API. Change it only with a corporate gateway that relays it with the same dialect.",
  'claude-sonnet-5':
    'claude-sonnet-5',
  'http://10.0.0.5:8000':
    'http://10.0.0.5:8000',
  'https://api.anthropic.com':
    'https://api.anthropic.com',
  'modello interno':
    'internal model',
  'qwen3:32b':
    'qwen3:32b',
  'Filtra gli host':
    'Filter hosts',
  'Versione non rilevata':
    'Version not detected',
  'filtra host per nome o indirizzo…':
    'filter hosts by name or address…',
  '{n} host in {c} cluster: apri un cluster o filtra':
    '{n} hosts in {c} clusters: open a cluster or filter',
  '{n} host su {tot}':
    '{n} hosts out of {tot}',
  'Vuota: 22. Vale per gli apparati senza porta nell’indirizzo.':
    'Empty: 22. Applies to devices without a port in the address.',
  'Excel':
    'Excel',
  'PDF':
    'PDF',
  'Ordina per questa colonna':
    'Sort by this column',
  'Scarica l’elenco con i filtri in vigore in Excel':
    'Download the list with the current filters as Excel',
  'Scarica l’elenco con i filtri in vigore in PDF':
    'Download the list with the current filters as PDF',
  'Con «Excel» e «PDF» scarichi l’elenco così com’è filtrato, con le colonne scelte e nell’ordine che vedi; cliccando l’intestazione di una colonna ordini per quella, di nuovo per invertire, su tutte le pagine.':
    'With “Excel” and “PDF” you download the list as filtered, with the chosen columns and in the order you see; clicking a column header sorts by it, again to reverse, across all pages.',
  'Chi amministra il tenant può passare all’ambiente vuoto.':
    'The tenant administrator can switch to the empty environment.',
  'L’ambiente è vuoto. Vuoi vedere la piattaforma popolata? Carica l’ambiente di esempio: una sede con rete, server, VM, un account cloud, rischi e contratti. Si toglie con un clic quando colleghi le tue sonde.':
    'The environment is empty. Want to see the platform populated? Load the sample environment: a site with network, servers, VMs, a cloud account, findings and contracts. One click removes it when you connect your probes.',
  'No, parto vuoto':
    "No, I'll start empty",
  'L’ambiente di esempio è riservato ai tenant in prova.':
    'The sample environment is reserved for trial tenants.',
  '192.0.2.1:4118':
    '192.0.2.1:4118',
  'status':
    'status',
  'WatchGuard Firebox (firewall)':
    'WatchGuard Firebox (firewall)',
  'Indirizzo della CLI (SSH)':
    'CLI address (SSH)',
  'Utente e password della CLI':
    'CLI user and password',
  'Indirizzo della gestione del Firebox e porta della Fireware CLI (4118 di serie; senza porta si assume 4118). La policy «WatchGuard» del firewall deve ammettere la sonda.':
    'Management address of the Firebox and port of the Fireware CLI (4118 by default; without a port, 4118 is assumed). The firewall’s «WatchGuard» policy must allow the probe.',
  'CENSO parla con il Firebox come farebbe un amministratore dalla riga di comando (SSH): «sysinfo», «who», «show connection count», «show vpn-status», «show log-cache» per il Traffic Monitor. Solo comandi di lettura, scelti da un elenco fisso nella sonda: nessun comando arriva dal Control Plane come testo.':
    'CENSO talks to the Firebox the way an administrator would from the command line (SSH): «sysinfo», «who», «show connection count», «show vpn-status», «show log-cache» for the Traffic Monitor. Read-only commands only, chosen from a fixed list in the probe: no command reaches the probe from the Control Plane as text.',
  "Sul Firebox: Fireware Web UI → System → Global Settings → «Enable command line interface (SSH)»; la policy «WatchGuard» (porta 4118) deve accettare l'indirizzo della sonda.":
    'On the Firebox: Fireware Web UI → System → Global Settings → «Enable command line interface (SSH)»; the «WatchGuard» policy (port 4118) must accept the probe’s address.',
  "Per il backup della configurazione la sonda fa eseguire «export config» verso un ricevitore FTP che apre su una porta alta per pochi secondi: il Firebox deve poter aprire una connessione verso la sonda, e il comando richiede un'utenza in lettura/scrittura (ruolo Device Administrator). Per la sola lettura basta l'utenza «status» (Device Monitor).":
    'For the configuration backup the probe has the Firebox run «export config» towards an FTP receiver it opens on a high port for a few seconds: the Firebox must be able to open a connection to the probe, and the command needs a read/write user (Device Administrator role). For reading alone the «status» user (Device Monitor) is enough.',
  "«status» (Device Monitor, sola lettura) per interrogare; «admin» o un utente Device Administrator solo se serve il backup della configurazione (l'export non è concesso in sola lettura).":
    '«status» (Device Monitor, read-only) to query; «admin» or a Device Administrator user only if the configuration backup is needed (export is not allowed read-only).',
  'La VPN si legge in tempo reale dai firewall Palo Alto, FortiGate e WatchGuard configurati in Integrazioni.':
    'VPN is read in real time from the Palo Alto, FortiGate and WatchGuard firewalls configured under Integrations.',
  'La tabella di routing dell’apparato. Dai firewall Palo Alto, FortiGate e WatchGuard si legge dal firewall con «Sincronizza dal firewall» (Palo Alto non la espone via SNMP).':
    'The device’s routing table. From Palo Alto, FortiGate and WatchGuard firewalls it is read from the firewall with «Sync from firewall» (Palo Alto does not expose it via SNMP).',
  'Nessun componente ancora: le ottiche di un firewall Palo Alto o FortiGate si leggono via API con il pulsante qui sopra (la CLI del WatchGuard non le elenca).':
    'No components yet: the optics of a Palo Alto or FortiGate firewall are read via API with the button above (the WatchGuard CLI does not list them).',
  'Per porte e vicini scansiona gli apparati via SNMP; per le rotte accendi l’opzione «Tabelle di routing» sulla scansione. Per i firewall Palo Alto (che le rotte non le danno via SNMP), FortiGate e WatchGuard, sulla sezione Routing del CI c’è «Sincronizza dal firewall».':
    'For ports and neighbours scan the devices via SNMP; for routes turn on the «Routing tables» option on the scan. For Palo Alto (which do not give routes via SNMP), FortiGate and WatchGuard firewalls, the CI’s Routing section has «Sync from firewall».',
  'VPN: le sessioni aperte (GlobalProtect sui Palo Alto, SSL VPN e IPsec sui FortiGate, Mobile VPN L2TP sui WatchGuard), chieste ai firewall al momento dell’apertura — postazione, IP assegnato, IP pubblico e da quando.':
    'VPN: the open sessions (GlobalProtect on Palo Alto, SSL VPN and IPsec on FortiGate, Mobile VPN L2TP on WatchGuard), asked to the firewalls when the page opens — workstation, assigned IP, public IP and since when.',
  'i firewall Palo Alto, Fortinet FortiGate e WatchGuard Firebox configurati, anche insieme: cruscotto (sistema, sessioni, utenti VPN, amministratori collegati), minacce, web filter, classifiche e interrogazione dei log. Sulla scheda di un CI, il traffico da e verso quell’host visto dal firewall. La guida per preparare il FortiGate (utente API in sola lettura, log) e il WatchGuard (CLI SSH sulla 4118, utenza «status» per leggere, «admin» solo per il backup) è nel modulo della credenziale.':
    'the Palo Alto, Fortinet FortiGate and WatchGuard Firebox firewalls configured, even together: dashboard (system, sessions, VPN users, connected administrators), threats, web filter, rankings and log queries. On a CI’s page, the traffic to and from that host as seen by the firewall. The guide to prepare the FortiGate (read-only API user, logs) and the WatchGuard (SSH CLI on 4118, «status» user to read, «admin» only for the backup) is in the credential form.',
  'Il WatchGuard non ha una REST API: CENSO legge la Fireware CLI dalla sonda con comandi di sola lettura scelti da un elenco fisso (sysinfo, who, show log-cache…), e il Traffic Monitor in memoria fa da registro del traffico. Il backup della configurazione lo esporta il Firebox stesso via FTP verso la sonda, su una porta alta aperta per pochi secondi: serve un utente Device Administrator e che il firewall raggiunga la sonda.':
    'The WatchGuard has no REST API: CENSO reads the Fireware CLI from the probe with read-only commands chosen from a fixed list (sysinfo, who, show log-cache…), and the in-memory Traffic Monitor serves as the traffic log. The configuration backup is exported by the Firebox itself via FTP to the probe, on a high port open for a few seconds: it needs a Device Administrator user and the firewall must reach the probe.',
  'Famiglie: Cisco, Huawei, Aruba/ProCurve, HP Comware, Extreme, Juniper, MikroTik, Fortinet, Palo Alto, Lantronix, Aethra e WatchGuard (il Firebox esporta la configurazione via FTP verso la sonda).':
    'Families: Cisco, Huawei, Aruba/ProCurve, HP Comware, Extreme, Juniper, MikroTik, Fortinet, Palo Alto, Lantronix, Aethra and WatchGuard (the Firebox exports its configuration via FTP to the probe).',
  'questo nodo: {ruolo}':
    'this node: {ruolo}',
  'Apri la finestra della porta':
    'Open the port window',
  'Cataloga':
    'Catalogue',
  'Cataloga una segnalazione o aggiungi una voce a mano.':
    'Catalogue a report or add an entry by hand.',
  'Cataloga {mac}':
    'Catalogue {mac}',
  'Catalogo MAC':
    'MAC catalogue',
  'Costruttore (IEEE)':
    'Manufacturer (IEEE)',
  'Eliminare la voce {p}?':
    'Delete the entry {p}?',
  'I clienti segnalano un MAC dalla finestra della porta di uno switch.':
    'Customers report a MAC from the port window of a switch.',
  'Il catalogo di CENSO integra il registro IEEE: un prefisso MAC (OUI, blocco o indirizzo intero) → costruttore, modello, categoria. Vale per tutti i clienti. Le segnalazioni arrivano dalla finestra della porta di uno switch: catalogarle chiude la segnalazione e insegna il riconoscimento a tutti.':
    'The CENSO catalogue complements the IEEE registry: a MAC prefix (OUI, block or full address) → manufacturer, model, category. It applies to every customer. Reports come from the port window of a switch: cataloguing one closes it and teaches the recognition to everyone.',
  'Il costruttore produce apparecchi elettromedicali: è un indizio sul costruttore, non sul dispositivo.':
    'The manufacturer makes medical devices: it is a clue about the manufacturer, not about the device.',
  'Indirizzi MAC visti sulla porta':
    'MAC addresses seen on the port',
  'Invia la segnalazione':
    'Send the report',
  'Invio…':
    'Sending…',
  'Modifica {p}':
    'Edit {p}',
  'Nessun indirizzo.':
    'No address.',
  'Nessuna segnalazione':
    'No reports',
  'Non vista all’ultima scansione':
    'Not seen at the last scan',
  'Nota di chi segnala':
    'Reporter’s note',
  'Nuova voce del catalogo':
    'New catalogue entry',
  'OUI (6 esadecimali) per tutta la linea, blocco MA-M (7) o MA-S (9), oppure l’indirizzo intero (12) per quel solo dispositivo. Vince il prefisso più lungo.':
    'OUI (6 hex digits) for the whole line, MA-M (7) or MA-S (9) block, or the full address (12) for that single device. The longest prefix wins.',
  'Porta {p}':
    'Port {p}',
  'Porta {p}: cosa vede':
    'Port {p}: what it sees',
  'Porte degli switch (dove si vede il MAC)':
    'Switch ports (where the MAC is seen)',
  'Prefissi MAC → costruttore, modello, categoria; le segnalazioni dei clienti da catalogare':
    'MAC prefixes → manufacturer, model, category; customers’ reports to catalogue',
  'Prefisso MAC':
    'MAC prefix',
  'Scarta':
    'Dismiss',
  'Scrivi che cos’è, se lo sai (marca, modello, reparto): chi cataloga non vede la tua rete. Una volta catalogato, CENSO lo riconoscerà ovunque.':
    'Write what it is, if you know (brand, model, ward): whoever catalogues it cannot see your network. Once catalogued, CENSO will recognise it everywhere.',
  'Segnala':
    'Report',
  'Segnala {mac} all’amministrazione di CENSO':
    'Report {mac} to the CENSO administration',
  'Segnalazione inviata: quando l’amministrazione di CENSO la cataloga, il riconoscimento vale per tutti.':
    'Report sent: once the CENSO administration catalogues it, the recognition applies to everyone.',
  'Segnalazioni ({n})':
    'Reports ({n})',
  'Ultima vista':
    'Last seen',
  'Voci del catalogo ({n})':
    'Catalogue entries ({n})',
  'aperta':
    'open',
  'aperte':
    'open',
  'catalogata':
    'catalogued',
  'catalogate':
    'catalogued',
  'catalogo CENSO':
    'CENSO catalogue',
  'da segnalazione':
    'from a report',
  'elettromedicale':
    'medical device',
  'es. elettromedicale (monitor paziente), stampante, telefono IP':
    'e.g. medical device (patient monitor), printer, IP phone',
  'es. pompa di infusione B. Braun Infusomat, sala 2':
    'e.g. B. Braun Infusomat infusion pump, room 2',
  'non più vista':
    'no longer seen',
  'scartata':
    'dismissed',
  'scartate':
    'dismissed',
  'usa l’indirizzo intero {mac}':
    'use the full address {mac}',
  '{n} indirizzi all’ultima scansione, {m} in tutto (le voci non più viste restano per novanta giorni).':
    '{n} addresses at the last scan, {m} in total (entries no longer seen stay for ninety days).',
  'È un apparecchio elettromedicale':
    'It is a medical device',
  'Sugli switch letti con l’opzione «Tabella MAC», nelle interfacce la colonna «Dispositivi collegati» dice quanti indirizzi ogni porta ha imparato e apre la finestra della porta (un puntino giallo accanto al numero = sulla porta c’è almeno un elettromedicale): MAC, IP se in CMDB, costruttore (registro IEEE e catalogo CENSO), CI abbinato con modello, VLAN, ultima vista; i costruttori di elettromedicali sono evidenziati. Un MAC che CENSO non riconosce si segnala da lì con «Segnala»: l’amministrazione di CENSO lo cataloga e da allora lo riconoscono tutti.':
    'On switches scanned with the «MAC table» option, the «Connected devices» column in the interfaces says how many addresses each port has learned and opens the port window (a yellow dot next to the number = at least one medical device is on the port): MAC, IP if in the CMDB, manufacturer (IEEE registry and CENSO catalogue), matched CI with model, VLAN, last seen; medical-device manufacturers are highlighted. A MAC that CENSO does not recognise is reported from there with «Report»: the CENSO administration catalogues it and from then on everyone recognises it.',
  'Un indirizzo MAC, in qualunque grafia (aa:bb:cc, AA-BB-CC, aabb.ccdd) e anche solo il prefisso, trova su quale switch e su quale porta lo si vede: il gruppo «Porte» apre lo switch sulla porta.':
    'A MAC address, in any spelling (aa:bb:cc, AA-BB-CC, aabb.ccdd) and even just the prefix, finds on which switch and port it is seen: the «Ports» group opens the switch on the port.',
  'es. Philips':
    'e.g. Philips',
  'es. IntelliVue MX800':
    'e.g. IntelliVue MX800',
  'Ascolto passivo (ARP, DHCP, mDNS, SSDP, NetBIOS, LLDP)':
    'Passive listening (ARP, DHCP, mDNS, SSDP, NetBIOS, LLDP)',
  'Chiede a Fingerbank gli indirizzi che il catalogo CENSO non conosce (al massimo cento) e salva le risposte con la classe.':
    'Asks Fingerbank about the addresses the CENSO catalogue does not know (at most one hundred) and saves the answers with the class.',
  'Classe CI':
    'CI class',
  'Fingerbank (riconoscimento dispositivi)':
    'Fingerbank (device recognition)',
  "Fingerbank classifica un dispositivo dalle sue impronte di rete: il MAC, la lista delle opzioni DHCP che chiede, la classe vendor DHCP, l'hostname. Risponde con il dispositivo («Printer or Scanner/HP/HP LaserJet»), il sistema operativo e un punteggio.":
    'Fingerbank classifies a device from its network fingerprints: the MAC, the list of DHCP options it requests, the DHCP vendor class, the hostname. It answers with the device («Printer or Scanner/HP/HP LaserJet»), the operating system and a score.',
  'Fingerbank: {n} chiesti, {r} riconosciuti, poi: {e}':
    'Fingerbank: {n} asked, {r} recognised, then: {e}',
  'Fingerbank: {n} chiesti, {r} riconosciuti, {c} CI classificati. Le risposte sono nel catalogo CENSO.':
    'Fingerbank: {n} asked, {r} recognised, {c} CIs classified. The answers are in the CENSO catalogue.',
  'Fonti dell’ascolto passivo':
    'Passive listening sources',
  'La chiave del tuo account Fingerbank (api.fingerbank.org → API key).':
    'The key of your Fingerbank account (api.fingerbank.org → API key).',
  'La chiave della classe CENSO (es. medical_device, printer, voip_phone, workstation): un CI ancora «unknown» dietro il MAC la eredita alla prossima scansione.':
    'The CENSO class key (e.g. medical_device, printer, voip_phone, workstation): a CI still «unknown» behind the MAC inherits it at the next scan.',
  "La sonda ascolta dalla propria scheda gli annunci dei dispositivi sul segmento — ARP (chi ha quale IP, anche statico), le richieste DHCP (l'impronta del sistema operativo), mDNS e SSDP (stampanti, TV, apparecchi Apple, spesso con il modello), NetBIOS (il nome Windows), LLDP (telefoni IP, access point). Non manda pacchetti e non apre porte in ingresso. Alimenta la finestra della porta degli switch, il riconoscimento del tipo di dispositivo e Fingerbank.":
    'The probe listens from its own NIC to what devices announce on the segment — ARP (who has which IP, static too), DHCP requests (the operating system fingerprint), mDNS and SSDP (printers, TVs, Apple devices, often with the model), NetBIOS (the Windows name), LLDP (IP phones, access points). It sends no packets and opens no inbound ports. It feeds the switch port window, device-type recognition and Fingerbank.',
  "Le impronte escono verso Fingerbank (Akamai). Con l'ascolto passivo acceso sulla sonda le impronte DHCP rendono il verdetto molto più preciso che con il solo MAC.":
    'The fingerprints leave towards Fingerbank (Akamai). With passive listening on the probe, DHCP fingerprints make the verdict far more precise than the MAC alone.',
  'Ogni risposta si scrive nel catalogo di CENSO con la classe: la volta dopo quel MAC si riconosce senza chiedere a nessuno, in ogni tenant. Il piano gratuito ha un tetto di richieste: CENSO chiede al massimo cento indirizzi per azione e ricorda sei ore le risposte vuote.':
    'Every answer is written into the CENSO catalogue with the class: next time that MAC is recognised without asking anyone, in every tenant. The free plan has a request cap: CENSO asks at most one hundred addresses per action and remembers empty answers for six hours.',
  'Riconosci con Fingerbank':
    'Recognise with Fingerbank',
  'Riconosciuto da ciò che il dispositivo annuncia in rete (mDNS, DHCP, LLDP, SSDP).':
    'Recognised from what the device announces on the network (mDNS, DHCP, LLDP, SSDP).',
  'Riconosco…':
    'Recognising…',
  'Vuoto: il servizio Fingerbank in cloud. Si cambia solo con un gateway aziendale che lo rilancia.':
    'Empty: the Fingerbank cloud service. Change it only with a corporate gateway that relays it.',
  'annunciato':
    'announced',
  'https://api.fingerbank.org':
    'https://api.fingerbank.org',
  'stessa impronta':
    'same fingerprint',
  'Ascolta ma non sente nulla: sulla scheda della sonda non passano annunci. In una VM controlla che la porta del vSwitch sia sullo stesso segmento dei dispositivi; in container serve la rete dell’host.':
    "Listening but hearing nothing: no announcements pass on the probe's NIC. In a VM check that the vSwitch port is on the same segment as the devices; in a container the host network is required.",
  'Ascolto passivo':
    'Passive listening',
  'Dispositivi consegnati':
    'Devices delivered',
  'La sonda non riporta ancora lo stato dell’ascolto: aspetta il prossimo heartbeat, o aggiornala se è una versione precedente.':
    'The probe does not report the listening state yet: wait for the next heartbeat, or update it if it is an older version.',
  'La sonda sente dalla propria scheda gli annunci dei dispositivi sul segmento (ARP, DHCP, mDNS, SSDP, NetBIOS, LLDP) e consegna un riassunto per MAC ogni due minuti. Qui vedi se ascolta e chi ha sentito: un dispositivo in tabella è la prova che intercetta.':
    'The probe hears from its own NIC what devices announce on the segment (ARP, DHCP, mDNS, SSDP, NetBIOS, LLDP) and delivers a per-MAC summary every two minutes. Here you see whether it listens and whom it heard: a device in the table is the proof it intercepts.',
  'Pacchetti utili sentiti':
    'Useful packets heard',
  'Schede':
    'NICs',
  'in ascolto':
    'listening',
  'non in ascolto':
    'not listening',
  'ultima consegna {q}':
    'last delivery {q}',
  'Annunci sentiti (ascolto passivo)':
    'Announcements heard (passive listening)',
  'Cattura':
    'Capture',
  'Catturati dal filo':
    'Captured from the wire',
  'Collector':
    'Collector',
  'Con costruttore':
    'With manufacturer',
  'Costruttori più frequenti':
    'Most frequent manufacturers',
  'Dispositivi individuati':
    'Devices identified',
  'Dispositivi individuati e identificati': 'Devices detected and identified',
  'Dispositivi sentiti':
    'Devices heard',
  'Elettromedicali':
    'Medical devices',
  'Flussi attivi':
    'Active flows',
  'Flussi decodificati dalle sonde':
    'Flows decoded by the probes',
  'Flussi nella finestra':
    'Flows in the window',
  'Elenco degli indirizzi MAC rilevati dalle tabelle degli apparati di rete e dall’analisi passiva del traffico, con le informazioni disponibili sul dispositivo (produttore, tipologia, identificazioni automatiche e fonti di riconoscimento). Rileva i dispositivi presenti nell’infrastruttura anche se non sono ancora censiti come CI.':
    'List of the MAC addresses found in the network devices’ tables and through passive traffic analysis, with the information available on the device (manufacturer, type, automatic identifications and recognition sources). It finds the devices in the infrastructure even when they are not yet inventoried as CIs.',
  'Host visti':
    'Hosts seen',
  'Mostra, per ogni sonda installata, il traffico acquisito tramite porte mirror (SPAN) o altre modalità di ascolto passivo e i dispositivi identificati tramite protocolli di rete locali quali ARP, DHCP, mDNS, SSDP, NetBIOS e LLDP.':
    'Shows, for each installed probe, the traffic captured through mirror ports (SPAN) or other passive listening and the devices identified through local network protocols such as ARP, DHCP, mDNS, SSDP, NetBIOS and LLDP.',
  'Nel catalogo CENSO':
    'In the CENSO catalogue',
  'Nessun dispositivo: lancia una scansione SNMP con «Tabella MAC» sugli switch.':
    'No devices: run an SNMP scan with «MAC table» on the switches.',
  'Nessun tipo riconosciuto: accendi l’ascolto passivo sulla sonda.':
    'No type recognised: turn on passive listening on the probe.',
  'Pacchetti intercettati':
    'Packets intercepted',
  'Pacchetti nella finestra':
    'Packets in the window',
  'Pacchetti ricevuti dalle sonde':
    'Packets received by the probes',
  'Mostra il volume di traffico rilevato, la percentuale decodificata e il numero di dispositivi identificati.': 'Shows the volume of traffic detected, the percentage decoded and the number of devices identified.',
  'Confronta i flussi di traffico rilevati nel periodo selezionato con quelli che le sonde hanno ricevuto e decodificato dall’avvio, tramite NetFlow, IPFIX, sFlow e cattura di rete.':
    'Compares the traffic flows recorded in the selected period with those the probes have received and decoded since start-up, through NetFlow, IPFIX, sFlow and network capture.',
  'Registro dei flussi non leggibile: {e}':
    'Flow log not readable: {e}',
  'Riconosciuti dagli annunci':
    'Recognised from announcements',
  'Copertura rispetto all’inventario di riferimento':
    'Coverage against the reference inventory',
  'La scala del riconoscimento giorno per giorno, misurata sui {n} dispositivi dell’inventario di riferimento (Armis, Medigate…): scoperto, identificato come tipo, riconosciuto, fino allo stesso modello.':
    'The recognition ladder day by day, measured on the {n} devices of the reference inventory (Armis, Medigate…): discovered, identified by type, recognised, up to the same model.',
  'Scoperti sulla rete':
    'Discovered on the network',
  'Identificati (almeno il tipo)':
    'Identified (at least the type)',
  'Riconosciuti (modello o elettromedicale)':
    'Recognised (model or medical)',
  'Stesso modello del riferimento':
    'Same model as the reference',
  "Un grafico d’andamento mostra, giorno per giorno, quanti dispositivi si vedono, quanti si riconoscono dagli annunci e quanti sono elettromedicali: così si capisce se il riconoscimento migliora man mano che si imparano firme, si estende lo SPAN o si mettono agenti sulle postazioni.":
    "One trend chart shows, day by day, how many devices are seen, how many are recognised from their announcements and how many are medical: this shows whether recognition improves as you learn signatures, extend the SPAN or deploy agents on the workstations.",
  'Se c’è un inventario di riferimento, un secondo gruppo di grafici mostra la stessa scala misurata su quello: quanti dei dispositivi attesi sono scoperti sulla rete, quanti identificati almeno come tipo, quanti riconosciuti e per quanti CENSO dice lo stesso modello — l’evidenza giorno per giorno di quanto l’ospedale è coperto senza fonti esterne.':
    'If a reference inventory exists, a second set of charts shows the same ladder measured against it: how many of the expected devices are discovered on the network, how many identified at least by type, how many recognised and for how many CENSO gives the same model — day-by-day evidence of how well the hospital is covered without external sources.',
  'Andamento del riconoscimento (30 giorni)':
    'Recognition trend (30 days)',
  'Un punto al giorno: così si vede se il riconoscimento migliora imparando firme, estendendo lo SPAN o mettendo agenti sulle postazioni.':
    'One point a day: this shows whether recognition improves as you learn signatures, extend the SPAN or deploy agents on the workstations.',
  'Scartati dal kernel':
    'Dropped by the kernel',
  'Segnalazioni aperte':
    'Open reports',
  'Sistemi operativi (host locali)':
    'Operating systems (local hosts)',
  'Tipi di dispositivo (ntopng)':
    'Device types (ntopng)',
  'Tipi riconosciuti':
    'Recognised types',
  'Top talker locali':
    'Local top talkers',
  'Traffic Analysis':
    'Traffic Analysis',
  'Traffico decodificato':
    'Decoded traffic',
  'Volume nella finestra':
    'Volume in the window',
  'dall’avvio':
    'since start',
  'dall’avvio, tutti i collector':
    'since start, all collectors',
  'ferma':
    'stopped',
  'la sonda non teneva il passo: i volumi sono incompleti':
    'the probe could not keep up: volumes are incomplete',
  'ntopng non ha ancora riconosciuto sistemi operativi.':
    'ntopng has not recognised any operating system yet.',
  'ntopng non riporta tipi di dispositivo.':
    'ntopng reports no device types.',
  'ntopng non risponde':
    'ntopng is not answering',
  'quanto traffico vediamo e quanto ne capiamo: volume, pacchetti e flussi decodificati nella finestra scelta e dall’avvio delle sonde; pacchetti catturati dal filo e annunci sentiti dall’ascolto passivo, sonda per sonda; dispositivi individuati sulle porte degli switch e nell’ascolto, con costruttore, tipo riconosciuto, voci nel catalogo CENSO ed elettromedicali. Se ntopng è configurato, anche host attivi, sistemi operativi, tipi di dispositivo e top talker visti da lì.':
    'how much traffic we see and how much we understand: volume, packets and decoded flows in the chosen window and since the probes started; packets captured from the wire and announcements heard by passive listening, probe by probe; devices identified on switch ports and in listening, with manufacturer, recognised type, CENSO catalogue entries and medical devices. With ntopng configured, also active hosts, operating systems, device types and top talkers seen from there.',
  'silenziosa':
    'silent',
  'silenziosi':
    'silent',
  'ultima ora':
    'last hour',
  'ultime 24 ore':
    'last 24 hours',
  '{a} sulle porte, {b} sentiti, {c} nelle ultime 24 ore':
    '{a} on ports, {b} heard, {c} in the last 24 hours',
  '{c} catalogate, {s} scartate':
    '{c} catalogued, {s} dismissed',
  '{n} annunci, {m} dispositivi':
    '{n} announcements, {m} devices',
  '{n} locali':
    '{n} local',
  '{n} pacchetti, {m} flussi':
    '{n} packets, {m} flows',
  '{n} pacchetti, {m} flussi, {d} scartati':
    '{n} packets, {m} flows, {d} dropped',
  'Elettromedicali su questa porta':
    'Medical devices on this port',
  '{n} elettromedicali su questa porta':
    '{n} medical devices on this port',
  'Ascolto DHCP':
    'DHCP listening',
  'Ascolta le richieste DHCP e leggi i lease (solo sul server DHCP di Windows)':
    'Listen to DHCP requests and read the leases (only on the Windows DHCP server)',
  'Con gli ip helper sui gateway il server DHCP riceve le richieste di tutte le VLAN: l’agente ne legge MAC, nome, impronta del sistema operativo e VLAN di provenienza, più i lease del servizio, e li consegna col keep-alive come l’ascolto passivo della sonda.':
    "With ip helpers on the gateways the DHCP server receives the requests of every VLAN: the agent reads MAC, name, operating-system fingerprint and source VLAN, plus the service's leases, and delivers them with the keep-alive like the probe's passive listening.",
  'In ascolto su {n} indirizzi: {p} richieste sentite, {l} lease letti, {q} da consegnare.':
    'Listening on {n} addresses: {p} requests heard, {l} leases read, {q} to deliver.',
  'Acceso ma non in ascolto: l’agente non ha ancora aperto le prese o non può (serve Windows e i diritti di amministratore).':
    'On but not listening: the agent has not opened the sockets yet or cannot (needs Windows and administrator rights).',
  'Spento.':
    'Off.',
  'Lease':
    'Leases',
  'Dispositivi collegati':
    'Connected devices',
  'Gli stessi avvistamenti arrivano anche dall’agente installato sul server DHCP di Windows (spunta «Ascolto DHCP» nella sua scheda: con gli ip helper vede le richieste di tutte le VLAN, con l’impronta del sistema operativo e la VLAN di provenienza, più i lease) e dai firewall ogni cinque minuti: il device inventory del FortiGate (tipo, sistema operativo, costruttore) e ARP e lease DHCP del Palo Alto (MAC↔IP).':
    'The same sightings also come from the agent installed on the Windows DHCP server (“DHCP listening” switch on its card: with ip helpers it sees the requests of every VLAN, with the operating-system fingerprint and the source VLAN, plus the leases) and from the firewalls every five minutes: the FortiGate device inventory (type, operating system, manufacturer) and the Palo Alto ARP and DHCP leases (MAC↔IP).',
  'Installato sul server DHCP di Windows, con «Ascolto DHCP» acceso diventa anche una fonte di dispositivi: le richieste DHCP di tutte le VLAN e i lease del servizio finiscono fra gli avvistamenti (finestra della porta, Traffic Analysis); la scheda dice se è in ascolto e quante richieste ha sentito.':
    "Installed on the Windows DHCP server, with “DHCP listening” on it also becomes a source of devices: the DHCP requests of every VLAN and the service's leases end up among the sightings (port window, Traffic Analysis); the card says whether it is listening and how many requests it has heard.",
  '{n} dispositivi (i primi {n}: restringi con il filtro).':
    '{n} devices (the first {n}: narrow with the filter).',
  '{n} dispositivi':
    '{n} devices',
  'Switch · porta':
    'Switch · port',
  'Segnalazione aperta':
    'Open report',
  'Voce del catalogo CENSO':
    'CENSO catalogue entry',
  'catalogo':
    'catalogue',
  'Nessun dispositivo per questo filtro.':
    'No device for this filter.',
  'Sulle porte degli switch':
    'On switch ports',
  'Sentiti (ascolto, DHCP, firewall)':
    'Heard (listening, DHCP, firewalls)',
  '{c} nelle ultime 24 ore':
    '{c} in the last 24 hours',
  'Ogni valore identifica il numero dei dispositivi rilevati associati al CI e, dove disponibile, lo switch e la porta su cui sono stati rilevati.': 'Each value identifies the number of detected devices associated with the CI and, where available, the switch and port on which they were detected.',
  'Per fonte':
    'By source',
  'Nessun avvistamento: accendi l’ascolto passivo sulla sonda, l’ascolto DHCP sull’agente del server DHCP o un firewall.':
    'No sightings: turn on passive listening on the probe, DHCP listening on the DHCP server agent, or a firewall.',
  'Dai firewall':
    'From the firewalls',
  'ogni cinque minuti acquisisce le informazioni sui dispositivi rilevati dai firewall. Rileva le associazioni MAC-IP per VLAN tramite ARP e DHCP, acquisisce tipo di dispositivo, sistema operativo e produttore. Ogni valore corrisponde all’elenco dei dispositivi rilevati.': 'every five minutes it collects the information on the devices detected by the firewalls. It reads the MAC-IP associations per VLAN through ARP and DHCP and collects device type, operating system and manufacturer. Each value corresponds to the list of detected devices.',
  'Nessun dispositivo dai firewall':
    'No device from the firewalls',
  'Serve una credenziale Palo Alto o FortiGate in Integrazioni: la lettura parte da sola al primo giro.':
    'A Palo Alto or FortiGate credential in Integrations is needed: the reading starts by itself on the first round.',
  'ARP':
    'ARP',
  'Lease DHCP':
    'DHCP leases',
  'Device inventory':
    'Device inventory',
  'ARP di {f}':
    'ARP of {f}',
  'Lease DHCP di {f}':
    'DHCP leases of {f}',
  'Device inventory di {f}':
    'Device inventory of {f}',
  'ARP (sonda)':
    'ARP (probe)',
  'DHCP (sonda)':
    'DHCP (probe)',
  'Richieste DHCP (agente sul server DHCP)':
    'DHCP requests (agent on the DHCP server)',
  'Lease DHCP (agente sul server DHCP)':
    'DHCP leases (agent on the DHCP server)',
  'ARP del firewall Palo Alto':
    'Palo Alto firewall ARP',
  'Lease DHCP del firewall Palo Alto':
    'Palo Alto firewall DHCP leases',
  'Device inventory FortiGate':
    'FortiGate device inventory',
  'Apri la lista':
    'Open the list',
  'Ogni numero si apre su un popup con la lista che lo compone (MAC, IP, nome, costruttore, tipo, CI abbinato, switch e porta), e dalla lista si va al CI o alla finestra della porta; anche i tipi, i costruttori e le fonti sono cliccabili. La card «Dai firewall» dice, firewall per firewall, quanti dispositivi vengono da ARP, lease DHCP e device inventory.':
    'Every number opens a popup with the list behind it (MAC, IP, name, manufacturer, type, matched CI, switch and port), and from the list you go to the CI or to the port window; types, manufacturers and sources are clickable too. The “From the firewalls” card says, firewall by firewall, how many devices come from ARP, DHCP leases and the device inventory.',
  'Per sentire più VLAN porta alla sonda una porta trunk con tutte le VLAN consentite: legge il tag 802.1Q di ogni frame e segna la VLAN in tabella, senza sottointerfacce sulla macchina.':
    'To hear more VLANs, bring the probe a trunk port with every VLAN allowed: it reads the 802.1Q tag of each frame and notes the VLAN in the table, with no sub-interfaces on the machine.',
  'Per sentire più VLAN con una sonda sola, porta alla sonda una porta trunk con tutte le VLAN consentite: la sonda legge il tag 802.1Q di ogni frame e segna la VLAN dell’avvistamento, senza una sottointerfaccia per VLAN sulla macchina (se è una VM, il port group deve lasciare passare i tag).':
    'To hear several VLANs with a single probe, bring it a trunk port with every VLAN allowed: the probe reads the 802.1Q tag of each frame and notes the VLAN of the sighting, with no sub-interface per VLAN on the machine (on a VM, the port group must let the tags through).',
  'Servizi virtuali in servizio':
    'Virtual services in service',
  'Server reali in servizio':
    'Real servers in service',
  'Certificati':
    'Certificates',
  'Certificati scaduti':
    'Expired certificates',
  'Bilanciamento':
    'Load balancing',
  'Servizi virtuali':
    'Virtual services',
  'Persistenza':
    'Persistence',
  'Server reali':
    'Real servers',
  'sotto-servizio':
    'sub-service',
  'peso':
    'weight',
  'Emittente':
    'Issuer',
  'Valido dal':
    'Valid from',
  'Seriale':
    'Serial',
  'Su un bilanciatore (Kemp LoadMaster via SNMP) la scheda ha la card «Bilanciamento»: i servizi virtuali con VIP, porta, protocollo, bilanciamento, persistenza, stato e connessioni, sotto ognuno i server reali con peso e stato, e i certificati con la scadenza evidenziata; i VIP sono anche indirizzi aggiuntivi del CI, quindi la composizione di servizio da un VIP arriva al bilanciatore.':
    'On a load balancer (Kemp LoadMaster via SNMP) the page has the “Load balancing” card: the virtual services with VIP, port, protocol, scheduling, persistence, state and connections, under each the real servers with weight and state, and the certificates with the expiry highlighted; the VIPs are also additional addresses of the CI, so the service composition from a VIP reaches the load balancer.',
  'Azzerare tutti gli avvistamenti di questo spazio? Nomi, impronte e fonti sentite ripartono da zero; la sonda, l’agente DHCP e i firewall li ricostruiscono nei minuti successivi. Il catalogo MAC e le impronte imparate restano.':
    'Clear all sightings of this space? Names, fingerprints and heard sources start from zero; the probe, the DHCP agent and the firewalls rebuild them in the following minutes. The MAC catalogue and the learned fingerprints remain.',
  'Azzerati {n} avvistamenti. Le fonti li ricostruiscono da sole: sonda ogni due minuti, agente DHCP ogni minuto, firewall ogni cinque.':
    '{n} sightings cleared. The sources rebuild them by themselves: probe every two minutes, DHCP agent every minute, firewalls every five.',
  'Cancella gli avvistamenti dell’ascolto passivo, dell’agente DHCP e dei firewall: da usare dopo una correzione, si ricostruiscono da soli.':
    'Deletes the sightings from passive listening, the DHCP agent and the firewalls: to be used after a fix, they rebuild by themselves.',
  '«Azzera avvistamenti» (amministratori) cancella tutto ciò che è stato sentito e lascia che sonda, agente DHCP e firewall lo ricostruiscano: serve dopo una correzione dell’ascolto; catalogo MAC e impronte imparate restano.':
    '“Clear sightings” (administrators) deletes everything that was heard and lets the probe, the DHCP agent and the firewalls rebuild it: useful after a listening fix; the MAC catalogue and the learned fingerprints remain.',
  'certificato':
    'certificate',
  'Cosa ha detto sul filo':
    'What it said on the wire',
  'medicale':
    'medical',
  'Nessun host per questo filtro.':
    'No host for this filter.',
  'Protocolli sul filo':
    'Protocols on the wire',
  'Analisi passiva del traffico: la sonda analizza il traffico acquisito da porta mirror o in modalità promiscua per identificare sistemi, servizi e applicazioni tramite HTTP, TLS, SSH, SMB, DICOM, HL7, SIP e WS-Discovery. I rilevamenti sono associati per IP al relativo CI e ogni valore apre l’elenco di dettaglio.': 'Passive traffic analysis: the probe analyses the traffic captured from a mirror port or in promiscuous mode to identify systems, services and applications through HTTP, TLS, SSH, SMB, DICOM, HL7, SIP and WS-Discovery. The detections are associated by IP with the relevant CI, and each value opens the detail list.',
  'Host con fatti sul filo':
    'Hosts with wire facts',
  'ultimo: {q}':
    'last: {q}',
  'Riconosciuti dal filo':
    'Recognised from the wire',
  'Medicali (DICOM / HL7)':
    'Medical (DICOM / HL7)',
  'Con certificato TLS':
    'With TLS certificate',
  'Host per protocollo':
    'Hosts by protocol',
  'Niente ancora: serve una porta mirror o una scheda in promiscuo con l’ascolto passivo acceso.':
    'Nothing yet: it takes a mirror port or a promiscuous interface with passive listening on.',
  'Dai protocolli sul filo (mirror/promiscuo)':
    'From the protocols on the wire (mirror/promiscuous)',
  'filo':
    'wire',
  'Visto sul filo':
    'Seen on the wire',
  'Ciò che questa macchina ha detto di sé nel traffico visto dalla sonda (porta mirror o promiscuo): protocolli unicast, per indirizzo.':
    'What this machine said about itself in the traffic seen by the probe (mirror port or promiscuous): unicast protocols, by address.',
  'Tipo riconosciuto':
    'Recognised type',
  'Parla DICOM o HL7: è un’apparecchiatura o un sistema sanitario.':
    'It speaks DICOM or HL7: it is a medical device or a healthcare system.',
  'La card «Protocolli sul filo» è ciò che la sonda capisce dal traffico unicast su una porta mirror o in promiscuo: User-Agent e Server HTTP, certificati e nomi TLS, banner SSH, nome/dominio/versione Windows da SMB, AE title DICOM e applicazioni HL7 (segnati medicali), telefoni SIP, telecamere e stampanti WS-Discovery; per IP, con il CI che ha quell’indirizzo. Nella scheda di un CI gli stessi fatti stanno nella card «Visto sul filo», e nella finestra della porta nella riga «filo».':
    'The “Protocols on the wire” card is what the probe understands from unicast traffic on a mirror port or in promiscuous mode: HTTP User-Agent and Server, TLS certificates and names, SSH banners, Windows name/domain/version from SMB, DICOM AE titles and HL7 applications (marked medical), SIP phones, WS-Discovery cameras and printers; by IP, with the CI that has that address. On a CI page the same facts are in the “Seen on the wire” card, and in the port window in the “wire” line.',
  'Con la cattura dal filo configurata l’ascolto è acceso da solo, senza spunta: la cattura produce le conversazioni (chi parla con chi, volumi), l’analisi l’identità dei dispositivi, sugli stessi pacchetti letti una volta sola («(cattura)» accanto al nome della scheda). La capability «Ascolto passivo» serve solo per ascoltare schede senza cattura (management, trunk).':
    'With wire capture configured, listening is on by itself, with no switch: capture produces the conversations (who talks to whom, volumes), the analysis the identity of the devices, on the same packets read once (“(capture)” next to the interface name). The “Passive listening” capability is only needed to listen on interfaces without capture (management, trunk).',
  'L’ascolto è spento: configura la cattura dal filo (lo accende da sé) oppure attiva la capability «Ascolto passivo» fra le capability qui sopra.':
    'Listening is off: configure wire capture (it turns it on by itself) or enable the “Passive listening” capability among the capabilities above.',
  'Con la cattura dal filo configurata sulla sonda (o la capability «Ascolto passivo» accesa, per le schede senza cattura), la finestra mostra anche nome, IP (da ARP, anche per chi ha l’indirizzo statico) e il tipo annunciato in rete (mDNS, DHCP, SSDP, NetBIOS, LLDP: stampante, telefono IP, PC Windows, apparecchio Apple…). Con una credenziale Fingerbank in Integrazioni, «Riconosci con Fingerbank» chiede il dispositivo per gli indirizzi ancora senza nome e salva la risposta nel catalogo CENSO con la classe: un CI «unknown» dietro quel MAC la eredita.':
    'With wire capture configured on the probe (or the “Passive listening” capability on, for interfaces without capture), the window also shows name, IP (from ARP, even for static addresses) and the type announced on the network (mDNS, DHCP, SSDP, NetBIOS, LLDP: printer, IP phone, Windows PC, Apple device…). With a Fingerbank credential in Integrations, “Recognise with Fingerbank” asks for the device for addresses still without a name and saves the answer in the CENSO catalogue with its class: an “unknown” CI behind that MAC inherits it.',
  'Il kernel ha scartato {n} pacchetti: la sonda non sta al passo con il traffico e i volumi riportati sono incompleti. Le perdite del kernel, a differenza del campionamento, non si possono correggere: il fattore non si conosce. Il campionamento non le riduce, perché la sonda legge comunque ogni pacchetto: copia meno traffico nello SPAN (solo le VLAN dei dispositivi, non storage e backup) oppure usa una sonda con più core.':
    'The kernel dropped {n} packets: the probe is not keeping up with the traffic and the reported volumes are incomplete. Kernel losses, unlike sampling, cannot be corrected: the factor is unknown. Sampling does not reduce them, because the probe reads every packet anyway: mirror less traffic into the SPAN (only the device VLANs, not storage and backup) or use a probe with more cores.',
  'anello del kernel · {n} lettori in parallelo':
    'kernel ring · {n} parallel readers',
  'classica, un pacchetto per chiamata: il kernel non ha concesso l’anello':
    'classic, one packet per call: the kernel did not grant the ring',
  'Ingegneria Clinica':
    'Clinical Engineering',
  'stesso modello':
    'same model',
  'modello diverso':
    'different model',
  'riconosciuto medicale':
    'recognised as medical',
  'tipo generico':
    'generic type',
  'visto, non riconosciuto':
    'seen, not recognised',
  'non visto':
    'not seen',
  'porta switch':
    'switch port',
  'ascolto':
    'listening',
  'Lettura del file…':
    'Reading the file…',
  'Nel file non c’è un foglio con una colonna di MAC o di IP.':
    'The file has no sheet with a MAC or IP column.',
  'Importare {n} righe dal foglio «{foglio}» come inventario {fonte}? Sostituisce l’import precedente della stessa fonte.':
    'Import {n} rows from the “{foglio}” sheet as the {fonte} inventory? It replaces the previous import from the same source.',
  'Import in corso…':
    'Importing…',
  'Importati {n} dispositivi ({s} righe saltate: senza MAC né IP o ripetute). Colonne riconosciute: {c}.':
    'Imported {n} devices ({s} rows skipped: no MAC or IP, or repeated). Recognised columns: {c}.',
  'Eliminare l’inventario {fonte} importato? Il catalogo CENSO e ciò che ha già imparato restano.':
    'Delete the imported {fonte} inventory? The CENSO catalogue and what it has already learnt stay.',
  'Confronto con un inventario di riferimento':
    'Comparison with a reference inventory',
  'Importa da Armis, Medigate o dall’Ingegneria Clinica un elenco Excel o CSV con MAC o IP. CENSO verifica per ogni dispositivo se è presente nella rete e se viene riconosciuto, confrontando i dati raccolti da switch, ascolto passivo, CMDB e traffico di rete. I dispositivi identificati contribuiscono inoltre a creare firme utili a riconoscere automaticamente gli stessi modelli.': 'Import from Armis, Medigate or Clinical Engineering an Excel or CSV list with MACs or IPs. For each device CENSO checks whether it is present on the network and whether it is recognised, comparing the data collected from switches, passive listening, CMDB and network traffic. The identified devices also help build signatures that automatically recognise the same models.',
  'Importa file':
    'Import file',
  'Nessun inventario importato.':
    'No inventory imported.',
  'Nell’inventario':
    'In the inventory',
  'Visti da CENSO':
    'Seen by CENSO',
  'Riconosciuti':
    'Recognised',
  '{p} · stesso modello o elettromedicale':
    '{p} · same model or medical',
  'Con lo stesso modello':
    'With the same model',
  'Non visti':
    'Not seen',
  'Dispositivi':
    'Devices',
  'Visti':
    'Seen',
  'Stesso modello':
    'Same model',
  'Firme da imparare':
    'Signatures to learn',
  'Visto da':
    'Seen by',
  'CENSO dice':
    'CENSO says',
  'prefisso → modello':
    'prefix → model',
  'prefisso → tipo':
    'prefix → type',
  'impronta DHCP → modello':
    'DHCP fingerprint → model',
  'Scritte nel catalogo: {p} prefissi, {f} impronte; {a} dispositivi riconosciuti subito dalle impronte.':
    'Written to the catalogue: {p} prefixes, {f} fingerprints; {a} devices recognised right away by the fingerprints.',
  '{n} proposte, {s} selezionate':
    '{n} proposals, {s} selected',
  'Impara nel catalogo CENSO':
    'Learn into the CENSO catalogue',
  'Firma':
    'Signature',
  'Insegna':
    'Teaches',
  'prefisso di {v}':
    'prefix of {v}',
  'già nel catalogo: {v}':
    'already in the catalogue: {v}',
  'Nessuna firma da proporre.':
    'No signature to propose.',
  'Dove il prefisso non basta ({n})':
    'Where the prefix is not enough ({n})',
  'Tipi e modelli':
    'Types and models',
  'PC o server di marca generica: servono altri segnali':
    'Generic-brand PC or server: other signals are needed',
  'tipi o modelli diversi con la stessa firma':
    'different types or models with the same signature',
  'Non è un file Excel (.xlsx) valido: salvalo di nuovo da Excel, oppure come CSV.':
    'Not a valid Excel (.xlsx) file: save it again from Excel, or as CSV.',
  'Compressione del file Excel non supportata ({m}): salvalo come CSV.':
    'Unsupported Excel file compression ({m}): save it as CSV.',
  'Questo browser non sa aprire i file Excel: salva il file come CSV.':
    'This browser cannot open Excel files: save the file as CSV.',
  'La sonda ne guarda al massimo {tetto} per job: la scansione si divide da sé in {k} job, uno per parte, che le sonde eseguono come gli altri.':
    'The probe handles at most {tetto} per job: the scan splits itself into {k} jobs, one per part, which the probes run like any other.',
  'Una scansione di rete guarda al massimo {max} indirizzi per job: con questo pool si divide da sé in {k} job, uno per parte.':
    'A network scan handles at most {max} addresses per job: with this pool it splits itself into {k} jobs, one per part.',
  '{k} job':
    '{k} jobs',
  'Gli access point gestiti, gli SSID diffusi e i client associati: da un controller con l’inventario avanzato apparati (via SSH), o dall’access point stesso con il driver SNMP (Aerohive / Extreme). I client si contano anche fra i dispositivi di Traffic Analysis.':
    'The managed access points, the broadcast SSIDs and the associated clients: from a controller with the advanced device inventory (via SSH), or from the access point itself with the SNMP driver (Aerohive / Extreme). Clients are also counted among the Traffic Analysis devices.',
  'Client Wi-Fi (dagli access point)':
    'Wi-Fi clients (from the access points)',
  'Anche gli access point Aerohive / Extreme (IQ Engine) hanno la sezione Wireless sulla propria scheda: con la scansione SNMP il driver legge modello, seriale, firmware, CPU e memoria, gli SSID di ogni radio e i client associati con nome, indirizzo, SSID e VLAN. I client entrano anche fra i dispositivi di Traffic Analysis (fonte «Client Wi-Fi»), quindi un elettromedicale in Wi-Fi risulta visto.':
    'Aerohive / Extreme access points (IQ Engine) also have the Wireless section on their own page: with the SNMP scan the driver reads model, serial, firmware, CPU and memory, the SSIDs of each radio and the associated clients with name, address, SSID and VLAN. Clients also join the Traffic Analysis devices (source “Wi-Fi clients”), so a medical device on Wi-Fi counts as seen.',
  'Riconosciuti dal prodotto':
    'Recognised from the product',
  'dal nome con cui il dispositivo si presenta':
    'from the name the device announces',
  'riconosciuto da «{e}»':
    'recognised from “{e}”',
  'nome del prodotto → modello':
    'product name → model',
  'da inventario di riferimento':
    'from a reference inventory',
  'Conoscenza dispositivi':
    'Device knowledge',
  'Firme imparate dagli inventari di riferimento di tutti i clienti: nome del prodotto, impronte DHCP, prefissi':
    'Signatures learnt from the reference inventories of all customers: product name, DHCP fingerprints, prefixes',
  'Proposte da tutti gli ospedali ({n})':
    'Proposals from all hospitals ({n})',
  'Impara le selezionate':
    'Learn the selected ones',
  'Tenant con un inventario: {n}.':
    'Tenants with an inventory: {n}.',
  'Nessuna proposta':
    'No proposals',
  'Importa un inventario di riferimento in un tenant (Traffic Analysis).':
    'Import a reference inventory in a tenant (Traffic Analysis).',
  'Ospedali':
    'Hospitals',
  'in disaccordo':
    'in disagreement',
  'Il numero «Riconosciuti dal prodotto» conta i dispositivi riconosciuti dal nome con cui si presentano (nome DHCP o NetBIOS, nome del CI, modello mDNS, certificato): molti elettromedicali hanno un MAC di scheda generica, e il nome del prodotto (EarlyVue VS30, PIC iX, MiSeq, Alinity…) è ciò che li distingue. CENSO conosce di suo le principali linee di prodotto medicali, e impara dagli inventari di riferimento di tutti gli ospedali i nomi che ancora non conosce.':
    'The “Recognised from the product” number counts the devices recognised from the name they announce (DHCP or NetBIOS name, CI name, mDNS model, certificate): many medical devices have a generic board MAC, and the product name (EarlyVue VS30, PIC iX, MiSeq, Alinity…) is what tells them apart. CENSO knows the main medical product lines on its own, and learns from the reference inventories of all hospitals the names it does not know yet.',
  'nome locale':
    'local name',
  'Inventario passivo':
    'Passive inventory',
  'Rileva e registra come CI i dispositivi riconosciuti passivamente, senza contattarli o sottoporli a scansione. Utilizza le informazioni raccolte da switch, traffico di rete, DHCP, firewall e identificazione del prodotto per determinarne costruttore, modello, IP, VLAN, switch e porta. Il rilevamento viene aggiornato ogni ora o su richiesta con «Esegui adesso». I CI già presenti da una scansione non vengono modificati; quelli non più rilevati seguono il normale ciclo di vita dei CI.': 'Detects and registers as CIs the devices recognised passively, without contacting them or scanning them. It uses the information collected from switches, network traffic, DHCP, firewalls and product identification to determine their manufacturer, model, IP, VLAN, switch and port. The detection is refreshed every hour or on demand with «Esegui adesso». CIs already present from a scan are not modified; those no longer detected follow the normal CI lifecycle.',
  'Cosa registrare':
    'What to register',
  'Solo gli elettromedicali':
    'Medical devices only',
  'Tutti i dispositivi riconosciuti':
    'All recognised devices',
  'Spento':
    'Off',
  'Visti negli ultimi giorni':
    'Seen in the last days',
  'Esegui adesso':
    'Run now',
  '{p} dispositivi registrati o aggiornati come CI su {c} idonei; {k} già nel CMDB da una scansione, lasciati com’erano.':
    '{p} devices registered or updated as CIs out of {c} eligible; {k} already in the CMDB from a scan, left as they were.',
  'Ultimo giro: {q} · {p} registrati o aggiornati, {c} idonei, {k} già nel CMDB.':
    'Last pass: {q} · {p} registered or updated, {c} eligible, {k} already in the CMDB.',
  'Gli elettromedicali non si scansionano: la card «Inventario passivo» li registra come CI senza contattarli, dai dati che CENSO già raccoglie (porte degli switch, ascolto, agente DHCP, firewall, nome del prodotto), ogni ora o con «Esegui adesso». Si sceglie se registrare solo gli elettromedicali (predefinito) o tutti i dispositivi riconosciuti, e da quanti giorni devono essere stati visti. Un dispositivo già nel CMDB da una scansione resta com’è.':
    'Medical devices are not scanned: the “Passive inventory” card registers them as CIs without contacting them, from the data CENSO already collects (switch ports, listening, DHCP agent, firewall, product name), every hour or with “Run now”. You choose whether to register only medical devices (default) or all recognised devices, and within how many days they must have been seen. A device already in the CMDB from a scan stays as it is.',
  'CENSO MED':
    'CENSO MED',
  'Avvisi di sicurezza per i dispositivi medici':
    'Security advisories for medical devices',
  'Aggiorna ora':
    'Refresh now',
  'Gli avvisi CISA ICS Medical (ICSMA), scaricati ogni notte dal feed CSAF pubblico di CISA: per ogni prodotto CVE, gravità, versioni interessate e rimedio, con il segno «sfruttata attivamente (CISA KEV)» dal catalogo KEV. Si abbinano ai CI elettromedicali dei tenant con CENSO MED per costruttore e famiglia di prodotto; la versione del firmware va verificata.':
    'The CISA ICS Medical Advisories (ICSMA), downloaded every night from CISA’s public CSAF feed: for each product CVEs, severity, affected versions and remedy, with the “actively exploited (CISA KEV)” flag from the KEV catalogue. They are matched to the medical device CIs of tenants with CENSO MED by manufacturer and product family; the firmware version must be verified.',
  'In catalogo: {a} avvisi, {p} prodotti; ultimo aggiornamento {q}.':
    'In the catalogue: {a} advisories, {p} products; last refresh {q}.',
  'Tutte le funzioni per gli elettromedicali (riconoscimento dal prodotto, inventario passivo, confronto con gli inventari di riferimento, vulnerabilità dagli avvisi CISA per i dispositivi medici) sono la licenza CENSO MED: senza, al loro posto compare una riga che lo spiega. Le vulnerabilità arrivano fra i rischi di sicurezza dei CI elettromedicali, con «versione da verificare» quando la versione del firmware non è nota.':
    'All the medical device functions (recognition from the product, passive inventory, comparison with reference inventories, vulnerabilities from the CISA advisories for medical devices) are the CENSO MED licence: without it, a line explaining so takes their place. Vulnerabilities appear among the security findings of medical device CIs, with “version to verify” when the firmware version is not known.',
  'Connesso su':
    'Connected on',
  'porta {p}':
    'port {p}',
  'dalla tabella MAC dello switch':
    'from the switch MAC table',
  'ARP dei router (SNMP)':
    'Router ARP (SNMP)',
  'Come li scrive l’inventario di riferimento':
    'As the reference inventory writes them',
  'MAC / IP dell’inventario':
    'Inventory MAC / IP',
  'Con la stessa opzione, sugli apparati che instradano (core, switch di livello 3, router) la scansione legge anche la tabella ARP: dà l’IP di ogni dispositivo delle VLAN instradate, anche di quelli che stanno solo sulla porta di uno switch e non si annunciano (fonte «ARP dei router»).':
    'With the same option, on devices that route (core, layer-3 switches, routers) the scan also reads the ARP table: it gives the IP of every device of the routed VLANs, including those that only sit on a switch port and do not announce themselves (source “Router ARP”).',
  'Base di riconoscimento ({n})':
    'Recognition database ({n})',
  'Coperti dalle proposte':
    'Covered by proposals',
  'Copertura senza fonti esterne':
    'Coverage without external sources',
  'Cosa c’è dietro':
    'What is behind it',
  'Imparare {n} firme? Quelle globali valgono subito in tutti i tenant, le altre solo negli ospedali che le hanno insegnate. Verifica che i contratti con i fornitori degli inventari consentano di usarne le classificazioni.':
    'Learn {n} signatures? Global ones apply at once in every tenant, the others only in the hospitals that taught them. Check that the contracts with the inventory vendors allow using their classifications.',
  'Informatici':
    'IT devices',
  'Nessun caso ambiguo':
    'No ambiguous cases',
  'Nessun inventario di riferimento':
    'No reference inventory',
  'Ospedale':
    'Hospital',
  'Per ogni elettromedicale degli inventari di riferimento: CENSO lo ritroverebbe da solo, con la sua base di riconoscimento e senza Armis, Medigate o file? L’inventario serve solo da metro: ciò che CENSO dice viene dalle sue osservazioni (porte degli switch, ascolto, CMDB, filo).':
    'For each medical device in the reference inventories: would CENSO find it on its own, with its recognition database and without Armis, Medigate or files? The inventory is only the yardstick: what CENSO says comes from its own observations (switch ports, listening, CMDB, wire).',
  'Per tipo: {v}.':
    'By kind: {v}.',
  'Riconosciuti da CENSO':
    'Recognised by CENSO',
  'Senza segnali':
    'No signals',
  'Togliere questa voce? Smette subito di riconoscere {s}.':
    'Remove this entry? It stops recognising {s} at once.',
  'Un’unica base per riconoscere i dispositivi in ogni tenant: prefissi MAC, impronte DHCP, nomi di prodotto imparati, regole di prodotto e porte degli annunci in rete. Una voce vale in tutti i tenant, oppure solo nell’ospedale indicato; a parità di firma vince quella dell’ospedale. Modifiche ed eliminazioni valgono entro un minuto.':
    'A single database to recognise devices in every tenant: MAC prefixes, DHCP fingerprints, learnt product names, product rules and network announcement ports. An entry applies in every tenant, or only in the hospital shown; for the same signature the hospital’s entry wins. Changes and deletions apply within a minute.',
  'di CENSO':
    'CENSO’s own',
  'già nella base: {v}':
    'already in the database: {v}',
  'impronta DHCP':
    'DHCP fingerprint',
  'in tutti i tenant':
    'in every tenant',
  'in {h}':
    'in {h}',
  'informatica generica':
    'generic IT',
  'nome dato dall’ospedale, non dal costruttore':
    'name given by the hospital, not by the manufacturer',
  'nome imparato':
    'learnt name',
  'prefisso MAC':
    'MAC prefix',
  'regola di prodotto':
    'product rule',
  'solo':
    'only',
  'solo elettromedicali':
    'medical devices only',
  'tipi diversi':
    'different types',
  'tutti i tenant':
    'every tenant',
  'un solo ospedale':
    'one hospital only',
  '{d} dispositivi, {h} ospedali':
    '{d} devices, {h} hospitals',
  '«Coperti dalle proposte»: diventano riconosciuti imparando le proposte qui sotto. «Informatici»: postazioni e server, si riconoscono con la scansione o l’agente, mai dal prefisso. «Senza segnali»: CENSO li vede ma nulla di ciò che raccoglie dice cosa sono. «Non visti»: lì manca una sonda o una porta in SPAN.':
    '«Covered by proposals»: they become recognised by learning the proposals below. «IT devices»: workstations and servers, recognised by scanning or by the agent, never by prefix. «No signals»: CENSO sees them but nothing it collects tells what they are. «Not seen»: a probe or a SPAN port is missing there.',
  'Le firme le scrive nella base di riconoscimento l’amministrazione della piattaforma, perché la base è condivisa fra i tenant.':
    'Signatures are written into the recognition database by the platform administration, because the database is shared between tenants.',
  'si impara solo per questo ospedale':
    'learnt for this hospital only',
  'Annunci in broadcast/multicast (sonda)':
    'Broadcast/multicast announcements (probe)',
  'annuncio in rete → modello':
    'network announcement → model',
  'annuncio in rete':
    'network announcement',
  'Molti elettromedicali si annunciano sulla rete con un protocollo del costruttore, in broadcast o multicast: un monitor che cerca la sua centrale, una pompa che cerca il suo server. La sonda che ascolta la VLAN li sente anche senza porta mirror e ne tiene la porta UDP e i nomi di prodotto in chiaro; il resto del contenuto non si conserva. La porta è una firma: CENSO conosce di suo quella dei monitor Philips IntelliVue e impara le altre dagli inventari di riferimento («annuncio in rete → modello»).':
    'Many medical devices announce themselves on the network with a vendor protocol, in broadcast or multicast: a monitor looking for its central station, a pump looking for its server. The probe listening on the VLAN hears them even without a mirror port and keeps their UDP port and the product names in clear; the rest of the payload is not kept. The port is a signature: CENSO knows the one of Philips IntelliVue monitors by itself and learns the others from reference inventories («network announcement → model»).',
  'Carica MIB':
    'Upload MIBs',
  'Caricati {n} MIB.':
    'Uploaded {n} MIBs.',
  'Caricati {n} MIB; non letti: {s}.':
    'Uploaded {n} MIBs; not read: {s}.',
  'Caricato':
    'Uploaded',
  'Carico {f}…':
    'Uploading {f}…',
  'Da caricare: {m}.':
    'To upload: {m}.',
  'Dai MIB dei costruttori CENSO prende solo le registrazioni di prodotto: un OID per modello, quello che l’apparato dichiara in sysObjectID. Riconoscono subito gli apparati di questo tenant e vengono proposte all’amministrazione della piattaforma per tutti i tenant. Commenti e descrizioni si tolgono prima dell’invio; del file si conservano solo le definizioni degli OID. Si scaricano dal portale di supporto del costruttore: servono i MIB «…-PRODUCTS-MIB» o «…-SMI» e quello che li importa.':
    'From vendor MIBs CENSO takes only the product registrations: one OID per model, the one the device declares in sysObjectID. They recognise this tenant’s devices at once and are proposed to the platform administration for every tenant. Comments and descriptions are removed before sending; of the file only the OID definitions are kept. Download them from the vendor’s support portal: you need the «…-PRODUCTS-MIB» or «…-SMI» MIBs and the one they import.',
  'Impara tutte quelle nuove':
    'Learn all new ones',
  'Le registrazioni di prodotto (sysObjectID → modello) dei MIB che i tenant caricano nella pagina Rete. Valgono subito nel tenant che le ha caricate; imparandole valgono in tutti. Elencate le nuove che danno già il modello ad apparati in CMDB.':
    'The product registrations (sysObjectID → model) of the MIBs tenants upload on the Network page. They apply at once in the tenant that uploaded them; learning them makes them apply everywhere. Listed: the new ones that already name devices in the CMDB.',
  'MIB caricati dai tenant ({n})':
    'MIBs uploaded by tenants ({n})',
  'MIB di prodotto ({n})':
    'Product MIBs ({n})',
  'Modelli riconoscibili dai MIB caricati: {n}.':
    'Models recognisable from the uploaded MIBs: {n}.',
  'OID':
    'OID',
  'Registrazioni: {r}; già globali {e}, nuove {n}, in disaccordo {c}, con apparati visti {d}.':
    'Registrations: {r}; already global {e}, new {n}, in disagreement {c}, with devices seen {d}.',
  'Rendere globali tutte le registrazioni nuove su cui i MIB dei tenant concordano?':
    'Make global every new registration the tenants’ MIBs agree on?',
  'Rese globali {n} registrazioni.':
    '{n} registrations made global.',
  'Scegli i file MIB':
    'Choose the MIB files',
  'Togliere il MIB {m}? I modelli che dava non si riconoscono più in questo tenant.':
    'Remove the MIB {m}? The models it gave are no longer recognised in this tenant.',
  'identità SNMP (MIB)':
    'SNMP identity (MIB)',
  'modulo ignoto':
    'unknown module',
  '{n} definizioni restano senza OID perché manca il MIB che definisce il loro nodo padre.':
    '{n} definitions remain without an OID because the MIB defining their parent node is missing.',
  'Sotto gli apparati, «MIB di prodotto» carica i MIB dei costruttori (amministratori del tenant): CENSO ne tiene solo le registrazioni di prodotto, un OID per modello, e da lì il modello degli apparati che dichiarano quell’identità in SNMP si ricava subito, anche per marche mai viste. La card dice quali MIB mancano ancora per risolvere il resto; le registrazioni vengono proposte all’amministrazione della piattaforma per valere in tutti i tenant.':
    'Below the devices, «Product MIBs» uploads vendor MIBs (tenant administrators): CENSO keeps only their product registrations, one OID per model, and from there the model of the devices declaring that identity in SNMP is known at once, even for brands never seen. The card says which MIBs are still missing to resolve the rest; the registrations are proposed to the platform administration to apply in every tenant.',
  'Approvare il driver? Arriva a tutte le sonde entro pochi minuti.':
    'Approve the driver? It reaches every probe within a few minutes.',
  'Con i MIB del costruttore caricati nella pagina Rete, CENSO genera dalla cattura una bozza di driver con i nomi veri: tabelle, stati decodificati, numero di serie e versione, CPU e memoria per Eagle. La approva l’amministrazione della piattaforma, poi vale su tutte le sonde.':
    'With the vendor MIBs uploaded on the Network page, CENSO drafts a driver from the capture with the real names: tables, decoded states, serial number and version, CPU and memory for Eagle. The platform administration approves it, then it applies on every probe.',
  'Driver':
    'Driver',
  'Driver SNMP ({n})':
    'SNMP drivers ({n})',
  'Eliminare il driver?':
    'Delete the driver?',
  'Genera bozza di driver':
    'Draft a driver',
  'Il JSON del driver non è valido.':
    'The driver JSON is not valid.',
  'JSON del driver':
    'Driver JSON',
  'Le bozze che i tenant generano da un campionamento SNMP con i MIB del costruttore. Controlla il resoconto e il JSON (nomi, ampiezza del match), poi approva: il driver arriva a tutte le sonde, per la discovery e per le metriche di Eagle, senza aggiornarle. Un driver non contiene codice, solo OID e nomi di attributo.':
    'The drafts tenants generate from an SNMP sample with the vendor MIBs. Check the report and the JSON (names, breadth of the match), then approve: the driver reaches every probe, for discovery and for Eagle metrics, without updating them. A driver contains no code, only OIDs and attribute names.',
  'Nessun driver':
    'No drivers',
  'Rigenera la bozza di driver':
    'Regenerate the driver draft',
  'Salva il JSON':
    'Save the JSON',
  'Si generano dal rapporto di un job di campionamento SNMP.':
    'They are generated from the report of an SNMP sampling job.',
  'approvato':
    'approved',
  'approvato da {a}':
    'approved by {a}',
  'approvato: in uso su tutte le sonde':
    'approved: in use on every probe',
  'bozza':
    'draft',
  'disattivato':
    'disabled',
  'in attesa di approvazione dall’amministrazione della piattaforma':
    'awaiting approval by the platform administration',
  'Con gli stessi MIB, il rapporto di un job di campionamento SNMP genera una bozza di driver: tabelle con i nomi veri, stati decodificati, numero di serie e versione, CPU e memoria per Eagle. L’amministrazione della piattaforma la controlla e la approva; da lì il driver arriva a tutte le sonde senza aggiornarle.':
    'With the same MIBs, the report of an SNMP sampling job drafts a driver: tables with the real names, decoded states, serial number and version, CPU and memory for Eagle. The platform administration checks and approves it; from then on the driver reaches every probe without updating them.',
  'Alias e CNAME identificano i nomi DNS alternativi associati all’oggetto, utilizzabili per la risoluzione e l’accesso oltre al nome DNS principale.':
    'Aliases and CNAMEs are the alternative DNS names of the object, usable for resolution and access besides the main DNS name.',
  'Apparati di rete, porte e collegamenti':
    'Network devices, ports and links',
  'Apparati raggiungibili e presenti nel CMDB di cui non tutte le informazioni necessarie sono state rilevate. Gli apparati della stessa serie sono raggruppati in una riga.':
    'Reachable devices in the CMDB for which not all the necessary information has been collected. Devices of the same series are grouped in one row.',
  'Da usare per rimuovere record errati, duplicati o relativi ad asset di test; se l’asset è ancora rilevabile in rete, verrà acquisito nuovamente alla scansione successiva.':
    'Use it to remove wrong, duplicate or test records; if the asset can still be found on the network, it will be acquired again at the next scan.',
  'Descrive le relazioni tra CI, incluse adiacenze di rete, virtualizzazione e dipendenze rilevate.':
    'Describes the relations between CIs, including network adjacencies, virtualisation and detected dependencies.',
  'Dettaglio rischio':
    'Risk detail',
  'Dettaglio rischio ({n})':
    'Risk detail ({n})',
  "Dismette definitivamente l'asset. Il CI e il relativo storico rimangono nel CMDB.":
    'Permanently retires the asset. The CI and its history remain in the CMDB.',
  'Filtra i CI non visti da 7 giorni':
    'Filter CIs not seen for 7 days',
  'Gestisce contratti di manutenzione, supporto, noleggio e licenza, associandoli agli asset coperti per garantirne la continuità indipendentemente dal CI.':
    'Manages maintenance, support, rental and licence contracts, linking them to the covered assets so they persist regardless of the CI.',
  'Gli asset con contratto cessato o copertura scaduta sono considerati non coperti.':
    'Assets with an ended contract or expired coverage are considered not covered.',
  'Gli indirizzi attraverso cui l’asset è raggiungibile da Internet: NAT, VIP o indirizzo della sede. Ogni indirizzo è gestito come un CI distinto e può essere condiviso da più asset. La verifica ne controlla la reputazione nelle liste di abuso.':
    'The addresses through which the asset can be reached from the Internet: NAT, VIP or site address. Each address is managed as a separate CI and can be shared by several assets. The check looks up its reputation in abuse lists.',
  'Indica la fonte di ciascun attributo del CI, gestendo le priorità in caso di dati provenienti da fonti diverse o discordanti.':
    'Shows the source of each CI attribute, handling priorities when data come from different or conflicting sources.',
  'Indica le fonti che hanno rilevato il CI e il numero di attributi attualmente gestiti da ciascuna fonte, evidenziandone il livello di conoscenza.':
    'Shows the sources that detected the CI and how many attributes each source currently manages, highlighting how well it knows it.',
  "La classe del dispositivo viene rilevata in automatico. Se non è corretta, scegli quella giusta nel menu Classe: la scelta resta anche nelle scansioni successive, finché non si torna all'automatico.":
    'The device class is detected automatically. If it is wrong, choose the right one in the Class menu: the choice stays through later scans, until you go back to automatic.',
  'La mappa rappresenta la topologia di rete; {n} collegamenti di traffico nascosti sono visualizzabili selezionando un asset o la vista {dipendenze}.':
    'The map shows the network topology; {n} hidden traffic links can be shown by selecting an asset or the {dipendenze} view.',
  'La mappa visualizza un sottoinsieme delle relazioni rilevate: i {n} collegamenti più osservati su {totale}, {nascosti} collegamenti di traffico nascosti. Applica i filtri per tipologia di relazione, seleziona «Dipendenze» o un nodo per analizzarne la topologia locale.':
    'The map shows a subset of the detected relations: the {n} most observed links out of {totale}, {nascosti} traffic links hidden. Apply the relation type filters, select «Dependencies» or a node to analyse its local topology.',
  'La mappa visualizza un sottoinsieme delle relazioni rilevate: i {n} collegamenti più osservati su {totale}. Applica i filtri per tipologia di relazione, seleziona «Dipendenze» o un nodo per analizzarne la topologia locale.':
    'The map shows a subset of the detected relations: the {n} most observed links out of {totale}. Apply the relation type filters, select «Dependencies» or a node to analyse its local topology.',
  'La tabella di routing L3 dell’asset viene acquisita via SNMP e popolata tramite una discovery SNMP con l’opzione «Tabelle di routing» abilitata.':
    'The asset’s L3 routing table is collected via SNMP and filled by an SNMP discovery with the «Routing tables» option enabled.',
  'Nessun MIB di prodotto importato: il sistema non può ricavare il modello dall’OID che l’apparato dichiara in {campo}. Scaricali dal portale di supporto del costruttore e caricali qui sotto, in «MIB di prodotto».':
    'No product MIB imported: the system cannot derive the model from the OID the device declares in {campo}. Download them from the vendor’s support portal and upload them below, in «Product MIBs».',
  'Nessun contratto da esportare':
    'No contracts to export',
  'Nessun elemento di sicurezza rilevato. L’ultima valutazione è ancora valida: per aggiornarla con i dati attuali dell’inventario, premi «Esegui valutazione».':
    'No security item found. The last assessment is still valid: to update it with the current inventory data, press «Run assessment».',
  'Porte up':
    'Ports up',
  'Raccoglie gli asset posseduti e amministrati, integrando i dati della discovery con informazioni gestionali non rilevabili dalla rete, come proprietà, costi e contratti.':
    'Collects the owned and managed assets, adding to the discovery data management information the network cannot detect, such as ownership, costs and contracts.',
  'Registra l’asset come bene per integrarlo con le informazioni amministrative e contabili non rilevabili dalla rete: proprietà, costo, centro di costo e contratto associato.':
    'Register the asset to add the administrative and accounting information the network cannot detect: ownership, cost, cost centre and linked contract.',
  'Registra un CI come asset dalla relativa scheda oppure modifica i filtri per ampliare i risultati.':
    'Register a CI as an asset from its page, or change the filters to widen the results.',
  'Registra un contratto per monitorare scadenze, asset coperti e costi.':
    'Register a contract to track expiries, covered assets and costs.',
  'Salva filtri e posizioni dei nodi in una vista condivisa con tutti gli utenti del tenant autorizzati.':
    'Saves filters and node positions in a view shared with all authorised users of the tenant.',
  '{n} CI non ancora registrati come asset. Dalla {scheda} se ne consente la registrazione, precompilando i dati già acquisiti dalla discovery (matricola, costruttore, modello e collocazione).':
    '{n} CIs not yet registered as assets. From the {scheda} they can be registered, with the data already collected by discovery prefilled (serial number, vendor, model and location).',
  '«Indirizzo aggiuntivo»: un indirizzo di un apparato oltre quello di gestione, come le interfacce VLAN, le loopback e i VIP. «IP pubblico»: un indirizzo con cui un asset è raggiungibile da Internet.':
    '«Additional address»: an address of a device besides the management one, such as VLAN interfaces, loopbacks and VIPs. «Public IP»: an address through which an asset can be reached from the Internet.',
  '«Segnalato» indica la presenza di un rischio associato all’indirizzo. «Non verificabile» indica che la verifica non ha prodotto un risultato. Gli indirizzi non più associati ad alcun CI vengono rimossi dall’inventario.':
    '«Flagged» means there is a risk associated with the address. «Not verifiable» means the check produced no result. Addresses no longer linked to any CI are removed from the inventory.',
  'Solo i CI non visti da {n} giorni, esclusi i dismessi.':
    'Only CIs not seen for {n} days, retired ones excluded.',
  'Solo i CI non ancora registrati come asset.':
    'Only CIs not yet registered as assets.',
  'Mostra le porte con un vicino':
    'Show the ports with a neighbour',
  'Mostra tutte le porte':
    'Show all ports',
  'Porte di {nome} con un vicino':
    'Ports of {nome} with a neighbour',
  'Porte di {nome} ({su}/{totale} up)':
    'Ports of {nome} ({su}/{totale} up)',
  'Anche l’infrastruttura (host, cluster, datastore, rete, storage) eredita la criticità dei servizi che sostiene. Vale per tutti i servizi del tenant.':
    'Infrastructure too (hosts, clusters, datastores, network, storage) inherits the criticality of the services it supports. Applies to all the tenant’s services.',
  'Business service ({n})':
    'Business services ({n})',
  'Criticità ereditata':
    'Inherited criticality',
  'Da quando':
    'Since',
  'Dipendenze ({n})':
    'Dependencies ({n})',
  'Dipendenze ricalcolate: {n} ({a} nuove, {r} uscite).':
    'Dependencies recomputed: {n} ({a} new, {r} gone).',
  'I CI che compongono il servizio, salvati dalla composizione ogni ora: ingressi, componenti della catena delle chiamate e l’infrastruttura sotto. Ogni CI li vede nella sua sezione «Business service» e ne eredita la criticità.':
    'The CIs making up the service, saved from the composition every hour: entries, components of the call chain and the infrastructure under them. Each CI sees them in its «Business services» section and inherits the criticality.',
  'I servizi di business di cui questo CI fa parte, ricavati dalla composizione di ciascun servizio (ingressi, catena delle chiamate, infrastruttura sotto) e aggiornati ogni ora.':
    'The business services this CI is part of, derived from each service’s composition (entries, call chain, infrastructure under it) and updated every hour.',
  'Il CI eredita la criticità più alta fra i servizi di cui è ingresso o componente; come infrastruttura li sostiene senza ereditarla, perché è condivisa da molti servizi.':
    'The CI inherits the highest criticality among the services it is an entry or component of; as infrastructure it supports them without inheriting it, because it is shared by many services.',
  'Il CI eredita la criticità più alta fra i suoi servizi, anche come infrastruttura.':
    'The CI inherits the highest criticality among its services, including as infrastructure.',
  'Metti il tag a tutte quelle elencate ({n})':
    'Tag all those listed ({n})',
  'Metti il tag alle {n} scelte':
    'Tag the {n} selected',
  'Nessuna dipendenza salvata':
    'No saved dependencies',
  'Porte, processo':
    'Ports, process',
  'Ricalcola ora':
    'Recompute now',
  'Si calcolano entro un’ora dalla creazione del servizio, oppure subito con «Ricalcola ora».':
    'They are computed within an hour of creating the service, or at once with «Recompute now».',
  'Tag da mettere':
    'Tags to add',
  'Tag messi su {n} dipendenze ({a} nuove assegnazioni).':
    'Tags put on {n} dependencies ({a} new assignments).',
  'Ultimo calcolo: {q}.':
    'Last computed: {q}.',
  'chiave:valore, separati da virgola':
    'key:value, comma separated',
  'componente':
    'component',
  'infrastruttura':
    'infrastructure',
  'ingresso':
    'entry',
  'livello {n}':
    'level {n}',
  'non ereditata':
    'not inherited',
  'processo non noto':
    'process unknown',
  'Ogni ora CENSO salva le dipendenze di ciascun servizio (ingressi, componenti della catena delle chiamate, infrastruttura sotto): nella pagina del servizio la card «Dipendenze» le elenca, le ricalcola subito con «Ricalcola ora» e mette un tag a tutte o a quelle scelte. Ogni CI che ne fa parte ha la sezione «Business service» nella sua scheda, con il ruolo, le porte su cui viene chiamato, il processo in ascolto (se c’è l’inventario avanzato) e la criticità che eredita: la più alta fra i servizi di cui è ingresso o componente. L’infrastruttura condivisa li sostiene senza ereditarla, salvo attivarlo nella stessa card. Nell’elenco dei CI la criticità ereditata è un filtro e una colonna.':
    'Every hour CENSO saves the dependencies of each service (entries, components of the call chain, infrastructure under them): on the service page the «Dependencies» card lists them, recomputes them at once with «Recompute now» and tags all of them or the chosen ones. Each CI that is part of it has the «Business services» section on its page, with the role, the ports it is called on, the listening process (when there is advanced inventory) and the criticality it inherits: the highest among the services it is an entry or component of. Shared infrastructure supports them without inheriting it, unless enabled in the same card. In the CI list the inherited criticality is a filter and a column.',
  'Dichiarato dall’eseguibile, non trovato nell’inventario software del CI':
    'Declared by the executable, not found in the CI’s software inventory',
  'Gli asset a supporto di funzioni critiche o importanti sono identificati tramite i business service. Per ciascun servizio sono indicati il numero di componenti e quelli con conformità critica o parziale. Nella tabella degli host, a parità di fascia di conformità, hanno priorità i componenti dei servizi più critici.': 'The assets supporting critical or important functions are identified through the business services. For each service, the number of components and those with critical or partial compliance are shown. In the host table, at equal compliance level, the components of the most critical services take priority.',
  'Asset ordinati per livello di rischio, con priorità rivolta ai Business Service più critici.': 'Assets ordered by risk level, prioritising the most critical Business Services.',
  'Il processo in ascolto su una porta e il software a cui appartiene arrivano dall’inventario avanzato (SSH o WinRM): su Linux il pacchetto che contiene l’eseguibile, su Windows il prodotto dichiarato dall’eseguibile, abbinati all’inventario software del CI. Sugli apparati senza inventario avanzato non sono noti.':
    'The process listening on a port and the software it belongs to come from the advanced inventory (SSH or WinRM): on Linux the package containing the executable, on Windows the product the executable declares, matched to the CI’s software inventory. On devices without advanced inventory they are not known.',
  'Infrastruttura':
    'Infrastructure',
  'Non conformi':
    'Not compliant',
  'dall’eseguibile':
    'from the executable',
  'Accanto al processo compare il software a cui appartiene: su Linux il pacchetto che contiene l’eseguibile, su Windows il prodotto dichiarato dall’eseguibile, abbinati all’inventario software del CI. La criticità ereditata ordina anche Security health («Da dove cominciare»: a parità di fascia prima i componenti dei servizi più critici) e i rapporti NIS2 e DORA, che hanno una tabella dei business service con i componenti non conformi.':
    'Next to the process comes the software it belongs to: on Linux the package containing the executable, on Windows the product the executable declares, matched to the CI’s software inventory. The inherited criticality also orders Security health («Where to start»: for the same band the components of the most critical services first) and the NIS2 and DORA reports, which have a table of business services with their non-compliant components.',
  'L’elenco si esporta con i pulsanti «Esporta Excel» e «Esporta PDF» in alto (il PDF è il report, con intestazione CENSO): servizio, criticità effettiva e dichiarata, responsabile, punti di ingresso, profondità e Business App collegate.':
    'The list exports with the “Export Excel” and “Export PDF” buttons at the top (the PDF is the report, with the CENSO header): service, effective and declared criticality, owner, entry points, depth and linked Business Apps.',
  'L’elenco si esporta con i pulsanti «Esporta Excel» e «Esporta PDF» in alto (il PDF è il report, con intestazione CENSO): applicazione, criticità, numero di funzionalità, elementi e CI, i Business Service che la usano e il responsabile.':
    'The list exports with the “Export Excel” and “Export PDF” buttons at the top (the PDF is the report, with the CENSO header): application, criticality, number of functions, elements and CIs, the Business Services using it and the owner.',
  'comportamento nei flussi':
    'flow behaviour',
  'chiama {p}':
    'calls {p}',
  'risponde su {p}':
    'answers on {p}',
  'Accendi «SBOM» negli agenti CENSO dei server (Discovery → Agenti d’inventario → Impostazioni): alla consegna successiva le applicazioni compaiono qui. In alternativa importa un documento CycloneDX o SPDX dalla sezione «Software (SBOM)» di un CI.':
    'Turn on «SBOM» in the CENSO agents of your servers (Discovery → Inventory agents → Settings): at the next delivery the applications appear here. Alternatively import a CycloneDX or SPDX document from the «Software (SBOM)» section of a CI.',
  'Accendi «SBOM» nelle impostazioni dell’agente CENSO di questa macchina, oppure importa un documento CycloneDX o SPDX.':
    'Turn on «SBOM» in the settings of this machine’s CENSO agent, or import a CycloneDX or SPDX document.',
  'Applicazioni':
    'Applications',
  'Avvisi':
    'Advisories',
  'Corretta in':
    'Fixed in',
  'Ecosistema':
    'Ecosystem',
  'Ecosistemi':
    'Ecosystems',
  'Elimina l’importazione':
    'Delete the import',
  'Eseguibile':
    'Executable',
  'Importa un documento CycloneDX o SPDX (JSON)':
    'Import a CycloneDX or SPDX document (JSON)',
  'Importata':
    'Imported',
  'importata':
    'imported',
  'Importata «{a}» ({f}): {n} componenti, {v} vulnerabili.':
    'Imported «{a}» ({f}): {n} components, {v} vulnerable.',
  'Importazione…':
    'Importing…',
  'La software bill of materials di ogni server: tutto il software installato e le librerie che contiene, con versione, licenza e vulnerabilità note; porte e processi legano ogni applicazione alle chiamate e al traffico. È l’add-on CENSO SBOM, e serve al registro DORA delle dipendenze ICT e a rispondere in pochi minuti a «dove abbiamo questa libreria?» quando esce una vulnerabilità.':
    'The software bill of materials of every server: all the installed software and the libraries it contains, with version, licence and known vulnerabilities; ports and processes tie each application to calls and traffic. It is the CENSO SBOM add-on, and it serves the DORA register of ICT dependencies and answers «where do we have this library?» in minutes when a vulnerability comes out.',
  'Il software installato su questo CI con le librerie che contiene, letto dall’agente CENSO (cartelle di installazione, processi, servizi, siti IIS), più quello importato da documenti CycloneDX o SPDX (fornitori, pipeline di build). Porte e processo, quando ci sono, legano l’applicazione alle chiamate e al traffico. Le vulnerabilità vengono dal catalogo OSV e finiscono anche fra i rischi di sicurezza del CI.':
    'The software installed on this CI with the libraries it contains, read by the CENSO agent (install folders, processes, services, IIS sites), plus what is imported from CycloneDX or SPDX documents (vendors, build pipelines). Ports and process, when present, tie the application to calls and traffic. Vulnerabilities come from the OSV catalogue and also appear among the CI’s security findings.',
  'Inventario di software, componenti e librerie rilevati sui server tramite agente o importazione di SBOM nei formati CycloneDX e SPDX. La correlazione tra processi, porte in ascolto e traffico collega i componenti software alle dipendenze applicative, alimenta il registro DORA e contribuisce all’identificazione dei sistemi impattati da vulnerabilità.':
    'Inventory of software, components and libraries found on the servers through the agent or by importing SBOMs in CycloneDX and SPDX formats. Correlating processes, listening ports and traffic links the software components to the application dependencies, feeds the DORA register and helps identify the systems affected by vulnerabilities.',
  'Le vulnerabilità vengono dal catalogo OSV (lo stesso dei pacchetti di sistema, aggiornato da Sicurezza → catalogo) e diventano rischi «Libreria vulnerabile» sul CI, con la versione che le corregge e il segno «sfruttata attivamente (CISA KEV)» dal catalogo CISA KEV. Una versione sconosciuta non è considerata vulnerabile.':
    'Vulnerabilities come from the OSV catalogue (the same as for system packages, updated from Security → catalogue) and become «Vulnerable library» findings on the CI, with the version that fixes them and the «actively exploited (CISA KEV)» flag from the CISA KEV catalogue. An unknown version is not considered vulnerable.',
  'Leggi il software installato e le sue librerie (CENSO SBOM)':
    'Read the installed software and its libraries (CENSO SBOM)',
  'Letta dall’agente CENSO':
    'Read by the CENSO agent',
  'Librerie vulnerabili':
    'Vulnerable libraries',
  'Licenze più usate':
    'Most used licences',
  'L’agente legge tutto il software installato — cartelle di installazione (Program Files e le cartelle in radice dei dischi, /opt, /srv, /usr/local, /var/www), processi in esecuzione, servizi anche fermi, siti IIS, librerie globali di pip e npm, più i percorsi personalizzati — e ne legge i componenti (Java, Node.js, Python, .NET, PHP, Ruby, Go) a bassa priorità e con un tempo massimo. Le porte in ascolto servono a legare le applicazioni a chiamate e traffico. Il risultato va nella Mappa software e nella sezione SBOM del CI. Richiede la licenza CENSO SBOM.':
    'The agent reads all the installed software — install folders (Program Files and the top-level folders of the drives, /opt, /srv, /usr/local, /var/www), running processes, services also when stopped, IIS sites, global pip and npm libraries, plus the custom paths — and reads their components (Java, Node.js, Python, .NET, PHP, Ruby, Go) at low priority and within a time limit. Listening ports tie applications to calls and traffic. The result goes to the Software map and the CI’s SBOM section. Requires the CENSO SBOM licence.',
  'Nella pagina di un business service la card «Software del servizio (SBOM)»: per ogni componente, le applicazioni in ascolto sulle porte su cui il servizio lo chiama; si scarica in CycloneDX per il registro DORA.':
    'On a business service page the «Service software (SBOM)» card: for each component, the applications listening on the ports the service calls it on; it downloads as CycloneDX for the DORA register.',
  'Nessun componente riconosciuto in questa applicazione.':
    'No component recognised in this application.',
  'Nessuna applicazione':
    'No application',
  'Nessuna applicazione ancora':
    'No application yet',
  'Nessuno SBOM sui componenti del servizio: accendi «SBOM» negli agenti CENSO dei server, o importa i documenti dei fornitori nella sezione «Software (SBOM)» dei CI.':
    'No SBOM on the service’s components: turn on «SBOM» in the servers’ CENSO agents, or import the vendors’ documents in the CIs’ «Software (SBOM)» section.',
  'Non dichiarata':
    'Not declared',
  'Ogni consegna dell’agente sostituisce le applicazioni lette prima; un documento importato sostituisce quello con lo stesso nome di applicazione. Se l’elenco è troppo grande per una consegna si omettono prima i percorsi e poi i componenti in eccesso, e l’applicazione risulta «troncata».':
    'Each agent delivery replaces the applications read before; an imported document replaces the one with the same application name. If the list is too large for a delivery, first the paths and then the excess components are left out, and the application shows as «truncated».',
  'Per ogni componente del servizio, le applicazioni in ascolto sulle porte su cui il servizio lo chiama, con le loro librerie: la distinta del software del servizio, da allegare al registro DORA.':
    'For each component of the service, the applications listening on the ports the service calls it on, with their libraries: the service’s software bill of materials, to attach to the DORA register.',
  'SBOM':
    'SBOM',
  'Scarica CycloneDX':
    'Download CycloneDX',
  'Senza la licenza CENSO SBOM la pagina lo spiega e l’agente non legge nulla anche con l’opzione accesa.':
    'Without the CENSO SBOM licence the page says so and the agent reads nothing even with the option on.',
  'Software (SBOM)':
    'Software (SBOM)',
  'Software del servizio (SBOM)':
    'Service software (SBOM)',
  'Solo i componenti vulnerabili':
    'Only vulnerable components',
  'Un documento con lo stesso nome di applicazione sostituisce quello importato prima.':
    'A document with the same application name replaces the one imported before.',
  'Vulnerabili':
    'Vulnerable',
  'alla consegna successiva l’agente legge tutto il software installato: le cartelle di installazione (Program Files e le cartelle in radice dei dischi su Windows; /opt, /srv, /usr/local, /var/www e Tomcat su Linux), i processi in esecuzione, i servizi anche fermi, i siti IIS, le librerie globali di pip e npm e i percorsi personalizzati; ne legge i componenti — Java (jar, war, ear, anche annidati), Node.js, Python, .NET, PHP (Composer), Ruby (Bundler), eseguibili Go — a bassa priorità e con un tempo massimo; le porte in ascolto e i processi si legano all’applicazione a cui appartengono;':
    'at the next delivery the agent reads all the installed software: the install folders (Program Files and the top-level folders of the drives on Windows; /opt, /srv, /usr/local, /var/www and Tomcat on Linux), running processes, services also when stopped, IIS sites, global pip and npm libraries and the custom paths; it reads their components — Java (jar, war, ear, nested too), Node.js, Python, .NET, PHP (Composer), Ruby (Bundler), Go executables — at low priority and within a time limit; listening ports and processes are tied to the application they belong to;',
  'corretta in {v}':
    'fixed in {v}',
  'elenco troncato (limiti di lettura)':
    'list truncated (read limits)',
  'installa l’agente CENSO sui server (Discovery → Agenti d’inventario) e nelle sue impostazioni accendi «SBOM»;':
    'install the CENSO agent on the servers (Discovery → Inventory agents) and turn on «SBOM» in its settings;',
  'per il software che l’agente non può leggere (appliance, prodotti chiusi) importa il documento CycloneDX o SPDX del fornitore o della vostra pipeline di build dalla sezione «Software (SBOM)» del CI.':
    'for software the agent cannot read (appliances, closed products) import the CycloneDX or SPDX document from the vendor or from your build pipeline in the CI’s «Software (SBOM)» section.',
  'porte':
    'ports',
  'sfruttata attivamente (CISA KEV)':
    'actively exploited (CISA KEV)',
  '{a} applicazioni, {c} componenti, {v} vulnerabili.':
    '{a} applications, {c} components, {v} vulnerable.',
  '{c} componenti, {v} vulnerabili.':
    '{c} components, {v} vulnerable.',
  '{n} componenti':
    '{n} components',
  '{n} vulnerabili':
    '{n} vulnerable',
  '{n} vulnerabilità':
    '{n} vulnerabilities',
  'Aggiungi una regola':
    'Add a rule',
  'Cartella':
    'Folder',
  'Cartella come applicazione (SBOM)':
    'Folder as application (SBOM)',
  'Contenuto del file':
    'File content',
  'Cosa leggere':
    'What to read',
  'Definisce percorsi aggiuntivi in cui l’agente ricerca software installato. Per ogni regola specifica percorso, pattern di file o cartelle e dati da acquisire; un’espressione regolare può estrarre nome, versione e produttore. Le regole valgono per tutti gli agenti del tenant e per gli inventari software. Le cartelle classificate come applicazione vengono incluse nello SBOM, se abilitato.':
    'Where the agent looks for installed software besides the places it already knows: a folder (also with * and variables such as %ProgramFiles% or $HOME), a pattern for the names of the files or folders to look at (e.g. *.jar, version.txt, app-*) and what to read. The regular expression extracts the product with the groups (?P<name>…), (?P<version>…) and (?P<vendor>…). They apply to every agent of the tenant, at each inventory that includes software (Fast + software, Full + software); folders «as application» go to the SBOM when it is on.',
  'Espressione regolare':
    'Regular expression',
  'Modello dei nomi':
    'Name pattern',
  'Nessuna regola: l’agente usa solo la ricerca integrata.':
    'No rule: the agent uses only its built-in search.',
  'Nome del file':
    'File name',
  'Nome del prodotto (fisso)':
    'Product name (fixed)',
  'Nome della cartella':
    'Folder name',
  'Percorsi software personalizzati':
    'Custom software paths',
  'Produttore (fisso)':
    'Vendor (fixed)',
  'Salvate: gli agenti le ricevono al prossimo contatto.':
    'Saved: the agents receive them at their next contact.',
  'Togli la regola':
    'Remove the rule',
  'es. Gestionale Acme':
    'e.g. Acme ERP',
  '«Percorsi software personalizzati», in fondo alla pagina: per il software che l’agente non trova da solo (un applicativo interno in /opt, un prodotto scompattato in una cartella di Windows) si indicano la cartella, il modello dei nomi e cosa leggere — il nome del file, il contenuto di un file di testo (es. version.txt), il nome della cartella, oppure la cartella come applicazione per lo SBOM (se contiene più programmi, ognuno nella sua sottocartella diventa un’applicazione con il suo nome) — con un’espressione regolare che estrae nome, versione e produttore. Valgono per tutti gli agenti del tenant, anche solo per Windows o Linux, e arrivano al prossimo contatto; i prodotti trovati compaiono nell’inventario software del CI con origine «custom_path» e sono valutati come gli altri.':
    '«Custom software paths», at the bottom of the page: for software the agent does not find by itself (an in-house application in /opt, a product unpacked in a Windows folder) you give the folder, the name pattern and what to read — the file name, the content of a text file (e.g. version.txt), the folder name, or the folder as an application for the SBOM (if it holds several programs, each in its own subfolder becomes an application with its name) — with a regular expression extracting name, version and vendor. They apply to every agent of the tenant, optionally only Windows or Linux, and arrive at the next contact; the products found appear in the CI’s software inventory with source «custom_path» and are assessed like the others.',
  'Dipendenza':
    'Dependency',
  'Diretta: dichiarata dall’applicazione. Transitiva: arriva tramite un’altra libreria.':
    'Direct: declared by the application. Transitive: arrives through another library.',
  'Dirette e transitive':
    'Direct and transitive',
  'Ogni componente è «diretto» se l’applicazione lo dichiara (package.json, il pom.xml dentro il war o il jar eseguibile, il progetto nel deps.json, composer.json, Gemfile.lock; per Python i pacchetti che nessun altro richiede) o «transitivo» se arriva tramite un’altra libreria; per gli eseguibili Go non si sa. Dai documenti importati lo dice il grafo delle dipendenze. Il rischio di una libreria transitiva suggerisce di aggiornare quella che la porta o di forzarne la versione.':
    'Each component is «direct» when the application declares it (package.json, the pom.xml inside the war or executable jar, the project in deps.json, composer.json, Gemfile.lock; for Python the packages no other package requires) or «transitive» when it arrives through another library; for Go executables it is not known. For imported documents the dependency graph says it. The finding of a transitive library suggests updating the library that brings it or forcing its version.',
  'Ogni libreria vulnerabile è anche un rischio di sicurezza sul CI che la contiene («Libreria vulnerabile»), con la versione che la corregge. Una dipendenza transitiva non è dichiarata dall’applicazione: si corregge aggiornando la libreria che la porta o forzandone la versione.':
    'Every vulnerable library is also a security finding on the CI that contains it («Vulnerable library»), with the version that fixes it. A transitive dependency is not declared by the application: it is fixed by updating the library that brings it or by forcing its version.',
  'Solo dirette':
    'Direct only',
  'Solo transitive':
    'Transitive only',
  'diretta':
    'direct',
  'transitiva':
    'transitive',
  '{d} dirette, {tr} transitive':
    '{d} direct, {tr} transitive',
  'Installata':
    'Installed',
  'Percorso personalizzato':
    'Custom path',
  'Mostra come albero delle dipendenze':
    'Show as dependency tree',
  'Per ogni libreria CENSO sa anche da quali altre dipende (l’albero): «Mostra come albero delle dipendenze» lo apre a partire dalle librerie dirette, e sotto ogni libreria transitiva compare la catena che la porta («tramite spring-boot-starter-web → spring-webmvc»). Il rischio di una libreria vulnerabile transitiva nomina la libreria diretta da aggiornare. L’albero si legge dai manifest delle librerie installate (pom.xml, package.json, METADATA, deps.json, composer.lock, Gemfile.lock) e, nei documenti importati, dal grafo CycloneDX o dalle relazioni SPDX.':
    'For every library CENSO also knows which others it depends on (the tree): «Show as dependency tree» opens it from the direct libraries, and under each transitive library the chain that brings it appears («via spring-boot-starter-web → spring-webmvc»). The finding of a vulnerable transitive library names the direct library to update. The tree is read from the manifests of the installed libraries (pom.xml, package.json, METADATA, deps.json, composer.lock, Gemfile.lock) and, in imported documents, from the CycloneDX graph or the SPDX relationships.',
  'già sopra':
    'already above',
  'tramite':
    'via',
  'Acceso ma non in ascolto.':
    'Switched on but not listening.',
  'Chiamate':
    'Calls',
  'Chiamate in uscita':
    'Outgoing calls',
  'Ciò che le applicazioni di questo server fanno davvero, dalle loro tracce OpenTelemetry raccolte dall’agente CENSO: le operazioni che ricevono, le chiamate che fanno (servizi, database, code) con il CI che le riceve, e quali servizi chiamano quali. Le tracce restano sul server: arrivano solo conteggi, errori e tempi riassunti ogni cinque minuti.':
    'What the applications of this server really do, from their OpenTelemetry traces collected by the CENSO agent: the operations they receive, the calls they make (services, databases, queues) with the CI receiving them, and which services call which. The traces stay on the server: only counts, errors and times summarised every five minutes arrive.',
  'Il valore più alto fra i 95° percentili di cinque minuti: un limite superiore.':
    'The highest of the five-minute 95th percentiles: an upper bound.',
  'In ascolto su {a}.':
    'Listening on {a}.',
  'L’agente apre un ricevitore OTLP/HTTP solo su 127.0.0.1:4318, la porta predefinita degli SDK e del Collector: nelle applicazioni basta OTEL_EXPORTER_OTLP_ENDPOINT=http://127.0.0.1:4318 (protocollo http/protobuf o http/json); un OpenTelemetry Collector già presente sulla macchina vi esporta con otlphttp. Le tracce restano sul server: ogni cinque minuti l’agente manda solo operazioni, chiamate e legami fra servizi con conteggi, errori e tempi, senza query, SQL, header o contenuti. Serve l’agente residente; gRPC non è ancora supportato.':
    'The agent opens an OTLP/HTTP receiver only on 127.0.0.1:4318, the default port of the SDKs and of the Collector: in the applications OTEL_EXPORTER_OTLP_ENDPOINT=http://127.0.0.1:4318 is enough (protocol http/protobuf or http/json); an OpenTelemetry Collector already on the machine exports to it with otlphttp. The traces stay on the server: every five minutes the agent sends only operations, calls and links between services with counts, errors and times, without queries, SQL, headers or contents. It needs the resident agent; gRPC is not supported yet.',
  'L’agente consegna e basta: non apre porte verso la rete, non accetta comandi. La direzione è una sola, da dentro verso la piattaforma. L’unica porta che può aprire, se accendi «Runtime», è il ricevitore OpenTelemetry su 127.0.0.1:4318, raggiungibile solo dalla macchina stessa.':
    'The agent only delivers: it opens no ports towards the network and accepts no commands. There is one direction only, from inside towards the platform. The only port it can open, if you turn on «Runtime», is the OpenTelemetry receiver on 127.0.0.1:4318, reachable only from the machine itself.',
  'Media ms':
    'Avg ms',
  'Nessuna traccia OpenTelemetry dai componenti del servizio: accendi «Runtime» negli agenti CENSO dei server e manda le tracce delle applicazioni a 127.0.0.1:4318.':
    'No OpenTelemetry trace from the service’s components: turn on «Runtime» in the servers’ CENSO agents and send the applications’ traces to 127.0.0.1:4318.',
  'Nessuna traccia nel periodo scelto.':
    'No trace in the chosen period.',
  'Operazione':
    'Operation',
  'Operazioni ricevute':
    'Operations received',
  'Porta, applicazione':
    'Port, application',
  'Ricevi le tracce OpenTelemetry delle applicazioni (CENSO SBOM)':
    'Receive the applications’ OpenTelemetry traces (CENSO SBOM)',
  'Runtime (OpenTelemetry)':
    'Runtime (OpenTelemetry)',
  'Runtime: con «Runtime» acceso nelle impostazioni dell’agente, le applicazioni del server mandano le loro tracce OpenTelemetry a 127.0.0.1:4318 (OTEL_EXPORTER_OTLP_ENDPOINT, oppure un Collector con otlphttp). Le tracce restano sul server: ogni cinque minuti arrivano a CENSO le operazioni ricevute, le chiamate in uscita (servizi, database, code, con il CI che le riceve) e quali servizi chiamano quali, con conteggi, errori e tempi. Le vedi nella sezione «Runtime» del CI e nella vista «Runtime» del business service, accanto a «Struttura» (lo SBOM): lì solo le operazioni sulle porte su cui il servizio chiama quel componente e ciò che quei servizi chiamano dopo.':
    'Runtime: with «Runtime» on in the agent settings, the server’s applications send their OpenTelemetry traces to 127.0.0.1:4318 (OTEL_EXPORTER_OTLP_ENDPOINT, or a Collector with otlphttp). The traces stay on the server: every five minutes CENSO receives the operations received, the outgoing calls (services, databases, queues, with the CI receiving them) and which services call which, with counts, errors and times. You see them in the CI’s «Runtime» section and in the business service’s «Runtime» view, beside «Structure» (the SBOM): there only the operations on the ports the service calls that component on, and what those services call next.',
  'Servizi che chiamano servizi':
    'Services calling services',
  'Struttura':
    'Structure',
  'p95 ms':
    'p95 ms',
  'I pacchetti del sistema operativo, pubblicati dalla distribuzione (o da Microsoft su Windows), dall’inventario software del CI. Le loro vulnerabilità sono nella sezione Sicurezza del CI.':
    'The operating system’s packages, published by the distribution (or by Microsoft on Windows), from the CI’s software inventory. Their vulnerabilities are in the CI’s Security section.',
  'Il software installato che non è del sistema operativo: pacchetti di terze parti anche se installati con rpm/deb, programmi di altri produttori, prodotti scompattati, percorsi personalizzati. Dall’inventario software del CI; le vulnerabilità sono nella sezione Sicurezza.':
    'The installed software that is not the operating system: third-party packages even when installed with rpm/deb, other publishers’ programs, unpacked products, custom paths. From the CI’s software inventory; the vulnerabilities are in the Security section.',
  'Lo SBOM di un server comprende anche l’inventario software del CI, separato in due: «Software di sistema» (i pacchetti rpm, deb, apk pubblicati dalla distribuzione, e su Windows i programmi Microsoft) e «Software extra sistema» (pacchetti di terze parti anche se installati con rpm/deb, programmi di altri produttori, prodotti scompattati, percorsi personalizzati), con i loro purl, anche nell’export CycloneDX; le loro vulnerabilità restano nella sezione Sicurezza del CI. La distinzione si fa dal produttore dichiarato dal pacchetto: un pacchetto senza produttore conta come di sistema. I programmi della distribuzione (sotto /usr, /etc, /lib…) e gli eseguibili senza librerie né porte non sono applicazioni. Un’applicazione Node senza node_modules si legge dai lock file (package-lock.json, yarn.lock, pnpm-lock.yaml); un’applicazione impacchettata senza manifest viene segnalata fra le lacune dell’inventario: in quel caso servono i percorsi personalizzati o lo SBOM della build.':
    'A server’s SBOM also includes the CI’s software inventory, split in two: «System software» (the rpm, deb, apk packages published by the distribution, and Microsoft programs on Windows) and «Software outside the system» (third-party packages even when installed with rpm/deb, other publishers’ programs, unpacked products, custom paths), with their purls, also in the CycloneDX export; their vulnerabilities stay in the CI’s Security section. The split comes from the vendor the package declares: a package without a vendor counts as system. The distribution’s programs (under /usr, /etc, /lib…) and executables without libraries or ports are not applications. A Node application without node_modules is read from its lock files (package-lock.json, yarn.lock, pnpm-lock.yaml); a packaged application without a manifest is reported among the inventory gaps: then the custom paths or the build’s SBOM are needed.',
  'Software di sistema':
    'System software',
  'Software extra sistema':
    'Software outside the system',
  'Mostra il dettaglio':
    'Show the detail',
  'Nascondi il dettaglio':
    'Hide the detail',
  'Non è dichiarata dall’applicazione: arriva tramite {catena}. Si corregge aggiornando {prima} o forzando la versione di {nome}.':
    'It is not declared by the application: it arrives via {catena}. It is fixed by updating {prima} or by forcing the version of {nome}.',
  'Per chiudere tutti gli avvisi: almeno la versione {v}.':
    'To close every advisory: at least version {v}.',
  'Su':
    'On',
  'nessuna correzione indicata':
    'no fix indicated',
  'prodotto o variante non confrontabile':
    'product or variant that cannot be compared',
  'pacchetto di debug':
    'debug package',
  'ciclo di vita del sistema non scaricato':
    'operating system lifecycle not downloaded',
  'cicli di vita dei sistemi':
    'operating system lifecycles',
  'Nel catalogo KEV della CISA: ci sono prove che venga già usata negli attacchi. Va corretta prima delle altre, qualunque sia il punteggio.':
    'In CISA’s KEV catalogue: there is evidence it is already used in attacks. Fix it before the others, whatever its score.',
  'Nessun server con librerie vulnerabili.':
    'No server with vulnerable libraries.',
  'non note':
    'unknown',
  'Solo vulnerabili':
    'Vulnerable only',
  'Librerie vulnerabili per gravità':
    'Vulnerable libraries by severity',
  'ultimo aggiornamento {quando}':
    'last update {quando}',
  'Applicazioni per tipo':
    'Applications by type',
  'Cerca una libreria in tutti i server (es. log4j, openssl, lodash)':
    'Search a library across all servers (e.g. log4j, openssl, lodash)',
  'Dipendenze dirette e transitive':
    'Direct and transitive dependencies',
  'Processo':
    'Process',
  'applicazioni':
    'applications',
  'Licenze diverse':
    'Distinct licences',
  'Librerie diverse':
    'Distinct libraries',
  'componenti':
    'components',
  'terze parti, scompattati, percorsi personalizzati':
    'third parties, unpacked, custom paths',
  'licenze':
    'licences',
  '{n} librerie diverse':
    '{n} distinct libraries',
  'Mostrati i primi {m} di {n}, per numero di server: cerca per nome per restringere.':
    'Showing the first {m} of {n}, by number of servers: search by name to narrow down.',
  'pacchetti della distribuzione':
    'distribution packages',
  'transitive':
    'transitive',
  'Nessuna libreria vulnerabile.':
    'No vulnerable library.',
  'server':
    'servers',
  'dirette':
    'direct',
  'Server con più librerie vulnerabili':
    'Servers with the most vulnerable libraries',
  'librerie':
    'libraries',
  'a schede: «Panoramica» con contatori (server, applicazioni, componenti, librerie vulnerabili, sfruttate attivamente, licenze, software di sistema ed extra sistema) e grafici (gravità, dirette e transitive, ecosistemi, licenze, tipi di applicazione, server più esposti); «Applicazioni», «Componenti» (ogni libreria per versione, con dove si trova), «Librerie vulnerabili» (con gli avvisi) e «Licenze». Le tabelle si filtrano e ordinano per colonna.':
    'tabbed: «Overview» with counters (servers, applications, components, vulnerable libraries, actively exploited, licences, system and non-system software) and charts (severity, direct and transitive, ecosystems, licences, application types, most exposed servers); «Applications», «Components» (every library by version, with where it is), «Vulnerable libraries» (with the advisories) and «Licences». Tables filter and sort per column.',
  'Componenti vulnerabili':
    'Vulnerable components',
  'Compose':
    'Compose',
  'Con contenuto letto':
    'With content read',
  'Container':
    'Containers',
  'Container ({n})':
    'Containers ({n})',
  'Container con pacchetti vulnerabili':
    'Containers with vulnerable packages',
  'I container di questo host letti dall’agente CENSO':
    'The containers of this host read by the CENSO agent',
  'I container si leggono dall’agente CENSO installato sugli host che li eseguono, come root: alla prossima raccolta completa compaiono qui. Il contenuto delle immagini richiede l’opzione «SBOM» dell’agente.':
    'Containers are read by the CENSO agent installed on the hosts running them, as root: at the next full collection they appear here. The images’ content needs the agent’s «SBOM» option.',
  'Inventario dei container rilevati dagli agenti sui runtime Docker, Podman, containerd e CRI-O, inclusi ambienti Kubernetes, k3s e RKE2, con informazioni su immagine, stato, porte esposte, pod e namespace. L’analisi SBOM identifica distribuzione, pacchetti e librerie software, mettendoli in relazione con le vulnerabilità note. Per motivi di sicurezza non vengono acquisite variabili d’ambiente e parametri della command line.':
    'Inventory of the containers found by the agents on Docker, Podman, containerd and CRI-O runtimes, including Kubernetes, k3s and RKE2 environments, with image, state, exposed ports, pod and namespace. The SBOM analysis identifies distribution, packages and software libraries and relates them to known vulnerabilities. For security reasons, environment variables and command-line parameters are not collected.',
  'Il contenuto (pacchetti di sistema e librerie) si legge una volta per immagine con l’opzione SBOM e sta nella sezione «Software (SBOM)».':
    'The content (system packages and libraries) is read once per image with the SBOM option and is in the «Software (SBOM)» section.',
  'Il contenuto si legge con l’opzione SBOM dell’agente, solo per i container in esecuzione.':
    'The content is read with the agent’s SBOM option, only for running containers.',
  'Immagine':
    'Image',
  'Immagini':
    'Images',
  'Inventario container':
    'Container inventory',
  'Kubernetes':
    'Kubernetes',
  'Namespace':
    'Namespace',
  'Namespace Kubernetes':
    'Kubernetes namespaces',
  'Namespace con più container':
    'Namespaces with the most containers',
  'Nessun container Kubernetes.':
    'No Kubernetes container.',
  'Nessun container ancora':
    'No container yet',
  'Pod':
    'Pods',
  'Progetti Compose':
    'Compose projects',
  'Runtime per host':
    'Runtimes per host',
  'Sistemi nelle immagini':
    'Systems in the images',
  'Stato dei container':
    'Container state',
  'Sui nodi Kubernetes, k3s o RKE2 l’agente legge i container tramite crictl.':
    'On Kubernetes, k3s or RKE2 nodes the agent reads the containers through crictl.',
  'container':
    'containers',
  'fermi':
    'stopped',
  'immagini':
    'images',
  'namespace':
    'namespaces',
  'non letto':
    'not read',
  'non usata':
    'unused',
  'richiede l’opzione SBOM':
    'needs the SBOM option',
  '{a} in esecuzione, {b} fermi':
    '{a} running, {b} stopped',
  '{n} non usate':
    '{n} unused',
  '{n} pod':
    '{n} pods',
  'i container trovati dagli agenti CENSO sugli host (Docker, Podman, containerd, CRI-O: anche Kubernetes, k3s, RKE2), a schede — Panoramica, Container, Immagini, Kubernetes — con immagine, stato, porte, pod, namespace e progetto Compose. Con l’opzione SBOM dell’agente si legge anche il contenuto di ogni immagine in esecuzione, una volta per immagine e senza eseguire niente dentro: distribuzione, pacchetti di sistema e librerie; i pacchetti vulnerabili diventano rischi «Pacchetto vulnerabile nel container» con il rimedio di ricostruire l’immagine. Variabili d’ambiente e righe di comando non si leggono mai: contengono segreti. Ogni host ha anche la sezione «Container» nella sua scheda.':
    'the containers the CENSO agents find on the hosts (Docker, Podman, containerd, CRI-O: Kubernetes, k3s, RKE2 too), in tabs — Overview, Containers, Images, Kubernetes — with image, state, ports, pod, namespace and Compose project. With the agent’s SBOM option the content of every running image is read too, once per image and without executing anything inside: distribution, system packages and libraries; the vulnerable packages become «Vulnerable package in the container» findings with the remedy of rebuilding the image. Environment variables and command lines are never read: they contain secrets. Every host also has the «Containers» section on its page.',
  'Altra o proprietaria':
    'Other or proprietary',
  'Application Maintenance':
    'Application Maintenance',
  'Che cosa vuol dire':
    'What it means',
  'Componenti per famiglia di licenza':
    'Components by licence family',
  'Copyleft debole':
    'Weak copyleft',
  'Copyleft forte':
    'Strong copyleft',
  'Di sistema':
    'System',
  'Elenco dei software':
    'Software list',
  'Extra sistema':
    'Outside the system',
  'Fine supporto':
    'End of support',
  'Il componente non dichiara una licenza: da verificare.':
    'The component declares no licence: to be checked.',
  'In scadenza entro 6 mesi':
    'Expiring within 6 months',
  'Inventario Software':
    'Software inventory',
  'monitora il ciclo di vita di software e sistemi operativi installati, identificando i prodotti già fuori supporto, quelli a rilascio continuo non aggiornati e quelli che termineranno il supporto nei prossimi sei mesi. Ogni elemento viene registrato anche come rischio nella sezione Sicurezza del CI e aggiornato a ogni valutazione.': 'Monitors the lifecycle of the installed software and operating systems, identifying products already out of support, rolling-release products that have fallen behind, and those whose support ends within the next six months. Each item is also recorded as a risk in the CI’s Security section and updated at every assessment.',
  'analizza dallo SBOM le licenze delle librerie presenti sui server e le classifica come permissive, copyleft debole, copyleft forte, proprietarie, altre o non dichiarate. Permette di individuare dove sono presenti componenti con obblighi di redistribuzione prima che diventino un rischio.': 'Analyses, from the SBOM, the licenses of the libraries present on the servers and classifies them as permissive, weak copyleft, strong copyleft, proprietary, other or undeclared. It helps locate components with redistribution obligations before they become a risk.',
  'Le modifiche alla libreria vanno condivise, il resto dell’applicazione no (LGPL, MPL, EPL…).':
    'Changes to the library must be shared, the rest of the application need not (LGPL, MPL, EPL…).',
  'Le scadenze compaiono dopo una valutazione di sicurezza dei CI con l’inventario software.':
    'Deadlines appear after a security assessment of CIs with a software inventory.',
  'Libreria':
    'Library',
  'Licenza non classificata o proprietaria: da verificare.':
    'Unclassified or proprietary licence: to be checked.',
  'Licenze dei software':
    'Software licences',
  'Nessuna scadenza':
    'No deadline',
  'Non aggiornati':
    'Not updated',
  'Permissiva':
    'Permissive',
  'Scadenze dei software':
    'Software deadlines',
  'Se l’applicazione è distribuita, va distribuita con la stessa licenza: da verificare con l’ufficio legale (GPL, AGPL…).':
    'If the application is distributed, it must be distributed under the same licence: to be checked with legal (GPL, AGPL…).',
  'Si usa e si ridistribuisce citando l’autore (MIT, Apache, BSD…).':
    'It is used and redistributed by crediting the author (MIT, Apache, BSD…).',
  'Software installati (nome e versione)':
    'Installed software (name and version)',
  'Inventario centralizzato dei software rilevati sui CI del tenant, identificati per nome e versione tramite package manager, programmi Windows, applicazioni distribuite e percorsi configurati. Per ogni prodotto mostra i CI su cui è installato, distinguendo tra software di sistema e software applicativo.':
    'Central inventory of the software found on the tenant’s CIs, identified by name and version through package managers, Windows programs, deployed applications and configured paths. For each product it shows the CIs where it is installed, telling system software from application software.',
  'da {n} giorni':
    '{n} days ago',
  'fuori supporto':
    'out of support',
  'in scadenza':
    'expiring',
  'non aggiornato':
    'not updated',
  'scadenze':
    'deadlines',
  'software':
    'software',
  'su {n} host':
    'on {n} hosts',
  '{pacchetto} {ciclo} esce dal supporto il {fine_leggibile}':
    '{pacchetto} {ciclo} leaves support on {fine_leggibile}',
  '{pacchetto} esce dal supporto il {fine_leggibile}':
    '{pacchetto} leaves support on {fine_leggibile}',
  'Il ciclo {ciclo} smette di ricevere correzioni il {fine_leggibile}: conviene pianificare adesso il passaggio a un ciclo supportato':
    'Cycle {ciclo} stops receiving fixes on {fine_leggibile}: plan the move to a supported cycle now',
  'Pianificare il passaggio a un ciclo ancora supportato prima della scadenza':
    'Plan the move to a still supported cycle before the deadline',
  'tutto il software installato nel tenant per nome e versione, dall’inventario dei CI, con il software di sistema separato da quello extra sistema; ogni riga si apre sugli host che lo hanno.':
    'all the software installed in the tenant by name and version, from the CIs’ inventory, with system software separated from software outside the system; each row opens the hosts that have it.',
  'la fine del supporto del software e dei sistemi operativi: già fuori supporto (rischio alto), prodotti a rilascio continuo rimasti indietro, e ciò che esce dal supporto nei prossimi sei mesi (rischio basso per il software, medio per i sistemi) da pianificare.':
    'the end of support of software and operating systems: already out of support (high finding), rolling-release products left behind, and what leaves support in the next six months (low finding for software, medium for systems) to plan.',
  'le licenze delle librerie per famiglia — permissive, copyleft debole, copyleft forte, altre, non dichiarate — con quali librerie e server le usano.':
    'the libraries’ licences by family — permissive, weak copyleft, strong copyleft, other, undeclared — with which libraries and servers use them.',
  'Aggiungi a una Business App…':
    'Add to a Business App…',
  'Aggiungi funzionalità':
    'Add function',
  'Apri la Mappa Software':
    'Open the Software Map',
  'Business App':
    'Business App',
  'Business App ({n})':
    'Business Apps ({n})',
  'Business service che la usano ({n})':
    'Business services using it ({n})',
  'CI al centro':
    'CI at the centre',
  'CI da cui partire':
    'CI to start from',
  'Cerca un server per nome o indirizzo':
    'Search a server by name or address',
  'Cerca un server per nome o indirizzo: la mappa parte da lì e segue le chiamate dei suoi programmi.':
    'Search a server by name or address: the map starts there and follows the calls of its programs.',
  'Che cosa fa l’applicazione per chi la usa, ciascuna con la sua criticità. La criticità dell’app è la più alta fra le sue funzionalità e passa ai Business Service che la usano.':
    'What the application does for its users, each with its criticality. The app’s criticality is the highest of its functions and passes to the business services using it.',
  'Chiamate dai flussi di rete':
    'Calls from network flows',
  'Clic su un programma per sceglierlo, sul nome di un server per metterlo al centro; ⁺ indica altre chiamate oltre la profondità.':
    'Click a program to pick it, a server name to centre it; ⁺ marks more calls beyond the depth.',
  'Connessioni stabilite':
    'Established connections',
  'Crea Business App':
    'Create Business App',
  'Crea un servizio':
    'Create a service',
  'Creare una Business App richiede il ruolo di amministratore del tenant.':
    'Creating a Business App requires the tenant administrator role.',
  'Descrizione (facoltativa)':
    'Description (optional)',
  'Elementi':
    'Elements',
  'Elementi ({n})':
    'Elements ({n})',
  'Eliminare la Business App «{nome}»?':
    'Delete the Business App “{nome}”?',
  'Eliminare la funzionalità «{nome}»?':
    'Delete the function “{nome}”?',
  'Escludi il programma che chiama':
    'Exclude the calling program',
  'Esclusi':
    'Excluded',
  'Fatto':
    'Done',
  'Funzionalità':
    'Functions',
  'Funzionalità ({n})':
    'Functions ({n})',
  'Gli elementi si aggiungono dalla Mappa Software: si sceglie un CI, si cliccano i programmi e si aggiungono all’app.':
    'Elements are added from the Software Map: pick a CI, click the programs and add them to the app.',
  'I programmi che compongono l’app, sui loro CI. Si aggiungono dalla Mappa Software.':
    'The programs making up the app, on their CIs. They are added from the Software Map.',
  'Rileva le dipendenze applicative dei CI a partire dai processi in esecuzione, dalle porte in ascolto e dalle comunicazioni, ed evidenzia le relazioni in ingresso e in uscita tra i componenti software. Identifica i componenti rilevanti e li usa per la definizione delle Business App.':
    'Finds the application dependencies of the CIs from the running processes, the listening ports and the communications, and highlights the incoming and outgoing relations between software components. It identifies the relevant components and uses them to define the Business Apps.',
  'Ingrandisci':
    'Zoom in',
  'Rappresentazione delle applicazioni dal punto di vista dei servizi erogati. Sono identificati i componenti software, le funzionalità e le criticità. La criticità associata alle applicazioni corrisponde a quella più elevata e viene ereditata dai Business Service e da tutti i CI che le compongono.':
    'The applications seen from the services they deliver: their software components, functions and criticality. An application’s criticality is the highest one and is inherited by the Business Services and by all the CIs that make it up.',
  'Le applicazioni di cui il servizio è fatto. Il servizio eredita la criticità più alta fra le loro funzionalità, se è più alta di quella dichiarata, e i CI che eseguono i loro elementi diventano suoi componenti.':
    'The applications the service is made of. The service inherits the highest criticality of their functions when above the declared one, and the CIs running their elements become its components.',
  'Le chiamate per processo arrivano dalle connessioni stabilite lette dall’inventario avanzato (SSH o WinRM) e dall’agente CENSO, quelle OpenTelemetry dall’agente, le altre dai flussi di rete (tratteggiate, con il processo che chiama non noto). Il nome di un server lo mette al centro; × esclude un server o un programma; DNS, NTP, monitoraggio e controller di dominio sono nascosti salvo chiederli. Cliccando i programmi li si sceglie e se ne fa una Business App, o li si aggiunge a una esistente. Non richiede l’add-on SBOM: con lo SBOM i programmi hanno anche il nome dell’applicazione.':
    'Calls by process come from the established connections read by the advanced inventory (SSH or WinRM) and by the CENSO agent, OpenTelemetry calls from the agent, the others from network flows (dashed, with the calling process unknown). A server name centres it; × excludes a server or a program; DNS, NTP, monitoring and domain controllers are hidden unless asked for. Clicking programs picks them to make a Business App or add them to an existing one. It does not require the SBOM add-on: with SBOM, programs also carry the application name.',
  'L’app non ha ancora elementi.':
    'The app has no elements yet.',
  'Mappa Software':
    'Software Map',
  'Metti questo CI al centro della mappa':
    'Put this CI at the centre of the map',
  'Nascosti':
    'Hidden',
  'Nella pagina del servizio, la card «Business App» sceglie le applicazioni di cui il servizio è fatto: il servizio eredita la loro criticità se è più alta di quella dichiarata (la card «Criticità» dice quando succede), e i CI che eseguono i loro elementi diventano componenti con ruolo «Business App» ed ereditano a loro volta. La criticità dichiarata resta quella che si modifica nel servizio.':
    'On the service page, the “Business App” card picks the applications the service is made of: the service inherits their criticality when above the declared one (the “Criticality” card says when this happens), and the CIs running their elements become components with role “Business App” and inherit in turn. The declared criticality remains the one edited on the service.',
  'Nessun elemento':
    'No elements',
  'Nessuna Business App':
    'No Business Apps',
  'Nessuna Business App collegata.':
    'No Business App linked.',
  'Nessuna chiamata trovata. Le chiamate per processo arrivano dall’inventario avanzato (SSH o WinRM) e dall’agente CENSO, che leggono le connessioni stabilite; quelle OpenTelemetry dall’agente; le altre dai flussi di rete.':
    'No calls found. Calls by process come from the advanced inventory (SSH or WinRM) and the CENSO agent, which read the established connections; OpenTelemetry calls from the agent; the others from network flows.',
  'Nessuna funzionalità ancora.':
    'No functions yet.',
  'Nome della nuova Business App':
    'Name of the new Business App',
  'Non ci sono ancora Business App: si creano dalla Mappa Software o dalla pagina Business App.':
    'There are no Business Apps yet: create them from the Software Map or the Business App page.',
  'Non ci sono ancora Business Service.':
    'There are no business services yet.',
  'Nuova Business App':
    'New Business App',
  'Nuova funzionalità (es. Emissione fatture)':
    'New function (e.g. Invoicing)',
  'Ognuno eredita la criticità dell’app se è più alta di quella dichiarata, e i CI degli elementi diventano suoi componenti.':
    'Each inherits the app’s criticality when above the declared one, and the CIs of the elements become its components.',
  'OpenTelemetry':
    'OpenTelemetry',
  'Programma':
    'Program',
  'Realizzata da':
    'Implemented by',
  'Riduci':
    'Zoom out',
  'Rimetti sulla mappa':
    'Put back on the map',
  'Scegli le Business App':
    'Choose the Business Apps',
  'Scegli per una Business App':
    'Pick for a Business App',
  'Scegli un CI':
    'Choose a CI',
  'Servizi comuni (DNS, NTP, monitoraggio…)':
    'Common services (DNS, NTP, monitoring…)',
  'Si creano dalla Mappa Software scegliendo i programmi che compongono un’applicazione, oppure da qui con «Nuova Business App».':
    'Create them from the Software Map by picking the programs making up an application, or from here with “New Business App”.',
  'Solo i programmi con chiamate':
    'Only programs with calls',
  'Sulla Mappa Software si sceglie un CI, si cliccano i programmi e si aggiungono a questa app.':
    'On the Software Map, pick a CI, click the programs and add them to this app.',
  'apri la scheda':
    'open its page',
  'con errori':
    'with errors',
  'connessione per processo':
    'connection by process',
  'connessioni':
    'connections',
  'connessioni stabilite (per processo)':
    'established connections (by process)',
  'criticità effettiva':
    'effective criticality',
  'dichiarata {a}, ereditata dalle Business App':
    'declared {a}, inherited from the Business Apps',
  'flussi di rete':
    'network flows',
  'il flusso applicativo attorno a un CI, come la Topologia ma fra programmi: si sceglie un server e la mappa mostra i suoi programmi con le porte su cui ascoltano, chi li chiama (a sinistra) e cosa chiamano (a destra), fino alla profondità scelta. Le frecce collegano il programma che chiama a quello che risponde, anche sulla stessa macchina (un proxy davanti al suo application server).':
    'the application flow around a CI, like Topology but between programs: pick a server and the map shows its programs with the ports they listen on, who calls them (left) and what they call (right), down to the chosen depth. Arrows link the calling program to the one answering, also on the same machine (a proxy in front of its application server).',
  'indirizzo non in CMDB':
    'address not in the CMDB',
  'le applicazioni come le chiama chi le usa. Si creano dalla Mappa Software scegliendo i programmi che le compongono (o da qui, aggiungendo poi gli elementi dalla mappa); dentro si elencano le funzionalità, ognuna con la sua criticità. La criticità dell’app è la più alta fra le sue funzionalità.':
    'applications as their users name them. Create them from the Software Map by picking the programs they are made of (or from here, adding the elements from the map later); inside, list the functions, each with its criticality. The app’s criticality is the highest of its functions.',
  'nessun processo noto in ascolto':
    'no known listening process',
  'nessun programma noto':
    'no known program',
  'nessuna funzionalità':
    'no functions',
  'oppure':
    'or',
  '{h} server, {p} programmi, {c} chiamate':
    '{h} servers, {p} programs, {c} calls',
  '{n} connessioni o chiamate':
    '{n} connections or calls',
  '{n} programmi scelti':
    '{n} programs picked',
  '← Torna alle Business App':
    '← Back to Business Apps',
  'Appena installato, in qualunque modo (comando, pacchetto, dalla sonda, a mano), l’agente si annuncia: in elenco compare con il nome della macchina, il sistema e lo stato «Installato» ancora prima del primo inventario. Il nome dell’agente è facoltativo: vuoto, prende quello della macchina.':
    'As soon as it is installed, in any way (command, package, from the probe, by hand), the agent introduces itself: it appears in the list with the machine’s name, its system and the “Installed” state even before the first inventory. The agent name is optional: left empty, it takes the machine’s.',
  'Chiave d’installazione':
    'Installation key',
  'Chiave d’installazione per più macchine':
    'Installation key for many machines',
  'Facoltativo: vuoto, l’agente prende il nome della macchina quando si installa e si annuncia.':
    'Optional: left empty, the agent takes the machine’s name when it is installed and introduces itself.',
  'Gli agenti hanno i tag (colonna «Tag», ✎ per modificarli) e l’elenco si filtra per nome, macchina o tag.':
    'Agents have tags (“Tag” column, ✎ to edit them) and the list can be filtered by name, machine or tag.',
  'Il nome della chiave (es. «Server Milano»): ogni macchina compare poi in elenco con il proprio nome.':
    'The key’s name (e.g. “Milan servers”): each machine then appears in the list with its own name.',
  'Installato':
    'Installed',
  'La macchina si è annunciata: l’agente è installato e il primo inventario sta arrivando.':
    'The machine has introduced itself: the agent is installed and the first inventory is on its way.',
  'La stessa configurazione (o lo stesso pacchetto) si installa su tutte le macchine, anche con GPO, SCCM o Ansible: al primo avvio ognuna si registra come agente a sé, con il nome della macchina, le impostazioni e i tag della chiave, e un segreto suo generato sulla macchina. Revocare la chiave ferma le nuove registrazioni, non gli agenti già registrati.':
    'The same configuration (or package) is installed on every machine, also via GPO, SCCM or Ansible: at its first start each enrols as an agent of its own, with the machine’s name, the key’s settings and tags, and its own secret generated on the machine. Revoking the key stops new enrolments, not the agents already enrolled.',
  'Per installare lo stesso pacchetto su molte macchine (GPO, SCCM, Ansible) si crea una «Chiave d’installazione per più macchine»: al primo avvio ogni macchina si registra come agente a sé, con il suo nome, le impostazioni e i tag della chiave e un segreto generato sulla macchina. La chiave mostra quante macchine si sono registrate; revocarla ferma le nuove registrazioni, non gli agenti già registrati.':
    'To install the same package on many machines (GPO, SCCM, Ansible) create an “Installation key for many machines”: at its first start each machine enrols as an agent of its own, with its name, the key’s settings and tags and a secret generated on the machine. The key shows how many machines enrolled; revoking it stops new enrolments, not the agents already enrolled.',
  'Per il mondo Linux aziendale c’è il pacchetto RPM (RHEL, Rocky, AlmaLinux, Oracle, SUSE…), che si scarica dalla finestra d’installazione con le istruzioni sempre accanto. È generico: contiene il binario e il servizio systemd «censo-inventory», non la configurazione — lo stesso pacchetto va su molti host, mentre il file con il segreto è diverso per ciascuno. Lo installa la tua piattaforma (Ansible: dnf, il template di /etc/censo/inventory.json, systemctl enable --now) e a tenerlo in piedi è systemd, non un cron.':
    'For enterprise Linux there is the RPM package (RHEL, Rocky, AlmaLinux, Oracle, SUSE…), downloaded from the install window with the instructions always beside it. It is generic: it ships the binary and the “censo-inventory” systemd service, not the configuration — the same package goes to many hosts, while the file with the secret differs for each. Your platform installs it (Ansible: dnf, the /etc/censo/inventory.json template, systemctl enable --now) and systemd, not cron, keeps it running.',
  'Questa è una chiave d’installazione: lo stesso comando o lo stesso pacchetto vale per tutte le macchine. Ognuna, al primo avvio, si registra come agente a sé con il nome della macchina e un segreto generato lì; rigenerare la chiave non tocca le macchine già registrate. Serve l’agente nella versione corrente.':
    'This is an installation key: the same command or package works for every machine. At its first start each enrols as an agent of its own with the machine’s name and a secret generated there; regenerating the key does not affect machines already enrolled. It requires the current agent version.',
  'Una configurazione da installare su più macchine: ognuna, al primo avvio, si registra come agente a sé con il proprio nome.':
    'A configuration to install on many machines: at its first start each enrols as an agent of its own, with its own name.',
  'chiave d’installazione · {n} macchine registrate':
    'installation key · {n} machines enrolled',
  'dalla chiave «{nome}»':
    'from key “{nome}”',
  'in attesa dell’installazione':
    'waiting for installation',
  'macchina':
    'machine',
  'nuovo agente':
    'new agent',
  'Dentro la macchina':
    'Inside the machine',
  'Dentro la macchina: i servizi e le chiamate interne':
    'Inside the machine: its services and internal calls',
  'Nessuna chiamata interna rilevata: servono l’inventario avanzato aggiornato o l’agente CENSO, che leggono le connessioni in loopback e i socket Unix.':
    'No internal calls found: they need the updated advanced inventory or the CENSO agent, which read loopback connections and Unix sockets.',
  'Socket Unix in ascolto':
    'Listening Unix sockets',
  'da fuori':
    'from outside',
  'Dentro una macchina conta anche ciò che non passa dalla rete: le chiamate in loopback e attraverso i socket Unix (nginx → php-fpm, PHP → database locale) sono disegnate, e «dentro ⤢» sul riquadro di un server apre la vista della macchina, a livelli dall’ingresso al database. Due servizi dello stesso programma — due JVM, due app Node, due pool php-fpm — sono distinti da ciò su cui ascoltano («java :8080», «java :8081»): ognuno si mette nella sua Business App, uno critico e l’altro no, anche se usano lo stesso database.':
    'Inside a machine what does not cross the network counts too: calls over loopback and Unix sockets (nginx → php-fpm, PHP → local database) are drawn, and “inside ⤢” on a server’s box opens the machine view, in layers from the entry to the database. Two services of the same program — two JVMs, two Node apps, two php-fpm pools — are told apart by what they listen on (“java :8080”, “java :8081”): each goes in its own Business App, one critical and the other not, even when they share the same database.',
  'Agente installato. Compare in Agenti d’inventario e consegna il primo inventario entro pochi minuti.':
    'Agent installed. It appears in Inventory agents and delivers its first inventory within a few minutes.',
  'Apri gli agenti':
    'Open the agents',
  'Dalla scheda di un server o di una VM, «Installa agente» fa installare l’agente a una sonda: si sceglie la sonda, il sistema e la credenziale (SSH per Linux — root o sudo senza password —, WinRM per Windows con un amministratore), e la sonda si collega e installa. L’agente nasce legato al CI e la finestra mostra l’avanzamento e, se va male, l’uscita dell’installer.':
    'From a server’s or VM’s page, “Install agent” has a probe install the agent: choose the probe, the system and the credential (SSH for Linux — root or passwordless sudo —, WinRM for Windows with an administrator), and the probe connects and installs. The agent is linked to the CI and the window shows the progress and, if it fails, the installer’s output.',
  'Gestisci le credenziali':
    'Manage credentials',
  'Il CI non ha un indirizzo di gestione: la sonda non saprebbe a cosa collegarsi.':
    'The CI has no management address: the probe would not know what to connect to.',
  'Il sistema del CI non è noto: sceglilo.':
    'The CI’s system is not known: choose it.',
  'Installa':
    'Install',
  'Installa agente':
    'Install agent',
  'Installa l’agente CENSO':
    'Install the CENSO agent',
  'Installazione avviata: agente «{nome}».':
    'Installation started: agent “{nome}”.',
  'Installazione non riuscita: {motivo}':
    'Installation failed: {motivo}',
  'La sonda scelta si collega alla macchina — SSH su Linux, WinRM su Windows — e installa l’agente, lo stesso installer che si incollerebbe a mano. L’agente nasce legato a questo CI e si annuncia appena parte.':
    'The chosen probe connects to the machine — SSH on Linux, WinRM on Windows — and installs the agent, the same installer one would paste by hand. The agent is linked to this CI and introduces itself as soon as it starts.',
  'La sonda si collega alla macchina e installa l’agente CENSO':
    'The probe connects to the machine and installs the CENSO agent',
  'La sonda sta installando… (stato: {s})':
    'The probe is installing… (state: {s})',
  'Reinstalla comunque':
    'Reinstall anyway',
  'Se la sonda ha il ricevitore delle consegne acceso, la macchina scarica l’installer da lei e le consegna l’inventario; altrimenti usa il Control Plane.':
    'If the probe’s delivery receiver is on, the machine downloads the installer from it and delivers its inventory to it; otherwise it uses the Control Plane.',
  'Sonda che installa':
    'Installing probe',
  'Su questo CI c’è già un agente attivo: «{nome}».':
    'This CI already has an active agent: “{nome}”.',
  'Un amministratore della macchina. WinRM deve essere attivo (HTTPS sulla 5986, o HTTP sulla 5985).':
    'An administrator of the machine. WinRM must be enabled (HTTPS on 5986, or HTTP on 5985).',
  'WinRM in HTTPS (5986)':
    'WinRM over HTTPS (5986)',
  'da aggiornare':
    'needs updating',
  'ha scoperto il CI':
    'discovered the CI',
  'l’agente consegnerà a questa sonda':
    'the agent will deliver to this probe',
  'root, oppure un utente con sudo senza password: l’installazione non può rispondere a una richiesta di password.':
    'root, or a user with passwordless sudo: the installation cannot answer a password prompt.',
  'Linux (SSH)':
    'Linux (SSH)',
  'API server':
    'API server',
  'Apache CloudStack':
    'Apache CloudStack',
  'Application credential':
    'Application credential',
  'Default':
    'Default',
  'ID':
    'ID',
  'Keystone':
    'Keystone',
  'KVM / libvirt':
    'KVM / libvirt',
  'OpenStack':
    'OpenStack',
  'RegionOne':
    'RegionOne',
  'admin':
    'admin',
  'https://cloudstack.example.com/client/api':
    'https://cloudstack.example.com/client/api',
  'https://k8s.example.com:6443':
    'https://k8s.example.com:6443',
  'https://keystone.example.com:5000/v3':
    'https://keystone.example.com:5000/v3',
  'public':
    'public',
  'Chiave API e chiave segreta':
    'API key and secret key',
  'Chiave segreta':
    'Secret key',
  'Classe ingress':
    'Ingress class',
  'Cluster Kubernetes':
    'Kubernetes cluster',
  "Come chiamare il cluster in CMDB; vuoto: l'indirizzo dell'API server.":
    'What to call the cluster in the CMDB; empty: the API server address.',
  "Da «Accounts → utente → Generate keys». Una chiave di amministratore (root o di dominio) legge tutta l'installazione; quella di un utente, il suo account. La chiave segreta firma le richieste e non viene mai inviata.":
    'From “Accounts → user → Generate keys”. An administrator key (root or domain) reads the whole installation; a user key, its own account. The secret key signs the requests and is never sent.',
  'Dominio del progetto':
    'Project domain',
  "Dominio dell'utente":
    'User domain',
  'Host KVM senza piattaforma sopra, via SSH con soli comandi «virsh» in lettura: macchine virtuali (UUID, vCPU, memoria, dischi, interfacce), stato e pool di archiviazione. I bersagli sono gli indirizzi degli host.':
    'KVM hosts with no platform on top, over SSH with read-only “virsh” commands only: virtual machines (UUID, vCPU, memory, disks, interfaces), state and storage pools. The targets are the hosts’ addresses.',
  'IP del cluster':
    'Cluster IP',
  'Il certificato e la chiave di un utente del kubeconfig («client-certificate-data» e «client-key-data», decodificati). Meglio un service account dedicato: un certificato client non si revoca da solo.':
    'The certificate and key of a kubeconfig user (“client-certificate-data” and “client-key-data”, decoded). A dedicated service account is better: a client certificate cannot be revoked on its own.',
  "Il cluster dall'API server (anche k3s, RKE2, OpenShift): nodi, workload (Deployment, StatefulSet, DaemonSet, CronJob) con immagini e repliche, servizi e ingress. I pod non diventano CI: si contano sui loro workload.":
    'The cluster from its API server (also k3s, RKE2, OpenShift): nodes, workloads (Deployment, StatefulSet, DaemonSet, CronJob) with images and replicas, services and ingresses. Pods do not become CIs: they are counted on their workloads.',
  'Indirizzi esterni':
    'External addresses',
  'Interfaccia degli endpoint':
    'Endpoint interface',
  'Istanze (con flavor, immagine, progetto, indirizzi fissi e floating, volumi), hypervisor, reti e bilanciatori Octavia di un cloud OpenStack, via Keystone v3 in sola lettura. Hypervisor e «tutti i progetti» richiedono un ruolo di amministratore.':
    'Instances (with flavor, image, project, fixed and floating addresses, volumes), hypervisors, networks and Octavia load balancers of an OpenStack cloud, via Keystone v3, read only. Hypervisors and “all projects” need an administrator role.',
  "L'indirizzo dell'API server (quello di «server:» nel kubeconfig).":
    'The API server address (the “server:” of the kubeconfig).',
  'La regione da leggere; vuota: la prima del catalogo.':
    'The region to read; empty: the first in the catalog.',
  'Legge istanze, volumi e IP di tutti i progetti: serve un ruolo di amministratore (o «reader» di sistema). Spenta: il solo progetto della credenziale.':
    'Reads instances, volumes and IPs of every project: needs an administrator (or system “reader”) role. Off: the credential’s project only.',
  'Progetto':
    'Project',
  'Repliche previste':
    'Desired replicas',
  'Repliche pronte':
    'Ready replicas',
  'Revocabile da sola, legata a un progetto e con i ruoli scelti alla creazione («openstack application credential create --role reader»).':
    'Revocable on its own, bound to a project and with the roles chosen at creation (“openstack application credential create --role reader”).',
  'Segreto':
    'Secret',
  'Tipo di servizio':
    'Service type',
  'Tipo di workload':
    'Workload kind',
  'Token di un service account':
    'Service account token',
  'Tutti i progetti':
    'All projects',
  'URL del management server; «/client/api» si aggiunge da sé se manca.':
    'Management server URL; “/client/api” is added if missing.',
  'URL di Keystone (identity v3); «/v3» si aggiunge da sé se manca.':
    'Keystone URL (identity v3); “/v3” is added if missing.',
  'Un service account con un ClusterRole in sola lettura (get, list su nodes, namespaces, pods, services, replicasets, deployments, statefulsets, daemonsets, cronjobs, ingresses). Il certificato della CA va in «Certificato della CA» («certificate-authority-data» del kubeconfig, decodificato).':
    'A service account with a read-only ClusterRole (get, list on nodes, namespaces, pods, services, replicasets, deployments, statefulsets, daemonsets, cronjobs, ingresses). The CA certificate goes in “CA certificate” (the kubeconfig’s “certificate-authority-data”, decoded).',
  'Un utente con il ruolo «reader» basta per leggere; per gli hypervisor serve «admin».':
    'A user with the “reader” role is enough to read; hypervisors need “admin”.',
  'Zone, cluster, host, macchine virtuali (offerta, template, account, NIC, volumi, IP pubblici), reti e regole di bilanciamento, con richieste firmate in sola lettura.':
    'Zones, clusters, hosts, virtual machines (offering, template, account, NICs, volumes, public IPs), networks and load balancing rules, with signed read-only requests.',
  'produzione':
    'production',
  'public (predefinita), internal o admin: quella che la sonda raggiunge.':
    'public (default), internal or admin: the one the probe can reach.',
  'I connettori di virtualizzazione (VMware, Microsoft Hyper-V, Nutanix, Proxmox, Sangfor, NexaVM, NexaSphere, ZStack, OpenStack, Apache CloudStack, KVM, Oracle Linux Virtualization Manager) leggono cluster, host, macchine virtuali, datastore e reti direttamente dalla piattaforma; il connettore Kubernetes legge il cluster: nodi, workload, servizi e ingress.':
    'The virtualisation connectors (VMware, Microsoft Hyper-V, Nutanix, Proxmox, Sangfor, NexaVM, NexaSphere, ZStack, OpenStack, Apache CloudStack, KVM, Oracle Linux Virtualization Manager) read clusters, hosts, virtual machines, datastores and networks straight from the platform; the Kubernetes connector reads the cluster: nodes, workloads, services and ingresses.',
  'OpenStack: credenziale con l’indirizzo di Keystone e un’application credential (o utente, password e progetto); con «Tutti i progetti» e un ruolo di amministratore legge anche gli hypervisor e le istanze di ogni progetto. Apache CloudStack: chiave API e chiave segreta di un amministratore; si leggono zone, cluster, host, VM (anche quelle dentro i progetti), router virtuali e VM di sistema, volumi, datastore primari e secondari, reti di progetto e reti fisiche. KVM senza piattaforma: i bersagli sono gli host e la credenziale è quella SSH; la sonda usa solo comandi «virsh» in lettura. OLVM (e oVirt, Red Hat Virtualization): indirizzo dell’engine e un utente in sola lettura scritto con il profilo («lettore@internal»), o un token SSO; gli indirizzi delle VM arrivano solo se sull’ospite c’è il guest agent. Kubernetes: indirizzo dell’API server e token di un service account con un ClusterRole in sola lettura (il modello pronto è nella documentazione); i pod non diventano CI, si contano sui workload.':
    'OpenStack: a credential with the Keystone address and an application credential (or user, password and project); with «All projects» and an administrator role it also reads the hypervisors and the instances of every project. Apache CloudStack: API key and secret key of an administrator; it reads zones, clusters, hosts, VMs (those inside projects too), virtual routers and system VMs, volumes, primary and secondary datastores, project networks and physical networks. KVM without a platform: the targets are the hosts and the credential is the SSH one; the probe uses only read «virsh» commands. OLVM (and oVirt, Red Hat Virtualization): engine address and a read-only user written with its profile («reader@internal»), or an SSO token; VM addresses arrive only if the guest agent runs on the guest. Kubernetes: API server address and the token of a service account with a read-only ClusterRole (the ready-made template is in the documentation); pods do not become CIs, they are counted on the workloads.',
  'Accesa ma non attiva.':
    'On but not active.',
  'Attiva.':
    'Active.',
  'Osserva le richieste HTTP di tutte le applicazioni con eBPF, senza toccarle (Linux, CENSO SBOM)':
    'Observe the HTTP requests of every application with eBPF, without touching them (Linux, CENSO SBOM)',
  'Osservazione applicativa':
    'Application observation',
  'Spenta.':
    'Off.',
  'Un programma eBPF guarda le chiamate di sistema e riconosce le richieste e le risposte HTTP di ogni processo, lato server e lato client: metodo, percorso, codice di stato e tempo di risposta, senza agenti nelle applicazioni né riavvii. Escono dal kernel solo la riga della richiesta e il codice di stato, mai header o contenuti; i parametri della richiesta si scartano. Il risultato va nella sezione Runtime del CI e nella Mappa Software, insieme a OpenTelemetry. Serve Linux 5.8 o successivo con BTF e l’agente residente come root; l’HTTPS è cifrato a quel livello e non si vede ancora.':
    'An eBPF program watches the system calls and recognises the HTTP requests and responses of every process, server and client side: method, path, status code and response time, with no agent in the applications and no restart. Only the request line and the status code leave the kernel, never headers or bodies; request parameters are dropped. The result goes in the CI’s Runtime section and on the Software Map, alongside OpenTelemetry. Needs Linux 5.8 or later with BTF and the resident agent running as root; HTTPS is encrypted at that level and is not seen yet.',
  'Adatta':
    'Fit',
  'Certificati TLS esposti':
    'Exposed TLS certificates',
  'Certificati TLS esposti ({n})':
    'Exposed TLS certificates ({n})',
  'Certificati TLS esposti: l’agente CENSO fa una stretta di mano TLS verso ogni porta in ascolto della macchina — anche quelle raggiungibili solo in locale — e ne legge il certificato: soggetto, nomi alternativi, emittente, scadenza, chiave, firma, impronta, autofirmato. Li trovi nella sezione «Certificati» del CI, ordinati per scadenza.':
    'Exposed TLS certificates: the CENSO agent makes a TLS handshake to every listening port of the machine — also those reachable only locally — and reads its certificate: subject, alternative names, issuer, expiry, key, signature, fingerprint, self-signed. You find them in the CI’s “Certificates” section, sorted by expiry.',
  'Chi chiama':
    'Caller',
  'Chi risponde':
    'Responder',
  'Chiamate o flussi':
    'Calls or flows',
  'Chiave e protocollo':
    'Key and protocol',
  'Cifrata (TLS)':
    'Encrypted (TLS)',
  'Cifratura non nota':
    'Encryption unknown',
  'Clic su una chiamata o su un programma.':
    'Click a call or a program.',
  'Clic su un’altra chiamata o su un programma per cambiare; clic sullo stesso per togliere.':
    'Click another call or program to change; click the same one to clear.',
  'Come lo sappiamo':
    'How we know',
  'Coppie in chiaro':
    'Clear-text pairs',
  'Esci dallo schermo intero (Esc)':
    'Exit full screen (Esc)',
  'l’agente CENSO esegue un handshake TLS verso le porte in ascolto dell’asset per rilevare i certificati esposti dai servizi e verificarne caratteristiche, validità e scadenza.': 'the CENSO agent performs a TLS handshake towards the asset’s listening ports to detect the certificates exposed by the services and verify their characteristics, validity and expiry.',
  'I servizi della macchina e le chiamate fra loro (loopback, indirizzi della macchina, socket Unix), con chi chiama da fuori a sinistra e dove si esce a destra. Passa sopra un servizio per vederne le chiamate; cliccalo per i dettagli.':
    'The machine’s services and the calls between them (loopback, the machine’s own addresses, Unix sockets), with outside callers on the left and outgoing calls on the right. Hover a service to see its calls; click it for details.',
  'In ascolto':
    'Listening',
  'In chiaro fra due macchine':
    'Clear text between two machines',
  'In entrata ({n})':
    'Incoming ({n})',
  'In uscita ({n})':
    'Outgoing ({n})',
  'Le chiamate sono colorate per tipo (web, database, code, infrastruttura, socket locali); la linea spessa è misurata dal runtime, quella tratteggiata viene solo dai flussi. «Segui il percorso» accende tutto il giro di una chiamata o di un programma — da dove parte a dove arriva, con un’animazione nel verso delle chiamate — e «Schermo intero» allarga la mappa. «Dentro la macchina» si apre in una finestra: colonne da chi chiama da fuori a dove si esce, passaggio del mouse per le chiamate di un servizio, clic per i suoi dettagli.':
    'Calls are coloured by type (web, database, queues, infrastructure, local sockets); a thick line is measured by the runtime, a dashed one comes only from flows. “Follow the path” lights up the whole route of a call or a program — from where it starts to where it ends, animated in the direction of the calls — and “Full screen” enlarges the map. “Inside the machine” opens in a window: columns from outside callers to outgoing calls, hover for a service’s calls, click for its details.',
  'Individua le coppie di asset che comunicano in chiaro, esponendo potenzialmente dati e credenziali a chi può osservare il traffico. Le evidenze sono ordinate per affidabilità: traffico HTTP non cifrato osservato a livello applicativo, processi associati a protocolli intrinsecamente non cifrati e flussi di rete sulle relative porte. I protocolli che possono usare TLS sulla stessa porta vengono segnalati solo quando il traffico in chiaro è stato effettivamente osservato. Le comunicazioni interne allo stesso host sono escluse.': 'Finds the pairs of assets that communicate in clear, potentially exposing data and credentials to anyone who can observe the traffic. The evidence is ordered by reliability: unencrypted HTTP traffic observed at the application level, processes associated with inherently unencrypted protocols, and network flows on the relevant ports. Protocols that can use TLS on the same port are flagged only when plaintext traffic was actually observed. Communications internal to the same host are excluded.',
  'Le prove arrivano dalle connessioni lette dall’agente CENSO e dall’inventario avanzato, dall’osservazione applicativa (eBPF) e dai flussi di rete.':
    'The evidence comes from the connections read by the CENSO agent and the advanced inventory, from application observation (eBPF) and from network flows.',
  'Macchine che chiamano in chiaro':
    'Machines calling in clear text',
  'Nessun traffico in chiaro trovato':
    'No clear-text traffic found',
  'Osservazione applicativa (eBPF): con l’interruttore acceso nelle impostazioni di un agente Linux, l’agente vede le richieste e le risposte HTTP di tutte le applicazioni del server — lato server e lato client — direttamente dal kernel, senza toccarle né riavviarle: metodo, percorso, codice di stato e tempo di risposta. Finiscono nella stessa sezione «Runtime» e nella Mappa Software, insieme a OpenTelemetry. Serve Linux 5.8 o successivo con BTF. Anche l’HTTPS si vede, per le applicazioni che usano OpenSSL (nginx, Apache, PHP, Python, curl…): l’agente legge la richiesta prima della cifratura; il TLS proprio di Go e Java non ancora.':
    'Application observation (eBPF): with the switch on in a Linux agent’s settings, the agent sees the HTTP requests and responses of every application on the server — server and client side — straight from the kernel, without touching or restarting them: method, path, status code and response time. They go in the same “Runtime” section and on the Software Map, alongside OpenTelemetry. Needs Linux 5.8 or later with BTF. HTTPS is seen too, for applications using OpenSSL (nginx, Apache, PHP, Python, curl…): the agent reads the request before encryption; Go’s and Java’s own TLS not yet.',
  'Per protocollo':
    'By protocol',
  'Scegli una chiamata o un programma: si illumina tutto il giro, da dove parte a dove arriva':
    'Pick a call or a program: its whole route lights up, from where it starts to where it ends',
  'Segui il percorso':
    'Follow the path',
  'Server che rispondono in chiaro':
    'Servers answering in clear text',
  'Soggetto':
    'Subject',
  'Togli dalla scelta':
    'Remove from the selection',
  'Traffico in chiaro':
    'Clear-text traffic',
  'altro':
    'other',
  'autofirmato':
    'self-signed',
  'chiama da fuori':
    'calls from outside',
  'cifrata':
    'encrypted',
  'code e messaggi':
    'queues and messaging',
  'coppie':
    'pairs',
  'database':
    'database',
  'fuori dalla macchina':
    'outside the machine',
  'in chiaro':
    'clear text',
  'in chiaro fra macchine':
    'clear text between machines',
  'linea spessa: misurata dal runtime (eBPF, OpenTelemetry)':
    'thick line: measured by the runtime (eBPF, OpenTelemetry)',
  'scade fra {n} giorni':
    'expires in {n} days',
  'servizi d’infrastruttura':
    'infrastructure services',
  'socket locali':
    'local sockets',
  'solo dai flussi di rete':
    'only from network flows',
  'valido ancora {n} giorni':
    'valid for {n} more days',
  'verso fuori':
    'outgoing',
  'visto dal runtime':
    'seen by the runtime',
  'web (HTTP/HTTPS)':
    'web (HTTP/HTTPS)',
  '{i} in entrata · {u} in uscita':
    '{i} in · {u} out',
  'Apri il CI':
    'Open the CI',
  'Il pacchetto {pacchetto} ({n} CVE aperte)':
    'The {pacchetto} package ({n} open CVEs)',
  'Le librerie dell’applicazione confrontate con il catalogo OSV. «Portata da» è la catena di dipendenze che la fa entrare: si corregge aggiornando la prima della catena.':
    'The application’s libraries checked against the OSV catalogue. “Brought in by” is the dependency chain that pulls it in: it is fixed by updating the first one in the chain.',
  'Librerie vulnerabili ({n} su {totale})':
    'Vulnerable libraries ({n} of {totale})',
  'Nessuna CVE aperta sul pacchetto':
    'No open CVE on the package',
  'Nessuna libreria vulnerabile':
    'No vulnerable library',
  'Per questo programma non ci sono librerie né pacchetto noti.':
    'No known libraries or package for this program.',
  'Si chiudono aggiornando il pacchetto':
    'They are closed by updating the package',
  'Sulla Mappa Software un programma con librerie vulnerabili o con CVE aperte sul proprio pacchetto ha un badge ⚠ con il numero — rosso se una è sfruttata attivamente o critica. Cliccandolo si apre l’elenco: per ogni libreria la versione, se è diretta o transitiva, da quale dipendenza è portata dentro, la versione che la corregge e gli avvisi uno per uno; sotto, le CVE del pacchetto con il comando per aggiornarlo.':
    'On the Software Map a program with vulnerable libraries or open CVEs on its own package has a ⚠ badge with the count — red if one is actively exploited or critical. Clicking it opens the list: for each library the version, whether it is direct or transitive, which dependency brings it in, the version that fixes it and the advisories one by one; below, the package’s CVEs with the command to update it.',
  'Vulnerabilità del programma':
    'Program vulnerabilities',
  'Vulnerabilità di {programma}':
    'Vulnerabilities of {programma}',
  'clic per il dettaglio':
    'click for details',
  'corretta in':
    'fixed in',
  'installata':
    'installed',
  'portata da':
    'brought in by',
  'sfruttata attivamente':
    'actively exploited',
  '{n} CVE del pacchetto':
    '{n} package CVEs',
  '{n} librerie vulnerabili':
    '{n} vulnerable libraries',
  'Finestra breve: i flussi di rete sono quelli degli ultimi minuti, le chiamate misurate dal runtime sono contate per ora (si vede l’ora in corso) e le connessioni sono quelle dell’ultimo inventario.':
    'Short window: network flows are those of the last minutes, calls measured by the runtime are counted per hour (the current hour is shown) and connections are those of the last inventory.',
  'Sulla Mappa Software il selettore «Traffico» scende fino agli ultimi 5 minuti, per vedere chi sta chiamando chi adesso. Su finestre sotto l’ora i flussi di rete sono quelli esatti, mentre le chiamate misurate dal runtime sono contate per ora e le connessioni sono quelle dell’ultimo inventario.':
    'On the Software Map the “Traffic” selector goes down to the last 5 minutes, to see who is calling whom right now. On windows under an hour network flows are exact, while calls measured by the runtime are counted per hour and connections are those of the last inventory.',
  'ultime 6 ore':
    'last 6 hours',
  'ultimi 15 minuti':
    'last 15 minutes',
  'ultimi 5 minuti':
    'last 5 minutes',
  'Dove si trova':
    'Where it is',
  'Dove si trova ({n} file)':
    'Where it is ({n} files)',
  'JAR dentro l’archivio':
    'JAR inside the archive',
  'La libreria è impacchettata dentro l’applicazione: va ricostruito e ridistribuito l’archivio con la versione nuova.':
    'The library is packaged inside the application: the archive must be rebuilt and redeployed with the new version.',
  'Nella scheda di ogni server la sezione «Software (SBOM)»: le applicazioni con i loro componenti, il filtro «Solo i componenti vulnerabili», l’importazione e il download in CycloneDX, di tutto il CI oppure di una sola applicazione («Scarica CycloneDX» dentro l’applicazione, e su ogni riga della vista «Applicazioni» di Application Maintenance). Per ogni libreria si legge dove si trova e cos’è quel file (un eseguibile Go che la contiene compilata, un JAR, una cartella di node_modules…) e come si corregge; se la stessa versione è in più file, è una riga sola con l’elenco dei file. Nella vista ad albero un pallino pieno segna le librerie vulnerabili e uno vuoto le dipendenze che le portano dentro; «Solo le catene che portano a una libreria vulnerabile» lascia aperti solo quei rami.':
    'In every server’s page the “Software (SBOM)” section: the applications with their components, the “Only vulnerable components” filter, import and CycloneDX download, of the whole CI or of one application (“Download CycloneDX” inside the application, and on every row of the “Applications” view of Application Maintenance). For each library you read where it is and what that file is (a Go executable with it compiled in, a JAR, a node_modules folder…) and how to fix it; if the same version is in several files, it is a single row with the list of files. In the tree view a full dot marks vulnerable libraries and an empty one the dependencies that bring them in; “Only the chains leading to a vulnerable library” keeps only those branches open.',
  'Non è un file a parte: va ricompilato il programma con la versione nuova del modulo (o installata la sua versione aggiornata).':
    'It is not a separate file: the program must be rebuilt with the new module version (or its updated release installed).',
  'Si aggiorna con bundler nella cartella del progetto.':
    'Update it with bundler in the project folder.',
  'Si aggiorna con composer nella cartella del progetto.':
    'Update it with composer in the project folder.',
  'Si aggiorna con pip nell’ambiente che contiene questa cartella (virtualenv o Python di sistema).':
    'Update it with pip in the environment containing this folder (virtualenv or system Python).',
  'Si aggiorna il pacchetto NuGet nel progetto e si ripubblica l’applicazione.':
    'Update the NuGet package in the project and republish the application.',
  'Si aggiorna la dipendenza nel progetto e si reinstalla (npm, yarn o pnpm).':
    'Update the dependency in the project and reinstall (npm, yarn or pnpm).',
  'Si sostituisce il JAR con la versione nuova, o si aggiorna la dipendenza e si ridistribuisce l’applicazione.':
    'Replace the JAR with the new version, or update the dependency and redeploy the application.',
  'Solo le catene che portano a una libreria vulnerabile':
    'Only the chains leading to a vulnerable library',
  'cartella del pacchetto in node_modules':
    'package folder in node_modules',
  'compilata dentro l’eseguibile':
    'compiled into the executable',
  'elencata nel file dei pacchetti .NET':
    'listed in the .NET packages file',
  'elencata nel file delle dipendenze':
    'listed in the dependency file',
  'elencata nel file delle dipendenze PHP':
    'listed in the PHP dependency file',
  'elencata nel file delle dipendenze Ruby':
    'listed in the Ruby dependency file',
  'elencata nel manifesto delle dipendenze .NET':
    'listed in the .NET dependency manifest',
  'file JAR':
    'JAR file',
  'in {n} file':
    'in {n} files',
  'pacchetto Python installato':
    'installed Python package',
  'porta dentro {n} librerie vulnerabili':
    'brings in {n} vulnerable libraries',
  'trovata in':
    'found in',
  'vulnerabile':
    'vulnerable',
  '● vulnerabile · ○ porta dentro una libreria vulnerabile':
    '● vulnerable · ○ brings in a vulnerable library',
  'Asset che sostengono funzioni critiche o importanti, monitorati':
    'Assets supporting critical or important functions, monitored',
  'Asset con distinta dei componenti (SBOM)':
    'Assets with a software bill of materials (SBOM)',
  'CENSO Eagle non è in uso: il monitoraggio va dimostrato con lo strumento adottato.':
    'CENSO Eagle is not in use: monitoring must be demonstrated with the tool adopted.',
  'Certificati TLS letti dall’agente':
    'TLS certificates read by the agent',
  'Componenti software di terze parti':
    'Third-party software components',
  'Concentrazione':
    'Concentration',
  'Cosa vede la piattaforma':
    'What the platform sees',
  'Da sistemare':
    'To fix',
  'Evidenze':
    'Evidence',
  'Evidenze dalla piattaforma':
    'Evidence from the platform',
  'Fornitori ICT terzi e concentrazione (artt. 28–29)':
    'ICT third-party providers and concentration (Arts. 28–29)',
  'Funzioni':
    'Functions',
  'Funzioni critiche o importanti (art. 8)':
    'Critical or important functions (Art. 8)',
  'Funzioni e fornitori':
    'Functions and providers',
  'In più, la sezione «Funzioni e fornitori»: le Business App con le loro funzioni critiche e quanti asset che le sostengono non sono conformi (art. 8), e i fornitori ICT dai contratti attivi e dai provider cloud dell’inventario, con gli asset critici che dipendono da ciascuno e la concentrazione (artt. 28–29). Accanto alle voci non misurate, ciò che la piattaforma vede su di esse.':
    'In addition, the “Functions and providers” section: Business Apps with their critical functions and how many of the assets supporting them are not compliant (Art. 8), and ICT providers from active contracts and the inventory’s cloud providers, with the critical assets depending on each and the concentration (Arts. 28–29). Next to the unmeasured items, what the platform sees on them.',
  'Business App, funzioni associate, criticità dichiarata e asset ICT che le supportano, con evidenza degli asset conformi e non conformi. Gli asset dei Business Service critici o alti sono {n}, di cui {k} non conformi.': 'Business Apps, associated functions, declared criticality and the ICT assets that support them, with evidence of compliant and non-compliant assets. The assets of critical or high Business Services are {n}, of which {k} non-compliant.',
  'Librerie di terze parti senza vulnerabilità gravi':
    'Third-party libraries without serious vulnerabilities',
  'Monitoraggio':
    'Monitoring',
  'Monitoraggio degli asset che sostengono funzioni critiche':
    'Monitoring of the assets supporting critical functions',
  'Nessun contratto registrato e nessun provider cloud nell’inventario.':
    'No contract registered and no cloud provider in the inventory.',
  'Nessuna Business App definita.':
    'No Business App defined.',
  'Se non sono definite Business App, devono essere create dalla Mappa Software, selezionando i programmi che compongono ciascuna applicazione.': 'If no Business Apps are defined, they must be created from the Software Map, selecting the programs that make up each application.',
  'Servizi chiamati in chiaro da altre macchine':
    'Services called in clear text by other machines',
  'asset con distinta dei componenti (SBOM)':
    'assets with a software bill of materials (SBOM)',
  'asset monitorati da CENSO Eagle':
    'assets monitored by CENSO Eagle',
  'autofirmati':
    'self-signed',
  'certificati TLS su {n} asset':
    'TLS certificates on {n} assets',
  'concentrazione massima {p}%':
    'highest concentration {p}%',
  'dall’inventario cloud':
    'from the cloud inventory',
  'in scadenza entro 30 giorni':
    'expiring within 30 days',
  'librerie vulnerabili su {n} asset':
    'vulnerable libraries on {n} assets',
  'scade il {d}':
    'expires on {d}',
  'scaduti':
    'expired',
  'servizi chiamati in chiaro su {n} server':
    'services called in clear text on {n} servers',
  'sfruttate attivamente (CISA KEV)':
    'actively exploited (CISA KEV)',
  '{c} contratti attivi':
    '{c} active contracts',
  '{n} fornitori senza contratto registrato':
    '{n} providers without a registered contract',
  'I percorsi dei componenti non sono arrivati: la distinta di questa macchina superava lo spazio di una consegna e l’agente li ha tolti. Con l’agente CENSO aggiornato ogni file viaggia una volta sola e i percorsi restano; al prossimo inventario compaiono.':
    'The component paths did not arrive: this machine’s bill of materials exceeded the space of one delivery and the agent removed them. With the updated CENSO agent each file travels only once and the paths are kept; they appear at the next inventory.',
  'Anagrafica dei certificati':
    'Certificate register',
  'Apri il flusso applicativo del CI':
    'Open the CI’s application flow',
  'Autofirmati ({e} raggiungibili da altre macchine)':
    'Self-signed ({e} reachable from other machines)',
  'CI e servizio':
    'CI and service',
  'Catena presentata':
    'Chain presented',
  'Certificati autofirmati':
    'Self-signed certificates',
  'Certificati su {h} host ({u} diversi)':
    'Certificates on {h} hosts ({u} distinct)',
  'Con chiave, firma o protocollo deboli':
    'With weak key, signature or protocol',
  'Crittografia':
    'Cryptography',
  'Debolezze':
    'Weaknesses',
  'Dettaglio del certificato':
    'Certificate details',
  'Dove è esposto':
    'Where it is exposed',
  'Emittente (CA)':
    'Issuer (CA)',
  'Entro 30 giorni':
    'Within 30 days',
  'Entro 90 giorni':
    'Within 90 days',
  'I certificati li legge l’agente CENSO, con una stretta di mano TLS verso ogni porta in ascolto della sua macchina.':
    'Certificates are read by the CENSO agent, with a TLS handshake to every listening port of its machine.',
  'Individua le comunicazioni non cifrate tra gli asset e analizza i certificati TLS presentati dai loro servizi, indicando chi li espone, chi li ha emessi, quando scadono e quali sono autofirmati o utilizzano configurazioni deboli.': 'Finds the unencrypted communications between assets and analyses the TLS certificates presented by their services, indicating who exposes them, who issued them, when they expire and which are self-signed or use weak configurations.',
  'Installazioni':
    'Installations',
  'Lo stesso certificato altrove':
    'The same certificate elsewhere',
  'Macchina che chiama':
    'Calling machine',
  'Nessun certificato autofirmato':
    'No self-signed certificate',
  'Nessun certificato in questa fascia':
    'No certificate in this range',
  'Nessun certificato letto':
    'No certificate read',
  'Nomi alternativi (SAN)':
    'Alternative names (SAN)',
  'Protocollo e cifrario':
    'Protocol and cipher',
  'Raggiungibili da altre macchine':
    'Reachable from other machines',
  'Scadenze e certificati scaduti':
    'Expiries and expired certificates',
  'Scadono entro 30 giorni':
    'Expire within 30 days',
  'Scaduti':
    'Expired',
  'Servizi chiamati in chiaro':
    'Services called in clear text',
  'Servizi in chiaro':
    'Clear-text services',
  'Servizio che lo espone':
    'Service exposing it',
  'Solo in locale':
    'Local only',
  'Tutti, per scadenza':
    'All, by expiry',
  'Certificati autofirmati: non sono verificati da una CA attendibile, quindi non garantiscono l’identità del servizio. Sui servizi raggiungibili dalla rete è consigliato sostituirli con certificati emessi da una CA interna o pubblica; sui servizi accessibili solo localmente il rischio è inferiore.': 'Self-signed certificates: they are not verified by a trusted CA, so they do not guarantee the identity of the service. On services reachable from the network it is advisable to replace them with certificates issued by an internal or public CA; on services accessible only locally the risk is lower.',
  'Valido fino al':
    'Valid until',
  'Vedi le coppie':
    'See the pairs',
  'autofirmato: l’emittente è il certificato stesso':
    'self-signed: the issuer is the certificate itself',
  'certificati':
    'certificates',
  'criticità':
    'criticality',
  'in ascolto solo in locale':
    'listening only locally',
  'nessun certificato intermedio inviato dal server':
    'no intermediate certificate sent by the server',
  'cinque schede. «Traffico in chiaro»: le coppie di macchine che si parlano senza cifratura, con la prova (HTTP visto senza TLS dall’osservazione applicativa o da OpenTelemetry, un processo collegato a un protocollo sempre in chiaro, i flussi di rete); i numeri in alto sono viste — le coppie, i server che rispondono in chiaro, le macchine che chiamano — e i protocolli filtrano. «Anagrafica dei certificati»: tutti i certificati che i servizi presentano, letti dall’agente CENSO. «Scadenze e certificati scaduti»: scaduti, entro 30 e 90 giorni. «Certificati autofirmati»: quelli raggiungibili da altre macchine prima. Cliccando un certificato si apre il dettaglio: il CI e il servizio che lo espone (porta, processo, software), i Business Service, dove è installato lo stesso certificato, e il certificato stesso — soggetto, nomi alternativi, CA emittente e catena, numero di serie, validità, chiave, firma, protocollo e impronta.':
    'five tabs. “Clear-text traffic”: the pairs of machines talking without encryption, with the evidence (HTTP seen without TLS by application observation or OpenTelemetry, a process connected to an always clear-text protocol, network flows); the numbers at the top are views — the pairs, the servers answering in clear text, the calling machines — and the protocols filter. “Certificate register”: every certificate the services present, read by the CENSO agent. “Expiries and expired certificates”: expired, within 30 and 90 days. “Self-signed certificates”: those reachable from other machines first. Clicking a certificate opens its details: the CI and the service exposing it (port, process, software), the Business Services, where the same certificate is installed, and the certificate itself — subject, alternative names, issuing CA and chain, serial number, validity, key, signature, protocol and fingerprint.',
  'scadenza non letta':
    'expiry not read',
  '1. CA e modello':
    '1. CA and template',
  '3. Incolla la CSR e invia':
    '3. Paste the CSR and send',
  'Aggiungi una connessione «CA Microsoft (AD CS)» nella scheda «Connessioni» — WinRM verso un server Windows del dominio oppure il sito Web Enrollment (/certsrv) — e abilita la capability «PKI (CA Microsoft)» sulla sonda che raggiunge la CA.':
    'Add a “Microsoft CA (AD CS)” connection in the “Connections” tab — WinRM to a Windows server of the domain or the Web Enrollment site (/certsrv) — and enable the “PKI (Microsoft CA)” capability on the probe that reaches the CA.',
  'Autorità di certificazione':
    'Certification authorities',
  'CA':
    'CA',
  'CA e modelli':
    'CAs and templates',
  'CA e modello':
    'CA and template',
  'CSR (-----BEGIN CERTIFICATE REQUEST-----)':
    'CSR (-----BEGIN CERTIFICATE REQUEST-----)',
  'Certificato richiesto':
    'Requested certificate',
  'Crea e invia alla CA':
    'Create and send to the CA',
  'Dal database della CA, con la connessione WinRM: i certificati emessi che scadono da 30 giorni fa in poi. Serve la lettura sulla CA (per esempio «Responsabile certificati»).':
    'From the CA database, over the WinRM connection: the issued certificates expiring from 30 days ago onward. Read access on the CA is needed (for example “Certificate Manager”).',
  'Eliminare la richiesta?':
    'Delete the request?',
  'Entrano da sé quando la CA emette una richiesta fatta da qui; si importano con «Importa certificato» o si leggono dal database della CA (connessione WinRM).':
    'They come in by themselves when the CA issues a request made here; they are imported with “Import certificate” or read from the CA database (WinRM connection).',
  'Gestione':
    'Management',
  'Gestione certificati':
    'Certificate management',
  'Importa certificato':
    'Import certificate',
  'Impronta SHA-1':
    'SHA-1 fingerprint',
  'Installato su':
    'Installed on',
  'Invia alla CA':
    'Send to the CA',
  'Invia subito alla CA':
    'Send to the CA right away',
  'Invio in corso…':
    'Sending…',
  'La connessione non consente di inviare richieste: la richiesta resta in bozza finché un amministratore non accende «Consenti la scrittura» nella credenziale.':
    'The connection does not allow sending requests: the request stays a draft until an administrator turns on “Allow writing” in the credential.',
  'Le richieste si preparano in «Nuova richiesta», o con «Rinnova con la CA» da un certificato del repository o di Cifratura.':
    'Requests are prepared in “New request”, or with “Renew with the CA” from a certificate of the repository or of Encryption.',
  'Leggi':
    'Read',
  'Leggi i certificati emessi dalla CA':
    'Read the certificates issued by the CA',
  'Lettura in corso…':
    'Reading…',
  'Linux e altri (OpenSSL)':
    'Linux and others (OpenSSL)',
  'Modelli di certificato':
    'Certificate templates',
  'Modelli pubblicati':
    'Published templates',
  'Nel repository':
    'In the repository',
  'Nessun certificato nel repository':
    'No certificate in the repository',
  'Nessuna CA aziendale trovata in Active Directory':
    'No enterprise CA found in Active Directory',
  'Nessuna connessione a una CA Microsoft':
    'No connection to a Microsoft CA',
  'Nessuna richiesta':
    'No request',
  'Nome comune (CN)':
    'Common name (CN)',
  'Nomi alternativi (separati da virgola)':
    'Alternative names (comma separated)',
  'Nuova richiesta':
    'New request',
  'Oppure incolla il testo':
    'Or paste the text',
  'Prepara i comandi':
    'Prepare the commands',
  'Pubblicato da':
    'Published by',
  'Quando il certificato è emesso, scaricalo dal repository e completa sul server con:':
    'When the certificate is issued, download it from the repository and complete on the server with:',
  'Questo modello costruisce il soggetto da Active Directory: i nomi della CSR vengono ignorati. Per un server web scegli un modello in cui il richiedente fornisce il soggetto (come «Server Web»).':
    'This template builds the subject from Active Directory: the CSR’s names are ignored. For a web server choose a template where the requester supplies the subject (like “Web Server”).',
  'Repository certificati':
    'Certificate repository',
  'Richiedente':
    'Requester',
  'Richieste':
    'Requests',
  'Ricontrolla':
    'Check again',
  'Rinnova con la CA':
    'Renew with the CA',
  'Salva il file .inf sul server, poi in un prompt da amministratore:':
    'Save the .inf file on the server, then in an administrator prompt:',
  'Salva la richiesta':
    'Save the request',
  'Scarica il file .inf':
    'Download the .inf file',
  'Soggetto dalla richiesta':
    'Subject from the request',
  'Sostituisce il certificato con impronta {s}…':
    'Replaces the certificate with fingerprint {s}…',
  'Sostituzione':
    'Replacement',
  'Togli dal repository':
    'Remove from the repository',
  'Togliere il certificato dal repository? Non viene revocato sulla CA.':
    'Remove the certificate from the repository? It is not revoked on the CA.',
  'Visti installati dall’agente':
    'Seen installed by the agent',
  'Windows (certreq)':
    'Windows (certreq)',
  'dal database della CA':
    'from the CA database',
  'elenco troncato: alza il limite o leggi più spesso':
    'list truncated: raise the limit or read more often',
  'emesso':
    'issued',
  'emesso tramite CENSO':
    'issued through CENSO',
  'importato':
    'imported',
  'in attesa di approvazione':
    'awaiting approval',
  'l’agente CENSO non lo vede esposto da nessuna parte':
    'the CENSO agent does not see it exposed anywhere',
  'non riuscito':
    'failed',
  'richiesta n. {n}':
    'request no. {n}',
  'rifiutato':
    'denied',
  'valido fino al {d}':
    'valid until {d}',
  '{a} nuovi, {u} già presenti':
    '{a} new, {u} already present',
  'Accendilo se WinRM o il sito /certsrv hanno un certificato della tua PKI che la sonda riconosce.':
    'Turn it on if WinRM or the /certsrv site have a certificate from your PKI that the probe recognises.',
  'Accesa, dalla Gestione certificati si inviano a questa CA le richieste di certificato (CSR) con il modello scelto e se ne ritira il certificato emesso. La chiave privata non passa mai da CENSO: la CSR si genera sulla macchina che userà il certificato.':
    'On, Certificate management sends certificate requests (CSR) to this CA with the chosen template and collects the issued certificate. The private key never passes through CENSO: the CSR is generated on the machine that will use the certificate.',
  'CA (server\\nome)':
    'CA (server\\name)',
  'CA Microsoft (AD CS)':
    'Microsoft CA (AD CS)',
  'Gestione dei certificati con la CA Microsoft (AD CS) attraverso la sonda: CA e modelli pubblicati, invio delle richieste (CSR) e ritiro dei certificati, via WinRM (solo gli script AD CS del catalogo della sonda) o via il sito Web Enrollment. Non apre porte e non avvia job. Abilitala dove la sonda raggiunge la CA.':
    'Certificate management with the Microsoft CA (AD CS) through the probe: published CAs and templates, sending requests (CSR) and collecting certificates, via WinRM (only the AD CS scripts of the probe’s catalogue) or via the Web Enrollment site. It opens no ports and starts no jobs. Enable it where the probe reaches the CA.',
  "Il sito di registrazione web della CA, con l'autenticazione di Windows (NTLM; Basic solo in HTTPS). Richieste e ritiro sì, elenco dei certificati emessi no.":
    'The CA’s web enrollment site, with Windows authentication (NTLM; Basic only over HTTPS). Requests and retrieval yes, list of issued certificates no.',
  'Indirizzo di Web Enrollment':
    'Web Enrollment address',
  "L'utente deve avere il permesso «Registra» (Enroll) sui modelli che vuoi usare. Per leggere i certificati emessi dal database della CA (il Repository) serve anche la lettura sulla CA, per esempio il ruolo di «Responsabile certificati»; solo in modalità WinRM. Le richieste che il modello mette in attesa di approvazione restano in attesa finché un responsabile non le approva sulla CA.":
    'The user needs the “Enroll” permission on the templates you want to use. To read the issued certificates from the CA database (the Repository) read access on the CA is also needed, for example the “Certificate Manager” role; WinRM mode only. Requests the template holds for approval stay pending until a manager approves them on the CA.',
  'Modalità Web Enrollment: il sito /certsrv della CA. Con questa modalità non si legge il database della CA.':
    'Web Enrollment mode: the CA’s /certsrv site. In this mode the CA database is not read.',
  'Modalità WinRM: la CA a cui mandare le richieste. Vuoto: si sceglie fra le CA pubblicate in Active Directory.':
    'WinRM mode: the CA to send requests to. Empty: choose among the CAs published in Active Directory.',
  'Modalità WinRM: un server Windows del dominio raggiungibile dalla sonda, di norma la CA stessa. Le CA aziendali e i modelli si leggono da Active Directory.':
    'WinRM mode: a Windows server of the domain reachable from the probe, usually the CA itself. Enterprise CAs and templates are read from Active Directory.',
  'PKI (CA Microsoft)':
    'PKI (Microsoft CA)',
  'Server Windows (WinRM)':
    'Windows server (WinRM)',
  'Solo su un segmento fidato. Il default è HTTPS sulla 5986.':
    'Only on a trusted segment. The default is HTTPS on 5986.',
  'Web Enrollment (/certsrv)':
    'Web Enrollment (/certsrv)',
  'WinRM (NTLM)':
    'WinRM (NTLM)',
  'ca01.dominio.local':
    'ca01.domain.local',
  'ca01.dominio.local\\DOMINIO Issuing CA':
    'ca01.domain.local\\DOMAIN Issuing CA',
  'es. portale.dominio.local':
    'e.g. portal.domain.local',
  'es. www.dominio.local, 10.0.0.5':
    'e.g. www.domain.local, 10.0.0.5',
  'https://ca01.dominio.local/certsrv':
    'https://ca01.domain.local/certsrv',
  '«DOMINIO\\utente» con il permesso Enroll sui modelli; per il Repository anche la lettura sulla CA. Le interfacce della CA non dipendono dalla lingua di Windows.':
    '“DOMAIN\\user” with the Enroll permission on the templates; for the Repository also read access on the CA. The CA’s interfaces do not depend on the language of Windows.',
  'Nota: CA e modelli sono letti dalla CA installata sul server, perché dalla sessione WinRM Active Directory non è raggiungibile (con NTLM le credenziali non fanno il secondo salto verso il domain controller). Richieste, ritiro e certificati emessi funzionano normalmente; dei modelli si conosce solo il nome, senza durata né l’indicazione se il soggetto viene dalla richiesta.':
    'Note: CAs and templates are read from the CA installed on the server, because Active Directory cannot be reached from the WinRM session (with NTLM the credentials do not make the second hop to the domain controller). Requests, retrieval and issued certificates work normally; of the templates only the name is known, without validity or whether the subject comes from the request.',
  "1. Il certificato. Se nel dominio c'è l'autoregistrazione dei certificati computer, di solito c'è già: deve comparire il nome completo del server. Se manca, chiedilo dalla Gestione certificati con il modello «Computer» o «Server Web». Annota l'impronta (Thumbprint).":
    '1. The certificate. If the domain has computer certificate auto-enrollment, it is usually already there: the server’s full name must appear. If it is missing, request it from Certificate management with the “Computer” or “Web Server” template. Note the fingerprint (Thumbprint).',
  "2. Il listener HTTPS, con l'impronta del passo 1. Usa l'impronta e non cercare il certificato per nome: sul server di una CA il certificato della CA ha lo stesso nome ma non serve all'autenticazione server, e WinRM lo rifiuta («The Enhanced Key Usage (EKU) field of the certificate is not set to Server Authentication»).":
    '2. The HTTPS listener, with the fingerprint from step 1. Use the fingerprint and do not look the certificate up by name: on a CA server the CA’s own certificate has the same name but is not for server authentication, and WinRM refuses it (“The Enhanced Key Usage (EKU) field of the certificate is not set to Server Authentication”).',
  "3. La porta 5986 nel firewall, meglio se aperta solo all'indirizzo della sonda.":
    '3. Port 5986 in the firewall, preferably open only to the probe’s address.',
  "Nella connessione: nel campo del server il nome completo (lo stesso del certificato), porta vuota (5986), questa casella spenta. «Verifica il certificato» accesa solo se la sonda si fida della CA che l'ha emesso.":
    'In the connection: the full name in the server field (the same as the certificate), port empty (5986), this box off. “Verify the certificate” on only if the probe trusts the CA that issued it.',
  'Per attivare WinRM in HTTPS sul server Windows servono tre cose: un certificato per il nome completo del server con uso «Autenticazione server», un listener HTTPS di WinRM e la porta 5986 aperta. I comandi vanno in PowerShell da amministratore, sul server.':
    'To turn on WinRM over HTTPS on the Windows server you need three things: a certificate for the server’s full name with “Server Authentication” usage, a WinRM HTTPS listener and port 5986 open. The commands go in PowerShell as administrator, on the server.',
  "Quando il certificato viene rinnovato il listener resta sull'impronta vecchia e WinRM in HTTPS smette di rispondere: aggiornalo con l'impronta nuova. La scadenza la vedi in Sicurezza → Cifratura, perché l'agente CENSO legge anche il certificato esposto sulla 5986.":
    'When the certificate is renewed the listener stays on the old fingerprint and WinRM over HTTPS stops answering: update it with the new fingerprint. You see the expiry in Security → Encryption, because the CENSO agent also reads the certificate exposed on 5986.',
  'Senza una CA (solo in laboratorio) puoi crearne uno autofirmato; in quel caso lascia spenta «Verifica il certificato».':
    'Without a CA (lab only) you can create a self-signed one; in that case leave “Verify the certificate” off.',
  'Spenta (consigliato), CENSO usa WinRM in HTTPS sulla porta 5986: utente e contenuto viaggiano cifrati. Accesa, usa HTTP sulla 5985: con NTLM la password non passa in chiaro, ma i dati sì — solo su un segmento di rete fidato.':
    'Off (recommended), CENSO uses WinRM over HTTPS on port 5986: user and content travel encrypted. On, it uses HTTP on 5985: with NTLM the password does not travel in clear, but the data does — only on a trusted network segment.',
  'Su molti server conviene una GPO: autoregistrazione dei certificati computer, «Allow remote server management through WinRM» (Componenti di Windows → Gestione remota Windows → Servizio WinRM) e la regola del firewall; il listener HTTPS si crea con il comando del passo 2, per esempio in uno script di avvio.':
    'On many servers use a GPO: computer certificate auto-enrollment, “Allow remote server management through WinRM” (Windows Components → Windows Remote Management → WinRM Service) and the firewall rule; the HTTPS listener is created with the command of step 2, for example in a startup script.',
  "Verifica: il comando seguente deve mostrare un listener «Transport = HTTPS», «Port = 5986» con l'impronta del certificato. Da un altro Windows, «Test-WSMan <server> -UseSSL» deve rispondere senza errori.":
    'Check: the following command must show a listener “Transport = HTTPS”, “Port = 5986” with the certificate’s fingerprint. From another Windows machine, “Test-WSMan <server> -UseSSL” must answer without errors.',
  'Altri certificati':
    'Other certificates',
  'Altri certificati ({n})':
    'Other certificates ({n})',
  'Certificati web':
    'Web certificates',
  'Certificati web ({n})':
    'Web certificates ({n})',
  'Più parole: tutte. a|b: l’una o l’altra. !x: esclude. =x: valore esatto. Sui numeri: >30, <=7, 10..20.':
    'Several words: all. a|b: either. !x: excludes. =x: exact value. On numbers: >30, <=7, 10..20.',
  'computer':
    'computer',
  'firma del codice':
    'code signing',
  'web':
    'web',
  'Nel filtro: più parole devono esserci tutte, «a|b» è l’una o l’altra, «!x» esclude, «=x» è il valore esatto, e sui numeri «>30», «<=7», «10..20». Le colonne con pochi valori che si ripetono (tipo, stato, origine) si filtrano da un menu; «Azzera filtri» li toglie tutti.':
    'In the filter: several words must all be present, “a|b” is either, “!x” excludes, “=x” is the exact value, and on numbers “>30”, “<=7”, “10..20”. Columns with few repeating values (type, state, origin) filter from a menu; “Clear filters” removes them all.',
  '2. Nomi e chiave':
    '2. Names and key',
  '3. Genera e invia':
    '3. Generate and send',
  'CENSO genera chiave e CSR e tiene la chiave nello store, cifrata con la chiave del tenant: quando la CA emette il certificato, un amministratore lo scarica in PFX con la catena, pronto da installare.':
    'CENSO generates key and CSR and keeps the key in the store, encrypted with the tenant’s key: when the CA issues the certificate, an administrator downloads it as a PFX with the chain, ready to install.',
  'Carica un certificato in qualsiasi formato — PEM, .cer/.crt/.der, .p7b, .pfx/.p12 — e scaricalo in un altro. Non salva niente nel repository né nello store: legge, converte e restituisce il file.':
    'Upload a certificate in any format — PEM, .cer/.crt/.der, .p7b, .pfx/.p12 — and download it in another. Nothing is saved in the repository or the store: it reads, converts and returns the file.',
  'Catena':
    'Chain',
  'Catena .p7b (PKCS#7)':
    'Chain .p7b (PKCS#7)',
  'Catena incompleta: apri «CA e modelli» per far entrare il certificato della CA, o importalo.':
    'Incomplete chain: open “CAs and templates” to bring in the CA certificate, or import it.',
  'Certificato .cer (DER)':
    'Certificate .cer (DER)',
  'Certificato .pem':
    'Certificate .pem',
  'Chiave .pem cifrata (PKCS#8)':
    'Encrypted .pem key (PKCS#8)',
  'Chiave generata in CENSO':
    'Key generated in CENSO',
  'Chiave sul server':
    'Key on the server',
  'Chiavi':
    'Keys',
  'Con la catena .pem':
    'With the chain .pem',
  'Con la catena delle CA':
    'With the CA chain',
  'Con la chiave privata':
    'With the private key',
  'Converti e scarica':
    'Convert and download',
  'Converti in':
    'Convert to',
  'Convertitore':
    'Converter',
  'Convertitore di certificati':
    'Certificate converter',
  'Dove nasce la chiave':
    'Where the key is born',
  'Eliminare la chiave dallo store? I certificati restano, ma senza chiave non si potranno più esportare in PFX da qui.':
    'Delete the key from the store? The certificates stay, but without the key they can no longer be exported as PFX from here.',
  'Esporta':
    'Export',
  'Esportazioni':
    'Exports',
  'gestisce l’intero ciclo di richiesta dei certificati tramite Microsoft AD CS, dalla preparazione alla generazione e al download. La chiave privata può essere generata sulla macchina di destinazione o conservata cifrata in CENSO.': 'manages the whole certificate request lifecycle through Microsoft AD CS, from preparation to generation and download. The private key can be generated on the target machine or kept encrypted in CENSO.',
  'Indica i nomi del certificato: CENSO prepara i comandi da eseguire sul server. La chiave privata resta lì; qui torna solo la CSR. È la strada più sicura.':
    'Enter the certificate’s names: CENSO prepares the commands to run on the server. The private key stays there; only the CSR comes back here. It is the safest way.',
  'La chiave privata è nello store, cifrata':
    'The private key is in the store, encrypted',
  'La chiave privata è nello store: la può esportare (PFX o PEM cifrata) un amministratore del tenant.':
    'The private key is in the store: a tenant administrator can export it (PFX or encrypted PEM).',
  'Chiavi private archiviate nello store cifrato con la chiave del tenant e protette dal relativo ciclo di rotazione. Non sono mai esposte in chiaro; l’esportazione è consentita solo agli amministratori insieme al certificato, in PFX o PEM protetto da password.': 'Private keys stored in the store encrypted with the tenant key and protected by its rotation cycle. They are never exposed in clear; export is allowed only to administrators together with the certificate, in PFX or password-protected PEM.',
  'Nessuna chiave nello store':
    'No key in the store',
  'Ogni esportazione finisce nell’audit del tenant e nel conteggio della chiave. Conserva la password a parte: senza, il file non si apre.':
    'Each export goes into the tenant’s audit and the key’s count. Keep the password separately: without it the file does not open.',
  'PEM, .cer/.crt/.der, .p7b, oppure .pfx/.p12 con la sua password: i certificati vanno nel repository, la chiave privata (se c’è) nello store, cifrata con la chiave del tenant.':
    'PEM, .cer/.crt/.der, .p7b, or .pfx/.p12 with its password: the certificates go into the repository, the private key (if any) into the store, encrypted with the tenant’s key.',
  'PFX / P12 (AES)':
    'PFX / P12 (AES)',
  'PFX compatibile (3DES, Windows prima del 2019)':
    'Compatible PFX (3DES, Windows before 2019)',
  'Password (almeno 8 caratteri)':
    'Password (at least 8 characters)',
  'Password del file (PFX o chiave cifrata)':
    'File password (PFX or encrypted key)',
  'Password del file (se PFX o chiave cifrata)':
    'File password (if PFX or encrypted key)',
  'Password del nuovo PFX':
    'Password of the new PFX',
  'Password della chiave (vuota: in chiaro)':
    'Key password (empty: unencrypted)',
  'Ripeti la password':
    'Repeat the password',
  'Solo la chiave .pem':
    'Key only .pem',
  'Solo le CA .pem':
    'CAs only .pem',
  'chiavi':
    'keys',
  'generata in CENSO':
    'generated in CENSO',
  'nello store':
    'in the store',
  'nessun certificato della CA nel repository':
    'no CA certificate in the repository',
  '{n} certificati importati, {k} chiavi nello store':
    '{n} certificates imported, {k} keys in the store',
  '«Con la catena» è quello che vuole un server (nginx, Apache, HAProxy); «Solo le CA» è il bundle da dare ai client; il .p7b si importa così in Windows.':
    '“With the chain” is what a server wants (nginx, Apache, HAProxy); “CAs only” is the bundle to give clients; the .p7b imports as is in Windows.',
  'i certificati dell’organizzazione e le richieste alla CA Microsoft (AD CS). «Repository certificati»: quelli emessi tramite CENSO, importati (anche PFX con la chiave) o letti dal database della CA, con scadenza, catena, responsabile e dove l’agente li vede installati; si scaricano in .pem, .cer, con la catena, solo le CA, .p7b e — per gli amministratori, con una password — in .pfx o chiave .pem. «Chiavi»: lo store delle chiavi private, cifrate con la chiave del tenant e mai mostrate. «Nuova richiesta»: scegli connessione, CA e modello, indica i nomi e la chiave; la chiave nasce sul server (CENSO prepara i comandi certreq o openssl e tu incolli la CSR) oppure in CENSO, nello store. «Richieste»: lo stato che la CA ha dato, con «Ricontrolla» per quelle in attesa di approvazione. «Convertitore»: un certificato da un formato all’altro, senza salvarlo. «CA e modelli»: cosa raggiunge ogni connessione. Serve una connessione «CA Microsoft (AD CS)» — WinRM verso un server del dominio oppure il sito Web Enrollment /certsrv —, con «Consenti la scrittura» per inviare, e la capability «PKI» sulla sonda che raggiunge la CA.':
    'the organisation’s certificates and the requests to the Microsoft CA (AD CS). “Certificate repository”: those issued through CENSO, imported (also PFX with the key) or read from the CA database, with expiry, chain, owner and where the agent sees them installed; they download as .pem, .cer, with the chain, CAs only, .p7b and — for administrators, with a password — as .pfx or .pem key. “Keys”: the store of private keys, encrypted with the tenant’s key and never shown. “New request”: choose connection, CA and template, enter the names and the key; the key is born on the server (CENSO prepares the certreq or openssl commands and you paste the CSR) or in CENSO, in the store. “Requests”: the state the CA gave, with “Check again” for those awaiting approval. “Converter”: a certificate from one format to another, without saving it. “CAs and templates”: what each connection reaches. It needs a “Microsoft CA (AD CS)” connection — WinRM to a server of the domain or the Web Enrollment site /certsrv —, with “Allow writing” to send, and the “PKI” capability on the probe that reaches the CA.',
  'AES':
    'AES',
  'SHA':
    'SHA',
  'AES (128, predefinito), AES192, AES256, DES. Sugli apparati Cisco configurati con «priv aes 192/256» usa AES192C o AES256C: Cisco allunga la chiave in un altro modo e con AES256 semplice non si capirebbero.':
    'AES (128, default), AES192, AES256, DES. On Cisco devices configured with “priv aes 192/256” use AES192C or AES256C: Cisco extends the key differently and with plain AES256 they would not understand each other.',
  'SHA (predefinito), SHA224, SHA256, SHA384, SHA512, MD5.':
    'SHA (default), SHA224, SHA256, SHA384, SHA512, MD5.',
  'Altro ({n})':
    'More ({n})',
  'Altre sezioni ({n})':
    'More sections ({n})',
  'Certificato con la catena':
    'Certificate with the chain',
  'Installa sul server':
    'Install on the server',
  'L’agente CENSO della macchina ritira certificato, catena e chiave cifrati per lui al prossimo contatto (entro un minuto) e li installa. Fa solo quello che scegli qui: nessun comando arriva da fuori.':
    'The machine’s CENSO agent collects certificate, chain and key, encrypted for it, at its next contact (within a minute) and installs them. It does only what you choose here: no command comes from outside.',
  'Nessun CI con un agente CENSO attivo su Windows o Linux.':
    'No CI with an active CENSO agent on Windows or Linux.',
  'Oppure file unico (HAProxy)':
    'Or a single file (HAProxy)',
  'Poi ricarica':
    'Then reload',
  'Porta HTTPS':
    'HTTPS port',
  'Ripeti':
    'Retry',
  'Sempre: importazione nell’archivio certificati del computer (LocalMachine\\My), chiave non esportabile.':
    'Always: import into the computer’s certificate store (LocalMachine\\My), key not exportable.',
  'Sito IIS (facoltativo)':
    'IIS site (optional)',
  'Solo sotto /etc/ssl, /etc/pki, /etc/nginx, /etc/apache2, /etc/httpd, /etc/haproxy, /etc/censo, /opt, /srv; i file che c’erano restano con il suffisso .censo-bak; la chiave è leggibile solo da root.':
    'Only under /etc/ssl, /etc/pki, /etc/nginx, /etc/apache2, /etc/httpd, /etc/haproxy, /etc/censo, /opt, /srv; the existing files are kept with the .censo-bak suffix; the key is readable by root only.',
  'Usalo per il listener WinRM HTTPS':
    'Use it for the WinRM HTTPS listener',
  'consegnato all’agente':
    'delivered to the agent',
  'in attesa dell’agente':
    'waiting for the agent',
  'installato':
    'installed',
  'niente':
    'nothing',
  'Un certificato con la chiave nello store si installa sul server dal suo dettaglio, «Installa sul server»: l’agente CENSO della macchina lo ritira cifrato e su Windows lo importa nell’archivio del computer (e, se scegli, nel binding HTTPS di IIS e nel listener WinRM), su Linux scrive certificato e chiave nei percorsi indicati e ricarica nginx, Apache o HAProxy; l’esito compare sotto, con «Ripeti».':
    'A certificate with its key in the store is installed on the server from its detail, “Install on the server”: the machine’s CENSO agent collects it encrypted and on Windows imports it into the computer’s store (and, if you choose, into the IIS HTTPS binding and the WinRM listener), on Linux writes certificate and key to the given paths and reloads nginx, Apache or HAProxy; the outcome shows below, with “Retry”.',
  'es. Default Web Site':
    'e.g. Default Web Site',
  'es. /etc/haproxy/certs/sito.pem':
    'e.g. /etc/haproxy/certs/site.pem',
  'Scarica MSI Windows (.msi)':
    'Download Windows MSI (.msi)',
  'Scarica pacchetto GPO (.zip)':
    'Download GPO package (.zip)',
  'Per Windows aziendale: l’MSI compare in «App installate», si disinstalla pulito e si assegna con GPO «Installazione software», Intune o SCCM; il pacchetto GPO è lo zip con in più uno script di avvio del computer pronto da indicare nei Criteri di gruppo.':
    'For enterprise Windows: the MSI appears in «Installed apps», uninstalls cleanly and is assigned via GPO «Software installation», Intune or SCCM; the GPO package is the zip plus a computer startup script ready to point to in Group Policy.',
  'Windows — MSI (App installate, GPO «Installazione software»)':
    'Windows — MSI (Installed apps, GPO «Software installation»)',
  'Scarica l’MSI: è specifico di questo agente (porta dentro la configurazione e il segreto) e aggiorna in loco se reinstalli lo stesso agente.':
    'Download the MSI: it is specific to this agent (it carries the configuration and the secret) and upgrades in place if you reinstall the same agent.',
  'A mano o in automazione: msiexec /i censo-inventory-*.msi /qn (installazione silenziosa, come SYSTEM). Compare in «App installate» come «CENSO · Agente d’inventario»; la disinstallazione toglie attività e file.':
    'By hand or automated: msiexec /i censo-inventory-*.msi /qn (silent install, as SYSTEM). It appears in «Installed apps» as «CENSO · Agente d’inventario»; uninstalling removes the task and the files.',
  'GPO: Configurazione computer → Criteri → Impostazioni software → Installazione software → Nuovo pacchetto → l’MSI dalla share, modalità «Assegnato». Si installa al riavvio.':
    'GPO: Computer Configuration → Policies → Software Settings → Software installation → New package → the MSI from the share, «Assigned» mode. Installs at reboot.',
  'Intune: carica l’MSI come app «Riga di comando» o LOB; SCCM: applicazione di tipo Windows Installer. Rilevamento: il prodotto MSI o C:\\ProgramData\\CENSO\\censo-inventory.exe.':
    'Intune: upload the MSI as a «Command line» or LOB app; SCCM: a Windows Installer type application. Detection: the MSI product or C:\\ProgramData\\CENSO\\censo-inventory.exe.',
  'Windows — script di avvio GPO (pacchetto GPO)':
    'Windows — GPO startup script (GPO package)',
  'Estrai lo zip del pacchetto GPO su una share letta dai computer (es. \\\\dominio\\NETLOGON\\censo-inventory).':
    'Extract the GPO package zip onto a share readable by the computers (e.g. \\\\domain\\NETLOGON\\censo-inventory).',
  'GPO → Configurazione computer → Criteri → Impostazioni di Windows → Script (avvio/arresto) → Avvio → Aggiungi: avvio-gpo.cmd dalla share.':
    'GPO → Computer Configuration → Policies → Windows Settings → Scripts (Startup/Shutdown) → Startup → Add: avvio-gpo.cmd from the share.',
  'Al riavvio installa solo dove manca (idempotente) e traccia in C:\\ProgramData\\CENSO\\gpo-avvio.log.':
    'At reboot it installs only where missing (idempotent) and logs to C:\\ProgramData\\CENSO\\gpo-avvio.log.',
  'Per Windows aziendale la «Distribuzione di massa» offre, oltre allo zip, un MSI e un pacchetto GPO. L’MSI compare in «App installate», si disinstalla pulito (toglie attività e file) e si assegna con la GPO «Installazione software», Intune o SCCM; è legato a questo agente e, reinstallandolo, aggiorna in loco. Il pacchetto GPO è lo zip con in più uno script di avvio del computer (avvio-gpo.cmd) da indicare nei Criteri di gruppo: al riavvio installa solo dove manca.':
    'For enterprise Windows, «Mass distribution» offers, besides the zip, an MSI and a GPO package. The MSI appears in «Installed apps», uninstalls cleanly (removing the task and files) and is assigned via the GPO «Software installation», Intune or SCCM; it is tied to this agent and, when reinstalled, upgrades in place. The GPO package is the zip plus a computer startup script (avvio-gpo.cmd) to point to in Group Policy: at reboot it installs only where missing.',
  'Oppure il playbook già pronto nel pacchetto: dalla cartella estratta, ansible-playbook -i <inventario> install.yml (copia il pacchetto sugli host e installa come root, idempotente; -e force=true per reinstallare).':
    'Or the ready-made playbook in the package: from the extracted folder, ansible-playbook -i <inventory> install.yml (copies the package to the hosts and installs as root, idempotent; -e force=true to reinstall).',
  'Togliere «{nome}» dal catalogo? Le sonde lo rimuoveranno.':
    'Remove «{nome}» from the catalog? The probes will drop it.',
  'Firmware (TFTP)':
    'Firmware (TFTP)',
  'Distribuzione del firmware ai CI, previa verifica dell’integrità dei file, tramite le sonde abilitate al servizio TFTP. Il protocollo è usato unicamente dalle sonde verso i CI.':
    'Firmware distribution to the CIs, after checking the files’ integrity, through the probes enabled for the TFTP service. The protocol is used only from the probes towards the CIs.',
  'Spazio del catalogo':
    'Catalog space',
  '{usato} di {tetto} usati':
    '{usato} of {tetto} used',
  'Caricamento… {pct}%':
    'Uploading… {pct}%',
  'Carica un firmware':
    'Upload a firmware',
  'Il caricamento avviene a blocchi per gestire anche file di grandi dimensioni; in caso di interruzione della rete, riprende automaticamente.': 'The upload happens in chunks to handle large files too; if the network drops, it resumes automatically.',
  'Nessun firmware nel catalogo. Carica il primo file qui sopra.':
    'No firmware in the catalog. Upload the first file above.',
  'Quali sonde servono «{nome}»':
    'Which probes serve «{nome}»',
  'Solo le sonde con la capability «Firmware (TFTP)» attiva. Attivala nella pagina Sonde, scheda capability.':
    'Only probes with the «Firmware (TFTP)» capability enabled. Enable it on the Probes page, capability tab.',
  'Nessuna sonda ha la capability «Firmware (TFTP)» attiva.':
    'No probe has the «Firmware (TFTP)» capability enabled.',
  '(sonda disattivata)':
    '(probe disabled)',
  'La sonda fa da server TFTP (sola lettura, UDP/69) per i firmware che la console carica e le assegna: gli apparati li scaricano da lì. I file arrivano alla sonda solo dal Control Plane, mai via TFTP. Si abilita dove la sonda raggiunge gli apparati da aggiornare. Non richiede licenza.':
    'The probe acts as a TFTP server (read-only, UDP/69) for the firmware the console uploads and assigns to it: the devices download them from there. Files reach the probe only from the Control Plane, never over TFTP. Enable it where the probe reaches the devices to update. No license required.',
  'il catalogo dei firmware che le sonde servono in TFTP agli apparati (per aggiornarli). Carichi un file — a blocchi, con ripresa — e scegli le sonde che lo serviranno; la sonda lo scarica dal Control Plane, verifica lo SHA-256 e lo serve in sola lettura su UDP/69. Serve la capability «Firmware (TFTP)» sulla sonda (senza licenza); lo spazio del catalogo ha un tetto di 4 GB. Da un apparato: copy tftp://<ip-sonda>/<file> flash:.':
    'the catalog of firmware the probes serve over TFTP to devices (to update them). Upload a file — in chunks, resumable — and choose the probes that will serve it; the probe downloads it from the Control Plane, verifies the SHA-256 and serves it read-only on UDP/69. It needs the «Firmware (TFTP)» capability on the probe (no license); the catalog space is capped at 4 GB. From a device: copy tftp://<probe-ip>/<file> flash:.',
  'Obbligatori':
    'Required',
  'Chiavi obbligatorie e valori ammessi':
    'Required keys and allowed values',
  'Le chiavi qui elencate devono stare su ogni CI; se una chiave ha dei valori ammessi, solo quelli si possono usare. Esempio tipico: «ambiente» con produzione/collaudo/sviluppo. L’obbligo si sorveglia (copertura qui sotto) e il patching lo pretende, ma non blocca la scoperta.':
    'The keys listed here must be present on every CI; if a key has allowed values, only those can be used. Typical example: «environment» with production/staging/development. The requirement is monitored (coverage below) and patching requires it, but it does not block discovery.',
  'Attiva «ambiente» standard':
    'Enable the standard «environment»',
  'qualunque valore (nessun vocabolario)':
    'any value (no vocabulary)',
  'aggiungi un valore ammesso e premi Invio':
    'add an allowed value and press Enter',
  'Aggiungi una chiave obbligatoria':
    'Add a required key',
  'Copertura sui CI':
    'Coverage on CIs',
  'Nessuna chiave obbligatoria definita.':
    'No required key defined.',
  '{con} di {tot} CI hanno «{chiave}»; {mancano} mancanti.':
    '{con} of {tot} CIs have «{chiave}»; {mancano} missing.',
  'Assegna «{val}» ai {n} CI senza':
    'Assign «{val}» to the {n} CIs without',
  'Nessuna attività registrata.':
    'No activity recorded.',
  'Cosa è stato fatto su questo CI nel tempo: i cambi tecnici rilevati e le azioni eseguite. Dalla più recente.':
    'What has been done to this CI over time: the technical changes detected and the actions performed. Most recent first.',
  'azione':
    'action',
  'Patching':
    'Patching',
  'Finestre di manutenzione':
    'Maintenance windows',
  'Software installato':
    "Installed software",
  'Critiche': 'Critical',
  'Sfruttate attivamente': 'Actively exploited',
  'Sfruttate': 'Exploited',
  'subito':
    'now',
  'Rollback':
    'Rollback',
  'Nuova finestra':
    'New window',
  'Nessuna finestra definita.':
    'No window defined.',
  'Lun':
    'Mon',
  'Mar':
    'Tue',
  'Mer':
    'Wed',
  'Gio':
    'Thu',
  'Ven':
    'Fri',
  'Sab':
    'Sat',
  'Dom':
    'Sun',
  'una volta':
    'once',
  'ogni giorno':
    'every day',
  'KB Windows':
    'Windows KB',
  'In uso ma non documentati in phpIPAM:':
    'In use but not documented in phpIPAM:',
  'Documenta in phpIPAM':
    'Document in phpIPAM',
  'es. openai.com':
    'e.g. openai.com',
  'es. ChatGPT':
    'e.g. ChatGPT',
  'Diagnostica':
    'Diagnostics',
  'catalogo {n} servizi':
    'catalogue {n} services',
  'catalogo AI non ricevuto (add-on non attivo o sonda da aggiornare)':
    'AI catalogue not received (add-on not active or probe needs updating)',
  'non attivo':
    'not active',
  'Servizi nel catalogo':
    'Services in catalogue',
  'IP riconosciuti (NetFlow)':
    'Recognised IPs (NetFlow)',
  'certi':
    'certain',
  'Ultimo avvistamento':
    'Last sighting',
  'Righe d’uso':
    'Usage rows',
  'Il catalogo è vuoto: il modulo non può riconoscere nulla. Di solito è una migrazione non applicata — riavvia il Control Plane.':
    'The catalogue is empty: the module cannot recognise anything. Usually a migration that was not applied — restart the Control Plane.',
  'Nessun IP ancora in mappa: il riconoscimento dai flussi NetFlow parte quando un mirror vede l’SNI (o dopo la risoluzione DNS del catalogo). Con un mirror attivo l’SNI rileva già da solo.':
    'No IP in the map yet: recognition from NetFlow starts once a mirror sees the SNI (or after DNS resolution of the catalogue). With an active mirror the SNI already detects on its own.',
  'Quando un oggetto non risponde perché è giù ciò che gli sta a monte (lo switch, l’host, il controller), Eagle non manda una mail per ciascun oggetto dietro: riconosce la causa dalle dipendenze e avvisa una volta sola, sulla radice, scrivendo l’impatto (quanti oggetti trascina e quali business service colpiti) e alzando la gravità se ne trascina altri. Un oggetto si può osservare da più punti (anche solo per il ping): è giù davvero solo se è giù da tutti.':
    'When an object does not respond because what is upstream of it is down (the switch, the host, the controller), Eagle does not send one email per object behind it: it recognises the cause from the dependencies and alerts once, on the root, writing the impact (how many objects it takes down and which business services are affected) and raising the severity when it takes others down. An object can be watched from several points (even just for ping): it is truly down only if it is down from all of them.',
  'Area clinica':
    'Clinical area',
  'Catalogo elettromedicali ({n})':
    'Medical device catalogue ({n})',
  'Classe di rischio':
    'Risk class',
  'Collegato al paziente':
    'Patient-connected',
  'Condizioni (tutte)':
    'Conditions (all)',
  'DICOM':
    'DICOM',
  'Dati curati':
    'Curated data',
  'Falsi positivi':
    'False positives',
  'Fine vita':
    'End of life',
  'HL7':
    'HL7',
  'Hardware (mai una VM)':
    'Hardware (never a VM)',
  'In più':
    'Extra',
  'LLDP':
    'LLDP',
  'Nessuna proprietà, da sola o in coppia, distingue questo modello dagli altri con precisione sufficiente: servono altri segnali (filo, annunci, flussi).':
    'No property, alone or in a pair, tells this model apart from the others with enough precision: more signals are needed (wire, announcements, flows).',
  'OEM':
    'OEM',
  'OUI':
    'OUI',
  'Precisione':
    'Precision',
  'Proprietà osservate':
    'Observed properties',
  'Recall':
    'Recall',
  'Regole di riconoscimento ({n})':
    'Recognition rules ({n})',
  'Ricostruire il catalogo e le regole dagli inventari di riferimento di tutti gli ospedali? I dati curati e le regole sospese restano.':
    'Rebuild the catalogue and the rules from every hospital’s reference inventories? Curated data and suspended rules are kept.',
  'Ricostruisci dal metro':
    'Rebuild from the benchmark',
  'Ricostruiscilo dal metro: servono inventari di riferimento importati nei tenant.':
    'Rebuild it from the benchmark: reference inventories imported in the tenants are needed.',
  'Ricostruzione…':
    'Rebuilding…',
  'Rivisto':
    'Reviewed',
  'SIP':
    'SIP',
  'SMB':
    'SMB',
  'SSDP':
    'SSDP',
  'SSH':
    'SSH',
  'Scheda MDS2 (URL)':
    'MDS2 form (URL)',
  'Un modello per riga, con le proprietà che CENSO osserva sui suoi dispositivi (prefissi, impronta DHCP, annunci, filo, flussi) e i dati che nessuna rete mostra. Per ogni modello le regole di riconoscimento: combinazioni di proprietà che lo distinguono dagli altri in tutti gli ospedali, mai un prefisso MAC da solo, e mai valide se toccano una macchina informatica (VM, agente CENSO). Gli inventari di riferimento sono il metro: «in più» sono dispositivi che nessun inventario elenca e che una regola riconosce — da rivedere, non errori.':
    'One model per row, with the properties CENSO observes on its devices (prefixes, DHCP fingerprint, announcements, wire, flows) and the data no network shows. For each model, the recognition rules: combinations of properties that tell it apart from the others in every hospital, never a MAC prefix alone, and never valid if they touch an IT machine (VM, CENSO agent). The reference inventories are the benchmark: «extra» are devices no inventory lists that a rule recognises — to review, not errors.',
  'User-Agent':
    'User-Agent',
  'WS-Discovery':
    'WS-Discovery',
  'candidata':
    'candidate',
  'classe vendor DHCP':
    'DHCP vendor class',
  'elettromedicali negli inventari':
    'medical devices in the inventories',
  'firmware':
    'firmware',
  'flussi: chiama':
    'flows: calls',
  'flussi: risponde':
    'flows: answers',
  'flusso':
    'flow',
  'in più, da rivedere':
    'extra, to review',
  'manuale':
    'manual',
  'modello mDNS':
    'mDNS model',
  'porta d’annuncio':
    'announcement port',
  'precisione ({n} falsi positivi)':
    'precision ({n} false positives)',
  'rivisto':
    'reviewed',
  'server HTTP':
    'HTTP server',
  'servizio mDNS':
    'mDNS service',
  'sistema operativo':
    'operating system',
  'validata':
    'validated',
  'visti da CENSO':
    'seen by CENSO',
  'Regole: {m} di modello, {f} di famiglia, {ti} di tipo, {v} di elettromedicale; validate in 2+ ospedali: {val}. Un dispositivo è riconosciuto al livello più preciso che una regola raggiunge; i valori sono cumulativi.':
    'Rules: {m} model, {f} family, {ti} type, {v} medical device; validated in 2+ hospitals: {val}. A device is recognised at the most precise level a rule reaches; the figures are cumulative.',
  'Riconosciuti (mod · fam · tipo · medic.)':
    'Recognised (model · family · type · medical)',
  'Ricostruzioni precedenti':
    'Previous rebuilds',
  'famiglia':
    'family',
  'modello · famiglia · tipo · elettromedicale':
    'model · family · type · medical device',
  'riconosciuti come {livello}':
    'recognised as {livello}',
  '{n} dispositivi riconosciuti a un livello con il valore sbagliato (es. il tipo).':
    '{n} devices recognised at a level with the wrong value (e.g. the type).',
  'Applica le selezionate ({n})':
    'Apply the selected ({n})',
  'Applicare {n} correzioni alla base? Le voci eliminate smettono subito di riconoscere, quelle ristrette valgono solo nell’ospedale indicato.':
    'Apply {n} corrections to the database? Deleted entries stop recognising at once, restricted ones apply only in the hospital shown.',
  'Correzione':
    'Correction',
  'Dove si vedono solo i flussi (NetFlow, IPFIX, sFlow) e nessun nome, CENSO riconosce un dispositivo da come si comporta: su quale porta chiama gli altri (un analizzatore che manda i risultati al middleware del laboratorio, una modalità che manda immagini al PACS) e su quale viene chiamato. Ogni sei ore ricava questi comportamenti dagli ultimi sette giorni di flussi, togliendo le porte generiche; una porta da sola però non dice il modello (la usano anche server e PC), quindi vale solo come indizio e il catalogo elettromedicali la usa insieme ad altre proprietà. I flussi vedono il traffico che passa da un router: un monitor che parla con la sua centrale nella stessa VLAN non compare.':
    'Where only flows are seen (NetFlow, IPFIX, sFlow) and no names, CENSO recognises a device by how it behaves: which port it calls others on (an analyser sending results to the laboratory middleware, a modality sending images to the PACS) and which port it is called on. Every six hours it derives these behaviours from the last seven days of flows, leaving out generic ports; a port alone does not tell the model, though (servers and PCs use it too), so it is only a hint and the medical device catalogue uses it together with other properties. Flows see traffic that goes through a router: a monitor talking to its central station on the same VLAN does not appear.',
  'Eliminate {d} voci, {r} ristrette a un ospedale.':
    'Deleted {d} entries, {r} restricted to one hospital.',
  'Imparate: {p} prefissi, {f} impronte, {h} nomi di prodotto, {a} annunci in rete; {o} solo per l’ospedale che le ha insegnate.':
    'Learnt: {p} prefixes, {f} fingerprints, {h} product names, {a} network announcements; {o} only for the hospital that taught them.',
  'La base passa i controlli':
    'The database passes the checks',
  'La card «Confronto con un inventario di riferimento» importa l’elenco dei dispositivi di Armis, di Medigate o dell’Ingegneria Clinica (Excel o CSV con una colonna di MAC o di IP; solo amministratori) e dice per ognuno se CENSO lo vede e se lo riconosce: stesso modello, elettromedicale, tipo generico, visto ma non riconosciuto, non visto. La tabella per tipo dà la copertura e ogni riga apre la lista. «Firme da imparare» propone le regole per la base di riconoscimento di CENSO (prefisso MAC, impronta DHCP o nome che portano sempre lo stesso modello o tipo); le scrive l’amministrazione della piattaforma. Le firme sicure valgono per tutti i tenant; un nome dato dall’ospedale vale solo per l’ospedale che l’ha insegnato. Una volta imparate restano anche se l’inventario di riferimento si cancella: è così che CENSO ritrova da solo gli elettromedicali. Non diventano mai una regola i PC e i server di marca generica, il prefisso di chi fa la scheda o il PC (OEM), l’impronta DHCP di Windows o Linux e la sola porta vista nei flussi: dicono chi ha fatto la scheda o che sistema gira, non quale dispositivo è.':
    'The «Comparison with a reference inventory» card imports the device list from Armis, Medigate or Clinical Engineering (Excel or CSV with a MAC or IP column; administrators only) and tells for each one whether CENSO sees it and recognises it: same model, medical device, generic type, seen but not recognised, not seen. The table by type gives the coverage and each row opens the list. «Signatures to learn» proposes rules for the CENSO recognition database (MAC prefix, DHCP fingerprint or name that always carry the same model or type); the platform administration writes them. Safe signatures apply to every tenant; a name given by the hospital applies only to the hospital that taught it. Once learnt they stay even if the reference inventory is deleted: that is how CENSO finds medical devices on its own. Generic-brand PCs and servers, the prefix of whoever makes the board or the PC (OEM), the DHCP fingerprint of Windows or Linux and a port seen in the flows alone never become a rule: they tell who made the board or which system runs, not which device it is.',
  'Le firme che gli inventari di riferimento caricati nei tenant insegnano, unite. Quelle sicure si imparano per tutti i tenant; un nome dato dall’ospedale o un modello su cui gli ospedali non concordano si imparano solo per gli ospedali che li hanno insegnati, ognuno con il suo modello. Il prefisso di chi fa la scheda o il PC (OEM), l’impronta DHCP di Windows o Linux e la sola porta vista nei flussi non arrivano più qui: il catalogo elettromedicali li usa solo insieme ad altre proprietà. Arrivano qui solo la firma e i conteggi, mai MAC, nomi o indirizzi dei clienti.':
    'The signatures the reference inventories loaded in the tenants teach, merged. Safe ones are learnt for every tenant; a name given by the hospital or a model the hospitals disagree on is learnt only for the hospitals that taught it, each with its own model. The prefix of whoever makes the board or the PC (OEM), the DHCP fingerprint of Windows or Linux and a port seen in the flows alone no longer arrive here: the medical device catalogue uses them only together with other properties. Only the signature and the counts arrive here, never customers’ MACs, names or addresses.',
  'Ogni voce della base passa dagli stessi controlli delle regole del catalogo elettromedicali. Si eliminano le impronte DHCP di Windows o Linux, i prefissi di chi fa la scheda o il PC (OEM), gli indirizzi dati «per stessa impronta» che l’inventario non conferma, le porte dei flussi insegnate da un solo ospedale e le parole comuni; i nomi che un ospedale dà ai suoi dispositivi restano solo per quell’ospedale. Le voci scritte a mano non si toccano. Nulla cambia finché non applichi.':
    'Every entry of the database goes through the same checks as the rules of the medical device catalogue. Deleted: DHCP fingerprints of Windows or Linux, prefixes of whoever makes the board or the PC (OEM), addresses given «by the same fingerprint» that the inventory does not confirm, flow ports taught by a single hospital and common words; the names a hospital gives its devices stay for that hospital only. Entries written by hand are not touched. Nothing changes until you apply.',
  'Prefissi e impronte DHCP dietro cui gli inventari mettono tipi o modelli diversi, postazioni e server di marca generica, prefissi di chi fa la scheda o il PC (OEM) e impronte DHCP di Windows o Linux: nessuna regola possibile da soli. Questi dispositivi si riconoscono dal nome che annunciano, dai protocolli sul filo, con la scansione o con le regole congiunte del catalogo elettromedicali.':
    'Prefixes and DHCP fingerprints behind which the inventories put different types or models, generic-brand workstations and servers, prefixes of whoever makes the board or the PC (OEM) and DHCP fingerprints of Windows or Linux: no rule is possible on their own. These devices are recognised by the name they announce, by the protocols on the wire, by scanning or by the combined rules of the medical device catalogue.',
  'Scrivere {n} firme nella base di riconoscimento CENSO? I nomi dati dall’ospedale valgono solo per questo ospedale, le altre per tutti i tenant. Verifica che il contratto con il fornitore dell’inventario consenta di usarne le classificazioni.':
    'Write {n} signatures into the CENSO recognition database? Names given by the hospital apply to this hospital only, the others to every tenant. Check that the contract with the inventory vendor allows using its classifications.',
  'Un prefisso MAC del costruttore del dispositivo che nell’inventario porta sempre lo stesso modello (o lo stesso tipo e marca), un’impronta DHCP o un nome che portano sempre lo stesso modello; i nomi dati dall’ospedale si imparano solo per questo ospedale. Non diventano mai una regola il prefisso di chi fa la scheda o il PC (OEM), l’impronta DHCP di Windows o Linux, la sola porta vista nei flussi e i PC o server di marca generica: dicono chi ha fatto la scheda o che sistema gira, non quale dispositivo è.':
    'A MAC prefix of the device’s own maker that always carries the same model (or the same type and brand) in the inventory, a DHCP fingerprint or a name that always carry the same model; names given by the hospital are learnt for this hospital only. The prefix of whoever makes the board or the PC (OEM), the DHCP fingerprint of Windows or Linux, a port seen in the flows alone and generic-brand PCs or servers never become a rule: they tell who made the board or which system runs, not which device it is.',
  'Verifica della base':
    'Database check',
  'impronta DHCP del sistema operativo (Windows, Linux): uguale su ogni PC con quella scheda':
    'DHCP fingerprint of the operating system (Windows, Linux): the same on every PC with that board',
  'impronta DHCP di Windows o Linux, non del prodotto':
    'DHCP fingerprint of Windows or Linux, not of the product',
  'impronta DHCP su una scheda o un PC di un OEM':
    'DHCP fingerprint on an OEM’s board or PC',
  'impronta del sistema operativo':
    'operating system fingerprint',
  'indirizzo dato «per stessa impronta» su una scheda OEM, che l’inventario non conferma':
    'address given «by the same fingerprint» on an OEM board, not confirmed by the inventory',
  'indirizzo intero valido in tutti i tenant: è il dispositivo di un cliente':
    'whole address valid in every tenant: it is a customer’s device',
  'l’inventario dell’ospedale lo elenca come un altro modello':
    'the hospital’s inventory lists it as another model',
  'nome dato da un ospedale ai suoi dispositivi, non dal costruttore':
    'name a hospital gives its devices, not the maker’s',
  'parola comune, non il nome di un prodotto':
    'common word, not a product name',
  'porta generica nei flussi':
    'generic port in the flows',
  'porta vista nei flussi di un solo ospedale: da sola non distingue il modello':
    'port seen in the flows of a single hospital: on its own it does not tell the model',
  'prefisso OEM':
    'OEM prefix',
  'prefisso di chi fa la scheda o il PC ({v}), non del costruttore del dispositivo':
    'prefix of whoever makes the board or the PC ({v}), not of the device’s maker',
  'prefisso di chi fa la scheda o il PC, non del costruttore del dispositivo':
    'prefix of whoever makes the board or the PC, not of the device’s maker',
  '{n} voci verificate, {f} da correggere: {v}.':
    '{n} entries checked, {f} to correct: {v}.',
  '{n} voci verificate.':
    '{n} entries checked.',
  'Agenti che leggono i nomi':
    'Agents reading names',
  'Catalogo ricevuto, lettura in avvio.':
    'Catalogue received, reading starting.',
  'Dai flussi sì: {s}.':
    'Flows do see: {s}.',
  'Dai soli flussi NetFlow non si riconoscono: {s}. Stanno su indirizzi condivisi (Cloudflare, Google, Microsoft, Akamai) con migliaia di altri siti, quindi un flusso verso di loro non dice quale sito. Li riconoscono l’ascolto passivo della sonda (il nome TLS, anche in HTTP/3, e le domande DNS) e l’agente CENSO sulla macchina, che vede i nomi che risolve.':
    'NetFlow flows alone cannot recognise: {s}. They sit on addresses shared (Cloudflare, Google, Microsoft, Akamai) with thousands of other sites, so a flow towards them does not tell which site. The probe’s passive listening (the TLS name, HTTP/3 included, and DNS queries) and the CENSO agent on the machine, which sees the names it resolves, do recognise them.',
  'Legge i nomi che la macchina risolve ({m}): {n} servizi nel catalogo, {r} usi riconosciuti dall’avvio.':
    'Reads the names the machine resolves ({m}): {n} services in the catalogue, {r} uses recognised since start.',
  'Lettura dei nomi non attiva':
    'Name reading not active',
  'Tre fonti, indicate nella colonna «Fonte»: la sonda (SNI in ascolto passivo), l’agente CENSO sulla macchina — che confronta con il catalogo i nomi che la macchina risolve (cache DNS su Windows, risposte DNS su Linux) e manda solo quelli dei servizi AI — e i flussi NetFlow verso gli IP propri di un servizio (imparati dall’SNI o risolti dal DNS del catalogo). ChatGPT, l’API di OpenAI, Copilot e Gemini stanno su indirizzi condivisi (Cloudflare, Google) con migliaia di altri siti: dai soli flussi non si possono distinguere, e per loro servono la sonda con un mirror o l’agente. La scheda Diagnostica dice quali servizi i flussi vedono e quali no, quanti agenti leggono i nomi e quando è arrivato l’ultimo uso.':
    'Three sources, shown in the «Source» column: the probe (SNI in passive listening), the CENSO agent on the machine — which matches the names the machine resolves (DNS cache on Windows, DNS replies on Linux) against the catalogue and sends only those of AI services — and NetFlow flows towards a service’s own IPs (learnt from the SNI or resolved from the catalogue’s DNS). ChatGPT, the OpenAI API, Copilot and Gemini sit on addresses shared (Cloudflare, Google) with thousands of other sites: flows alone cannot tell them apart, and for them the probe with a mirror or the agent is needed. The Diagnostics card tells which services flows see and which not, how many agents read names and when the last use arrived.',
  'agente':
    'agent',
  'cache DNS di Windows':
    'Windows DNS cache',
  'flussi NetFlow':
    'NetFlow flows',
  'risposte DNS':
    'DNS replies',
  'sonda (SNI)':
    'probe (SNI)',
  'Adozione':
    'Adoption',
  'Andamento nel tempo':
    'Over time',
  'Asset alla prima volta':
    'First-time assets',
  'Asset che usano un’AI per la prima volta, giorno per giorno. In tutto {n} asset l’hanno usata almeno una volta.':
    'Assets using an AI for the first time, day by day. In all, {n} assets have used one at least once.',
  'Da dove arriva il dato':
    'Where the data comes from',
  'Giorni con uso':
    'Days with use',
  'I primi quattro servizi hanno una tinta propria, gli altri sono sommati in «altri servizi».':
    'The top four services have their own colour, the others are summed in «other services».',
  'Le statistiche, sempre senza nomi: usi, asset e servizi con il confronto rispetto al periodo precedente della stessa durata; l’andamento per giorno per servizio (i primi quattro, gli altri sommati) o per tipo (chat, codice, immagini, ricerca); la mappa giorno della settimana × ora nell’ora locale, con la quota d’uso fuori orario e nel fine settimana; l’adozione (asset che usano un’AI per la prima volta); da quali fonti arriva il dato; la concentrazione (quanta parte degli usi fa il 10% degli asset più attivi); i servizi nuovi nel periodo e, per ogni servizio, giorni d’uso, volume, prima e ultima volta.':
    'The statistics, always without names: uses, assets and services compared with the previous period of the same length; the daily trend by service (the top four, the others summed) or by type (chat, code, images, search); the weekday × hour map in local time, with the share of use outside working hours and at the weekend; adoption (assets using an AI for the first time); which sources the data comes from; concentration (how much of the use the 10% most active assets make); the new services in the period and, for each service, days of use, volume, first and last time.',
  'Nessun asset nuovo nel periodo.':
    'No new assets in the period.',
  'Nessuna fonte nel periodo.':
    'No source in the period.',
  'Prima volta':
    'First time',
  'Raggruppa per':
    'Group by',
  'Rispetto a prima':
    'Compared with before',
  'Servizi nel dettaglio':
    'Services in detail',
  'Servizi nuovi nel periodo':
    'New services in the period',
  'Ultima volta':
    'Last time',
  'Un uso visto da due fonti conta per entrambe. Il {p}% degli asset più attivi ({n} su {m}) fa il {q}% degli usi.':
    'A use seen by two sources counts for both. The {p}% most active assets ({n} of {m}) make {q}% of the uses.',
  'Usi per giorno':
    'Uses per day',
  'Usi per giorno della settimana e ora, nell’ora locale. Fuori orario (prima delle 8, dalle 19 o nel fine settimana): {f}% · nel fine settimana: {w}% · ora più intensa: {h}.':
    'Uses by weekday and hour, in local time. Outside working hours (before 8, from 19 or at the weekend): {f}% · at the weekend: {w}% · busiest hour: {h}.',
  'Usi per giorno, dal {da} al {a}.':
    'Uses per day, from {da} to {a}.',
  'Uso per giorno della settimana e ora':
    'Use by weekday and hour',
  'altri servizi':
    'other services',
  'meno':
    'less',
  'per servizio':
    'by service',
  'più':
    'more',
  'rispetto al periodo precedente della stessa durata':
    'compared with the previous period of the same length',
  'usi':
    'uses',
  '{v} rispetto al periodo precedente ({p})':
    '{v} vs the previous period ({p})',
  'Alta disponibilità':
    'High availability',
  'Appliance':
    'Appliances',
  'Archiviazione del database':
    'Database storage',
  'Classe del database':
    'Database class',
  'Componenti da aggiornare':
    'Components to update',
  'Contenitore (CDB)':
    'Container (CDB)',
  'DB home':
    'DB home',
  'Data center':
    'Data center',
  "Data center, cluster, host KVM (con modello, seriale e UUID dell'hardware), macchine virtuali (CPU, memoria, sistema ospite, indirizzi dal guest agent, dischi, reti), domini di storage e reti logiche di un engine OLVM, via REST API v4 in sola lettura. Funziona anche con oVirt e Red Hat Virtualization.":
    "Data centers, clusters, KVM hosts (with hardware model, serial and UUID), virtual machines (CPU, memory, guest system, addresses from the guest agent, disks, networks), storage domains and logical networks of an OLVM engine, via REST API v4, read only. Also works with oVirt and Red Hat Virtualization.",
  'Database appliance':
    'Database appliance',
  'Dischi assegnati (GB)':
    'Provisioned disks (GB)',
  'Dischi occupati (GB)':
    'Used disks (GB)',
  'Famiglia CPU del cluster':
    'Cluster CPU family',
  'Forma del database':
    'Database shape',
  'Frequenza CPU (MHz)':
    'CPU frequency (MHz)',
  'Gestito da':
    'Managed by',
  'Id OLVM':
    'OLVM id',
  "L'appliance (modello, versione, edizione, componenti installati e aggiornabili, DB home), i nodi con seriale e UUID dell'hardware, i database (versione, edizione, ruolo, SI/RAC, CDB/PDB, home, storage) e le VM/DB system KVM. Bersagli: gli indirizzi dei nodi (in un HA bastano entrambi, l'appliance resta una). Credenziale SSH di root, o di un utente con sudo senza password per odacli: si eseguono solo comandi di lettura.":
    "The appliance (model, version, edition, installed and updatable components, DB homes), the nodes with hardware serial and UUID, the databases (version, edition, role, SI/RAC, CDB/PDB, home, storage) and the KVM VMs/DB systems. Targets: the node addresses (in an HA pair list both, the appliance stays one). SSH credential of root, or of a user with password-less sudo for odacli: only read commands are run.",
  'Livello di compatibilità':
    'Compatibility level',
  'Modello ODA':
    'ODA model',
  'Nome univoco del database':
    'Database unique name',
  'Oracle Database Appliance (ODA)':
    'Oracle Database Appliance (ODA)',
  'Oracle Linux Virtualization Manager (OLVM)':
    'Oracle Linux Virtualization Manager (OLVM)',
  'PDB':
    'PDB',
  'Percorso della DB home':
    'DB home path',
  'Ruolo del database':
    'Database role',
  'Senza stato':
    'Stateless',
  'Set di caratteri':
    'Character set',
  'Sistema operativo dichiarato':
    'Declared operating system',
  'Spazio promesso alle VM':
    'Space committed to VMs',
  'Tipo di VM':
    'VM type',
  'Tipo di database':
    'Database type',
  'Tipo di dominio':
    'Domain type',
  'Tipo di host':
    'Host type',
  "URL dell'engine («https://olvm.example.com»; «/ovirt-engine/api» si può omettere). Con utente e password l'utente va scritto con il profilo: «admin@internal» o «lettore@dominio». Basta un ruolo in sola lettura sul sistema (ReadOnlyAdmin, o UserRole con visibilità su tutti i data center). Il token è un token SSO dell'engine (Bearer). Vale anche per oVirt e Red Hat Virtualization.":
    "Engine URL («https://olvm.example.com»; «/ovirt-engine/api» can be omitted). With user and password, write the user with its profile: «admin@internal» or «reader@domain». A read-only role on the system is enough (ReadOnlyAdmin, or UserRole with visibility on all data centers). The token is an engine SSO token (Bearer). Also valid for oVirt and Red Hat Virtualization.",
  'Versione ODA':
    'ODA version',
  'Versione libvirt':
    'libvirt version',
  'Crea una credenziale SSH di root (o di un utente con sudo senza password per odacli) e lancia il job «Oracle Database Appliance (ODA)» con gli indirizzi dei nodi come bersagli: in un ODA HA indica entrambi i nodi, l’appliance resta una sola.':
    'Create an SSH credential of root (or of a user with password-less sudo for odacli) and run the «Oracle Database Appliance (ODA)» job with the node addresses as targets: in an HA ODA list both nodes, the appliance stays one.',
  'I sistemi integrati hardware e software, che si comprano e si aggiornano come un oggetto solo. Oggi Oracle Database Appliance (ODA): la sonda entra in SSH sui nodi ed esegue solo comandi di lettura di odacli, e in Configuration Item compaiono l’appliance (modello, versione, edizione, componenti installati e aggiornabili, DB home), i nodi come server con seriale e UUID dell’hardware, i database con versione, edizione, ruolo, tipo (singola istanza o RAC), CDB/PDB e home, e le VM o DB system KVM.':
    'Integrated hardware and software systems, bought and updated as a single object. Today Oracle Database Appliance (ODA): the probe connects over SSH to the nodes and runs only odacli read commands, and Configuration Items show the appliance (model, version, edition, installed and updatable components, DB homes), the nodes as servers with hardware serial and UUID, the databases with version, edition, role, type (single instance or RAC), CDB/PDB and home, and the KVM VMs or DB systems.',
  'Come preparo gli apparati per CENSO con SNMP v3?':
    'How do I prepare devices for CENSO with SNMP v3?',
  'SNMP v3 authPriv (SHA-2 e AES) è il modo consigliato: autentica e cifra. CENSO legge in sola lettura dalla sonda, sulla porta UDP 161.':
    'SNMP v3 authPriv (SHA-2 and AES) is the recommended way: it authenticates and encrypts. CENSO reads read-only from the probe, on UDP port 161.',
  'A) Utente v3 su una vista in sola lettura':
    'A) A v3 user on a read-only view',
  'Crea una vista, un gruppo v3 in lettura su quella vista e un utente con autenticazione e cifratura; limita il gruppo all’indirizzo della sonda con un’ACL.':
    'Create a view, a v3 group with read on that view and a user with authentication and encryption; restrict the group to the probe address with an ACL.',
  'In CENSO scegli gli stessi protocolli: SHA o SHA-256 per l’autenticazione, AES per la cifratura (sui Cisco con «aes 192/256» usa AES192C o AES256C).':
    'In CENSO choose the same protocols: SHA or SHA-256 for authentication, AES for encryption (on Cisco devices with «aes 192/256» use AES192C or AES256C).',
  'B) Il percorso di rete':
    'B) The network path',
  'Dalla sonda agli apparati deve passare UDP 161: verificalo con un firewall in mezzo.':
    'UDP 161 must pass from the probe to the devices: check it when there is a firewall in between.',
  'Come preparo gli apparati per CENSO con SNMP v2c?':
    'How do I prepare devices for CENSO with SNMP v2c?',
  'CENSO legge gli apparati (switch, router, firewall, UPS, iDRAC/iLO, storage) in SNMP in sola lettura dalla sonda, sulla porta UDP 161. Dove l’apparato lo supporta, preferisci SNMP v3 authPriv.':
    'CENSO reads devices (switches, routers, firewalls, UPS, iDRAC/iLO, storage) over SNMP, read-only, from the probe on UDP port 161. Where the device supports it, prefer SNMP v3 authPriv.',
  'A) Una community in sola lettura, limitata alla sonda':
    'A) A read-only community, limited to the probe',
  'Crea una community dedicata in sola lettura (mai «public»), legata a un’ACL che ammette solo l’indirizzo della sonda.':
    'Create a dedicated read-only community (never «public»), bound to an ACL that allows only the probe address.',
  'Non dare mai una community in scrittura: a CENSO basta leggere.':
    'Never give a write community: reading is all CENSO needs.',
  'In CENSO puoi raggruppare più credenziali SNMP: la sonda le prova in ordine finché una risponde.':
    'In CENSO you can group several SNMP credentials: the probe tries them in order until one answers.',
  'Come preparo gli apparati per CENSO con SNMP v1?':
    'How do I prepare devices for CENSO with SNMP v1?',
  'Come preparo Microsoft Azure per CENSO (app in sola lettura)?':
    'How do I prepare Microsoft Azure for CENSO (read-only app)?',
  'CENSO legge le risorse delle sottoscrizioni con un’app registrata in Entra ID (service principal) che ha il ruolo Reader: nessuna scrittura.':
    'CENSO reads the resources of the subscriptions with an app registered in Entra ID (service principal) that has the Reader role: no writes.',
  'A) L’app e il suo segreto':
    'A) The app and its secret',
  'Entra ID → Registrazioni app → Nuova registrazione: un nome (per esempio «censo-inventory»). Annota ID tenant (directory) e ID applicazione (client).':
    'Entra ID → App registrations → New registration: a name (for example «censo-inventory»). Note the tenant (directory) ID and the application (client) ID.',
  'Certificati e segreti: carica un certificato (consigliato: il segreto non lascia mai la sonda) oppure crea un segreto client e copialo subito.':
    'Certificates & secrets: upload a certificate (recommended: the secret never leaves the probe) or create a client secret and copy it at once.',
  'B) I ruoli':
    'B) The roles',
  'Su ogni sottoscrizione da inventariare: Controllo di accesso (IAM) → Aggiungi assegnazione di ruolo → «Reader» all’app.':
    'On each subscription to inventory: Access control (IAM) → Add role assignment → «Reader» to the app.',
  'Per i costi mensili aggiungi «Cost Management Reader»; per l’import avanzato «Log Analytics Reader» sul workspace.':
    'For monthly costs add «Cost Management Reader»; for the advanced import «Log Analytics Reader» on the workspace.',
  'Come preparo Google Cloud per CENSO (service account in sola lettura)?':
    'How do I prepare Google Cloud for CENSO (read-only service account)?',
  'CENSO legge i progetti con un service account che ha il ruolo Viewer. Il modo consigliato è l’impersonazione: la chiave distribuita può solo impersonare, e revocare l’accesso non richiede di ruotarla.':
    'CENSO reads the projects with a service account that has the Viewer role. The recommended way is impersonation: the distributed key can only impersonate, and revoking access does not require rotating it.',
  'A) Il service account':
    'A) The service account',
  'IAM e amministrazione → Service account → Crea: un nome (per esempio «censo-inventory») e il ruolo «Viewer» (roles/viewer) sul progetto o sulla cartella.':
    'IAM & Admin → Service accounts → Create: a name (for example «censo-inventory») and the «Viewer» role (roles/viewer) on the project or folder.',
  'Abilita l’API «IAM Service Account Credentials» nel progetto.':
    'Enable the «IAM Service Account Credentials» API in the project.',
  'B) L’accesso':
    'B) The access',
  'Impersonazione (consigliata): all’identità che CENSO usa concedi «Service Account Token Creator» (roles/iam.serviceAccountTokenCreator) sul service account di inventario, non sul progetto.':
    'Impersonation (recommended): grant the identity CENSO uses «Service Account Token Creator» (roles/iam.serviceAccountTokenCreator) on the inventory service account, not on the project.',
  'In alternativa: Chiavi → Aggiungi chiave → JSON, e incolla qui i campi del file.':
    'Alternatively: Keys → Add key → JSON, and paste the fields of the file here.',
  'Come preparo Oracle Cloud per CENSO (chiave API in sola lettura)?':
    'How do I prepare Oracle Cloud for CENSO (read-only API key)?',
  'CENSO legge la tenancy (o un compartimento) con la chiave API di un utente che sta in un gruppo con policy di sola lettura.':
    'CENSO reads the tenancy (or a compartment) with the API key of a user in a group with read-only policies.',
  'A) Utente, gruppo e policy':
    'A) User, group and policy',
  'Identity → Users: crea l’utente «censo»; Groups: crea «CensoReaders» e mettici l’utente.':
    'Identity → Users: create the user «censo»; Groups: create «CensoReaders» and add the user.',
  'Policies: una policy di sola lettura sul compartimento (o sulla tenancy).':
    'Policies: a read-only policy on the compartment (or the tenancy).',
  'B) La chiave API':
    'B) The API key',
  'Sull’utente: API Keys → Add API Key. Genera la coppia (chiave **senza passphrase**: un processo non presidiato non può digitarla) e annota l’impronta che Oracle mostra.':
    'On the user: API Keys → Add API Key. Generate the pair (key **without passphrase**: an unattended process cannot type it) and note the fingerprint Oracle shows.',
  'Qui servono OCID della tenancy e dell’utente, impronta, regione (identificativo, per esempio «eu-milan-1») e la chiave privata.':
    'Here you need the tenancy and user OCIDs, the fingerprint, the region (identifier, for example «eu-milan-1») and the private key.',
  'Come preparo ntopng per CENSO (utente o token in lettura)?':
    'How do I prepare ntopng for CENSO (read user or token)?',
  'CENSO interroga ntopng in tempo reale attraverso una sonda, sulla porta della console (di norma 3000).':
    'CENSO queries ntopng in real time through a probe, on the console port (usually 3000).',
  'Impostazioni → Gestisci utenti → Aggiungi: un utente non privilegiato dedicato (per esempio «censo»), con le reti che deve vedere.':
    'Settings → Manage users → Add: a dedicated non-privileged user (for example «censo»), with the networks it must see.',
  'Sull’utente genera il token API (consigliato): si revoca da solo, senza toccare la password.':
    'On the user generate the API token (recommended): it can be revoked on its own, without touching the password.',
  'B) La sonda':
    'B) The probe',
  'Scegli qui sotto la sonda che raggiunge ntopng: le richieste escono solo da lì.':
    'Choose below the probe that reaches ntopng: requests leave only from there.',
  'Il certificato della console di solito è interno o autofirmato: incolla la CA o l’impronta SHA-256 nei campi TLS, invece di disattivare la verifica.':
    'The console certificate is usually internal or self-signed: paste the CA or the SHA-256 fingerprint in the TLS fields, instead of turning verification off.',
  'Come preparo Cynet per CENSO (utente API)?':
    'How do I prepare Cynet for CENSO (API user)?',
  'CENSO legge da Cynet la copertura degli endpoint (chi ha l’agente e chi no) e gli allarmi, in sola lettura.':
    'CENSO reads from Cynet the endpoint coverage (who has the agent and who does not) and the alerts, read-only.',
  'A) L’utente API':
    'A) The API user',
  'Console Cynet → Settings → API Management → Create API User, con permessi di sola lettura.':
    'Cynet console → Settings → API Management → Create API User, with read-only permissions.',
  'Copia Access Key e Secret Key (Cynet le mostra una volta sola) e il Client ID (Site ID) del sito.':
    'Copy Access Key and Secret Key (Cynet shows them only once) and the site’s Client ID (Site ID).',
  'B) Da dove si parla':
    'B) Where it is queried from',
  'Cynet in cloud: scegli «Control Plane» in «Da dove si parla». On-premise: la sonda che raggiunge la console.':
    'Cynet in the cloud: choose «Control Plane» in «Where it is spoken from». On-premises: the probe that reaches the console.',
  'Come preparo il Palo Alto per CENSO (amministratore in sola lettura)?':
    'How do I prepare the Palo Alto for CENSO (read-only administrator)?',
  'CENSO interroga il firewall (o Panorama) con la XML API di PAN-OS, attraverso una sonda: legge stato, sessioni, log e configurazione, non cambia nulla.':
    'CENSO queries the firewall (or Panorama) with the PAN-OS XML API, through a probe: it reads status, sessions, logs and configuration, it changes nothing.',
  'A) Il ruolo in sola lettura':
    'A) The read-only role',
  'Device → Admin Roles → Add: un profilo (per esempio «censo-readonly») con la Web UI disattivata e, nella scheda XML API, abilitati Report, Log, Configuration e Operational Requests; nessuna scrittura (Commit disattivato).':
    'Device → Admin Roles → Add: a profile (for example «censo-readonly») with the Web UI disabled and, in the XML API tab, Report, Log, Configuration and Operational Requests enabled; no writes (Commit disabled).',
  'B) L’amministratore e la chiave':
    'B) The administrator and the key',
  'Device → Administrators → Add: un amministratore **locale** (RADIUS/TACACS+ non generano la chiave) con il profilo del passo A.':
    'Device → Administrators → Add: a **local** administrator (RADIUS/TACACS+ do not generate the key) with the profile of step A.',
  'Ottieni la chiave API una volta e incollala qui (consigliato), oppure lascia che la sonda la generi a ogni interrogazione con utente e password.':
    'Get the API key once and paste it here (recommended), or let the probe generate it at every query with user and password.',
  'C) La sonda':
    'C) The probe',
  'Abilita la capability «Firewall» sulla sonda che raggiunge la gestione (HTTPS 443) e sceglila qui sotto.':
    'Enable the «Firewall» capability on the probe that reaches the management interface (HTTPS 443) and choose it below.',
  'Come preparo il WatchGuard Firebox per CENSO (Fireware CLI)?':
    'How do I prepare the WatchGuard Firebox for CENSO (Fireware CLI)?',
  'CENSO legge il Firebox dalla Fireware CLI in SSH sulla porta 4118, attraverso una sonda.':
    'CENSO reads the Firebox from the Fireware CLI over SSH on port 4118, through a probe.',
  'A) L’account':
    'A) The account',
  'Per interrogare basta l’account «status» (Device Monitor, sola lettura): cambiane la password di serie.':
    'To query, the «status» account is enough (Device Monitor, read-only): change its default password.',
  'Solo se vuoi anche il backup della configurazione serve «admin» o un Device Administrator: l’export non è concesso in sola lettura.':
    'Only if you also want the configuration backup you need «admin» or a Device Administrator: export is not allowed read-only.',
  'B) La policy di gestione':
    'B) The management policy',
  'La policy «WatchGuard» del Firebox deve ammettere l’indirizzo della sonda sulla 4118.':
    'The Firebox «WatchGuard» policy must allow the probe address on 4118.',
  'Abilita la capability «Firewall» sulla sonda e sceglila qui sotto.':
    'Enable the «Firewall» capability on the probe and choose it below.',
  'Come preparo PowerDNS per CENSO (REST API)?':
    'How do I prepare PowerDNS for CENSO (REST API)?',
  'CENSO legge zone e record di PowerDNS Authoritative Server dalla REST API, in sola lettura finché non accendi «Consenti la scrittura».':
    'CENSO reads zones and records of PowerDNS Authoritative Server from the REST API, read-only until you turn on «Allow writing».',
  'A) La REST API':
    'A) The REST API',
  'Nel file di configurazione abilita API e web server, con una chiave lunga e casuale e il solo indirizzo della sonda fra quelli ammessi; poi riavvia PowerDNS.':
    'In the configuration file enable API and web server, with a long random key and only the probe address among the allowed ones; then restart PowerDNS.',
  'La sonda deve raggiungere la porta 8081. Copia qui la stessa «api-key».':
    'The probe must reach port 8081. Copy the same «api-key» here.',
  'Come preparo Veeam Backup & Replication per CENSO (utente Backup Viewer)?':
    'How do I prepare Veeam Backup & Replication for CENSO (Backup Viewer user)?',
  'CENSO legge job, sessioni, repository e macchine protette dalla REST API di VBR (porta 9419), in sola lettura.':
    'CENSO reads jobs, sessions, repositories and protected machines from the VBR REST API (port 9419), read-only.',
  'Crea un’utenza dedicata, di dominio («DOMINIO\\utente») o locale del server VBR («SERVER\\utente»).':
    'Create a dedicated account, domain («DOMINIO\\utente») or local to the VBR server («SERVER\\utente»).',
  'Console Veeam → Users and Roles → Add: assegnale il ruolo «Veeam Backup Viewer».':
    'Veeam console → Users and Roles → Add: give it the «Veeam Backup Viewer» role.',
  'B) La sonda e la versione':
    'B) The probe and the version',
  'La sonda deve raggiungere la 9419 del server VBR.':
    'The probe must reach port 9419 of the VBR server.',
  'Lascia «Automatica» nella versione API: CENSO la sceglie dalla build del server (12.x e 13). Scegline una solo per forzarla.':
    'Leave the API version on «Automatic»: CENSO picks it from the server build (12.x and 13). Choose one only to force it.',
  'Come preparo Vinchin Backup & Recovery per CENSO?':
    'How do I prepare Vinchin Backup & Recovery for CENSO?',
  'CENSO legge job, risorse protette e storage dalla console Vinchin (API sotto /api/ sulla 443).':
    'CENSO reads jobs, protected resources and storage from the Vinchin console (API under /api/ on 443).',
  'Crea un’utenza dedicata che **veda tutte le risorse**: Vinchin mostra a ogni utente solo ciò che gli è assegnato, e un’utenza senza risorse legge zero job senza errori.':
    'Create a dedicated account that **sees all resources**: Vinchin shows each user only what is assigned to it, and an account without resources reads zero jobs without errors.',
  'Meglio se in sola lettura. La chiave API (Sistema → Impostazioni di sistema → API Key Management) è un’alternativa: se la pagina Backup dà errore, torna a utente e password.':
    'Better read-only. The API key (System → System settings → API Key Management) is an alternative: if the Backup page fails, go back to user and password.',
  'La sonda deve raggiungere la console sulla 443.':
    'The probe must reach the console on 443.',
  'Come preparo Commvault per CENSO (utente con ruolo View)?':
    'How do I prepare Commvault for CENSO (user with the View role)?',
  'CENSO legge client, job e storage dall’API del Command Center o del Web Server, in sola lettura.':
    'CENSO reads clients, jobs and storage from the Command Center or Web Server API, read-only.',
  'Command Center → Manage → Security → Users → Add: un’utenza dedicata (di dominio nella forma «DOMINIO\\utente»).':
    'Command Center → Manage → Security → Users → Add: a dedicated account (domain, in the form «DOMINIO\\utente»).',
  'Associazioni: il ruolo «View» sulla CommCell (o sulle entità da vedere).':
    'Associations: the «View» role on the CommCell (or on the entities to see).',
  'La sonda deve raggiungere l’API («https://<server>/commandcenter/api», oppure «…/webconsole/api»).':
    'The probe must reach the API («https://<server>/commandcenter/api», or «…/webconsole/api»).',
  'Come preparo Rubrik per CENSO (service account in sola lettura)?':
    'How do I prepare Rubrik for CENSO (read-only service account)?',
  'CENSO legge da Rubrik Security Cloud (dal Control Plane) o da un cluster CDM (da una sonda) oggetti protetti, SLA e job, in sola lettura.':
    'CENSO reads protected objects, SLAs and jobs from Rubrik Security Cloud (from the Control Plane) or from a CDM cluster (from a probe), read-only.',
  'A) Rubrik Security Cloud':
    'A) Rubrik Security Cloud',
  'Impostazioni → Utenti e accessi → Service account → Aggiungi: un ruolo in sola lettura.':
    'Settings → Users and access → Service accounts → Add: a read-only role.',
  'Scarica il file JSON: qui servono client ID e secret. L’endpoint è «https://<account>.my.rubrik.com».':
    'Download the JSON file: here you need the client ID and secret. The endpoint is «https://<account>.my.rubrik.com».',
  'B) Cluster CDM':
    'B) CDM cluster',
  'Impostazioni → Utenti → Service account sul cluster, con un ruolo in sola lettura; in alternativa un’utenza locale o di dominio in sola lettura.':
    'Settings → Users → Service accounts on the cluster, with a read-only role; alternatively a read-only local or domain account.',
  'La sonda deve raggiungere l’indirizzo del cluster sulla 443.':
    'The probe must reach the cluster address on 443.',
  'Come preparo phpIPAM per CENSO (API app)?':
    'How do I prepare phpIPAM for CENSO (API app)?',
  'Come preparo il DNS Microsoft per CENSO (WinRM)?':
    'How do I prepare Microsoft DNS for CENSO (WinRM)?',
  'CENSO legge zone e record del DNS di Windows con PowerShell via WinRM sul server DNS (di norma un domain controller).':
    'CENSO reads zones and records of Windows DNS with PowerShell over WinRM on the DNS server (usually a domain controller).',
  'A) WinRM sul server DNS':
    'A) WinRM on the DNS server',
  'Attiva WinRM con il listener HTTPS (5986) e una regola firewall che ammetta la sonda; la guida «WinRM HTTPS» nella documentazione ha i comandi.':
    'Enable WinRM with the HTTPS listener (5986) and a firewall rule that allows the probe; the «WinRM HTTPS» guide in the documentation has the commands.',
  'B) L’account':
    'B) The account',
  'Un account di dominio dedicato, membro di «Remote Management Users» sul server e con la lettura delle zone DNS (in molte installazioni significa «DnsAdmins»).':
    'A dedicated domain account, member of «Remote Management Users» on the server and with read on the DNS zones (in many installations this means «DnsAdmins»).',
  'Per la scrittura (solo se accendi «Consenti la scrittura») serve la scrittura sulle zone.':
    'For writing (only if you turn on «Allow writing») write access on the zones is needed.',
  'Come preparo la CA Microsoft (AD CS) per CENSO?':
    'How do I prepare the Microsoft CA (AD CS) for CENSO?',
  'CENSO legge le CA aziendali e i modelli da Active Directory e, via WinRM, il database dei certificati emessi; può anche richiedere certificati se lo consenti.':
    'CENSO reads the enterprise CAs and templates from Active Directory and, over WinRM, the database of issued certificates; it can also request certificates if you allow it.',
  'Un account di dominio dedicato, membro di «Remote Management Users» sulla CA.':
    'A dedicated domain account, member of «Remote Management Users» on the CA.',
  'Per elencare i certificati emessi: sulla CA (certsrv.msc → Proprietà → Sicurezza) il permesso di lettura e, dove richiesto, «Rilascio e gestione certificati»; per richiederne, «Registrazione» sui modelli da usare.':
    'To list issued certificates: on the CA (certsrv.msc → Properties → Security) the read permission and, where required, «Issue and Manage Certificates»; to request them, «Enroll» on the templates to use.',
  'B) Il collegamento':
    'B) The connection',
  'WinRM (consigliato): HTTPS 5986 dalla sonda alla CA.':
    'WinRM (recommended): HTTPS 5986 from the probe to the CA.',
  'Web Enrollment: il sito /certsrv della CA; con questa modalità non si legge il database della CA.':
    'Web Enrollment: the /certsrv site of the CA; with this mode the CA database is not read.',
  'Come preparo vCenter per CENSO (utente in sola lettura)?':
    'How do I prepare vCenter for CENSO (read-only user)?',
  'CENSO legge cluster, host, VM, datastore e reti dal vCenter con un’utenza dedicata in sola lettura, sulla porta 443.':
    'CENSO reads clusters, hosts, VMs, datastores and networks from vCenter with a dedicated read-only account, on port 443.',
  'vSphere Client → Amministrazione → Single Sign-On → Utenti e gruppi: crea l’utente (per esempio «censo@vsphere.local») oppure usa un account di dominio dedicato.':
    'vSphere Client → Administration → Single Sign-On → Users and Groups: create the user (for example «censo@vsphere.local») or use a dedicated domain account.',
  'B) Il ruolo':
    'B) The role',
  'Amministrazione → Autorizzazioni globali (o la radice del vCenter) → Aggiungi: l’utente con il ruolo predefinito «Sola lettura» e «Propaga agli oggetti figli» spuntato.':
    'Administration → Global permissions (or the vCenter root) → Add: the user with the built-in «Read-only» role and «Propagate to children» ticked.',
  'La sonda deve raggiungere il vCenter in HTTPS 443.':
    'The probe must reach vCenter over HTTPS 443.',
  'Come preparo Nutanix per CENSO (utente Viewer)?':
    'How do I prepare Nutanix for CENSO (Viewer user)?',
  'CENSO legge cluster, host e VM da Prism Central (porta 9440) in sola lettura.':
    'CENSO reads clusters, hosts and VMs from Prism Central (port 9440), read-only.',
  'Prism Central → Admin Center → IAM (o Gestione utenti locali): crea un’utenza dedicata, locale o da directory.':
    'Prism Central → Admin Center → IAM (or Local user management): create a dedicated account, local or from the directory.',
  'Assegnale il ruolo «Prism Viewer» (o «Viewer»), non uno di amministrazione.':
    'Give it the «Prism Viewer» (or «Viewer») role, not an administration one.',
  'La sonda deve raggiungere Prism Central sulla 9440.':
    'The probe must reach Prism Central on 9440.',
  'Come preparo Sangfor aCloud per CENSO?':
    'How do I prepare Sangfor aCloud for CENSO?',
  'CENSO legge host e macchine virtuali dalla console aCloud, in sola lettura.':
    'CENSO reads hosts and virtual machines from the aCloud console, read-only.',
  'Nella console aCloud, fra gli amministratori, crea un’utenza dedicata con permessi di sola visualizzazione su tutte le risorse da inventariare.':
    'In the aCloud console, among the administrators, create a dedicated account with view-only permissions on all resources to inventory.',
  'Se la versione lo consente, usa un token API invece della password.':
    'If the version allows it, use an API token instead of the password.',
  'La sonda deve raggiungere la console aCloud in HTTPS.':
    'The probe must reach the aCloud console over HTTPS.',
  'Come preparo OLVM per CENSO (utente ReadOnlyAdmin)?':
    'How do I prepare OLVM for CENSO (ReadOnlyAdmin user)?',
  'CENSO legge data center, cluster, host, VM, domini di storage e reti dall’API REST dell’engine OLVM (vale anche per oVirt e Red Hat Virtualization), in sola lettura sulla 443.':
    'CENSO reads data centers, clusters, hosts, VMs, storage domains and networks from the REST API of the OLVM engine (also valid for oVirt and Red Hat Virtualization), read-only on 443.',
  'Sull’engine crea un utente del profilo interno (oppure usa un utente di dominio già collegato all’engine).':
    'On the engine create a user of the internal profile (or use a domain user already linked to the engine).',
  'Nella credenziale l’utente va scritto con il profilo: «censo@internal» o «utente@dominio».':
    'In the credential, write the user with its profile: «censo@internal» or «utente@dominio».',
  'Amministrazione → Configura → Permessi di sistema → Aggiungi: l’utente con il ruolo «ReadOnlyAdmin».':
    'Administration → Configure → System Permissions → Add: the user with the «ReadOnlyAdmin» role.',
  'La sonda deve raggiungere l’engine in HTTPS 443.':
    'The probe must reach the engine over HTTPS 443.',
  'Gli indirizzi IP delle VM arrivano solo se sugli ospiti gira il guest agent (qemu-guest-agent).':
    'VM IP addresses arrive only if the guest agent runs on the guests (qemu-guest-agent).',
  'Come preparo Proxmox VE per CENSO (token PVEAuditor)?':
    'How do I prepare Proxmox VE for CENSO (PVEAuditor token)?',
  'CENSO legge nodi, VM, container, storage e reti di un nodo o del cluster (porta 8006) in sola lettura.':
    'CENSO reads nodes, VMs, containers, storage and networks of a node or of the cluster (port 8006), read-only.',
  'A) Utente e token':
    'A) User and token',
  'Crea un utente dedicato e un token API; assegna il ruolo «PVEAuditor» su «/» con propagazione **al token**: con la separazione dei privilegi il token non eredita i diritti dell’utente, e senza il ruolo Proxmox risponde con meno dati invece di dare errore.':
    'Create a dedicated user and an API token; give the «PVEAuditor» role on «/» with propagation **to the token**: with privilege separation the token does not inherit the user’s rights, and without the role Proxmox answers with less data instead of an error.',
  'Nel campo token serve la forma completa «utente@realm!nome=segreto».':
    'The token field needs the full form «user@realm!name=secret».',
  'La sonda deve raggiungere la 8006 di un nodo.':
    'The probe must reach port 8006 of a node.',
  'Come preparo ZStack per CENSO (AccessKey in sola lettura)?':
    'How do I prepare ZStack for CENSO (read-only AccessKey)?',
  'CENSO legge host, macchine virtuali, storage e reti di ZStack dalle API del management node, in sola lettura.':
    'CENSO reads hosts, virtual machines, storage and networks of ZStack from the management node APIs, read-only.',
  'A) L’account e la chiave':
    'A) The account and the key',
  'Crea (o scegli) un account o sub-account dedicato con i soli permessi di lettura sulle risorse da inventariare.':
    'Create (or choose) a dedicated account or sub-account with read-only permissions on the resources to inventory.',
  'Per quell’account genera un’AccessKey (consigliata): il segreto firma le richieste e non viaggia mai. Copia AccessKey ID e segreto nei campi qui sotto.':
    'For that account generate an AccessKey (recommended): the secret signs the requests and never travels. Copy AccessKey ID and secret into the fields below.',
  'B) L’indirizzo giusto':
    'B) The right address',
  'L’endpoint è quello delle API del management node, di norma «http://indirizzo:8080», non quello della console web (che risponde in HTTPS su un’altra porta).':
    'The endpoint is the management node API one, usually «http://address:8080», not the web console one (which answers over HTTPS on another port).',
  'La sonda deve raggiungere quella porta.':
    'The probe must reach that port.',
  'Come preparo NexaVM Cloud per CENSO (AccessKey in sola lettura)?':
    'How do I prepare NexaVM Cloud for CENSO (read-only AccessKey)?',
  'CENSO legge host, macchine virtuali, storage e reti di NexaVM Cloud dalle API del management node, in sola lettura.':
    'CENSO reads hosts, virtual machines, storage and networks of NexaVM Cloud from the management node APIs, read-only.',
  'Come preparo NexaSphere per CENSO (AccessKey in sola lettura)?':
    'How do I prepare NexaSphere for CENSO (read-only AccessKey)?',
  'CENSO legge host, macchine virtuali, storage e reti di NexaSphere dalle API del management node, in sola lettura.':
    'CENSO reads hosts, virtual machines, storage and networks of NexaSphere from the management node APIs, read-only.',
  'Come preparo OpenStack per CENSO (application credential reader)?':
    'How do I prepare OpenStack for CENSO (reader application credential)?',
  'CENSO legge istanze, volumi, reti, IP e bilanciatori via Keystone v3 e il catalogo dei servizi, in sola lettura.':
    'CENSO reads instances, volumes, networks, IPs and load balancers via Keystone v3 and the service catalogue, read-only.',
  'A) L’application credential':
    'A) The application credential',
  'Con un utente che ha il ruolo «reader» sul progetto crea un’application credential con il solo ruolo reader: è revocabile da sola e non porta la password dell’utente.':
    'With a user that has the «reader» role on the project, create an application credential with the reader role only: it can be revoked on its own and does not carry the user’s password.',
  'Per tutti i progetti e per gli hypervisor serve un ruolo di amministratore (o «reader» di sistema): accendi allora «Tutti i progetti».':
    'For all projects and for the hypervisors an administrator role (or system «reader») is needed: then turn on «All projects».',
  'La sonda deve raggiungere Keystone e gli endpoint del catalogo (Nova, Neutron, Cinder, Octavia) dell’interfaccia scelta (public, internal o admin).':
    'The probe must reach Keystone and the catalogue endpoints (Nova, Neutron, Cinder, Octavia) of the chosen interface (public, internal or admin).',
  'Come preparo Apache CloudStack per CENSO (chiavi API)?':
    'How do I prepare Apache CloudStack for CENSO (API keys)?',
  'CENSO legge zone, cluster, host, VM, volumi e reti con richieste firmate, in sola lettura.':
    'CENSO reads zones, clusters, hosts, VMs, volumes and networks with signed requests, read-only.',
  'A) L’utente e le chiavi':
    'A) The user and the keys',
  'Crea un utente dedicato con il ruolo «Read-Only Admin» (CloudStack 4.15+) per leggere tutta l’installazione, o un ruolo utente per il solo account.':
    'Create a dedicated user with the «Read-Only Admin» role (CloudStack 4.15+) to read the whole installation, or a user role for its account only.',
  'Accounts → utente → Generate keys: copia chiave API e chiave segreta (la segreta firma le richieste e non viaggia).':
    'Accounts → user → Generate keys: copy the API key and the secret key (the secret signs the requests and does not travel).',
  'La sonda deve raggiungere il management server («…/client/api»).':
    'The probe must reach the management server («…/client/api»).',
  'Come preparo Kubernetes per CENSO (service account in sola lettura)?':
    'How do I prepare Kubernetes for CENSO (read-only service account)?',
  'CENSO legge nodi, workload, servizi e ingress dall’API server (di norma 6443) con un service account e un ClusterRole in sola lettura.':
    'CENSO reads nodes, workloads, services and ingresses from the API server (usually 6443) with a service account and a read-only ClusterRole.',
  'A) Service account e permessi':
    'A) Service account and permissions',
  'Crea il service account, un ClusterRole con «get» e «list» sulle risorse lette e il ClusterRoleBinding.':
    'Create the service account, a ClusterRole with «get» and «list» on the resources read, and the ClusterRoleBinding.',
  'Copia qui il token e il certificato della CA del cluster (dal kubeconfig, «certificate-authority-data» decodificato).':
    'Copy here the token and the cluster CA certificate (from the kubeconfig, «certificate-authority-data» decoded).',
  'La sonda deve raggiungere l’API server (l’indirizzo «server:» del kubeconfig).':
    'The probe must reach the API server (the «server:» address of the kubeconfig).',
  'Come preparo Huawei OceanStor per CENSO (utente in sola lettura)?':
    'How do I prepare Huawei OceanStor for CENSO (read-only user)?',
  'CENSO legge array, pool, LUN, host e porte dalla REST del DeviceManager (porta 8088), in sola lettura.':
    'CENSO reads arrays, pools, LUNs, hosts and ports from the DeviceManager REST (port 8088), read-only.',
  'DeviceManager → Impostazioni → Utenti e sicurezza → Crea: un’utenza locale dedicata con ruolo di sola lettura (monitor), mai quella di amministrazione.':
    'DeviceManager → Settings → Users and security → Create: a dedicated local account with a read-only (monitor) role, never the administration one.',
  'La sonda deve raggiungere l’indirizzo di gestione dell’array sulla 8088.':
    'The probe must reach the array management address on 8088.',
  'Come preparo i sistemi per CENSO con SSH (utente dedicato)?':
    'How do I prepare systems for CENSO with SSH (dedicated user)?',
  'Con SSH CENSO legge server Linux/Unix, apparati di rete (backup della configurazione, moduli, SFP), host KVM e Oracle Database Appliance. Usa un utente dedicato alla discovery, mai quello con cui gli amministratori entrano a mano.':
    'With SSH CENSO reads Linux/Unix servers, network devices (configuration backup, modules, SFPs), KVM hosts and Oracle Database Appliance. Use a user dedicated to discovery, never the one administrators log in with by hand.',
  'A) Server Linux e Unix':
    'A) Linux and Unix servers',
  'Crea l’utente «censo» e mettigli la chiave pubblica generata per CENSO (la guida «Come creo una chiave SSH?» qui accanto spiega come).':
    'Create the user «censo» and give it the public key generated for CENSO (the «How do I create an SSH key?» guide next to it explains how).',
  'Senza root l’inventario funziona: ciò che richiede root (seriali, moduli di memoria) compare nel rapporto come lacuna dichiarata. Per averlo basta sudo per il solo dmidecode.':
    'Without root the inventory works: what needs root (serials, memory modules) shows in the report as a declared gap. To get it, sudo for dmidecode alone is enough.',
  'B) Apparati di rete':
    'B) Network devices',
  'Un account locale o AAA che arrivi direttamente al livello che esegue i comandi di lettura («show running-config», «show inventory», «display current-configuration»): CENSO non manda mai «enable». Su Cisco: privilege level 15 o un livello personalizzato con quei comandi.':
    'A local or AAA account that lands directly at the level that runs the read commands («show running-config», «show inventory», «display current-configuration»): CENSO never sends «enable». On Cisco: privilege level 15 or a custom level with those commands.',
  'Dove la piattaforma lo consente, limita l’account all’indirizzo della sonda.':
    'Where the platform allows it, restrict the account to the probe address.',
  'C) Host KVM e Oracle Database Appliance':
    'C) KVM hosts and Oracle Database Appliance',
  'KVM senza piattaforma: l’utente nel gruppo «libvirt» basta; in alternativa root o sudo senza password per «virsh». La sonda esegue solo comandi di lettura.':
    'KVM without a platform: the user in the «libvirt» group is enough; alternatively root or password-less sudo for «virsh». The probe runs only read commands.',
  'Oracle Database Appliance: «odacli» vuole root. Usa root o un utente con sudo senza password per odacli; la sonda esegue solo «describe-…» e «list-…», mai comandi che cambiano qualcosa.':
    'Oracle Database Appliance: «odacli» wants root. Use root or a user with password-less sudo for odacli; the probe runs only «describe-…» and «list-…», never commands that change anything.',
  'D) La rete':
    'D) The network',
  'Dalla sonda ai sistemi deve passare TCP 22 (o la porta SSH indicata nel job).':
    'TCP 22 (or the SSH port set in the job) must pass from the probe to the systems.',
  'Come preparo Windows per CENSO (WinRM)?':
    'How do I prepare Windows for CENSO (WinRM)?',
  'CENSO inventaria server e postazioni Windows con PowerShell via WinRM, di norma in HTTPS sulla 5986 e NTLM. Le macchine senza WinRM possono usare l’agente d’inventario.':
    'CENSO inventories Windows servers and workstations with PowerShell over WinRM, usually HTTPS on 5986 with NTLM. Machines without WinRM can use the inventory agent.',
  'A) WinRM sugli host (o da GPO)':
    'A) WinRM on the hosts (or by GPO)',
  'Attiva WinRM con il listener HTTPS (serve un certificato di autenticazione server) e una regola firewall che ammetta solo la sonda.':
    'Enable WinRM with the HTTPS listener (a server-authentication certificate is needed) and a firewall rule that allows only the probe.',
  'Il minimo: un account di dominio dedicato, membro di «Remote Management Users» e con «Abilita account» e «Abilitazione remota» sullo spazio WMI root\\cimv2 (wmimgmt.msc → Controllo WMI → Sicurezza). Ciò che non si legge compare nel rapporto.':
    'The minimum: a dedicated domain account, member of «Remote Management Users» and with «Enable Account» and «Remote Enable» on the WMI namespace root\\cimv2 (wmimgmt.msc → WMI Control → Security). What cannot be read shows in the report.',
  'L’inventario più completo: amministratore locale sugli host.':
    'The most complete inventory: local administrator on the hosts.',
  'Come preparo Active Directory per CENSO (LDAP)?':
    'How do I prepare Active Directory for CENSO (LDAP)?',
  'CENSO legge da un domain controller computer, sistemi operativi e struttura del dominio: basta un utente normale, mai un account amministrativo.':
    'CENSO reads computers, operating systems and domain structure from a domain controller: a normal user is enough, never an administrative account.',
  'Un utente di dominio dedicato: l’appartenenza a «Domain Users» basta per leggere gli oggetti computer.':
    'A dedicated domain user: membership of «Domain Users» is enough to read computer objects.',
  'Nella credenziale «DOMINIO\\utente» o «utente@dominio».':
    'In the credential «DOMINIO\\utente» or «utente@dominio».',
  'LDAPS (636) consigliato: il domain controller deve avere un certificato. In alternativa LDAP 389 con StartTLS; il bind semplice solo su TLS.':
    'LDAPS (636) recommended: the domain controller must have a certificate. Alternatively LDAP 389 with StartTLS; simple bind only over TLS.',
  'Come preparo PostgreSQL per CENSO (ruolo di sola lettura)?':
    'How do I prepare PostgreSQL for CENSO (read-only role)?',
  'CENSO legge solo versione, elenco e dimensione dei database dai cataloghi di sistema: mai i dati nelle tabelle.':
    'CENSO reads only version, list and size of the databases from the system catalogues: never the data in the tables.',
  'A) Il ruolo':
    'A) The role',
  'Crea un ruolo dedicato; «pg_read_all_stats» dà le dimensioni anche dei database a cui non si connette.':
    'Create a dedicated role; «pg_read_all_stats» gives the sizes of the databases it does not connect to as well.',
  'In pg_hba.conf ammetti la sonda; la porta di default è 5432 (indicala nel bersaglio se è un’altra).':
    'In pg_hba.conf allow the probe; the default port is 5432 (set it in the target if it is another).',
  'Come preparo Oracle Database per CENSO (utente di sola lettura)?':
    'How do I prepare Oracle Database for CENSO (read-only user)?',
  'CENSO legge solo versione, PDB e loro dimensione dalle viste di sistema: mai i dati.':
    'CENSO reads only version, PDBs and their size from the system views: never the data.',
  'A) L’utente comune (multitenant)':
    'A) The common user (multitenant)',
  'Crea un utente comune con sessione e catalogo di sistema; CONTAINER_DATA serve a vedere le dimensioni dei PDB.':
    'Create a common user with session and system catalogue; CONTAINER_DATA is needed to see the PDB sizes.',
  'La sonda deve raggiungere il listener (di norma 1521); il «service name» è quello del CDB.':
    'The probe must reach the listener (usually 1521); the «service name» is the CDB one.',
  'Come preparo SQL Server per CENSO (login di sola lettura)?':
    'How do I prepare SQL Server for CENSO (read-only login)?',
  'CENSO legge solo versione, elenco e dimensione dei database dalle viste di sistema: mai i dati.':
    'CENSO reads only version, list and size of the databases from the system views: never the data.',
  'A) Il login':
    'A) The login',
  'Un login SQL dedicato; VIEW ANY DEFINITION serve alle dimensioni (sys.master_files).':
    'A dedicated SQL login; VIEW ANY DEFINITION is needed for the sizes (sys.master_files).',
  'L’autenticazione SQL deve essere attiva; la sonda deve raggiungere la 1433 (o la porta dell’istanza). Le istanze con nome si scrivono «SERVER\\ISTANZA».':
    'SQL authentication must be enabled; the probe must reach 1433 (or the instance port). Named instances are written «SERVER\\ISTANZA».',
  'Come preparo MySQL o MariaDB per CENSO (utente di sola lettura)?':
    'How do I prepare MySQL or MariaDB for CENSO (read-only user)?',
  'CENSO legge solo versione, schemi e loro dimensione da information_schema: mai i dati.':
    'CENSO reads only version, schemas and their size from information_schema: never the data.',
  'Un utente dedicato, legato all’indirizzo della sonda, che veda l’elenco dei database.':
    'A dedicated user, bound to the probe address, that sees the list of databases.',
  'Le dimensioni vengono da information_schema.tables, che mostra solo le tabelle su cui l’utente ha un privilegio: senza, le dimensioni restano a zero.':
    'Sizes come from information_schema.tables, which shows only the tables the user has some privilege on: without it, sizes stay at zero.',
  'La sonda deve raggiungere la 3306 (o la porta indicata nel bersaglio).':
    'The probe must reach 3306 (or the port set in the target).',
  'Come ottengo la chiave Fingerbank per CENSO?':
    'How do I get the Fingerbank key for CENSO?',
  'Fingerbank riconosce i dispositivi dall’impronta DHCP e dal nome: CENSO gli manda solo impronte, mai dati dei clienti.':
    'Fingerbank recognises devices from the DHCP fingerprint and the name: CENSO sends it only fingerprints, never customer data.',
  'A) La chiave':
    'A) The key',
  'Crea un account su fingerbank.org e copia la chiave dal profilo (API key).':
    'Create an account on fingerbank.org and copy the key from the profile (API key).',
  'Il piano gratuito ha un limite di richieste: CENSO memorizza le risposte e non richiede due volte la stessa impronta.':
    'The free plan has a request limit: CENSO stores the answers and does not ask twice for the same fingerprint.',
  'Come ottengo la chiave Anthropic per l’assistente?':
    'How do I get the Anthropic key for the assistant?',
  'L’assistente usa Claude con una chiave API del tuo account Anthropic; la chiave resta cifrata nel tenant.':
    'The assistant uses Claude with an API key of your Anthropic account; the key stays encrypted in the tenant.',
  'console.anthropic.com → API keys → Create key, in un workspace dedicato a CENSO.':
    'console.anthropic.com → API keys → Create key, in a workspace dedicated to CENSO.',
  'Nel workspace puoi fissare un limite di spesa mensile.':
    'In the workspace you can set a monthly spend limit.',
  'Come collego un modello interno per l’assistente?':
    'How do I connect an internal model for the assistant?',
  'L’assistente può usare un modello ospitato in casa che parla l’API OpenAI (/v1/chat/completions): vLLM, Ollama, LM Studio, LocalAI.':
    'The assistant can use an in-house model that speaks the OpenAI API (/v1/chat/completions): vLLM, Ollama, LM Studio, LocalAI.',
  'A) Il servizio':
    'A) The service',
  'Esponi il servizio sulla rete (Ollama: variabile OLLAMA_HOST=0.0.0.0, porta 11434; vLLM: opzione --api-key per chiedere una chiave).':
    'Expose the service on the network (Ollama: variable OLLAMA_HOST=0.0.0.0, port 11434; vLLM: option --api-key to require a key).',
  'Il modello deve supportare le chiamate agli strumenti (tool calling).':
    'The model must support tool calls (tool calling).',
  'Se il servizio sta nella rete del cliente, scegli la sonda che lo raggiunge in «Da dove si parla».':
    'If the service is in the customer network, choose the probe that reaches it in «Where it is spoken from».',
  'Ogni tipo di credenziale ha accanto la sua guida «Come preparo … per CENSO?»: quale utente creare sul sistema, con quale ruolo in sola lettura, da quale porta deve passare la sonda, con i comandi pronti da copiare dove servono.':
    'Every credential type has its own «How do I prepare … for CENSO?» guide next to it: which user to create on the system, with which read-only role, which port the probe must pass through, with commands ready to copy where needed.',
  'Al':
    'To',
  'Il periodo può essere al massimo di 366 giorni.':
    'The period can be at most 366 days.',
  'Il periodo si sceglie fra 7, 30, 90 giorni e un anno, oppure dal… al…; il confronto è sempre con il periodo precedente della stessa durata. L’export PDF comincia con una pagina di grafici (gli stessi della pagina, con la mappa giorno × ora nel fuso di chi lo scarica) e prosegue con le tabelle; l’Excel ha le stesse tabelle, un foglio per ciascuna.':
    'The period is chosen among 7, 30, 90 days and one year, or from… to…; the comparison is always with the previous period of the same length. The PDF export starts with a page of charts (the same as the page, with the weekday × hour map in the time zone of whoever downloads it) and continues with the tables; the Excel file has the same tables, one sheet each.',
  'Indica la data d’inizio e quella di fine.':
    'Set the start date and the end date.',
  'L’inizio viene dopo la fine.':
    'The start comes after the end.',
  'Personalizzato…':
    'Custom…',
  'Un anno':
    'One year',
  'IP AI tenuti dal filtro flussi':
    'AI IPs kept by the flow filter',
  'Il filtro dei flussi tiene sempre il traffico verso questi {n} indirizzi di servizi AI, anche con il perimetro «entrambi interni»: le sonde li ricevono con la configurazione dei flussi, così il volume e il riconoscimento dai flussi non si perdono.':
    'The flow filter always keeps traffic towards these {n} AI service addresses, even with the «both internal» perimeter: probes receive them with the flow configuration, so volume and flow-based recognition are not lost.',
  'La sonda legge il nome anche quando il ClientHello è spezzato su più segmenti, in HTTP/3 (QUIC) e in IPv6, e dalle domande DNS dei client: è l’unico modo quando il browser cifra il nome (Encrypted ClientHello, che Chrome e Firefox usano verso Cloudflare). Un uso si conta al più una volta al minuto per asset e servizio («minuti d’uso»), come per l’agente. Il filtro dei flussi tiene sempre il traffico verso gli indirizzi dei servizi AI, anche con il perimetro «entrambi interni».':
    'The probe reads the name even when the ClientHello is split across several segments, over HTTP/3 (QUIC) and IPv6, and from clients’ DNS queries: it is the only way when the browser encrypts the name (Encrypted ClientHello, which Chrome and Firefox use towards Cloudflare). A use is counted at most once a minute per asset and service («minutes of use»), as for the agent. The flow filter always keeps traffic towards AI service addresses, even with the «both internal» perimeter.',
  'Automatica (consigliata)':
    'Automatic (recommended)',
  '{v} (valore salvato)':
    '{v} (saved value)',
  '1.3-rev1 · VBR 13':
    '1.3-rev1 · VBR 13',
  '1.3-rev0 · VBR 13':
    '1.3-rev0 · VBR 13',
  '1.2-rev1 · VBR 12.3':
    '1.2-rev1 · VBR 12.3',
  '1.2-rev0 · VBR 12.2':
    '1.2-rev0 · VBR 12.2',
  '1.1-rev2 · VBR 12.1':
    '1.1-rev2 · VBR 12.1',
  '1.1-rev1 · VBR 12.0':
    '1.1-rev1 · VBR 12.0',
  '1.1-rev0 · VBR 12.0':
    '1.1-rev0 · VBR 12.0',
  '{n} minuti d’uso riconosciuti':
    '{n} minutes of use recognised',
  'Nomi letti dal filo':
    'Names read from the wire',
  'TLS {a} · QUIC {b} · DNS {c}':
    'TLS {a} · QUIC {b} · DNS {c}',
  '{n} con il nome cifrato (ECH)':
    '{n} with the name encrypted (ECH)',
  'Il lettore AI non vede nessun nome sul filo: il mirror non porta il traffico dei client verso Internet né le loro domande DNS. Controlla che la porta mirror copi entrambe le direzioni del collegamento verso il firewall (o del core), non solo un segmento di server.':
    'The AI reader sees no names on the wire: the mirror does not carry the clients’ traffic towards the Internet nor their DNS queries. Check that the mirror port copies both directions of the link towards the firewall (or of the core), not just a server segment.',
  '{n} nomi chiesti da indirizzi fuori dalle reti interne sono stati scartati. Se la rete usa indirizzi pubblici propri, o il mirror sta dopo il NAT, aggiungili alle reti interne in Impostazioni → Traffico da conservare.':
    '{n} names requested by addresses outside the internal networks were discarded. If the network uses its own public addresses, or the mirror sits after the NAT, add them to the internal networks in Settings → Traffic to keep.',
  'Nella scheda della sonda, «Nomi letti dal filo» dice quanti nomi la sonda legge (TLS, QUIC, DNS) prima del confronto con il catalogo: a zero, il mirror non porta il traffico dei client verso Internet. I client sono gli indirizzi privati e quelli nelle reti interne di «Traffico da conservare»: chi ha indirizzi pubblici propri, o un mirror dopo il NAT, li aggiunge lì.':
    'In the probe card, «Names read from the wire» says how many names the probe reads (TLS, QUIC, DNS) before matching them against the catalogue: at zero, the mirror does not carry the clients’ traffic towards the Internet. Clients are the private addresses and those in the internal networks of «Traffic to keep»: a network with its own public addresses, or a mirror after the NAT, adds them there.',
  'Inventario delle zone DNS e dei relativi record acquisiti dai server PowerDNS e Microsoft DNS. I dati sono riferiti all’ultima sincronizzazione disponibile e si aggiornano automaticamente in sottofondo.':
    'Inventory of the DNS zones and their records read from the PowerDNS and Microsoft DNS servers. The data refer to the last available sync and update automatically in the background.',
  'Scegli una zona':
    'Choose a zone',
  'Seleziona una zona DNS dall’elenco a sinistra: le zone dirette sono in alto, le zone inverse (PTR) in basso. In alternativa, usa la ricerca per trovare un nome DNS o un indirizzo IP in tutte le zone configurate.':
    'Select a DNS zone from the list on the left: forward zones are at the top, reverse zones (PTR) at the bottom. Otherwise, use the search to find a DNS name or an IP address in all the configured zones.',
  'Nuovo record':
    'New record',
  'Filtra le zone':
    'Filter zones',
  'Dirette':
    'Forward',
  'Inverse':
    'Reverse',
  'in pausa':
    'paused',
  'integrata in AD ({replica})':
    'AD-integrated ({replica})',
  'aggiornamento dinamico: {modo}':
    'dynamic update: {modo}',
  'serial {n}':
    'serial {n}',
  'Il CI della CMDB con questo indirizzo':
    'The CMDB CI with this address',
  'Mostra i record del nome a cui punta':
    'Show the records of the name it points to',
  'vai':
    'go',
  '{n} secondi':
    '{n} seconds',
  'Registrato':
    'Registered',
  'vecchio':
    'stale',
  'Filtra per nome, valore o CI…':
    'Filter by name, value or CI…',
  'Filtra i record':
    'Filter records',
  'Registrazione':
    'Registration',
  'Statici e dinamici':
    'Static and dynamic',
  'Solo dinamici (registrati dai client)':
    'Dynamic only (registered by clients)',
  'Solo statici':
    'Static only',
  'Dinamici vecchi: oltre 30 giorni ({n})':
    'Stale dynamic: over 30 days ({n})',
  'Mostra SOA e NS della zona':
    'Show the zone’s SOA and NS',
  'record':
    'records',
  'Nessun record con questi filtri.':
    'No records with these filters.',
  'Cerca un nome o un indirizzo in tutte le zone…':
    'Search a name or an address across all zones…',
  'Cerca in tutte le zone':
    'Search all zones',
  '{n} risultati in {cercate} zone su {totali}':
    '{n} results in {cercate} of {totali} zones',
  'le altre non sono ancora state lette: lo fa il giro in sottofondo':
    'the others have not been read yet: the background pass does it',
  'mostrati i primi':
    'showing the first ones',
  'L’indirizzo non appartiene alla zona inversa {zona}.':
    'The address does not belong to the reverse zone {zona}.',
  'Scrivi il nome (o @ per la zona stessa).':
    'Type the name (or @ for the zone itself).',
  'Passo 1 di 2: il record. Prima di scrivere, CENSO controlla sul server che cosa c’è già.':
    'Step 1 of 2: the record. Before writing, CENSO checks on the server what is already there.',
  'Passo 2 di 2: che cosa succede sul server. Niente è ancora stato scritto.':
    'Step 2 of 2: what happens on the server. Nothing has been written yet.',
  '{tipo}: {descrizione}':
    '{tipo}: {descrizione}',
  'per esempio 10.0.0.5':
    'for example 10.0.0.5',
  'Scrivi l’indirizzo: il nome del PTR nella zona si calcola da solo.':
    'Type the address: the PTR name in the zone is worked out by itself.',
  'Per un SRV il nome è servizio e protocollo, per esempio «_ldap._tcp» o «_sip._udp».':
    'For an SRV the name is service and protocol, for example «_ldap._tcp» or «_sip._udp».',
  'Indirizzo IPv4':
    'IPv4 address',
  'Indirizzo IPv6':
    'IPv6 address',
  'Peso':
    'Weight',
  'Alias di':
    'Alias of',
  'Server di posta':
    'Mail server',
  'Server dei nomi':
    'Name server',
  'Senza virgolette: le mette il server se servono.':
    'Without quotes: the server adds them if needed.',
  'Altro…':
    'Other…',
  'Secondi':
    'Seconds',
  'Controllo sul server…':
    'Checking on the server…',
  'Continua':
    'Continue',
  '{nome} ha già altri record ({tipi}): un CNAME deve essere l’unico record del suo nome. Togli prima gli altri, o scegli un altro nome.':
    '{nome} already has other records ({tipi}): a CNAME must be the only record of its name. Remove the others first, or pick another name.',
  '{nome} è un alias (CNAME): non può avere altri record. Scrivi il record sul nome a cui punta, o togli prima il CNAME.':
    '{nome} is an alias (CNAME): it cannot have other records. Write the record on the name it points to, or remove the CNAME first.',
  '{nome} ha già un record {tipo} con questo valore.':
    '{nome} already has a {tipo} record with this value.',
  'Il record {nome} {tipo} {valore} esiste già: non c’è niente da aggiungere.':
    'The record {nome} {tipo} {valore} already exists: there is nothing to add.',
  '{nome} ha già {n} record {tipo}':
    '{nome} already has {n} {tipo} records',
  'Aggiorna: sostituisci con {valore}':
    'Update: replace with {valore}',
  'Aggiungi accanto (il nome avrà {n} valori)':
    'Add alongside (the name will have {n} values)',
  'Il vecchio indirizzo':
    'The old address',
  'Togli il PTR di {ip} (punta a questo nome, nella zona {zona})':
    'Remove the PTR of {ip} (it points to this name, in zone {zona})',
  'Scrivi sul DNS':
    'Write to the DNS',
  'Il PTR di {ip}':
    'The PTR of {ip}',
  'Punta già a {nome}: niente da fare.':
    'It already points to {nome}: nothing to do.',
  'Il server non ha una zona inversa per {ip}: il PTR non si può creare qui.':
    'The server has no reverse zone for {ip}: the PTR cannot be created here.',
  'Non riesco a leggere il PTR ({motivo}): lo lascio com’è.':
    'I cannot read the PTR ({motivo}): I leave it as it is.',
  'Crea il PTR {ip} → {nome} nella zona {zona}':
    'Create the PTR {ip} → {nome} in zone {zona}',
  'Oggi punta a: {valori}':
    'Today it points to: {valori}',
  'Aggiorna il PTR: {ip} → {nome}':
    'Update the PTR: {ip} → {nome}',
  'Aggiungi un secondo PTR, lasciando quello di oggi':
    'Add a second PTR, keeping today’s one',
  'Lascia il PTR com’è':
    'Leave the PTR as it is',
  'PTR di {ip} non scritto: {motivo}':
    'PTR of {ip} not written: {motivo}',
  'tolto il PTR di {ip}':
    'PTR of {ip} removed',
  'PTR {ip} scritto in {zona}':
    'PTR {ip} written in {zona}',
  'Record modificato: {nome} {tipo} {valore}':
    'Record changed: {nome} {tipo} {valore}',
  'Record aggiunto: {nome} {tipo} {valore}':
    'Record added: {nome} {tipo} {valore}',
  'sostituiti: {valori}':
    'replaced: {valori}',
  'Record tolto: {nome} {tipo} {valore}':
    'Record removed: {nome} {tipo} {valore}',
  'Togli il record':
    'Remove the record',
  'Dalla zona {zona}. Non si annulla: per rimetterlo va riscritto.':
    'From zone {zona}. It cannot be undone: to put it back it must be written again.',
  'Controllo il PTR sul server…':
    'Checking the PTR on the server…',
  'Togli anche il PTR di {ip}, che punta a questo nome (zona {zona})':
    'Also remove the PTR of {ip}, which points to this name (zone {zona})',
  'Il PTR di {ip} punta a un altro nome: resta com’è.':
    'The PTR of {ip} points to another name: it stays as it is.',
  'Nome → indirizzo IPv4':
    'Name → IPv4 address',
  'Nome → indirizzo IPv6':
    'Name → IPv6 address',
  'Alias di un altro nome':
    'Alias of another name',
  'Server di posta del dominio':
    'The domain’s mail server',
  'Server dei nomi (delega)':
    'Name server (delegation)',
  'Indirizzo → nome (zona inversa)':
    'Address → name (reverse zone)',
  'Servizio: priorità, peso, porta e destinazione':
    'Service: priority, weight, port and target',
  'Testo (SPF, verifiche di dominio…)':
    'Text (SPF, domain verifications…)',
  '{n} h':
    '{n} h',
  'Il nome contiene caratteri che il DNS non accetta.':
    'The name contains characters the DNS does not accept.',
  'Il TTL deve essere un numero di secondi.':
    'The TTL must be a number of seconds.',
  'Serve un indirizzo IPv4, per esempio 10.0.0.5.':
    'An IPv4 address is needed, for example 10.0.0.5.',
  'Serve un indirizzo IPv6, per esempio 2001:db8::5.':
    'An IPv6 address is needed, for example 2001:db8::5.',
  'Serve un nome host, per esempio server1.esempio.it.':
    'A host name is needed, for example server1.esempio.it.',
  'La priorità è un numero da 0 a 65535.':
    'The priority is a number from 0 to 65535.',
  'Serve il nome del server di posta.':
    'The mail server name is needed.',
  'Priorità, peso e porta sono numeri da 0 a 65535.':
    'Priority, weight and port are numbers from 0 to 65535.',
  'Serve il nome host di destinazione.':
    'The target host name is needed.',
  'Il testo è vuoto.':
    'The text is empty.',
  'Il testo deve stare su una riga sola.':
    'The text must fit on a single line.',
  'La pagina si apre con l’ultima lettura (la data è accanto) e «Aggiorna» rilegge in sottofondo; un giro ogni mezz’ora tiene fresche tutte le zone, e la ricerca in alto trova un nome o un indirizzo in tutte. A sinistra le zone, dirette e inverse; a destra i record della zona scelta, con i filtri per tipo, i record dinamici vecchi (oltre 30 giorni) segnati e l’indirizzo che porta al CI della CMDB. Se la connessione consente la scrittura, «Nuovo record» guida in due passi: il record, con i campi del suo tipo e l’anteprima, poi che cosa succede sul server — se il nome ha già un valore (aggiornarlo o aggiungere accanto), i conflitti (un CNAME vuole il nome tutto per sé) e il PTR dell’indirizzo, da creare o aggiornare; modificando o togliendo un A si toglie anche il PTR vecchio.':
    'The page opens with the last read (the date is next to it) and «Aggiorna» reads again in the background; a pass every half hour keeps every zone fresh, and the search at the top finds a name or an address in all of them. Zones on the left, forward and reverse; on the right the records of the chosen zone, with filters by type, stale dynamic records (over 30 days) marked, and the address leading to the CMDB CI. When the connection allows writing, «Nuovo record» guides in two steps: the record, with the fields of its type and a preview, then what happens on the server — whether the name already has a value (update it or add alongside), the conflicts (a CNAME wants the name to itself) and the PTR of the address, to create or update; changing or removing an A also removes the old PTR.',
  'server1.esempio.it':
    'server1.example.com',
  'v=spf1 include:esempio.it -all':
    'v=spf1 include:example.com -all',
  'Le connessioni DNS si configurano in Sorgenti dati.':
    'DNS connections are set up in Data sources.',
  'Filtra per nome o rete…':
    'Filter by name or network…',
  '{da}–{a} di {n}':
    '{da}–{a} of {n}',
  'informativa':
    'informational',
  'aperto':
    'open',
  'in lavorazione':
    'in progress',
  'chiuso':
    'closed',
  'Allarmi ({n})':
    'Alerts ({n})',
  'Vulnerabilità ({n})':
    'Vulnerabilities ({n})',
  'isolati':
    'isolated',
  'Allarmi aperti':
    'Open alerts',
  '{n} host×CVE':
    '{n} host×CVE',
  'asset':
    'assets',
  'Gli allarmi di Cynet degli ultimi 30 giorni (i più recenti, fino a 500 di {n}); quelli aperti diventano reperti sul CI.':
    'Cynet alerts of the last 30 days (the most recent, up to 500 of {n}); the open ones become findings on the CI.',
  'Solo aperti o in lavorazione':
    'Open or in progress only',
  'allarmi':
    'alerts',
  'Nessun allarme aperto.':
    'No open alerts.',
  'Nessun allarme.':
    'No alerts.',
  'Allarme':
    'Alert',
  'Le vulnerabilità che Cynet trova sugli endpoint (modulo ESPM), raggruppate per CVE, le più gravi prima: apri una riga per vedere gli host colpiti. Diventano reperti sui CI.':
    'The vulnerabilities Cynet finds on endpoints (ESPM module), grouped by CVE, the most severe first: open a row to see the affected hosts. They become findings on the CIs.',
  'Nessuna vulnerabilità aperta (o il Site GUID non è configurato: senza, Cynet non dà le vulnerabilità).':
    'No open vulnerabilities (or the Site GUID is not configured: without it, Cynet gives no vulnerabilities).',
  'offline':
    'offline',
  'isolato':
    'isolated',
  'Ultimo utente':
    'Last user',
  'Host in Cynet':
    'Hosts in Cynet',
  'Gli endpoint con l’agente Cynet, con quello che Cynet sa di loro e il CI corrispondente in CENSO. Gruppo, dominio e ultimo utente compaiono se la console li fornisce.':
    'The endpoints with the Cynet agent, with what Cynet knows of them and the matching CI in CENSO. Group, domain and last user appear when the console provides them.',
  'Nessun host.':
    'No hosts.',
  'La pagina si apre con l’ultima lettura e si aggiorna in sottofondo; è divisa in sezioni: la panoramica con la copertura, gli allarmi recenti (gravità, tipo, utente, file, stato, con il CI dell’host), le vulnerabilità raggruppate per CVE con gli host colpiti, e tutti gli host di Cynet con agente, ultima scansione, gruppo, dominio, ultimo utente e isolamento quando la console li fornisce.':
    'The page opens with the last read and refreshes in the background; it is split into sections: the overview with the coverage, the recent alerts (severity, type, user, file, status, with the host’s CI), the vulnerabilities grouped by CVE with the affected hosts, and every Cynet host with agent, last scan, group, domain, last user and isolation when the console provides them.',

  // Patching: fleet, patch catalog, jobs, guided job (patch_jobs_service).
  'Agente da aggiornare':
    'Agent needs updating',
  'Agente non in linea':
    'Agent offline',
  'Aggiornabile':
    'Patchable',
  'Aggiornamenti di sicurezza':
    'Security updates',
  'Aggiornamenti di sicurezza mancanti':
    'Missing security updates',
  'Al riavvio':
    'At reboot',
  'Anche quelli non di sicurezza (correzioni, nuove versioni minori). Da preferire in collaudo prima della produzione.':
    'Non-security ones too (fixes, new minor versions). Best tried in test before production.',
  'Ancora mancante':
    'Still missing',
  'Ancora mancanti dopo l’installazione: {p}':
    'Still missing after the install: {p}',
  'Annulla l’intervento':
    'Cancel the job',
  'Annullare l’intervento? Ciò che non è ancora partito non partirà.':
    'Cancel the job? What has not started yet will not start.',
  'Annullata':
    'Canceled',
  'Approva e avvia':
    'Approve and start',
  'Approva il rollback':
    'Approve the rollback',
  'Approvata':
    'Approved',
  'Approvato da {u} il {d}':
    'Approved by {u} on {d}',
  'Avanzamento':
    'Progress',
  'Bloccata':
    'Blocked',
  'Bloccato':
    'Blocked',
  'Calcolo cosa succederà su ogni macchina…':
    'Working out what will happen on each machine…',
  'Chiesto':
    'Requested',
  'Completato con errori':
    'Completed with errors',
  'Completato, da riavviare':
    'Completed, reboot needed',
  'Cosa farà':
    'What it will do',
  'Cosa installare?':
    'What to install?',
  'Cosa installerebbe (prova) ({n})':
    'What it would install (dry run) ({n})',
  'Cosa è cambiato ({n})':
    'What changed ({n})',
  'Crea da approvare':
    'Create for approval',
  'Crea e approva':
    'Create and approve',
  'Crea l’intervento da approvare: niente parte prima.':
    'Creates the job for approval: nothing starts before.',
  'Da aggiornare':
    'To patch',
  'Da approvare':
    'To approve',
  'Da approvare: lo fa un amministratore del tenant. Intanto «Prova prima» mostra cosa installerebbe ogni macchina.':
    'To approve: a tenant administrator does it. Meanwhile «Try first» shows what each machine would install.',
  'Da installare per prime':
    'Install these first',
  'Da riavviare':
    'Reboot needed',
  'Data e ora':
    'Date and time',
  'Durata (minuti)':
    'Duration (minutes)',
  'Eliminare la finestra «{n}»?':
    'Delete the window «{n}»?',
  'Entro SLA':
    'Within SLA',
  'Entro i tempi (SLA)':
    'On time (SLA)',
  'Esempio: «Sabato notte», sabato dalle 22:00 per 4 ore.':
    'Example: «Saturday night», Saturday from 22:00 for 4 hours.',
  'Filtro':
    'Filter',
  'Finestre':
    'Windows',
  'Finita':
    'Finished',
  'Fuori SLA':
    'Past SLA',
  'Fuso':
    'Time zone',
  'Giorni e orari in cui un intervento programmato «in finestra» può partire. Fuori dalla finestra aspetta la prossima apertura.':
    'Days and hours in which a job scheduled «in a window» may start. Outside the window it waits for the next opening.',
  'Il riavvio è programmato: si completa quando la macchina riparte.':
    'The reboot is scheduled: it completes when the machine starts again.',
  'Imposta il tag dalla scheda della macchina: l’approvazione lo pretende.':
    'Set the tag from the machine’s page: approval requires it.',
  'In coda':
    'Queued',
  'In installazione':
    'Installing',
  'In una data e ora':
    'At a date and time',
  'In una finestra di manutenzione':
    'In a maintenance window',
  'Iniziata':
    'Started',
  'Installa le patch scelte':
    'Install the chosen patches',
  'Installa le {n} scelte':
    'Install the {n} chosen',
  'Installa l’agente dalla scheda della macchina: il patching passa da lì.':
    'Install the agent from the machine’s page: patching goes through it.',
  'Installa tutte le patch di sicurezza':
    'Install all security patches',
  'Installa {p}':
    'Install {p}',
  'Installata, ancora mancante':
    'Installed, still missing',
  'Installata: si completa al riavvio, che non è stato concesso.':
    'Installed: it completes at the reboot, which was not allowed.',
  'Installazione in corso':
    'Install in progress',
  'Installazioni singole':
    'Single installs',
  'Interventi':
    'Jobs',
  'Interventi in corso':
    'Jobs in progress',
  'Intervento':
    'Job',
  'KB scelte':
    'Chosen KBs',
  'La prova dice: niente da installare, la macchina è già aggiornata.':
    'The dry run says: nothing to install, the machine is up to date.',
  'Le bloccate restano nell’intervento: sistemato il motivo (agente, tag), «Riprova le bloccate» le fa partire. Agente spento o da aggiornare non blocca: parte appena può.':
    'Blocked machines stay in the job: once the reason is fixed (agent, tag), «Retry the blocked» starts them. An agent that is off or needs updating does not block: it starts as soon as it can.',
  'Le macchine compaiono qui quando hanno l’agente CENSO o quando la valutazione di sicurezza trova patch mancanti. Installa l’agente dalla scheda di una macchina, o da Inventario → Agenti.':
    'Machines appear here once they have the CENSO agent or the security assessment finds missing patches. Install the agent from a machine’s page, or from Inventory → Agents.',
  'Le patch mancanti di tutte le macchine e la loro installazione tramite l’agente CENSO: scegli cosa, dove e quando, un amministratore approva, e dopo l’installazione CENSO verifica che le patch non risultino più mancanti. Nessun riavvio senza il tuo consenso.':
    'The missing patches of every machine and their installation through the CENSO agent: choose what, where and when, an administrator approves, and after the install CENSO verifies the patches are no longer missing. No reboot without your consent.',
  'L’agente si aggiorna da solo; poi l’intervento parte.':
    'The agent updates itself; then the job starts.',
  'L’agente sta installando.':
    'The agent is installing.',
  'L’andamento si costruisce da oggi: un punto al giorno.':
    'The trend builds up from today: one point a day.',
  'L’intervento parte appena l’agente torna in linea.':
    'The job starts as soon as the agent is back online.',
  'Macchine':
    'Machines',
  'Macchine a cui manca ({n})':
    'Machines missing it ({n})',
  'Macchine aggiornate':
    'Machines up to date',
  'Macchine con il tag «{t}»':
    'Machines with the tag «{t}»',
  'Macchine fuori SLA':
    'Machines past SLA',
  'Manca il tag obbligatorio':
    'Required tag missing',
  'Manca il tag {k}':
    'Tag {k} missing',
  'Mostra il log completo':
    'Show the full log',
  'Nascondi il log':
    'Hide the log',
  'Nessun intervento in corso.':
    'No job in progress.',
  'Nessun intervento.':
    'No jobs.',
  'Nessun inventario arrivato dopo l’installazione: verifica non fatta':
    'No inventory arrived after the install: not verified',
  'Nessuna delle patch scelte manca qui':
    'None of the chosen patches is missing here',
  'Nessuna finestra: creala nella sezione «Finestre».':
    'No window: create one in the «Windows» section.',
  'Nessuna installazione prima dell’approvazione.':
    'Nothing is installed before approval.',
  'Nessuna installazione su questa macchina.':
    'No installs on this machine.',
  'Nessuna macchina con questo filtro.':
    'No machine with this filter.',
  'Nessuna macchina da aggiornare, per ora.':
    'No machines to patch, for now.',
  'Nessuna macchina.':
    'No machines.',
  'Nessuna patch con questo filtro.':
    'No patch with this filter.',
  'Nessuna patch mancante trovata su questa macchina.':
    'No missing patch found on this machine.',
  'Nessuna patch mancante.':
    'No missing patches.',
  'Nome (facoltativo)':
    'Name (optional)',
  'Non aggiornabili':
    'Not patchable',
  'Non pronte':
    'Not ready',
  'Non riavviare':
    'Do not reboot',
  'Non riceve niente da questo intervento.':
    'Gets nothing from this job.',
  'Non riuscita':
    'Failed',
  'Non verificabile qui: manca il motore di valutazione':
    'Cannot be verified here: the assessment engine is missing',
  'Non verificata':
    'Not verified',
  'Nuovo intervento':
    'New job',
  'Nuovo intervento su queste':
    'New job on these',
  'Ogni macchina dice cosa installerebbe, senza toccare niente.':
    'Each machine says what it would install, touching nothing.',
  'Ogni macchina parte al primo contatto dell’agente (circa un minuto); quelle spente partono quando tornano.':
    'Each machine starts at the agent’s next contact (about a minute); machines that are off start when they come back.',
  'Pacchetti Linux':
    'Linux packages',
  'Pacchetti scelti':
    'Chosen packages',
  'Pacchetto Linux':
    'Linux package',
  'Parte al prossimo contatto dell’agente (circa un minuto).':
    'Starts at the agent’s next contact (about a minute).',
  'Parte all’apertura della finestra di manutenzione.':
    'Starts when the maintenance window opens.',
  'Parte dopo l’aggiornamento dell’agente.':
    'Starts after the agent update.',
  'Parte il {d}':
    'Starts on {d}',
  'Parte quando l’agente torna in linea.':
    'Starts when the agent is back online.',
  'Parte solo dentro la finestra (giorni e orari), ogni volta che si apre finché non ha finito.':
    'Starts only inside the window (days and hours), each time it opens until it is done.',
  'Patch':
    'Patches',
  'Patch di sistema mancanti ({n})':
    'Missing system patches ({n})',
  'Patch mancanti':
    'Missing patches',
  'Patch scelte':
    'Chosen patches',
  'Per ambiente':
    'By environment',
  'Per tag (es. ambiente)':
    'By tag (e.g. environment)',
  'Preavviso':
    'Notice',
  'Programmata':
    'Scheduled',
  'Programmato':
    'Scheduled',
  'Pronta':
    'Ready',
  'Proposte dalla scheda di una macchina, fuori da un intervento.':
    'Proposed from a machine’s page, outside a job.',
  'Prova prima':
    'Try first',
  'Quando?':
    'When?',
  'Riavvia':
    'Reboot',
  'Riavvia le macchine che aspettano un riavvio per completare un’installazione, con preavviso agli utenti collegati.':
    'Reboots the machines waiting for a reboot to complete an install, warning the logged-in users first.',
  'Riavvia se serve':
    'Reboot if needed',
  'Riavviata':
    'Rebooted',
  'Riavvio':
    'Reboot',
  'Riprova le bloccate':
    'Retry the blocked',
  'Riprova le non riuscite':
    'Retry the failed',
  'Rollback eseguito':
    'Rolled back',
  'Scegli':
    'Choose',
  'Scelgo le macchine':
    'I choose the machines',
  'Se una macchina deve riavviarsi resta «Da riavviare»; il riavvio lo fai tu, o con un intervento «Solo riavvio».':
    'A machine that needs a reboot stays «Reboot needed»; you reboot it, or with a «Reboot only» job.',
  'Senza agente CENSO':
    'No CENSO agent',
  'Serve un inventario dall’interno (agente o WinRM/SSH) che dica Windows o Linux.':
    'An inventory from the inside (agent or WinRM/SSH) must say Windows or Linux.',
  'Sfruttata':
    'Exploited',
  'Sfruttata attivamente: è nel catalogo CISA KEV delle vulnerabilità usate negli attacchi.':
    'Actively exploited: it is in the CISA KEV catalogue of vulnerabilities used in attacks.',
  'Si completa al riavvio':
    'Completes at reboot',
  'Sistema operativo non riconosciuto':
    'Operating system not recognised',
  'Software installato da aggiornare ({n})':
    'Installed software to update ({n})',
  'Sola lettura: per installare serve il permesso di scrittura sul modulo «Patching».':
    'Read only: installing needs write permission on the «Patching» module.',
  'Solo le macchine che lo chiedono, con un messaggio agli utenti collegati e un preavviso.':
    'Only the machines that ask for it, with a message to the logged-in users and a notice period.',
  'Solo riavvio':
    'Reboot only',
  'Su Windows gli aggiornamenti di sicurezza da Windows Update o dal WSUS dell’azienda; su Linux i pacchetti con correzioni di sicurezza (apt, dnf, yum, zypper, apk). È la scelta di ogni mese.':
    'On Windows the security updates from Windows Update or the company’s WSUS; on Linux the packages with security fixes (apt, dnf, yum, zypper, apk). The monthly choice.',
  'Su quali macchine?':
    'On which machines?',
  'Subito dopo l’approvazione':
    'Right after approval',
  'Tempi di installazione (SLA): critiche entro {c} giorni, alte {a}, medie {m}, basse {b}. Una patch oltre i tempi conta «fuori SLA».':
    'Install deadlines (SLA): critical within {c} days, high {a}, medium {m}, low {b}. A patch past its deadline counts as «past SLA».',
  'Tutte le macchine con questo tag al momento della creazione.':
    'Every machine with this tag when the job is created.',
  'Tutte le macchine e gli interventi in Patching':
    'All machines and jobs in Patching',
  'Tutte le patch':
    'All patches',
  'Tutti gli aggiornamenti':
    'All updates',
  'Tutti gli aggiornamenti disponibili':
    'All available updates',
  'Tutti gli ambienti':
    'All environments',
  'Tutti gli interventi':
    'All jobs',
  'Tutti i tipi':
    'All kinds',
  'Ultima installazione':
    'Last install',
  'Un intervento installa le patch su più macchine insieme: si sceglie cosa, dove e quando, un amministratore lo approva, e CENSO verifica che le patch siano davvero installate.':
    'A job installs patches on many machines at once: you choose what, where and when, an administrator approves it, and CENSO verifies the patches are really installed.',
  'Una installazione alla volta: questa parte dopo.':
    'One install at a time: this one starts afterwards.',
  'Una riga per patch, con le macchine a cui manca. La priorità unisce gravità, sfruttamento attivo (CISA KEV), tempi superati e numero di macchine. Le vulnerabilità di applicazioni e librerie restano in SBOM: si chiudono aggiornando l’applicazione.':
    'One row per patch, with the machines missing it. Priority combines severity, active exploitation (CISA KEV), missed deadlines and number of machines. Application and library vulnerabilities stay in SBOM: they are closed by updating the application.',
  'Una volta sola, dall’ora indicata.':
    'Once only, from the given time.',
  'Urgente':
    'Urgent',
  'Urgenti':
    'Urgent',
  'Verifica in corso':
    'Verifying',
  'Verifica: in attesa dell’inventario dopo l’installazione':
    'Verification: waiting for the inventory after the install',
  'Verificata':
    'Verified',
  'Verificata: le patch non risultano più mancanti':
    'Verified: the patches are no longer missing',
  'Verificate':
    'Verified',
  'agente da aggiornare':
    'agent needs updating',
  'agente non in linea':
    'agent offline',
  'agente sentito {d}':
    'agent heard {d}',
  'bloccate finché non si sistema':
    'blocked until fixed',
  'da riavviare':
    'reboot needed',
  'es. Aggiornamenti di ottobre — collaudo':
    'e.g. October updates — test',
  'es. Sabato notte':
    'e.g. Saturday night',
  'es. {k}:collaudo':
    'e.g. {k}:test',
  'escluse':
    'left out',
  'gravità {s}, vista per la prima volta il {d}, tempi: {n} giorni':
    'severity {s}, first seen on {d}, deadline: {n} days',
  'il {d}':
    'on {d}',
  'installate':
    'installed',
  'installazione in corso: dettagli':
    'install in progress: details',
  'macchine':
    'machines',
  'macchine fuori SLA':
    'machines past SLA',
  'minuti':
    'minutes',
  'nella finestra «{w}»':
    'in the window «{w}»',
  'nessun riavvio: lo segnala soltanto':
    'no reboot: only reported',
  'non cambiato':
    'unchanged',
  'non riuscite':
    'failed',
  'oltre SLA':
    'past SLA',
  'partiranno':
    'will start',
  'patch':
    'patches',
  'patch mancanti':
    'missing patches',
  'patch per vulnerabilità usate negli attacchi (CISA KEV)':
    'patches for vulnerabilities used in attacks (CISA KEV)',
  'prova chiesta, in attesa dell’agente':
    'dry run requested, waiting for the agent',
  'riavvio se serve':
    'reboot if needed',
  'riavvio se serve, con {n} minuti di preavviso':
    'reboot if needed, with {n} minutes’ notice',
  'scegli la finestra…':
    'choose the window…',
  'senza «{k}»':
    'without «{k}»',
  'sicurezza':
    'security',
  'subito dopo l’approvazione':
    'right after approval',
  'versione corretta {v}':
    'fixed version {v}',
  '{a} senza agente, {g} senza tag obbligatorio':
    '{a} without an agent, {g} without the required tag',
  '{f} non riuscite, {r} da riavviare (ultimi 30 giorni)':
    '{f} failed, {r} to reboot (last 30 days)',
  '{n} KB':
    '{n} KBs',
  '{n} macchine con patch oltre i tempi':
    '{n} machines with patches past their deadline',
  '{n} macchine escluse':
    '{n} machines left out',
  '{n} macchine scelte':
    '{n} machines chosen',
  '{n} patch scelte, su {m} macchine':
    '{n} patches chosen, on {m} machines',
  '{n} scelte. Le macchine non pronte si possono scegliere: il riepilogo dirà cosa manca.':
    '{n} chosen. Machines that are not ready can be chosen: the summary will say what is missing.',
  '{n} software':
    '{n} software',
  '{n} su {m}':
    '{n} of {m}',
  '{n} su {m} senza patch mancanti':
    '{n} of {m} with no missing patches',
  '{n} urgenti':
    '{n} urgent',
  '«Nuovo intervento» sceglie cosa installare, su quali macchine e quando.':
    '«New job» chooses what to install, on which machines and when.',
  '1 macchina esclusa':
    '1 machine left out',
  'Dopo l’installazione l’agente manda subito un nuovo inventario e CENSO rivaluta la macchina: «Verificata» vuol dire che le patch non risultano più mancanti, «Ancora mancante» le elenca. Una macchina «Da riavviare» si completa da sola quando riparte; dal dettaglio di un’installazione applicata c’è il «Rollback».':
    'After the install the agent sends a new inventory straight away and CENSO re-assesses the machine: «Verified» means the patches are no longer missing, «Still missing» lists them. A machine marked «Reboot needed» completes on its own when it starts again; an applied install has «Rollback» in its details.',
  'Installa le scelte':
    'Install the chosen',
  'Per installare le patch: «Nuovo intervento» (in Patching, dalla riga di una patch o dalla scheda di una macchina) chiede cosa — gli aggiornamenti di sicurezza, tutti gli aggiornamenti, le patch scelte o solo un riavvio —, dove — macchine scelte o un tag come «ambiente:collaudo» —, quando — subito, in una finestra o a una data — e se riavviare, con quanti minuti di preavviso agli utenti. L’ultimo passo dice macchina per macchina cosa farà, e perché una macchina resterà fuori.':
    'To install patches: «New job» (in Patching, from a patch’s row or from a machine’s page) asks what — the security updates, all updates, the chosen patches or only a reboot —, where — chosen machines or a tag such as «ambiente:collaudo» —, when — now, in a window or at a date — and whether to reboot, with how many minutes’ notice to the users. The last step says machine by machine what will happen, and why a machine will be left out.',
  'Su Windows gli aggiornamenti arrivano da Windows Update o dal WSUS dell’azienda, scaricati e installati dalla macchina stessa: nessun file passa da CENSO e nessun PowerShell viene avviato. Su Linux si usa il gestore della distribuzione (apt, dnf, yum, zypper, apk) senza domande interattive, dopo aver aggiornato gli elenchi. Le vulnerabilità di applicazioni e librerie non sono qui: stanno in SBOM/Security health e si chiudono aggiornando l’applicazione. Serve l’add-on «Patching» e l’agente sulla macchina.':
    'On Windows the updates come from Windows Update or the company’s WSUS, downloaded and installed by the machine itself: no file goes through CENSO and no PowerShell is started. On Linux the distribution’s manager is used (apt, dnf, yum, zypper, apk) with no interactive questions, after refreshing the lists. Application and library vulnerabilities are not here: they are in SBOM/Security health and are closed by updating the application. It needs the «Patching» add-on and the agent on the machine.',
  'Un amministratore approva («Crea e approva», o «Approva e avvia» sull’intervento); prima si può chiedere «Prova prima», che fa dire a ogni macchina cosa installerebbe senza toccare niente. Una macchina bloccata (tag mancante, agente assente) resta nell’intervento con il motivo: sistemato, «Riprova le bloccate» la fa partire; «Riprova le non riuscite» rilancia quelle andate male.':
    'An administrator approves («Create and approve», or «Approve and start» on the job); before that, «Try first» has each machine say what it would install without touching anything. A blocked machine (missing tag, no agent) stays in the job with the reason: once fixed, «Retry the blocked» starts it; «Retry the failed» runs the ones that went wrong again.',
  'il gestore delle patch del parco, sull’agente CENSO di ogni macchina. «Panoramica»: quante macchine sono aggiornate ed entro i tempi (SLA: per difetto critiche entro 15 giorni, alte 30, medie 90, basse 180; in «Politica SLA» si cambiano, anche per ambiente — la produzione più stretta del collaudo — e con un tetto per le patch sfruttate attivamente, CISA KEV), per ambiente, le patch da installare per prime, gli interventi in corso e l’andamento giorno per giorno. «Macchine»: cosa manca a ogni macchina, da quanto, e se si può aggiornare adesso — senza agente, agente non in linea o da aggiornare, tag obbligatorio mancante, sistema non riconosciuto —, con il rimedio scritto. «Patch»: una riga per KB, pacchetto o programma installato, con le macchine a cui manca, la gravità, lo sfruttamento attivo (CISA KEV), la probabilità di sfruttamento nei prossimi 30 giorni (EPSS), l’età e un rischio da 0 a 100 che mette insieme tutto — gravità, KEV o EPSS, la criticità dei business service della macchina, la produzione, l’esposizione a Internet e lo SLA — con le ragioni scritte nella riga aperta; anche le macchine hanno il loro rischio, quello della patch peggiore. «Interventi»: cosa è stato installato dove, macchina per macchina, con l’esito, cosa è cambiato (da quale versione a quale) e il log completo. «Finestre»: giorni e orari in cui un intervento programmato può partire.':
    'the patch manager of the fleet, through the CENSO agent on each machine. «Overview»: how many machines are up to date and on time (SLA: by default critical within 15 days, high 30, medium 90, low 180; they are changed in «SLA policy», also per environment — production stricter than test — and with a cap for actively exploited patches, CISA KEV), by environment, the patches to install first, the jobs in progress and the day-by-day trend. «Machines»: what each machine is missing, for how long, and whether it can be patched now — no agent, agent offline or needing an update, required tag missing, system not recognised —, with the remedy written out. «Patches»: one row per KB, package or installed program, with the machines missing it, severity, active exploitation (CISA KEV), the probability of exploitation in the next 30 days (EPSS), age and a risk from 0 to 100 that puts it all together — severity, KEV or EPSS, the criticality of the machine’s business services, production, Internet exposure and the SLA — with the reasons written in the opened row; machines have their risk too, that of their worst patch. «Jobs»: what was installed where, machine by machine, with the outcome, what changed (from which version to which) and the full log. «Windows»: the days and hours in which a scheduled job may start.',
  'la più vecchia da {n} giorni':
    'oldest for {n} days',
  '{n} critiche':
    '{n} critical',
  'Età delle patch mancanti':
    'Age of the missing patches',
  'Installazioni mancanti':
    'Missing installs',
  'Installazioni negli ultimi {n} giorni':
    'Installs in the last {n} days',
  'Installazioni riuscite':
    'Successful installs',
  'Le vulnerabilità delle librerie delle applicazioni ({n} componenti) non sono qui: sono nella sezione':
    'The vulnerabilities of the applications’ libraries ({n} components) are not here: they are in the section',
  'Macchine da aggiornare':
    'Machines to patch',
  'Macchine più esposte (patch mancanti)':
    'Most exposed machines (missing patches)',
  'Nel periodo':
    'In the period',
  'Nessuna installazione nel periodo.':
    'No installs in the period.',
  'Nessuna macchina da aggiornare.':
    'No machines to patch.',
  'Nessuna patch li mette in sicurezza: va aggiornato il sistema operativo.':
    'No patch makes them safe: the operating system must be upgraded.',
  'Nessuna.':
    'None.',
  'Nessuno.':
    'None.',
  'Non aggiornabili ({n})':
    'Not patchable ({n})',
  'Oltre i tempi':
    'Past the deadline',
  'Patch da installare per prime (macchine)':
    'Patches to install first (machines)',
  'Patch mancanti per tipo':
    'Missing patches by kind',
  'Per gravità: entro e oltre i tempi':
    'By severity: on time and late',
  'Per sistema':
    'By system',
  'Report completo (Excel)':
    'Full report (Excel)',
  'Report completo (PDF)':
    'Full report (PDF)',
  'Report delle patch':
    'Patch report',
  'Sintesi direzionale (PDF)':
    'Executive summary (PDF)',
  'Sistemi fuori supporto ({n})':
    'End-of-life systems ({n})',
  'Situazione oggi':
    'Today',
  'Tempo medio di installazione':
    'Average install time',
  'entro SLA':
    'within SLA',
  'entro {n} giorni':
    'within {n} days',
  'età mediana {n} giorni':
    'median age {n} days',
  'installazioni':
    'installs',
  'installazioni mancanti':
    'missing installs',
  'macchine da aggiornare':
    'machines to patch',
  'nessuna mancante':
    'none missing',
  'patch non più mancanti dopo l’inventario':
    'patches no longer missing after the inventory',
  'su {n} macchine':
    'on {n} machines',
  '{e} fuori supporto, {r} da riavviare':
    '{e} end of life, {r} to reboot',
  '{n} non riuscite':
    '{n} failed',
  '{n} patch diverse, {u} urgenti':
    '{n} distinct patches, {u} urgent',
  '{n} riavvii':
    '{n} reboots',
  '«Sintesi direzionale»: poche pagine con i numeri, i grafici e la situazione scritta in frasi, per chi decide. «Report completo»: gli stessi grafici e poi tutte le tabelle — macchine, patch, patch per macchina, installazioni e interventi del periodo, macchine non aggiornabili, sistemi fuori supporto, rischi accettati ed eccezioni, tempo di correzione, copertura per macchina, riduzione del rischio, cicli delle politiche e ticket —, anche in Excel.':
    '«Executive summary»: a few pages with the numbers, the charts and the situation written in sentences, for decision makers. «Full report»: the same charts and then every table — machines, patches, patches per machine, installs and jobs of the period, machines that cannot be patched, end-of-life systems, accepted risks and exceptions, time to remediate, coverage per machine, risk reduction, policy cycles and tickets —, also as Excel.',
  'La sezione Sicurezza del CI non elenca le vulnerabilità delle librerie delle applicazioni: stanno nella sua sezione «Software (SBOM)», accanto al componente che le porta, e un rimando dice quante sono.':
    'The CI’s Security section does not list the vulnerabilities of the applications’ libraries: they are in its «Software (SBOM)» section, next to the component that carries them, and a pointer says how many there are.',
  '«Report»: per 30, 90, 180 giorni o un anno, la situazione di oggi (macchine aggiornate ed entro i tempi, installazioni mancanti, sfruttate, oltre i tempi, non aggiornabili) e ciò che è stato fatto nel periodo (installazioni riuscite e non, percentuale di successo, aggiornamenti installati, verificate, tempo medio), con l’andamento, le installazioni giorno per giorno, la gravità entro e oltre i tempi, l’età delle patch mancanti, gli ambienti, i sistemi, le patch da installare per prime e le macchine più esposte. «Sintesi direzionale (PDF)» è il riassunto per chi decide, con i grafici e la situazione scritta in frasi; «Report completo» aggiunge tutte le tabelle (anche patch per macchina, installazioni e interventi del periodo, rischi accettati), in PDF o Excel.':
    '«Report»: for 30, 90, 180 days or a year, today’s situation (machines up to date and on time, missing installs, exploited, past the deadline, not patchable) and what was done in the period (successful and failed installs, success rate, updates installed, verified, average time), with the trend, the installs day by day, severity on time and late, the age of the missing patches, environments, systems, the patches to install first and the most exposed machines. «Executive summary (PDF)» is the summary for decision makers, with the charts and the situation written in sentences; «Full report» adds every table (also patches per machine, installs and jobs of the period, accepted risks), as PDF or Excel.',
  'Aggiorna tutti i programmi':
    'Update all programs',
  'Con cosa l’agente aggiorna i programmi di terze parti su questa macchina.':
    'What the agent updates third-party programs with on this machine.',
  'I programmi di terze parti (Chrome, Firefox, Acrobat Reader, 7-Zip, Zoom…) si aggiornano dallo stesso posto: scegliendoli fra le patch, dalla scheda della macchina o con un intervento «Programmi di terze parti». Su Windows l’agente usa Chocolatey se il programma è suo, altrimenti winget (App Installer), e per Chrome ed Edge anche il loro aggiornatore; la scheda della macchina dice se winget o Chocolatey ci sono. Su Linux i programmi vengono dai repository e si aggiornano come pacchetti. Per i programmi non c’è rollback: winget e Chocolatey non tornano alla versione di prima.':
    'Third-party programs (Chrome, Firefox, Acrobat Reader, 7-Zip, Zoom…) are updated from the same place: by choosing them among the patches, from the machine’s page or with a «Third-party programs» job. On Windows the agent uses Chocolatey when the program is one of its packages, otherwise winget (App Installer), and for Chrome and Edge also their own updater; the machine’s page says whether winget or Chocolatey are there. On Linux programs come from the repositories and are updated as packages. Programs have no rollback: winget and Chocolatey do not go back to the previous version.',
  'Linux: con gli aggiornamenti del sistema':
    'Linux: with the system updates',
  'Nessuna KB, pacchetto o programma mancante da scegliere.':
    'No missing KB, package or program to choose.',
  'Né winget né Chocolatey':
    'Neither winget nor Chocolatey',
  'Programmi con vulnerabilità note (browser, runtime, strumenti): su Windows si aggiornano con winget o Chocolatey della macchina, o con l’aggiornatore del produttore (Chrome, Edge); su Linux dal loro repository.':
    'Programs with known vulnerabilities (browsers, runtimes, tools): on Windows they are updated with the machine’s winget or Chocolatey, or with the vendor’s updater (Chrome, Edge); on Linux from their repository.',
  'Programmi di terze parti':
    'Third-party programs',
  'Programmi scelti':
    'Chosen programs',
  'Si aggiornano solo Chrome ed Edge, con il loro aggiornatore: per gli altri programmi serve App Installer (winget) o Chocolatey sulla macchina.':
    'Only Chrome and Edge are updated, with their own updater: other programs need App Installer (winget) or Chocolatey on the machine.',
  'Solo le KB, i pacchetti o i programmi che scegli qui sotto: su ogni macchina si installa solo quello che le manca.':
    'Only the KBs, packages or programs you choose below: each machine installs only what it is missing.',
  'Su Linux i programmi vengono dai repository: si aggiornano con «Tutti gli aggiornamenti» o scegliendo il pacchetto.':
    'On Linux programs come from the repositories: they are updated with «All updates» or by choosing the package.',
  'Su Windows tutti i programmi che hanno una versione nuova — Chrome, Firefox, Acrobat Reader, 7-Zip, Zoom… — con winget o Chocolatey della macchina, e Chrome ed Edge anche con il loro aggiornatore.':
    'On Windows every program that has a new version — Chrome, Firefox, Acrobat Reader, 7-Zip, Zoom… — with the machine’s winget or Chocolatey, and Chrome and Edge also with their own updater.',
  'chiesto all’aggiornatore del produttore':
    'requested from the vendor’s updater',
  'non offerto da winget né da Chocolatey':
    'not offered by winget or Chocolatey',
  'programmi con {m}':
    'programs with {m}',
  'programmi: né winget né Chocolatey (solo Chrome ed Edge)':
    'programs: neither winget nor Chocolatey (Chrome and Edge only)',
  'È un programma installato: su Windows l’agente lo aggiorna con winget o Chocolatey della macchina (Chrome ed Edge anche con il loro aggiornatore), su Linux dal suo repository. Senza winget né Chocolatey la macchina lo dice nel riepilogo.':
    'It is an installed program: on Windows the agent updates it with the machine’s winget or Chocolatey (Chrome and Edge also with their own updater), on Linux from its repository. Without winget or Chocolatey the machine says so in the summary.',
  'Anche il singolo servizio si esporta, dalla sua pagina: il PDF ha la mappa della composizione disegnata a colonne — client, ingressi, poi ogni salto fino al database e sotto l’infrastruttura — con le porte sulle frecce, leggibile anche stampata, e dopo le tabelle dei componenti e di tutti i collegamenti con porte, traffico e flussi, nella finestra scelta in pagina; l’Excel ha le stesse tabelle.':
    'A single service can be exported too, from its page: the PDF has the composition map drawn in columns — clients, entries, then every hop down to the database and the infrastructure underneath — with the ports on the arrows, readable even printed, followed by the tables of the components and of every connection with ports, traffic and flows, over the window chosen on the page; the Excel has the same tables.',
  'Dalla pagina di una Business App si esporta la sua mappa: i CI che ne eseguono gli elementi, con i programmi, il traffico fra loro nelle ultime 24 ore con le porte, e i Business Service che la usano; poi le tabelle di elementi, collegamenti, funzionalità e servizi.':
    'From a Business App’s page you export its map: the CIs that run its elements, with the programs, the traffic between them over the last 24 hours with the ports, and the Business Services that use it; then the tables of elements, connections, functions and services.',
  'Larghezza del menu':
    'Menu width',
  'Mostra o nascondi il menu (tasto [)':
    'Show or hide the menu (key [)',
  'Trascina per allargare o stringere il menu; doppio clic per la larghezza normale':
    'Drag to widen or narrow the menu; double-click for the normal width',
  'Aggiungi ai preferiti':
    'Add to favourites',
  'Come mostrare il menu':
    'How to show the menu',
  'Espandi il menu':
    'Expand the menu',
  'Espandi il menu (tasto [)':
    'Expand the menu (key [)',
  'In cima al menu tre pulsanti scelgono come mostrarlo: esteso (fisso a sinistra, si allarga o si stringe trascinandone il bordo destro), a icone (una colonna stretta: passando su un’icona si aprono a fianco le pagine della sezione) o nascosto (la pagina usa tutta la larghezza e il menu si apre dal bordo sinistro o con ☰). Il tasto «[» passa da esteso a icone e ritorno. La stellina accanto a una voce la mette fra i Preferiti, in cima al menu e nella colonna a icone. ⌘K (Ctrl+K) apre la ricerca: trova anche le pagine del menu, e a campo vuoto propone preferiti e pagine recenti; ↑↓ e Invio per aprire. Le scelte restano per questo browser.':
    'At the top of the menu three buttons choose how to show it: full (fixed on the left, widened or narrowed by dragging its right edge), icons (a narrow column: hovering an icon opens the section’s pages beside it) or hidden (the page uses the full width and the menu opens from the left edge or with ☰). The «[» key switches between full and icons. The star next to an item puts it among the Favourites, at the top of the menu and in the icon column. ⌘K (Ctrl+K) opens the search: it also finds the menu’s pages, and with an empty field it suggests favourites and recent pages; ↑↓ and Enter to open. The choices are kept for this browser.',
  'Menu a icone':
    'Icon menu',
  'Menu a icone: le voci di una sezione si aprono passandoci sopra':
    'Icon menu: a section’s pages open when you hover over it',
  'Menu esteso':
    'Full menu',
  'Menu esteso, fisso a sinistra (tasto [ per passare alle icone)':
    'Full menu, fixed on the left (key [ to switch to icons)',
  'Menu nascosto':
    'Hidden menu',
  'Menu nascosto: la pagina usa tutta la larghezza, il menu si apre dal bordo sinistro o con ☰':
    'Hidden menu: the page uses the full width, the menu opens from the left edge or with ☰',
  'Pagine':
    'Pages',
  'Pagine recenti':
    'Recent pages',
  'Preferiti':
    'Favourites',
  'Scrivi il nome di una pagina per andarci, o almeno due caratteri per cercare nell’inventario. ↑↓ per scegliere, Invio per aprire.':
    'Type the name of a page to go there, or at least two characters to search the inventory. ↑↓ to choose, Enter to open.',
  'Togli dai preferiti':
    'Remove from favourites',
  'Vai a una pagina, o cerca nomi, IP, MAC, porte, software, seriali…':
    'Go to a page, or search names, IPs, MACs, ports, software, serials…',
  'Negli hit si selezionano più righe per segnarle viste o archiviarle in una volta; «Dettagli» mostra il payload completo e ha gli stessi comandi per la riga.':
    'In the hits you can select several rows to mark them as seen or archive them at once; «Dettagli» shows the full payload and has the same commands for that row.',
  'Scarica le righe filtrate, con le colonne mostrate, in CSV (si apre con Excel)':
    'Download the filtered rows, with the shown columns, as CSV (opens in Excel)',
  'Scegli le colonne da mostrare':
    'Choose the columns to show',
  'Tutte le tabelle si usano allo stesso modo: sopra, «Cerca in tutte le colonne», quante righe ci sono (e quante passano i filtri), «Colonne» per scegliere quali vedere, «Esporta CSV» per scaricare le righe filtrate con le colonne mostrate (si apre con Excel) e «Righe» per quante righe per pagina (25 all’inizio). Sotto ogni titolo c’è il filtro della colonna, e cliccando il titolo si ordina (di nuovo per invertire). Colonne scelte e righe per pagina restano per questo browser, tabella per tabella.':
    'Every table works the same way: above it, «Search all columns», how many rows there are (and how many pass the filters), «Columns» to choose which ones to see, «Export CSV» to download the filtered rows with the shown columns (opens in Excel) and «Rows» for how many rows per page (25 at first). Under each heading there is the column’s filter, and clicking the heading sorts by it (again to reverse). The chosen columns and rows per page are kept for this browser, table by table.',
  'account':
    'accounts',
  'aggiornamenti':
    'updates',
  'amministratori':
    'administrators',
  'apparati':
    'devices',
  'attributi':
    'attributes',
  'avvisi':
    'advisories',
  'azioni':
    'actions',
  'calendari':
    'calendars',
  'campi':
    'fields',
  'classi':
    'classes',
  'collegamenti':
    'links',
  'comunicazioni':
    'communications',
  'connettori':
    'connectors',
  'contatti':
    'contacts',
  'contratti':
    'contracts',
  'controlli':
    'controls',
  'datastore':
    'datastores',
  'dipendenze':
    'dependencies',
  'dispositivi':
    'devices',
  'driver':
    'drivers',
  'elementi':
    'elements',
  'esecuzioni':
    'runs',
  'eventi':
    'events',
  'finestre':
    'windows',
  'firme':
    'signatures',
  'fonti':
    'sources',
  'fornitori':
    'suppliers',
  'funzionalità':
    'functions',
  'gruppi':
    'groups',
  'indirizzi':
    'addresses',
  'mappature':
    'mappings',
  'modelli':
    'models',
  'modifiche':
    'changes',
  'moduli':
    'modules',
  'parametri':
    'parameters',
  'passi':
    'steps',
  'piani':
    'plans',
  'pool':
    'pools',
  'prodotti':
    'products',
  'proposte':
    'proposals',
  'provider':
    'providers',
  'registrazioni':
    'registrations',
  'regole':
    'rules',
  'richieste':
    'requests',
  'rischi':
    'risks',
  'segnalazioni':
    'reports',
  'servizi':
    'services',
  'tenant':
    'tenants',
  'tipi':
    'types',
  'utenti':
    'users',
  'versioni':
    'versions',
  'vicini':
    'neighbours',
  'voci':
    'entries',
  '{n} hit selezionati':
    '{n} hits selected',
  '(nessuno)':
    '(none)',
  '+{n} altre':
    '+{n} more',
  '1 problema':
    '1 issue',
  'A mano':
    'Manually',
  'Ad alto rischio':
    'High risk',
  'Adempimenti':
    'Obligations',
  'Adempimenti NIS2 a 30, 7 e 1 giorno dalla scadenza e quando scadono; per gli incidenti, prima della scadenza di pre-notifica, notifica e relazione finale. Gli avvisi vanno anche al responsabile e al punto di contatto NIS2.':
    'NIS2 obligations 30, 7 and 1 day before the deadline and when they fall due; for incidents, before the early warning, notification and final report deadlines. Alerts also go to the owner and to the NIS2 point of contact.',
  'Adempimenti NIS2 da caricare':
    'NIS2 obligations to load',
  'Adempimenti scaduti':
    'Overdue obligations',
  'Adempimento':
    'Obligation',
  'Adempimento eliminato.':
    'Obligation deleted.',
  'Adempimento previsto dalla norma: le date vengono dalle determinazioni ACN in vigore quando è stato caricato. Se una determinazione le sposta, correggile qui.':
    'Statutory obligation: the dates come from the ACN determinations in force when it was loaded. If a determination moves them, correct them here.',
  'Adempimento salvato.':
    'Obligation saved.',
  'Aggiorna da InternetDB':
    'Refresh from InternetDB',
  'Aggiorna la pre-notifica: valutazione iniziale dell’incidente, gravità e impatto, indicatori di compromissione se disponibili.':
    'Updates the early warning: initial assessment of the incident, severity and impact, indicators of compromise if available.',
  'Aggiornato da {chi} il {quando}':
    'Updated by {chi} on {quando}',
  'Aggiungi al perimetro':
    'Add to scope',
  'Aggiungi nota':
    'Add note',
  'Aggiungi un servizio…':
    'Add a service…',
  'Annulla le {n} esclusioni a mano':
    'Undo the {n} manual exclusions',
  'Annulla l’invio':
    'Undo submission',
  'Approvazione dell’organo di gestione':
    'Management body approval',
  'Apri la scheda dell’IP pubblico':
    'Open the public IP page',
  'Apri le Impostazioni':
    'Open Settings',
  'Art. 25: è significativo l’incidente che ha causato o può causare una grave perturbazione operativa dei servizi o perdite finanziarie per il soggetto, oppure danni materiali o immateriali considerevoli ad altre persone fisiche o giuridiche. Finché non è valutato gli orologi corrono comunque: meglio una pre-notifica non necessaria che una notifica in ritardo.':
    'Art. 25: an incident is significant if it has caused or can cause severe operational disruption of the services or financial losses for the entity, or considerable material or non-material damage to other natural or legal persons. Until it is assessed, the clocks run anyway: better an unnecessary early warning than a late notification.',
  'Avvisa sulle scadenze NIS2 e sugli incidenti':
    'Alert on NIS2 deadlines and incidents',
  'Business Service':
    'Business Service',
  'CI coinvolti':
    'Affected CIs',
  'CVE che InternetDB associa all’indirizzo':
    'CVEs that InternetDB associates with the address',
  'CVE note':
    'Known CVEs',
  'Calcolato dal termine della notifica finché l’invio della notifica non è registrato.':
    'Calculated from the notification deadline until the notification submission is recorded.',
  'Calcolato il {d}.':
    'Calculated on {d}.',
  'Carica gli adempimenti NIS2':
    'Load NIS2 obligations',
  'Carica gli adempimenti NIS2 previsti dalla norma, o aggiungi quelli interni con «Nuovo adempimento».':
    'Load the statutory NIS2 obligations, or add internal ones with «New obligation».',
  'Caricati {n} adempimenti NIS2: controlla date e responsabili.':
    'Loaded {n} NIS2 obligations: check dates and owners.',
  'Cerca un CI per nome o indirizzo':
    'Search for a CI by name or address',
  'Chi ha i permessi di scrittura può caricare gli adempimenti NIS2 previsti dalla norma.':
    'Users with write permission can load the statutory NIS2 obligations.',
  'Chiede di nuovo a InternetDB ciò che sa degli indirizzi pubblici (nessuna scansione)':
    'Asks InternetDB again what it knows about the public addresses (no scanning)',
  'Chiudendo l’incidente le notifiche non registrate restano «non inviate» nel registro.':
    'When the incident is closed, unrecorded notifications stay «not sent» in the register.',
  'Chiusi negli ultimi 12 mesi':
    'Closed in the last 12 months',
  'Ciò che dimostra l’adempimento in un’ispezione: il protocollo della comunicazione ad ACN, il verbale, il documento o il collegamento dove si trova.':
    'What proves the obligation was met in an inspection: the reference number of the communication to ACN, the minutes, the document or the link to where it is kept.',
  'Ciò che l’organizzazione espone a Internet: indirizzi, servizi raggiungibili e sistemi che ci stanno dietro, con il livello di rischio e il motivo. È la superficie d’attacco da conoscere e da dichiarare.':
    'What the organisation exposes to the Internet: addresses, reachable services and the systems behind them, with the risk level and the reason. It is the attack surface to know and to declare.',
  'Coinvolge altri Stati membri: da dichiarare già nella pre-notifica.':
    'Involves other Member States: to be declared already in the early warning.',
  'Compila il profilo':
    'Fill in the profile',
  'Comunicazione ACN di inclusione':
    'ACN inclusion notice',
  'Consulta Shodan InternetDB per gli IP pubblici':
    'Query Shodan InternetDB for public IPs',
  'Controlli misurati su tutto l’inventario.':
    'Controls measured on the whole inventory.',
  'Controlli misurati sul perimetro NIS2: {n} sistemi.':
    'Controls measured on the NIS2 scope: {n} systems.',
  'Crealo dalla pagina dei servizi':
    'Create it from the services page',
  'Cronologia':
    'Timeline',
  'Da comunicare ad ACN':
    'To be reported to ACN',
  'Da dichiarare già nella pre-notifica.':
    'To be declared already in the early warning.',
  'Da pianificare':
    'To be scheduled',
  'Da questa data decorrono i 9 mesi per la notifica degli incidenti e i 18 mesi per le misure di sicurezza di base.':
    'The 9 months for incident notification and the 18 months for baseline security measures run from this date.',
  'Da qui partono gli orologi dell’art. 25: pre-notifica entro 24 h, notifica entro 72 h, relazione finale entro un mese dalla notifica.':
    'The art. 25 clocks start here: early warning within 24 h, notification within 72 h, final report within one month of the notification.',
  'Data e ora dell’invio':
    'Submission date and time',
  'Definisci il perimetro':
    'Define the scope',
  'Descrizione dettagliata con gravità e impatto, tipo di minaccia o causa di fondo, misure di attenuazione adottate e in corso, impatto transfrontaliero.':
    'Detailed description with severity and impact, type of threat or root cause, mitigation measures applied and ongoing, cross-border impact.',
  'E-mail del responsabile':
    'Owner email',
  'Elenco dei servizi non disponibile.':
    'Service list not available.',
  'Eliminare l’adempimento «{titolo}»? Per uno che non vi riguarda è meglio lo stato «non applicabile», che resta come scelta motivata.':
    'Delete the obligation «{titolo}»? For one that does not apply to you, the «not applicable» status is better: it stays as a reasoned choice.',
  'Eliminati, o arrivati con un aggiornamento di CENSO. Si caricano senza toccare quelli già presenti.':
    'Deleted ones, or ones that came with a CENSO update. They are loaded without touching those already present.',
  'Email del sostituto':
    'Deputy email',
  'Entrano i punti di ingresso e i componenti di ogni servizio scelto ({n} scelti).':
    'Includes the entry points and components of each selected service ({n} selected).',
  'Entro':
    'Due by',
  'Esclude i sistemi dal perimetro: non saranno più proposti':
    'Excludes the systems from the scope: they will no longer be suggested',
  'Escludo…':
    'Excluding…',
  'Escono dalla piattaforma solo gli indirizzi pubblici del tenant, in sola lettura: nessuna scansione viene eseguita.':
    'Only the tenant’s public addresses leave the platform, read-only: no scan is performed.',
  'Esporta calendario (.ics)':
    'Export calendar (.ics)',
  'Esposti fuori perimetro':
    'Exposed outside scope',
  'Evidenza':
    'Evidence',
  'Fatti negli ultimi 12 mesi':
    'Completed in the last 12 months',
  'Gli adempimenti aperti con una data, come eventi di un giorno con un promemoria una settimana prima: si importano in Outlook, Google Calendar o Apple Calendar.':
    'Open obligations with a date, as one-day events with a reminder one week before: they can be imported into Outlook, Google Calendar or Apple Calendar.',
  'Gli indirizzi esposti arrivano da: gli IP pubblici dichiarati nella scheda di un CI (NAT, VIP) o rilevati sulle interfacce dei dispositivi; la discovery cloud (IP pubblici delle istanze); il traffico ricevuto da Internet (serve il filtro «Almeno uno interno»); InternetDB, se attivo nel profilo NIS2.':
    'Exposed addresses come from: the public IPs declared on a CI page (NAT, VIP) or detected on device interfaces; cloud discovery (public IPs of instances); traffic received from the Internet (requires the «At least one internal» filter); InternetDB, if enabled in the NIS2 profile.',
  'Gli obblighi con una scadenza del D.Lgs. 138/2024 e delle determinazioni ACN: registrazione annuale sulla piattaforma ACN, aggiornamento delle informazioni, notifica degli incidenti e misure di base dalla comunicazione di inclusione, approvazione e formazione degli organi di amministrazione, formazione del personale, analisi dei rischi, test di continuità, audit, fornitori. Ognuno con il suo riferimento, tutti modificabili.':
    'The obligations with a deadline under D.Lgs. 138/2024 and the ACN determinations: annual registration on the ACN platform, information update, incident notification and baseline measures from the inclusion notice, approval and training of management bodies, staff training, risk analysis, continuity tests, audits, suppliers. Each with its reference, all editable.',
  'Ha ricevuto connessioni da indirizzi Internet nei flussi':
    'Received connections from Internet addresses in the flows',
  'Hypervisor, storage e apparati di rete su cui poggiano i componenti dei servizi scelti.':
    'Hypervisors, storage and network devices that the components of the selected services run on.',
  'I giorni di promemoria sono numeri interi fra 0 e 365, separati da virgola.':
    'Reminder days are whole numbers between 0 and 365, separated by commas.',
  'I promemoria via e-mail vanno al responsabile dell’incidente, al punto di contatto NIS2 e ai destinatari NIS2 del tenant: 6 h prima della pre-notifica, 12 h prima della notifica, 7 giorni prima della relazione finale e a ogni scadenza. Un incidente non ancora valutato fa correre gli orologi comunque.':
    'Email reminders go to the incident owner, the NIS2 point of contact and the tenant’s NIS2 recipients: 6 h before the early warning, 12 h before the notification, 7 days before the final report and at each deadline. The clocks run even for an incident not yet assessed.',
  'I sistemi tornano nel perimetro se una regola li comprende, e tornano tra le proposte.':
    'The systems return to the scope if a rule covers them, and go back among the suggestions.',
  'IP esposti ad alto rischio':
    'High-risk exposed IPs',
  'Il filtro del traffico è «Entrambi interni»: i flussi da e verso Internet vengono scartati all’arrivo, quindi manca la parte «raggiunto da Internet». Per vederla: Impostazioni → Traffico da conservare → «Almeno uno interno» (lo sceglie un amministratore del tenant). Gli IP pubblici dichiarati, il cloud e InternetDB funzionano comunque.':
    'The traffic filter is «Both internal»: flows to and from the Internet are discarded on arrival, so the «reached from the Internet» part is missing. To see it: Settings → Traffic to keep → «At least one internal» (chosen by a tenant administrator). Declared public IPs, cloud and InternetDB work anyway.',
  'Il numero che CSIRT Italia assegna alla segnalazione.':
    'The number CSIRT Italia assigns to the report.',
  'Il perimetro NIS2 comprende i sistemi informativi e di rete usati per le attività e i servizi comunicati ad ACN. Si scrive con regole (Business Service, reti, tag), così segue l’inventario invece di diventare una lista da aggiornare a mano.':
    'The NIS2 scope includes the network and information systems used for the activities and services communicated to ACN. It is written with rules (Business Service, networks, tags), so it follows the inventory instead of becoming a list to update by hand.',
  'Il sistema non è nel perimetro NIS2':
    'The system is not in the NIS2 scope',
  'In liste di abuso':
    'On abuse lists',
  'In scadenza entro 30 giorni':
    'Due within 30 days',
  'Incidenti aperti':
    'Open incidents',
  'Includi l’infrastruttura sotto i servizi':
    'Include the infrastructure underneath the services',
  'Indica data e ora del rilevamento e della presa di conoscenza.':
    'Enter the date and time of detection and of awareness.',
  'Indirizzi IP pubblici':
    'Public IP addresses',
  'Indirizzi esposti':
    'Exposed addresses',
  'Indirizzo privato: InternetDB conosce solo gli indirizzi pubblici.':
    'Private address: InternetDB only knows public addresses.',
  'Indirizzo pubblico di un’istanza cloud':
    'Public address of a cloud instance',
  'Indirizzo pubblico scritto su un CI (NAT, VIP) o rilevato sull’interfaccia di un dispositivo':
    'Public address entered on a CI (NAT, VIP) or detected on a device interface',
  'Inizio della finestra':
    'Window start',
  'InternetDB':
    'InternetDB',
  'InternetDB non è attivo. Si accende nel profilo NIS2 (sezione «Profilo e perimetro»): è una lettura passiva dei dati di Shodan, senza alcuna scansione, e dalla piattaforma escono solo gli indirizzi pubblici dell’organizzazione.':
    'InternetDB is not enabled. It is turned on in the NIS2 profile («Profile and scope» section): it is a passive read of Shodan data, with no scanning at all, and only the organisation’s public addresses leave the platform.',
  'InternetDB non è attivo: si accende nel profilo NIS2.':
    'InternetDB is not enabled: turn it on in the NIS2 profile.',
  'InternetDB è attivo: per gli indirizzi pubblici CENSO legge ciò che Shodan sa (porte aperte, CVE note, nomi host), senza scansioni. Si aggiorna da solo una volta al giorno.':
    'InternetDB is enabled: for public addresses CENSO reads what Shodan knows (open ports, known CVEs, hostnames), without scanning. It refreshes itself once a day.',
  'InternetDB: {n} indirizzi verificati, {m} non riusciti.':
    'InternetDB: {n} addresses checked, {m} failed.',
  'Intorno ai controlli c’è il programma NIS2. «Profilo e perimetro»: soggetto essenziale o importante, punto di contatto e sostituto, data della comunicazione ACN (da lì contano i 9 mesi per la notifica degli incidenti e i 18 per le misure di base); il perimetro si definisce con Business Service, reti, tag e singoli CI, e i controlli si misurano sui suoi sistemi. Le proposte per allargarlo dicono cosa resta fuori e perché: sistemi esposti su Internet, infrastruttura da cui dipendono, sistemi con cui parlano, controller di dominio.':
    'Around the controls sits the NIS2 programme. «Profile and scope»: essential or important entity, point of contact and deputy, date of the ACN notice (the 9 months for incident notification and the 18 for baseline measures count from there); the scope is defined with Business Services, networks, tags and individual CIs, and the controls are measured on its systems. The suggestions to widen it say what is left out and why: systems exposed on the Internet, infrastructure they depend on, systems they talk to, domain controllers.',
  'Inviata il {quando}':
    'Sent on {quando}',
  'La finestra serve agli adempimenti con un periodo, come la registrazione dal 1° gennaio al 28 febbraio. Per chiudere un adempimento usa «Fatto»: registra l’evidenza e, se è ricorrente, prepara la prossima occorrenza.':
    'The window is for obligations with a period, such as registration from 1 January to 28 February. To close an obligation use «Done»: it records the evidence and, if it is recurring, prepares the next occurrence.',
  'La piattaforma non vede sistemi fuori dal perimetro legati a quelli dentro.':
    'The platform sees no systems outside the scope linked to those inside.',
  'La presa di conoscenza non può precedere il rilevamento.':
    'Awareness cannot precede detection.',
  'Le date vengono dalla norma e dalle determinazioni ACN, che possono cambiare: verificale con le determinazioni in vigore. Quelli che contano dalla comunicazione di inclusione (9 e 18 mesi) prendono la data dal profilo NIS2; se manca, restano da pianificare.':
    'The dates come from the law and the ACN determinations, which may change: check them against the determinations in force. Those that count from the inclusion notice (9 and 18 months) take the date from the NIS2 profile; if it is missing, they remain to be scheduled.',
  'Le porte raggiunte da Internet vengono dai flussi degli ultimi {d} giorni: {n} coppie indirizzo/porta con sorgenti esterne.':
    'Ports reached from the Internet come from the flows of the last {d} days: {n} address/port pairs with external sources.',
  'Le regole non comprendono ancora nessun sistema.':
    'The rules do not cover any system yet.',
  'Lette dai dati, non copiate: certificati dei sistemi del perimetro o esposti che scadono entro 60 giorni, contratti ICT che finiscono entro 90, dichiarazioni non riesaminate da un anno, passi di notifica degli incidenti aperti. Si gestiscono dove stanno: il certificato sul CI, il contratto in Contratti, la dichiarazione e l’incidente in questa pagina.':
    'Read from the data, not copied: certificates of in-scope or exposed systems expiring within 60 days, ICT contracts ending within 90, declarations not reviewed for a year, notification steps of open incidents. They are managed where they live: the certificate on the CI, the contract in Contracts, the declaration and the incident on this page.',
  'L’indirizzo e-mail del responsabile non è valido.':
    'The owner’s email address is not valid.',
  'L’inizio della finestra non può essere dopo la scadenza.':
    'The window start cannot be after the deadline.',
  'Mancano':
    'Time left',
  'Modifica i dati':
    'Edit details',
  'Modifica le regole':
    'Edit rules',
  'Modifica l’adempimento':
    'Edit obligation',
  'Mostra anche quelli fatti':
    'Show completed ones too',
  'Mostra tutto l’inventario':
    'Show the whole inventory',
  'Mostrati solo: {f}.':
    'Showing only: {f}.',
  'Motivazione':
    'Rationale',
  'Nell’aggiornamento annuale delle informazioni (dal 15 aprile al 31 maggio) ACN chiede lo spazio di indirizzamento IP pubblico e i nomi di dominio del soggetto.':
    'In the annual information update (15 April to 31 May) ACN asks for the entity’s public IP address space and domain names.',
  'Nessun Business Service definito.':
    'No Business Service defined.',
  'Nessun adempimento aperto.':
    'No open obligations.',
  'Nessun adempimento in elenco':
    'No obligations listed',
  'Nessun adempimento nuovo da caricare; le date mancanti sono state riprese dal profilo NIS2, dove possibile.':
    'No new obligations to load; missing dates were taken from the NIS2 profile where possible.',
  'Nessun altro servizio':
    'No other services',
  'Nessun incidente registrato':
    'No incidents recorded',
  'Nessun indirizzo esposto conosciuto':
    'No known exposed addresses',
  'Nessun nome di dominio noto: i nomi arrivano dagli host noti a InternetDB, se attivo. Aggiungi a mano i domini registrati dal soggetto.':
    'No known domain names: names come from the hosts known to InternetDB, if enabled. Add the domains registered by the entity by hand.',
  'Nessun problema noto sui certificati dei sistemi esposti.':
    'No known issues with the certificates of exposed systems.',
  'Nessun servizio corrisponde.':
    'No matching services.',
  'Nessun servizio rischioso, vulnerabilità o segnalazione nota.':
    'No known risky services, vulnerabilities or reports.',
  'Nessun sistema collegato: dichiara l’IP pubblico nella scheda del CI che lo espone (NAT, VIP).':
    'No linked system: declare the public IP on the page of the CI that exposes it (NAT, VIP).',
  'Nessuna evidenza: segnarlo fatto comunque? In un’ispezione l’evidenza è ciò che dimostra l’adempimento.':
    'No evidence: mark it as done anyway? In an inspection, the evidence is what proves the obligation was met.',
  'Nessuna notifica in attesa':
    'No pending notifications',
  'Nessuna porta nota: né i flussi da Internet né InternetDB ne indicano.':
    'No known ports: neither Internet flows nor InternetDB report any.',
  'Nessuna scadenza ricavata nei prossimi mesi.':
    'No derived deadlines in the coming months.',
  'Nessuna voce.':
    'No entries.',
  'Nomi di dominio':
    'Domain names',
  'Nomi host':
    'Hostnames',
  'Non ancora determinato':
    'Not yet determined',
  'Non ancora verificata.':
    'Not checked yet.',
  'Non ancora verificato: l’aggiornamento è giornaliero, oppure subito con «Aggiorna da InternetDB».':
    'Not checked yet: the refresh is daily, or immediate with «Refresh from InternetDB».',
  'Non proporre più':
    'Don’t suggest again',
  'Notifica':
    'Notification',
  'Notifiche a CSIRT Italia':
    'Notifications to CSIRT Italia',
  'Notifiche scadute':
    'Overdue notifications',
  'Nuovo adempimento':
    'New obligation',
  'Passi non inviati a CSIRT Italia oltre il termine':
    'Steps not sent to CSIRT Italia past the deadline',
  'Perché proporlo':
    'Why suggested',
  'Perché questo livello di rischio':
    'Why this risk level',
  'Perché è dentro':
    'Why it is in scope',
  'Perimetro NIS2':
    'NIS2 scope',
  'Perimetro NIS2 non definito: i controlli sono misurati su tutto l’inventario.':
    'NIS2 scope not defined: controls are measured on the whole inventory.',
  'Perimetro non definito.':
    'Scope not defined.',
  'Porta aperta secondo InternetDB (dati Shodan)':
    'Open port according to InternetDB (Shodan data)',
  'Porte aperte':
    'Open ports',
  'Possibile impatto transfrontaliero':
    'Possible cross-border impact',
  'Pre-notifica':
    'Early warning',
  'Prepara l’elenco':
    'Prepare the list',
  'Presa di conoscenza':
    'Awareness',
  'Profilo NIS2':
    'NIS2 profile',
  'Profilo e perimetro':
    'Profile and scope',
  'Profilo non ancora compilato':
    'Profile not filled in yet',
  'Profilo salvato: gli obblighi NIS2 sono datati a partire dalle date del profilo.':
    'Profile saved: NIS2 obligations are dated from the profile dates.',
  'Promemoria per e-mail a 30, 7 e 1 giorno (o ai giorni scelti), il giorno della scadenza e una volta a scadenza passata: al responsabile, al punto di contatto NIS2 e ai destinatari degli avvisi del tenant. Si spengono in':
    'Email reminders at 30, 7 and 1 day (or on the chosen days), on the due date and once after the deadline has passed: to the owner, the NIS2 point of contact and the tenant’s alert recipients. They are turned off in',
  'Proposte per allargare il perimetro':
    'Suggestions to widen the scope',
  'Prossima scadenza':
    'Next deadline',
  'Punto di contatto':
    'Point of contact',
  'Quando gli organi di amministrazione hanno approvato le misure di gestione del rischio.':
    'When the management bodies approved the risk-management measures.',
  'Quando l’evento è stato rilevato (allarme, segnalazione, anomalia).':
    'When the event was detected (alarm, report, anomaly).',
  'Ragione sociale':
    'Company name',
  'Registra incidente':
    'Log incident',
  'Registra l’incidente appena se ne ha conoscenza: da quel momento corrono le 24 ore per la pre-notifica.':
    'Log the incident as soon as you become aware of it: the 24 hours for the early warning start from that moment.',
  'Registra l’invio':
    'Record submission',
  'Registrazione ACN':
    'ACN registration',
  'Registro degli incidenti':
    'Incident register',
  'Regole aggiornate da {chi} il {quando}':
    'Rules updated by {chi} on {quando}',
  'Relazione finale':
    'Final report',
  'Responsabile: {nome}':
    'Owner: {nome}',
  'Reti e tag':
    'Networks and tags',
  'Ricalcola l’elenco':
    'Recalculate the list',
  'Riceve i promemoria della scadenza.':
    'Receives the deadline reminders.',
  'Riceve i promemoria delle scadenze insieme al punto di contatto NIS2.':
    'Receives deadline reminders together with the NIS2 point of contact.',
  'Ricorrenza':
    'Recurrence',
  'Riferimento CSIRT':
    'CSIRT reference',
  'Riferimento della registrazione ACN':
    'ACN registration reference',
  'Riferimento normativo':
    'Legal reference',
  'Rilevanza':
    'Relevance',
  'Scadenze':
    'Deadlines',
  'Scadenze NIS2':
    'NIS2 deadlines',
  'Scadenze NIS2 non disponibili.':
    'NIS2 deadlines not available.',
  'Scadenze dai dati della piattaforma':
    'Deadlines from platform data',
  'Se si sospetta che l’incidente sia causato da atti illegittimi o malevoli e se può avere un impatto transfrontaliero.':
    'Whether the incident is suspected of being caused by unlawful or malicious acts and whether it could have a cross-border impact.',
  'Segna come fatto':
    'Mark as done',
  'Segna come fatto: «{titolo}»':
    'Mark as done: “{titolo}”',
  'Segna come inviata':
    'Mark as sent',
  'Senza regole il report misura l’intero inventario ({n} sistemi).':
    'Without rules the report covers the entire inventory ({n} systems).',
  'Separati da virgola. Vuoto = 30, 7 e 1 giorno prima.':
    'Comma-separated. Empty = 30, 7 and 1 day before.',
  'Serve un titolo.':
    'A title is required.',
  'Servizi coinvolti':
    'Affected services',
  'Servizi rischiosi raggiungibili':
    'Reachable risky services',
  'Servizio a rischio {r} se raggiungibile da Internet':
    '{r}-risk service if reachable from the Internet',
  'Settore':
    'Sector',
  'Shodan InternetDB':
    'Shodan InternetDB',
  'Si verificano su InternetDB gli IP pubblici registrati come CI: questo indirizzo non lo è ancora.':
    'Public IPs registered as CIs are checked on InternetDB: this address is not registered yet.',
  'Significatività':
    'Significance',
  'Significatività da valutare: gli orologi corrono comunque.':
    'Significance to be assessed: the clocks are running anyway.',
  'Sistemi aggiunti o esclusi a mano':
    'Manually added or excluded systems',
  'Sistemi dietro':
    'Systems behind',
  'Sistemi dietro l’indirizzo':
    'Systems behind the address',
  'Sistemi fuori dal perimetro che la piattaforma vede legati a quelli dentro: esposti su Internet, infrastruttura da cui dipendono, sistemi con cui parlano nel traffico degli ultimi 7 giorni, servizi comuni come controller di dominio e hypervisor.':
    'Systems outside the scope that the platform sees as linked to those inside: exposed on the Internet, infrastructure they depend on, systems they talk to in the last 7 days of traffic, shared services such as domain controllers and hypervisors.',
  'Sistemi nel perimetro':
    'Systems in scope',
  'Sistemi nell’inventario':
    'Systems in the inventory',
  'Situazione':
    'Status',
  'Soggetto essenziale':
    'Essential entity',
  'Soggetto importante':
    'Important entity',
  'Sorgenti Internet':
    'Internet sources',
  'Sospetta azione illegittima o malevola':
    'Suspected unlawful or malicious act',
  'Sostituto':
    'Deputy',
  'Sottoreti pubbliche':
    'Public subnets',
  'Sottosettore':
    'Subsector',
  'Superficie esposta':
    'Exposed surface',
  'Tipo di soggetto':
    'Entity type',
  'Tipo di soggetto, punto di contatto e data della comunicazione ACN servono a datare gli obblighi NIS2.':
    'Entity type, point of contact and the date of the ACN communication are used to date the NIS2 obligations.',
  'Togli i {n} sistemi aggiunti a mano':
    'Remove the {n} manually added systems',
  'Toglie il sistema dal perimetro anche se una regola lo comprende':
    'Removes the system from the scope even if a rule includes it',
  'Torna al perimetro':
    'Back to scope',
  'Ultimo visto':
    'Last seen',
  'Un tag per riga, nella forma «chiave:valore»: entra ogni sistema che ne ha almeno uno.':
    'One tag per line, in the form “key:value”: every system that has at least one of them is included.',
  'Una rete per riga, in notazione CIDR: entra ogni sistema con un indirizzo dentro.':
    'One network per line, in CIDR notation: every system with an address inside is included.',
  'Valutato non significativo: nessuna notifica dovuta a CSIRT Italia.':
    'Assessed as not significant: no notification due to CSIRT Italia.',
  'Verificato il':
    'Checked on',
  'Vulnerabilità critiche sui sistemi dietro l’indirizzo':
    'Critical vulnerabilities on the systems behind the address',
  'Vulnerabilità sfruttate attivamente sui sistemi dietro l’indirizzo':
    'Actively exploited vulnerabilities on the systems behind the address',
  'adempimenti':
    'obligations',
  'adempimenti e scadenze ricavate':
    'obligations and derived deadlines',
  'amministrazione remota, condivisione file, database…':
    'remote administration, file sharing, databases…',
  'annuale':
    'annual',
  'con l’infrastruttura sotto i servizi':
    'with the infrastructure under the services',
  'consultato':
    'queried',
  'contenuto':
    'contained',
  'contratto':
    'contract',
  'cosa si è scoperto, cosa si è deciso, cosa si è comunicato':
    'what was found, what was decided, what was communicated',
  'cosa è successo, cosa si è visto, cosa si è fatto finora':
    'what happened, what was observed, what has been done so far',
  'da 1 sorgente':
    'from 1 source',
  'da assegnare':
    'to be assigned',
  'da fare':
    'to do',
  'da pianificare':
    'to be scheduled',
  'da valutare':
    'to be assessed',
  'da {n} sorgenti':
    'from {n} sources',
  'dal {inizio} al {fine}':
    'from {inizio} to {fine}',
  'dichiarazione':
    'declaration',
  'dipendenza':
    'dependency',
  'domani':
    'tomorrow',
  'entro 24 h dalla presa di conoscenza':
    'within 24 h of becoming aware',
  'entro 72 h dalla presa di conoscenza':
    'within 72 h of becoming aware',
  'entro un mese dalla notifica':
    'within one month of the notification',
  'entro un’ora':
    'within an hour',
  'es. Sanità':
    'e.g. Health',
  'es. ransomware sui file server, indisponibilità del portale':
    'e.g. ransomware on the file servers, portal unavailable',
  'esposto su Internet':
    'exposed on the Internet',
  'fatto':
    'done',
  'fra {n} h':
    'in {n} h',
  'fra {n} min':
    'in {n} min',
  'fra {n} ore':
    'in {n} hours',
  'fuori perimetro':
    'out of scope',
  'il {data}':
    'on {data}',
  'in lista di abuso':
    'on an abuse list',
  'incidente':
    'incident',
  'incidente registrato':
    'incident logged',
  'indirizzi ad alto rischio':
    'high-risk addresses',
  'indirizzi con servizi rischiosi':
    'addresses with risky services',
  'indirizzi con sistemi fuori perimetro':
    'addresses with out-of-scope systems',
  'indirizzi in liste di abuso':
    'addresses on abuse lists',
  'inviata':
    'sent',
  'inviata a un’altra ora…':
    'sent at a different time…',
  'inviata in ritardo':
    'sent late',
  'inviata nei termini':
    'sent on time',
  'mensile':
    'monthly',
  'misure di base entro il {d}':
    'baseline measures by {d}',
  'nel perimetro':
    'in scope',
  'nessuna notifica dovuta':
    'no notification due',
  'nessuna regola: conta tutto':
    'no rules: everything counts',
  'non consultato':
    'not queried',
  'non dovuta':
    'not due',
  'non inviata':
    'not sent',
  'non inviata: incidente chiuso senza registrarne l’invio':
    'not sent: incident closed without recording its submission',
  'non registrata':
    'not recorded',
  'non significativo':
    'not significant',
  'normativo':
    'regulatory',
  'notifica degli incidenti dal {d}':
    'incident notification from {d}',
  'notifica inviata':
    'notification sent',
  'notifica: invio annullato':
    'notification: submission cancelled',
  'notifiche, «Avvisa sulle scadenze NIS2 e sugli incidenti».':
    'notifications, “Alert on NIS2 deadlines and incidents”.',
  'numero di protocollo, documento, collegamento…':
    'reference number, document, link…',
  'oggi':
    'today',
  'perché è o non è significativo':
    'why it is or is not significant',
  'perimetro NIS2':
    'NIS2 scope',
  'pianificato':
    'scheduled',
  'pre-notifica inviata':
    'early warning sent',
  'pre-notifica: invio annullato':
    'early warning: submission cancelled',
  'privato, raggiunto da Internet':
    'private, reached from the Internet',
  'proposte perimetro NIS2':
    'NIS2 scope suggestions',
  'relazione finale inviata':
    'final report sent',
  'relazione finale: invio annullato':
    'final report: submission cancelled',
  'scaduta da {n} giorni':
    'overdue by {n} days',
  'scaduta da {n} h':
    'overdue by {n} h',
  'scaduta da {n} min':
    'overdue by {n} min',
  'scaduto da {n} ore':
    'expired {n} hours ago',
  'scaduto ieri':
    'expired yesterday',
  'semestrale':
    'semi-annual',
  'senza l’infrastruttura sotto i servizi':
    'without the infrastructure under the services',
  'senza una data di scadenza':
    'without a due date',
  'servizio comune':
    'shared service',
  'significativo':
    'significant',
  'sistemi':
    'systems',
  'sistemi esposti non inclusi nel perimetro NIS2':
    'exposed systems not included in the NIS2 scope',
  'stato: {stato}':
    'status: {stato}',
  'termine':
    'deadline',
  'traffico':
    'traffic',
  'trimestrale':
    'quarterly',
  'una tantum':
    'one-off',
  '{a} aggiunti, {e} esclusi':
    '{a} added, {e} excluded',
  '{a} sistemi aggiunti a mano, {e} esclusi a mano. Un’esclusione vale sempre, anche se una regola comprende il sistema.':
    '{a} systems added manually, {e} excluded manually. An exclusion always applies, even if a rule includes the system.',
  '{n} CVE da InternetDB':
    '{n} CVEs from InternetDB',
  '{n} a rischio medio':
    '{n} medium risk',
  '{n} adempimenti previsti dalla norma non sono in elenco':
    '{n} obligations required by law are not in the list',
  '{n} con significatività da valutare':
    '{n} with significance to be assessed',
  '{n} in scadenza nei prossimi 30 giorni, {m} da pianificare':
    '{n} due in the next 30 days, {m} to be scheduled',
  '{n} indirizzi esposti, {m} fuori dal perimetro':
    '{n} exposed addresses, {m} out of scope',
  '{n} notifiche scadute':
    '{n} overdue notifications',
  '{n} problemi':
    '{n} issues',
  '{n} pubblici':
    '{n} public',
  '{n} selezionati':
    '{n} selected',
  '{n} sfruttate':
    '{n} exploited',
  '{n} sistemi esposti su Internet sono fuori dal perimetro: è la lacuna più grave, valuta di includerli.':
    '{n} systems exposed on the Internet are out of scope: this is the most serious gap, consider including them.',
  '{ref}: prossima notifica fra {h} h':
    '{ref}: next notification in {h} h',
  '«Scadenze»: gli adempimenti NIS2 precaricati con il riferimento (registrazione e aggiornamento annuale su ACN, misure di base, organi di amministrazione, formazione, rischi, continuità, audit, fornitori), modificabili, con responsabile, ricorrenza ed evidenza, più le scadenze ricavate dai dati (certificati, contratti ICT, dichiarazioni, incidenti) e il calendario .ics; i promemoria arrivano per email. «Incidenti»: il registro con gli orologi dell’art. 25 — pre-notifica entro 24 ore dalla presa di conoscenza, notifica entro 72, relazione finale entro un mese — e i promemoria prima di ogni scadenza.':
    '“Deadlines”: the preloaded NIS2 obligations with their legal reference (annual registration and update on ACN, baseline measures, management bodies, training, risks, continuity, audits, suppliers), editable, with owner, recurrence and evidence, plus the deadlines derived from the data (certificates, ICT contracts, declarations, incidents) and the .ics calendar; reminders arrive by email. “Incidents”: the register with the art. 25 clocks — early warning within 24 hours of becoming aware, notification within 72, final report within one month — and reminders before each deadline.',
  '«Superficie esposta»: gli IP pubblici dichiarati, quelli delle interfacce e del cloud, ciò che dai flussi risulta raggiunto da Internet e, se lo accendi nel profilo, ciò che sa Shodan InternetDB (sola lettura, nessuna scansione); per ogni indirizzo porte, servizi da non esporre, sistemi dietro, certificati, vulnerabilità, reputazione e il rischio con il motivo, più l’elenco di indirizzi e domini da comunicare ad ACN. Per vedere il traffico da Internet, in Impostazioni il traffico da conservare deve essere «Almeno uno interno».':
    '“Exposed surface”: the declared public IPs, those of interfaces and cloud, what the flows show as reached from the Internet and, if you enable it in the profile, what Shodan InternetDB knows (read-only, no scanning); for each address: ports, services that should not be exposed, systems behind, certificates, vulnerabilities, reputation and the risk with its reason, plus the list of addresses and domains to report to ACN. To see traffic from the Internet, the traffic to keep in Settings must be “At least one internal”.',
  '«{titolo}» segnato come fatto.':
    '“{titolo}” marked as done.',
  '«{titolo}» segnato come fatto. Creata la prossima occorrenza, in scadenza il {data}.':
    '“{titolo}” marked as done. Next occurrence created, due on {data}.',
  'È ciò che CENSO conosce: confrontalo con le assegnazioni del provider prima di comunicarlo.':
    'This is what CENSO knows: compare it with the provider’s allocations before reporting it.',
  'È {ricorrenza}: alla conferma nasce la prossima occorrenza, in scadenza il {data}. Questa resta nello storico con la sua evidenza.':
    'This obligation is {ricorrenza}: on confirmation the next occurrence is created, due on {data}. This one stays in the history with its evidence.',
  'D.Lgs. 138/2024, art. 24':
    'D.Lgs. 138/2024, art. 24',
  'nome@azienda.it':
    'name@company.com',
  'Almeno {n} caratteri: il motivo resta nel registro delle letture e compare nell’e-mail di avviso.':
    'At least {n} characters: the reason stays in the read log and appears in the alert e-mail.',
  'Annulla la rotazione':
    'Cancel rotation',
  'Annullare la rotazione della chiave {id}?':
    'Cancel the rotation of key {id}?',
  'Annullare la rotazione della chiave {id}? L’agente ha già aggiunto la nuova chiave: sul volume restano tutte e due.':
    'Cancel the rotation of key {id}? The agent has already added the new key: both stay on the volume.',
  'Avvisa quando viene letta una chiave BitLocker':
    'Alert when a BitLocker key is read',
  'BitLocker':
    'BitLocker',
  'CENSO non custodisce chiavi di questo volume: con la raccolta accesa arrivano al prossimo inventario dell’agente.':
    'CENSO holds no keys for this volume: with collection on, they arrive with the agent’s next inventory.',
  'Chiave di ripristino BitLocker':
    'BitLocker recovery key',
  'Chiave in CENSO':
    'Key in CENSO',
  'Chiavi custodite':
    'Keys held',
  'Cifrato %':
    'Encrypted %',
  'Cifratura in corso':
    'Encryption in progress',
  'Cifratura sospesa':
    'Encryption paused',
  'Con la raccolta spenta la rotazione dopo la lettura non parte: accendi anche la raccolta delle chiavi.':
    'With collection off, rotation after a read does not start: turn on key collection too.',
  'Controlla l’ID chiave sulla schermata di ripristino. Se la macchina è giusta ma la chiave manca, l’agente non l’ha ancora inviata: serve la raccolta accesa e un inventario dopo l’accensione.':
    'Check the key ID on the recovery screen. If the machine is right but the key is missing, the agent has not sent it yet: collection must be on and an inventory must run after it was turned on.',
  'Copia non disponibile qui: seleziona il testo qui sotto e copialo a mano.':
    'Copy not available here: select the text below and copy it manually.',
  'C’è già una rotazione in corso su questo volume.':
    'A rotation is already in progress on this volume.',
  'Decifratura in corso':
    'Decryption in progress',
  'Decifratura sospesa':
    'Decryption paused',
  'Due passi: l’agente aggiunge una nuova password di ripristino (e prova a copiarla in Active Directory o Entra ID), poi — quando CENSO l’ha custodita — toglie la vecchia. Si annullano le rotazioni in coda, in attesa della nuova chiave o non riuscite.':
    'Two steps: the agent adds a new recovery password (and tries to back it up to Active Directory or Entra ID), then — once CENSO holds it — removes the old one. Rotations that are queued, waiting for the new key or failed can be cancelled.',
  'Filtro dei volumi':
    'Volume filter',
  'Gli agenti Windows inviano anche le password di ripristino dei volumi. Viaggiano sigillate nella busta dell’agente e sono conservate cifrate con la chiave del tenant: mai sul CI, mai nelle e-mail. Le chiavi API del tenant non possono leggerle. Spenta, CENSO conosce solo lo stato dei volumi.':
    'Windows agents also send the volumes’ recovery passwords. They travel sealed in the agent’s envelope and are stored encrypted with the tenant key: never on the CI, never in e-mails. The tenant’s API keys cannot read them. When it is off, CENSO knows only the state of the volumes.',
  'Hai la sola lettura del modulo BitLocker: puoi cercare le chiavi ma non mostrarle.':
    'You have read-only access to the BitLocker module: you can search for keys but not show them.',
  'I volumi arrivano con l’inventario dell’agente CENSO sulle macchine Windows. Le macchine Windows senza dati hanno un agente assente o più vecchio di questa versione.':
    'Volumes arrive with the CENSO agent inventory on Windows machines. Windows machines with no data have no agent or one older than this version.',
  'ID chiave':
    'Key ID',
  'ID chiave (le prime 8 cifre mostrate nella schermata di ripristino BitLocker) o nome della macchina':
    'Key ID (the first 8 digits shown on the BitLocker recovery screen) or machine name',
  'La chiave verrà sostituita dall’agente appena la macchina è in linea: prima aggiunge una nuova chiave di ripristino, poi — quando CENSO l’ha custodita — toglie questa.':
    'The agent will replace the key as soon as the machine is online: first it adds a new recovery key, then — once CENSO holds it — removes this one.',
  'La chiave è stata nascosta dopo 5 minuti. Per rivederla serve una nuova lettura, registrata come la prima.':
    'The key was hidden after 5 minutes. Seeing it again requires a new read, logged like the first one.',
  'La chiave è stata nascosta.':
    'The key has been hidden.',
  'La cifratura BitLocker dei volumi Windows, letta dall’agente CENSO, e le chiavi di ripristino custodite cifrate. Ogni lettura di una chiave chiede un motivo, resta nel registro ed è notificata; dopo la lettura l’agente sostituisce la chiave.':
    'BitLocker encryption of Windows volumes, read by the CENSO agent, and the recovery keys held encrypted. Every read of a key asks for a reason, stays in the log and is notified; after the read the agent replaces the key.',
  'La raccolta delle chiavi è spenta: CENSO conosce lo stato dei volumi ma non riceve nuove chiavi di ripristino. Un amministratore del tenant la accende in «Panoramica».':
    'Key collection is off: CENSO knows the state of the volumes but receives no new recovery keys. A tenant administrator turns it on in «Overview».',
  'La rotazione dopo la lettura è spenta: la chiave resta quella di prima finché qualcuno non la sostituisce.':
    'Rotation after a read is off: the key stays the same until someone replaces it.',
  'La schermata blu di ripristino mostra l’«ID chiave di ripristino» (in inglese «Recovery key ID»): bastano i primi caratteri. Si cerca anche per nome della macchina.':
    'The blue recovery screen shows the «Recovery key ID»: the first characters are enough. You can also search by machine name.',
  'La sostituzione automatica non è partita.':
    'Automatic replacement did not start.',
  'La sostituzione della chiave è già in corso: {stato}.':
    'Key replacement is already in progress: {stato}.',
  'Le ultime 500 letture.':
    'The last 500 reads.',
  'Letto dall’agente':
    'Read by the agent',
  'Letture':
    'Reads',
  'Letture negli ultimi 30 giorni':
    'Reads in the last 30 days',
  'L’agente aggiunge una nuova password di ripristino e, appena CENSO l’ha custodita, toglie questa: la macchina non resta mai senza una chiave custodita.':
    'The agent adds a new recovery password and, as soon as CENSO holds it, removes this one: the machine is never left without a key held in CENSO.',
  'Macchine con dati BitLocker':
    'Machines with BitLocker data',
  'Mai modificate: raccolta spenta e rotazione dopo la lettura accesa, i valori predefiniti.':
    'Never changed: collection off and rotation after a read on, the defaults.',
  'Metto in coda…':
    'Queuing…',
  'Mostra la chiave':
    'Show key',
  'Motivo della lettura':
    'Reason for reading',
  'Motivo della rotazione':
    'Reason for rotation',
  'Nessun volume BitLocker ricevuto':
    'No BitLocker volumes received',
  'Nessun volume per questo filtro.':
    'No volumes for this filter.',
  'Nessuna chiave custodita per questo volume.':
    'No key held for this volume.',
  'Nessuna chiave trovata per «{q}»':
    'No keys found for «{q}»',
  'Nessuna lettura':
    'No reads',
  'Nessuna rotazione':
    'No rotations',
  'Nessuna voce nel registro della rotazione.':
    'No entries in the rotation log.',
  'Non cifrato':
    'Not encrypted',
  'Ogni chiave mostrata dalla console compare qui: chi, quando, da dove e perché.':
    'Every key shown by the console appears here: who, when, from where and why.',
  'Ogni lettura di una chiave chiede un motivo, viene registrata (chi, quando, da dove e perché) e notificata per e-mail ai destinatari dell’evento «BitLocker» in Impostazioni. L’e-mail non contiene mai la chiave.':
    'Every read of a key asks for a reason, is logged (who, when, from where and why) and notified by e-mail to the recipients of the «BitLocker» event in Settings. The e-mail never contains the key.',
  'Ogni lettura di una chiave di ripristino BitLocker: macchina, volume, chi l’ha letta, quando e perché. La chiave non è mai nella mail.':
    'Every read of a BitLocker recovery key: machine, volume, who read it, when and why. The key is never in the e-mail.',
  'Password di ripristino':
    'Recovery password',
  'Prima di leggere, controlla che l’ID chiave mostrato dalla schermata di ripristino BitLocker sia {id}. La lettura viene registrata (chi, quando, da dove e perché) e notificata per e-mail ai destinatari dell’evento BitLocker; di norma, dopo la lettura, l’agente sostituisce la chiave.':
    'Before reading, check that the recovery key ID shown on the BitLocker recovery screen is {id}. The read is logged (who, when, from where and why) and notified by e-mail to the recipients of the BitLocker event; normally, after the read, the agent replaces the key.',
  'Protetti senza chiave in CENSO ({n})':
    'Protected without a key in CENSO ({n})',
  'Protettori':
    'Protectors',
  'Questa chiave non è più segreta: chi l’ha vista o ascoltata può sbloccare il volume. Non scriverla nel ticket e non inviarla per e-mail.':
    'This key is no longer secret: anyone who has seen or heard it can unlock the volume. Do not write it in the ticket or send it by e-mail.',
  'Questa chiave è già stata sostituita sul volume: non c’è niente da ruotare.':
    'This key has already been replaced on the volume: there is nothing to rotate.',
  'Raccogli le chiavi di ripristino':
    'Collect recovery keys',
  'Raccolta delle chiavi':
    'Key collection',
  'Richiesta da':
    'Requested by',
  'Rimovibile':
    'Removable',
  'Rotazione':
    'Rotation',
  'Rotazione della chiave {id} annullata.':
    'Rotation of key {id} cancelled.',
  'Rotazione della chiave {id} messa in coda: l’agente la esegue appena la macchina è in linea.':
    'Rotation of key {id} queued: the agent will run it as soon as the machine is online.',
  'Rotazione: {stato}.':
    'Rotation: {stato}.',
  'Rotazioni':
    'Rotations',
  'Rotazioni in corso':
    'Rotations in progress',
  'Ruota':
    'Rotate',
  'Ruota adesso':
    'Rotate now',
  'Ruota la chiave di ripristino':
    'Rotate the recovery key',
  'Ruota la chiave dopo ogni lettura':
    'Rotate the key after every read',
  'Scrivi almeno {n} caratteri.':
    'Enter at least {n} characters.',
  'Serve la raccolta delle chiavi accesa e l’agente CENSO sulla macchina; si ruotano i volumi con una lettera di unità.':
    'Requires key collection to be turned on and the CENSO agent on the machine; only volumes with a drive letter are rotated.',
  'Si nasconde da sola fra {tempo}':
    'Hides automatically in {tempo}',
  'Sistema operativo non protetto ({n})':
    'Operating system not protected ({n})',
  'Solo un amministratore del tenant può cambiare queste impostazioni.':
    'Only a tenant administrator can change these settings.',
  'Sono mostrate le prime 50: scrivi qualche carattere in più.':
    'Showing the first 50: type a few more characters.',
  'Trova la chiave di ripristino':
    'Find the recovery key',
  'Trova una chiave':
    'Find a key',
  'Tutti ({n})':
    'All ({n})',
  'Ultima lettura':
    'Last read',
  'Ultima lettura: {quando}, {chi}':
    'Last read: {quando}, {chi}',
  'Ultima modifica: {quando}, da {chi}':
    'Last modified: {quando}, by {chi}',
  'Una chiave letta non è più segreta. L’agente aggiunge una nuova password di ripristino e, appena CENSO l’ha custodita, toglie la vecchia: la macchina non resta mai senza una chiave custodita. Senza la raccolta delle chiavi la rotazione non può funzionare, perché la nuova chiave non arriverebbe mai a CENSO.':
    'A key that has been read is no longer secret. The agent adds a new recovery password and, as soon as CENSO holds it, removes the old one: the machine is never left without a held key. Without key collection, rotation cannot work, because the new key would never reach CENSO.',
  'Una chiave letta non è più segreta: CENSO la fa ruotare dall’agente (si può spegnere), in due tempi — prima l’agente aggiunge una chiave nuova (e prova a copiarla in Active Directory o Entra ID), poi, quando la nuova è custodita in CENSO, toglie la vecchia: la macchina non resta mai senza una chiave di ripristino conservata. Serve l’agente aggiornato; le chiavi API del tenant non possono leggerle.':
    'A key that has been read is no longer secret: CENSO has the agent rotate it (this can be turned off), in two steps — first the agent adds a new key (and tries to copy it to Active Directory or Entra ID), then, once the new one is held in CENSO, it removes the old one: the machine is never left without a stored recovery key. Requires an up-to-date agent; tenant API keys cannot read them.',
  'Una rotazione parte dopo la lettura di una chiave (se l’impostazione è accesa) o con «Ruota» nella sezione Volumi.':
    'A rotation starts after a key is read (if the setting is on) or with «Rotate» in the Volumes section.',
  'Volumi ({n})':
    'Volumes ({n})',
  'Volumi di sistema NON protetti':
    'System volumes NOT protected',
  'Volumi di sistema protetti':
    'Protected system volumes',
  'Volumi protetti senza chiave in CENSO':
    'Protected volumes without a key in CENSO',
  'aggiunge la nuova chiave':
    'adding the new key',
  'annullata':
    'cancelled',
  'attende la nuova chiave':
    'waiting for the new key',
  'chiave comunicata al telefono, rotazione periodica…':
    'key given over the phone, periodic rotation…',
  'completata':
    'completed',
  'copiata negli appunti':
    'copied to clipboard',
  'in coda':
    'queued',
  'in esecuzione sull’agente':
    'running on the agent',
  'la raccolta delle chiavi è spenta':
    'key collection is turned off',
  'letture':
    'reads',
  'lo stato BitLocker dei volumi delle macchine Windows con l’agente CENSO (protezione, cifratura, metodo, protettori) e, se un amministratore accende «Raccogli le chiavi di ripristino», le chiavi stesse: viaggiano nella busta cifrata dell’agente, sono conservate cifrate con la chiave del tenant e non stanno mai sulla scheda del CI né nelle mail. «Trova una chiave» cerca per l’ID chiave che la schermata di ripristino mostra (le prime 8 cifre) o per nome della macchina; «Mostra la chiave» chiede il motivo, e ogni lettura resta nel registro «Letture» con chi, quando, da dove e perché, e arriva per mail ai destinatari del tenant.':
    'the BitLocker state of the volumes of Windows machines with the CENSO agent (protection, encryption, method, protectors) and, if an administrator turns on «Collect recovery keys», the keys themselves: they travel in the agent’s encrypted envelope, are stored encrypted with the tenant key and never appear on the CI page or in e-mails. «Find a key» searches by the key ID shown on the recovery screen (the first 8 digits) or by machine name; «Show key» asks for the reason, and every read is kept in the «Reads» log with who, when, from where and why, and is e-mailed to the tenant’s recipients.',
  'non messa in coda':
    'not queued',
  'non riuscita':
    'failed',
  'passo: {passo}':
    'step: {passo}',
  'richiesta da {chi}':
    'requested by {chi}',
  'rotazioni':
    'rotations',
  'sostituita':
    'replaced',
  'ticket 4711, utente bloccato alla schermata di ripristino':
    'ticket 4711, user stuck at the recovery screen',
  'toglie la vecchia chiave':
    'removing the old key',
  'volumi':
    'volumes',
  'Apri Application Maintenance':
    'Open Application Maintenance',
  'Apri BitLocker':
    'Open BitLocker',
  'Apri Cifratura':
    'Open Encryption',
  'Apri Eagle':
    'Open Eagle',
  'Asset che sostengono funzioni critiche o importanti (dai Business Service) sotto il monitoraggio di CENSO Eagle.':
    'Assets that support critical or important functions (from the Business Services) under CENSO Eagle monitoring.',
  'Certificati TLS letti dagli agenti CENSO sui servizi esposti e servizi che altre macchine chiamano in chiaro.':
    'TLS certificates read by the CENSO agents on exposed services, and services other machines call in clear text.',
  'Chiedi l’inventario agli agenti':
    'Ask the agents for an inventory',
  'Chiedo…':
    'Asking…',
  'Cifratura dei dati in transito':
    'Encryption of data in transit',
  'Con l’agente CENSO, dati non ancora arrivati':
    'With the CENSO agent, data not yet received',
  'Inventario chiesto a {n} agenti: i dati arrivano entro qualche minuto dagli agenti già aggiornati.':
    'Inventory requested from {n} agents: the data arrives within a few minutes from the agents already updated.',
  'Librerie delle applicazioni dalla distinta dei componenti (SBOM) confrontate con gli avvisi di vulnerabilità.':
    'Application libraries from the bill of materials (SBOM) checked against vulnerability advisories.',
  'L’agente si aggiorna da solo e manda lo stato BitLocker (e, con la raccolta accesa, le chiavi) al prossimo inventario. Puoi chiederlo subito.':
    'The agent updates itself and sends the BitLocker state (and, with collection on, the keys) at the next inventory. You can ask for it now.',
  'Macchine Windows senza dettaglio BitLocker ({n})':
    'Windows machines without BitLocker detail ({n})',
  'Monitorato':
    'Monitored',
  'Mostra gli asset':
    'Show the assets',
  'Nessun certificato in questo elenco.':
    'No certificate in this list.',
  'Nessuna area chiede un intervento. Clicca un numero per vedere gli asset che lo compongono.':
    'No area needs action. Click a number to see the assets behind it.',
  'Nessuna informazione':
    'No information',
  'Nome nel certificato':
    'Name in the certificate',
  'Né agente né scansione WinRM con credenziali: installa l’agente CENSO o aggiungi una credenziale Windows alla discovery.':
    'Neither an agent nor a WinRM scan with credentials: install the CENSO agent or add a Windows credential to the discovery.',
  'Protezione BitLocker':
    'BitLocker protection',
  'Senza agente, stato dalla scansione WinRM':
    'No agent, state from the WinRM scan',
  'Sfruttate (KEV)':
    'Exploited (KEV)',
  'Vedi le macchine non protette':
    'See the unprotected machines',
  'Volumi di sistema delle macchine Windows protetti da BitLocker: in dettaglio dove c’è l’agente CENSO, dalla scansione WinRM della sonda altrove.':
    'Operating system volumes of the Windows machines protected by BitLocker: in detail where the CENSO agent runs, from the probe’s WinRM scan elsewhere.',
  'agente CENSO':
    'CENSO agent',
  'asset critici o importanti NON monitorati':
    'critical or important assets NOT monitored',
  'asset critici o importanti monitorati':
    'critical or important assets monitored',
  'da sistemare':
    'to fix',
  'in regola':
    'in order',
  'macchine Windows senza informazioni':
    'Windows machines without information',
  'macchine con il volume di sistema NON protetto':
    'machines with the system volume NOT protected',
  'macchine con il volume di sistema protetto':
    'machines with the system volume protected',
  'protetti senza chiave di ripristino in CENSO':
    'protected without a recovery key in CENSO',
  'scansione WinRM':
    'WinRM scan',
  '{n} aree su {tot} chiedono un intervento. Clicca un numero per vedere gli asset che lo compongono.':
    '{n} areas out of {tot} need action. Click a number to see the assets behind it.',
  '{p} con BitLocker attivo, {n} senza. La scansione della sonda dice se un volume è protetto, non quali né le chiavi: per quelle serve l’agente.':
    '{p} with BitLocker on, {n} without. The probe’s scan says whether a volume is protected, not which ones nor the keys: for those the agent is needed.',
  'La panoramica dice anche perché alcune macchine Windows non hanno dati: con l’agente che non li ha ancora mandati («Chiedi l’inventario agli agenti»), senza agente ma con lo stato BitLocker dalla scansione WinRM della sonda, o senza informazioni.':
    'The overview also says why some Windows machines have no data: with the agent that has not sent it yet («Ask the agents for an inventory»), no agent but the BitLocker state from the probe’s WinRM scan, or no information.',
  'Nella sezione «Evidenze» ogni area (cifratura in transito, dati a riposo, componenti di terze parti, monitoraggio) ha l’articolo e lo stato, e ogni numero si apre sull’elenco degli asset che lo compongono, con il collegamento alla pagina dove si interviene.':
    'In the «Evidence» section each area (encryption in transit, data at rest, third-party components, monitoring) has its article and state, and every number opens the list of the assets behind it, with the link to the page where to act.',
  'A ogni passata (ogni 15 minuti) si chiede il record PTR degli indirizzi con la risposta scaduta, al più 500, i visti più di recente per primi.':
    'On each pass (every 15 minutes) the PTR record is requested for addresses whose answer has expired, up to 500, most recently seen first.',
  'Accendi “Consenti la scrittura” nella credenziale phpIPAM':
    'Turn on “Allow writing” in the phpIPAM credential',
  'Aggiunge l’indirizzo in phpIPAM, nella subnet che lo contiene, con il nome DNS e la descrizione':
    'Adds the address to phpIPAM, in the subnet that contains it, with the DNS name and the description',
  'Apri nell’inventario indirizzi: scrivi cos’è':
    'Open in the address inventory: write what it is',
  'Chiede di nuovo il nome DNS inverso, senza aspettare la scadenza della cache':
    'Requests the reverse DNS name again, without waiting for the cache to expire',
  'Con un nome DNS':
    'With a DNS name',
  'Cos’è':
    'What it is',
  'Cos’è {indirizzo}':
    'What is {indirizzo}',
  'Cos’è: Office 365, VPN del fornitore X, stampante del reparto…':
    'What it is: Office 365, vendor X VPN, department printer…',
  'Descritti da una persona':
    'Described by a person',
  'Documenta su IPAM':
    'Document in IPAM',
  'Documentato in phpIPAM nella subnet {subnet}.':
    'Documented in phpIPAM in subnet {subnet}.',
  'Documentato in phpIPAM.':
    'Documented in phpIPAM.',
  'Errori di risoluzione':
    'Resolution errors',
  'Inventario degli indirizzi IP non correlati a un CI censito: sistemi interni non inventariati e servizi esterni raggiunti dalla rete. Per ciascun indirizzo sono disponibili, quando noti, nome DNS, classificazione e informazioni descrittive.':
    'Inventory of the IP addresses not linked to an inventoried CI: internal systems nobody inventoried and external services reached by the network. For each address, when known, the DNS name, classification and descriptive information are available.',
  'Hai la sola lettura dell’inventario indirizzi: le impostazioni non si possono cambiare.':
    'You have read-only access to the address inventory: the settings cannot be changed.',
  'I nomi DNS li risolve una sonda con il DNS interno (o il server indicato nelle impostazioni della pagina), gli IP pubblici anche il Control Plane; ogni risposta resta in cache per il TTL che il DNS ha dato, fra 15 minuti e 7 giorni. «Documenta su IPAM» scrive l’indirizzo in phpIPAM con il nome e la descrizione, senza bisogno di un CI.':
    'DNS names are resolved by a probe with the internal DNS (or the server set in the page settings), public IPs also by the Control Plane; each answer stays in cache for the TTL the DNS gave, between 15 minutes and 7 days. «Document in IPAM» writes the address into phpIPAM with the name and the description, without needing a CI.',
  'I nomi restano in cache per il TTL della risposta DNS (fra 15 minuti e 7 giorni). I nomi interni li chiede una sonda, perché solo lei raggiunge il DNS della rete; per gli indirizzi pubblici, se nessuna sonda risponde, li chiede il Control Plane. Cache e impostazioni sono di questo tenant.':
    'Names stay in cache for the TTL of the DNS answer (between 15 minutes and 7 days). Internal names are requested by a probe, since only a probe reaches the network’s DNS; for public addresses, if no probe answers, the Control Plane requests them. Cache and settings belong to this tenant.',
  'I servizi internet con cui parla la rete. Spenta, si chiedono solo i nomi degli indirizzi interni.':
    'The internet services the network talks to. When off, only the names of internal addresses are requested.',
  'Il tuo ruolo non può scrivere nel modulo IPAM':
    'Your role cannot write to the IPAM module',
  'Indirizzo IP, anche con la porta (10.0.0.53:5353).':
    'IP address, optionally with the port (10.0.0.53:5353).',
  'Interni':
    'Internal',
  'Inventario indirizzi':
    'Address inventory',
  'Le righe nascono dai flussi (NetFlow, IPFIX, sFlow o la cattura della sonda), ogni 15 minuti: ogni indirizzo delle conversazioni dell’ultimo giorno che non è un CI. Con il filtro del traffico «Entrambi interni» gli indirizzi di internet non vengono tenuti.':
    'Rows come from flows (NetFlow, IPFIX, sFlow or the probe’s capture), every 15 minutes: every address in the last day’s conversations that is not a CI. With the «Both internal» traffic filter, internet addresses are not kept.',
  'Legge subito i flussi, collega gli indirizzi diventati CI e chiede i nomi DNS scaduti, senza aspettare la passata dei 15 minuti':
    'Reads the flows now, links addresses that have become CIs and requests expired DNS names, without waiting for the 15-minute pass',
  'Mostro solo: {filtro} ({n}).':
    'Showing only: {filtro} ({n}).',
  'Multicast, broadcast e indirizzi riservati non hanno un nome DNS.':
    'Multicast, broadcast and reserved addresses have no DNS name.',
  'Nessun indirizzo ancora':
    'No addresses yet',
  'Nessun indirizzo per i filtri impostati.':
    'No addresses match the filters set.',
  'Nessuna sonda in linea: i nomi interni restano da risolvere.':
    'No probe online: internal names remain unresolved.',
  'Nessuno l’ha ancora descritto.':
    'Nobody has described it yet.',
  'Nome DNS inverso (PTR)':
    'Reverse DNS name (PTR)',
  'Nomi DNS chiesti per {n} indirizzi.':
    'DNS names requested for {n} addresses.',
  'Nomi DNS chiesti per {n} indirizzi: {esito}.':
    'DNS names requested for {n} addresses: {esito}.',
  'Non documentato: {motivo}':
    'Not documented: {motivo}',
  'Ora sono CI':
    'Now inventoried as CIs',
  'Passata completata: niente di nuovo, i nomi in cache sono ancora validi.':
    'Pass completed: nothing new, cached names are still valid.',
  'Passata completata: {esito}.':
    'Pass completed: {esito}.',
  'Prima vista':
    'First seen',
  'Pubblici':
    'Public',
  'Risoluzione accesa, anche per gli indirizzi pubblici':
    'Resolution on, also for public addresses',
  'Risoluzione accesa, solo per gli indirizzi interni':
    'Resolution on, internal addresses only',
  'Risoluzione dei nomi DNS':
    'DNS name resolution',
  'Risoluzione spenta: i nomi DNS non vengono chiesti.':
    'Resolution off: DNS names are not requested.',
  'Risolvi i nomi DNS inversi':
    'Resolve reverse DNS names',
  'Risolvi i selezionati':
    'Resolve selected',
  'Risolvo…':
    'Resolving…',
  'Si risolvono i primi {max}.':
    'The first {max} are resolved.',
  'Si vede accanto all’indirizzo nelle viste del traffico: chi lo incontra dopo non deve scoprirlo di nuovo.':
    'Shown next to the address in the traffic views: whoever comes across it later won’t have to figure it out again.',
  'Sonda che chiede i nomi':
    'Probe that requests the names',
  'TTL non indicato dal DNS':
    'TTL not given by the DNS',
  'TTL {durata}':
    'TTL {durata}',
  'Ultima modifica di {chi}.':
    'Last changed by {chi}.',
  'Ultima richiesta non riuscita: {motivo}':
    'Last request failed: {motivo}',
  'anche per gli indirizzi pubblici':
    'also for public addresses',
  'che ora sono CI':
    'that are now CIs',
  'chi lo conosce: una persona, un reparto, un fornitore':
    'who knows it: a person, a department, a vendor',
  'chiesto il {data}':
    'requested on {data}',
  'con un errore di risoluzione':
    'with a resolution error',
  'con un nome DNS':
    'with a DNS name',
  'da risolvere':
    'to resolve',
  'descritti da una persona':
    'described by a person',
  'dispositivo':
    'device',
  'fonte: Control Plane':
    'source: Control Plane',
  'fonte: sonda':
    'source: probe',
  'fornitore':
    'vendor',
  'il resolver della sonda':
    'the probe’s resolver',
  'indirizzi interni':
    'internal addresses',
  'indirizzi pubblici':
    'public addresses',
  'la più recente in linea':
    'the most recent online',
  'nessun record PTR':
    'no PTR record',
  'non inventariato':
    'not inventoried',
  'non risolto':
    'unresolved',
  'nuovo tentativo dopo il {data}':
    'retry after {data}',
  'ogni indirizzo che compare nel traffico e non è un CI: gli interni che nessuno ha inventariato e i servizi su Internet con cui la rete parla, con prima e ultima volta visto, le porte e il nome DNS inverso. Scrivi cos’è, la categoria e il responsabile: compaiono accanto all’indirizzo nelle viste del traffico, e «Non inventariato» porta qui.':
    'every address that appears in traffic and is not a CI: internal ones nobody has inventoried and the Internet services the network talks to, with first and last seen, the ports and the reverse DNS name. Write what it is, the category and the owner: they appear next to the address in the traffic views, and «Not inventoried» leads here.',
  'phpIPAM non ha detto perché':
    'phpIPAM did not say why',
  'pubblico':
    'public',
  'rete':
    'network',
  'server DNS: {server}':
    'DNS server: {server}',
  'servizio esterno':
    'external service',
  'sonda non più presente':
    'probe no longer exists',
  'sonda: {nome}':
    'probe: {nome}',
  'sospetto':
    'suspicious',
  'speciale':
    'special',
  'valido fino al {data}':
    'valid until {data}',
  'vuoto: il resolver della sonda':
    'empty: the probe’s resolver',
  '{n} indirizzi nuovi':
    '{n} new addresses',
  '{n} indirizzi selezionati':
    '{n} addresses selected',
  '{n} nomi DNS trovati':
    '{n} DNS names found',
  '{n} non risolti':
    '{n} unresolved',
  '{n} ora sono CI':
    '{n} are now CIs',
  '{n} senza record PTR':
    '{n} without a PTR record',
  '{n} tolti perché non visti da 90 giorni':
    '{n} removed as not seen for 90 days',
  'Risolvi il nome DNS':
    'Resolve the DNS name',
  'Ultima modifica di {chi} il {quando}.':
    'Last changed by {chi} on {quando}.',
  'Nessun destinatario: la regola non ha indirizzi e i destinatari generali sono vuoti.':
    'No recipients: the rule has no addresses and the general recipients are empty.',
  'Invio non riuscito: controlla la posta in uscita in Impostazioni.':
    'Sending failed: check the outbound mail in Settings.',
  'Alcuni destinatari non hanno ricevuto l’avviso.':
    'Some recipients did not receive the alert.',
  'Il registro dei certificati non si è potuto leggere.':
    'The certificate register could not be read.',
  'quando ne compare uno (al primo controllo anche quelli già presenti)':
    'when one appears (at the first check, also those already present)',
  'quando ne compare uno nuovo':
    'when a new one appears',
  '{giorni} giorni prima':
    '{giorni} days before',
  'da scaduto, ogni {n} giorni':
    'after expiry, every {n} days',
  'da scaduto, una volta':
    'after expiry, once',
  'raggiungibili da altre macchine':
    'reachable from other machines',
  'anche quelli solo in locale':
    'also those local only',
  'che contengono {parole}':
    'that contain {parole}',
  'dei servizi {servizi}':
    'of the {servizi} services',
  'criticità almeno {c}':
    'criticality at least {c}',
  'i destinatari generali':
    'the general recipients',
  '{n} giorni prima':
    '{n} days before',
  'Scadenze dei certificati':
    'Certificate expiries',
  'Eliminare la regola «{nome}»? Lo storico dei suoi avvisi si perde con lei.':
    'Delete the rule “{nome}”? The history of its alerts is lost with it.',
  'Nessuna regola: nessuno viene avvisato quando un certificato sta per scadere.':
    'No rules: nobody is alerted when a certificate is about to expire.',
  'Autofirmati':
    'Self-signed',
  'Quando avvisa':
    'When it alerts',
  'Quali certificati':
    'Which certificates',
  'Chi li riceve':
    'Who receives them',
  'Ultimo avviso':
    'Last alert',
  'Spegni':
    'Turn off',
  'Accendi':
    'Turn on',
  'Le regole guardano il registro una volta l’ora. Ogni soglia parte una volta sola per certificato (la più vicina, se ne sono passate più d’una insieme) e un certificato rinnovato ricomincia da capo. Ogni destinatario riceve una sola mail con tutti i suoi certificati di quel giro.':
    'Rules check the register once an hour. Each threshold fires only once per certificate (the nearest one, if more than one has passed at the same time) and a renewed certificate starts over from scratch. Each recipient receives a single email with all their certificates from that round.',
  'Nuova regola: scadenze':
    'New rule: expiries',
  'Nuova regola: autofirmati':
    'New rule: self-signed',
  'Avvisi inviati':
    'Alerts sent',
  'Chi l’ha ricevuto':
    'Who received it',
  'Nessun avviso inviato finora.':
    'No alert sent so far.',
  'Prova mandata a {email} con {n} certificati.':
    'Test sent to {email} with {n} certificates.',
  'Avvisa per':
    'Alert for',
  'Giorni prima della scadenza':
    'Days before expiry',
  'Altro numero di giorni':
    'Other number of days',
  'Una mail a ogni soglia. Se il certificato è già oltre più soglie quando la regola nasce, parte una mail sola, per la più vicina.':
    'One email per threshold. If the certificate is already past several thresholds when the rule is created, only one email is sent, for the nearest one.',
  'Avvisa anche quando è scaduto ed è ancora installato':
    'Also alert when it is expired and still installed',
  'Un certificato scaduto che nessuno sostituisce: il caso peggiore.':
    'An expired certificate that nobody replaces: the worst case.',
  'Ripeti l’avviso da scaduto ogni (giorni)':
    'Repeat the alert after expiry every (days)',
  '0 = una volta sola.':
    '0 = only once.',
  'Al primo controllo manda anche quelli già presenti, in un riepilogo':
    'On the first check, also send those already present, in a summary',
  'Vale solo alla nascita della regola.':
    'It only applies when the rule is created.',
  'Spenta: si avvisa solo per quelli che compaiono da adesso; gli attuali sono nella scheda «Certificati autofirmati».':
    'Off: alerts are sent only for those appearing from now on; the current ones are on the “Self-signed certificates” tab.',
  'Solo quelli raggiungibili da altre macchine':
    'Only those reachable from other machines',
  'Lascia fuori i servizi in ascolto solo sulla macchina stessa (127.0.0.1).':
    'Excludes services listening only on the machine itself (127.0.0.1).',
  'Nome, SAN, emittente o CI contengono':
    'Name, SAN, issuer or CI contain',
  'vuoto: tutti':
    'empty: all',
  'Più parole separate da virgola: basta una (ospedale.it, pacs).':
    'Several words, comma-separated: one is enough (ospedale.it, pacs).',
  'Criticità del servizio almeno':
    'Service criticality at least',
  'qualsiasi':
    'any',
  'Solo i certificati di questi business service':
    'Only certificates from these business services',
  'Nessuno scelto: tutti.':
    'None chosen: all.',
  'Chi le riceve':
    'Who receives them',
  'Indirizzi email':
    'Email addresses',
  'Togli {email}':
    'Remove {email}',
  'Utenti del tenant o qualunque indirizzo (una lista di gruppo, il fornitore che gestisce il servizio).':
    'Tenant users or any address (a distribution list, the vendor managing the service).',
  'Anche i destinatari generali delle notifiche':
    'Also the general notification recipients',
  'Nessuno impostato: si impostano in Impostazioni → Posta in uscita.':
    'None set: they are set in Settings → Outbound mail.',
  'Nessun business service ha come responsabile un indirizzo email: si imposta nella scheda del servizio.':
    'No business service has an email address as its owner: it is set on the service’s page.',
  'Crea la regola':
    'Create the rule',
  'Il tuo profilo non ha un indirizzo email':
    'Your profile has no email address',
  'Manda una prova a me':
    'Send a test to myself',
  'Hai la sola lettura della Cifratura: le regole non si possono cambiare.':
    'You have read-only access to Encryption: the rules cannot be changed.',
  'Creata da {chi}; ultima modifica di {modifica}.':
    'Created by {chi}; last modified by {modifica}.',
  'Anteprima: {errore}':
    'Preview: {errore}',
  'Al prossimo controllo':
    'At the next check',
  '{n} autofirmati già presenti vengono annotati senza mandare niente; da lì in poi si avvisa per i nuovi.':
    '{n} self-signed already present are recorded without sending anything; alerts are sent for new ones from then on.',
  'Niente da mandare adesso: la regola copre {n} certificati del registro e nessuno ha passato una soglia.':
    'Nothing to send right now: the rule covers {n} certificates in the register and none has crossed a threshold.',
  'Partirebbero {n} certificati, a {m} destinatari.':
    '{n} certificates would be sent, to {m} recipients.',
  'Riceverebbero: {elenco}':
    'Would receive: {elenco}',
  'Nessuno riceverebbe questi avvisi: aggiungi un destinatario.':
    'Nobody would receive these alerts: add a recipient.',
  'Nessun avviso inviato per questo certificato.':
    'No alert sent for this certificate.',
  'Imposta le notifiche':
    'Configure notifications',
  'La scheda «Notifiche» decide chi viene avvisato e quando: ogni regola sceglie i giorni prima della scadenza (60, 30, 14, 7, 1… e da scaduto, anche ripetuto) oppure i nuovi autofirmati, quali certificati (solo quelli raggiungibili da altre macchine, parole nel nome o nell’emittente, business service, criticità) e chi li riceve: indirizzi scritti a mano o scelti fra gli utenti, i destinatari generali del tenant, il responsabile di ogni business service per i soli suoi certificati. L’anteprima dice cosa partirebbe al prossimo controllo e a chi, «Manda una prova a me» mostra la mail senza registrarla, e il «Log notifiche» — come «Avvisi inviati» nel dettaglio di ogni certificato — dice chi è stato avvisato e quando. Le mail hanno la veste di quelle di Eagle, nella lingua scelta.':
    'The “Notifications” tab decides who is alerted and when: each rule chooses the days before expiry (60, 30, 14, 7, 1… and after expiry, even repeated) or new self-signed certificates, which certificates (only those reachable from other machines, words in the name or issuer, business service, criticality) and who receives them: addresses written by hand or chosen from the users, the tenant’s general recipients, the owner of each business service for its own certificates only. The preview shows what would be sent at the next check and to whom, “Send a test to myself” displays the email without recording it, and the “Notification log” — like “Alerts sent” in each certificate’s detail — shows who was alerted and when. The emails have the same look as Eagle’s, in the chosen language.',
  'e del repository non usati ({tipi})':
    'and the unused ones in the repository ({tipi})',
  'i responsabili':
    'the owners',
  'non usato · repository':
    'not in use · repository',
  'Anche i certificati del repository che nessuna macchina usa':
    'Also the repository certificates no machine uses',
  'I certificati di Gestione certificati che nessuna macchina espone: nella mail sono segnati «non usato». Quelli già sostituiti da uno più recente con lo stesso nome non si segnalano; computer e utenti si rinnovano da soli con l’autoenrollment e sono spenti di default. Nessun tipo scelto: il repository resta fuori.':
    'The Certificate management certificates no machine exposes: the mail marks them «not in use». Those already replaced by a more recent one with the same name are not reported; computer and user certificates renew themselves through auto-enrollment and are off by default. No type chosen: the repository is left out.',
  'Anche i responsabili, ciascuno solo per i suoi certificati':
    'Also the owners, each only for their own certificates',
  'Il responsabile del business service e, per i certificati del repository, quello scritto sul certificato, quando è un indirizzo email.':
    'The business service owner and, for repository certificates, the one written on the certificate, when it is an email address.',
  'Servizi con un indirizzo email come responsabile: {elenco}':
    'Services with an email address as owner: {elenco}',
  'non usato':
    'not in use',
  'Le regole sulle scadenze guardano anche i certificati del repository di Gestione certificati che nessuna macchina espone: nella mail sono segnati «non usato» e li riceve anche il responsabile scritto sul certificato; quelli già sostituiti da uno più recente con lo stesso nome non si segnalano.':
    'Expiry rules also watch the Certificate management repository certificates no machine exposes: the mail marks them «not in use» and the owner written on the certificate receives them too; those already replaced by a more recent one with the same name are not reported.',
  'avvisi inviati':
    'alerts sent',
  'Regole attive':
    'Active rules',
  'Regole con un problema':
    'Rules with a problem',
  'Certificati segnalati negli ultimi 30 giorni':
    'Certificates reported in the last 30 days',
  'Log notifiche':
    'Notification log',
  'Regole di notifica':
    'Notification rules',
  'Crea una regola sulle scadenze o sugli autofirmati e scegli chi riceve gli avvisi.':
    'Create a rule on expiries or on self-signed certificates and choose who receives the alerts.',
  'Ogni certificato di cui una regola ha avvisato qualcuno: quando, perché e chi ha ricevuto la mail. Gli avvisi non consegnati a nessuno non compaiono: la regola li riprova al giro dopo.':
    'Every certificate a rule alerted someone about: when, why and who received the email. Alerts delivered to nobody do not appear: the rule retries them at the next round.',
  'Chi viene avvisato prima che scadano si decide nella scheda «Notifiche».':
    'Who is alerted before they expire is decided in the “Notifications” tab.',
  'Chi viene avvisato quando ne compare uno nuovo si decide nella scheda «Notifiche».':
    'Who is alerted when a new one appears is decided in the “Notifications” tab.',
  'Scarica CycloneDX di tutto il CI':
    'Download CycloneDX of the whole CI',
  ', rallentato {m} volte dal sistema':
    ', throttled {m} times by the system',
  '2000':
    '2000',
  'A) Il token di lettura':
    'A) The read token',
  'A) L’utente e il token':
    'A) The user and the token',
  "Accesa, CENSO può — sempre dopo un piano che mostra che cosa farà e con la conferma di un operatore — avviare, riprovare e annullare pipeline; aprire merge request che aggiungono alla pipeline il job che produce l'SBOM o il cancello di sicurezza, o la configurazione di Renovate; avviare il runner di Renovate su un progetto; scrivere le variabili CI del cancello. Non scrive mai sul ramo principale: ogni modifica arriva in una merge request da un ramo «censo/…», da approvare in GitLab.":
    'When on, CENSO can — always after a plan that shows what it will do and with an operator’s confirmation — start, retry and cancel pipelines; open merge requests that add to the pipeline the job that produces the SBOM or the security gate, or the Renovate configuration; run the Renovate runner on a project; write the gate’s CI variables. It never writes to the main branch: every change arrives in a merge request from a “censo/…” branch, for approval in GitLab.',
  'Access token':
    'Access token',
  'Aggiornamento in corso…':
    'Updating…',
  'Aggiungi una connessione':
    'Add a connection',
  'Apri in GitLab':
    'Open in GitLab',
  'Apri nel tab di {fonte}':
    'Open in {fonte}’s tab',
  'Artefatti':
    'Artifacts',
  'Artefatti nei registri':
    'Artifacts in registries',
  'Artefatti pubblicati':
    'Artifacts published',
  'Artefatti pubblicati e dove girano':
    'Artifacts published and where they run',
  'Artefatto':
    'Artifact',
  'Artifactory':
    'Artifactory',
  'Auto DevOps è attivo.':
    'Auto DevOps is on.',
  'Avviata da':
    'Started by',
  'Avviso e componente':
    'Advisory and component',
  'B) Il tetto della Community Edition':
    'B) The Community Edition cap',
  'B) Il token di scrittura, solo se serve':
    'B) The write token, only if needed',
  'B) Quanto legge':
    'B) How much it reads',
  'C) Da dove si parla':
    'C) Where it is queried from',
  'CENSO interroga i repository uno alla volta con AQL e una query per volta (Artifactory ne ammette poche in parallelo), ogni due ore.':
    'CENSO queries the repositories one at a time with AQL, one query at a time (Artifactory allows only a few in parallel), every two hours.',
  'CENSO legge progetti, pipeline e l’SBOM che le pipeline producono con un token di sola lettura. Le scritture (merge request, pipeline, variabili CI) usano un secondo token e solo se accendi «Consenti la scrittura».':
    'CENSO reads projects, pipelines and the SBOM that pipelines produce with a read-only token. Writes (merge requests, pipelines, CI variables) use a second token, and only if you turn on “Allow writing”.',
  'CENSO legge, in sola lettura, gli artefatti dei repository local, le immagini Docker con il loro digest e gli SBOM CycloneDX pubblicati accanto agli artefatti; se c’è Xray, anche i suoi risultati.':
    'CENSO reads, read-only, the artifacts of local repositories, Docker images with their digest, and the CycloneDX SBOMs published alongside the artifacts; if Xray is there, also its results.',
  'CENSO legge, in sola lettura, i componenti dei repository hosted (quello che pubblicate), con i checksum, i digest delle immagini Docker e gli SBOM CycloneDX pubblicati accanto ai componenti.':
    'CENSO reads, read-only, the components of hosted repositories (what you publish), with the checksums, the digests of Docker images, and the CycloneDX SBOMs published alongside the components.',
  'CENSO non scrive mai sul ramo principale: ogni modifica arriva in una merge request da approvare. Ricorda però che un ramo nuovo fa partire la sua pipeline sui vostri runner.':
    'CENSO never writes to the main branch: every change arrives in a merge request to approve. Remember, though, that a new branch starts its pipeline on your runners.',
  'CENSO resta sotto il numero scritto in «Richieste al giorno» (2000 se vuoto) e legge i repository ogni sei ore.':
    'CENSO stays under the number written in “Requests per day” (2000 if empty) and reads the repositories every six hours.',
  'CVSS':
    'CVSS',
  'Chiavi dei repository local separate da virgola. Vuoto: tutti i repository local leggibili.':
    'Comma-separated keys of local repositories. Empty: all local repositories readable.',
  'Codice del token':
    'Token code',
  'Collega GitLab, Artifactory o Nexus dalla scheda «Connessioni»: CENSO legge in sottofondo progetti, pipeline e artefatti, e l’SBOM che le pipeline producono.':
    'Connect GitLab, Artifactory or Nexus from the “Connections” tab: CENSO reads projects, pipelines and artifacts in the background, and the SBOM that pipelines produce.',
  'Come preparo GitLab per CENSO?':
    'How do I prepare GitLab for CENSO?',
  'Come preparo JFrog Artifactory per CENSO?':
    'How do I prepare JFrog Artifactory for CENSO?',
  'Come preparo Sonatype Nexus Repository per CENSO?':
    'How do I prepare Sonatype Nexus Repository for CENSO?',
  'Commit e merge request portano «Requested-by: <utente> via CENSO»: in GitLab si vede chi l’ha chiesto.':
    'Commits and merge requests carry “Requested-by: <user> via CENSO”: in GitLab you can see who asked for it.',
  'Community/OSS: un utente dedicato con i privilegi nx-repository-view-*-*-browse e nx-repository-view-*-*-read sui repository da leggere.':
    'Community/OSS: a dedicated user with the nx-repository-view-*-*-browse and nx-repository-view-*-*-read privileges on the repositories to read.',
  'Componenti nell’SBOM':
    'Components in the SBOM',
  'Componenti non valutati':
    'Unevaluated components',
  'Con SBOM':
    'With SBOM',
  'Con SBOM dalla pipeline':
    'With SBOM from the pipeline',
  'Con librerie sfruttate attivamente':
    'With actively exploited libraries',
  'Con librerie vulnerabili':
    'With vulnerable libraries',
  'Con l’edizione Pro usa un «User token» invece della password: si revoca da solo.':
    'With the Pro edition, use a “User token” instead of the password: it revokes itself.',
  'Configurazione in':
    'Configuration in',
  'Connessioni e giri in sottofondo':
    'Connections and background passes',
  'Container in esecuzione':
    'Containers running',
  'Così come le riporta ciascuna fonte, senza ricalcoli.':
    'Exactly as each source reports them, with no recalculation.',
  'Crea un ruolo con i privilegi «nx-repository-view-*-*-browse» e «nx-repository-view-*-*-read» (oppure gli stessi limitati ai repository da leggere) e assegnalo a un utente dedicato.':
    'Create a role with the “nx-repository-view-*-*-browse” and “nx-repository-view-*-*-read” privileges (or the same limited to the repositories to read) and assign it to a dedicated user.',
  'Crea un token di gruppo (Impostazioni del gruppo → Token di accesso) con ruolo «Reporter» e scope «read_api» sui gruppi da leggere. Su GitLab.com i token di gruppo e di progetto richiedono Premium o Ultimate: altrimenti crea un utente bot dedicato, aggiungilo ai gruppi come Reporter e usa un suo token personale con lo stesso scope.':
    'Create a group token (Group Settings → Access Tokens) with the “Reporter” role and “read_api” scope on the groups to read. On GitLab.com, group and project tokens require Premium or Ultimate: otherwise create a dedicated bot user, add it to the groups as Reporter, and use its personal token with the same scope.',
  'Crea un utente dedicato (o un gruppo) con permesso «Read» sui repository local da leggere; per collegare gli artefatti alle pipeline aggiungi «Read» sui build.':
    'Create a dedicated user (or a group) with “Read” permission on the local repositories to read; to link the artifacts to the pipelines, also add “Read” on builds.',
  'D’accordo':
    'Agree',
  'Edizione Pro: il token utente (nome e codice) creato in «User token», revocabile senza toccare la password.':
    'Pro edition: the user token (name and code) created in “User token”, revocable without touching the password.',
  'Elenco troncato: si mostrano i primi 20.000.':
    'List truncated: the first 20,000 are shown.',
  'Facoltativo, con scope api: si usa solo per le scritture (merge request, pipeline, variabili CI) e solo con «Consenti la scrittura» accesa. Le letture usano sempre il token di lettura.':
    'Optional, with api scope: used only for writes (merge requests, pipelines, CI variables) and only with “Allow writing” turned on. Reads always use the read token.',
  'Facoltativo, se usate Nexus Lifecycle (IQ Server): letto con le stesse credenziali, i suoi risultati compaiono in un tab a sé.':
    'Optional, if you use Nexus Lifecycle (IQ Server): read with the same credentials, its results appear in a tab of their own.',
  'Fonte non disponibile':
    'Source not available',
  'Genera un access token per quell’utente (Administration → Identity and Access → Access Tokens) con una scadenza: è la modalità consigliata.':
    'Generate an access token for that user (Administration → Identity and Access → Access Tokens) with an expiry: this is the recommended mode.',
  'GitLab':
    'GitLab',
  'GitLab Registry':
    'GitLab Registry',
  'GitLab Ultimate':
    'GitLab Ultimate',
  'GitLab Ultimate non rilevato':
    'GitLab Ultimate not detected',
  'GitLab si rilegge ogni 30 minuti (solo i progetti con attività), Artifactory ogni 2 ore, Nexus ogni 6 ore entro il tetto di richieste giornaliero. Le chiamate partono una alla volta, dalla sonda o dal Control Plane come scelto nella connessione.':
    'GitLab is re-read every 30 minutes (only projects with activity), Artifactory every 2 hours, Nexus every 6 hours within the daily request cap. Calls go out one at a time, from the probe or the Control Plane as chosen in the connection.',
  'GitLab.com o un’istanza pubblica: dal Control Plane. Un’istanza interna: da una sonda con la capability «Software prodotto» che raggiunge l’indirizzo sulla 443.':
    'GitLab.com or a public instance: from the Control Plane. An internal instance: from a probe with the “Produced software” capability that reaches the address on port 443.',
  'Gruppi da leggere':
    'Groups to read',
  'I componenti come li dà {fonte}, senza unirli alle altre fonti: lo stesso componente visto da più fonti compare in ciascun tab, e nel «Confronto» una volta sola.':
    'The components as {fonte} gives them, without merging them with the other sources: the same component seen by several sources appears in each tab, and in “Comparison” only once.',
  'Il double check: ogni riga è un elemento, ogni colonna una fonte. «In disaccordo» = le fonti lo vedono ma con gravità o versione che corregge diverse; «una sola fonte» = gli altri non lo riportano (spesso perché non guardano lo stesso build). Un clic su una cella apre la riga nel tab della sua fonte.':
    'The double check: each row is an item, each column a source. “Disagree” = the sources see it but with a different severity or fixed version; “single source” = the others do not report it (often because they are not looking at the same build). A click on a cell opens the row in its source’s tab.',
  'Il file della pipeline è {f}.':
    'The pipeline file is {f}.',
  'Il software che l’organizzazione costruisce e pubblica: progetti e pipeline di GitLab, artefatti di Artifactory e Nexus, l’SBOM di ogni build con le sue vulnerabilità e dove l’immagine gira. Ogni fonte ha il suo tab, così com’è; «Confronto» mette le fonti una accanto all’altra.':
    'The software the organisation builds and publishes: GitLab projects and pipelines, Artifactory and Nexus artifacts, the SBOM of every build with its vulnerabilities, and where the image is running. Every source has its own tab, exactly as it is; “Comparison” puts the sources side by side.',
  'Immagini in esecuzione':
    'Images running',
  'Impronta':
    'Fingerprint',
  'In disaccordo':
    'Disagree',
  'In esecuzione su':
    'Running on',
  'Indirizzo di Artifactory':
    'Artifactory address',
  'Indirizzo di GitLab':
    'GitLab address',
  'Indirizzo di Nexus':
    'Nexus address',
  'Indirizzo di Nexus IQ':
    'Nexus IQ address',
  'JFrog Artifactory':
    'JFrog Artifactory',
  'JFrog in cloud (*.jfrog.io): dal Control Plane. Un server interno: da una sonda con la capability «Software prodotto».':
    'JFrog in the cloud (*.jfrog.io): from the Control Plane. An internal server: from a probe with the “Produced software” capability.',
  'La Community Edition ha un limite di 100.000 richieste al giorno per tutta l’istanza: superato, smette di accettare componenti nuovi e i vostri build si fermano.':
    'The Community Edition has a limit of 100,000 requests per day for the whole instance: once exceeded, it stops accepting new components and your builds stop.',
  'Le immagini Docker si leggono dal manifest (anche multi-architettura): il digest le collega ai container che CENSO vede in esecuzione sui server.':
    'Docker images are read from the manifest (including multi-architecture): the digest links them to the containers CENSO sees running on the servers.',
  'Le pipeline non lasciano un SBOM CycloneDX: aggiungi alla pipeline il job che lo produce (censo-sbom.cdx.json, oppure un report cyclonedx).':
    'The pipelines do not leave a CycloneDX SBOM: add to the pipeline the job that produces it (censo-sbom.cdx.json, or a cyclonedx report).',
  'Le vulnerabilità che il motore di CENSO trova negli SBOM dei build (catalogo OSV, sfruttate attivamente da CISA KEV), con lo stesso confronto di versioni del software installato.':
    'The vulnerabilities that CENSO’s engine finds in the build SBOMs (OSV catalogue, actively exploited per CISA KEV), with the same version comparison as installed software.',
  'Le vulnerabilità come le riporta {fonte}, con le sue parole per la gravità: niente è ricalcolato da CENSO.':
    'The vulnerabilities as {fonte} reports them, in its own words for severity: nothing is recalculated by CENSO.',
  'Letto':
    'Read',
  "Lettura in sottofondo dei sistemi dove l'organizzazione costruisce e pubblica il suo software: progetti, pipeline e SBOM di GitLab, artefatti di Artifactory e Nexus. Non apre porte e non avvia job — le chiamate escono da questa sonda attraverso il relay. Abilitala dove la sonda raggiunge quei sistemi.":
    'Background reading of the systems where the organisation builds and publishes its software: GitLab projects, pipelines and SBOM, Artifactory and Nexus artifacts. It opens no ports and starts no jobs — the calls go out from this probe through the relay. Enable it where the probe reaches those systems.',
  'MR di sicurezza aperte':
    'Open security MRs',
  'MR di sicurezza di Renovate':
    'Renovate security MRs',
  'MR di sicurezza di Renovate aperte':
    'Open Renovate security MRs',
  'Metti una scadenza al token e annotala: GitLab lo disattiva da solo alla scadenza.':
    'Set an expiry on the token and note it down: GitLab deactivates it on its own at expiry.',
  'Nessun SBOM per questo progetto':
    'No SBOM for this project',
  'Nessun artefatto.':
    'No artifacts.',
  'Nessun componente da questa fonte.':
    'No components from this source.',
  'Nessun componente in questo SBOM.':
    'No components in this SBOM.',
  'Nessun giro ancora':
    'No pass yet',
  'Nessun progetto in questo filtro.':
    'No projects in this filter.',
  'Nessuna connessione ancora':
    'No connections yet',
  'Nessuna merge request di Renovate aperta.':
    'No open Renovate merge request.',
  'Nessuna pipeline letta ancora.':
    'No pipelines read yet.',
  'Nessuna pipeline letta.':
    'No pipelines read.',
  'Nessuna vulnerabilità da questa fonte.':
    'No vulnerabilities from this source.',
  'Nexus':
    'Nexus',
  'Nexus IQ':
    'Nexus IQ',
  'Niente in questo filtro.':
    'Nothing in this filter.',
  'Nome del token':
    'Token name',
  'Nomi dei repository hosted separati da virgola. Vuoto: tutti gli hosted leggibili.':
    'Comma-separated names of hosted repositories. Empty: all hosted repositories readable.',
  'Non si sa se «Pipelines must succeed» sia acceso.':
    'It is not known whether “Pipelines must succeed” is on.',
  'Non valutati':
    'Not evaluated',
  'Ogni fonte ha il suo tab, con i suoi risultati così come li dà, anche se si ripetono: per i componenti la pipeline di GitLab, Artifactory, Nexus e la Dependency List di GitLab Ultimate; per le vulnerabilità CENSO, Xray, Nexus IQ e GitLab. «Confronto» le mette una accanto all’altra — d’accordo, in disaccordo su gravità o versione che corregge, viste da una sola fonte — e un clic sulla cella apre la riga nella sua fonte. Per ogni progetto si vedono anche Renovate (configurazione, avvisi OSV, merge request di sicurezza aperte), i rilasci con il loro SBOM scaricabile e se «Pipelines must succeed» è acceso.':
    'Every source has its own tab, with its results exactly as it gives them, even when they repeat: for components, the GitLab pipeline, Artifactory, Nexus and the GitLab Ultimate Dependency List; for vulnerabilities, CENSO, Xray, Nexus IQ and GitLab. “Comparison” puts them side by side — agree, disagree on severity or fixed version, seen by a single source — and a click on the cell opens the row in its source. For every project you also see Renovate (configuration, OSV alerts, open security merge requests), the releases with their downloadable SBOM, and whether “Pipelines must succeed” is on.',
  'Ogni tag con il suo SBOM è conservato: è la documentazione del rilascio (per il CRA, dieci anni o il periodo di supporto se più lungo).':
    'Every tag with its SBOM is kept: it is the release’s documentation (for the CRA, ten years or the support period if longer).',
  'Percorsi dei gruppi separati da virgola, sottogruppi compresi. Vuoto: tutti i progetti a cui il token di lettura ha accesso.':
    'Comma-separated group paths, including subgroups. Empty: all the projects the read token has access to.',
  'Pipeline':
    'Pipeline',
  'Pipeline GitLab':
    'GitLab pipeline',
  'Pipeline e merge':
    'Pipeline and merge',
  'Pipeline fallita':
    'Pipeline failed',
  'Progetti':
    'Projects',
  'Progetto o artefatto':
    'Project or artifact',
  'Pubblicato':
    'Published',
  "Quante richieste CENSO può fare al giorno. La Community Edition ha un tetto di 100.000 richieste al giorno per tutta l'istanza, oltre il quale smette di accettare componenti nuovi: CENSO resta sotto questo numero (vuoto: 2000).":
    'How many requests CENSO can make per day. The Community Edition has a cap of 100,000 requests per day for the whole instance, beyond which it stops accepting new components: CENSO stays under this number (empty: 2000).',
  'Ramo o tag':
    'Branch or tag',
  'Ramo principale':
    'Main branch',
  'Registri':
    'Registries',
  'Renovate':
    'Renovate',
  'Renovate non è configurato in questo progetto.':
    'Renovate is not configured in this project.',
  'Repository da leggere':
    'Repositories to read',
  'Richieste al giorno':
    'Requests per day',
  'Riferimento della fonte':
    'Source reference',
  'Rilasci (tag) con SBOM':
    'Releases (tags) with SBOM',
  'SBOM allegato':
    'SBOM attached',
  'SBOM letto':
    'SBOM read',
  'Scarica il documento originale':
    'Download the original document',
  'Senza Renovate':
    'Without Renovate',
  'Senza SBOM':
    'Without SBOM',
  'Serve il «Token di scrittura» (scope api) con ruolo Developer sui progetti per merge request e pipeline, e Maintainer per le variabili CI e per avviare pipeline con variabili (GitLab 17.1 e successivi). Attenzione: un ramo nuovo fa partire la sua pipeline sui vostri runner, con le variabili non protette, prima della revisione.':
    'You need the “Write token” (api scope) with the Developer role on the projects for merge requests and pipelines, and Maintainer for CI variables and for starting pipelines with variables (GitLab 17.1 and later). Careful: a new branch starts its pipeline on your runners, with unprotected variables, before the review.',
  'Software prodotto':
    'Produced software',
  'Software prodotto (GitLab, Artifactory, Nexus)':
    'Produced software (GitLab, Artifactory, Nexus)',
  'Solo le immagini in esecuzione sui server.':
    'Only the images running on the servers.',
  'Sonatype Nexus Repository':
    'Sonatype Nexus Repository',
  'Token di gruppo o di progetto (su GitLab.com richiede Premium o Ultimate), oppure il token personale di un utente bot dedicato: scope read_api e ruolo Reporter sui gruppi da leggere.':
    'A group or project token (on GitLab.com this requires Premium or Ultimate), or the personal token of a dedicated bot user: read_api scope and Reporter role on the groups to read.',
  'Token di lettura':
    'Read token',
  'Token di scrittura':
    'Write token',
  "URL base dell'istanza, senza /api/v4: https://gitlab.com per GitLab.com.":
    'Base URL of the instance, without /api/v4: https://gitlab.com for GitLab.com.',
  "URL base dell'istanza, senza /service/rest.":
    'Base URL of the instance, without /service/rest.',
  'URL base della piattaforma, senza /artifactory: https://<nome>.jfrog.io o il server interno.':
    'Base URL of the platform, without /artifactory: https://<name>.jfrog.io or the internal server.',
  'Ultima lettura non riuscita: {e}':
    'Last read failed: {e}',
  'Ultima pipeline':
    'Last pipeline',
  'Ultima pipeline fallita':
    'Last pipeline failed',
  'Ultimo download':
    'Last download',
  'Ultimo giro {quando}: {n} richieste{r}':
    'Last pass {quando}: {n} requests{r}',
  "Un access token (Bearer) di un utente con permesso Read sui repository da leggere; per collegare gli artefatti ai build anche Read sui build. Se c'è Xray, lo stesso token ne legge i risultati.":
    'An access token (Bearer) of a user with Read permission on the repositories to read; to link the artifacts to the builds, also Read on builds. If Xray is there, the same token reads its results too.',
  'Un secondo token con scope «api»: ruolo «Developer» per aprire merge request e avviare pipeline, «Maintainer» per scrivere le variabili CI del cancello e per avviare pipeline con variabili (da GitLab 17.1 serve un ruolo minimo per le variabili di pipeline).':
    'A second token with “api” scope: “Developer” role to open merge requests and start pipelines, “Maintainer” to write the gate’s CI variables and to start pipelines with variables (from GitLab 17.1 a minimum role is needed for pipeline variables).',
  'Un utente dedicato in sola lettura: la password viaggia a ogni richiesta.':
    'A dedicated read-only user: the password travels with every request.',
  'Una sola fonte':
    'Single source',
  'Un’istanza interna: da una sonda con la capability «Software prodotto». Un’istanza pubblica: anche dal Control Plane.':
    'An internal instance: from a probe with the “Produced software” capability. A public instance: also from the Control Plane.',
  'User token':
    'User token',
  'Vulnerabilità secondo le altre fonti':
    'Vulnerabilities according to the other sources',
  'Xray':
    'Xray',
  'Xray non rilevato su Artifactory':
    'Xray not detected on Artifactory',
  'Xray {v}':
    'Xray {v}',
  'artefatti':
    'artifacts',
  'artefatto':
    'artifact',
  'avvisi OSV accesi':
    'OSV alerts on',
  'avvisi OSV non determinabili':
    'OSV alerts undetermined',
  'avvisi OSV non determinabili (preset o configurazione del runner)':
    'OSV alerts undetermined (preset or runner configuration)',
  'avvisi OSV spenti':
    'OSV alerts off',
  'avvisi OSV spenti: Renovate non apre MR per le vulnerabilità':
    'OSV alerts off: Renovate does not open MRs for vulnerabilities',
  'connessione {nome}':
    'connection {nome}',
  'creata':
    'created',
  'd’accordo':
    'agree',
  'ecosistemi senza catalogo':
    'ecosystems with no catalogue',
  'ecosistemi senza catalogo: non vuol dire «nessuna vulnerabilità»':
    'ecosystems with no catalogue: does not mean “no vulnerabilities”',
  'giro in corso':
    'pass in progress',
  'giro richiesto':
    'pass requested',
  'https://acme.jfrog.io':
    'https://acme.jfrog.io',
  'https://gitlab.example.com':
    'https://gitlab.example.com',
  'https://iq.example.com':
    'https://iq.example.com',
  'https://nexus.example.com':
    'https://nexus.example.com',
  'il software che l’organizzazione costruisce e pubblica. Collega GitLab (con un token di sola lettura), JFrog Artifactory e Sonatype Nexus dalla scheda «Connessioni»: CENSO legge in sottofondo progetti, pipeline e tag, l’SBOM CycloneDX che i job lasciano negli artefatti, quello pubblicato accanto agli artefatti nei registri, i digest delle immagini — e dice su quali server quell’immagine sta girando. Le vulnerabilità degli SBOM le valuta lo stesso motore del software installato; i componenti di ecosistemi senza catalogo sono contati come «non valutati», mai come sicuri.':
    'the software the organisation builds and publishes. Connect GitLab (with a read-only token), JFrog Artifactory and Sonatype Nexus from the “Connections” tab: CENSO reads projects, pipelines and tags in the background, the CycloneDX SBOM that jobs leave in the artifacts, the one published alongside the artifacts in the registries, the image digests — and says on which servers that image is running. The SBOMs’ vulnerabilities are assessed by the same engine as installed software; components from ecosystems with no catalogue are counted as “not evaluated”, never as safe.',
  'letto {quando}':
    'read {quando}',
  'libs-release-local, docker-local':
    'libs-release-local, docker-local',
  'maven-releases, npm-internal, docker-hosted':
    'maven-releases, npm-internal, docker-hosted',
  'nessun SBOM da valutare':
    'no SBOM to evaluate',
  'nessun SBOM pubblicato accanto agli artefatti':
    'no SBOM published alongside the artifacts',
  'nessun SBOM pubblicato accanto ai componenti':
    'no SBOM published alongside the components',
  'nessun catalogo per questo ecosistema':
    'no catalogue for this ecosystem',
  'nessun indirizzo di Nexus IQ nella connessione Nexus':
    'no Nexus IQ address in the Nexus connection',
  'nessuna connessione Artifactory':
    'no Artifactory connection',
  'nessuna connessione GitLab':
    'no GitLab connection',
  'nessuna connessione Nexus':
    'no Nexus connection',
  'nessuna pipeline ha ancora prodotto un SBOM':
    'no pipeline has produced an SBOM yet',
  'non configurato':
    'not configured',
  'non valutato':
    'not evaluated',
  'piattaforma, prodotti/pagamenti':
    'platform, products/payments',
  'pipeline':
    'pipeline',
  'progetti':
    'projects',
  'progetto':
    'project',
  'prossimo {quando}':
    'next {quando}',
  'ramo principale':
    'main branch',
  'righe':
    'rows',
  'rilasci':
    'releases',
  'rilascio':
    'release',
  'saltata':
    'skipped',
  'senza SBOM':
    'no SBOM',
  'su {n} immagini pubblicate':
    'out of {n} images published',
  'ultimo giro non riuscito':
    'last pass failed',
  'una sola fonte':
    'single source',
  '{c} componenti':
    '{c} components',
  '{n} MR aperte':
    '{n} open MRs',
  '{n} componenti di ecosistemi senza catalogo: non valutati.':
    '{n} components from ecosystems with no catalogue: not evaluated.',
  '{n} di sicurezza':
    '{n} security',
  '{n} non valutati':
    '{n} not evaluated',
  '{n} progetti con Renovate':
    '{n} projects with Renovate',
  '{n} richieste oggi':
    '{n} requests today',
  '«Pipelines must succeed» è acceso: una pipeline fallita blocca il merge.':
    '“Pipelines must succeed” is on: a failed pipeline blocks the merge.',
  '«Pipelines must succeed» è spento: una pipeline fallita non blocca il merge.':
    '“Pipelines must succeed” is off: a failed pipeline does not block the merge.',
  'Aggiorna il job SBOM':
    'Update the SBOM job',
  'Aggiungi alla pipeline il job che produce l’SBOM':
    'Add the job that produces the SBOM to the pipeline',
  'Aggiungi il job SBOM':
    'Add the SBOM job',
  'Annulla la pipeline':
    'Cancel the pipeline',
  'Annulla la pipeline #{id}':
    'Cancel pipeline #{id}',
  'Apertura…':
    'Opening…',
  'Apri la merge request':
    'Open the merge request',
  'Avvia la pipeline':
    'Start the pipeline',
  'Avvia una pipeline':
    'Start a pipeline',
  'Azioni di CENSO in GitLab':
    'CENSO’s actions in GitLab',
  'CI Lint di GitLab':
    'GitLab’s CI Lint',
  'Chiesto da':
    'Requested by',
  'Ciò che CENSO ha fatto in GitLab su richiesta: pipeline avviate, riprovate o annullate, merge request aperte (con il loro stato, aggiornato a ogni giro). Ogni azione è anche nell’audit del tenant.':
    'What CENSO has done in GitLab on request: pipelines started, retried or cancelled, merge requests opened (with their status, updated on every pass). Every action is also in the tenant audit log.',
  'Con «Consenti la scrittura» e il token di scrittura nella connessione GitLab, dal dettaglio di un progetto si avvia, si riprova o si annulla una pipeline e si aggiunge il job che produce l’SBOM: prima un piano — che cosa partirà o si fermerà, la differenza del .gitlab-ci.yml, la verifica della CI Lint di GitLab, le conseguenze —, poi una merge request da un ramo di CENSO. Il ramo principale non si tocca mai, ogni azione resta nella scheda «Modifiche» e nell’audit.':
    'With “Allow writing” and the write token in the GitLab connection, from a project’s detail you can start, retry or cancel a pipeline and add the job that produces the SBOM: first a plan — what will start or stop, the diff of .gitlab-ci.yml, the check of GitLab’s CI Lint, the consequences — then a merge request from a CENSO branch. The main branch is never touched; every action stays in the “Changes” tab and in the audit.',
  'Conseguenze':
    'Consequences',
  'C’è già una merge request di CENSO aperta per questo:':
    'There is already an open CENSO merge request for this:',
  'Dichiaralo anche come report «cyclonedx» di GitLab':
    'Also declare it as a GitLab “cyclonedx” report',
  'Fai ripartire i job':
    'Restart the jobs',
  'Fissata per versione e per impronta (@sha256:…). Con un registro interno metti qui il suo mirror.':
    'Pinned by version and by digest (@sha256:…). With an internal registry, put its mirror here.',
  'Il job censo-sbom genera a ogni pipeline censo-sbom.cdx.json (CycloneDX 1.6) con cdxgen e lo lascia negli artefatti, da dove CENSO lo legge da solo: nessun segreto nella pipeline.':
    'The censo-sbom job generates censo-sbom.cdx.json (CycloneDX 1.6) with cdxgen on every pipeline and leaves it in the artifacts, from where CENSO reads it on its own: no secret in the pipeline.',
  'Il progetto usa Auto DevOps e non ha un .gitlab-ci.yml: crearlo spegne Auto DevOps.':
    'The project uses Auto DevOps and has no .gitlab-ci.yml: creating one turns off Auto DevOps.',
  'Il ramo nuovo fa partire la sua pipeline sui vostri runner, con le variabili non protette, prima di qualunque revisione.':
    'The new branch starts its pipeline on your runners, with unprotected variables, before any review.',
  'Immagine di cdxgen':
    'cdxgen image',
  'Installa le dipendenze per risolverle':
    'Install the dependencies to resolve them',
  'La modifica arriva in una merge request verso «{target}»: sul ramo principale non cambia niente finché non viene approvata.':
    'The change arrives in a merge request to “{target}”: nothing changes on the main branch until it is approved.',
  'La pipeline ha «workflow:rules»: il job di CENSO gira solo nelle pipeline che quelle regole creano.':
    'The pipeline has “workflow:rules”: CENSO’s job runs only in the pipelines those rules create.',
  'La scrittura è spenta per questa connessione: la accende un amministratore con «Consenti la scrittura» nella connessione GitLab.':
    'Writing is off for this connection: an administrator turns it on with “Allow writing” in the GitLab connection.',
  'L’SBOM sarà anche un report «cyclonedx» di GitLab: su Ultimate cambiano la Dependency List e le politiche di sicurezza del progetto.':
    'The SBOM will also be a GitLab “cyclonedx” report: on Ultimate, this changes the project’s Dependency List and security policies.',
  'MR aperta':
    'MR open',
  'MR approvata':
    'MR approved',
  'MR chiusa':
    'MR closed',
  'Merge request !{iid} aperta dal ramo {ramo}.':
    'Merge request !{iid} opened from branch {ramo}.',
  'Modifiche':
    'Changes',
  'Nessun job fallito da far ripartire.':
    'No failed jobs to restart.',
  'Nessun job in corso da fermare.':
    'No running jobs to stop.',
  'Nessuna azione di CENSO in GitLab finora.':
    'No CENSO action in GitLab so far.',
  'Nessuna variabile si può aggiungere da qui: una variabile scritta in console (come CI_DEBUG_TRACE) potrebbe stampare i segreti nei log dei job.':
    'No variable can be added from here: a variable written in the console (like CI_DEBUG_TRACE) could print the secrets in the job logs.',
  'Niente è ancora stato fatto.':
    'Nothing has been done yet.',
  'Niente è ancora stato scritto.':
    'Nothing has been written yet.',
  'Parte una pipeline su «{ref}» con il .gitlab-ci.yml di quel ramo: tutti i job che le sue regole prevedono, sui vostri runner.':
    'A pipeline starts on “{ref}” with that branch’s .gitlab-ci.yml: all the jobs its rules call for, on your runners.',
  'Per agire da qui serve il «Token di scrittura» nella connessione GitLab (scope api).':
    'To act from here you need the “Write token” in the GitLab connection (api scope).',
  'Pipeline #{id} annullata.':
    'Pipeline #{id} cancelled.',
  'Pipeline #{id} avviata su «{ref}».':
    'Pipeline #{id} started on “{ref}”.',
  'Pipeline #{id}: job falliti fatti ripartire.':
    'Pipeline #{id}: failed jobs restarted.',
  'Prepara il piano':
    'Prepare the plan',
  'Rami':
    'Branches',
  'Renovate: avvio mirato':
    'Renovate: targeted run',
  'Renovate: avvisi OSV':
    'Renovate: OSV alerts',
  'Renovate: configurazione':
    'Renovate: configuration',
  'Renovate: runner':
    'Renovate: runner',
  'Ripartono i job falliti o annullati di questa pipeline:':
    'This pipeline’s failed or cancelled jobs restart:',
  'Riprova':
    'Retry',
  'Riprova la pipeline #{id}':
    'Retry pipeline #{id}',
  'Si aggiorna il blocco di CENSO in {f}.':
    'CENSO’s block in {f} is updated.',
  'Si aggiunge un blocco in fondo a {f}: il resto del file non cambia.':
    'A block is added at the end of {f}: the rest of the file does not change.',
  'Si crea {f}.':
    '{f} is created.',
  'Si fermano i job in corso o in attesa di questa pipeline:':
    'This pipeline’s running or pending jobs stop:',
  'Spenta (consigliato), cdxgen legge i file di blocco così come sono. Accesa, esegue i gestori di pacchetti: servono le credenziali dei registri privati nella pipeline.':
    'Off (recommended), cdxgen reads the lock files as they are. On, it runs the package managers: you need your private registries’ credentials in the pipeline.',
  'Stage':
    'Stage',
  'Su GitLab Ultimate alimenta la Dependency List e le politiche di sicurezza del progetto: cambia ciò che GitLab vi mostra.':
    'On GitLab Ultimate it feeds the Dependency List and the project’s security policies: it changes what GitLab shows you.',
  'Tag dei runner':
    'Runner tags',
  'cancello di sicurezza':
    'security gate',
  'cdxgen installerà le dipendenze per risolverle: nella pipeline servono le credenziali dei vostri registri privati, altrimenti il job fallisce o l’SBOM è parziale.':
    'cdxgen will install the dependencies to resolve them: the pipeline needs your private registries’ credentials, otherwise the job fails or the SBOM is partial.',
  'configurazione valida':
    'valid configuration',
  'errori':
    'errors',
  'job SBOM':
    'SBOM job',
  'non eseguita':
    'not run',
  'pipeline annullata':
    'pipeline cancelled',
  'pipeline avviata':
    'pipeline started',
  'pipeline riprovata':
    'pipeline retried',
  'vuoto: qualsiasi runner':
    'empty: any runner',
  '«Pipelines must succeed» è spento: un job fallito non blocca il merge.':
    '“Pipelines must succeed” is off: a failed job does not block the merge.',
  'Accendi gli avvisi OSV':
    'Turn on OSV alerts',
  'Accendi gli avvisi di sicurezza OSV di Renovate':
    'Turn on Renovate’s OSV security alerts',
  'Aggiorna il runner di Renovate':
    'Update the Renovate runner',
  'Anche un giro pianificato sui gruppi della connessione':
    'Also a scheduled pass through the connection’s groups',
  'Avvia Renovate':
    'Start Renovate',
  'Avvia Renovate adesso':
    'Start Renovate now',
  'Avviare una pipeline con una variabile richiede il ruolo Maintainer sul progetto del runner.':
    'Starting a pipeline with a variable requires the Maintainer role on the runner’s project.',
  'Il file viene riscritto con il rientro di due spazi: la differenza mostra anche le righe solo riformattate.':
    'The file is rewritten with two-space indentation: the diff also shows the lines that were only reformatted.',
  'Il giro pianificato parte solo con una pianificazione di GitLab sul progetto del runner (CI/CD → Pianificazioni): la crei tu, con l’orario che preferisci.':
    'The scheduled pass starts only with a GitLab schedule on the runner’s project (CI/CD → Schedules): you create it, at the time you prefer.',
  "Il progetto GitLab che esegue Renovate per gli altri. Vuoto: CENSO cerca un progetto con «renovate» nel percorso che usa l'immagine o il template di Renovate.":
    'The GitLab project that runs Renovate for the others. Empty: CENSO looks for a project with “renovate” in its path that uses Renovate’s image or template.',
  'Il runner ha bisogno della variabile CI RENOVATE_TOKEN (token del bot con scope api, read_user, write_repository): CENSO non crea token, la aggiunge un Maintainer.':
    'The runner needs the CI variable RENOVATE_TOKEN (the bot’s token with api, read_user, write_repository scopes): CENSO does not create tokens, a Maintainer adds it.',
  'Immagine di Renovate':
    'Renovate image',
  'Le librerie vulnerabili citate nella merge request':
    'The vulnerable libraries listed in the merge request',
  'Nel progetto del runner {r} parte una pipeline con CENSO_RENOVATE_REPOSITORY = {p}: Renovate tratta solo questo progetto.':
    'In the runner’s project {r} a pipeline starts with CENSO_RENOVATE_REPOSITORY = {p}: Renovate handles only this project.',
  'Nessun progetto letto da questa connessione: aspetta il primo giro.':
    'No project read from this connection yet: wait for the first pass.',
  'Nessun runner di Renovate trovato.':
    'No Renovate runner found.',
  'Non si sa se RENOVATE_TOKEN ci sia (può stare nel gruppo o nell’istanza): se manca, la pipeline fallisce.':
    'It is not known whether RENOVATE_TOKEN is present (it may live in the group or the instance): if it is missing, the pipeline fails.',
  'Prepara il runner di Renovate':
    'Prepare the Renovate runner',
  'Progetto del runner':
    'Runner project',
  'Progetto del runner Renovate':
    'Renovate runner project',
  'Proponi la configurazione di Renovate':
    'Propose the Renovate configuration',
  'RENOVATE_TOKEN assente':
    'RENOVATE_TOKEN missing',
  'RENOVATE_TOKEN non c’è nel runner: la pipeline fallirà finché un Maintainer non lo aggiunge.':
    'RENOVATE_TOKEN is not in the runner: the pipeline will fail until a Maintainer adds it.',
  'RENOVATE_TOKEN non verificabile':
    'RENOVATE_TOKEN not verifiable',
  'RENOVATE_TOKEN presente':
    'RENOVATE_TOKEN present',
  'RENOVATE_TOKEN è impostato nel runner.':
    'RENOVATE_TOKEN is set in the runner.',
  'Renovate apre (o aggiorna) le sue merge request in {p}, con il suo account e le sue regole.':
    'Renovate opens (or updates) its merge requests in {p}, with its own account and rules.',
  'Renovate apre merge request solo per correggere le vulnerabilità. Spenta, propone anche tutti gli altri aggiornamenti delle dipendenze.':
    'Renovate opens merge requests only to fix vulnerabilities. Off, it also proposes all the other dependency updates.',
  'Renovate apre merge request solo quando il suo runner tratta questo progetto: dopo il merge, «Avvia Renovate adesso» o il giro pianificato del runner.':
    'Renovate opens merge requests only when its runner handles this project: after the merge, “Start Renovate now” or the runner’s scheduled pass.',
  'Renovate avviato su {p}: pipeline #{id} nel runner.':
    'Renovate started on {p}: pipeline #{id} in the runner.',
  'Renovate lavora con l’account di quel token: le sue merge request portano il suo nome.':
    'Renovate works with that token’s account: its merge requests carry its name.',
  'Renovate, con lo stesso piano in due passi: «Proponi la configurazione di Renovate» apre una merge request con renovate.json e gli avvisi di sicurezza OSV accesi (raccomandato: solo aggiornamenti di sicurezza), che nella descrizione elenca le librerie vulnerabili dell’ultimo SBOM; «Accendi gli avvisi OSV» li accende in una configurazione esistente. Renovate gira nel runner dell’organizzazione: dalla scheda «Connessioni» «Prepara il runner di Renovate» aggiunge al progetto del runner il job di avvio su richiesta di CENSO, e dal progetto «Avvia Renovate adesso» lo fa girare su quel progetto solo. Il token di Renovate resta nel runner (variabile RENOVATE_TOKEN): CENSO non lo crea e non lo legge.':
    'Renovate, with the same two-step plan: “Propose the Renovate configuration” opens a merge request with renovate.json and the OSV security alerts turned on (recommended: security updates only), which lists the vulnerable libraries from the latest SBOM in its description; “Turn on OSV alerts” turns them on in an existing configuration. Renovate runs in the organization’s runner: from the “Connections” tab, “Prepare the Renovate runner” adds the job for CENSO’s on-demand start to the runner’s project, and from the project, “Start Renovate now” runs it on that project only. Renovate’s token stays in the runner (RENOVATE_TOKEN variable): CENSO neither creates it nor reads it.',
  'Runner di Renovate':
    'Renovate runner',
  'Runner di Renovate indicato ma non trovato: controlla il percorso nella connessione.':
    'Renovate runner specified but not found: check the path in the connection.',
  'Serve il runner con l’avvio su richiesta di CENSO':
    'You need the runner with CENSO’s on-demand start',
  'Si modifica {f}.':
    '{f} is modified.',
  'Solo aggiornamenti di sicurezza (consigliato)':
    'Security updates only (recommended)',
  'Solo aggiornamenti di sicurezza: le altre proposte di aggiornamento sono spente.':
    'Security updates only: the other update proposals are off.',
  'Tutti gli aggiornamenti: Renovate proporrà anche quelli che non correggono vulnerabilità.':
    'All updates: Renovate will also propose the ones that do not fix vulnerabilities.',
  'Un job di avvio su richiesta nel progetto che esegue Renovate: parte solo quando CENSO avvia una pipeline per un progetto preciso, e tratta quello solo. Il token di Renovate resta in quel progetto.':
    'An on-demand start job in the project that runs Renovate: it starts only when CENSO starts a pipeline for a specific project, and it handles only that one. Renovate’s token stays in that project.',
  'Un progetto dedicato, per esempio «renovate-runner»: il job gira lì con il token del bot. Indicalo anche nella connessione («Progetto del runner Renovate») perché CENSO lo usi.':
    'A dedicated project, for example “renovate-runner”: the job runs there with the bot’s token. Specify it in the connection too (“Renovate runner project”) so that CENSO uses it.',
  'Un secondo job che gira quando una pianificazione di GitLab lo avvia (la pianificazione la crei tu in CI/CD → Pianificazioni) e tratta i progetti dei gruppi che hanno già una configurazione di Renovate.':
    'A second job that runs when a GitLab schedule starts it (you create the schedule in CI/CD → Schedules) and handles the projects of the groups that already have a Renovate configuration.',
  'Una merge request che accende «osvVulnerabilityAlerts» nella configurazione di Renovate esistente: senza, Renovate non apre merge request per le vulnerabilità.':
    'A merge request that turns on “osvVulnerabilityAlerts” in the existing Renovate configuration: without it, Renovate does not open merge requests for vulnerabilities.',
  'Una merge request che aggiunge renovate.json: Renovate con gli avvisi di sicurezza OSV accesi, che apre merge request per correggere le librerie vulnerabili. La descrizione elenca quelle che CENSO trova nell’ultimo SBOM.':
    'A merge request that adds renovate.json: Renovate with the OSV security alerts turned on, which opens merge requests to fix the vulnerable libraries. The description lists the ones CENSO finds in the latest SBOM.',
  'avvio su richiesta di CENSO pronto':
    'CENSO’s on-demand start ready',
  'e altre {n}':
    'and {n} more',
  'piattaforma/renovate-runner':
    'piattaforma/renovate-runner',
  'senza avvio su richiesta di CENSO':
    'without CENSO’s on-demand start',
  'sfruttata':
    'exploited',
  'Gli avvisi OSV di Renovate coprono le dipendenze dirette (npm, Maven, PyPI, Go, NuGet, Cargo, Packagist, RubyGems, Hex, Hackage): una libreria transitiva si corregge aggiornando quella che la porta.':
    'Renovate’s OSV alerts cover direct dependencies (npm, Maven, PyPI, Go, NuGet, Cargo, Packagist, RubyGems, Hex, Hackage): a transitive library is fixed by updating the one that brings it in.',
  'D) Renovate, se lo usate':
    'D) Renovate, if you use it',
  'CENSO non esegue Renovate e non ne tiene il token: lo avvia nel runner dell’organizzazione, un progetto GitLab dedicato (per esempio «renovate-runner») che lo esegue per gli altri. Scrivi il suo percorso in «Progetto del runner Renovate»; vuoto, CENSO cerca da solo un progetto con «renovate» nel percorso.':
    'CENSO does not run Renovate and does not keep its token: it starts it in the organisation’s runner, a dedicated GitLab project (for example “renovate-runner”) that runs it for the others. Write its path in “Renovate runner project”; left empty, CENSO looks by itself for a project with “renovate” in its path.',
  'Nel runner serve la variabile CI RENOVATE_TOKEN: il token di un utente bot con scope «api», «read_user» e «write_repository», membro come Developer dei progetti da aggiornare. Le merge request di Renovate portano il nome di quel bot.':
    'The runner needs the CI variable RENOVATE_TOKEN: the token of a bot user with scopes “api”, “read_user” and “write_repository”, a Developer member of the projects to update. Renovate’s merge requests carry that bot’s name.',
  'Per «Avvia Renovate adesso» il token di scrittura deve essere Maintainer sul progetto del runner: avvia una pipeline con la variabile CENSO_RENOVATE_REPOSITORY. Per sapere se RENOVATE_TOKEN c’è, anche il token di lettura deve poter elencare le variabili del runner (Maintainer); altrimenti CENSO lo dice «non verificabile».':
    'For “Start Renovate now” the write token must be Maintainer on the runner project: it starts a pipeline with the variable CENSO_RENOVATE_REPOSITORY. To know whether RENOVATE_TOKEN is there, the read token must also be able to list the runner’s variables (Maintainer); otherwise CENSO reports it as “not verifiable”.',
  'A ogni pipeline il job censo-gate chiede a CENSO il verdetto sull’SBOM che quella pipeline ha prodotto. CENSO legge pipeline e SBOM da GitLab con il token di lettura, non si fida di quello che il job gli dice, e tiene conto delle valutazioni VEX in vigore.':
    'On every pipeline, the censo-gate job asks CENSO for a verdict on the SBOM that pipeline produced. CENSO reads the pipeline and SBOM from GitLab with the read token, does not trust what the job tells it, and takes into account the VEX assessments in force.',
  'Aggiorna la valutazione VEX':
    'Update the VEX assessment',
  'Aggiungi alla pipeline il cancello di sicurezza':
    'Add the security gate to the pipeline',
  'Aggiungi il job del cancello':
    'Add the gate job',
  'Al massimo un anno.':
    'At most one year.',
  'Attiva il cancello':
    'Turn on the gate',
  'Attiva il cancello di sicurezza':
    'Turn on the security gate',
  'Avvisa (consigliato)':
    'Warn (recommended)',
  'Avvisa su':
    'Warn on',
  'Blocca':
    'Block',
  'Blocca su':
    'Block on',
  'Bloccano':
    'Blocking',
  'Bloccate':
    'Blocked',
  'CENSO (OSV, KEV)':
    'CENSO (OSV, KEV)',
  'CENSO crea il token e lo scrive come variabile CI mascherata (e nascosta dove GitLab lo consente) CENSO_GATE_TOKEN del progetto: serve il ruolo Maintainer al token di scrittura.':
    'CENSO creates the token and writes it as the project’s masked (and hidden where GitLab allows it) CI variable CENSO_GATE_TOKEN: the write token requires the Maintainer role.',
  'CENSO non ha potuto scrivere la variabile CI: copiala tu adesso.':
    'CENSO could not write the CI variable: copy it yourself now.',
  'Cambia la regola':
    'Change the rule',
  'Cancello':
    'Gate',
  'Cancello di sicurezza':
    'Security gate',
  'Cancello di sicurezza e VEX (ultimi 7 giorni)':
    'Security gate and VEX (last 7 days)',
  'Cancello spento, variabile CI tolta.':
    'Gate off, CI variable removed.',
  'Cancello spento. Togli tu la variabile CI CENSO_GATE_TOKEN dal progetto.':
    'Gate off. Remove the CI variable CENSO_GATE_TOKEN from the project yourself.',
  'Che cosa fa il cancello':
    'What the gate does',
  'Chi legge la valutazione — un collega, un cliente, un’autorità — deve poterla verificare.':
    'Whoever reads the assessment — a colleague, a customer, an authority — must be able to verify it.',
  'Con avviso':
    'With a warning',
  'Contano i suoi risultati sui componenti di questa build, se la connessione li riporta per questo progetto o per i suoi artefatti.':
    'Its results on this build’s components count, if the connection reports them for this project or its artifacts.',
  'Da correggere':
    'To fix',
  'Di chi':
    'By',
  'Dopo, aggiungi il job del cancello alla pipeline con «Aggiungi il job del cancello»: fino ad allora nessuna pipeline lo chiama.':
    'Then, add the gate job to the pipeline with “Add the gate job”: until then, no pipeline calls it.',
  'Escluse da VEX':
    'Excluded by VEX',
  'Escluse da una valutazione VEX':
    'Excluded by a VEX assessment',
  'Esporta CycloneDX VEX':
    'Export CycloneDX VEX',
  'Esporta OpenVEX':
    'Export OpenVEX',
  'GitLab non raggiungibile in quel momento':
    'GitLab unreachable at that time',
  'I runner devono raggiungere CENSO all’indirizzo del cancello (HTTPS, porta 443): se non lo raggiungono, decide la regola «se CENSO non può decidere».':
    'The runners must reach CENSO at the gate’s address (HTTPS, port 443): if they cannot reach it, the “if CENSO cannot decide” rule decides.',
  'I verdetti del cancello di sicurezza, pipeline per pipeline: il job censo-gate chiede, CENSO legge pipeline e SBOM da GitLab e decide con la regola del progetto. Il cancello si attiva dal dettaglio di ogni progetto.':
    'The security gate’s verdicts, pipeline by pipeline: the censo-gate job asks, CENSO reads the pipeline and SBOM from GitLab and decides with the project’s rule. The gate is turned on from each project’s detail.',
  'Il cancello continua a contarla: è un’annotazione.':
    'The gate still counts it: it is only an annotation.',
  'Il cancello di sicurezza, dal dettaglio del progetto: a ogni pipeline il job censo-gate chiede a CENSO un verdetto sull’SBOM di quella pipeline — superato, avviso (job arancione) o bloccato — secondo una regola: avvisa o blocca, su vulnerabilità sfruttate attivamente o anche critiche e alte, che cosa fare se CENSO non può decidere, quali fonti contano. CENSO legge da sé pipeline e SBOM da GitLab; il token del cancello vale solo per i verdetti di quel progetto e sta nella variabile CI mascherata CENSO_GATE_TOKEN. Il rapporto finisce nel log del job, con il collegamento al verdetto in CENSO.':
    'The security gate, from the project’s detail: on every pipeline the censo-gate job asks CENSO for a verdict on that pipeline’s SBOM — passed, warning (orange job) or blocked — according to a rule: warn or block, on actively exploited vulnerabilities or also critical and high ones, what to do if CENSO cannot decide, which sources count. CENSO reads the pipeline and SBOM from GitLab on its own; the gate’s token is valid only for that project’s verdicts and lives in the masked CI variable CENSO_GATE_TOKEN. The report ends up in the job log, with a link to the verdict in CENSO.',
  'Il cancello non la conta finché la valutazione è in vigore.':
    'The gate does not count it while the assessment is in force.',
  'Il cancello non è attivo. Acceso, ogni pipeline chiede a CENSO un verdetto sul proprio SBOM: le vulnerabilità sfruttate attivamente (o anche le critiche e le alte) diventano un avviso o un blocco, con il rapporto nel log del job.':
    'The gate is not on. Turned on, every pipeline asks CENSO for a verdict on its own SBOM: actively exploited vulnerabilities (or also critical and high ones) become a warning or a block, with the report in the job log.',
  'Il cancello si spegne e il token si revoca: il job censo-gate resta nel file ma non parte più senza la variabile. Continuare?':
    'The gate turns off and the token is revoked: the censo-gate job stays in the file but no longer runs without the variable. Continue?',
  'Il job censo-gate chiede a CENSO, dopo censo-sbom, il verdetto sull’SBOM di questa pipeline secondo la regola del cancello, e stampa il rapporto nel log. Il token resta nella variabile CI mascherata, mai nel file.':
    'The censo-gate job asks CENSO, after censo-sbom, for the verdict on this pipeline’s SBOM according to the gate’s rule, and prints the report in the log. The token stays in the masked CI variable, never in the file.',
  'Il job censo-gate parte dopo censo-sbom e chiede a CENSO il verdetto sull’SBOM di quella pipeline: esce con 0 se passa, 3 per un avviso (job arancione, la pipeline prosegue), 1 se blocca.':
    'The censo-gate job starts after censo-sbom and asks CENSO for the verdict on that pipeline’s SBOM: it exits with 0 if it passes, 3 for a warning (orange job, the pipeline continues), 1 if it blocks.',
  'Il job diventa arancione e la pipeline prosegue: il modo con cui cominciare.':
    'The job turns orange and the pipeline continues: the way to start out.',
  'Il job fallisce; con «Pipelines must succeed» il merge si ferma.':
    'The job fails; with “Pipelines must succeed” the merge stops.',
  'Il rapporto stampato nel log della pipeline':
    'The report printed in the pipeline log',
  'Il token attuale smette subito di funzionare: le pipeline in corso prenderanno la regola «se CENSO non può decidere». Continuare?':
    'The current token stops working immediately: pipelines in progress will take the “if CENSO cannot decide” rule. Continue?',
  'Il token del cancello':
    'The gate’s token',
  'Il token del cancello non è ancora nella variabile CI: senza, il job non parte. Attiva il cancello o rigenera il token.':
    'The gate’s token is not yet in the CI variable: without it, the job does not start. Turn on the gate or regenerate the token.',
  'Immagine di curl':
    'curl image',
  'In GitLab: Impostazioni → CI/CD → Variabili → Aggiungi variabile. Chiave CENSO_GATE_TOKEN, visibilità «Mascherata» (o «Mascherata e nascosta»), «Proteggi la variabile» spento, «Espandi il riferimento della variabile» spento.':
    'In GitLab: Settings → CI/CD → Variables → Add variable. Key CENSO_GATE_TOKEN, visibility “Masked” (or “Masked and hidden”), “Protect variable” off, “Expand variable reference” off.',
  'In scadenza':
    'Expiring soon',
  'Indirizzo che il job chiama':
    'Address the job calls',
  'La scrittura non è attiva su questa connessione: CENSO crea il token e lo mostra una volta sola, da copiare tu nella variabile CI CENSO_GATE_TOKEN (mascherata, non protetta).':
    'Writing is not on for this connection: CENSO creates the token and shows it only once, for you to copy into the CI variable CENSO_GATE_TOKEN (masked, not protected).',
  'La valutazione si revoca e resta nello storico: il cancello torna a contare la vulnerabilità. Continuare?':
    'The assessment is revoked and stays in the history: the gate starts counting the vulnerability again. Continue?',
  'La variabile non è protetta, così vale anche per i rami e le merge request: chi può far girare una pipeline può leggerla, ma con quel token ottiene solo i verdetti di questo progetto.':
    'The variable is not protected, so it is also valid for branches and merge requests: whoever can run a pipeline can read it, but with that token they only get this project’s verdicts.',
  'Lascia passare':
    'Let through',
  'Le fonti che contano':
    'The sources that count',
  'Le pipeline delle fork non hanno la variabile CENSO_GATE_TOKEN: lì il job del cancello non parte.':
    'Fork pipelines do not have the CENSO_GATE_TOKEN variable: there, the gate job does not start.',
  'Le valutazioni VEX di tutti i progetti: cosa l’organizzazione dichiara su una vulnerabilità in un suo componente (stati e giustificazioni di CycloneDX). Scadono sempre; il cancello conta di nuovo quelle scadute o revocate. Si scrivono dal dettaglio del progetto o da un verdetto del cancello.':
    'The VEX assessments of all projects: what the organization declares about a vulnerability in one of its components (CycloneDX states and justifications). They always expire; the gate counts the expired or revoked ones again. They are written from the project’s detail or from a gate verdict.',
  'Le valutazioni in vigore escono anche verso i clienti: CycloneDX VEX o OpenVEX, con gli stessi stati e le stesse spiegazioni.':
    'The assessments in force also go out to customers: CycloneDX VEX or OpenVEX, with the same states and the same explanations.',
  'Manca il dominio della console (Amministrazione → Piattaforma → Dominio): il job non saprebbe dove chiamare CENSO.':
    'The console’s domain is missing (Administration → Platform → Domain): the job would not know where to call CENSO.',
  'Modo «avvisa»: quello che bloccherebbe diventa un job arancione, la pipeline prosegue.':
    '“Warn” mode: what would block becomes an orange job, the pipeline continues.',
  'Modo «blocca»: il job fallisce e, con «Pipelines must succeed», il merge si ferma finché non si corregge o non si scrive una valutazione VEX.':
    '“Block” mode: the job fails and, with “Pipelines must succeed”, the merge stops until it is fixed or a VEX assessment is written.',
  'Nella pipeline non c’è il job censo-sbom: la stessa merge request lo aggiunge, perché il cancello legge l’SBOM che produce.':
    'The pipeline does not have the censo-sbom job: the same merge request adds it, because the gate reads the SBOM it produces.',
  'Nessun SBOM nella pipeline, GitLab o CENSO irraggiungibili: il verdetto dice sempre perché.':
    'No SBOM in the pipeline, GitLab or CENSO unreachable: the verdict always says why.',
  'Nessun progetto ha ancora il cancello attivo.':
    'No project has the gate on yet.',
  'Nessun token: rigeneralo.':
    'No token: regenerate it.',
  'Nessun verdetto finora: arriva alla prima pipeline con il job censo-gate.':
    'No verdict so far: it arrives at the first pipeline with the censo-gate job.',
  'Nessuna valutazione VEX.':
    'No VEX assessment.',
  'Non decise':
    'Undecided',
  'Nuova valutazione VEX':
    'New VEX assessment',
  'Nuova valutazione su':
    'New assessment on',
  'Per esempio un falso positivo del catalogo. Spenta, vale solo per questo progetto.':
    'For example a false positive in the catalog. Off, it applies only to this project.',
  'Per esempio: il lookup JNDI è disattivato e la classe JndiLookup è tolta dal jar (verificato nel build).':
    'For example: the JNDI lookup is disabled and the JndiLookup class is removed from the jar (verified in the build).',
  'Per tutti i progetti del tenant':
    'For all the tenant’s projects',
  'Perché non siamo interessati':
    'Why we are not affected',
  'Pipeline bloccate':
    'Blocked pipelines',
  'Pipeline con avviso':
    'Pipelines with a warning',
  'Pipeline superate':
    'Passed pipelines',
  'Progetti con il cancello':
    'Projects with the gate',
  'Quello che l’organizzazione dichiara su questa vulnerabilità in questo componente, con gli stati e le giustificazioni di CycloneDX. Scade sempre: alla scadenza il cancello torna a contarla. Ogni modifica resta nello storico.':
    'What the organization declares about this vulnerability in this component, with CycloneDX’s states and justifications. It always expires: on expiry the gate starts counting it again. Every change stays in the history.',
  'Ramo o MR':
    'Branch or MR',
  'Regola del cancello di sicurezza':
    'Security gate rule',
  'Regola salvata.':
    'Rule saved.',
  'Revocate':
    'Revoked',
  'Rigenera il token':
    'Regenerate the token',
  'Risposta':
    'Response',
  'Salva la regola':
    'Save the rule',
  'Salva la valutazione':
    'Save the assessment',
  'Scade il':
    'Expires on',
  'Scadute':
    'Expired',
  'Se CENSO non può decidere':
    'If CENSO cannot decide',
  'Sempre: il motore che valuta ogni SBOM.':
    'Always: the engine that evaluates every SBOM.',
  'Sfruttate attivamente e critiche':
    'Actively exploited and critical',
  'Sfruttate attivamente, critiche e alte':
    'Actively exploited, critical and high',
  'Si vede solo adesso: CENSO ne conserva solo l’impronta. Se lo perdi, rigeneralo.':
    'It is shown only now: CENSO keeps only its fingerprint. If you lose it, regenerate it.',
  'Solo per la versione {v}':
    'Only for version {v}',
  'Spegni il cancello':
    'Turn off the gate',
  'Spenta, vale per ogni versione del componente.':
    'Off, it applies to every version of the component.',
  'Spiegazione':
    'Explanation',
  'Superate':
    'Passed',
  'Token scritto nella variabile CI mascherata CENSO_GATE_TOKEN del progetto.':
    'Token written in the project’s masked CI variable CENSO_GATE_TOKEN.',
  'Token …{h} creato {quando}':
    'Token …{h} created {quando}',
  'VEX':
    'VEX',
  'VEX in scadenza (30 giorni)':
    'VEX expiring (30 days)',
  'VEX scadute':
    'Expired VEX',
  'Valuta (VEX)':
    'Assess (VEX)',
  'Valutazioni (VEX)':
    'Assessments (VEX)',
  'Valutazioni VEX in vigore':
    'VEX assessments in force',
  'Valutazioni VEX: per una vulnerabilità che non vi riguarda (codice non raggiungibile, configurazione non usata, falso positivo…) si scrive una valutazione con gli stati e le giustificazioni di CycloneDX, una spiegazione e una scadenza: finché è in vigore il cancello non la conta, e il rapporto lo dice. Si esportano per i clienti in CycloneDX VEX o OpenVEX; i tab «Cancello» e «VEX» raccolgono verdetti e valutazioni di tutti i progetti.':
    'VEX assessments: for a vulnerability that does not concern you (unreachable code, unused configuration, false positive…) you write an assessment with CycloneDX’s states and justifications, an explanation and an expiry: while it is in force the gate does not count it, and the report says so. They are exported for customers in CycloneDX VEX or OpenVEX; the “Gate” and “VEX” tabs collect verdicts and assessments from all projects.',
  'Vulnerabilità sfruttate attivamente (CISA KEV)':
    'Actively exploited vulnerabilities (CISA KEV)',
  'aggiornare':
    'update',
  'avvisa':
    'warn',
  'avviso':
    'warning',
  'blocca':
    'block',
  'bloccato':
    'blocked',
  'c’è un rimedio provvisorio':
    'there is a temporary workaround',
  'da copiare a mano nella variabile CI':
    'to copy by hand into the CI variable',
  'errore interno di CENSO':
    'internal CENSO error',
  'falso positivo':
    'false positive',
  'il cancello le conta di nuovo':
    'the gate counts them again',
  'il codice vulnerabile non è presente':
    'the vulnerable code is not present',
  'il codice vulnerabile non è raggiungibile':
    'the vulnerable code is not reachable',
  'il job censo-sbom non è riuscito':
    'the censo-sbom job did not succeed',
  'in analisi':
    'in triage',
  'in valutazione':
    'being assessed',
  'la pipeline non è sul commit indicato dal job':
    'the pipeline is not on the commit the job points to',
  'la valutazione non è finita in tempo':
    'the assessment did not finish in time',
  'l’SBOM della pipeline non si legge':
    'the pipeline’s SBOM cannot be read',
  'nella variabile CI scritta da CENSO':
    'in the CI variable CENSO wrote',
  'nessun SBOM, GitLab non raggiungibile…':
    'no SBOM, GitLab unreachable…',
  'nessun job della pipeline ha lasciato un SBOM CycloneDX':
    'no job in the pipeline left a CycloneDX SBOM',
  'non deciso':
    'undecided',
  'non interessato':
    'not affected',
  'non si correggerà':
    'will not be fixed',
  'non si può correggere':
    'cannot be fixed',
  'ogni versione':
    'every version',
  'pipeline non trovata nel progetto':
    'pipeline not found in the project',
  'protetto al perimetro':
    'protected at the perimeter',
  'protetto da un controllo di mitigazione':
    'protected by a mitigating control',
  'protetto dal compilatore':
    'protected by the compiler',
  'protetto in esecuzione':
    'protected at runtime',
  'risolta':
    'resolved',
  'risolta, con la provenienza (pedigree)':
    'resolved, with provenance (pedigree)',
  'scaduta il {d}':
    'expired on {d}',
  'se CENSO non può decidere':
    'if CENSO cannot decide',
  'serve un ambiente che non c’è':
    'it needs an environment that is not there',
  'serve una configurazione che non usiamo':
    'it needs a configuration we do not use',
  'serve una dipendenza che non c’è':
    'it needs a dependency that is not there',
  'sfruttabile: siamo interessati':
    'exploitable: we are affected',
  'superato':
    'passed',
  'tornare alla versione precedente':
    'roll back to the previous version',
  'tutti i progetti':
    'all projects',
  'valutazioni':
    'assessments',
  'verdetti':
    'verdicts',
  '{n} senza catalogo':
    '{n} without a catalog',
  '{v} di {n} valutati':
    '{v} of {n} assessed',
  '«Pipelines must succeed» è spento in questo progetto: il cancello fa fallire il job ma non ferma il merge.':
    '“Pipelines must succeed” is off in this project: the gate makes the job fail but does not stop the merge.',
  '— una vulnerabilità dell’SBOM —':
    '— a vulnerability in the SBOM —',
  'Aggiorna la misura':
    'Update the measure',
  'Aggiornamenti di sicurezza separati da quelli funzionali':
    'Security updates separated from functional ones',
  'Aggiornamenti firmati o verificabili, canali protetti; dove serve, distribuiti in automatico.':
    'Signed or verifiable updates, protected channels; where needed, distributed automatically.',
  'Aggiornamenti senza ritardo, gratuiti, con avvisi':
    'Updates without delay, free of charge, with advisories',
  'Aggiornamento del fabbricante':
    'Update from the manufacturer',
  'Aggiungi come rilascio':
    'Add as a release',
  'Aggiungi un rilascio':
    'Add a release',
  'Al fabbricante, senza indebito ritardo: la vulnerabilità, il prodotto e le versioni interessate.':
    'To the manufacturer, without undue delay: the vulnerability, the product and the versions affected.',
  'Almeno cinque anni, salvo un uso previsto più breve (art. 13(8)).':
    'At least five years, unless the expected use is shorter (Article 13(8)).',
  'Anche quelle risolte':
    'Even the resolved ones',
  'Anche un rilascio già sul mercato prima del CRA: gli obblighi di segnalazione valgono anche per quelli (art. 69(3)).':
    'Even a release already on the market before the CRA: the reporting obligations apply to those too (Article 69(3)).',
  'Apri il prodotto':
    'Open the product',
  'Apri la segnalazione':
    'Open the report',
  'Avvisa sul registro CRA':
    'Alert on the CRA register',
  'CENSO l’ha vista per la prima volta il {q}.':
    'CENSO first saw it on {q}.',
  'CENSO l’ha vista per la prima volta il {q}: non è per forza il momento della presa di conoscenza, ma non può essere molto dopo.':
    'CENSO first saw it on {q}: that is not necessarily the moment of becoming aware, but it cannot be much later.',
  'CISA KEV o ENISA EUVD, nei rilasci supportati':
    'CISA KEV or ENISA EUVD, in supported releases',
  'CRA':
    'CRA',
  'CRA Compliance':
    'CRA Compliance',
  'CSIRT coordinatore':
    'Coordinating CSIRT',
  'Cancello di sicurezza nelle pipeline':
    'Security gate in the pipelines',
  'Catalogo CISA delle vulnerabilità sfruttate':
    'CISA catalog of exploited vulnerabilities',
  'Che cosa deve contenere':
    'What it must contain',
  'Che cosa fare':
    'What to do',
  'Chiudi la segnalazione':
    'Close the report',
  'Ciò che i dati non misurano (allegato I, parte II): lo dichiara l’organizzazione, con una nota e il documento che lo prova.':
    'What the data does not measure (Annex I, Part II): the organization declares it, with a note and the document that proves it.',
  'Codici di due lettere: la preallerta li indica.':
    'Two-letter codes: the early warning lists them.',
  'Come importatore o distributore: informare il fabbricante senza indebito ritardo e, se il rischio è significativo, le autorità di vigilanza del mercato.':
    'As importer or distributor: inform the manufacturer without undue delay and, if the risk is significant, the market surveillance authorities.',
  'Comincia dai prodotti: quelli nel campo del CRA, il ruolo dell’organizzazione, i progetti che li costruiscono. I rilasci arrivano dai tag con SBOM o si aggiungono a mano.':
    'Start with the products: those in the scope of the CRA, the organization’s role, the projects that build them. Releases come from tags with an SBOM or are added by hand.',
  'Comunicazioni':
    'Communications',
  'Conferma: partono i termini':
    'Confirm: the deadlines start',
  'Confermo adesso quando l’organizzazione ne è venuta a conoscenza':
    'Confirm now when the organization became aware of it',
  'Contatto del fabbricante per le vulnerabilità':
    'Manufacturer’s contact for vulnerabilities',
  'Controllo in corso…':
    'Check in progress…',
  'Critica (allegato IV)':
    'Critical (Annex IV)',
  'CycloneDX':
    'CycloneDX',
  'Da quel momento partono le 24 e le 72 ore. Si può confermare anche dopo, dalla segnalazione.':
    'From that moment, the 24 and 72 hours start. It can also be confirmed later, from the report.',
  'Da valutare':
    'To assess',
  'Dagli SBOM dei rilasci supportati, valutati con lo stesso motore del resto di CENSO. «Sfruttata» vuol dire nel catalogo CISA KEV o nell’elenco ENISA EUVD: è ciò che obbliga alla segnalazione.':
    'From the SBOMs of the supported releases, assessed with the same engine as the rest of CENSO. “Exploited” means in the CISA KEV catalog or the ENISA EUVD list: that is what triggers the reporting obligation.',
  'Dalla piattaforma unica di ENISA, al CSIRT coordinatore{c} e a ENISA. Registra qui ogni invio con il riferimento ricevuto.':
    'From ENISA’s single reporting platform, to the Coordinating CSIRT{c} and to ENISA. Record every submission here with the reference received.',
  'Descrizione della vulnerabilità con gravità e impatto, informazioni su chi la sfrutta se disponibili, dettagli dell’aggiornamento di sicurezza o delle altre misure correttive (art. 14(2)(c)).':
    'Description of the vulnerability with severity and impact, information on who is exploiting it if available, details of the security update or other corrective measures (Article 14(2)(c)).',
  'Descrizione dettagliata dell’incidente con gravità e impatto, tipo di minaccia o causa probabile, misure di mitigazione applicate e in corso (art. 14(4)(c)).':
    'Detailed description of the incident with severity and impact, type of threat or probable cause, mitigating measures applied and under way (Article 14(4)(c)).',
  'Dichiarati dall’organizzazione':
    'Declared by the organization',
  'Dichiarato da {chi}':
    'Declared by {chi}',
  'Disponibile dal':
    'Available from',
  'Distributore':
    'Distributor',
  'Distribuzione sicura degli aggiornamenti':
    'Secure distribution of updates',
  'Documento di riferimento':
    'Reference document',
  "Dopo l'aggiornamento: descrizione, prodotti interessati, impatto, gravità e come rimediare, in modo chiaro e accessibile.":
    'After the update: description, affected products, impact, severity and how to remedy it, in a clear and accessible way.',
  'Dove tecnicamente possibile, le correzioni di sicurezza escono separate dalle nuove funzioni.':
    'Where technically possible, security fixes are released separately from new features.',
  'Elenco ENISA delle vulnerabilità sfruttate':
    'ENISA list of exploited vulnerabilities',
  'Fabbricante':
    'Manufacturer',
  'Fabbricante da informare':
    'Manufacturer to inform',
  'Facoltativo, ma senza SBOM CENSO non vede le vulnerabilità di questo rilascio.':
    'Optional, but without an SBOM CENSO cannot see this release’s vulnerabilities.',
  'Gli Stati membri in cui il prodotto è stato reso disponibile, se noti (art. 14(2)(a)).':
    'The Member States where the product has been made available, if known (Article 14(2)(a)).',
  'Gli obblighi di segnalazione dell’art. 14 si applicano dall’11 settembre 2026 (art. 71(2)), anche ai prodotti già sul mercato (art. 69(3)).':
    'The Article 14 reporting obligations apply from 11 September 2026 (Article 71(2)), including for products already on the market (Article 69(3)).',
  'Gli utenti interessati, e se serve tutti, sulla vulnerabilità e sulle misure per mitigarla, in formato strutturato e leggibile da macchina (art. 14(8)): l’avviso CSAF si scarica qui.':
    'Affected users, and all of them if needed, about the vulnerability and the measures to mitigate it, in a structured, machine-readable format (Article 14(8)): the CSAF advisory can be downloaded here.',
  'Ha delle segnalazioni: si mette fuori ambito, non si cancella':
    'It has reports: it is marked out of scope, not deleted',
  'I rilasci con il loro SBOM: dai tag dei progetti di «Software prodotto» (ogni tag, o proposti) o aggiunti a mano, anche quelli già sul mercato prima del CRA, con l’SBOM caricato; si conservano per dieci anni o per il periodo di supporto, e si scaricano in CycloneDX con le vulnerabilità e le valutazioni VEX.':
    'The releases with their SBOM: from the tags of «Produced software» projects (every tag, or proposed ones) or added by hand, including those already on the market before the CRA, with the SBOM uploaded; they are kept for ten years or for the support period, and are downloaded in CycloneDX with the vulnerabilities and the VEX assessments.',
  'I tag sono proposti, li conferma una persona':
    'Tags are proposed, a person confirms them',
  'I termini non sono ancora partiti: partono dal momento in cui l’organizzazione è venuta a conoscenza della vulnerabilità sfruttata o dell’incidente. Lo conferma una persona.':
    'The deadlines have not started yet: they start from the moment the organization became aware of the exploited vulnerability or the incident. A person confirms it.',
  'Identificativo (CVE…)':
    'Identifier (CVE…)',
  'Il CSIRT chiede una relazione intermedia':
    'The CSIRT requests an intermediate report',
  'Il dominio dell’editore negli avvisi CSAF.':
    'The publisher’s domain in the CSAF advisories.',
  'Il prodotto e i suoi rilasci si cancellano dal registro. Continuare?':
    'The product and its releases will be deleted from the register. Continue?',
  'Il rapporto finale segue la misura correttiva o di mitigazione: registrala prima.':
    'The final report follows the corrective or mitigating measure: record it first.',
  'Il registro del Cyber Resilience Act (Reg. UE 2024/2847) per il software che l’organizzazione mette sul mercato: prodotti e rilasci con il loro SBOM, le vulnerabilità sfruttate attivamente da valutare, le segnalazioni dell’art. 14 con i loro termini, i requisiti dell’allegato I.':
    'The Cyber Resilience Act (Regulation (EU) 2024/2847) register for the software the organization places on the market: products and releases with their SBOM, actively exploited vulnerabilities to assess, Article 14 reports with their deadlines, the Annex I requirements.',
  'Importante, classe I (allegato III)':
    'Important, class I (Annex III)',
  'Importante, classe II (allegato III)':
    'Important, class II (Annex III)',
  'Importatore':
    'Importer',
  'Incidente grave sulla sicurezza del prodotto (art. 14(4))':
    'Severe incident having an impact on the product’s security (Article 14(4))',
  'Informazione agli utenti':
    'Information to users',
  'Informazione al fabbricante':
    'Information to the manufacturer',
  'Informazione alle autorità di vigilanza del mercato':
    'Information to the market surveillance authorities',
  'Informazioni generali sul prodotto, la natura dello sfruttamento e della vulnerabilità, le misure correttive o di mitigazione prese e quelle che gli utenti possono prendere, la sensibilità delle informazioni (art. 14(2)(b)).':
    'General information about the product, the nature of the exploitation and of the vulnerability, the corrective or mitigating measures taken and those users can take, the sensitivity of the information (Article 14(2)(b)).',
  'Informazioni generali sulla natura dell’incidente, una prima valutazione, le misure correttive o di mitigazione prese e quelle che gli utenti possono prendere, la sensibilità delle informazioni (art. 14(4)(b)).':
    'General information about the nature of the incident, an initial assessment, the corrective or mitigating measures taken and those users can take, the sensitivity of the information (Article 14(4)(b)).',
  'Inviata':
    'Sent',
  'Inviata il':
    'Sent on',
  'Le correzioni di sicurezza arrivano senza indebito ritardo e senza costi, con un avviso che dice cosa fare.':
    'Security fixes arrive without undue delay and free of charge, with an advisory that says what to do.',
  'Le misure prese e quelle che gli utenti possono prendere vanno nella notifica e nel rapporto finale.':
    'The measures taken and those users can take go in the notification and the final report.',
  'Le segnalazioni dell’art. 14 alla piattaforma unica di ENISA, verso il CSIRT coordinatore: i termini partono dalla presa di conoscenza confermata da una persona.':
    'The Article 14 reports to ENISA’s single reporting platform, to the Coordinating CSIRT: the deadlines start from the awareness confirmed by a person.',
  'Le vulnerabilità sfruttate attivamente (catalogo CISA KEV o elenco ENISA EUVD) nei rilasci supportati dei prodotti nel campo del CRA, non escluse da una valutazione VEX. Ognuna va valutata: se il prodotto è davvero interessato, si apre la segnalazione; se non lo è, si scrive la VEX dal prodotto.':
    'The actively exploited vulnerabilities (CISA KEV catalog or ENISA EUVD list) in the supported releases of products in the scope of the CRA, not excluded by a VEX assessment. Each one must be assessed: if the product is really affected, the report is opened; if it is not, the VEX is written from the product.',
  'Le vulnerabilità sfruttate attivamente trovate nei rilasci supportati dei prodotti in ambito, da valutare per la segnalazione; i termini delle segnalazioni (preallerta, notifica, relazione intermedia, rapporto finale) prima che scadano e quando scadono. Gli avvisi vanno anche al responsabile del prodotto.':
    'The actively exploited vulnerabilities found in the supported releases of products in scope, to be assessed for reporting; the reports’ deadlines (early warning, notification, intermediate report, final report) before they expire and when they expire. The alerts also go to the product’s owner.',
  'Manca l’ultimo passo ({p}): per chiudere lo stesso, scrivi perché.':
    'The last step is missing ({p}): to close it anyway, write why.',
  'Mette il prodotto sul mercato col proprio nome: segnala al CSIRT coordinatore e a ENISA (art. 14).':
    'Places the product on the market under its own name: reports to the Coordinating CSIRT and to ENISA (Article 14).',
  'Mette sul mercato UE un prodotto di un fabbricante extra UE: informa il fabbricante e, se il rischio è significativo, le autorità (art. 19).':
    'Places on the EU market a product from a non-EU manufacturer: informs the manufacturer and, if the risk is significant, the authorities (Article 19).',
  'Misura correttiva o di mitigazione':
    'Corrective or mitigating measure',
  'Mitigazione':
    'Mitigation',
  'Modifica la scheda':
    'Edit the record',
  'Motivo o nota di chiusura':
    'Reason or closing note',
  'Nel campo del CRA':
    'In the scope of the CRA',
  'Nessun prodotto ancora':
    'No products yet',
  'Nessun prodotto nel campo del CRA':
    'No product in the scope of the CRA',
  'Nessun prodotto: aggiungi quelli che l’organizzazione mette sul mercato.':
    'No products: add the ones the organization places on the market.',
  'Nessun progetto in Software prodotto: i rilasci si aggiungono a mano.':
    'No project in Produced software: releases are added by hand.',
  'Nessun rilascio ancora.':
    'No release yet.',
  'Nessuna correzione prevista':
    'No fix planned',
  'Nessuna misura disponibile':
    'No measure available',
  'Nessuna novità.':
    'Nothing new.',
  'Nessuna segnalazione.':
    'No reports.',
  'Nessuna vulnerabilità nei rilasci supportati.':
    'No vulnerabilities in the supported releases.',
  'Nessuna vulnerabilità sfruttata nei rilasci supportati.':
    'No exploited vulnerabilities in the supported releases.',
  'Niente da valutare.':
    'Nothing to assess.',
  'Notifica (72 ore)':
    'Notification (72 hours)',
  'Nuova segnalazione':
    'New report',
  'Nuova segnalazione CRA':
    'New CRA report',
  'Ogni rilascio con il suo SBOM, conservato dieci anni o per il periodo di supporto se più lungo (art. 13(13)). Il CycloneDX scaricato qui ha anche le vulnerabilità e le valutazioni VEX; il documento originale resta quello pubblicato dalla pipeline.':
    'Every release with its SBOM, kept for ten years or for the support period if longer (Article 13(13)). The CycloneDX downloaded here also has the vulnerabilities and the VEX assessments; the original document remains the one published by the pipeline.',
  'Ogni tag con SBOM diventa un rilascio':
    'Every tag with an SBOM becomes a release',
  'Per esempio: aggiornare alla 1.0.1, che porta log4j-core 2.17.1; in alternativa togliere la classe JndiLookup.':
    'For example: update to 1.0.1, which brings log4j-core 2.17.1; alternatively, remove the JndiLookup class.',
  'Perché fuori dal CRA':
    'Why outside the CRA',
  'Perché questo periodo di supporto':
    'Why this support period',
  'Perché è fuori dal CRA':
    'Why it is outside the CRA',
  'Periodo di supporto definito':
    'Support period defined',
  'Politica CVD pubblicata':
    'CVD policy published',
  'Politica di divulgazione coordinata':
    'Coordinated disclosure policy',
  'Politica di divulgazione coordinata (URL)':
    'Coordinated disclosure policy (URL)',
  'Politica di divulgazione coordinata delle vulnerabilità':
    'Coordinated vulnerability disclosure policy',
  'Preallerta (24 ore)':
    'Early warning (24 hours)',
  'Prodotti':
    'Products',
  'Prodotti nel CRA':
    'Products in the CRA',
  'Prodotti senza contatto':
    'Products without a contact',
  'Prodotti senza periodo di supporto':
    'Products without a support period',
  'Progetti che lo costruiscono':
    'Projects that build it',
  'Prossimo passo':
    'Next step',
  'Prossimo termine':
    'Next deadline',
  'Pubblicazione delle vulnerabilità corrette':
    'Publication of fixed vulnerabilities',
  'Punto di contatto per le vulnerabilità':
    'Contact point for vulnerabilities',
  'Punto di contatto unico per le vulnerabilità':
    'Single point of contact for vulnerabilities',
  'Quando la misura è disponibile partono i 14 giorni del rapporto finale (art. 14(2)(c)); la versione corretta compare nell’avviso CSAF per gli utenti.':
    'When the measure is available, the final report’s 14 days start (Article 14(2)(c)); the fixed version appears in the CSAF advisory for users.',
  'Quello dello Stato membro dello stabilimento principale.':
    'That of the Member State of the main establishment.',
  'Rapporto finale (14 giorni dalla misura correttiva)':
    'Final report (14 days from the corrective measure)',
  'Rapporto finale (un mese dalla notifica)':
    'Final report (one month from the notification)',
  'Registra':
    'Record',
  'Registra la misura':
    'Record the measure',
  'Registra la richiesta':
    'Record the request',
  'Registrata il {q} da {chi}':
    'Recorded on {q} by {chi}',
  'Relazione intermedia (su richiesta del CSIRT)':
    'Intermediate report (on the CSIRT’s request)',
  'Rende disponibile un prodotto altrui: informa il fabbricante e, se il rischio è significativo, le autorità (art. 20).':
    'Makes someone else’s product available: informs the manufacturer and, if the risk is significant, the authorities (Article 20).',
  'Responsabile (email)':
    'Owner (email)',
  'Revisioni del codice, test di sicurezza, penetration test: con quale frequenza e su quali rilasci.':
    'Code reviews, security testing, penetration tests: how often and on which releases.',
  'Riceve gli avvisi del registro.':
    'Receives the register’s alerts.',
  'Ricontrolla adesso':
    'Check again now',
  'Riferimento ricevuto':
    'Reference received',
  'Rilasci':
    'Releases',
  'Rilasci dai tag':
    'Releases from tags',
  'Rilasci supportati':
    'Supported releases',
  'Rimedio provvisorio':
    'Temporary workaround',
  'Ruolo dell’organizzazione':
    'Organization’s role',
  'SBOM del rilascio (CycloneDX o SPDX, JSON)':
    'Release SBOM (CycloneDX or SPDX, JSON)',
  'SBOM di ogni rilascio supportato':
    'SBOM of every supported release',
  'Scarica l’avviso CSAF per gli utenti':
    'Download the CSAF advisory for users',
  'Scheda del prodotto':
    'Product record',
  'Se l’incidente è sospettato di essere causato da atti illeciti o malevoli e gli Stati membri in cui il prodotto è disponibile (art. 14(4)(a)).':
    'If the incident is suspected to be caused by unlawful or malicious acts, and the Member States where the product is available (Article 14(4)(a)).',
  'Segnalazione':
    'Report',
  'Segnalazioni':
    'Reports',
  'Segnalazioni nei termini':
    'Reports within deadline',
  'Senza presa di conoscenza':
    'No awareness',
  'Sfruttata secondo':
    'Exploited according to',
  'Sfruttate aperte':
    'Exploited, open',
  'Sfruttate da valutare':
    'Exploited, to assess',
  'Sito del fabbricante (https)':
    'Manufacturer’s website (https)',
  'Solo le sfruttate attivamente ({n} di {m})':
    'Only the actively exploited ones ({n} of {m})',
  'Solo se il rischio di cibersicurezza è significativo: le autorità di vigilanza del mercato degli Stati membri in cui il prodotto è disponibile.':
    'Only if the cybersecurity risk is significant: the market surveillance authorities of the Member States where the product is available.',
  'Sospetto di atti illeciti o malevoli':
    'Suspicion of unlawful or malicious acts',
  'Sostiene un software libero destinato ad attività commerciali: segnala nella misura in cui partecipa allo sviluppo (art. 24).':
    'Supports free software intended for commercial activities: reports to the extent it participates in the development (Article 24).',
  'Stati membri':
    'Member States',
  'Stati membri in cui il prodotto è disponibile':
    'Member States where the product is available',
  'Stati membri in cui è disponibile':
    'Member States where it is available',
  'Stato del rilascio':
    'Release status',
  'Steward open source':
    'Open-source software steward',
  'Supportato fino al':
    'Supported until',
  'Supporto fino al':
    'Support until',
  'Tag con SBOM non ancora nel registro:':
    'Tags with an SBOM not yet in the register:',
  'Termini scaduti':
    'Deadlines expired',
  'Test e riesami di sicurezza efficaci e regolari':
    'Effective and regular security testing and reviews',
  'Un indirizzo email o una pagina per segnalare (art. 13(17)).':
    'An email address or a page to report to (Article 13(17)).',
  'Un prodotto con elementi digitali messo a disposizione sul mercato UE. Fuori: i servizi SaaS senza componenti installati dal cliente, il software usato solo internamente, i prodotti regolati da altre norme (dispositivi medici, veicoli…).':
    'A product with digital elements made available on the EU market. Out of scope: SaaS services without components installed by the customer, software used only internally, products regulated by other rules (medical devices, vehicles…).',
  'Una politica CVD adottata e applicata: chi riceve le segnalazioni, in quanto tempo risponde, come coordina la pubblicazione.':
    'An adopted and applied CVD policy: who receives the reports, how quickly it responds, how it coordinates publication.',
  'Uscito il':
    'Released on',
  'Vai ai prodotti':
    'Go to products',
  'Versione corretta':
    'Fixed version',
  'Vista da CENSO':
    'Seen by CENSO',
  'Vulnerabilità nei rilasci supportati':
    'Vulnerabilities in the supported releases',
  'Vulnerabilità sfruttata attivamente (art. 14(2))':
    'Actively exploited vulnerability (Article 14(2))',
  'Vulnerabilità sfruttate trattate senza ritardo':
    'Exploited vulnerabilities handled without delay',
  'Vuoto: il periodo del prodotto.':
    'Empty: the product’s period.',
  'a mano, con SBOM':
    'by hand, with SBOM',
  'a mano, senza SBOM':
    'by hand, without SBOM',
  'assente':
    'absent',
  'chiusa':
    'closed',
  'confermata da {chi}':
    'confirmed by {chi}',
  'da definire':
    'to be defined',
  'da inviare':
    'to send',
  'dalla piattaforma ENISA o dal CSIRT':
    'from the ENISA platform or from the CSIRT',
  'dopo la misura correttiva':
    'after the corrective measure',
  'entro {q}':
    'by {q}',
  'esclusa da una VEX':
    'excluded by a VEX',
  'fuori dal CRA':
    'out of the CRA',
  'i tag sono proposti':
    'tags are proposed',
  'i termini non sono ancora partiti':
    'the deadlines have not started yet',
  'il CSIRT chiede una relazione intermedia':
    'the CSIRT requests an intermediate report',
  'il registro del Cyber Resilience Act (Reg. UE 2024/2847) per il software che l’organizzazione mette sul mercato (add-on CENSO CRA). I prodotti, ciascuno con il perché è dentro o fuori dal CRA, la categoria, il ruolo dell’organizzazione — fabbricante, steward open source, importatore, distributore — il periodo di supporto, il punto di contatto per le vulnerabilità, la politica di divulgazione e il CSIRT coordinatore.':
    'the Cyber Resilience Act (Regulation (EU) 2024/2847) register for the software the organization places on the market (CENSO CRA add-on). The products, each with why it is in or out of the CRA, the category, the organization’s role — manufacturer, open-source software steward, importer, distributor — the support period, the contact point for vulnerabilities, the disclosure policy and the Coordinating CSIRT.',
  'in attesa della presa di conoscenza':
    'awaiting awareness',
  'incidente grave':
    'severe incident',
  'inviata: {p}':
    'sent: {p}',
  'misura correttiva registrata':
    'corrective measure recorded',
  'nel campo':
    'in scope',
  'nel campo del CRA':
    'in the scope of the CRA',
  'nessun sospetto di atti malevoli':
    'no suspicion of malicious acts',
  'non più nei rilasci supportati':
    'no longer in the supported releases',
  'ogni tag con SBOM diventa un rilascio':
    'every tag with an SBOM becomes a release',
  'presa di conoscenza confermata':
    'awareness confirmed',
  'presa di conoscenza corretta':
    'awareness corrected',
  'prodotto CRA':
    'CRA product',
  'punto di contatto per le vulnerabilità':
    'contact point for vulnerabilities',
  'ritirato':
    'withdrawn',
  'segnalata':
    'reported',
  'segnalazione aperta':
    'open report',
  'segnalazione chiusa':
    'closed report',
  'senza termine fisso':
    'no fixed deadline',
  'solo su richiesta':
    'only on request',
  'sospetto di atti illeciti o malevoli':
    'suspicion of unlawful or malicious acts',
  'supportato':
    'supported',
  'supporto finito':
    'support ended',
  'tag {r} di {p}':
    'tag {r} of {p}',
  'tutti con SBOM':
    'all with SBOM',
  'vulnerabilità sfruttata':
    'exploited vulnerability',
  '{f}: si conserva com’è, per dieci anni o per il periodo di supporto.':
    '{f}: it is kept as is, for ten years or for the support period.',
  '{n} fuori ambito':
    '{n} out of scope',
  '{n} nuove vulnerabilità sfruttate da valutare.':
    '{n} new exploited vulnerabilities to assess.',
  '{n} senza SBOM':
    '{n} without SBOM',
  '«Da valutare»: ogni ora CENSO cerca nei rilasci supportati dei prodotti nel campo le vulnerabilità sfruttate attivamente — catalogo CISA KEV o elenco ENISA EUVD — non escluse da una VEX, e ne avvisa il responsabile. Da lì si apre la segnalazione dell’art. 14: i termini partono quando una persona conferma la presa di conoscenza (CENSO mostra a parte quando l’ha vista) — preallerta entro 24 ore, notifica entro 72, rapporto finale entro 14 giorni dalla misura correttiva o, per un incidente grave, entro un mese dalla notifica; la relazione intermedia quando la chiede il CSIRT. Ogni invio si registra con il riferimento ricevuto, ogni termine ha i suoi promemoria e la cronologia resta. Per gli utenti, l’avviso CSAF leggibile da macchina.':
    '«To assess»: every hour CENSO looks in the supported releases of the products in scope for actively exploited vulnerabilities — CISA KEV catalog or ENISA EUVD list — not excluded by a VEX, and alerts the owner. From there the Article 14 report is opened: the deadlines start when a person confirms awareness (CENSO shows separately when it saw it) — early warning within 24 hours, notification within 72, final report within 14 days of the corrective measure or, for a severe incident, within one month of the notification; the intermediate report when the CSIRT requests it. Every submission is recorded with the reference received, every deadline has its own reminders and the history remains. For users, the machine-readable CSAF advisory.',
  '«Requisiti»: l’allegato I, parte II, misurato prodotto per prodotto dove i dati lo dicono (SBOM dei rilasci, vulnerabilità trattate, segnalazioni nei termini, contatto, supporto, cancello nelle pipeline) e dichiarato dall’organizzazione dove non lo dicono (politica CVD, test, pubblicazione delle correzioni, distribuzione sicura degli aggiornamenti).':
    '«Requirements»: Annex I, Part II, measured product by product where the data says so (release SBOMs, vulnerabilities handled, reports within deadline, contact, support, gate in the pipelines) and declared by the organization where it does not (CVD policy, testing, publication of fixes, secure distribution of updates).',
  'Aree':
    'Areas',
  'Aree della console':
    'Console areas',
  'Cicli di vita e costi':
    'Lifecycles and costs',
  'Come stanno adesso i sistemi che contano: cruscotto, allarmi, notifiche.':
    'How the systems that matter are doing right now: dashboard, alarms, notifications.',
  'Compliance':
    'Compliance',
  'Conformità':
    'Compliance',
  'Cosa c’è e come è collegato: raccolta, inventario, servizi, software, traffico, integrazioni.':
    'What is there and how it is connected: collection, inventory, services, software, traffic, integrations.',
  'Difese e sorgenti':
    'Defences and sources',
  'Home':
    'Home',
  'I beni dell’organizzazione, le licenze del software e le anagrafiche.':
    'The organisation’s assets, software licences and master data.',
  'La console è divisa in aree — Discovery, Planning, Monitoring (con Eagle), Security, Asset, Contracts, Compliance, Settings: il pulsante con la griglia in alto a sinistra le mostra, e il menu di sinistra tiene solo le sezioni dell’area scelta. Si vedono le aree in cui il tuo ruolo apre almeno una pagina. Alcune pagine stanno in più aree (Patching in Security e Planning, Report in Planning e Compliance, Audit in Compliance e Settings, Licenze dei software in Asset e Contracts, Anagrafiche in Asset e Settings). Aprendo una pagina di un’altra area l’area cambia da sola, e tornando su un’area si riparte dall’ultima pagina aperta lì. La home mostra le aree e sotto la dashboard; Home e Preferiti restano in cima al menu in ogni area, e il filtro del menu e ⌘K cercano in tutte.':
    'The console is divided into areas — Discovery, Planning, Monitoring (with Eagle), Security, Asset, Contracts, Compliance, Settings: the grid button at the top left shows them, and the menu on the left keeps only the sections of the chosen area. You see the areas in which your role opens at least one page. Some pages live in more than one area (Patching in Security and Planning, Report in Planning and Compliance, Audit in Compliance and Settings, Software licences in Asset and Contracts, Master data in Asset and Settings). Opening a page of another area switches the area by itself, and going back to an area resumes from the last page opened there. The home page shows the areas and the dashboard below them; Home and Favourites stay at the top of the menu in every area, and the menu filter and ⌘K search all of them.',
  'Monitoring':
    'Monitoring',
  'NIS2, DORA e CRA, con i registri e i report che servono a dimostrarlo.':
    'NIS2, DORA and CRA, with the registers and reports that prove it.',
  'Planning':
    'Planning',
  'Postura e difese: vulnerabilità, cifratura, certificati, patching, sorgenti di sicurezza.':
    'Posture and defences: vulnerabilities, encryption, certificates, patching, security sources.',
  'Quanto margine resta, quanto costa, che cosa scade: capacity, costi cloud, cicli di vita, patching, report.':
    'How much headroom is left, what it costs, what expires: capacity, cloud costs, lifecycles, patching, reports.',
  'Registri e report':
    'Registers and reports',
  'Security':
    'Security',
  'Settings':
    'Settings',
  'Utenti e ruoli, impostazioni, chiavi API, riconoscimento, audit.':
    'Users and roles, settings, API keys, recognition, audit.',
  'Predefiniti di CENSO':
    'CENSO predefined',
  'Personalizzati':
    'Custom',
  'Nessun ruolo personalizzato: duplica un ruolo predefinito e ritoccalo, o creane uno nuovo.':
    'No custom role: duplicate a predefined role and adjust it, or create a new one.',
  'Predefinito':
    'Predefined',
  'Ruolo predefinito di CENSO: i permessi si aggiornano da soli con le nuove funzioni, ognuna col livello della sua area. Si assegna così com’è; per cambiarlo, duplicalo.':
    'CENSO predefined role: its permissions update themselves with new features, each with the level of its area. Assign it as it is; to change it, duplicate it.',
  'Duplica per personalizzarlo':
    'Duplicate to customise',
  'Ruoli predefiniti di CENSO':
    'CENSO predefined roles',
  'Team sistemi':
    'Systems team',
  'Responsabile compliance':
    'Compliance officer',
  'Auditor':
    'Auditor',
  'NOC':
    'NOC',
  'Opera su sicurezza, traffico e conformità; consulta inventario e pianificazione. Niente credenziali né impostazioni.':
    'Works on security, traffic and compliance; views inventory and planning. No credentials or settings.',
  'Opera su rete, raccolta, configurazioni degli apparati, DNS, IPAM e firewall; vede solo le credenziali di rete.':
    'Works on network, discovery, device configurations, DNS, IPAM and firewalls; sees network credentials only.',
  'Opera su server, virtualizzazione, cloud, backup, agenti, patching e certificati; consulta la rete.':
    'Works on servers, virtualisation, cloud, backup, agents, patching and certificates; views the network.',
  'Opera sul software prodotto (pipeline, SBOM, cancello, VEX) e sul registro CRA; consulta il software installato.':
    'Works on in-house software (pipelines, SBOM, gate, VEX) and on the CRA register; views installed software.',
  'Opera su asset, contratti, licenze del software e anagrafiche; consulta inventario, scadenze e costi.':
    'Works on assets, contracts, software licences and master data; views inventory, end-of-life dates and costs.',
  'Opera su NIS2, DORA e CRA e sui report; consulta tutto il resto tranne credenziali, chiavi BitLocker e impostazioni.':
    'Works on NIS2, DORA, CRA and reports; views everything else except credentials, BitLocker keys and settings.',
  'Consulta tutto in sola lettura, registro di audit compreso; niente credenziali, chiavi BitLocker né impostazioni.':
    'Views everything read-only, audit log included; no credentials, BitLocker keys or settings.',
  'Consulta inventario, servizi, apparati, indirizzi, asset e stato dei sistemi; non modifica nulla.':
    'Views inventory, services, devices, addresses, assets and system status; changes nothing.',
  'Opera sul monitoraggio (cruscotto, allarmi, notifiche); consulta inventario, rete e capacity.':
    'Works on monitoring (dashboard, alarms, notifications); views inventory, network and capacity.',
  'Ruoli predefiniti: CENSO ne porta nove pronti da assegnare — Team sicurezza, Team rete, Team sistemi, DevSecOps, Asset e contratti, Responsabile compliance, Auditor, Help desk, NOC — in cima all’elenco dei ruoli e nel selettore degli utenti. Ognuno dà un livello per area del portale, con poche eccezioni (il team di rete vede solo le credenziali di rete, l’auditor non vede le chiavi BitLocker), e le funzioni nuove arrivano da sole col livello della loro area. Non si modificano: «Duplica per personalizzarlo» ne fa una copia da cambiare.':
    'Predefined roles: CENSO ships nine ready to assign — Security team, Network team, Systems team, DevSecOps, Assets and contracts, Compliance officer, Auditor, Help desk, NOC — at the top of the role list and in the users’ role selector. Each one gives a level per portal area, with a few exceptions (the network team sees network credentials only, the auditor does not see BitLocker keys), and new features arrive on their own with the level of their area. They are not edited: «Duplicate to customise» makes a copy to change.',
  'Artifactory: un access token con permesso Read sui repository local (e sui build, per collegare gli artefatti alle pipeline); Xray si rileva da solo. Nexus: un utente con i privilegi di browse e read sui repository hosted, o un user token; l’indirizzo di Nexus IQ è facoltativo; «Richieste al giorno» tiene CENSO sotto il tetto della Community Edition.':
    'Artifactory: an access token with Read permission on the local repositories (and on builds, to link the artifacts to the pipelines); Xray is detected automatically. Nexus: a user with browse and read privileges on the hosted repositories, or a user token; the Nexus IQ address is optional; “Requests per day” keeps CENSO under the Community Edition cap.',
  'CENSO legge in sottofondo: GitLab ogni 30 minuti (solo i progetti con attività), Artifactory ogni 2 ore, Nexus ogni 6 ore; «Aggiorna» chiede un giro subito. Ogni connessione ha il riquadro «Come preparo … per CENSO?» con i passi.':
    'CENSO reads in the background: GitLab every 30 minutes (only projects with activity), Artifactory every 2 hours, Nexus every 6 hours; “Refresh” asks for a pass right away. Every connection has the “How do I prepare … for CENSO?” panel with the steps.',
  'Collegare GitLab, Artifactory e Nexus':
    'Connect GitLab, Artifactory and Nexus',
  'Da DevSecOps → Connessioni. Un’istanza su Internet si raggiunge dal Control Plane; una interna da una sonda con la capability «Software prodotto». Con un certificato di una CA interna si incolla la CA (o l’impronta SHA-256) nella connessione.':
    'From DevSecOps → Connections. An instance on the Internet is reached from the Control Plane; an internal one from a probe with the “Produced software” capability. With a certificate from an internal CA, paste the CA (or the SHA-256 fingerprint) in the connection.',
  'DevSecOps':
    'DevSecOps',
  'GitLab: un token di sola lettura (scope read_api, ruolo Reporter sui gruppi da leggere) basta per leggere tutto. Il token di scrittura (scope api) è facoltativo e si usa solo con «Consenti la scrittura» accesa: Developer per merge request e pipeline, Maintainer per la variabile del cancello e per avviare Renovate nel runner.':
    'GitLab: a read-only token (read_api scope, Reporter role on the groups to read) is enough to read everything. The write token (api scope) is optional and is used only with “Allow writing” turned on: Developer for merge requests and pipelines, Maintainer for the gate’s variable and to start Renovate in the runner.',
  'Il job censo-gate dice «CENSO non ha risposto»: i runner non raggiungono l’indirizzo della console in HTTPS; risponde 401: il token è stato rigenerato o il cancello è spento.':
    'The censo-gate job says “CENSO did not answer”: the runners do not reach the console’s address over HTTPS; it answers 401: the token has been regenerated or the gate is off.',
  'Il software che l’organizzazione costruisce e pubblica — progetti e pipeline di GitLab, artefatti di Artifactory e Nexus, il cancello di sicurezza, Renovate — sta nell’area DevSecOps: vedi la sezione DevSecOps di questa guida.':
    'The software the organisation builds and publishes — GitLab projects and pipelines, Artifactory and Nexus artifacts, the security gate, Renovate — is in the DevSecOps area: see the DevSecOps section of this guide.',
  'Il software che l’organizzazione produce: pipeline e SBOM dei build, registri, vulnerabilità, cancello di sicurezza, Renovate, VEX.':
    'The software the organisation produces: pipelines and build SBOMs, registries, vulnerabilities, the security gate, Renovate, VEX.',
  'La guida operativa completa, con i permessi passo per passo e tutti i casi: docs/operations/devsecops.md.':
    'The complete operations guide, with the permissions step by step and all the cases: docs/operations/devsecops.md.',
  'Le fonti e il confronto':
    'The sources and the comparison',
  'Le voci del menu':
    'The menu entries',
  'Modifiche in GitLab':
    'Changes in GitLab',
  'Panoramica (i numeri dell’area e le connessioni), Progetti, Pipeline, Registri (con i server su cui gira ogni immagine), Componenti e Vulnerabilità (un tab per fonte più «Confronto»), Cancello di sicurezza (i verdetti di tutte le pipeline), Valutazioni VEX, Modifiche in GitLab (ciò che CENSO ha fatto su richiesta), Connessioni, e il registro CRA, che sta anche in Compliance.':
    'Overview (the area’s numbers and the connections), Projects, Pipeline, Registries (with the servers each image is running on), Components and Vulnerabilities (one tab per source plus “Comparison”), Security gate (the verdicts of all the pipelines), VEX assessments, Changes in GitLab (what CENSO has done on request), Connections, and the CRA register, which is also in Compliance.',
  'Pipeline e job SBOM':
    'Pipeline and SBOM job',
  'Problemi frequenti':
    'Common issues',
  'Sicurezza della pipeline':
    'Pipeline security',
  'Un progetto «senza SBOM»: nessun job lascia un CycloneDX; aggiungi il job SBOM dal progetto. Componenti «non valutati»: ecosistemi senza catalogo, detti e non nascosti.':
    'A project “without SBOM”: no job leaves a CycloneDX; add the SBOM job from the project. Components “not evaluated”: ecosystems without a catalogue, named as such and not hidden.',
  'Valutazioni VEX':
    'VEX assessments',
  '«Ultimo giro non riuscito (401)»: il token è scaduto o è stato revocato. «connection reset» o nessuna risposta da un’istanza interna: la connessione parte da una sonda senza la capability «Software prodotto», o che non raggiunge l’indirizzo.':
    '“Last pass failed (401)”: the token has expired or has been revoked. “connection reset” or no response from an internal instance: the connection comes from a probe without the “Produced software” capability, or one that does not reach the address.',
  '{nomi} si collegano dall’area DevSecOps:':
    '{nomi} are connected from the DevSecOps area:',
  'DevSecOps → Connessioni':
    'DevSecOps → Connections',
  'Ricompare ovunque nello stesso momento — elenchi, conteggi della lista CI, conformità, scadenze dei software, patching — e finché vale non conta in nessuna di queste viste: un fine supporto accettato non segna la macchina fuori supporto né fuori SLA.':
    'It reappears everywhere at the same moment — lists, CI list counts, compliance, software end-of-life dates, patching — and while it holds it counts in none of these views: an accepted end of support does not mark the machine out of support or out of SLA.',
  'Aggiungi il software o il sistema che questo contratto mantiene supportato.':
    'Add the software or system this contract keeps supported.',
  'Aggiungi un’estensione':
    'Add an extension',
  'CI coperti oggi':
    'CIs covered today',
  'Calcolo…':
    'Computing…',
  'Che cosa':
    'What',
  'Copri con un contratto':
    'Cover with a contract',
  'Coprirebbe {f} reperti su {n} CI, fino al {d}.':
    'It would cover {f} findings on {n} CIs, until {d}.',
  'Dai reperti di fine supporto':
    'From the end-of-support findings',
  'Eliminare l’estensione «{nome}»? I reperti che copre tornano subito fuori supporto.':
    'Delete the extension «{nome}»? The findings it covers are out of support again at once.',
  'Finché vale, il fine supporto di questo CI non conta come rischio aperto: torna da solo alla fine dell’estensione o del contratto.':
    'While it holds, this CI’s end of support does not count as an open risk: it comes back on its own when the extension or the contract ends.',
  'Fino al':
    'Until',
  'I beni coperti dal contratto':
    'The assets the contract covers',
  'I prodotti e i sistemi che oggi risultano fuori supporto o in scadenza, con quante macchine.':
    'The products and systems that are out of support or about to be today, with how many machines.',
  'Il CI non è registrato come bene: i contratti coprono i beni, ma un’estensione del supporto può scegliere anche i CI.':
    'The CI is not registered as an asset: contracts cover assets, but a support extension can also pick CIs.',
  'Il produttore offre supporto esteso fino al {d}.':
    'The producer offers extended support until {d}.',
  'Modifica l’estensione':
    'Edit the extension',
  'Nessun contratto in vigore: registralo prima in Contratti (tipo «Supporto esteso»).':
    'No contract in force: register it first in Contracts (type «Extended support»).',
  'Nessuna estensione':
    'No extension',
  'Oggi non coprirebbe niente.':
    'Today it would cover nothing.',
  'Prima l’anteprima: dice quali reperti e quali macchine copre. Niente è scritto finché non confermi.':
    'Preview first: it says which findings and which machines it covers. Nothing is written until you confirm.',
  'Prodotto (chiave)':
    'Product (key)',
  'Registra l’estensione':
    'Register the extension',
  'Righe selezionate':
    'Selected rows',
  'Salva l’estensione':
    'Save the extension',
  'Scegli prima il prodotto dai reperti: le macchine sono quelle che lo hanno.':
    'Choose the product from the findings first: the machines are those that have it.',
  'Scegli righe dello stesso prodotto e ciclo (o dello stesso sistema).':
    'Choose rows of the same product and cycle (or of the same system).',
  'Su quali macchine':
    'On which machines',
  'Supporto esteso':
    'Extended support',
  'Supporto esteso da contratto':
    'Extended support from a contract',
  'Supporto esteso fino al':
    'Extended support until',
  'Supporto esteso in scadenza':
    'Extended support ending',
  'Tutti i CI con questo prodotto':
    'Every CI with this product',
  'Un contratto che estende il supporto oltre la data del produttore (ESU di Microsoft, ELS di Red Hat, Extended Support di Oracle…): finché vale, le macchine coperte non risultano fuori supporto ma «supporto esteso fino al …», con il contratto citato; alla fine dell’estensione, o del contratto, tornano fuori supporto da sole.':
    'A contract that extends support beyond the producer’s date (Microsoft ESU, Red Hat ELS, Oracle Extended Support…): while it holds, the covered machines are not out of support but «extended support until …», with the contract named; when the extension or the contract ends, they are out of support again on their own.',
  'Windows Server 2012 R2':
    'Windows Server 2012 R2',
  'anno di ESU, codice d’attivazione dove si trova, chi lo rinnova…':
    'ESU year, where the activation key is, who renews it…',
  'ciclo {c}':
    'cycle {c}',
  'coperto fino al {d}':
    'covered until {d}',
  'estensioni':
    'extensions',
  'fino al {d}':
    'until {d}',
  'in vigore':
    'in force',
  'le macchine dei beni del contratto, con le date di copertura di ogni bene':
    'the machines of the contract’s assets, with each asset’s coverage dates',
  'non in vigore':
    'not in force',
  'per le macchine senza un bene registrato':
    'for machines without a registered asset',
  'per un accordo a volume che vale per tutto il parco':
    'for a volume agreement that applies to the whole fleet',
  'scadenza del contratto (estensione al {d})':
    'contract end (extension until {d})',
  'scegli un contratto in vigore':
    'choose a contract in force',
  'scegli, oppure scrivi qui sotto':
    'choose, or write below',
  'supporto esteso':
    'extended support',
  'supporto esteso in scadenza':
    'extended support ending',
  '{n} CI':
    '{n} CIs',
  '{n} di {tot} scelte':
    '{n} of {tot} chosen',
  '{n} già coperti':
    '{n} already covered',
  '{n} selezionate':
    '{n} selected',
  'Un contratto può anche estendere il supporto di un software o di un sistema oltre la data del produttore (ESU di Microsoft, ELS di Red Hat…): nella scheda «Supporto esteso» scegli che cosa, fino a quando e su quali macchine, e l’anteprima dice quali reperti copre. Finché vale, quelle macchine risultano «supporto esteso fino al …» e non fuori supporto — né in conformità, né nel rischio, né nel patching — e tornano fuori supporto da sole alla fine dell’estensione o del contratto. Il rinnovo porta con sé le estensioni.':
    'A contract can also extend the support of a software or a system beyond the producer’s date (Microsoft ESU, Red Hat ELS…): in the «Extended support» tab choose what, until when and on which machines, and the preview says which findings it covers. While it holds, those machines show «extended support until …» and not out of support — neither in compliance, nor in risk, nor in patching — and they are out of support again on their own when the extension or the contract ends. Renewal carries the extensions over.',
  'Le righe coperte da un contratto di supporto esteso compaiono come «supporto esteso» con il contratto; selezionando righe dello stesso prodotto, «Copri con un contratto» crea l’estensione.':
    'Rows covered by an extended-support contract show as «extended support» with the contract; selecting rows of the same product, «Cover with a contract» creates the extension.',
  'Anagrafica certificati':
    'Certificate register',
  'L’anagrafica ha anche una voce di menu sua, «Anagrafica certificati», ed è diversa da «Gestione certificati», che riguarda i certificati richiesti alla CA dell’organizzazione.':
    'The register also has its own menu entry, «Certificate register», and it differs from «Certificate management», which is about the certificates requested from the organisation’s CA.',
  'I certificati che le macchine espongono sono in Anagrafica certificati.':
    'The certificates the machines expose are in Certificate register.',
  'Accetta come fornitori':
    'Accept as suppliers',
  'Asset forniti':
    'Assets supplied',
  'Avvisi CSAF':
    'CSAF advisories',
  'Business App sostenute':
    'Business Apps supported',
  'CENSO propone i fornitori leggendo inventario, contratti e asset: premi «Aggiorna» per il primo catalogo, poi conferma le proposte in «Da catalogare».':
    'CENSO proposes the suppliers by reading inventory, contracts and assets: press «Refresh» for the first catalogue, then confirm the proposals in «To catalogue».',
  'CI critici dove è solo installato':
    'Critical CIs where it is only installed',
  'CISA KEV o ENISA EUVD':
    'CISA KEV or ENISA EUVD',
  'Calcolata dalle Business App e dai servizi che sostiene direttamente; scegline una solo per correggerla.':
    'Computed from the Business Apps and services it directly supports; choose one only to correct it.',
  'Cessati':
    'Ceased',
  'Che cosa fornisce':
    'What it supplies',
  'Cloud ({n})':
    'Cloud ({n})',
  'Con prodotti fuori supporto':
    'With products out of support',
  'Con sfruttate':
    'With exploited',
  'Con vulnerabilità sfruttate':
    'With exploited vulnerabilities',
  'Contatto di sicurezza':
    'Security contact',
  'Contratti e asset':
    'Contracts and assets',
  'Contratti in vigore':
    'Contracts in force',
  'Crea il fornitore':
    'Create the supplier',
  'Critici':
    'Critical',
  'Critici o importanti':
    'Critical or important',
  'Da catalogare':
    'To catalogue',
  'Da catalogare ({n})':
    'To catalogue ({n})',
  'Dal registro fornitori':
    'From the supplier register',
  'Dove l’ha visto':
    'Where it was seen',
  'Dove si segnala una vulnerabilità dei suoi prodotti (PSIRT).':
    'Where a vulnerability of its products is reported (PSIRT).',
  'Eliminare il fornitore «{nome}»? I suoi nomi tornano fra le proposte; contratti e asset perdono il collegamento, non il testo del fornitore.':
    'Delete the supplier «{nome}»? Its names go back among the proposals; contracts and assets lose the link, not the supplier text.',
  'Email del referente':
    'Contact person’s email',
  'Fornitori':
    'Suppliers',
  'Fornitori ICT':
    'ICT suppliers',
  'Fornitori ICT e cloud':
    'ICT and cloud suppliers',
  'Hardware ({n})':
    'Hardware ({n})',
  'Hardware, sistemi e cloud':
    'Hardware, systems and cloud',
  'I nomi che CENSO ha trovato e che non sono ancora un fornitore, con dove li ha visti. Nomi scritti in modi diversi («Microsoft Corporation», «Microsoft Corp.») sono già uniti; i pacchetti del sistema operativo sono della distribuzione, non di chi li ha impacchettati. Accetta per creare un fornitore per ciascuno, unisci a un fornitore esistente, o ignora: un nome ignorato non torna.':
    'The names CENSO found that are not a supplier yet, with where it saw them. Names written in different ways («Microsoft Corporation», «Microsoft Corp.») are already merged; operating system packages belong to the distribution, not to whoever packaged them. Accept to create one supplier each, merge into an existing supplier, or ignore: an ignored name does not come back.',
  'I nomi con cui CENSO riconosce questo fornitore nel software installato, nei contratti e negli asset, con le scritture viste. Stacca un nome che non è di questo fornitore: torna fra le proposte al prossimo catalogo.':
    'The names CENSO recognizes this supplier by in installed software, contracts and assets, with the spellings seen. Detach a name that is not this supplier’s: it goes back among the proposals at the next catalogue.',
  'I primi {n} di {tot}.':
    'The first {n} of {tot}.',
  'I primi {n} di {tot}: usa la ricerca per gli altri.':
    'The first {n} of {tot}: use the search for the others.',
  'I programmi si riconoscono dal produttore scritto nell’inventario software (agente, sonda, cloud).':
    'Programs are recognized by the publisher written in the software inventory (agent, probe, cloud).',
  'I reperti aperti sui prodotti di questo fornitore installati qui: vulnerabilità dei pacchetti e dei programmi, fine supporto, aggiornamenti mancanti del sistema, firmware. Quelli coperti da un’eccezione (rischio accettato, supporto esteso da contratto) non compaiono.':
    'The open findings on this supplier’s products installed here: package and program vulnerabilities, end of support, missing system updates, firmware. Those covered by an exception (accepted risk, extended support from a contract) do not appear.',
  'Ignora':
    'Ignore',
  'Il collegamento al registro fornitori: CENSO lo fa da solo se il nome è già noto.':
    'The link to the supplier register: CENSO makes it on its own if the name is already known.',
  'Il registro dei fornitori con quello che gira di loro, i contratti, le scadenze e i rinnovi, le licenze del software.':
    'The supplier register with what of theirs runs here, contracts, deadlines and renewals, software licences.',
  'Il registro è vuoto':
    'The register is empty',
  'Il tuo ruolo non vede i contratti.':
    'Your role does not see contracts.',
  'LEI':
    'LEI',
  'La criticità viene da ciò che il fornitore sostiene direttamente: il suo software quando è un elemento di una Business App, il sistema operativo, l’hardware e il cloud dei CI che sono interi in una Business App o in un business service. Un programma solo installato su un server critico non rende critico chi lo pubblica.':
    'Criticality comes from what the supplier directly supports: its software when it is an element of a Business App, the operating system, hardware and cloud of the CIs that are whole in a Business App or a business service. A program merely installed on a critical server does not make its publisher critical.',
  'Le proposte da confermare sono in «Da catalogare».':
    'The proposals to confirm are in «To catalogue».',
  'Legal Entity Identifier: la chiave del registro delle informazioni DORA.':
    'Legal Entity Identifier: the key of the DORA register of information.',
  'Lingua delle mail':
    'Language of the mails',
  'Manutenzione':
    'Maintenance',
  'Modifica il fornitore':
    'Edit the supplier',
  'Nessun contratto collegato a questo fornitore.':
    'No contract linked to this supplier.',
  'Nessun fornitore qui':
    'No supplier here',
  'Nessun nome':
    'No name',
  'Nessun nome ignorato':
    'No ignored name',
  'Nessun programma':
    'No program',
  'Nessun reperto aperto':
    'No open finding',
  'Niente da catalogare':
    'Nothing to catalogue',
  'Nomi ({n})':
    'Names ({n})',
  'Nuovo fornitore':
    'New supplier',
  'Ogni nome trovato è già un fornitore o è stato ignorato.':
    'Every name found is already a supplier or has been ignored.',
  'Pagina degli avvisi di sicurezza':
    'Security advisories page',
  'Panoramica fornitori':
    'Supplier overview',
  'Partita IVA':
    'VAT number',
  'Prodotti CRA di cui è il fabbricante':
    'CRA products it manufactures',
  'Prodotti in uso':
    'Products in use',
  'Proposti':
    'Proposed',
  'Referente interno':
    'Internal contact',
  'Reperti aperti':
    'Open findings',
  'Reperto':
    'Finding',
  'Riempie nome e contatto se sono vuoti.':
    'Fills name and contact if empty.',
  'Rimetti fra i proposti':
    'Put back among the proposed',
  'Rivenditore':
    'Reseller',
  'Senza contatto di sicurezza':
    'Without security contact',
  'Servizi ICT':
    'ICT services',
  'Sistemi operativi ({n})':
    'Operating systems ({n})',
  'Sito':
    'Website',
  'Somiglia a':
    'Looks like',
  'Stacca':
    'Detach',
  'Suppliers & Contracts':
    'Suppliers & Contracts',
  'Ultimo catalogo: {quando}. CENSO lo rifà da solo ogni ora.':
    'Last catalogue: {quando}. CENSO redoes it on its own every hour.',
  'Un fornitore è ICT quando nella sua scheda ha il tipo «Servizi ICT» o «Cloud».':
    'A supplier is ICT when its page has the type «ICT services» or «Cloud».',
  'Unisci':
    'Merge',
  'Unisci a':
    'Merge into',
  'Volte':
    'Times',
  'a chi si segnala una vulnerabilità?':
    'who do you report a vulnerability to?',
  'cessato':
    'ceased',
  'dai legami diretti':
    'from the direct links',
  'dai legami diretti: {c}':
    'from the direct links: {c}',
  'dove gira qualcosa di suo':
    'where something of theirs runs',
  'hardware':
    'hardware',
  'i fornitori di servizi ICT e cloud del registro, con i contratti, le funzioni che sostengono e la loro criticità: la base del registro delle informazioni DORA e della sicurezza della catena di fornitura NIS2.':
    'the register’s ICT and cloud service suppliers, with their contracts, the functions they support and their criticality: the basis of the DORA register of information and of NIS2 supply chain security.',
  'il registro di tutte le terze parti: CENSO propone i nomi che trova (chi pubblica il software installato, chi produce l’hardware, i cloud, i fornitori scritti nei contratti e negli asset) e tu li confermi; per ogni fornitore, che cosa gira di suo, dove, con quali vulnerabilità e scadenze, e per quali funzioni dell’organizzazione.':
    'the register of every third party: CENSO proposes the names it finds (who publishes the installed software, who makes the hardware, the clouds, the suppliers written in contracts and assets) and you confirm them; for each supplier, what of theirs runs, where, with which vulnerabilities and deadlines, and for which functions of the organisation.',
  'nei loro prodotti installati qui':
    'in their products installed here',
  'nomi':
    'names',
  'nomi trovati e non ancora confermati':
    'names found and not yet confirmed',
  'programmi':
    'programs',
  'reperti':
    'findings',
  'riconosciuto dal nome, o nessuno':
    'recognized from the name, or none',
  'scheda':
    'page',
  'software installato':
    'installed software',
  'sostengono funzioni critiche o alte':
    'support critical or high functions',
  'tipo non indicato':
    'type not given',
  'unisci a…':
    'merge into…',
  '{nome} {versione}: dove è installato':
    '{nome} {versione}: where it is installed',
  '{n} fornitori creati':
    '{n} suppliers created',
  '{n} nomi di nuovo proposti':
    '{n} names proposed again',
  '{n} nomi ignorati':
    '{n} names ignored',
  '{n} nomi uniti':
    '{n} names merged',
  '{v} l’anno':
    '{v} a year',
  'il registro delle terze parti, nell’area Suppliers & Contracts. CENSO legge chi pubblica il software installato (i pacchetti del sistema operativo sono della distribuzione, non di chi li ha impacchettati), chi produce l’hardware che gestisce, i cloud, i fornitori scritti nei contratti, negli asset e nei prodotti CRA, unisce i nomi scritti in modi diversi e li propone in «Da catalogare»: accetti, unisci a un fornitore o ignori. La scheda di ogni fornitore dice che cosa gira di suo e dove, le vulnerabilità aperte dei suoi prodotti (le sfruttate per prime), i prodotti fuori supporto, l’hardware e il cloud, i contratti e gli asset; la criticità viene da ciò che sostiene direttamente nelle Business App e nei business service. Contratti, asset e prodotti CRA si collegano al fornitore da soli quando il nome è già noto, o con il selettore. I fornitori ICT e cloud sono anche in Compliance.':
    'the register of third parties, in the Suppliers & Contracts area. CENSO reads who publishes the installed software (operating system packages belong to the distribution, not to whoever packaged them), who makes the hardware it manages, the clouds, the suppliers written in contracts, assets and CRA products, merges names written in different ways and proposes them in «To catalogue»: you accept, merge into a supplier or ignore. Each supplier’s page says what of theirs runs and where, the open vulnerabilities of their products (exploited first), products out of support, hardware and cloud, contracts and assets; criticality comes from what it directly supports in Business Apps and business services. Contracts, assets and CRA products link to the supplier on their own when the name is already known, or with the selector. ICT and cloud suppliers are also in Compliance.',
  'Italiano':
    'Italian',
  'Inglese':
    'English',
  'Francese':
    'French',
  'Business App «{nome}»':
    'Business App «{nome}»',
  'Categoria di legge':
    'Legal category',
  'Critici senza rilasci':
    'Critical without releases',
  'Criticità per l’organizzazione':
    'Criticality for the organisation',
  'Da qualificare':
    'To qualify',
  'I prodotti nascono fuori dal CRA, «da qualificare»: una Business App è spesso software usato solo internamente, che il CRA non riguarda. Aprili e metti nel campo quelli che l’organizzazione mette sul mercato UE.':
    'The products are born outside the CRA, «to qualify»: a Business App is often software used only internally, which the CRA does not cover. Open them and put in scope those the organisation places on the EU market.',
  'Il prodotto è questa applicazione: la sua criticità per l’organizzazione è quella della Business App (la più alta delle sue funzionalità).':
    'The product is this application: its criticality for the organisation is the Business App’s (the highest of its functions).',
  'Importa da Business App':
    'Import from Business App',
  'Importa {n} applicazioni':
    'Import {n} applications',
  'Non è la categoria di legge (allegati III e IV): dice quanto il prodotto conta, per ordinare le cose da valutare.':
    'It is not the legal category (Annexes III and IV): it says how much the product matters, to order what to assess.',
  'Ogni Business App è già collegata a un prodotto, o non ce ne sono.':
    'Every Business App is already linked to a product, or there are none.',
  'Prodotti critici o importanti':
    'Critical or important products',
  'Un prodotto per ogni applicazione scelta, collegato: la criticità segue quella della Business App.':
    'One product per chosen application, linked: the criticality follows the Business App’s.',
  'criticità da definire':
    'criticality to define',
  'dalla Business App: {c}':
    'from the Business App: {c}',
  'importati dalle Business App: nel campo del CRA o no?':
    'imported from the Business Apps: in the CRA scope or not?',
  'per l’organizzazione: dalla Business App o scelta a mano':
    'for the organisation: from the Business App or chosen by hand',
  'senza un rilascio con SBOM il registro non può valutarli':
    'without a release with an SBOM the register cannot assess them',
  'Ogni prodotto ha anche una criticità per l’organizzazione, distinta dalla categoria di legge: viene dalla Business App collegata (la più alta delle sue funzionalità) o si sceglie a mano, e ordina le vulnerabilità da valutare e le segnalazioni. «Importa da Business App» crea un prodotto per applicazione, fuori dal CRA finché non lo qualifichi.':
    'Each product also has a criticality for the organisation, distinct from the legal category: it comes from the linked Business App (the highest of its functions) or is chosen by hand, and it orders the vulnerabilities to assess and the reports. «Import from Business App» creates one product per application, outside the CRA until you qualify it.',
  'Applica i «non interessato» ({n})':
    'Apply the «not affected» ({n})',
  'Avvisa sulle richieste ai fornitori':
    'Notify about requests to suppliers',
  'Che cosa chiedi':
    'What you ask',
  'Chiedi al fornitore':
    'Ask the supplier',
  'Chiudi la richiesta':
    'Close the request',
  'Dichiarazioni':
    'Statements',
  'Fine del supporto':
    'End of support',
  'Giustificazione':
    'Justification',
  'Il tuo messaggio, in testa alla mail':
    'Your message, at the top of the mail',
  'In copia':
    'Cc',
  'Invia al fornitore':
    'Send to the supplier',
  'La mail dice prodotti, versioni e quante installazioni: mai i nomi delle macchine.':
    'The mail says products, versions and how many installations: never the names of the machines.',
  'Lascia in bozza':
    'Keep as draft',
  'Le richieste ai fornitori: vulnerabilità dei loro prodotti trovate qui, fine supporto, SBOM, questionari. Si scrivono dalla scheda del fornitore («Chiedi al fornitore»); il sollecito parte da solo prima della scadenza e alla scadenza.':
    'The requests to suppliers: vulnerabilities of their products found here, end of support, SBOM, questionnaires. They are written from the supplier’s page («Ask the supplier»); the reminder goes out on its own before the due date and at it.',
  'Manca il contatto di sicurezza del fornitore: aggiungilo nella scheda, o scrivi il destinatario nella richiesta.':
    'The supplier’s security contact is missing: add it on the page, or write the recipient in the request.',
  'Nessuna dichiarazione: arrivano dalle risposte alle richieste.':
    'No statement: they come from the answers to requests.',
  'Nessuna richiesta.':
    'No request.',
  'Nessuna vulnerabilità aperta sui suoi prodotti: niente da chiedere.':
    'No open vulnerability on its products: nothing to ask.',
  'Niente è ancora stato inviato.':
    'Nothing has been sent yet.',
  'Perché chiudi la richiesta?':
    'Why are you closing the request?',
  'Preparo…':
    'Preparing…',
  'Quello che il fornitore ha dichiarato sui suoi prodotti («non interessato»): finché vale, toglie dai rischi aperti le stesse vulnerabilità dello stesso prodotto installato qui. Non vale per il cancello delle pipeline né per il registro CRA, che riguardano il software dell’organizzazione.':
    'What the supplier stated about its products («not affected»): while it holds, it takes the same vulnerabilities of the same product installed here out of the open risks. It does not apply to the pipeline gate nor to the CRA register, which are about the organisation’s own software.',
  'Questionario di sicurezza':
    'Security questionnaire',
  'Registra le risposte':
    'Record the answers',
  'Revocare la dichiarazione? I reperti che toglieva tornano subito aperti.':
    'Revoke the statement? The findings it took out are open again at once.',
  'Richiesta {rif}: la mail che riceverà':
    'Request {rif}: the mail it will receive',
  'Richieste ai fornitori':
    'Requests to suppliers',
  'Risposta entro (giorni)':
    'Answer within (days)',
  'Risposte':
    'Answers',
  'Risposte a':
    'Replies to',
  'Risposte registrate':
    'Answers recorded',
  'SBOM dei prodotti':
    'SBOM of the products',
  'Una richiesta a un fornitore (vulnerabilità, fine supporto, SBOM) rimasta senza risposta alla scadenza. Il sollecito al fornitore parte comunque, in copia al referente.':
    'A request to a supplier (vulnerabilities, end of support, SBOM) left without an answer at the due date. The reminder to the supplier goes out anyway, in copy to the contact person.',
  'Valida fino al':
    'Valid until',
  'Valide fino al':
    'Valid until',
  'Vedi la mail':
    'See the mail',
  'Vulnerabilità nei suoi prodotti':
    'Vulnerabilities in its products',
  'corretto in una versione':
    'fixed in a version',
  'dichiarazione applicata':
    'statement applied',
  'dichiarazioni':
    'statements',
  'entro il {d}':
    'by {d}',
  'il contatto di sicurezza del fornitore':
    'the supplier’s security contact',
  'interessato':
    'affected',
  'inviata il {d}':
    'sent on {d}',
  'nessuna risposta':
    'no answer',
  'note dalla risposta':
    'notes from the answer',
  'perché?':
    'why?',
  'rimedio provvisorio':
    'workaround',
  'risposta':
    'answered',
  'versione che corregge':
    'version that fixes',
  'vuoto: il referente interno':
    'empty: the internal contact',
  '{k} di {n} CVE escluse dal fornitore':
    '{k} of {n} CVEs excluded by the supplier',
  '{n} dichiarazioni applicate: i reperti corrispondenti escono dai rischi aperti fino alla scadenza.':
    '{n} statements applied: the matching findings leave the open risks until the expiry.',
  '{n} installazioni':
    '{n} installations',
  'È ancora una bozza: inviala da «Chiedi al fornitore» o rifalla.':
    'It is still a draft: send it from «Ask the supplier» or write it again.',
  'Dalla scheda di un fornitore, «Chiedi al fornitore» prepara una mail nella sua lingua con le vulnerabilità dei suoi prodotti trovate qui (prodotti, versioni, quante installazioni: mai i nomi delle macchine), la mostra prima di inviarla e la sollecita da sola prima della scadenza. Le risposte si registrano per voce; un «non interessato» diventa una dichiarazione del fornitore, con una scadenza, che toglie dai rischi aperti le stesse vulnerabilità di quel prodotto finché vale.':
    'From a supplier’s page, «Ask the supplier» prepares a mail in its language with the vulnerabilities of its products found here (products, versions, how many installations: never the names of the machines), shows it before sending and reminds it on its own before the due date. The answers are recorded per item; a «not affected» becomes a statement of the supplier, with an expiry, that takes the same vulnerabilities of that product out of the open risks while it holds.',
  'Aggiungi una certificazione':
    'Add a certification',
  'Certificazione':
    'Certification',
  'Certificazioni':
    'Certifications',
  'Collegamento al certificato':
    'Link to the certificate',
  'DORA: registro delle informazioni':
    'DORA: register of information',
  'Eliminare la certificazione?':
    'Delete the certification?',
  'Eliminare la valutazione?':
    'Delete the assessment?',
  'Emessa il':
    'Issued on',
  'Fornitore ICT (DORA)':
    'ICT provider (DORA)',
  'Fornitore terzo di servizi ICT':
    'ICT third-party service provider',
  'I fornitori ICT del registro fornitori (dichiarati, o di tipo cloud e servizi ICT) e quelli con contratti attivi, con gli asset coperti e i provider cloud che ospitano l’inventario. La «concentrazione» è la quota di asset a supporto di funzioni critiche o importanti che dipende da ciascun fornitore. Per chi sostiene funzioni critiche o importanti il registro segnala LEI, sostituibilità e strategia di uscita mancanti. L’esportazione del registro delle informazioni è nell’area Fornitori.':
    'The ICT suppliers of the supplier register (declared, or of kind cloud and ICT services) and those with active contracts, with the assets covered and the cloud providers hosting the inventory. «Concentration» is the share of assets supporting critical or important functions that depends on each supplier. For those supporting critical or important functions the register flags a missing LEI, substitutability and exit strategy. The export of the register of information is in the Suppliers area.',
  'Il fornitore non è critico né importante: la verifica NIS2 riguarda quelli che lo sono, ma le valutazioni restano utili.':
    'The supplier is neither critical nor important: the NIS2 check is about those that are, but assessments remain useful.',
  'La base del registro delle informazioni DORA: fornitori ICT, accordi contrattuali, funzioni sostenute. Una base per i modelli EBA, non la tassonomia completa.':
    'The basis of the DORA register of information: ICT providers, contractual arrangements, functions supported. A basis for the EBA templates, not the full taxonomy.',
  'La strategia di uscita è stata provata':
    'The exit strategy has been tested',
  'Misurata sui fornitori critici o importanti del registro fornitori (dalla criticità di ciò che sostengono): per ciascuno serve una valutazione in corso di validità, un contatto di sicurezza e nessuna certificazione scaduta.':
    'Measured on the critical or important suppliers of the supplier register (from the criticality of what they support): each one needs an assessment in date, a security contact and no expired certification.',
  'Nessuna certificazione registrata.':
    'No certification recorded.',
  'Nessuna valutazione registrata.':
    'No assessment recorded.',
  'Prossima entro':
    'Next by',
  'Registra una valutazione':
    'Record an assessment',
  'Sedi dei dati':
    'Data locations',
  'Sedi dei dati (paesi)':
    'Data locations (countries)',
  'Sicurezza della catena di fornitura (art. 21, c. 2, d)':
    'Supply chain security (art. 21(2)(d))',
  'Sostituibilità':
    'Substitutability',
  'Strategia di uscita':
    'Exit strategy',
  'Valutazione in corso di validità, contatto di sicurezza, nessuna certificazione scaduta.':
    'Assessment in date, security contact, no expired certification.',
  'Valutazioni':
    'Assessments',
  'Valutazioni e certificazioni':
    'Assessments and certifications',
  'Verifica della catena di fornitura (NIS2)':
    'Supply chain check (NIS2)',
  'a posto':
    'in order',
  'anche senza la spunta, i tipi «Cloud» e «Servizi ICT» lo sono':
    'even without the tick, the «Cloud» and «ICT services» kinds are',
  'audit':
    'audit',
  'certificazioni':
    'certifications',
  'certificazioni in date':
    'certifications in date',
  'come si sostituisce, in quanto tempo, dove vanno i dati':
    'how it is replaced, how long it takes, where the data go',
  'con condizioni':
    'with conditions',
  'contatto di sicurezza':
    'security contact',
  'dal contratto, non ancora nel registro fornitori':
    'from the contract, not yet in the supplier register',
  'difficile':
    'difficult',
  'facile':
    'easy',
  'impossibile':
    'impossible',
  'negativa':
    'negative',
  'nessun fornitore critico':
    'no critical supplier',
  'non valutata':
    'not assessed',
  'positiva':
    'positive',
  'provata':
    'tested',
  'questionario':
    'questionnaire',
  'riesame':
    'review',
  'scritta':
    'written',
  'scritta e provata':
    'written and tested',
  'valutazione in corso di validità':
    'assessment in date',
  '{ok} di {n} a posto':
    '{ok} of {n} in order',
  'Nella scheda si registrano le valutazioni (con la prossima da fare) e le certificazioni (con la scadenza), con i promemoria; per DORA il LEI, la sostituibilità, la strategia di uscita e le sedi dei dati. «Fornitori ICT» esporta la base del registro delle informazioni DORA, e il rapporto NIS2 misura la sicurezza della catena di fornitura sui fornitori critici: valutazione in corso di validità, contatto di sicurezza, nessuna certificazione scaduta.':
    'On the page you record the assessments (with the next one due) and the certifications (with their expiry), with reminders; for DORA the LEI, substitutability, exit strategy and data locations. «ICT suppliers» exports the basis of the DORA register of information, and the NIS2 report measures supply chain security on the critical suppliers: assessment in date, security contact, no expired certification.',
  'In fascia critica':
    'In the critical band',
  'Applica i «non interessato»':
    'Apply the «not affected»',
  'Confronta':
    'Compare',
  'Dichiarati e non trovati':
    'Declared and not found',
  'Dichiarazioni del documento {f}':
    'Statements of the document {f}',
  'Documenti del fornitore':
    'Supplier documents',
  'Eliminare il documento? Le dichiarazioni già applicate restano.':
    'Delete the document? The statements already applied stay.',
  'File JSON':
    'JSON file',
  'Installati e non dichiarati':
    'Installed and not declared',
  'Lettura…':
    'Reading…',
  'Nessun documento del fornitore.':
    'No document from the supplier.',
  'Nessuna applicazione installata con questo nome: l’agente con l’SBOM acceso la troverebbe sulle macchine dove gira.':
    'No installed application with this name: the agent with the SBOM on would find it on the machines where it runs.',
  'Originale':
    'Original',
  'SBOM del fornitore e installato: {p}':
    'Supplier SBOM and installed: {p}',
  'Un SBOM (CycloneDX o SPDX) si confronta con quello che l’agente trova installato; una VEX (CycloneDX o OpenVEX) o un avviso CSAF porta le dichiarazioni del fornitore, che diventano tue quando le applichi.':
    'An SBOM (CycloneDX or SPDX) is compared with what the agent finds installed; a VEX (CycloneDX or OpenVEX) or a CSAF advisory carries the supplier’s statements, which become yours when you apply them.',
  'Versioni diverse':
    'Different versions',
  'come si chiama installato':
    'its name once installed',
  'dichiarata {d}, installata {i}':
    'declared {d}, installed {i}',
  'documenti':
    'documents',
  'manca il perché: non si applica':
    'the why is missing: not applied',
  '{a} applicazioni installate · {d} componenti dichiarati · {i} trovati · {s} uguali':
    '{a} installed applications · {d} components declared · {i} found · {s} the same',
  '{n} componenti, {v} vulnerabili':
    '{n} components, {v} vulnerable',
  '{n} dichiarazioni applicate; {s} senza giustificazione non applicate.':
    '{n} statements applied; {s} without justification not applied.',
  '{n} dichiarazioni, {na} «non interessato»':
    '{n} statements, {na} «not affected»',
  'Nella scheda «Documenti» si carica quello che il fornitore ti dà: uno SBOM si confronta con quello che l’agente trova installato (componenti in più, in meno, versioni diverse), una VEX o un avviso CSAF porta le sue dichiarazioni «non interessato», che applichi con una scadenza.':
    'In the «Documents» tab you upload what the supplier gives you: an SBOM is compared with what the agent finds installed (components more, fewer, different versions), a VEX or a CSAF advisory carries its «not affected» statements, which you apply with an expiry.',
  'Generata':
    'Generated',
  'Gestito da CENSO: è chi produce CENSO, la sua sonda e il suo agente. Ne tiene aggiornati SBOM e VEX a ogni versione (scheda Documenti); i dati della scheda, le valutazioni e i contratti sono tuoi.':
    'Managed by CENSO: it is who makes CENSO, its probe and its agent. CENSO keeps its SBOM and VEX up to date at every version (Documents tab); the details on this page, the assessments and the contracts are yours.',
  'Gli stessi documenti nel registro fornitori':
    'The same documents in the supplier registry',
  'L’elenco dei componenti (CycloneDX) della versione installata qui, letto dai file distribuiti, con l’impronta SHA-256 di ciascun file; la VEX dice, per ogni vulnerabilità nota dei componenti, se il prodotto ne è interessato e perché. Produttore: DiscoveryGuard Ltd.':
    'The list of components (CycloneDX) of the version installed here, read from the files distributed, with the SHA-256 fingerprint of each file; the VEX says, for every known vulnerability of the components, whether the product is affected and why. Manufacturer: DiscoveryGuard Ltd.',
  'Nessun documento.':
    'No document.',
  'SBOM dell’agente':
    'Agent SBOM',
  'SBOM e VEX della sonda':
    'Probe SBOM and VEX',
  'SBOM e VEX dell’agente':
    'Agent SBOM and VEX',
  'SBOM e VEX di CENSO':
    'CENSO SBOM and VEX',
  'Scarica SBOM':
    'Download SBOM',
  'Scarica VEX':
    'Download VEX',
  'gestito da CENSO':
    'managed by CENSO',
  'nessuna dichiarazione':
    'no statement',
  'non presente in questa installazione':
    'not present in this installation',
  '{n} sfruttabili':
    '{n} exploitable',
  'In fondo alla pagina lo SBOM e la VEX della sonda di questa versione, da scaricare in CycloneDX.':
    'At the bottom of the page, the SBOM and the VEX of this version of the probe, to download in CycloneDX.',
  'CENSO stesso è un fornitore: in ogni registro c’è DiscoveryGuard Ltd, che produce CENSO, la sonda e l’agente, «gestito da CENSO». Fra i suoi documenti ci sono lo SBOM e la VEX di CENSO, di CENSO Agent e di CENSO Probe della versione installata, aggiornati a ogni versione: si valutano, si confrontano con l’agente installato e si applicano come quelli di ogni fornitore. Non si elimina, e i nomi «CENSO» con cui l’agente compare fra i programmi installati sono suoi; i dati della scheda, le valutazioni e i contratti li gestisci tu.':
    'CENSO itself is a supplier: every registry has DiscoveryGuard Ltd, who makes CENSO, the probe and the agent, «managed by CENSO». Among its documents are the SBOM and the VEX of CENSO, CENSO Agent and CENSO Probe for the installed version, updated at every version: they are evaluated, compared with the installed agent and applied like any supplier’s. It cannot be deleted, and the «CENSO» names the agent shows among the installed programs are its own; the details on its page, the assessments and the contracts are yours to manage.',
  'La sezione «SBOM dell’agente» fa scaricare lo SBOM e la VEX dell’agente di questa versione, con l’impronta SHA-256 di ogni file distribuito.':
    'The «Agent SBOM» section downloads the SBOM and the VEX of this version of the agent, with the SHA-256 fingerprint of every file distributed.',
  'Le macchine che hai scelto ({n}): togli la spunta per escluderne una.':
    'The machines you chose ({n}): untick one to leave it out.',
  'Mostra solo le scelte':
    'Show only the chosen ones',
  'Scegli fra tutte le macchine':
    'Choose among all machines',
  'Sulle {n} macchine che hai scelto: le ritrovi già spuntate nel passo «Dove».':
    'On the {n} machines you chose: you will find them already ticked in the «Where» step.',
  'Scegliendo prima le macchine nella sezione «Macchine» («Nuovo intervento su queste») il passo «Dove» mostra solo quelle, già spuntate, e «Patch scelte» propone solo le patch che mancano a loro; «Scegli fra tutte le macchine» allarga la scelta. La colonna «Sistema» di «Macchine» e «Patch» divide Windows e Linux.':
    'When the machines are chosen first in the «Machines» section («New job on these»), the «Where» step shows only them, already ticked, and «Chosen patches» offers only the patches they miss; «Choose among all machines» widens the choice. The «System» column of «Machines» and «Patches» splits Windows and Linux.',
  'Gestione interna':
    'Internal management',
  'Sicurezza dei prodotti':
    'Product security',
  'Sottoalberi da campionare':
    'Subtrees to sample',
  'Vuoto: la sonda sceglie — la MIB del prodotto quando la conosce (su un Fabric Interconnect Cisco UCS telaio, blade, CPU e memoria), altrimenti il ramo del costruttore. Più OID separati da virgola.':
    'Empty: the probe chooses — the product’s own MIB when it knows it (on a Cisco UCS Fabric Interconnect: chassis, blades, CPUs and memory), otherwise the vendor’s branch. Several OIDs separated by commas.',
  'Il campionamento cattura il ramo del costruttore, o i sottoalberi indicati nel campo «Sottoalberi da campionare»; su un Fabric Interconnect Cisco UCS prende da sé la MIB UCS (telaio, blade, IOM, alimentatori, CPU, memoria). Se una credenziale non riesce, il registro del job dice perché.':
    'Sampling captures the vendor’s branch, or the subtrees given in the «Subtrees to sample» field; on a Cisco UCS Fabric Interconnect it takes the UCS MIB by itself (chassis, blades, IOMs, power supplies, CPUs, memory). When a credential does not work, the job log says why.',
  'es. psirt@produttore.com':
    'e.g. psirt@vendor.com',
  'Gli RPM di questa installazione non sono firmati: un dnf o yum con gpgcheck=1 li rifiuta («is not signed»). La firma si configura sul server con la chiave privata di firma (docs/operations/firma-pacchetti-rpm.md); finché manca, si installano con «dnf install --nogpgcheck» dopo averne controllato la provenienza.':
    'The RPMs of this installation are not signed: a dnf or yum with gpgcheck=1 refuses them («is not signed»). Signing is configured on the server with the private signing key (docs/operations/firma-pacchetti-rpm.md); until then they are installed with «dnf install --nogpgcheck» after checking where they come from.',
  'Quando il server ha la chiave di firma, gli RPM sono firmati con GPG e la finestra offre la chiave pubblica da importare (rpm --import, gpgcheck=1); se non l’ha, la finestra dice che non sono firmati e un dnf con gpgcheck=1 li rifiuta.':
    'When the server has the signing key, the RPMs are GPG-signed and the window offers the public key to import (rpm --import, gpgcheck=1); when it does not, the window says they are not signed, and a dnf with gpgcheck=1 refuses them.',
  'Deve essere un numero fra {min} e {max}, o vuoto per non toccarla.':
    'Must be a number between {min} and {max}, or empty to leave it as it is.',
  "MTU dell'interfaccia":
    'Interface MTU',
  'Non toccare':
    'Leave as is',
  '9000 (jumbo)':
    '9000 (jumbo)',
  "Se nel mirror passano jumbo frame (iSCSI, NFS, vMotion, backup), a 1500 la scheda butta via ogni frame più lungo prima che la cattura lo veda, e nessun contatore della cattura lo conta. Su un'interfaccia d'ascolto senza indirizzo un MTU alto non costa nulla.":
    'If jumbo frames go through the mirror (iSCSI, NFS, vMotion, backups), at 1500 the NIC throws away every longer frame before the capture sees it, and no capture counter counts it. On a listening interface without an address a large MTU costs nothing.',
  'Disattiva GRO/LRO':
    'Turn off GRO/LRO',
  'La scheda (LRO) o il kernel (GRO) uniscono segmenti consecutivi in un pacchetto solo prima che la cattura lo veda: conteggi, dimensioni e tempi non sono più quelli del filo. Su un mirror conviene spegnerli.':
    'The NIC (LRO) or the kernel (GRO) merge consecutive segments into a single packet before the capture sees it: counts, sizes and timing are no longer those on the wire. On a mirror they are better off.',
  'La sonda li applica prima di mettersi in ascolto, e di nuovo a ogni riavvio della macchina. Gira senza privilegi di rete: li applica per lei un piccolo servizio di sistema ({servizio}), che l’aggiornatore installa da sé. In Docker la sonda va installata con {opzione}.':
    'The probe applies them before it starts listening, and again at every reboot of the machine. It runs without network privileges: a small system service ({servizio}) applies them for it, and the updater installs it by itself. In Docker the probe must be installed with {opzione}.',
  'MTU {n}':
    'MTU {n}',
  'richiesta {n}':
    'requested {n}',
  'acceso':
    'on',
  'Interfaccia di cattura non preparata del tutto.':
    'Capture interface not fully set up.',
  'Da quando la cattura è partita la scheda ha rifiutato {n} frame più lunghi del suo MTU ({mtu}): di norma jumbo frame nel mirror (iSCSI, NFS, vMotion, backup). Non arrivano alla cattura e nessun altro contatore li conta. Imposta qui sopra l’MTU dell’interfaccia a 9000 o 9216.':
    'Since the capture started the NIC has refused {n} frames longer than its MTU ({mtu}): usually jumbo frames in the mirror (iSCSI, NFS, vMotion, backups). They do not reach the capture and no other counter counts them. Set the interface MTU above to 9000 or 9216.',
  'Da quando la cattura è partita la scheda ha perso {n} frame perché il suo anello di ricezione era pieno, prima del kernel. Se crescono, ingrandisci l’anello della scheda (ethtool -G {interfaccia} rx 4096) o copia meno traffico nello SPAN.':
    'Since the capture started the NIC has lost {n} frames because its receive ring was full, before the kernel. If they keep growing, enlarge the NIC ring (ethtool -G {interfaccia} rx 4096) or copy less traffic into the SPAN.',
  'Interfaccia di cattura (MTU, GRO/LRO)':
    'Capture interface (MTU, GRO/LRO)',
  'Le impostazioni scelte in «Sorgenti dei flussi» le applica un servizio di sistema a parte, perché la sonda gira senza privilegi di rete: parte quando la sonda le cambia e a ogni avvio. Il suo stato e il suo esito:':
    'The settings chosen in «Flow sources» are applied by a separate system service, since the probe runs without network privileges: it starts when the probe changes them and at every boot. Its status and its result:',
  'In «Sorgenti dei flussi» della scheda si sceglie l’interfaccia della cattura dal filo e il campionamento; se nel mirror passano jumbo frame (iSCSI, NFS, vMotion, backup), anche l’MTU dell’interfaccia (9000 o 9216) e GRO/LRO spenti. La sonda li applica da sé, anche dopo un riavvio della macchina, e avvisa quando la scheda butta via frame più lunghi del suo MTU.':
    'In the probe’s «Flow sources» you choose the interface for capture from the wire and the sampling; if jumbo frames go through the mirror (iSCSI, NFS, vMotion, backups), also the interface MTU (9000 or 9216) and GRO/LRO off. The probe applies them by itself, also after a reboot of the machine, and warns when the NIC throws away frames longer than its MTU.',
  'Aggiornare l’aggiornatore':
    'Updating the updater',
  'Da ottobre 2026 l’aggiornatore si aggiorna da sé, una volta l’ora. Una sonda installata prima ha la copia vecchia, che non lo sa fare: va sostituita una volta con quella del Control Plane. Il comando la scarica e la esegue: installa timer e servizi mancanti e aggiorna la sonda se serve. Non riarruola la sonda: usa il suo certificato, nessun token.':
    'Since October 2026 the updater updates itself, once an hour. A probe installed earlier has the old copy, which cannot: it must be replaced once with the Control Plane’s. The command downloads and runs it: it installs the missing timer and services and updates the probe if needed. It does not re-enrol the probe: it uses its certificate, no token.',
  'Questa sonda è una Censo Appliance, senza shell: dal menu dell’appliance, con l’utente censoadmin, scegli «CENSO probe» → «Enroll / reinstall the probe» e incolla il comando qui sotto. Il menu ne prende solo l’indirizzo e l’impronta del certificato e lo esegue da root.':
    'This probe is a Censo Appliance, with no shell: in the appliance menu, as the censoadmin user, choose «CENSO probe» → «Enroll / reinstall the probe» and paste the command below. The menu takes only the address and the certificate fingerprint from it and runs it as root.',
  'VLAN documentate':
    'Documented VLANs',
  'Leggo le VLAN da phpIPAM…':
    'Reading the VLANs from phpIPAM…',
  'Nessuna VLAN':
    'No VLANs',
  'phpIPAM non ha VLAN, oppure l’app non ha i permessi di lettura sulle VLAN.':
    'phpIPAM has no VLANs, or the app cannot read them.',
  'Domini L2':
    'L2 domains',
  'Senza subnet':
    'Without subnets',
  'Il tab VLAN elenca le VLAN di phpIPAM con numero, nome, dominio L2 (spesso la sede), descrizione e le subnet associate; una subnet si apre sul suo confronto con la CMDB.':
    'The VLAN tab lists the phpIPAM VLANs with number, name, L2 domain (often the site), description and the associated subnets; a subnet opens its comparison with the CMDB.',
  'Scadenza dei nuovi firmware':
    'Expiry of new firmware',
  'dopo {n} giorni':
    'after {n} days',
  'Un firmware scaduto esce dal catalogo da solo e le sonde lo tolgono entro un minuto. Vale per i file caricati d’ora in poi; per quelli già nel catalogo si cambia riga per riga.':
    'An expired firmware leaves the catalogue by itself and the probes remove it within a minute. It applies to files uploaded from now on; for those already in the catalogue it is changed row by row.',
  'Scadenza di «{nome}»':
    'Expiry of «{nome}»',
  'Scadenza…':
    'Expiry…',
  'non scade':
    'never expires',
  'Ogni firmware ha una scadenza (30 giorni se non la cambi): scaduto, esce dal catalogo da solo e le sonde lo tolgono; nella pagina si cambia la scadenza predefinita e, riga per riga, si proroga o si toglie.':
    'Every firmware has an expiry (30 days unless you change it): once expired it leaves the catalogue by itself and the probes remove it; the page sets the default expiry and, row by row, extends or removes it.',
  'consegna al Control Plane non riuscita: {e}':
    'delivery to the Control Plane failed: {e}',
  '{n} avvistamenti persi':
    '{n} sightings lost',
  'Consegna al Control Plane':
    'Delivery to the Control Plane',
  'non riuscita dal {q}':
    'failing since {q}',
  'riuscita, l’ultima il {q}':
    'working, the last one on {q}',
  'ancora nessuna consegna':
    'no delivery yet',
  '{n} in attesa':
    '{n} waiting',
  'Ciò che la sonda sente non arriva al Control Plane.':
    'What the probe hears does not reach the Control Plane.',
  'Un lotto rifiutato si scarta e non blocca più gli altri; un Control Plane lento o irraggiungibile fa aspettare il resto al giro dopo.':
    'A refused batch is dropped and no longer blocks the others; a slow or unreachable Control Plane makes the rest wait for the next round.',
  'Avvistamenti persi dall’avvio dell’ascolto: {a} oltre la memoria della sonda, {b} rifiutati dal Control Plane.':
    "Sightings lost since listening started: {a} beyond the probe's memory, {b} refused by the Control Plane.",
  'file mancante':
    'file missing',
  'Il file non è più sul Control Plane: le sonde non possono scaricarlo. Eliminalo e caricalo di nuovo.':
    'The file is no longer on the Control Plane: the probes cannot download it. Delete it and upload it again.',
  // CENSO MED (ADR-0027)
  '(non indicato)':
    '(not specified)',
  'Anche gli esclusi a mano':
    'Also those excluded by hand',
  'Anche i dismessi':
    'Also the decommissioned ones',
  'Anche quelli non più elettromedicali':
    'Also those no longer medical devices',
  'Classe di protezione':
    'Protection class',
  'Codice CIVAB':
    'CIVAB code',
  'Codice CND':
    'CND code',
  'Collaudo':
    'Acceptance test',
  'Con avvisi di sicurezza':
    'With security advisories',
  'Cosa vede CENSO':
    'What CENSO sees',
  'Da registrare':
    'To register',
  'Di scorta':
    'Spare',
  'Elenco troncato: sono mostrati i primi {n} dispositivi.':
    'List truncated: the first {n} devices are shown.',
  'Elettromedicale':
    'Medical device',
  'Esclusi a mano':
    'Excluded by hand',
  'Fuori servizio':
    'Out of service',
  'Gli elettromedicali dell’ospedale: quanti sono, cosa resta da registrare, dove sono, cosa va controllato.':
    'The hospital’s medical devices: how many there are, what is left to register, where they are, what needs checking.',
  'Gli elettromedicali per l’Ingegneria clinica: dove sono, schede, cespiti e contratti, verifiche.':
    'Medical devices for clinical engineering: where they are, records, fixed assets and contracts, checks.',
  'Gli elettromedicali riconosciuti da CENSO, con la scheda dell’Ingegneria clinica.':
    'The medical devices recognised by CENSO, with the clinical engineering record.',
  'I dispositivi compaiono qui quando CENSO li riconosce come elettromedicali (inventario passivo, riconoscimento, inventari di riferimento).':
    'Devices appear here when CENSO recognises them as medical devices (passive inventory, recognition, reference inventories).',
  'Identificazione':
    'Identification',
  'Il catalogo degli elettromedicali si costruisce dagli inventari di riferimento: questo modello non c’è ancora.':
    'The medical device catalogue is built from the reference inventories: this model is not in it yet.',
  'Il dispositivo non è più nella CMDB.':
    'The device is no longer in the CMDB.',
  'Il modello nel catalogo':
    'The model in the catalogue',
  'In riparazione':
    'Under repair',
  'Ingegneria clinica':
    'Clinical engineering',
  'La scheda resta per la sua storia.':
    'The record stays for its history.',
  'Modello non ancora nel catalogo.':
    'Model not in the catalogue yet.',
  'Modulo MDS2':
    'MDS2 form',
  'N. inventario':
    'Inventory no.',
  'Nessun avviso di sicurezza su questo dispositivo.':
    'No security advisory on this device.',
  'Nessun elettromedicale in questo elenco.':
    'No medical device in this list.',
  'Nessun elettromedicale riconosciuto finora.':
    'No medical device recognised so far.',
  'Opera sugli elettromedicali in CENSO MED (schede, reparti, cespiti, contratti, verifiche); non vede il resto della CMDB.':
    'Works on the medical devices in CENSO MED (records, wards, fixed assets, contracts, checks); does not see the rest of the CMDB.',
  'Parte applicata':
    'Applied part',
  'Per produttore':
    'By manufacturer',
  'Per reparto':
    'By ward',
  'Per stato':
    'By status',
  'Prima volta visto':
    'First seen',
  'Reparto':
    'Ward',
  'Riconosciuto da':
    'Recognised by',
  'Rilevato con':
    'Discovered via',
  'Rilevazione':
    'Discovery',
  'Scheda creata da CENSO il {quando}: nessuno l’ha ancora compilata.':
    'Record created by CENSO on {quando}: nobody has filled it in yet.',
  'Senza numero d’inventario':
    'Without an inventory number',
  'Senza reparto né sede':
    'Without ward or site',
  'Sicurezza elettrica (CEI EN 62353)':
    'Electrical safety (IEC 62353)',
  'Stanza':
    'Room',
  'Stato e responsabile':
    'Status and owner',
  'Tipo di dispositivo':
    'Device type',
  'Togli il filtro della panoramica':
    'Remove the overview filter',
  'UDI-DI':
    'UDI-DI',
  'Ultima modifica il {quando} da {chi}.':
    'Last changed on {quando} by {chi}.',
  'Versione del sistema operativo':
    'Operating system version',
  'Voce':
    'Entry',
  'accettato':
    'accepted',
  'segnati «non è un elettromedicale»':
    'marked «not a medical device»',
  'CENSO crea la scheda quando un CI diventa elettromedicale e la tiene allineata ogni ora (nome, produttore, modello). Se il dispositivo smette di esserlo o sparisce dalla CMDB, la scheda che nessuno ha compilato si cancella; quella compilata resta, fuori dall’area, con la sua storia. Se due CI si fondono, vince la scheda compilata.':
    'CENSO creates the record when a CI becomes a medical device and keeps it aligned every hour (name, manufacturer, model). If the device stops being one or leaves the CMDB, a record nobody filled in is deleted; a filled-in one stays, outside the area, with its history. When two CIs merge, the filled-in record wins.',
  'Dispositivi: l’elenco, con le spunte per vedere anche quelli non più elettromedicali, gli esclusi a mano e i dismessi. Ogni riga apre la scheda: dati dell’Ingegneria clinica, cosa vede CENSO, il modello nel catalogo degli elettromedicali e gli avvisi di sicurezza (CISA ICSMA).':
    'Devices: the list, with ticks to also show those no longer medical devices, those excluded by hand and the decommissioned ones. Each row opens the record: clinical engineering data, what CENSO sees, the model in the medical device catalogue and the security advisories (CISA ICSMA).',
  'Fra i ruoli predefiniti (Impostazioni → Utenti → Ruoli) c’è «Ingegneria clinica»: opera in CENSO MED e non vede il resto della CMDB, nemmeno dalla ricerca, dal riepilogo della Home o dalle notifiche. Entrando arriva direttamente alla Panoramica di CENSO MED. I ruoli Team sicurezza, Asset e contratti, Responsabile compliance e Auditor vedono l’area in sola lettura.':
    'Among the predefined roles (Settings → Users → Roles) there is «Clinical engineering»: it works in CENSO MED and does not see the rest of the CMDB, not even from search, the Home summary or the notifications. On login it lands directly on the CENSO MED overview. The Security team, Assets and contracts, Compliance officer and Auditor roles see the area read-only.',
  'Il ruolo «Ingegneria clinica»':
    'The «Clinical engineering» role',
  'La scheda segue il dispositivo':
    'The record follows the device',
  'Panoramica: quanti sono, quanti senza numero d’inventario, senza reparto o con avvisi di sicurezza, e la ripartizione per stato, reparto, produttore e categoria. Ogni numero apre l’elenco filtrato.':
    'Overview: how many there are, how many without an inventory number, without a ward or with security advisories, and the breakdown by status, ward, manufacturer and category. Each number opens the filtered list.',
  'l’area degli elettromedicali per l’Ingegneria clinica, con la licenza CENSO MED. Mostra solo i dispositivi che CENSO ha riconosciuto come elettromedicali (classe «Dispositivo medico» o una categoria medicale dal riconoscimento), ognuno con la sua scheda: ciò che CENSO vede in rete accanto a ciò che l’Ingegneria clinica scrive (numero d’inventario, reparto, stanza, responsabile, classe elettrica).':
    'the medical devices area for clinical engineering, with the CENSO MED licence. It shows only the devices CENSO recognised as medical devices (class «Medical device» or a medical category from recognition), each with its record: what CENSO sees on the network next to what clinical engineering writes (inventory number, ward, room, owner, electrical class).',
  'CENSO riconosce la serie ma non ancora il modello. Lo prende dall’inventario di riferimento dell’ospedale (stesso MAC), dal nome con cui il dispositivo si presenta, o da chi lo scrive nella scheda.':
    'CENSO recognises the series but not yet the model. It takes it from the hospital’s reference inventory (same MAC), from the name the device announces, or from whoever writes it in the record.',
  'Serie':
    'Series',
  'da determinare':
    'to be determined',
  'dal CMMS':
    'from the CMMS',
  'dal nome del dispositivo, confrontato con il catalogo':
    'from the device’s name, matched against the catalogue',
  'dal riconoscimento':
    'from recognition',
  'dall’inventario di riferimento ({fonte})':
    'from the reference inventory ({fonte})',
  'modello da determinare':
    'model to be determined',
  'scritto a mano':
    'written by hand',
  'serie {s}':
    'series {s}',
  'Serie e modello':
    'Series and model',
  'Spesso CENSO riconosce la serie (IntelliVue, CARESCAPE, LOGIQ) e non il modello: la scheda mostra la serie e «modello da determinare». Il modello arriva dall’inventario di riferimento dell’ospedale per lo stesso MAC, dalla sigla del modello nel nome con cui il dispositivo si presenta (per esempio «MX450»), o da chi lo scrive. La scheda dice da dove viene. Nel CMDB e in Traffic Analysis il modello dell’inventario di riferimento non entra, perché è ciò con cui CENSO viene misurato.':
    'CENSO often recognises the series (IntelliVue, CARESCAPE, LOGIQ) and not the model: the record shows the series and «model to be determined». The model comes from the hospital’s reference inventory for the same MAC, from the model code in the name the device announces (for example «MX450»), or from whoever writes it. The record says where it comes from. In the CMDB and in Traffic Analysis the reference inventory’s model is not used, because it is what CENSO is measured against.',
  '(come ora)':
    '(as now)',
  '+ Nuova sede':
    '+ New site',
  '+ Nuovo reparto':
    '+ New ward',
  'Alimentazione interna (a batteria)':
    'Internally powered (battery)',
  'Applicati {n} di {m}…':
    'Applied {n} of {m}…',
  'Azioni sui dispositivi scelti':
    'Actions on the selected devices',
  'B: nessun contatto diretto con il paziente':
    'B: no direct contact with the patient',
  'BF: a contatto con il paziente, isolata':
    'BF: in contact with the patient, floating',
  'CF: per l’applicazione diretta al cuore':
    'CF: for direct cardiac application',
  'Catalogo modelli':
    'Model catalogue',
  'Classe I: protezione con il conduttore di terra':
    'Class I: protection through the earth conductor',
  'Classe II: doppio isolamento':
    'Class II: double insulation',
  'Con avvisi':
    'With advisories',
  'Cosa cambia':
    'What changes',
  'Cosa cambia su {n} dispositivi':
    'What changes on {n} devices',
  'Dopo':
    'After',
  'Escludi dall’area':
    'Exclude from the area',
  'Escluso dall’area: «{motivo}».':
    'Excluded from the area: «{motivo}».',
  'Fermi o in riparazione':
    'Stopped or under repair',
  'I dispositivi di questo modello':
    'The devices of this model',
  'I modelli degli elettromedicali dell’ospedale: quanti sono, dove, in che stato, con i dati del catalogo.':
    'The models of the hospital’s medical devices: how many, where, in what state, with the catalogue data.',
  'Il CI non è più riconosciuto come elettromedicale: la scheda resta per la sua storia.':
    'The CI is no longer recognised as a medical device: the record stays for its history.',
  'Il catalogo degli elettromedicali non ha ancora questo modello: classe di rischio, fine vita e modulo MDS2 arriveranno da lì.':
    'The medical device catalogue does not have this model yet: risk class, end of life and MDS2 form will come from there.',
  'Il dispositivo esce dall’area CENSO MED: dagli elenchi, dalla panoramica, dalla ricerca. La scheda resta (si rivede con «Anche gli esclusi a mano») e il CI nella CMDB non cambia.':
    'The device leaves the CENSO MED area: the lists, the overview, the search. The record stays (shown again with «Also those excluded by hand») and the CI in the CMDB does not change.',
  'Il dispositivo torna negli elenchi dell’area. Il motivo dell’esclusione si cancella.':
    'The device goes back into the area’s lists. The exclusion reason is cleared.',
  'Il numero dell’Ingegneria clinica: unico nell’ospedale.':
    'Clinical engineering’s number: unique in the hospital.',
  'Modello da determinare':
    'Model to be determined',
  'Modello non indicato':
    'Model not specified',
  'Modifica in blocco':
    'Bulk change',
  'Modifica in blocco…':
    'Bulk change…',
  'Modificati {n} dispositivi.':
    '{n} devices changed.',
  'Motivo dello stato':
    'Reason for the status',
  'Nessun modello: i dispositivi non sono ancora stati riconosciuti.':
    'No model: the devices have not been recognised yet.',
  'Nome del reparto':
    'Ward name',
  'Nome della sede':
    'Site name',
  'Non è un elettromedicale':
    'Not a medical device',
  'Per esempio: «è il PC del medico di guardia», «stampante di reparto». Resta nella scheda.':
    'For example: «it is the on-call doctor’s PC», «ward printer». It stays in the record.',
  'Per sede':
    'By site',
  'Perché non è un elettromedicale':
    'Why it is not a medical device',
  'Prima':
    'Before',
  'Reparti':
    'Wards',
  'Rimetti fra gli elettromedicali':
    'Put back among the medical devices',
  'Rimetti nell’area':
    'Put back in the area',
  'Rivedi le modifiche ({n})':
    'Review the changes ({n})',
  'Senza n. inventario':
    'Without inventory no.',
  'Si conosce solo la serie: i dati del catalogo valgono per un modello preciso.':
    'Only the series is known: the catalogue data apply to a specific model.',
  'Spunta i campi da cambiare: gli altri restano come sono su ogni dispositivo.':
    'Tick the fields to change: the others stay as they are on each device.',
  'Trovato da CENSO: scrivilo solo se è sbagliato.':
    'Found by CENSO: write it only if it is wrong.',
  'Un dispositivo dismesso esce dagli elenchi dell’area (resta visibile con «Anche i dismessi»).':
    'A decommissioned device leaves the area’s lists (still visible with «Also the decommissioned ones»).',
  'Un modello scritto qui resta: la sincronizzazione oraria non lo cambia più. Svuotalo per lasciarlo cercare di nuovo a CENSO.':
    'A model written here stays: the hourly sync no longer changes it. Empty it to let CENSO look for it again.',
  'fermi o in riparazione {n}':
    'stopped or under repair {n}',
  'in uso {n}':
    'in use {n}',
  'non indicato':
    'not specified',
  'si conosce solo la serie':
    'only the series is known',
  '{n} dispositivi scelti':
    '{n} devices selected',
  '{n} elettromedicali':
    '{n} medical devices',
  '{s} (modello da determinare)':
    '{s} (model to be determined)',
  'Catalogo modelli: i modelli dei dispositivi dell’ospedale, quanti sono, in quali reparti, quanti fermi o con avvisi, con classe di rischio e fine vita dal catalogo. Un modello apre l’elenco dei suoi dispositivi.':
    'Model catalogue: the models of the hospital’s devices, how many, in which wards, how many stopped or with advisories, with risk class and end of life from the catalogue. A model opens the list of its devices.',
  'Compilare le schede':
    'Filling in the records',
  '«Modifica la scheda» (dalla pagina del dispositivo): numero d’inventario (unico nell’ospedale), seriale, UDI-DI, CND, CIVAB, serie e modello, reparto, sede, centro di costo, stanza e letto, stato, responsabile, date di collaudo e installazione, classe di protezione e parte applicata (CEI EN 62353), note. Prima di salvare si rivede cosa cambia. Reparti e sedi nuovi si creano lì, senza passare dalle Anagrafiche.':
    '«Edit the record» (from the device page): inventory number (unique in the hospital), serial, UDI-DI, CND, CIVAB, series and model, ward, site, cost centre, room and bed, status, owner, acceptance and installation dates, protection class and applied part (IEC 62353), notes. Before saving you review what changes. New wards and sites are created there, without going through Master data.',
  'Un modello scritto a mano resta: la sincronizzazione oraria non lo cambia più. Svuotato, CENSO torna a cercarlo.':
    'A model written by hand stays: the hourly sync no longer changes it. Once emptied, CENSO looks for it again.',
  'Modifica in blocco: nell’elenco dei dispositivi si spuntano le righe (anche tutte quelle filtrate) e si danno a tutte lo stesso reparto, sede, centro di costo, stato, responsabile o classe elettrica. I campi non spuntati restano come sono.':
    'Bulk change: in the device list you tick the rows (even all the filtered ones) and give them all the same ward, site, cost centre, status, owner or electrical class. The fields not ticked stay as they are.',
  '«Non è un elettromedicale»: toglie il dispositivo dall’area, con il motivo; il CI nella CMDB non cambia. Si rimette con lo stesso pulsante.':
    '«Not a medical device»: removes the device from the area, with the reason; the CI in the CMDB does not change. It is put back with the same button.',
  'Elenco dei dispositivi e catalogo dei modelli si esportano in Excel o PDF. Un reparto o una sede in uso dagli elettromedicali, nelle Anagrafiche, si disattiva invece di togliersi.':
    'The device list and the model catalogue export to Excel or PDF. A ward or site used by medical devices is deactivated in Master data instead of being removed.',
  'Abbinata per':
    'Matched by',
  'Abbinate a una scheda':
    'Matched to a record',
  'Aggiornate {n} schede.':
    '{n} records updated.',
  'Aggiornate {n} schede; create le voci: {v}.':
    '{n} records updated; entries created: {v}.',
  'Applica le proposte':
    'Apply the proposals',
  'Applico…':
    'Applying…',
  'Chi indica ogni chiave':
    'What each key points to',
  'Colonne riconosciute: {c}.':
    'Columns recognised: {c}.',
  'Con qualcosa da cambiare':
    'With something to change',
  'Confronto con le schede…':
    'Comparing with the records…',
  'Crea i reparti e le sedi che mancano nelle anagrafiche':
    'Create the wards and sites missing from master data',
  'Dispositivi dell’elenco che CENSO non ha riconosciuto in rete, o che nessuna chiave lega a una scheda (nomi uguali su più dispositivi non bastano). Ne sono mostrati al più 500.':
    'Devices of the list CENSO has not recognised on the network, or that no key ties to a record (the same name on several devices is not enough). At most 500 are shown.',
  'Foglio':
    'Sheet',
  'Il file':
    'The file',
  'Importa dal CMMS':
    'Import from the CMMS',
  'In conflitto':
    'In conflict',
  'Le chiavi della riga indicano dispositivi diversi (per esempio il numero d’inventario uno, il MAC un altro): non si indovina, si corregge l’elenco o la scheda.':
    'The keys of the row point to different devices (for example the inventory number to one, the MAC to another): nothing is guessed, fix the list or the record.',
  'Le proposte':
    'The proposals',
  'L’elenco degli elettromedicali dell’Ingegneria clinica, esportato dal CMMS o da un foglio: Excel o CSV, con una riga di intestazioni. Le colonne si riconoscono dal nome (numero d’inventario, matricola, MAC, IP, nome, modello, codice CND, CIVAB, UDI, reparto, presidio, stanza, date di collaudo e installazione). Il file non si salva: serve solo a proporre le modifiche.':
    'Clinical engineering’s list of medical devices, exported from the CMMS or a spreadsheet: Excel or CSV, with a header row. Columns are recognised by name (inventory number, serial, MAC, IP, name, model, CND code, CIVAB, UDI, ward, site, room, acceptance and installation dates). The file is not saved: it only serves to propose changes.',
  'L’elenco dell’Ingegneria clinica diventa una serie di proposte per le schede: si sceglie cosa applicare.':
    'Clinical engineering’s list becomes a set of proposals for the records: you choose what to apply.',
  'Non abbinate':
    'Not matched',
  'Quali campi applicare':
    'Which fields to apply',
  'Riga':
    'Row',
  'Righe in conflitto':
    'Rows in conflict',
  'Righe lette':
    'Rows read',
  'Righe senza una scheda':
    'Rows without a record',
  'Rivedi e applica ({n} schede)':
    'Review and apply ({n} records)',
  'Scegli il file…':
    'Choose the file…',
  'Schede non aggiornate':
    'Records not updated',
  'Senza, i reparti e le sedi che l’elenco nomina e le anagrafiche non hanno restano fuori.':
    'Without it, the wards and sites the list names and master data lacks are left out.',
  'Si aggiornano {n} schede, con {m} modifiche in tutto. Ogni scheda passa dagli stessi controlli di una modifica a mano; una che non li supera resta com’è e lo si dice.':
    '{n} records are updated, with {m} changes in all. Each record goes through the same checks as a manual change; one that fails them stays as it is, and you are told.',
  'Si creano nelle anagrafiche: {v}.':
    'Created in master data: {v}.',
  'Un modello preso dall’elenco resta come uno scritto a mano: la sincronizzazione oraria non lo cambia.':
    'A model taken from the list stays like one written by hand: the hourly sync does not change it.',
  'indirizzo IP':
    'IP address',
  'nome':
    'name',
  'numero di serie':
    'serial number',
  'numero d’inventario':
    'inventory number',
  'nuova voce':
    'new entry',
  'schede':
    'records',
  '{nome} ({n} righe)':
    '{nome} ({n} rows)',
  'L’elenco dell’Ingegneria clinica (export del CMMS o un foglio Excel o CSV) non sovrascrive niente: diventa proposte. Ogni riga si abbina a una scheda per numero d’inventario, MAC, numero di serie, IP o nome (il nome solo se un dispositivo solo ce l’ha); per ognuna CENSO mostra cosa l’elenco dice di diverso, e si sceglie quali schede e quali campi applicare, e se creare i reparti e le sedi che mancano. Una riga le cui chiavi indicano dispositivi diversi è un conflitto, non si indovina. Il file non si salva e non diventa l’inventario di riferimento di Traffic Analysis; un modello preso dall’elenco resta come uno scritto a mano.':
    'Clinical engineering’s list (CMMS export or an Excel or CSV sheet) overwrites nothing: it becomes proposals. Each row is matched to a record by inventory number, MAC, serial number, IP or name (the name only if a single device has it); for each one CENSO shows what the list says differently, and you choose which records and fields to apply, and whether to create the missing wards and sites. A row whose keys point to different devices is a conflict and is not guessed. The file is not saved and does not become Traffic Analysis’s reference inventory; a model taken from the list stays like one written by hand.',
  'AE title DICOM':
    'DICOM AE title',
  'Accetta i suggerimenti migliori':
    'Accept the best suggestions',
  'Accettare {n} suggerimenti su {m} dispositivi?':
    'Accept {n} suggestions on {m} devices?',
  'Accettati {n} suggerimenti.':
    '{n} suggestions accepted.',
  'Affidabilità':
    'Reliability',
  'Aggiungi la regola':
    'Add the rule',
  'Allora il reparto è':
    'Then the ward is',
  'Altro elettromedicale (centrale, gateway)':
    'Another medical device (central station, gateway)',
  'Attività di rete negli ultimi 30 giorni, non esami. Un’ora è attiva con almeno {soglia} di traffico; «fermo» = nessuna ora attiva da {n} giorni; «mai visto nei flussi» = il suo traffico non passa da un router che esporta i flussi, non che sia fermo.':
    'Network activity in the last 30 days, not exams. An hour is active with at least {soglia} of traffic; «idle» = no active hour for {n} days; «never seen in the flows» = its traffic does not go through a router that exports flows, not that it is idle.',
  'CENSO la ricalcola ogni ora; «Ricalcola adesso» nella vista Dove sono la calcola subito.':
    'CENSO recomputes it every hour; «Recompute now» in the Where view computes it at once.',
  'Calcolato il {quando}.':
    'Computed on {quando}.',
  'Con chi':
    'With whom',
  'Con chi parlano gli elettromedicali negli ultimi 7 giorni, dai flussi (NetFlow/IPFIX). Il collegamento fisico è la porta dello switch, in «Dove sono».':
    'Whom the medical devices talked to in the last 7 days, from the flows (NetFlow/IPFIX). The physical connection is the switch port, in «Where they are».',
  'Con chi parlano gli elettromedicali: PACS, integrazioni HL7, centrali, server, indirizzi esterni.':
    'Whom the medical devices talk to: PACS, HL7 integrations, central stations, servers, external addresses.',
  'Dati fino al {giorno}.':
    'Data up to {giorno}.',
  'Descrizione della porta':
    'Port description',
  'Descrizione della porta contiene':
    'Port description contains',
  'Dove la rete vede ogni elettromedicale (switch, porta, VLAN, cosa dice di sé) e i suggerimenti per reparto, sede, stanza e letto.':
    'Where the network sees each medical device (switch, port, VLAN, what it says about itself) and the suggestions for ward, site, room and bed.',
  'Dove lo vede la rete':
    'Where the network sees it',
  'Dove sono':
    'Where they are',
  'Fermi da {n} giorni':
    'Idle for {n} days',
  'Fermo':
    'Idle',
  'Gli interlocutori':
    'The peers',
  'Indirizzo esterno sconosciuto':
    'Unknown external address',
  'Indirizzo interno senza CI':
    'Internal address without a CI',
  'Integrazione HL7':
    'HL7 integration',
  'Inventario di riferimento':
    'Reference inventory',
  'Inventario di riferimento contiene':
    'Reference inventory contains',
  'Mai visti nei flussi':
    'Never seen in the flows',
  'Mai visto nei flussi':
    'Never seen in the flows',
  'Nessun giorno con traffico negli ultimi 30 giorni.':
    'No day with traffic in the last 30 days.',
  'Nessun suggerimento: la scheda dice già ciò che le fonti dicono, o le fonti non dicono niente.':
    'No suggestion: the record already says what the sources say, or the sources say nothing.',
  'Nessun traffico negli ultimi 7 giorni nei flussi (NetFlow/IPFIX): può parlare solo dentro la sua VLAN.':
    'No traffic in the flows (NetFlow/IPFIX) in the last 7 days: it may talk only inside its VLAN.',
  'Nessuna ora attiva negli ultimi 30 giorni.':
    'No active hour in the last 30 days.',
  'Nessuna regola.':
    'No rule.',
  'Ore attive (30 g)':
    'Active hours (30 d)',
  'Ore attive al giorno':
    'Active hours per day',
  'Ore attive in 30 giorni':
    'Active hours in 30 days',
  'Ore con DICOM o HL7':
    'Hours with DICOM or HL7',
  'PACS (immagini DICOM)':
    'PACS (DICOM images)',
  'Porte dello switch che iniziano con':
    'Switch ports starting with',
  'Posizione non ancora calcolata.':
    'Location not computed yet.',
  'Quando si usa (ultimi 30 giorni)':
    'When it is used (last 30 days)',
  'Quanto si usa ogni elettromedicale, dall’attività di rete degli ultimi 30 giorni.':
    'How much each medical device is used, from the network activity of the last 30 days.',
  'Regole di posizione':
    'Location rules',
  'Ricalcola adesso':
    'Recompute now',
  'Ricalcolo…':
    'Recomputing…',
  'Riceve immagini DICOM':
    'Receives DICOM images',
  'Salva la soglia':
    'Save the threshold',
  'Se':
    'If',
  'Senza traffico nei flussi':
    'Without traffic in the flows',
  'Server o PC':
    'Server or PC',
  'Servizi di rete':
    'Network services',
  'Soglia di un’ora attiva (KB)':
    'Active hour threshold (KB)',
  'Stanza e letto':
    'Room and bed',
  'Struttura HL7':
    'HL7 facility',
  'Suggerimenti':
    'Suggestions',
  'Switch e porta':
    'Switch and port',
  'Traffico in 30 giorni':
    'Traffic in 30 days',
  'Ultima attività':
    'Last activity',
  'Una regola dice dove stanno i dispositivi che hanno qualcosa in comune: «la VLAN 210 è la Rianimazione», «le porte con BOX nella descrizione sono del Pronto soccorso». Diventa un suggerimento su ogni dispositivo a cui si applica, al prossimo calcolo; non scrive niente da sola.':
    'A rule says where the devices that have something in common are: «VLAN 210 is Intensive care», «ports with BOX in the description are the Emergency department». It becomes a suggestion on every device it applies to, at the next computation; it writes nothing by itself.',
  'chiama':
    'calls',
  'descrizione della porta dello switch':
    'switch port description',
  'e la sede è':
    'and the site is',
  'giorni attivi':
    'active days',
  'interlocutori':
    'peers',
  'inventario di riferimento':
    'reference inventory',
  'nessuno switch ha imparato il suo MAC':
    'no switch has learnt its MAC',
  'nome della VLAN':
    'VLAN name',
  'ore attive':
    'active hours',
  'posizione HL7 (reparto^stanza^letto)':
    'HL7 location (ward^room^bed)',
  'regola di posizione':
    'location rule',
  'struttura HL7':
    'HL7 facility',
  'È attività di rete, non esami: un’ora è «attiva» con almeno {soglia} di traffico. Le ore con DICOM o HL7 sono quelle in cui il dispositivo ha scambiato immagini o messaggi clinici. «Mai visto nei flussi» non vuol dire fermo: il suo traffico può non passare da un router che esporta i flussi.':
    'This is network activity, not exams: an hour is «active» with at least {soglia} of traffic. The hours with DICOM or HL7 are those in which the device exchanged images or clinical messages. «Never seen in the flows» does not mean idle: its traffic may not go through a router that exports flows.',
  'è chiamato':
    'is called',
  'Dove sono, a cosa sono collegati, quanto si usano':
    'Where they are, what they connect to, how much they are used',
  'Dove sono: per ogni dispositivo lo switch e la porta su cui la rete ha imparato il suo MAC, la descrizione della porta, la VLAN (col nome dall’IPAM), la struttura HL7 e gli AE title DICOM, la posizione nell’inventario di riferimento (anche «REPARTO^STANZA^LETTO» di Medigate). Da qui CENSO suggerisce reparto, sede, stanza e letto; non scrive niente finché qualcuno non accetta, uno alla volta o per i dispositivi scelti. Le regole di posizione («la VLAN 210 è la Rianimazione») diventano suggerimenti su tutti i dispositivi a cui si applicano.':
    'Where they are: for each device the switch and port on which the network learnt its MAC, the port description, the VLAN (with its name from IPAM), the HL7 facility and the DICOM AE titles, the location in the reference inventory (also Medigate’s «WARD^ROOM^BED»). From these CENSO suggests ward, site, room and bed; it writes nothing until someone accepts, one at a time or for the selected devices. Location rules («VLAN 210 is Intensive care») become suggestions on every device they apply to.',
  'Collegamenti: con chi parla ogni dispositivo negli ultimi 7 giorni, dai flussi, con il ruolo dell’interlocutore: PACS, integrazione HL7, altro elettromedicale (centrale, gateway), servizi di rete, server, indirizzo esterno sconosciuto. Un elettromedicale che parla con un indirizzo pubblico sconosciuto è un segnale da guardare.':
    'Connections: whom each device talked to in the last 7 days, from the flows, with the peer’s role: PACS, HL7 integration, another medical device (central station, gateway), network services, server, unknown external address. A medical device talking to an unknown public address is a signal to look at.',
  'Utilizzo: le ore di attività di rete negli ultimi 30 giorni, le ore con DICOM o HL7 e una mappa giorno × ora. Non sono esami. Un dispositivo senza ore attive da 14 giorni è «fermo»; uno mai visto nei flussi non è fermo, è invisibile ai flussi. La soglia di un’ora attiva (50 KB) si cambia dalla vista Utilizzo.':
    'Usage: the hours of network activity in the last 30 days, the hours with DICOM or HL7 and a day × hour map. They are not exams. A device without active hours for 14 days is «idle»; one never seen in the flows is not idle, it is invisible to the flows. The threshold of an active hour (50 KB) is changed from the Usage view.',
  'Alienato':
    'Disposed of',
  'Cespite':
    'Fixed asset',
  'Cespite e contratti':
    'Fixed asset and contracts',
  'Collega a un contratto':
    'Link to a contract',
  'Copri subito questo dispositivo':
    'Cover this device right away',
  'Cosa scrivi tu':
    'What you write',
  'Crea il cespite':
    'Create the fixed asset',
  'Crea il cespite…':
    'Create the fixed asset…',
  'Crea il contratto':
    'Create the contract',
  'Dalla scheda e dalla rete':
    'From the record and the network',
  'Il cespite entra nel registro degli asset legato a questo dispositivo: uno solo per dispositivo.':
    'The fixed asset enters the asset register linked to this device: only one per device.',
  'Il contratto entra nel registro dei contratti, visibile all’Ingegneria clinica.':
    'The contract enters the contract register, visible to clinical engineering.',
  'Il dispositivo non è ancora nel registro dei beni. Il cespite si crea dalla scheda: nome, seriale, produttore, modello, reparto e sede sono già qui.':
    'The device is not in the asset register yet. The fixed asset is created from the record: name, serial, manufacturer, model, ward and site are already here.',
  'Il ruolo «Ingegneria clinica» lo vede in CENSO MED e ci collega gli elettromedicali (per esempio un contratto di manutenzione). Gli altri contratti non li vede.':
    'The «Clinical engineering» role sees it in CENSO MED and links the medical devices to it (for example a maintenance contract). It does not see the other contracts.',
  'In magazzino':
    'In stock',
  'In manutenzione':
    'Under maintenance',
  'Nessun contratto copre questo dispositivo.':
    'No contract covers this device.',
  'Non trovato':
    'Not found',
  'Numero di cespite':
    'Fixed asset number',
  'Nuovo contratto di manutenzione':
    'New maintenance contract',
  'Nuovo contratto di manutenzione…':
    'New maintenance contract…',
  'Ordine di acquisto':
    'Purchase order',
  'Per collegare un contratto serve il cespite: il contratto copre il bene.':
    'Linking a contract needs the fixed asset: the contract covers the asset.',
  'Prezzo di acquisto':
    'Purchase price',
  'Reparto, sede e centro di costo vengono dalla scheda.':
    'Ward, site and cost centre come from the record.',
  'Riservato':
    'Reserved',
  'Rivedi':
    'Review',
  'Togliere questo dispositivo dalla copertura di «{r}»?':
    'Remove this device from the coverage of «{r}»?',
  'Visibile all’Ingegneria clinica':
    'Visible to clinical engineering',
  'nessun contratto visibile all’Ingegneria clinica':
    'no contract visible to clinical engineering',
  'per esempio: intervento entro 8 ore, 2 manutenzioni preventive l’anno':
    'for example: on site within 8 hours, 2 preventive maintenances a year',
  'Dalla scheda di un dispositivo, sezione «Cespite e contratti»: il cespite si crea da una bozza che ha già nome, seriale, produttore, modello, reparto e sede (uno solo per dispositivo, nel registro degli asset). Il dispositivo si collega ai contratti che l’Ingegneria clinica vede: quelli con «Visibile all’Ingegneria clinica» acceso nel registro dei contratti e quelli che coprono già un suo dispositivo; gli altri restano invisibili. Un contratto di manutenzione si può creare da lì, e nasce visibile.':
    'From a device’s page, section «Fixed asset and contracts»: the fixed asset is created from a draft that already has name, serial, manufacturer, model, ward and site (only one per device, in the asset register). The device is linked to the contracts clinical engineering sees: those with «Visible to clinical engineering» on in the contract register and those already covering one of its devices; the others stay invisible. A maintenance contract can be created from there, and is born visible.',
  'Aggiungi PDF…':
    'Add PDF…',
  'Aggiungi foto del modello…':
    'Add model photo…',
  'Aggiungi foto…':
    'Add photos…',
  'Apri a dimensione intera':
    'Open at full size',
  'Caricato il':
    'Uploaded on',
  'Didascalia':
    'Caption',
  'Foto del dispositivo':
    'Device photos',
  'Foto del modello':
    'Model photos',
  'Foto e documenti':
    'Photos and documents',
  'Il browser non riesce a elaborare l’immagine.':
    'The browser cannot process the image.',
  'Le foto non devono mostrare pazienti né dati personali. Il browser le ridisegna prima di inviarle: niente dati EXIF né posizione GPS. Foto fino a 4 MiB, PDF fino a 10 MiB; spazio usato {u} di {q}.':
    'Photos must not show patients or personal data. The browser redraws them before sending: no EXIF data, no GPS position. Photos up to 4 MiB, PDFs up to 10 MiB; space used {u} of {q}.',
  'Nessun documento: manuale, dichiarazione di conformità, rapporti.':
    'No documents: manual, declaration of conformity, reports.',
  'Nessuna foto.':
    'No photos.',
  'Togliere questa foto?':
    'Remove this photo?',
  'Valgono per tutti i dispositivi dello stesso modello in questo ospedale.':
    'They apply to every device of the same model in this hospital.',
  'documento':
    'document',
  'Foto e documenti: le foto del dispositivo (fino a 6) e del modello (fino a 4, valgono per tutti i dispositivi di quel modello nell’ospedale) e i PDF (manuale, dichiarazione di conformità, rapporti). Il browser ridisegna ogni foto prima di inviarla, quindi non partono né i dati EXIF né la posizione GPS; le foto non devono mostrare pazienti. Si accettano solo JPEG, PNG, WebP e PDF, riconosciuti dal contenuto e non dal nome.':
    'Photos and documents: the photos of the device (up to 6) and of the model (up to 4, valid for every device of that model in the hospital) and PDFs (manual, declaration of conformity, reports). The browser redraws every photo before sending it, so neither EXIF data nor GPS position leave; photos must not show patients. Only JPEG, PNG, WebP and PDF are accepted, recognised by their content and not by their name.',
  'Allega il rapporto (PDF)…':
    'Attach the report (PDF)…',
  'Altra verifica':
    'Other check',
  'Applica i predefiniti del modello':
    'Apply the model defaults',
  'CENSO non giudica le misure: i limiti dipendono da classe, parte applicata e metodo, e l’esito è del tecnico.':
    'CENSO does not judge the measurements: the limits depend on class, applied part and method, and the outcome is the technician’s.',
  'Chi la fa':
    'Done by',
  'Ciò che di solito serve a questo modello: si applica a un dispositivo dalla sua scheda, e riempie solo ciò che manca.':
    'What this model usually needs: it is applied to a device from its page, and fills in only what is missing.',
  'Corrente di dispersione della parte applicata (µA)':
    'Applied part leakage current (µA)',
  'Corrente di dispersione dell’apparecchio (µA)':
    'Equipment leakage current (µA)',
  'Destinatari dell’area (separati da virgola)':
    'Area recipients (comma separated)',
  'Entro 60 giorni':
    'Within 60 days',
  'Eseguita da':
    'Performed by',
  'La prossima scadenza del piano dello stesso tipo si sposta dalla data di questa verifica.':
    'The next due date of the plan of the same kind moves from the date of this check.',
  'La verifica non è stata superata. Il dispositivo resta com’è finché non lo decidi tu: fuori servizio, compare così in tutte le viste e negli elenchi.':
    'The check was not passed. The device stays as it is until you decide: out of service, it shows as such in every view and list.',
  'Le scadenze delle manutenzioni e delle verifiche di sicurezza elettrica (CEI EN 62353) di tutti gli elettromedicali, e chi riceve i promemoria.':
    'The due dates of the maintenance and electrical safety checks (IEC 62353) of every medical device, and who gets the reminders.',
  'L’evento è spento nelle Impostazioni: non parte nessuna mail.':
    'The event is off in Settings: no mail is sent.',
  'Manutenzione preventiva':
    'Preventive maintenance',
  'Manutenzioni e verifiche':
    'Maintenance and checks',
  'Mettere il dispositivo fuori servizio?':
    'Take the device out of service?',
  'Metti fuori servizio':
    'Take out of service',
  'Misure (come le dà lo strumento)':
    'Measurements (as the instrument gives them)',
  'Modifica il piano':
    'Edit the plan',
  'Nessun piano di verifica: si creano dalla scheda di ogni dispositivo, o dai predefiniti del modello.':
    'No check plan: they are created from each device’s page, or from the model defaults.',
  'Nessun piano: quali verifiche, ogni quanto.':
    'No plan: which checks, how often.',
  'Nessuna verifica registrata.':
    'No check recorded.',
  'Non ora':
    'Not now',
  'Non superata':
    'Failed',
  'Non superate (90 giorni)':
    'Failed (90 days)',
  'Nuovo piano di verifica':
    'New check plan',
  'Nuovo piano…':
    'New plan…',
  'Ogni':
    'Every',
  'Ogni (mesi)':
    'Every (months)',
  'Più avanti':
    'Later',
  'Predefiniti del modello':
    'Model defaults',
  'Predefiniti del modello applicati: {n} piani nuovi.':
    'Model defaults applied: {n} new plans.',
  'Promemoria':
    'Reminders',
  'Rapporto e note':
    'Report and notes',
  'Registra una verifica':
    'Record a check',
  'Registra una verifica…':
    'Record a check…',
  'Resistenza del conduttore di protezione (Ω)':
    'Protective earth resistance (Ω)',
  'Resistenza di isolamento (MΩ)':
    'Insulation resistance (MΩ)',
  'Salva i predefiniti':
    'Save the defaults',
  'Scaduta':
    'Overdue',
  'Senza data':
    'No date',
  'Stato del dispositivo':
    'Device status',
  'Superata':
    'Passed',
  'Superata con riserva':
    'Passed with reservations',
  'Taratura':
    'Calibration',
  'Togliere il piano «{k}»?':
    'Remove the plan «{k}»?',
  'Togliere questa verifica e il suo rapporto?':
    'Remove this check and its report?',
  'Un piano sospeso non compare nelle scadenze e non manda promemoria.':
    'A suspended plan does not appear in the due dates and sends no reminders.',
  'Una mail al giorno con le verifiche a 60, 30, 7 giorni dalla scadenza e alla scadenza, e ogni settimana quelle scadute. Va ai destinatari delle notifiche del tenant (evento «manutenzioni e verifiche degli elettromedicali», in Impostazioni) e a quelli scritti qui.':
    'One mail a day with the checks 60, 30, 7 days from their due date and on it, and every week the overdue ones. It goes to the tenant’s notification recipients (event «maintenance and checks of the medical devices», in Settings) and to those written here.',
  'Verifica funzionale':
    'Functional check',
  'Verifica non superata':
    'Check failed',
  'Verifiche fatte':
    'Checks done',
  'Verifiche non superate negli ultimi 90 giorni':
    'Checks failed in the last 90 days',
  'Vuota: oggi, o dall’ultima verifica dello stesso tipo. Poi la sposta ogni verifica registrata.':
    'Empty: today, or from the last check of the same kind. Then every recorded check moves it.',
  'alternativo':
    'alternative',
  'differenziale':
    'differential',
  'il fornitore o l’Ingegneria clinica':
    'the supplier or clinical engineering',
  'mesi':
    'months',
  'metodo':
    'method',
  'rapporto':
    'report',
  'tecnico e ditta':
    'technician and company',
  'verifiche':
    'checks',
  'nome@ospedale.it':
    'name@hospital.org',
  'Ogni dispositivo ha i suoi piani: quale verifica (manutenzione preventiva, sicurezza elettrica CEI EN 62353, taratura, funzionale), ogni quanti mesi, con quale contratto e chi la fa. Una verifica registrata (data, chi, esito, misure di sicurezza elettrica, rapporto PDF) sposta la prossima scadenza. CENSO non giudica le misure; se una verifica non è superata propone di mettere il dispositivo fuori servizio, e decide una persona. I predefiniti del modello (piani e classe elettrica), scritti nella scheda del modello, si applicano a un dispositivo e riempiono solo ciò che manca. La vista «Manutenzioni e verifiche» elenca le scadenze di tutta l’area; ogni giorno parte una mail con quelle a 60, 30, 7 giorni e alla scadenza, e ogni settimana quelle scadute, ai destinatari delle notifiche (evento acceso in Impostazioni) e a quelli dell’area.':
    'Each device has its plans: which check (preventive maintenance, IEC 62353 electrical safety, calibration, functional), every how many months, with which contract and who does it. A recorded check (date, who, outcome, electrical safety measurements, PDF report) moves the next due date. CENSO does not judge the measurements; if a check fails it proposes taking the device out of service, and a person decides. The model defaults (plans and electrical class), written in the model card, are applied to a device and fill in only what is missing. The «Maintenance and checks» view lists the due dates of the whole area; every day a mail goes out with those at 60, 30, 7 days and on the due date, and every week the overdue ones, to the notification recipients (event on in Settings) and to the area’s.',
  'Altri candidati ({n})':
    'Other candidates ({n})',
  'CND':
    'CND',
  'Classificazione CND':
    'CND classification',
  'Codice catalogo':
    'Catalogue code',
  'Codici CND':
    'CND codes',
  'Confermate':
    'Confirmed',
  'Corrispondenza':
    'Match',
  'Corrispondenza probabile, da controllare: più voci del registro portano il codice del modello.':
    'Probable match, to check: several registry entries carry the model code.',
  'Corrispondenza probabile: il codice del modello compare nel registro una volta sola.':
    'Probable match: the model code appears only once in the registry.',
  'Corrispondenza verificata dall’amministrazione della piattaforma.':
    'Match verified by the platform administration.',
  'Corrispondenze dei modelli':
    'Model matches',
  'Da confermare':
    'To confirm',
  'Denominazione':
    'Name',
  'Denominazione commerciale':
    'Commercial name',
  'Dispositivi del registro tenuti':
    'Registry devices kept',
  'Fonti elettromedicali':
    'Medical device sources',
  'Fuori commercio dal':
    'Off the market since',
  'Iscritto al Repertorio':
    'Listed in the Repertorio',
  'Ministero':
    'Ministry',
  'Ministero della Salute (CND e registro dei dispositivi) e openFDA: aggiornamento e corrispondenze dei modelli da confermare':
    'Italian Ministry of Health (CND and device registry) and openFDA: refresh and model matches to confirm',
  'Ministero della Salute — Classificazione CND':
    'Italian Ministry of Health — CND classification',
  'Ministero della Salute — Registro dei dispositivi medici':
    'Italian Ministry of Health — Medical device registry',
  'Nessuna corrispondenza in questo stato.':
    'No match in this state.',
  'Nessuna corrispondenza nel registro dei dispositivi medici per questo modello.':
    'No match in the medical device registry for this model.',
  'Nessuna fonte letta finora: «Esegui adesso», o si aggiornano da sole una volta al mese di notte.':
    'No source read so far: «Run now», or they refresh by themselves once a month at night.',
  'Numero nel registro':
    'Registry number',
  'Quali':
    'Which',
  'Registro del Ministero della Salute':
    'Italian Ministry of Health registry',
  'Rifiutate':
    'Rejected',
  'Rivista da':
    'Reviewed by',
  'Scegli questa':
    'Choose this one',
  'Si conosce solo la serie: il registro si consulta per un modello preciso.':
    'Only the series is known: the registry is looked up for a specific model.',
  'Usa questo codice CND':
    'Use this CND code',
  'apparecchiature (CND Z e W) dei produttori noti':
    'equipment (CND Z and W) of known manufacturers',
  'confermate':
    'confirmed',
  'dal numero di prodotto, nel registro del Ministero della Salute':
    'from the product number, in the Italian Ministry of Health registry',
  'in commercio':
    'on the market',
  'openFDA':
    'openFDA',
  'probabile':
    'probable',
  'rifiutate':
    'rejected',
  'Registro del Ministero: per ogni modello CENSO cerca nel registro dei dispositivi medici del Ministero della Salute la voce con il suo codice (per esempio «INTELLIVUE MX450», codice 866062) e ne mostra classificazione CND, CIVAB, iscrizione al Repertorio e fuori commercio, come «verificata» o «probabile». «Usa questo codice CND» lo scrive nella scheda. Se un dispositivo dice in rete il suo numero di prodotto, il registro dà anche il modello esatto.':
    'Ministry registry: for each model CENSO looks in the Italian Ministry of Health medical device registry for the entry with its code (for example «INTELLIVUE MX450», code 866062) and shows its CND classification, CIVAB, Repertorio listing and end of sale, as «verified» or «probable». «Use this CND code» writes it on the record. If a device announces its product number on the network, the registry also gives the exact model.',
  'Aperti':
    'Open',
  'Apri il richiamo sul sito dell’autorità':
    'Open the recall on the authority’s website',
  'Attiva {fonte}':
    'Turn on {fonte}',
  'Autorità':
    'Authority',
  'Avviato il':
    'Started on',
  'Avvisi di sicurezza informatica (CISA ICSMA)':
    'Cybersecurity advisories (CISA ICSMA)',
  'Avvisi informatici aperti':
    'Open cyber advisories',
  'Azione richiesta dalla ditta':
    'Action requested by the firm',
  'Basi dati pubbliche':
    'Public databases',
  'CISA ICSMA':
    'CISA ICSMA',
  'CISA — avvisi di sicurezza informatica dei dispositivi medici (ICSMA)':
    'CISA — cybersecurity advisories for medical devices (ICSMA)',
  'CND / codice FDA':
    'CND / FDA code',
  'Causa':
    'Cause',
  'Chiamate openFDA oggi':
    'openFDA calls today',
  'Classe FDA':
    'FDA class',
  'Codice prodotto FDA':
    'FDA product code',
  'Come lo gestite':
    'How you handle it',
  'Con la fonte openFDA attiva, CENSO cerca ogni modello nella banca dati GUDID della FDA e ne mostra nella scheda nome commerciale, codice catalogo, classe FDA, codice prodotto, sicurezza in risonanza magnetica e la definizione del termine GMDN, al posto della destinazione d’uso che la FDA non pubblica. Ogni giorno legge i richiami e i rapporti di enforcement dei produttori dei vostri dispositivi e li abbina ai modelli: un richiamo vale per un modello quando ne porta la sigla (per esempio «MX450», anche dentro «MX400/450») o il codice catalogo (866062); un richiamo che nomina solo la serie non si abbina a nessuno.':
    'With the openFDA source on, CENSO looks up every model in the FDA’s GUDID database and shows on the record its brand name, catalogue number, FDA class, product code, MRI safety and the definition of its GMDN term, in place of the intended use the FDA does not publish. Every day it reads the recalls and enforcement reports of your devices’ manufacturers and matches them to the models: a recall applies to a model when it carries its code (for example «MX450», also inside «MX400/450») or its catalogue number (866062); a recall that names only the series matches none.',
  'Corrispondenza probabile, da controllare: più modelli della FDA portano il codice del modello.':
    'Probable match, to be checked: several FDA models carry the model’s code.',
  'Corrispondenza probabile: un solo modello della FDA porta il codice del modello.':
    'Probable match: only one FDA model carries the model’s code.',
  'Cosa dice l’autorità':
    'What the authority says',
  'Cosa dà':
    'What it provides',
  'Definizione del termine GMDN {codice} «{nome}»: la banca dati FDA non riporta la destinazione d’uso del fabbricante.':
    'Definition of the GMDN term {codice} «{nome}»: the FDA database does not carry the manufacturer’s intended use.',
  'Destinazione d’uso':
    'Intended use',
  'Di classe I':
    'Class I',
  'Dispositivi interessati':
    'Devices concerned',
  'Ditta':
    'Firm',
  'FDA (Stati Uniti)':
    'FDA (United States)',
  'FDA e richiami':
    'FDA and recalls',
  'FDA — identificazione del dispositivo (GUDID)':
    'FDA — device identification (GUDID)',
  'Gestito':
    'Handled',
  'I richiami e gli avvisi di sicurezza delle autorità sui modelli dei vostri elettromedicali, e gli avvisi di sicurezza informatica sui vostri dispositivi: come li gestite.':
    'The authorities’ recalls and safety notices on the models of your medical devices, and the cybersecurity advisories on your devices: how you handle them.',
  'I testi sono quelli pubblicati dall’autorità, nella sua lingua.':
    'The texts are the ones the authority published, in its language.',
  'I vostri dispositivi interessati ({n})':
    'Your devices concerned ({n})',
  'Il Paese accende le basi dati che gli servono; ognuna si può poi accendere o spegnere a mano.':
    'The country turns on the databases it needs; each can then be turned on or off by hand.',
  'Il Paese dell’ospedale accende le basi dati pubbliche che gli servono (registri dei dispositivi, richiami); ognuna si può accendere o spegnere a mano.':
    'The hospital’s country turns on the public databases it needs (device registers, recalls); each can be turned on or off by hand.',
  'Il Paese non è ancora stato scelto: CENSO assume l’Italia. Sceglietelo per attivare le basi dati giuste.':
    'The country has not been chosen yet: CENSO assumes Italy. Choose it to turn on the right databases.',
  'Il richiamo riguarda il modello: verificate con il fornitore numero di serie e versione dei vostri esemplari.':
    'The recall concerns the model: check with the supplier the serial number and version of your units.',
  'In gestione':
    'In progress',
  'In «Paese e basi dati» si sceglie il Paese dell’ospedale: il Paese accende le basi dati pubbliche che gli servono (per l’Italia il Ministero della Salute, per gli Stati Uniti la FDA) e ognuna si può poi accendere o spegnere a mano; un ospedale italiano, per esempio, può tenere anche i richiami della FDA, perché gli stessi modelli si vendono in entrambi i mercati. Le schede, il catalogo modelli e i richiami mostrano solo le fonti attive. Le fonti si leggono una volta per tutta la piattaforma e non si scaricano se nessun ospedale le usa. Gli avvisi di sicurezza informatica della CISA valgono ovunque e sono sempre attivi. Finché il Paese non è scelto, CENSO assume l’Italia.':
    'In «Country and databases» you choose the hospital’s country: the country turns on the public databases it needs (the Ministero della Salute for Italy, the FDA for the United States) and each can then be turned on or off by hand; an Italian hospital, for example, can keep the FDA’s recalls too, because the same models are sold in both markets. The records, the models catalogue and the recalls show only the sources that are on. The sources are read once for the whole platform and are not downloaded if no hospital uses them. CISA’s cybersecurity advisories apply everywhere and are always on. Until the country is chosen, CENSO assumes Italy.',
  'La vista «Richiami e avvisi» elenca i richiami sui vostri modelli con i dispositivi interessati, e accanto gli avvisi di sicurezza informatica della CISA sui vostri dispositivi. Per ogni richiamo si annota come lo si gestisce (da valutare, in gestione, gestito, non ci riguarda) con una nota: il richiamo riguarda il modello, il numero di serie e la versione si verificano con il fornitore. I richiami pubblicati negli ultimi 60 giorni arrivano una volta per mail ai destinatari delle notifiche (evento acceso in Impostazioni) e a quelli dell’area; quelli più vecchi si vedono solo nell’elenco. I testi sono quelli dell’autorità, nella sua lingua.':
    'The «Recalls and notices» view lists the recalls on your models with the devices concerned, and next to them CISA’s cybersecurity advisories on your devices. For each recall you record how it is handled (to assess, in progress, handled, does not concern us) with a note: the recall concerns the model, serial number and version are checked with the supplier. Recalls published in the last 60 days are mailed once to the notification recipients (event on in Settings) and to the area’s; older ones are only listed. The texts are the authority’s, in its language.',
  'Le basi dati pubbliche (Ministero della Salute, openFDA…), lette in sottofondo solo se un ospedale le usa (il suo Paese le accende), e le corrispondenze con i modelli del catalogo da confermare.':
    'The public databases (Ministero della Salute, openFDA…), read in the background only if a hospital uses them (its country turns them on), and the matches with the catalogue’s models to confirm.',
  'Le fonti si leggono una volta per tutta la piattaforma; ogni ospedale vede solo quelle attive per sé. Una fonte che nessun ospedale usa non viene scaricata.':
    'The sources are read once for the whole platform; each hospital sees only the ones on for it. A source no hospital uses is not downloaded.',
  'Ministero della Salute — Classificazione CND e Banca dati dei dispositivi medici':
    'Ministero della Salute (Italy) — CND classification and medical devices database',
  'N. di serie':
    'Serial no.',
  'Nessun avviso CISA sui vostri elettromedicali.':
    'No CISA advisory on your medical devices.',
  'Nessun richiamo sui modelli dei vostri elettromedicali. Si cercano per modello esatto: i dispositivi di cui si conosce solo la serie non si abbinano.':
    'No recall on the models of your medical devices. They are searched by exact model: devices whose series only is known are not matched.',
  'Nessun richiamo sul modello dalle fonti attive.':
    'No recall on the model from the sources that are on.',
  'Nessuna corrispondenza nella banca dati GUDID della FDA per questo modello (o non ancora cercata).':
    'No match in the FDA’s GUDID database for this model (or not looked up yet).',
  'Nessuna fonte di richiami attiva per il vostro ospedale.':
    'No recall source is on for your hospital.',
  'Nome commerciale':
    'Brand name',
  'Non ci riguarda':
    'Does not concern us',
  'Ospedali che la usano':
    'Hospitals using it',
  'Paese della fonte':
    'Source country',
  'Paese dell’ospedale':
    'Hospital country',
  'Paese e basi dati':
    'Country and databases',
  'Per il vostro Paese':
    'For your country',
  'Pubblicato il':
    'Published on',
  'Regolamento':
    'Regulation',
  'Richiami da gestire':
    'Recalls to handle',
  'Richiami e avvisi':
    'Recalls and notices',
  'Richiami e avvisi ({n})':
    'Recalls and notices ({n})',
  'Richiami e avvisi di sicurezza delle autorità':
    'Authorities’ recalls and safety notices',
  'Richiami letti da: {fonti}.':
    'Recalls read from: {fonti}.',
  'Richiami sul modello':
    'Recalls on the model',
  'Richiami sul modello ({n})':
    'Recalls on the model ({n})',
  'Richiami tenuti':
    'Recalls kept',
  'Richiamo':
    'Recall',
  'Salvato: le schede e i richiami seguono le nuove fonti.':
    'Saved: the records and recalls follow the new sources.',
  'Si conosce solo la serie: i richiami si abbinano al modello preciso.':
    'Only the series is known: recalls are matched to the exact model.',
  'Si conosce solo la serie: la banca dati si consulta per un modello preciso.':
    'Only the series is known: the database is looked up for an exact model.',
  'Sicurezza in risonanza magnetica':
    'MRI safety',
  'Specialità':
    'Specialty',
  'Stato presso l’autorità':
    'Status at the authority',
  'Tutti i richiami':
    'All recalls',
  'UDI-DI primario':
    'Primary UDI-DI',
  'Ultima modifica: {chi}, {quando}':
    'Last change: {chi}, {quando}',
  'Unione europea':
    'European Union',
  'Visto dal':
    'Seen since',
  'classificazione':
    'classification',
  'con chiave API':
    'with API key',
  'identificazione dei modelli':
    'model identification',
  'la classe più grave (FDA)':
    'the most serious class (FDA)',
  'openFDA — Food and Drug Administration (Stati Uniti)':
    'openFDA — Food and Drug Administration (United States)',
  'per esempio: sentito il fornitore, aggiornamento software pianificato il …':
    'for example: supplier contacted, software update planned on …',
  'richiami':
    'recalls',
  'sempre attiva':
    'always on',
  'senza chiave: CENSO_OPENFDA_API_KEY ne dà 120 mila':
    'without a key: CENSO_OPENFDA_API_KEY gives 120 thousand',
  'sicurezza informatica':
    'cybersecurity',
  '{n} abbinati a un modello':
    '{n} matched to a model',
  '«Esegui adesso» legge la fonte anche se nessun ospedale la usa ancora; da sola si aggiorna solo quando serve a qualcuno.':
    '«Run now» reads the source even if no hospital uses it yet; on its own it updates only when someone needs it.',
  'Avvisa su manutenzioni, verifiche e richiami degli elettromedicali':
    'Notify about medical devices’ maintenance, checks and recalls',
  'CENSO MED: le manutenzioni e le verifiche di sicurezza elettrica in scadenza (60, 30, 7 giorni prima e alla scadenza), ogni settimana quelle scadute, e i nuovi richiami delle autorità sui modelli dei vostri elettromedicali. Gli avvisi vanno anche ai destinatari scelti nell’area CENSO MED.':
    'CENSO MED: maintenance and electrical safety checks coming due (60, 30, 7 days before and on the due date), every week the overdue ones, and the authorities’ new recalls on the models of your medical devices. Notices also go to the recipients chosen in the CENSO MED area.',
  'EUDAMED':
    'EUDAMED',
  'Per gli ospedali dell’Unione europea (e di Norvegia, Islanda e Liechtenstein) è attiva EUDAMED, la banca dati europea dei dispositivi medici: per ogni modello CENSO trova il dispositivo registrato dal fabbricante e ne mostra il riferimento, la classe di rischio secondo il regolamento MDR, il codice della nomenclatura europea EMDN (gli stessi codici della CND italiana: «Usa questo codice EMDN» lo scrive nella scheda), se è ancora sul mercato e in quali Paesi dell’Unione è disponibile. La parte di EUDAMED sugli avvisi di sicurezza non è pubblica: i richiami arrivano dalle autorità nazionali.':
    'For hospitals in the European Union (and in Norway, Iceland and Liechtenstein) EUDAMED, the European database of medical devices, is on: for every model CENSO finds the device registered by the manufacturer and shows its reference, its risk class under the MDR, its code in the European nomenclature EMDN (the same codes as the Italian CND: «Use this EMDN code» writes it on the record), whether it is still on the market and in which countries of the Union it is available. EUDAMED’s part on safety notices is not public: recalls come from the national authorities.',
  'EUDAMED — Banca dati europea dei dispositivi medici (Commissione europea)':
    'EUDAMED — European database on medical devices (European Commission)',
  'EUDAMED — banca dati europea':
    'EUDAMED — European database',
  'Nessuna corrispondenza in EUDAMED per questo modello (o non ancora cercata).':
    'No match in EUDAMED for this model (or not looked up yet).',
  'Usa questo codice EMDN':
    'Use this EMDN code',
  'Corrispondenza probabile: un solo dispositivo del fabbricante in EUDAMED porta il codice del modello.':
    'Probable match: only one of the manufacturer’s devices in EUDAMED carries the model’s code.',
  'Corrispondenza probabile, da controllare: più dispositivi del fabbricante in EUDAMED portano il codice del modello.':
    'Probable match, to be checked: several of the manufacturer’s devices in EUDAMED carry the model’s code.',
  'Riferimento del fabbricante':
    'Manufacturer’s reference',
  'Classe di rischio (MDR)':
    'Risk class (MDR)',
  'Nomenclatura EMDN':
    'EMDN nomenclature',
  'Sul mercato':
    'On the market',
  'Paesi UE in cui è disponibile':
    'EU countries where available',
  'Descrizione del fabbricante':
    'Manufacturer’s description',
  'sul mercato':
    'on the market',
  'non più immesso sul mercato':
    'no longer placed on the market',
  'non destinato al mercato UE':
    'not intended for the EU market',
  'MHRA (Regno Unito)':
    'MHRA (United Kingdom)',
  'Health Canada (Canada)':
    'Health Canada (Canada)',
  'Richiami delle autorità nazionali':
    'National authorities’ recalls',
  'Oltre alla FDA, CENSO legge i richiami e gli avvisi di sicurezza delle autorità nazionali, ognuno attivo per il suo Paese e accendibile a mano dagli altri: MHRA per il Regno Unito (le Field Safety Notice settimanali e gli avvisi dell’MHRA, con il modello indicato nella riga «Model»), Health Canada per il Canada (i richiami dei dispositivi medici, con la tabella dei prodotti interessati e i loro codici), BfArM per la Germania, ANSM per la Francia e Swissmedic per la Svizzera (gli avvisi di sicurezza dei fabbricanti). Si tengono solo i richiami dei produttori che CENSO conosce, e si abbinano ai modelli come quelli della FDA.':
    'Besides the FDA, CENSO reads the recalls and safety notices of the national authorities, each on for its own country and switchable on by hand for the others: the MHRA for the United Kingdom (the weekly Field Safety Notices and the MHRA’s alerts, with the model given in the «Model» line), Health Canada for Canada (the medical device recalls, with the table of affected products and their codes), the BfArM for Germany, the ANSM for France and Swissmedic for Switzerland (the manufacturers’ field safety notices). Only the recalls of the makers CENSO knows are kept, and they are matched to the models like the FDA’s.',
  'MHRA — avvisi di sicurezza e Field Safety Notice (Regno Unito)':
    'MHRA — safety alerts and Field Safety Notices (United Kingdom)',
  'Health Canada — richiami dei dispositivi medici (Canada)':
    'Health Canada — medical device recalls (Canada)',
  'BfArM (Germania)':
    'BfArM (Germany)',
  'ANSM (Francia)':
    'ANSM (France)',
  'Swissmedic (Svizzera)':
    'Swissmedic (Switzerland)',
  'Altri avvisi sui vostri produttori ({n})':
    'Other notices on your makers ({n})',
  'Avvisi degli ultimi 180 giorni che non indicano un modello del catalogo ma vengono dai produttori dei vostri elettromedicali: spesso nominano solo il prodotto e i modelli stanno nella lettera del fabbricante. Da controllare; non arrivano per mail.':
    'Notices of the last 180 days that name no model of the catalogue but come from the makers of your medical devices: they often name only the product and the models are in the manufacturer’s letter. To be checked; they are not mailed.',
  'Molti avvisi nazionali nominano solo il prodotto («Azurion») e lasciano i modelli alla lettera del fabbricante: quelli dei produttori dei vostri elettromedicali compaiono in «Altri avvisi sui vostri produttori», da controllare, senza mail.':
    'Many national notices name only the product («Azurion») and leave the models to the manufacturer’s letter: those of your medical devices’ makers appear in «Other notices on your makers», to be checked, without mail.',
  'BfArM — avvisi di sicurezza dei fabbricanti (Germania)':
    'BfArM — manufacturers’ field safety notices (Germany)',
  'ANSM — informazioni di sicurezza sui dispositivi medici (Francia)':
    'ANSM — safety information on medical devices (France)',
  'Swissmedic — azioni correttive di sicurezza (Svizzera)':
    'Swissmedic — field safety corrective actions (Switzerland)',
  'Ogni ospedale con inventario misurato solo con ciò che hanno insegnato gli altri: è ciò che CENSO farebbe in un ospedale senza riferimento. «Senza cattura» toglie i fatti del filo (DICOM, HL7, User-Agent, TLS, SSH, SMB, SIP), che senza porta mirror o TAP non arrivano. «Solo validate»: le regole viste in almeno due ospedali, quelle che entrano in uso da sole. In ogni cella: riconosciuti come modello · come elettromedicale.':
    'Each hospital with an inventory measured only with what the others taught: it is what CENSO would do in a hospital without a reference. «Without capture» removes the wire facts (DICOM, HL7, User-Agent, TLS, SSH, SMB, SIP), which do not arrive without a mirror port or TAP. «Only validated»: the rules seen in at least two hospitals, the ones that come into use by themselves. In each cell: recognised as model · as medical device.',
  'Quanto si riconosce un ospedale nuovo':
    'How much a new hospital is recognised',
  'con il proprio inventario':
    'with its own inventory',
  'dagli altri, senza cattura':
    'from the others, without capture',
  'dagli altri, solo regole validate':
    'from the others, only validated rules',
  'imparando dagli altri ospedali':
    'learning from the other hospitals',
  'modello · elettromedicale; {n} falsi positivi':
    'model · medical device; {n} false positives',
  'modello: {p}':
    'model: {p}',
  'ospedale nuovo, {scenario}: elettromedicale':
    'new hospital, {scenario}: medical device',
  'senza cattura, solo validate':
    'without capture, only validated',
  'sospesa da CENSO: {motivo}':
    'suspended by CENSO: {motivo}',
  'Rete dei monitor':
    'Monitor network',
  'modello dalla rete dei monitor':
    'model from the monitor network',
  'modello {m}':
    'model {m}',
  'rete dei monitor (centrale o monitor)':
    'monitor network (central station or monitor)',
  'software {v}':
    'software {v}',
  'tipo dalla rete dei monitor':
    'type from the monitor network',
  'Se la sonda ha una gamba nella rete dei monitor, legge anche ciò che i monitor annunciano di sé: reparto e stanza configurati sulla centrale GE CARESCAPE, letto, modello, seriale e versione software dei monitor Philips IntelliVue quando li dichiarano. Diventano suggerimenti di confidenza alta. I dati dei pazienti che passano su quella rete non vengono letti né salvati.':
    'If the probe has a leg in the monitor network, it also reads what the monitors announce about themselves: the unit and room configured on the GE CARESCAPE central station, and the bed, model, serial number and software version of Philips IntelliVue monitors when they declare them. They become high-confidence suggestions. Patient data passing on that network is neither read nor saved.',
  '{w} di questi sono noti solo per indirizzo.':
    '{w} of them are known only by address.',
  'Anche i sistemi che parlano DICOM o HL7 visti solo per indirizzo, dietro un router (PACS, RIS, motori HL7, analizzatori di laboratorio): un apparecchio riconosciuto dal prodotto diventa elettromedicale, gli altri server o postazione secondo il sistema che hanno dichiarato sul filo. I PC d’ufficio visti solo per indirizzo non si registrano.':
    'Also the systems speaking DICOM or HL7 seen only by address, behind a router (PACS, RIS, HL7 engines, lab analysers): an apparatus recognised by its product becomes a medical device, the others a server or a workstation according to the system they declared on the wire. Office PCs seen only by address are not registered.',
  'Registra anche i sistemi che CENSO conosce solo per indirizzo perché stanno dietro un router (la sonda vede il loro IP, non il MAC) e che parlano DICOM o HL7: PACS, RIS, motori di integrazione, analizzatori di laboratorio. Un apparecchio riconosciuto dal prodotto (un Alinity, un Luminos) diventa elettromedicale; un computer che serve un’applicazione clinica resta un computer, server o postazione secondo la versione di Windows o la distribuzione che ha dichiarato sul filo, con AE title, applicazioni HL7 e protocolli clinici fra gli attributi. Quando la versione di Windows vale per due edizioni (build 14393: Server 2016 o Windows 10) il CI è un server per convenzione, con le due possibilità scritte. Se poi una scansione o l’hypervisor trova lo stesso host, ritrova questo CI invece di crearne un secondo. I PC d’ufficio visti solo per indirizzo non si registrano: con un IP dinamico l’indirizzo non identifica niente.':
    'It also registers the systems CENSO knows only by address because they sit behind a router (the probe sees their IP, not their MAC) and that speak DICOM or HL7: PACS, RIS, integration engines, lab analysers. An apparatus recognised by its product (an Alinity, a Luminos) becomes a medical device; a computer serving a clinical application stays a computer, a server or a workstation according to the Windows version or the distribution it declared on the wire, with AE titles, HL7 applications and clinical protocols among its attributes. When the Windows version fits two editions (build 14393: Server 2016 or Windows 10) the CI is a server by convention, with both possibilities written down. If a scan or the hypervisor later finds the same host, it finds this CI instead of creating a second one. Office PCs seen only by address are not registered: with a dynamic IP the address identifies nothing.',
  'AE title':
    'AE title',
  'Altro traffico':
    'Other traffic',
  'Applicazione clinica':
    'Clinical application',
  'Applicazioni HL7':
    'HL7 applications',
  'Che cos’è':
    'What it is',
  'Cosa dice di sé':
    'What it says about itself',
  'Cosa fa':
    'What it does',
  'Elettromedicali collegati':
    'Connected medical devices',
  'Gli elettromedicali che gli parlano ({n})':
    'The medical devices talking to it ({n})',
  'Gli manda immagini (DICOM)':
    'Sends it images (DICOM)',
  'Gli manda immagini o lo interroga (DICOM)':
    'Sends images to it or queries it (DICOM)',
  'I computer che parlano DICOM o HL7 e non sono elettromedicali: PACS, RIS, motori di integrazione, middleware di laboratorio, postazioni di refertazione. Li riconosce la sonda dal traffico; quanti elettromedicali gli parlano viene dai flussi degli ultimi 7 giorni.':
    'The computers that speak DICOM or HL7 and are not medical devices: PACS, RIS, integration engines, lab middleware, reporting workstations. The probe recognises them from the traffic; how many medical devices talk to them comes from the flows of the last 7 days.',
  'I sistemi con cui lavorano gli elettromedicali: PACS, RIS, motori HL7, middleware di laboratorio, postazioni di refertazione. Sono computer, non elettromedicali, e non entrano nei conteggi dei dispositivi.':
    'The systems the medical devices work with: PACS, RIS, HL7 engines, lab middleware, reporting workstations. They are computers, not medical devices, and do not count among the devices.',
  'Implementazione':
    'Implementation',
  'Messaggi HL7':
    'HL7 messages',
  'Nome sulla rete':
    'Name on the network',
  'Non in CMDB':
    'Not in the CMDB',
  'PACS, RIS, HL7: computer, non elettromedicali':
    'PACS, RIS, HL7: computers, not medical devices',
  'Server web':
    'Web server',
  'Sistema clinico':
    'Clinical system',
  'Sistemi clinici':
    'Clinical systems',
  'Sistemi clinici: i computer con cui lavorano gli elettromedicali — PACS, RIS, motori di integrazione HL7, middleware di laboratorio, postazioni di refertazione. La sonda li riconosce dal traffico DICOM e HL7; non sono elettromedicali e non entrano nei conteggi dei dispositivi, ma l’Ingegneria clinica li vede con il sistema operativo, l’applicazione, gli AE title e le applicazioni HL7. Aprendone uno si vedono gli elettromedicali che gli parlano (dai flussi degli ultimi 7 giorni) e cosa ci fanno: gli mandano immagini, ne ricevono, scambiano messaggi HL7. «Non in CMDB» conta quelli visti sul filo e non ancora registrati.':
    'Clinical systems: the computers the medical devices work with — PACS, RIS, HL7 integration engines, lab middleware, reporting workstations. The probe recognises them from DICOM and HL7 traffic; they are not medical devices and do not count among the devices, but clinical engineering sees them with their operating system, application, AE titles and HL7 applications. Opening one shows the medical devices that talk to it (from the flows of the last 7 days) and what they do with it: they send it images, receive images from it, exchange HL7 messages. “Not in the CMDB” counts those seen on the wire and not registered yet.',
  'Struttura (facility)':
    'Facility',
  'Ultima volta sul filo':
    'Last seen on the wire',
  'può essere {edizioni}':
    'may be {edizioni}',
  'visti sul filo, non ancora registrati':
    'seen on the wire, not registered yet',
  'Alcuni apparati esportano i flussi verso un solo collector, come il distributed switch di VMware: con la sonda lì, gli altri strumenti (NMS, SIEM, ntopng) li perderebbero. La sonda manda a ogni destinazione una copia di ogni pacchetto che riceve, intatta e prima di qualunque campionamento, template compresi.':
    'Some devices export flows to one collector only, like the VMware distributed switch: with the probe there, the other tools (NMS, SIEM, ntopng) would lose them. The probe sends each destination a copy of every packet it receives, unchanged and before any sampling, templates included.',
  'Destinazioni (indirizzo:porta, una per riga, al più 8)':
    'Destinations (address:port, one per line, at most 8)',
  'Il collector di destinazione vede il vDS come se esportasse a lui, non la sonda: serve quando distingue gli apparati dall’indirizzo o ne riceve più d’uno. Richiede il permesso della cattura (CAP_NET_RAW) e solo IPv4; un router con il controllo anti-spoofing (uRPF) fra sonda e destinazione scarta questi pacchetti. Senza la spunta i pacchetti partono dalla sonda: il collector va configurato per accettarli dal suo indirizzo.':
    'The destination collector sees the vDS as if it exported to it, not the probe: needed when it tells devices apart by address or receives more than one. Requires the capture permission (CAP_NET_RAW) and IPv4 only; a router with anti-spoofing (uRPF) between the probe and the destination drops these packets. Without the tick the packets leave from the probe: the collector must be set to accept them from its address.',
  'Inoltro ad altri collector':
    'Forwarding to other collectors',
  'La sonda non può mantenere il mittente originale e invia dal proprio indirizzo: {motivo}':
    'The probe cannot keep the original sender and sends from its own address: {motivo}',
  'Mantieni l’indirizzo dell’esportatore come mittente':
    'Keep the exporter’s address as the sender',
  'Sempre lì, «Inoltro ad altri collector»: per gli apparati che esportano i flussi verso un solo collector (il distributed switch di VMware) la sonda manda una copia di ogni pacchetto ricevuto agli altri strumenti (NMS, SIEM, ntopng), fino a otto destinazioni indirizzo:porta. Con «Mantieni l’indirizzo dell’esportatore come mittente» il collector di destinazione vede il vDS e non la sonda (serve il permesso della cattura, solo IPv4). Sotto il campo, per ogni destinazione, quanti pacchetti sono partiti e gli eventuali errori.':
    'In the same place, “Forwarding to other collectors”: for devices that export flows to one collector only (the VMware distributed switch) the probe sends a copy of every packet received to the other tools (NMS, SIEM, ntopng), up to eight address:port destinations. With “Keep the exporter’s address as the sender” the destination collector sees the vDS and not the probe (it needs the capture permission, IPv4 only). Under the field, for each destination, how many packets left and any errors.',
  '{n} con il mittente originale':
    '{n} with the original sender',
  '{n} pacchetti inoltrati':
    '{n} packets forwarded',
  'Altri interlocutori':
    'Other peers',
  'Altri {n} interlocutori meno attivi non sono disegnati.':
    '{n} other less active peers are not drawn.',
  'Apri il sistema clinico':
    'Open the clinical system',
  'Apri la scheda del dispositivo':
    'Open the device record',
  'Conversazioni dagli agenti CENSO sui sistemi clinici':
    'Conversations from the CENSO agents on the clinical systems',
  'Conversazioni dagli agenti: dove sui sistemi clinici (PACS, RIS, motori HL7, centrali) c’è l’agente CENSO, ogni 30 secondi legge la tabella delle connessioni della macchina — con chi parla, su quale porta, con quale programma — e mai il contenuto: niente immagini, messaggi, parametri o dati dei pazienti. CENSO tiene solo le conversazioni con elettromedicali e sistemi clinici o sulle porte DICOM e HL7, per 30 giorni. Così si vedono anche i monitor che parlano con la centrale nella stessa VLAN, dove i flussi non arrivano, e le ore in cui un elettromedicale è collegato contano come utilizzo. Un sistema visto solo dal suo agente compare comunque fra i sistemi clinici, con il segno «agente». Si spegne in «Sistemi clinici».':
    'Conversations from the agents: where the CENSO agent runs on the clinical systems (PACS, RIS, HL7 engines, central stations), every 30 seconds it reads the machine’s connection table — whom it talks to, on which port, with which program — and never the content: no images, messages, parameters or patient data. CENSO keeps only the conversations with medical devices and clinical systems or on the DICOM and HL7 ports, for 30 days. This also shows the monitors talking to their central station in the same VLAN, where flows do not reach, and the hours a medical device is connected count as usage. A system seen only by its agent still appears among the clinical systems, marked “agent”. It is switched off in “Clinical systems”.',
  'Dove sui sistemi clinici c’è l’agente CENSO, ogni 30 secondi legge la tabella delle connessioni: con chi parla, su quale porta, quale programma. Mai il contenuto: niente immagini, messaggi o dati dei pazienti. Si tengono solo le conversazioni con elettromedicali e sistemi clinici o sulle porte DICOM e HL7, per 30 giorni; le ore in cui un elettromedicale è collegato contano come utilizzo. Vede i monitor che parlano con la centrale nella stessa VLAN, dove i flussi non arrivano.':
    'Where the CENSO agent runs on the clinical systems, every 30 seconds it reads the connection table: whom it talks to, on which port, which program. Never the content: no images, messages or patient data. Only the conversations with medical devices and clinical systems or on the DICOM and HL7 ports are kept, for 30 days; the hours a medical device is connected count as usage. It sees the monitors talking to their central station in the same VLAN, where flows do not reach.',
  'Elettromedicali e sistemi clinici, e chi parla con chi: dai flussi e dagli agenti CENSO sui sistemi clinici. Indirizzi, porte e programmi, mai il contenuto. Doppio clic su un nodo per aprirlo.':
    'Medical devices and clinical systems, and who talks to whom: from the flows and from the CENSO agents on the clinical systems. Addresses, ports and programs, never the content. Double-click a node to open it.',
  'Le ore in cui l’elettromedicale è collegato a un sistema clinico con l’agente CENSO contano come attive anche senza flussi.':
    'The hours the medical device is connected to a clinical system with the CENSO agent count as active even without flows.',
  'L’agente CENSO su questo sistema riporta le sue conversazioni':
    'The CENSO agent on this system reports its conversations',
  'Mappa clinica':
    'Clinical map',
  'Mappa clinica: elettromedicali, sistemi clinici e chi parla con chi, dai flussi e dagli agenti, nella stessa mappa della correlazione (viste gerarchica, radiale, organica). Il colore dice il protocollo (DICOM, HL7, altro), il tratteggio che il collegamento l’ha visto solo l’agente. Con molti elettromedicali la mappa dell’area li raggruppa per modello; doppio clic su un nodo apre la scheda del dispositivo o il sistema clinico. La stessa mappa, attorno al singolo oggetto, sta nei Collegamenti della scheda del dispositivo, nella finestra di ogni sistema clinico e nella scheda del CI («Mappa clinica»).':
    'Clinical map: medical devices, clinical systems and who talks to whom, from the flows and from the agents, in the same map as the correlation (hierarchical, radial, organic views). The colour tells the protocol (DICOM, HL7, other), a dashed line that only the agent saw the link. With many medical devices the area map groups them by model; double-click a node to open the device record or the clinical system. The same map, around a single object, is in the Connections of the device record, in the window of each clinical system and in the CI page (“Clinical map”).',
  'Molti elettromedicali: sono raggruppati per modello. Aprendo un sistema si vedono uno per uno.':
    'Many medical devices: they are grouped by model. Opening a system shows them one by one.',
  'Nessun collegamento clinico ancora: servono i flussi (NetFlow/IPFIX) o l’agente CENSO sui sistemi clinici.':
    'No clinical link yet: it needs the flows (NetFlow/IPFIX) or the CENSO agent on the clinical systems.',
  'Nessun elettromedicale gli parla nei flussi degli ultimi 7 giorni né nelle conversazioni dell’agente CENSO: il traffico non passa da un router che esporta i flussi, e sul sistema non c’è l’agente.':
    'No medical device talks to it in the flows of the last 7 days nor in the CENSO agent’s conversations: the traffic does not cross a router that exports flows, and the system has no agent.',
  'flussi':
    'flows',
  'visto solo dall’agente CENSO':
    'seen only by the CENSO agent',
  '{n} letture':
    '{n} readings',
  'In HL7 si legano anche i sistemi che usano lo stesso nome di applicazione o di struttura (per esempio il server «ALINITY» e gli analizzatori «ALINITY1», «ALINITY2»): linea a puntini, un’ipotesi dai nomi delle intestazioni, mai dal contenuto dei messaggi.':
    'In HL7 the systems that use the same application or facility name are linked too (for example the server “ALINITY” and the analysers “ALINITY1”, “ALINITY2”): dotted line, a hypothesis from the names in the headers, never from the content of the messages.',
  'Tutti i protocolli':
    'All protocols',
  'stesso nome HL7':
    'same HL7 name',
  'stesso nome HL7 (ipotesi)':
    'same HL7 name (hypothesis)',
  '{a} e {b} usano lo stesso nome HL7 «{nome}»: un’ipotesi dai nomi, non traffico osservato.':
    '{a} and {b} use the same HL7 name “{nome}”: a hypothesis from the names, not observed traffic.',
  'Sopra la mappa si sceglie il protocollo: la mappa DICOM mostra le modalità e i PACS, la mappa HL7 gli analizzatori, i middleware e il LIS, e lega anche i sistemi che usano lo stesso nome di applicazione o di struttura nelle intestazioni HL7 (il server «ALINITY» e gli analizzatori «ALINITY1», «ALINITY2»), a puntini perché è un’ipotesi dai nomi.':
    'Above the map you choose the protocol: the DICOM map shows the modalities and the PACS, the HL7 map the analysers, the middleware and the LIS, and it also links the systems that use the same application or facility name in the HL7 headers (the server “ALINITY” and the analysers “ALINITY1”, “ALINITY2”), dotted because it is a hypothesis from the names.',
  'Acceso, non collegato alla centrale':
    'On, not connected to a central station',
  'Dispositivo medico collegato':
    'Medical device connected',
  'Dove si trova adesso':
    'Where it is now',
  'I dettagli degli elettromedicali sono riservati all’Ingegneria clinica e agli amministratori (CENSO MED).':
    'The details of medical devices are reserved to clinical engineering and the administrators (CENSO MED).',
  'Nessun segnale':
    'No signal',
  'Non visto da più di un giorno':
    'Not seen for more than a day',
  'Non visto da qualche ora':
    'Not seen for a few hours',
  'Per tipo di dispositivo':
    'By device type',
  'Posizione dichiarata dal dispositivo':
    'Position declared by the device',
  'Quali elettromedicali sono in uso o liberi adesso, e dove si trovano: per trovare un monitor o un ecografo libero, o raggiungere un dispositivo richiamato.':
    'Which medical devices are in use or free now, and where they are: to find a free monitor or ultrasound, or reach a recalled device.',
  'Reparto registrato':
    'Registered ward',
  'Segnali di rete, non certezze cliniche: «in uso» = negli ultimi {m} minuti collegato a un sistema clinico (agente CENSO) o nelle ultime {h} ore traffico clinico nei flussi; «acceso, non collegato alla centrale» = un monitor Philips IntelliVue che si annuncia (lo fa solo senza centrale); «libero» = in rete ma senza traffico clinico da almeno {l} ore. La posizione «dichiarata» è quella impostata sul dispositivo dal personale, con l’ora in cui il dispositivo l’ha detta.':
    'Network signals, not clinical certainties: “in use” = in the last {m} minutes connected to a clinical system (CENSO agent) or clinical traffic in the flows in the last {h} hours; “on, not connected to a central station” = a Philips IntelliVue monitor announcing itself (it does so only without a central); “free” = on the network but without clinical traffic for at least {l} hours. The “declared” position is the one the staff set on the device, with the time the device said it.',
  'Ultima attività clinica':
    'Last clinical activity',
  'Ultima volta in rete':
    'Last seen on the network',
  'Usato di recente':
    'Used recently',
  'letto {b}':
    'bed {b}',
  'Disponibilità: quali elettromedicali sono in uso o liberi adesso, e dove si trovano, per trovare un monitor o un ecografo libero o raggiungere un dispositivo richiamato. «In uso» se negli ultimi minuti è collegato a un sistema clinico (agente CENSO) o nelle ultime ore ha traffico clinico nei flussi; «acceso, non collegato alla centrale» se è un monitor Philips IntelliVue che si annuncia (lo fa solo senza centrale); «libero» se è in rete senza traffico clinico da qualche ora. Sono segnali di rete, non certezze cliniche. La «posizione dichiarata dal dispositivo» è quella che il personale imposta sul monitor (letto, reparto, stanza), con l’ora in cui il dispositivo l’ha detta: sta accanto alla posizione registrata dall’Ingegneria clinica senza sostituirla, nella scheda, in «Dove sono» e, per ogni richiamo, nella colonna «Dove si trova adesso».':
    'Availability: which medical devices are in use or free now, and where they are, to find a free monitor or ultrasound or reach a recalled device. “In use” if in the last minutes it is connected to a clinical system (CENSO agent) or in the last hours it has clinical traffic in the flows; “on, not connected to a central station” if it is a Philips IntelliVue monitor announcing itself (it does so only without a central); “free” if it is on the network without clinical traffic for a few hours. These are network signals, not clinical certainties. The “position declared by the device” is the one the staff set on the monitor (bed, ward, room), with the time the device said it: it sits next to the position registered by clinical engineering without replacing it, in the record, in “Where they are” and, for each recall, in the “Where it is now” column.',
  'Chi vede CENSO MED: solo gli amministratori del tenant e il ruolo «Ingegneria clinica» (o un ruolo personalizzato che lo concede). Gli altri ruoli e gli utenti operatore o di sola lettura non vedono l’area; nella finestra della porta di uno switch vedono soltanto che c’è un dispositivo medico collegato, con MAC e VLAN, senza modello, nome, indirizzo né collegamenti.':
    'Who sees CENSO MED: only the tenant’s administrators and the “Clinical engineering” role (or a custom role that grants it). The other roles and the operator or read-only users do not see the area; in the window of a switch port they only see that a medical device is connected, with MAC and VLAN, without model, name, address or links.',
  'Elettromedicale: scheda, cespite, contratti e verifiche li gestisce l’Ingegneria clinica in CENSO MED.':
    'Medical device: record, asset, contracts and checks are managed by clinical engineering in CENSO MED.',
  'Nel CMDB l’elettromedicale resta un CI per tutti (classe, indirizzi, porta), ma senza le informazioni dell’Ingegneria clinica: chi non ha CENSO MED non vede il cespite né i contratti di manutenzione legati al dispositivo e non può registrarlo come bene; al loro posto una riga dice che li gestisce l’Ingegneria clinica.':
    'In the CMDB the medical device stays a CI for everyone (class, addresses, port), but without clinical engineering’s information: who does not have CENSO MED sees neither the asset nor the maintenance contracts linked to the device and cannot register it as an asset; in their place a line says clinical engineering manages them.',
  'Cattura degli annunci (90s)':
    'Announcements sample (90s)',
  'Solo gli annunci degli apparati':
    'Only the devices’ announcements',
  'Tiene solo ciò che gli apparati mandano a tutti (broadcast e multicast) per presentarsi in rete: nessuna conversazione fra due macchine, quindi nessun traffico clinico, e nessuna delle porte GE che portano il nome del paziente. Serve a scrivere il riconoscimento di un nuovo costruttore dai suoi annunci. Meglio farla con apparati non associati a un paziente (in laboratorio o in attesa), perché un costruttore che non conosciamo potrebbe mettere qualcosa di troppo anche negli annunci.':
    'It keeps only what the devices send to everyone (broadcast and multicast) to introduce themselves on the network: no conversation between two machines, so no clinical traffic, and none of the GE ports that carry the patient’s name. It is used to write the recognition of a new manufacturer from its announcements. Better done with devices not associated with a patient (in the lab or on standby), because a manufacturer we do not know might put something too many in its announcements as well.',
  'La cattura di prova ha l’opzione «Solo gli annunci degli apparati»: per 90 secondi tiene solo ciò che gli apparati mandano a tutti per presentarsi in rete, senza conversazioni fra macchine (quindi senza traffico clinico) e senza le porte GE con il nome del paziente. È il file da cui si scrive il riconoscimento di un nuovo costruttore; meglio farla con apparati non associati a un paziente.':
    'The test capture has the option “Only the devices’ announcements”: for 90 seconds it keeps only what the devices send to everyone to introduce themselves on the network, without conversations between machines (so without clinical traffic) and without the GE ports with the patient’s name. It is the file from which the recognition of a new manufacturer is written; better done with devices not associated with a patient.',
  'Identità dal filo':
    'Identity from the wire',
  'Apparecchi riconosciuti dal filo':
    'Devices recognised from the wire',
  'Indirizzi visti':
    'Addresses seen',
  'Stazione':
    'Station',
  'Ultima calibrazione':
    'Last calibration',
  'abbinato':
    'matched',
  'apparecchi':
    'devices',
  'Leggi l’identità degli apparecchi da DICOM e HL7 sul filo':
    'Read the devices’ identity from DICOM and HL7 on the wire',
  'Lettura spenta. Attivala qui sopra per riconoscere gli apparecchi dai loro messaggi.':
    'Reading off. Turn it on above to recognise the devices from their messages.',
  'Ancora nessuna identità letta: serve che la sonda veda il traffico DICOM/HL7 degli apparecchi.':
    'No identity read yet: the probe needs to see the devices’ DICOM/HL7 traffic.',
  'Da attivare dopo averlo concordato e documentato con l’ospedale (DPIA). Richiede la licenza CENSO MED e che la sonda veda il traffico (porta mirror). Serve l’aggiornamento delle sonde.':
    'To be turned on after it is agreed and documented with the hospital (DPIA). It requires the CENSO MED licence and that the probe sees the traffic (mirror port). The probes must be updated.',
  'Gli apparecchi riconosciuti dai messaggi DICOM e HL7 che la sonda vede sul filo: solo l’identità dell’apparato (costruttore, modello, numero di serie, UDI, software), mai dati del paziente. Da attivare per ospedale.':
    'The devices recognised from the DICOM and HL7 messages the probe sees on the wire: only the device identity (manufacturer, model, serial number, UDI, software), never patient data. Turned on per hospital.',
  'Identità dal filo: dove gli apparecchi non hanno API — tanta roba vecchia e in chiaro — CENSO legge dai messaggi DICOM e HL7 che la sonda vede la sola identità dell’apparato (costruttore, modello, numero di serie, Device UID/UDI, software, modalità, stazione, struttura, reparto, date). Non legge mai dati del paziente: in DICOM salta il gruppo del paziente e si ferma prima dei pixel, in HL7 legge solo i segmenti dell’apparecchiatura e mai le misure cliniche. Gli apparecchi sono distinti per numero di serie o UDI, mai per solo indirizzo. La funzione c’è solo con licenza CENSO MED e si attiva per ospedale, da concordare e documentare (DPIA); serve che la sonda veda il traffico (porta mirror).':
    'Identity from the wire: where the devices have no API — much old, cleartext equipment — CENSO reads from the DICOM and HL7 messages the probe sees only the device identity (manufacturer, model, serial number, Device UID/UDI, software, modality, station, institution, department, dates). It never reads patient data: in DICOM it skips the patient group and stops before the pixels, in HL7 it reads only the equipment segments and never the clinical measurements. Devices are told apart by serial number or UDI, never by address alone. The feature exists only with the CENSO MED licence and is turned on per hospital, to be agreed and documented (DPIA); the probe must see the traffic (mirror port).',
  'Ultima acquisizione':
    'Last acquisition',
  'abbinato (CI)':
    'matched (CI)',
  'non ancora':
    'not yet',
  'dichiarato dal dispositivo sul filo (DICOM/HL7)':
    'declared by the device on the wire (DICOM/HL7)',
  'Attività dal filo (DICOM/HL7)':
    'Activity from the wire (DICOM/HL7)',
  'Fabbricazione':
    'Manufacture',
  'Nessuna lettura ancora.':
    'No read yet.',
  'Quante volte al giorno l’apparecchio si è fatto riconoscere sul filo coi suoi messaggi d’identità: una traccia d’uso senza leggere nulla dei pazienti. In tutto {n} letture.':
    'How many times a day the device made itself recognised on the wire with its identity messages: a usage trace without reading anything of the patients. {n} reads in all.',
  'Ultima acquisizione: {q}.':
    'Last acquisition: {q}.',
  'CENSO legge dai messaggi DICOM e HL7 che vede sul filo la sola identità degli apparecchi — costruttore, modello, numero di serie, Device UID/UDI, versione software, modalità, stazione, struttura e reparto, date di fabbricazione, installazione e calibrazione. Non legge mai dati del paziente: in DICOM salta il gruppo del paziente e si ferma prima dei pixel; in HL7 legge solo i segmenti dell’apparecchiatura, mai le misure cliniche. I dispositivi sono distinti per numero di serie o UDI, mai per solo indirizzo. «Letture» conta quante volte l’apparecchio si è fatto riconoscere sul filo, per una statistica d’uso; quando il numero di serie o l’UDI combacia con il suo indirizzo, l’identità arricchisce il dispositivo nel CMDB (modello e seriale esatti). Gli apparecchi visti solo attraverso un PACS/archivio (un unico indirizzo che riceve immagini da molte modalità, anche di altre strutture) sono segnati «solo in archivio» e non diventano dispositivi: sono studi conservati, non apparecchi locali.':
    "CENSO reads from the DICOM and HL7 messages it sees on the wire only the equipment's identity — manufacturer, model, serial number, Device UID/UDI, software version, modality, station, institution and department, dates of manufacture, installation and calibration. It never reads patient data: in DICOM it skips the patient group and stops before the pixels; in HL7 it reads only the equipment segments, never the clinical measurements. Devices are told apart by serial number or UDI, never by address alone. “Reads” counts how many times the device identified itself on the wire, for a usage statistic; when the serial number or UDI matches its address, the identity enriches the device in the CMDB (exact model and serial). Equipment seen only through a PACS/archive (a single address that receives images from many modalities, including those of other sites) is marked “archive only” and does not become a device: these are stored studies, not local equipment.",
  'Dispositivi esterni':
    'External devices',
  'Dispositivi identificati solo attraverso un PACS/archivio (un indirizzo che riceve immagini da molte modalità, spesso di altre strutture): sono studi conservati o referti arrivati da fuori, catalogati a parte e non contati fra gli elettromedicali dell’ospedale.':
    "Devices identified only through a PACS/archive (an address that receives images from many modalities, often from other sites): these are stored studies or reports arrived from outside, catalogued separately and not counted among the hospital's medical devices.",
  'Dispositivo esterno: identificato solo attraverso un PACS/archivio.':
    'External device: identified only through a PACS/archive.',
  'Identificato solo attraverso un PACS/archivio, spesso di un’altra struttura: non è un apparecchio dell’ospedale.':
    'Identified only through a PACS/archive, often from another site: it is not a device of the hospital.',
  'Visto solo attraverso un PACS/archivio: è uno studio archiviato o inoltrato, spesso di un’altra struttura, non un apparecchio locale.':
    'Seen only through a PACS/archive: it is an archived or forwarded study, often from another site, not a local device.',
  'solo in archivio':
    'archive only',
  'visti solo via PACS/archivio, spesso di altre strutture':
    'seen only via PACS/archive, often from other sites',
  'Un apparecchio diventa un dispositivo dell’ospedale solo quando lo vediamo a un indirizzo suo; quelli visti solo attraverso un PACS/archivio (un indirizzo che riceve immagini da molte modalità, spesso di altre strutture) sono «Dispositivi esterni» — studi conservati o referti arrivati da fuori — catalogati a parte e fuori dai conteggi dell’area.':
    "A piece of equipment becomes a device of the hospital only when we see it at an address of its own; those seen only through a PACS/archive (an address that receives images from many modalities, often from other sites) are “External devices” — stored studies or reports arrived from outside — catalogued separately and kept out of the area's counts.",
  'CI principali':
    'primary CIs',
  'CI secondari':
    'secondary CIs',
  'I CI meno disponibili nel periodo, con il traguardo dello SLA.':
    'The least available CIs in the period, with the SLA target.',
  'I numeri che contano, l’andamento degli allarmi e i parametri per stato, in una riga.':
    'The numbers that matter, the trend of the alarms and the parameters by state, in one row.',
  'I tratti in cui un CI non rispondeva, sul filo del tempo.':
    'The stretches in which a CI did not respond, on the time line.',
  'La classifica dei valori attuali con le soglie e l’andamento: CPU, memoria, volumi, interfacce…':
    'The ranking of the current values with the thresholds and the trend: CPU, memory, volumes, interfaces…',
  'Una tessera per CI, colorata dal suo stato peggiore, con i parametri per stato.':
    'One tile per CI, coloured by its worst state, with the parameters by state.',
  'aggiornato alle {ora}':
    'updated at {ora}',
  'andamento':
    'trend',
  'attenzione da {v}':
    'warning from {v}',
  'critico da {v}':
    'critical from {v}',
  'nessun allarme nel periodo':
    'no alarms in the period',
  'non rispettano':
    'do not meet it',
  'parametri in allarme':
    'parameters in alarm',
  'poche misure':
    'few readings',
  'rispettano':
    'meet it',
  'scala da {base} a 100 %; la tacca è il traguardo dello SLA':
    'scale from {base} to 100 %; the tick is the SLA target',
  'senza dati':
    'no data',
  'su {max}':
    'of {max}',
  'traguardo {v}':
    'target {v}',
  'traguardo {v} %':
    'target {v} %',
  'una dashboard a widget, ognuno con il suo grafico. In alto «Come stanno le cose»: i numeri che contano (gravi, attenzione, ok, in attesa, CI, sonde), l’andamento degli allarmi nella finestra e i parametri per stato; poi la parete dei CI — una tessera per CI, colorata dal suo stato peggiore, con i parametri per stato — i problemi aperti e l’andamento degli allarmi nel tempo. Nelle altre sezioni gli incidenti sul filo del tempo (un rigo per CI), la disponibilità più bassa su una scala da 90 a 100 % con la tacca del traguardo SLA, i parametri per stato a ruota, il rispetto degli SLA, le classifiche dei parametri più alti — per ogni riga il valore, la barra con le tacche delle soglie e la sparkline delle ultime ore — e le sonde con il loro carico.':
    'a dashboard made of widgets, each with its own chart. At the top, “How things are”: the numbers that matter (severe, warning, ok, waiting, CIs, probes), the trend of the alarms in the window and the parameters by state; then the wall of CIs — one tile per CI, coloured by its worst state, with the parameters by state — the open problems and the trend of the alarms over time. In the other sections the incidents on the time line (one row per CI), the lowest availability on a scale from 90 to 100 % with the tick of the SLA target, the parameters by state as a ring, SLA compliance, the rankings of the highest parameters — for each row the value, the bar with the threshold ticks and the sparkline of the last hours — and the probes with their load.',
  'valore attuale e ultime {ore} ore':
    'current value and last {ore} hours',
  '{nome}: ultime {ore} ore, minimo {min}, massimo {max}, ultimo {ultimo}':
    '{nome}: last {ore} hours, minimum {min}, maximum {max}, last {ultimo}',
  '{nome}: {n} incidenti':
    '{nome}: {n} incidents',
  '{n} incidenti · {d} in totale':
    '{n} incidents · {d} in total',
  '{n} parametri per stato':
    '{n} parameters by state',
  '{n} parametri su {m} CI':
    '{n} parameters on {m} CIs',
  '{n}% di letture in ritardo':
    '{n}% of readings late',
  '{v} di disponibilità':
    '{v} availability',
  '{v} di disponibilità, traguardo {s} %':
    '{v} availability, target {s} %',
  '«Personalizza» cambia sezioni, widget, misure e finestre; la sezione aperta sta nell’indirizzo, così un collegamento porta a quella giusta. Si aggiorna da solo ogni minuto. I parametri in allarme compaiono anche nella campanella in alto, con una barra verde, gialla o rossa a seconda della gravità.':
    '“Customise” changes sections, widgets, sizes and windows; the open section is in the address, so a link leads to the right one. It refreshes by itself every minute. Parameters in alarm also appear in the bell at the top, with a green, yellow or red bar depending on severity.',
  'Aggiungi serie':
    'Add a series',
  'Al massimo {n} serie qui: per averne altre aggiungi un secondo widget.':
    'At most {n} series here: for more, add a second widget.',
  'Etichetta nella legenda':
    'Label in the legend',
  'Fascia minimo–massimo':
    'Minimum–maximum band',
  'Grafico personalizzato':
    'Custom chart',
  'Il cruscotto si compone: ogni widget ha un titolo tuo («Titolo e opzioni…» in modifica; quello del catalogo resta come sottotitolo). «Grafico personalizzato» mette nello stesso grafico le serie che scegli, anche di CI diversi — linee, aree, barre o impilate (la somma) — con un pannello per ogni unità di misura; «Parete dei link» è una scheda per interfaccia, fino a sedici per widget (le WAN in uno, le porte fra i data center in un altro), con la descrizione della porta come titolo, lo stato, il traffico in e out, l’utilizzo con le soglie e il mini-grafico. Nella scelta di un’interfaccia ogni porta porta la sua descrizione, l’indirizzo e lo stato: non serve tenere aperta la scheda dello switch.':
    "The dashboard is composed: every widget has a title of yours (“Title and options…” while editing; the catalogue's stays as subtitle). “Custom chart” puts the series you choose in one chart, even from different CIs — lines, areas, bars or stacked (the sum) — with one panel per unit of measure; “Link wall” is one card per interface, up to sixteen per widget (the WANs in one, the ports between data centres in another), with the port description as title, the state, traffic in and out, utilisation with the thresholds and the mini chart. When choosing an interface every port carries its description, address and state: no need to keep the switch page open.",
  'Lascia vuoto per il titolo del catalogo; quello del catalogo resta come sottotitolo.':
    "Leave empty for the catalogue title; the catalogue's stays as subtitle.",
  'Le serie che scegli — anche di CI diversi — a linee, aree, barre o impilate.':
    'The series you choose — even from different CIs — as lines, areas, bars or stacked.',
  'Le serie con unità diverse finiscono in pannelli separati, uno per unità: un asse solo per grafico.':
    'Series with different units go to separate panels, one per unit: one axis per chart.',
  'Mini-grafico':
    'Mini chart',
  'Modifica il widget «{w}»':
    'Edit the widget “{w}”',
  'Nell’elenco ogni porta porta la sua descrizione: è quella che diventa il titolo della scheda (si può cambiare).':
    "In the list every port carries its description: it becomes the card's title (it can be changed).",
  'Nessun parametro acceso su questo CI.':
    'No parameter is on for this CI.',
  'Opzioni':
    'Options',
  'Parete dei link':
    'Link wall',
  'Scegli le interfacce in «Titolo e opzioni» (in modifica): ogni porta mostra la sua descrizione.':
    'Choose the interfaces in “Title and options” (while editing): every port shows its description.',
  'Scegli le serie in «Titolo e opzioni» (in modifica).':
    'Choose the series in “Title and options” (while editing).',
  'Serie {n}':
    'Series {n}',
  'Soglie di attenzione e critica':
    'Warning and critical thresholds',
  'Tipo di grafico':
    'Chart type',
  'Titolo del widget':
    'Widget title',
  'Titolo e opzioni…':
    'Title and options…',
  'Una scheda per interfaccia: descrizione della porta, stato, traffico in e out, utilizzo con le soglie e il mini-grafico.':
    'One card per interface: port description, state, traffic in and out, utilisation with thresholds and the mini chart.',
  'aree':
    'areas',
  'barre':
    'bars',
  'cerca per nome, indirizzo o classe…':
    'search by name, address or class…',
  'impilato (la somma)':
    'stacked (the sum)',
  'linee':
    'lines',
  'togli la serie':
    'remove the series',
  'totale':
    'total',
  'traffico in e out':
    'traffic in and out',
  'utilizzo con le soglie':
    'utilisation with thresholds',
  '{e} errori · {s} scarti nella finestra':
    '{e} errors · {s} discards in the window',
  '{n} CI su {m}':
    '{n} CIs of {m}',
  '{n} interfacce non sono più monitorate: {elenco}':
    '{n} interfaces are no longer monitored: {elenco}',
  '{n} interfacce · {f}':
    '{n} interfaces · {f}',
  '{n} serie non sono più monitorate: {elenco}':
    '{n} series are no longer monitored: {elenco}',
  'A → B':
    'A → B',
  'Adiacenza trovata: {ci} porta {porta}, ma quella porta non è monitorata da Eagle. Attivala sul CI o scegli un’altra interfaccia.':
    'Adjacency found: {ci} port {porta}, but that port is not monitored by Eagle. Turn it on in the CI or choose another interface.',
  'Adiacenze non leggibili al momento.':
    'Adjacencies cannot be read right now.',
  'B → A':
    'B → A',
  'Capo A (il più vicino)':
    'End A (the near one)',
  'Capo B (l’altro capo)':
    'End B (the far one)',
  'Dall’adiacenza {via}: {ci} · {porta}{stato}':
    'From the {via} adjacency: {ci} · {porta}{stato}',
  'I due capi':
    'The two ends',
  'Il capo B si propone dall’adiacenza LLDP/CDP della porta A, ma resta tuo: la descrizione della porta è quella che conta. Senza capo B il link è a un capo solo (una WAN verso il provider).':
    'End B is proposed from the LLDP/CDP adjacency of port A, but it stays yours: the port description is what counts. Without end B the link is one-sided (a WAN towards the provider).',
  'Link':
    'Link',
  'Nessuna adiacenza LLDP/CDP su questa porta: scegli l’altro capo a mano.':
    'No LLDP/CDP adjacency on this port: choose the far end by hand.',
  'Nome del capo':
    'Name of the end',
  'Proponi l’altro capo dall’adiacenza':
    'Propose the far end from the adjacency',
  'Scegli i due capi in «Titolo e opzioni» (in modifica).':
    'Choose the two ends in “Title and options” (while editing).',
  'Un capo non è più monitorato: controlla le interfacce in «Titolo e opzioni».':
    'One end is no longer monitored: check the interfaces in “Title and options”.',
  'Un collegamento fra due interfacce: i due versi in Mbit/s, l’utilizzo con le soglie a ogni capo, lo stato, il mini-grafico. Il capo B si propone dall’adiacenza.':
    'A link between two interfaces: both directions in Mbit/s, utilisation with thresholds at each end, the state, the mini chart. End B is proposed from the adjacency.',
  'altro capo':
    'far end',
  'di utilizzo':
    'utilisation',
  'es. DC Roma':
    'e.g. DC Rome',
  'es. Sede Milano':
    'e.g. Milan site',
  'nessun altro capo (link a un solo capo)':
    'no far end (one-sided link)',
  'non monitorato da Eagle':
    'not monitored by Eagle',
  'parametro spento':
    'parameter off',
  '{a} verso {b}: {ab}; {b} verso {a}: {ba}':
    '{a} towards {b}: {ab}; {b} towards {a}: {ba}',
  '«Link» disegna un collegamento fra due interfacce: i due versi in Mbit/s (A→B è l’uscita di A, B→A la sua entrata), l’utilizzo con le soglie a ogni capo, lo stato peggiore sul tubo, il mini-grafico. Il capo B si propone dall’adiacenza LLDP/CDP della porta A — e resta modificabile; senza capo B il link è a un capo solo, come una WAN verso il provider. Ai due capi puoi dare un nome («Sede Milano», «DC Roma»).':
    "“Link” draws a connection between two interfaces: both directions in Mbit/s (A→B is A's output, B→A its input), utilisation with thresholds at each end, the worst state on the pipe, the mini chart. End B is proposed from the LLDP/CDP adjacency of port A — and stays editable; without end B the link is one-sided, like a WAN towards the provider. You can name the two ends (“Milan site”, “DC Rome”).",
  'Altezza della tela':
    'Canvas height',
  'Colora gli archi per utilizzo':
    'Colour the edges by utilisation',
  'La vista non ha nodi con questi filtri.':
    'The view has no nodes with these filters.',
  'La vista salvata non esiste più: scegline un’altra in «Titolo e opzioni».':
    'The saved view no longer exists: choose another in “Title and options”.',
  'Le viste si creano e si modificano in Rete → Mappa; qui si scelgono.':
    'Views are created and edited in Network → Map; here you choose one.',
  'Mappa con lo stato':
    'Map with state',
  'Mappa parziale: {n} collegamenti in tutto, qui i primi.':
    'Partial map: {n} links in all, the first ones here.',
  'Mappa «{v}» con lo stato di Eagle':
    'Map “{v}” with Eagle state',
  'Nessuna vista salvata: aprila in Rete → Mappa, disponi i nodi e salva la vista.':
    'No saved view: open Network → Map, arrange the nodes and save the view.',
  'Ogni arco LLDP/CDP prende lo stato peggiore delle sue due interfacce e uno spessore in proporzione all’utilizzo; le porte si riconoscono dal nome (local_port, remote_port).':
    'Every LLDP/CDP edge takes the worst state of its two interfaces and a thickness proportional to utilisation; ports are matched by name (local_port, remote_port).',
  'Scegli una vista di topologia salvata in «Titolo e opzioni» (in modifica). Le viste si creano in Rete → Mappa.':
    'Choose a saved topology view in “Title and options” (while editing). Views are created in Network → Map.',
  'Una vista di topologia salvata, colorata da Eagle: i nodi dallo stato, gli archi dall’utilizzo delle interfacce.':
    'A saved topology view, coloured by Eagle: nodes by state, edges by interface utilisation.',
  'Vista di topologia salvata':
    'Saved topology view',
  'alta (scorre nel riquadro)':
    'tall (scrolls in the box)',
  'apri la mappa':
    'open the map',
  'arco: stato peggiore dei due capi, spessore = utilizzo':
    'edge: worst state of the two ends, thickness = utilisation',
  'critico o non risponde':
    'critical or not responding',
  'interfacce non monitorate':
    'interfaces not monitored',
  '{k} non monitorati':
    '{k} not monitored',
  '{n} nodi fissati':
    '{n} pinned nodes',
  '{v} · {n} nodi · {m} collegamenti{extra}':
    '{v} · {n} nodes · {m} links{extra}',
  '«Mappa con lo stato» prende una vista di topologia salvata in Rete → Mappa (filtri, fuoco e nodi fissati a mano) e la colora con Eagle: ogni nodo dallo stato peggiore dei suoi parametri (grigio se non è monitorato), ogni arco LLDP/CDP dallo stato peggiore delle sue due interfacce, con uno spessore in proporzione all’utilizzo; clic su un nodo apre la sua sezione Eagle, «schermo intero» la porta a parete. Le viste si creano e si sistemano in Rete → Mappa, qui si scelgono.':
    '“Map with state” takes a topology view saved in Network → Map (filters, focus and nodes pinned by hand) and colours it with Eagle: every node by the worst state of its parameters (grey if not monitored), every LLDP/CDP edge by the worst state of its two interfaces, with a thickness proportional to utilisation; clicking a node opens its Eagle section, “full screen” puts it on the wall. Views are created and arranged in Network → Map; here you choose one.',
  '15 minuti':
    '15 minutes',
  '3 ore':
    '3 hours',
  '30 minuti':
    '30 minutes',
  'Dashboard importata: salvala come tua o per il tenant.':
    'Dashboard imported: save it as yours or for the tenant.',
  'Dashboard importata: {n} riferimenti (CI o viste) non esistono in questo tenant — sistemali in «Titolo e opzioni», poi salva.':
    'Dashboard imported: {n} references (CIs or views) do not exist in this tenant — fix them in “Title and options”, then save.',
  'File della dashboard':
    'Dashboard file',
  'Il file non è una dashboard esportata da CENSO.':
    'The file is not a dashboard exported by CENSO.',
  'Importa…':
    'Import…',
  'La dashboard importata non ha sezioni.':
    'The imported dashboard has no sections.',
  'Le finestre dei grafici vanno da 15 minuti a 7 giorni (le sonde aggregano a 5 minuti). In modifica, «Esporta» scarica la dashboard in un file e «Importa…» la ricostruisce, anche in un altro tenant: CI e viste di topologia si ritrovano per nome, e quelli che lì non esistono vengono contati perché tu li sistemi in «Titolo e opzioni» prima di salvare.':
    'Chart windows go from 15 minutes to 7 days (probes aggregate at 5 minutes). While editing, “Export” downloads the dashboard to a file and “Import…” rebuilds it, even in another tenant: CIs and topology views are matched by name, and those that do not exist there are counted so you can fix them in “Title and options” before saving.',
  'Questa è una dashboard «{a}»: importala nella sua pagina.':
    'This is a “{a}” dashboard: import it in its own page.',
  'Scarica la dashboard in un file, da importare qui o in un altro tenant':
    'Download the dashboard to a file, to import here or in another tenant',
  'dietro {root}':
    'behind {root}',
  'colpa della sonda {probe}':
    'probe {probe} is to blame',
  'non raggiunto da {probes}, {n} dietro':
    'unreachable from {probes}, {n} behind it',
  'non raggiunto da {probes}':
    'unreachable from {probes}',
  'trascina {n} oggetti':
    'drags {n} objects down',
  'cieca: {down} su {total} non raggiunti':
    'blind: {down} of {total} unreachable',
  'ferma: {n} oggetti senza letture':
    'silent: {n} objects without readings',
  'raggiungibile da {probes}':
    'reachable from {probes}',
  'La sonda non raggiunge più quasi nulla di ciò che legge: la causa è lei, non l’oggetto.':
    'The probe can no longer reach almost anything it reads: the probe is the cause, not the object.',
  'sonda {name}':
    'probe {name}',
  'cause':
    'causes',
  'Con più sonde la causa si cerca anche «da dove»: se un oggetto è giù da una sonda e su da un’altra è un percorso (la WAN, il firewall, la rotta), e la mail dice da quale sonda non si raggiunge, da quale sì e quanti oggetti stanno dietro di lui visti da quella stessa sonda — quelli si tacciono. Una sonda viva che non raggiunge più quasi nulla di ciò che legge, senza una dipendenza che lo spieghi, è «cieca»: la causa è lei, una sola mail sulla sonda e gli oggetti taciuti. Una sonda che non manda più il battito è «ferma»: gli oggetti che leggeva solo lei passano a «nessun dato» invece di restare fermi all’ultimo valore, una mail sulla sonda, e un «rientrata» quando torna. Nel cruscotto ogni problema porta il suo verdetto (dietro a chi, quanti trascina, da quale sonda non si raggiunge) e «Come stanno le cose» conta le cause, non gli oggetti.':
    'With several probes the cause is also sought “from where”: if an object is down from one probe and up from another it is a path (the WAN, the firewall, the route), and the email says from which probe it cannot be reached, from which it can, and how many objects sit behind it as seen from that same probe — those are silenced. A live probe that can no longer reach almost anything it reads, with no dependency explaining it, is “blind”: the probe is the cause, one email about the probe and the objects silenced. A probe that stops sending its heartbeat is “silent”: the objects only it was reading switch to “no data” instead of staying frozen at the last value, one email about the probe, and a “recovered” when it returns. On the dashboard every problem carries its verdict (behind whom, how many it drags down, from which probe it cannot be reached) and “How things stand” counts causes, not objects.',
  'conteggi':
    'counts',
  'Nei grafici ogni tacca dell’asse porta l’unità di misura («25 %», «400 Mbit/s»), con una scala sola per grafico che si adatta ai valori: oltre i 1000 Mbit/s si legge in Gbit/s, i millisecondi oltre il secondo in secondi; pillole, soglie e riquadro al passaggio del mouse usano la stessa scala.':
    'In the charts every axis tick carries the unit of measure (“25 %”, “400 Mbit/s”), with one scale per chart that adapts to the values: above 1000 Mbit/s it reads in Gbit/s, milliseconds beyond one second in seconds; pills, thresholds and the hover box use the same scale.',
  'Accendi o spegni':
    'Switch on or off',
  'Applica a {n} CI':
    'Apply to {n} CIs',
  'Applica adesso':
    'Apply now',
  'Applica un profilo…':
    'Apply a profile…',
  'Gruppi di servizi':
    'Service groups',
  'Le regole valgono per tipo di parametro: ogni CI prende quelli che ha e sa leggere.':
    'The rules work by parameter kind: each CI takes the ones it has and can read.',
  'Nessun profilo (Eagle → Profili)':
    'No profile (Eagle → Profiles)',
  'Nessun profilo ancora: creane uno, poi applicalo dall’elenco dei CI o legalo a un tag.':
    'No profile yet: create one, then apply it from the CI list or bind it to a tag.',
  'Nessuna regola: aggiungine una.':
    'No rule: add one.',
  'Nomi':
    'Names',
  'Notifica · SLA':
    'Notification · SLA',
  'Nuovo profilo':
    'New profile',
  'Parametri di monitoraggio per {n} CI':
    'Monitoring parameters for {n} CIs',
  'Parametri…':
    'Parameters…',
  'Profili':
    'Profiles',
  'Profili di monitoraggio ({n})':
    'Monitoring profiles ({n})',
  'Profili di monitoraggio: regole per tipo di parametro da applicare a più CI insieme, o da soli ai CI con un tag.':
    'Monitoring profiles: rules by parameter kind to apply to many CIs at once, or by themselves to the CIs carrying a tag.',
  'Profilo di monitoraggio':
    'Monitoring profile',
  'Regole al volo sui CI selezionati: accendi o spegni dei tipi di parametro, cambia soglie e frequenze.':
    'Ad-hoc rules on the selected CIs: switch parameter kinds on or off, change thresholds and intervals.',
  'Salute dei dischi':
    'Disk health',
  'Salva anche come profilo (nome, facoltativo)':
    'Also save as a profile (name, optional)',
  'Servizi automatici fermi':
    'Automatic services stopped',
  'Soglia di {s}':
    '{s} threshold',
  'Spegni ciò che il profilo non nomina (altrimenti il resto resta com’è)':
    'Switch off what the profile does not name (otherwise the rest stays as it is)',
  'Spegni ciò che le regole non nominano':
    'Switch off what the rules do not name',
  'Tag che lo applica da solo (chiave:valore, o solo chiave)':
    'Tag that applies it by itself (key:value, or key alone)',
  'UPS: autonomia':
    'UPS: runtime',
  'UPS: batteria':
    'UPS: battery',
  'UPS: carico':
    'UPS: load',
  'Ultima applicazione':
    'Last applied',
  'Un profilo è un elenco di regole per tipo di parametro — ping, CPU, interfacce, servizi per nome… — con soglie e frequenza. Si applica a più CI insieme dall’elenco dei Configuration Item («Applica profilo…») o da solo ai CI che portano un tag, ogni dieci minuti.':
    'A profile is a list of rules by parameter kind — ping, CPU, interfaces, services by name… — with thresholds and interval. It is applied to many CIs at once from the Configuration Item list (“Apply a profile…”) or by itself to the CIs carrying a tag, every ten minutes.',
  'Valore di {s}':
    '{s} value',
  'accendi':
    'switch on',
  'credenziale da provare':
    'credential to try',
  'del profilo':
    'of the class profile',
  'es. Server web':
    'e.g. Web servers',
  'es. ruolo:core-switch':
    'e.g. role:core-switch',
  'frequenza predefinita':
    'default interval',
  'i profili di monitoraggio: regole per tipo di parametro — ping, CPU, memoria, interfacce (tutte, quelle del profilo della classe o per nome), servizi e processi per nome anche con *, porte TCP — con frequenza e soglie (un numero, nessuna, o la predefinita del CI). Un profilo si applica a più CI insieme dall’elenco dei Configuration Item («Applica un profilo…», oppure «Parametri…» per regole al volo, salvabili come profilo) o da solo ai CI che portano un tag (chiave:valore o solo chiave), ogni dieci minuti e con «Applica adesso»; con più tag sullo stesso CI vince la priorità più alta. Di norma tocca solo ciò che nomina; «Spegni ciò che il profilo non nomina» sostituisce tutto. L’esito dice quanti CI e parametri ha acceso e quanti parametri non erano leggibili e perché (per esempio «senza credenziale SNMP»: è il motivo per cui un apparato di rete prende solo il ping finché non ha una credenziale). Il profilo può anche assegnare gruppo di notifica e SLA.':
    'the monitoring profiles: rules by parameter kind — ping, CPU, memory, interfaces (all, those of the class profile or by name), services and processes by name, with * too, TCP ports — with interval and thresholds (a number, none, or the CI’s default). A profile is applied to many CIs at once from the Configuration Item list (“Apply a profile…”, or “Parameters…” for ad-hoc rules, which can be saved as a profile) or by itself to the CIs carrying a tag (key:value or key alone), every ten minutes and with “Apply now”; with several tags on the same CI the highest priority wins. By default it only touches what it names; “Switch off what the profile does not name” replaces everything. The outcome says how many CIs and parameters it switched on and how many parameters could not be read and why (for instance “without SNMP credential”: that is why a network device only gets the ping until it has a credential). The profile can also assign a notification group and an SLA.',
  'metodo non ancora disponibile':
    'method not available yet',
  'nessuna fonte che lo legga':
    'no source reads it',
  'non cambiare':
    'leave unchanged',
  'non leggibili: {elenco}':
    'unreadable: {elenco}',
  'non più in inventario':
    'no longer in the inventory',
  'per nome':
    'by name',
  'predefinita':
    'default',
  'profili':
    'profiles',
  'profilo «{nome}»':
    'profile “{nome}”',
  'quelli del profilo della classe':
    'those of the class profile',
  'senza credenziale SNMP':
    'without SNMP credential',
  'spegne il resto':
    'switches the rest off',
  'spegni':
    'switch off',
  '{n} CI col tag':
    '{n} CIs with the tag',
  '{n} CI impostati, {p} parametri accesi':
    '{n} CIs set, {p} parameters switched on',
  '«Seleziona tutti i N CI del filtro» prende tutti quelli del filtro (anche un tag scelto dalla barra in alto), non solo la pagina; con Eagle attivo, «Metti in monitoraggio» accende sui selezionati il profilo della loro classe, «Applica un profilo…» uno dei profili di Eagle → Profili e «Parametri…» delle regole al volo (tipi di parametro da accendere o spegnere, soglie, frequenze).':
    '“Select all N CIs of the filter” takes all those of the filter (a tag chosen from the top bar too), not only the page; with Eagle active, “Put under monitoring” switches on the class profile on the selected ones, “Apply a profile…” one of the profiles of Eagle → Profiles and “Parameters…” ad-hoc rules (parameter kinds to switch on or off, thresholds, intervals).',
  'La prima pagina è una dashboard a sezioni — Panoramica, Traffico, Sicurezza, Protezione e capacità, Eagle — e in ogni sezione una griglia di widget presi da un catalogo: numeri, classifiche (chi fa più traffico, servizi più usati), ruote, andamenti. «Personalizza» aggiunge sezioni e widget, li sposta trascinandoli, ne cambia la misura e le regolazioni; «Salva come mia» la salva sul server, così la ritrovi da qualsiasi computer. Un amministratore la salva «per il tenant»: è quella dei monitor a parete e di chi non ne ha una propria; se ne esiste già una con quel nome la aggiorna, non ne crea un doppione. Quando ne hai più d’una (la tua e quella del tenant) in alto le scegli da un menu, e la scelta resta per questo browser. In modifica, sotto la testata, c’è chi l’ha creata e quando, chi l’ha modificata per ultimo, e — per chi l’ha creata — «Blocca le modifiche»: una dashboard del tenant bloccata la cambia o la elimina solo chi l’ha creata (o un amministratore della piattaforma); gli altri la vedono e possono salvarne una copia come propria. «Sinottico» la mostra a tutto schermo, senza menu, con la rotazione fra le sezioni. Il menu a sinistra si filtra scrivendo, e le sezioni si aprono e chiudono dal titolo.':
    'The first page is a dashboard in sections — Overview, Traffic, Security, Protection and capacity, Eagle — and in every section a grid of widgets taken from a catalogue: numbers, rankings (who moves the most traffic, the most used services), donuts, trends. “Customise” adds sections and widgets, moves them by dragging, changes their size and settings; “Save as mine” saves it on the server, so you find it from any computer. An administrator saves it “for the tenant”: it is the one of the wall monitors and of those without one of their own; if one with that name already exists it is updated, not duplicated. When you have more than one (yours and the tenant’s) you pick it from a menu at the top, and the choice stays for this browser. While editing, under the header, you read who created it and when, who changed it last, and — for its creator — “Lock changes”: a locked tenant dashboard is changed or deleted only by its creator (or a platform administrator); the others see it and can save a copy as their own. “Synoptic” shows it full screen, without menu, rotating through the sections. The left menu filters as you type, and sections open and close from their title.',
  'Blocca le modifiche: solo chi l’ha creata può modificarla (vale quando è salvata per il tenant)':
    'Lock changes: only its creator can change it (applies when it is saved for the tenant)',
  'Bloccata da {chi}: solo chi l’ha creata può modificarla. Puoi salvarla come tua.':
    'Locked by {chi}: only its creator can change it. You can save it as yours.',
  'Dashboard del tenant «{n}»':
    'Tenant dashboard “{n}”',
  'Del tenant':
    'Tenant’s',
  'Elimina dal tenant':
    'Delete from the tenant',
  'Eliminare la dashboard del tenant «{n}»? Chi la usa tornerà alla predefinita.':
    'Delete the tenant dashboard “{n}”? Whoever uses it goes back to the default one.',
  'La mia':
    'Mine',
  'La tua dashboard «{n}»':
    'Your dashboard “{n}”',
  'Non ancora salvata: «Salva come mia» la fa tua, «Salva per il tenant» la dà a tutti (solo un amministratore).':
    'Not saved yet: “Save as mine” makes it yours, “Save for the tenant” gives it to everybody (administrators only).',
  'Quale dashboard':
    'Which dashboard',
  'bloccata da {chi}: solo chi l’ha creata può modificarla':
    'locked by {chi}: only its creator can change it',
  'creata da {chi} il {quando}':
    'created by {chi} on {quando}',
  'creata il {quando}':
    'created on {quando}',
  'ultima modifica di {chi} il {quando}':
    'last changed by {chi} on {quando}',
  'ultima modifica il {quando}':
    'last changed on {quando}',
  'Aprendo un’interfaccia non c’è un numero solo: il traffico in entrata e in uscita in Mbit/s (due linee), i pacchetti al secondo nei due versi (se l’apparato espone i contatori), gli errori e gli scarti nell’intervallo, e sotto l’utilizzo con le soglie. I numeri in alto sono l’ultima lettura.':
    'Opening an interface there is not a single number: inbound and outbound traffic in Mbit/s (two lines), packets per second in both directions (when the device exposes the counters), errors and discards in the interval, and below the utilisation with its thresholds. The numbers at the top are the last reading.',
  'Pacchetti in entrata':
    'Packets in',
  'Pacchetti in uscita':
    'Packets out',
  'Pacchetti al secondo':
    'Packets per second',
  'in':
    'in',
  'out':
    'out',
  'nomi con * come jolly, separati da virgola (facoltativo)':
    'names with * as wildcard, comma-separated (optional)',
  'nome (es. Gi1/0/1)':
    'name (e.g. Gi1/0/1)',
  'come la regola':
    'as the rule',
  'Togli la voce':
    'Remove the item',
  'Prendi da un CI…':
    'Take from a CI…',
  'Nessuna voce: i nomi qui sopra (con *) valgono da soli; una voce ha le sue regolazioni.':
    'No item: the names above (with *) work on their own; an item carries its own settings.',
  'Prendi le voci da un CI':
    'Take the items from a CI',
  'Un CI monitorato e i suoi {tipo}: le voci scelte entrano nella regola con il nome (e la descrizione) presi dal CI.':
    'A monitored CI and its {tipo}: the items you tick enter the rule with the name (and description) taken from the CI.',
  'Scegli un CI.':
    'Choose a CI.',
  'Questo CI non ha parametri di questo tipo.':
    'This CI has no parameters of this kind.',
  'Aggiungi {n} voci':
    'Add {n} items',
  'In una regola «per nome» ogni voce — un’interfaccia, un servizio, una porta — ha le sue regolazioni (accendi o spegni, frequenza, soglie); ciò che non dice lo prende dalla regola. «Prendi da un CI…» elenca i parametri di quel tipo di un CI monitorato, con la descrizione della porta: li spunti e diventano voci della regola, un template fatto da uno switch vero da applicare agli altri.':
    'In a “by name” rule every item — an interface, a service, a port — has its own settings (on or off, interval, thresholds); what it leaves out it takes from the rule. “Take from a CI…” lists the parameters of that kind of a monitored CI, with the port description: tick them and they become items of the rule, a template made from a real switch to apply to the others.',
  'AWX':
    'AWX',
  'AWX / Automation Controller utilizza la CMDB come sorgente dinamica d’inventario tramite una chiave API in sola lettura; i tag vengono esposti come gruppi, consentendo di confrontare gli host gestiti da AWX con quelli censiti e individuare eventuali disallineamenti.':
    'AWX / Automation Controller uses the CMDB as a dynamic inventory source through a read-only API key; the tags are exposed as groups, so the hosts managed by AWX can be compared with the inventoried ones and any misalignment found.',
  'Abilitato':
    'Enabled',
  'Ansible AWX':
    'Ansible AWX',
  "Basic auth con un utente in sola lettura: funziona, ma un token si revoca senza toccare l'utente.":
    'Basic auth with a read-only user: it works, but a token can be revoked without touching the user.',
  'Classi':
    'Classes',
  'Come preparo AWX per CENSO?':
    'How do I prepare AWX for CENSO?',
  'Confronto con gli host di AWX':
    'Comparison with the AWX hosts',
  'Confronto…':
    'Comparing…',
  'Host in AWX':
    'Host in AWX',
  'In AWX, sconosciuti a CENSO ({n})':
    'In AWX, unknown to CENSO ({n})',
  'In CENSO, non in AWX ({n})':
    'In CENSO, not in AWX ({n})',
  'Indirizzo di AWX':
    'AWX address',
  'Inventario da confrontare':
    'Inventory to compare',
  'Inventario di AWX':
    'AWX inventory',
  'Inventario dinamico (AWX ← CENSO)':
    'Dynamic inventory (AWX ← CENSO)',
  'Lettura in tempo reale degli host di AWX / Automation Controller (REST API) per confrontarli con la CMDB. Non apre porte e non avvia job — la query esce da questa sonda attraverso il relay. Abilitala dove la sonda raggiunge AWX.':
    'Live reading of the hosts of AWX / Automation Controller (REST API) to compare them with the CMDB. Opens no port and starts no job — the query leaves this probe through the relay. Enable it where the probe reaches AWX.',
  'Nessuno: tutti gli host della CMDB sono in AWX.':
    'None: every host of the CMDB is in AWX.',
  "Nome o id dell'inventario di AWX con cui confrontare la CMDB. Vuoto: tutti gli host di AWX.":
    'Name or id of the AWX inventory to compare the CMDB with. Empty: every AWX host.',
  'Produzione':
    'Production',
  'Scarica lo script':
    'Download the script',
  'Token di accesso':
    'Access token',
  'URL base di AWX o di Automation Controller, senza /api: il prefisso (/api/v2 oppure /api/controller/v2) lo trova CENSO da solo.':
    'Base URL of AWX or Automation Controller, without /api: CENSO finds the prefix (/api/v2 or /api/controller/v2) by itself.',
  'Un token personale (Users → Tokens) con ambito «Read», di un utente che veda gli inventari da confrontare (ruolo Auditor, o «Read» sugli inventari). Viaggia come `Authorization: Bearer`.':
    'A personal token (Users → Tokens) with scope “Read”, of a user who sees the inventories to compare (Auditor role, or “Read” on the inventories). It travels as `Authorization: Bearer`.',
  'ansible_host':
    'ansible_host',
  'classi: server,switch':
    'classes: server,switch',
  'come si configura':
    'how to set it up',
  'https://awx.example.com':
    'https://awx.example.com',
  'inventario della credenziale (o tutti)':
    'the credential’s inventory (or all)',
  'leggo gli inventari…':
    'reading the inventories…',
  'tag: ruolo:web':
    'tag: role:web',
  'Accendi i {n} filtrati':
    'Switch on the {n} filtered',
  'Accendi tutti ({n})':
    'Switch all on ({n})',
  'Spegni i {n} filtrati':
    'Switch off the {n} filtered',
  'Spegni tutti ({n})':
    'Switch all off ({n})',
  'Spegni i {n} che non rispondono':
    'Switch off the {n} that do not respond',
  'I pulsanti agiscono sulle {n} righe lasciate dai filtri della tabella.':
    'The buttons act on the {n} rows the table filters leave.',
  'Filtra la tabella (per esempio Stato = «non risponde») e i pulsanti agiscono solo su quelle righe.':
    'Filter the table (for example State = “does not respond”) and the buttons act on those rows only.',
  'In «Modifica», sopra ogni gruppo di parametri, «Accendi i N filtrati» e «Spegni i N filtrati» agiscono sulle righe lasciate dai filtri della tabella (per esempio Stato = «non risponde», o il nome delle porte di uplink), e «Spegni i N che non rispondono» è la scorciatoia: su uno switch con trenta porte giù non si tolgono trenta spunte.':
    'In “Edit”, above each group of parameters, “Switch on the N filtered” and “Switch off the N filtered” act on the rows the table filters leave (for example State = “does not respond”, or the name of the uplink ports), and “Switch off the N that do not respond” is the shortcut: on a switch with thirty ports down you do not untick thirty boxes.',
  'utilizzo (%)':
    'utilisation (%)',
  'traffico in entrata (Mbit/s)':
    'inbound traffic (Mbit/s)',
  'traffico in uscita (Mbit/s)':
    'outbound traffic (Mbit/s)',
  'pacchetti in entrata (pkt/s)':
    'inbound packets (pkt/s)',
  'pacchetti in uscita (pkt/s)':
    'outbound packets (pkt/s)',
  'errori nell’intervallo':
    'errors in the interval',
  'scarti nell’intervallo':
    'discards in the interval',
  'entrata':
    'inbound',
  'pacchetti in entrata':
    'inbound packets',
  'pacchetti in uscita':
    'outbound packets',
  'scarti':
    'discards',
  'Misura':
    'Measure',
  'Misura della serie {n}':
    'Measure of series {n}',
  'traffico in entrata e in uscita (Mbit/s)':
    'inbound and outbound traffic (Mbit/s)',
  'pacchetti al secondo nei due versi':
    'packets per second, both ways',
  'errori e scarti nell’intervallo':
    'errors and discards in the interval',
  'Errori e scarti':
    'Errors and discards',
  'La sonda non ha ancora queste serie per l’interfaccia (arrivano con le finestre successive, o l’apparato non espone i contatori).':
    'The probe does not have these series for the interface yet (they come with the next windows, or the device does not expose the counters).',
  'L’andamento di un parametro di un CI a tua scelta; per un’interfaccia anche il traffico in Mbit/s.':
    'The trend of a parameter of a CI of your choice; for an interface, the traffic in Mbit/s too.',
  '«Grafico di un parametro» su un’interfaccia mostra, con «Misura», il traffico in entrata e in uscita in Mbit/s, i pacchetti al secondo o errori e scarti al posto dell’utilizzo; in «Grafico personalizzato» ogni serie di un’interfaccia ha la sua «Misura» (utilizzo, entrata, uscita, pacchetti, errori, scarti), così due WAN si confrontano in Mbit/s nello stesso grafico.':
    '“Chart of a parameter” on an interface shows, with “Measure”, the inbound and outbound traffic in Mbit/s, the packets per second or errors and discards instead of the utilisation; in “Custom chart” every series of an interface has its own “Measure” (utilisation, inbound, outbound, packets, errors, discards), so two WANs compare in Mbit/s on the same chart.',
  '48 ore':
    '48 hours',
  'tutti gli episodi':
    'all episodes',
  'solo critici e non risponde':
    'critical and not responding only',
  'solo chi non rispondeva':
    'only what did not respond',
  'Apri il grafico del parametro':
    'Open the parameter\'s chart',
  'Episodi di allarme per intervallo':
    'Alarm episodes per interval',
  '{n} episodi':
    '{n} episodes',
  'Storico degli allarmi':
    'Alarm history',
  'Nessun allarme nel periodo.':
    'No alarm in the period.',
  'Nessun episodio con questo filtro.':
    'No episode with this filter.',
  'e altri {n} episodi nel periodo':
    'and {n} more episodes in the period',
  'Gli episodi di allarme del periodo, anche quelli già rientrati: su cosa, quando, per quanto. Un clic apre il grafico.':
    'The alarm episodes of the period, the ones already over included: on what, when, for how long. A click opens the chart.',
  'Il parametro non è più monitorato: restano le misure conservate.':
    'The parameter is no longer monitored: the kept measures remain.',
  '«Storico degli allarmi» elenca gli episodi del periodo (24 ore, 48 ore, 7 giorni) anche se già rientrati — su quale CI e parametro, da quando a quando, per quanto, con le barre per ora o per giorno — così un «giù» di tre minuti alle quattro di notte non sfugge; un clic apre il grafico in una finestra, senza lasciare il cruscotto. Lo stesso vale per le tessere con un problema nella parete e per le righe dei problemi aperti: si aprono in una finestra, con il collegamento alla scheda del CI dentro. Il sinottico a parete ha il pannello «Ultimi allarmi» delle 24 ore.':
    '“Alarm history” lists the episodes of the period (24 hours, 48 hours, 7 days) even when already over — on which CI and parameter, from when to when, for how long, with bars per hour or per day — so a 3-minute down at four in the morning does not slip by; a click opens the chart in a window, without leaving the dashboard. The same goes for the tiles with a problem on the wall and for the rows of the open problems: they open in a window, with the link to the CI\'s page inside. The wall display has the “Latest alarms” panel of the last 24 hours.',
  'Ping rapido (latenza)':
    'Fast ping (latency)',
  'Ping rapido':
    'Fast ping',
  'latenza misurata ogni {s} secondi e riassunta al minuto (media, minimo, massimo, pacchetti persi): le fluttuazioni che un ping al minuto non vede':
    'latency measured every {s} seconds and summarised per minute (average, minimum, maximum, packets lost): the fluctuations a ping per minute does not see',
  'Ultimo minuto':
    'Last minute',
  'da {a} a {b}':
    'from {a} to {b}',
  'Persi':
    'Lost',
  'Perdita nella finestra':
    'Loss in the window',
  '{n} ping':
    '{n} pings',
  'Fra i parametri di disponibilità c’è anche il «Ping rapido (latenza)», spento di default: un pacchetto ogni 5 secondi e, ogni minuto, media, minimo, massimo e pacchetti persi — la fascia del grafico è la fluttuazione, che un ping al minuto non vede. Lo stato si decide sul minuto: attenzione con un pacchetto perso o la media oltre la soglia, critico con la metà persi, «non risponde» se non risponde nessuno. Accendilo sugli uplink e sui siti remoti che «ballano», non su tutto il parco: sono dodici pacchetti al minuto per oggetto.':
    'Among the availability parameters there is also the “Fast ping (latency)”, off by default: one packet every 5 seconds and, every minute, average, minimum, maximum and packets lost — the band of the chart is the fluctuation, which a ping per minute does not see. The state is decided on the minute: warning with one packet lost or the average over the threshold, critical with half lost, “does not respond” if nothing answers. Switch it on for the uplinks and the remote sites that “wobble”, not for the whole estate: it is twelve packets a minute per object.',
  'KMS':
    'KMS',
  'HashiCorp Vault (Transit)':
    'HashiCorp Vault (Transit)',
  'AWS KMS, Azure Key Vault e GCP KMS: con lo stesso contratto, quando servono.':
    'AWS KMS, Azure Key Vault and GCP KMS: same contract, when needed.',
  'Indirizzo di Vault':
    'Vault address',
  'Namespace (facoltativo)':
    'Namespace (optional)',
  'Motore Transit':
    'Transit engine',
  'Nome della chiave':
    'Key name',
  'Token di Vault':
    'Vault token',
  'lascia vuoto per tenere quello salvato':
    'leave empty to keep the saved one',
  'Una policy con «encrypt» e «decrypt» su quella chiave, niente altro.':
    'A policy with “encrypt” and “decrypt” on that key, nothing else.',
  'Da dove si chiama Vault':
    'Where Vault is called from',
  'dal Control Plane (Vault raggiungibile da Internet)':
    'from the Control Plane (Vault reachable from the Internet)',
  'attraverso una sonda (Vault solo nella tua rete)':
    'through a probe (Vault only on your network)',
  'Verifica il certificato TLS di Vault':
    'Verify Vault\'s TLS certificate',
  'Prova la connessione':
    'Test the connection',
  'Salva e ri-avvolgi con questa chiave':
    'Save and re-wrap with this key',
  'Adotta questa chiave':
    'Adopt this key',
  'Da ora le chiavi dei tuoi segreti sono avvolte dalla tua chiave: i segreti esistenti vengono ricifrati in background e le versioni che la piattaforma poteva aprire spariscono a fine ricifratura. Se la chiave non risponde, discovery e query live si fermano.':
    'From now on the keys of your secrets are wrapped by your key: the existing secrets are re-encrypted in background and the versions the platform could open disappear when that is over. If the key does not answer, discovery and live queries stop.',
  'Un momento…':
    'One moment…',
  'La tua chiave (BYOK)':
    'Your key (BYOK)',
  'in ritiro':
    'retiring',
  'Stai tornando alla chiave della piattaforma: la tua chiave resta finché le versioni che ha avvolto non sono ricifrate.':
    'You are going back to the platform\'s key: yours stays until the versions it wrapped are re-encrypted.',
  '{n} versioni della chiave dati le può ancora aprire la piattaforma: spariscono a ricifratura finita.':
    '{n} versions of the data key can still be opened by the platform: they disappear when re-encryption is over.',
  'Solo la tua chiave apre i segreti di questo tenant. Da {quando}.':
    'Only your key opens this tenant\'s secrets. Since {quando}.',
  'Vault si chiama attraverso una tua sonda.':
    'Vault is called through one of your probes.',
  'Vault si chiama dal Control Plane.':
    'Vault is called from the Control Plane.',
  'Cambia impostazioni o token':
    'Change settings or token',
  'Torna alla chiave della piattaforma':
    'Back to the platform\'s key',
  'Le chiavi dei segreti tornano sotto la chiave della piattaforma; serve che Vault risponda ancora per ricifrare. Confermi?':
    'The keys of the secrets go back under the platform\'s key; Vault must still answer to re-encrypt. Confirm?',
  'Vale per i segreti (credenziali, connettori, token), non per l’inventario del database condiviso; durante l’uso i segreti stanno in memoria del Control Plane. Se il KMS non risponde, discovery e query live di questo tenant si fermano con «chiave non disponibile».':
    'It covers the secrets (credentials, connectors, tokens), not the inventory in the shared database; while in use the secrets are in the Control Plane\'s memory. If the KMS does not answer, discovery and live queries of this tenant stop with “key unavailable”.',
  'Vault risponde: cifra e decifra con la chiave «{ref}».':
    'Vault answers: it encrypts and decrypts with the key “{ref}”.',
  'Nel piano enterprise c’è «Porta la tua chiave» (BYOK): colleghi il tuo HashiCorp Vault (motore Transit), lo provi e lo adotti; da lì le chiavi dei tuoi segreti sono avvolte dalla tua chiave e aperte chiedendolo a Vault, dal Control Plane o attraverso una tua sonda se Vault sta solo nella tua rete. Finita la ricifratura, le versioni che la piattaforma poteva aprire spariscono. Revochi togliendo la policy al token: da quel momento discovery e query live del tenant si fermano con «chiave non disponibile», ed è voluto. Vale per i segreti, non per l’inventario del database condiviso.':
    'The enterprise plan has “Bring your own key” (BYOK): you connect your HashiCorp Vault (Transit engine), test it and adopt it; from then on the keys of your secrets are wrapped by your key and opened by asking Vault, from the Control Plane or through one of your probes if Vault lives only on your network. When re-encryption is over, the versions the platform could open disappear. You revoke by taking the policy off the token: from that moment discovery and live queries of the tenant stop with “key unavailable”, by design. It covers the secrets, not the inventory of the shared database.',
  'Parete':
    'Wall',
  'Sinottico di Eagle':
    'Eagle wall display',
  'Apri il sinottico':
    'Open the wall display',
  'Scegli e ridimensiona i widget del sinottico':
    'Choose and resize the widgets of the wall display',
  'Il sinottico è vuoto: scegli i widget da «Personalizza».':
    'The wall display is empty: choose the widgets from “Customise”.',
  'Il sinottico a parete si personalizza da Eagle → Sinottico (o da «Personalizza» sul monitor): è una dashboard come il cruscotto, con i widget di Eagle da scegliere, ridimensionare (quattro larghezze e «alto»), trascinare e dividere in sezioni che ruotano al ritmo scelto; si salva come propria o per il tenant. Sul monitor restano i numeri in alto e l’orologio, e la parete dei CI mostra tutti i CI.':
    'The wall display is customised from Eagle → Wall display (or from “Customise” on the monitor): it is a dashboard like the cockpit, with Eagle\'s widgets to choose, resize (four widths and “tall”), drag and split into sections that rotate at the chosen pace; it is saved as yours or for the tenant. On the monitor the numbers at the top and the clock stay, and the wall of CIs shows every CI.',
  'Sul sinottico: cambia pagina ogni':
    'On the wall display: change page every',
  'mai: la parete scorre':
    'never: the wall scrolls',
  '10 secondi':
    '10 seconds',
  '15 secondi':
    '15 seconds',
  '20 secondi':
    '20 seconds',
  '30 secondi':
    '30 seconds',
  '60 secondi':
    '60 seconds',
  'Vale sul sinottico a parete: le tessere che non entrano nello schermo si mostrano a pagine, in ordine di gravità (i problemi nella prima), senza che nessuno debba scorrere.':
    'On the wall display: the tiles that do not fit the screen come in pages, by severity (the problems in the first one), with no one having to scroll.',
  'pagina {p} di {n}':
    'page {p} of {n}',
  'Sul monitor niente lampeggia: i widget si aggiornano tenendo il disegno (la dissolvenza c’è solo al primo caricamento) e le sole tessere che pulsano sono quelle «non risponde». Le sezioni ruotano al tempo impostato in modifica («Sinottico: cambia sezione ogni N s», 0 = ferma), e la parete dei CI cambia pagina da sola al tempo scelto nelle sue opzioni, mostrando per prime le tessere con problemi.':
    'Nothing blinks on the monitor: the widgets refresh keeping their drawing (the fade is only on the first load) and the only pulsing tiles are the “does not respond” ones. The sections rotate at the time set in edit mode (“Wall display: change section every N s”, 0 = still), and the wall of CIs turns its pages on its own at the pace chosen in its options, showing the tiles with problems first.',
  'Link per i monitor non valido, revocato o scaduto':
    'Monitor link not valid, revoked or expired',
  'Questo monitor non può aprire il sinottico':
    'This monitor cannot open the wall display',
  'Il sinottico è vuoto: si compone dalla console, in Eagle → Sinottico.':
    'The wall display is empty: it is composed from the console, in Eagle → Wall display.',
  'Revocare il link «{name}»? Il monitor che lo usa resta a schermo vuoto.':
    'Revoke the link “{name}”? The monitor using it is left with an empty screen.',
  'Titolo sul monitor':
    'Title on the monitor',
  'il nome della dashboard':
    'the dashboard\'s name',
  'IP autorizzati':
    'Allowed IPs',
  'qualsiasi indirizzo':
    'any address',
  'Link per i monitor':
    'Links for the monitors',
  'Nuovo link':
    'New link',
  'Link creato: copialo adesso, non sarà più visibile.':
    'Link created: copy it now, it will not be shown again.',
  'Aprilo nel browser del monitor, in modalità chiosco o a schermo intero: il sinottico parte da solo e si aggiorna da sé.':
    'Open it in the monitor\'s browser, in kiosk mode or full screen: the wall display starts on its own and keeps itself up to date.',
  'es. NOC, corridoio piano 2':
    'e.g. NOC, 2nd floor corridor',
  'personale':
    'personal',
  'IP autorizzati (uno per riga, anche reti come 192.168.10.0/24)':
    'Allowed IPs (one per line, networks like 192.168.10.0/24 too)',
  'Senza indirizzi il link vale da ovunque: chiunque lo abbia vede il monitoraggio. Meglio elencare gli IP dei monitor.':
    'Without addresses the link works from anywhere: whoever has it sees the monitoring. Better list the monitors\' IPs.',
  'Crea link':
    'Create link',
  '«Nuovo link» ne crea uno: lo apri sul monitor e il sinottico parte senza accesso.':
    '“New link” creates one: open it on the monitor and the wall display starts with no login.',
  'link':
    'links',
  'Il sinottico a parete ha un link apposta per i monitor: da Eagle → Sinottici pubblicati, «Link per i monitor» crea un indirizzo (/monitor/eagle/…) che apre il sinottico senza accesso, in sola lettura, dagli indirizzi IP che elenchi (anche reti), con la dashboard e il titolo che scegli, e si revoca con un clic; l’elenco dice da quale indirizzo e quando è stato usato l’ultima volta. In alto a sinistra il marchio CENSO · DiscoveryGuard, a destra «Eagle» con il titolo del sinottico (il nome della dashboard, o quello scritto sul link).':
    'The wall display has a link made for the monitors: from Eagle → Published wall displays, “Links for the monitors” creates an address (/monitor/eagle/…) that opens the wall with no login, read-only, from the IP addresses you list (networks too), with the dashboard and title you choose, and is revoked with a click; the list says from which address and when it was last used. Top left the CENSO · DiscoveryGuard brand, on the right “Eagle” with the wall\'s title (the dashboard\'s name, or the one written on the link).',
  'Porta dello switch':
    'Switch port',
  'Fra le colonne aggiuntive ci sono «Switch» e «Porta dello switch»: a quale apparato e porta è attaccato ogni CI, dalle relazioni lette dalla MAC table dello switch (e da LLDP/CDP), con il collegamento alla scheda dello switch; finiscono anche nell’Excel e nel PDF, così i monitor di un reparto si estraggono con la loro porta senza aprire ogni scheda.':
    'Among the extra columns are “Switch” and “Switch port”: which device and port each CI is plugged into, from the relations read from the switch\'s MAC table (and from LLDP/CDP), with a link to the switch\'s page; they go into the Excel and the PDF too, so the monitors of a ward are exported with their port without opening every page.',
  '¼ di riga':
    '¼ of a row',
  '⅓ di riga':
    '⅓ of a row',
  '½ riga':
    '½ row',
  '⅔ di riga':
    '⅔ of a row',
  '¾ di riga':
    '¾ of a row',
  'riga intera':
    'whole row',
  'Mostra a parete':
    'Show on the wall',
  'Gestione cruscotti':
    'Dashboard management',
  'Il link si gestisce dalla console, in Eagle → Sinottici pubblicati → «Link per i monitor».':
    'The link is managed from the console, in Eagle → Published wall displays → “Links for the monitors”.',
  'Nuovo nome':
    'New name',
  '{name} (copia)':
    '{name} (copy)',
  'Eliminare «{name}»? È del tenant: sparisce per tutti, anche dai monitor che la mostrano.':
    'Delete “{name}”? It is the tenant\'s: it disappears for everyone, also from the monitors showing it.',
  'Eliminare «{name}»?':
    'Delete “{name}”?',
  'del tenant':
    'the tenant\'s',
  'mia':
    'mine',
  'Widget':
    'Widgets',
  'Modificata':
    'Changed',
  'Blocco':
    'Lock',
  'bloccata':
    'locked',
  'A parete':
    'On the wall',
  'Rendi mia':
    'Make it mine',
  'Condividi con il tenant':
    'Share with the tenant',
  'Nuovo cruscotto':
    'New cockpit',
  'Nessun cruscotto salvato.':
    'No saved cockpit.',
  'Il cruscotto predefinito si vede comunque: da «Personalizza» si salva come proprio o per il tenant, e compare qui.':
    'The default cockpit is shown anyway: from “Personalise” it is saved as yours or for the tenant, and it appears here.',
  'cruscotti':
    'dashboards',
  'CI monitorati per classe':
    'Monitored CIs by class',
  'e altre {n} classi':
    'and {n} more classes',
  'Indirizzo IP da raggiungere':
    'IP address to reach',
  'es. Internet da Sesto':
    'e.g. Internet from Sesto',
  'Sonda che misura':
    'Measuring probe',
  'Collego…':
    'Linking…',
  'Cambia bersaglio':
    'Change target',
  'Collegato: {name} ({address}){probe}':
    'Linked: {name} ({address}){probe}',
  'misura {probe}':
    'measured by {probe}',
  '«Collega» crea (una volta) il CI per l’indirizzo con ping e ping rapido accesi dalla sonda scelta; la latenza arriva entro un minuto.':
    '“Link” creates (once) the CI for the address with ping and the fast ping on from the chosen probe; the latency arrives within a minute.',
  'Indirizzo e sonda':
    'Address and probe',
  'Latenza verso un indirizzo':
    'Latency to an address',
  'Scegli l’indirizzo e la sonda in «Personalizza», poi «Collega».':
    'Choose the address and the probe in “Personalise”, then “Link”.',
  'da {probe}':
    'from {probe}',
  'Il tempo di risposta verso un IP pubblico (o un capo remoto), misurato da una sonda a scelta.':
    'The response time to a public IP (or a remote end), measured by a probe of your choice.',
  '{n} di {tot} CI':
    '{n} of {tot} CIs',
  'Il widget «Latenza verso un indirizzo» disegna il tempo di risposta verso un IP pubblico (8.8.8.8, il capo remoto di una WAN) misurato da una sonda a scelta: in «Personalizza» scrivi l’indirizzo, un nome e la sonda, «Collega» crea una volta il CI per quell’indirizzo con ping e ping rapido accesi e la sonda come punto di osservazione, e il grafico è quello del ping rapido (media, minimo e massimo al minuto). Nella parete dei CI sul monitor le tessere cambiano pagina da sole (15 secondi se non scegli altro), e nel cruscotto il sottotitolo dice «40 di 159 CI» quando non entrano tutte. Il menu per scegliere quale dashboard sta a sinistra, sotto il titolo.':
    'The “Latency to an address” widget draws the response time to a public IP (8.8.8.8, the far end of a WAN) measured by a probe of your choice: in “Personalise” type the address, a name and the probe, “Link” creates once the CI for that address with ping and the fast ping on and the probe as its observation point, and the graph is the fast ping\'s (average, minimum and maximum per minute). On the monitor the wall of CIs turns its pages on its own (15 seconds unless you choose otherwise), and in the cockpit the subtitle says “40 of 159 CIs” when not all fit. The menu to pick a dashboard sits on the left, under the title.',
  'fine dello schermo':
    'end of the screen',
  'Trascina per ridimensionare':
    'Drag to resize',
  'Posizione e misura in celle (24 colonne × 12 righe = uno schermo)':
    'Position and size in cells (24 columns × 12 rows = one screen)',
  'Scegli un’immagine in «Personalizza»: un file dal computer o un indirizzo.':
    'Choose a picture in “Personalise”: a file from the computer or an address.',
  'Scrivi il testo in «Personalizza».':
    'Write the text in “Personalise”.',
  'L’immagine resta troppo grande anche ridotta: usa un file più semplice o un indirizzo.':
    'The picture stays too big even reduced: use a simpler file or an address.',
  'Cambia file…':
    'Change file…',
  'Scegli un file…':
    'Choose a file…',
  'Usa l’indirizzo':
    'Use the address',
  'Togli l’immagine':
    'Remove the picture',
  'Il file viene ridotto nel browser (lato lungo 1600 px) e salvato dentro la dashboard: {kb} kB.':
    'The file is reduced in the browser (longer side 1600 px) and saved inside the dashboard: {kb} kB.',
  'Un file dal computer viene ridotto nel browser e salvato dentro la dashboard; un indirizzo deve essere raggiungibile dal monitor.':
    'A file from the computer is reduced in the browser and saved inside the dashboard; an address must be reachable from the monitor.',
  'Composizione':
    'Composition',
  'tutta dentro':
    'all inside',
  'riempi, tagliando':
    'fill, cropping',
  'stira':
    'stretch',
  'Didascalia (facoltativa)':
    'Caption (optional)',
  'Con cornice e titolo':
    'With frame and title',
  'piccolo':
    'small',
  'grande':
    'large',
  'titolo':
    'heading',
  'Allineamento':
    'Alignment',
  'a sinistra':
    'left',
  'al centro':
    'centred',
  'a destra':
    'right',
  'Una planimetria, uno schema, un logo: un file dal computer o un indirizzo.':
    'A floor plan, a diagram, a logo: a file from the computer or an address.',
  'Un titolo, una didascalia, una legenda.':
    'A heading, a caption, a legend.',
  'Una matrice di 24 colonne per 12 righe (uno schermo): ogni widget si sposta e si ridimensiona a mano, anche sopra un altro.':
    'A matrix of 24 columns by 12 rows (one screen): every widget is moved and resized by hand, even over another.',
  'Disposizione libera':
    'Free layout',
  'Sinottici pubblicati':
    'Published wall displays',
  'Con «Disposizione libera» una sezione diventa una matrice di 24 colonne per 12 righe (uno schermo del monitor): ogni widget si sposta trascinandolo dalla maniglia ⋮⋮ e si ridimensiona dall’angolo in basso a destra, in celle, anche sopra un altro widget (l’ultimo sta sopra); in modifica una linea tratteggiata segna dove finisce lo schermo. Nel catalogo, gruppo «Composizione», ci sono «Immagine» (una planimetria, uno schema, un logo: un file dal computer, ridotto nel browser e salvato dentro la dashboard, o un indirizzo; senza cornice è uno sfondo) e «Testo» (titoli, didascalie, legende, in quattro dimensioni). Così un sinottico si compone come si vuole: la pianta del reparto sotto, le tessere dei CI e le scritte sopra. Un sinottico pubblicato è un cruscotto assegnato a un monitor: la voce «Sinottici pubblicati» elenca i monitor, con il cruscotto che ognuno mostra e il link che lo apre senza accesso.':
    'With “Free layout” a section becomes a matrix of 24 columns by 12 rows (one monitor screen): every widget is moved by dragging its ⋮⋮ handle and resized from the bottom-right corner, in cells, even over another widget (the last one is on top); in edit mode a dashed line marks where the screen ends. The catalogue\'s “Composition” group has “Image” (a floor plan, a diagram, a logo: a file from the computer, reduced in the browser and saved inside the dashboard, or an address; without a frame it is a background) and “Text” (headings, captions, legends, in four sizes). So a wall display is composed as you like: the ward\'s floor plan below, the CI tiles and the labels on top. A published wall display is a cockpit assigned to a monitor: the “Published wall displays” entry lists the monitors, with the cockpit each one shows and the link that opens it with no login.',
  'la rete del primo uso':
    'the network of the first use',
  'l’indirizzo del primo uso':
    'the address of the first use',
  'Indirizzi autorizzati':
    'Allowed addresses',
  'quelli elencati qui sotto':
    'the ones listed below',
  'la rete /24 del primo uso (monitor in DHCP)':
    'the /24 network of the first use (monitor on DHCP)',
  'Il link vale da ovunque finché non viene aperto la prima volta: da allora solo dalla rete /24 di quel monitor, anche se il DHCP gli cambia indirizzo.':
    'The link works from anywhere until it is opened for the first time: from then on only from that monitor\'s /24 network, even if DHCP changes its address.',
  'Dal primo uso in poi vale solo da quell’indirizzo.':
    'From the first use on it works only from that address.',
  'Un monitor in DHCP: indica la rete della sua subnet (es. 192.168.10.0/24) o scegli il blocco al primo uso.':
    'A monitor on DHCP: list its subnet (e.g. 192.168.10.0/24) or choose the lock at the first use.',
  'Un monitor in DHCP non ha un indirizzo da elencare: si indica la sua rete (es. 192.168.10.0/24), oppure il link si «blocca» da solo al primo uso, sull’indirizzo o sulla rete /24 del monitor che lo apre per primo; fino ad allora vale da ovunque, quindi aprilo subito sul monitor giusto.':
    'A monitor on DHCP has no address to list: give its network (e.g. 192.168.10.0/24), or let the link “lock” itself at the first use, to the address or the /24 network of the monitor that opens it first; until then it works from anywhere, so open it on the right monitor right away.',
  'Chiave multi agente':
    'Multi-agent key',
  'è una chiave':
    'is a key',
  'Il comando vale un’ora (fino alle {ora}) e contiene il segreto di questa chiave: è l’unica volta in cui compare. Passata la finestra si rigenera — rigenerare la chiave invalida il comando e il pacchetto vecchi (le macchine nuove non si registrano più con quelli), ma le macchine già registrate continuano a funzionare: ognuna ha un segreto suo.':
    'The command is valid for an hour (until {ora}) and carries the secret of this key: the only time it is shown. Once the window closes it is regenerated — regenerating the key invalidates the old command and package (new machines no longer enrol with them), but the machines already enrolled keep working: each has a secret of its own.',
  'Il comando vale un’ora e contiene il segreto di questa chiave: è l’unica volta in cui compare. Passata la finestra si rigenera — rigenerare la chiave invalida il comando e il pacchetto vecchi (le macchine nuove non si registrano più con quelli), ma le macchine già registrate continuano a funzionare: ognuna ha un segreto suo.':
    'The command is valid for an hour and carries the secret of this key: the only time it is shown. Once the window closes it is regenerated — regenerating the key invalidates the old command and package (new machines no longer enrol with them), but the machines already enrolled keep working: each has a secret of its own.',
  'Altre azioni':
    'More actions',
  'Solo gli amministratori assegnano i cruscotti ai monitor.':
    'Only administrators assign cockpits to monitors.',
  'Chiedi a un amministratore del tenant di creare il link per il monitor.':
    'Ask a tenant administrator to create the link for the monitor.',
  'Tutti i cruscotti di Eagle: aprirli, mandarli a parete, duplicarli, rinominarli, condividerli con il tenant; i monitor si assegnano in «Sinottici pubblicati».':
    'All the cockpits of Eagle: open them, send them to the wall, duplicate, rename, share them with the tenant; monitors are assigned in “Published wall displays”.',
  'Cruscotto mostrato':
    'Cockpit shown',
  'quello del tenant':
    'the tenant\'s',
  'Cruscotto da mostrare':
    'Cockpit to show',
  'quello del tenant (o la disposizione da parete predefinita)':
    'the tenant\'s (or the default wall layout)',
  'Un monitor a parete: un link che mostra il cruscotto scelto senza accesso, in sola lettura per Eagle (non apre la console), solo dagli indirizzi IP che autorizzi, revocabile con un clic. Il segreto si mostra una volta sola.':
    'A wall monitor: a link that shows the chosen cockpit with no login, read-only for Eagle (it does not open the console), only from the IP addresses you allow, revoked with a click. The secret is shown once.',
  'Cruscotti':
    'Cockpits',
  'I monitor a cui assegnare un cruscotto, con il loro link senza accesso, stanno in':
    'The monitors to assign a cockpit to, with their no-login link, are in',
  'Eliminare il link «{name}»? Sparisce dall’elenco, con il suo ultimo uso.':
    'Delete the link “{name}”? It disappears from the list, with its last use.',
  'In ogni riquadro il grafico del tempo di risposta del ping (ultima ora)':
    'In every tile the ping response-time graph (last hour)',
  'Con il «Ping rapido (latenza)» acceso: media, minimo e massimo di dodici pacchetti al minuto; con il ping normale: il miglior tempo dei tre pacchetti al minuto (serve la sonda aggiornata).':
    'With the “Fast ping (latency)” on: average, minimum and maximum of twelve packets a minute; with the plain ping: the best time of the three packets a minute (the probe must be up to date).',
  'Misura (interfacce e ping)':
    'Measure (interfaces and ping)',
  'il valore con le soglie':
    'the value with its thresholds',
  'tempo di risposta del ping (ms)':
    'ping response time (ms)',
  'Per un’interfaccia: le serie che la sonda tiene accanto all’utilizzo. Per il ping: il tempo di risposta accanto alla perdita. Per gli altri parametri si disegna il valore.':
    'For an interface: the series the probe keeps next to the utilisation. For the ping: the response time next to the loss. For the other parameters the value is drawn.',
  'Tempo di risposta':
    'Response time',
  'Nella parete dei CI la spunta «In ogni riquadro il grafico del tempo di risposta del ping» disegna dentro ogni tessera il tempo di risposta dell’ultima ora, letto per tutta la parete in una chiamata sola: con il ping rapido la media con la fascia minimo–massimo di dodici pacchetti al minuto, con il ping normale il miglior tempo dei tre pacchetti al minuto (la sonda lo manda come serie derivata «ping/rtt», senza un pacchetto in più); le tessere si allungano per farle posto. Lo stesso tempo di risposta si sceglie come «Misura» nel «Grafico di un parametro» sul ping.':
    'In the wall of CIs the “In every tile the ping response-time graph” option draws inside each tile the response time of the last hour, read for the whole wall in one call: with the fast ping the average with the min–max band of twelve packets a minute, with the plain ping the best time of the three packets a minute (the probe sends it as the derived series “ping/rtt”, with no extra packet); the tiles grow to make room. The same response time is chosen as “Measure” in “Graph of a parameter” on the ping.',
  'Link nuovo':
    'New link',
  'Riavvia il PC':
    'Reboot the PC',
  'Riavviare il PC del monitor? Il sinottico torna da solo all’accesso.':
    'Reboot the monitor\'s PC? The wall display comes back on its own at logon.',
  'Scollegare «{name}»? Sul suo schermo tornerà il codice di accoppiamento.':
    'Unpair “{name}”? The pairing code will be back on its screen.',
  'con un problema':
    'with a problem',
  'in linea, browser fermo':
    'online, browser stopped',
  'Schermo':
    'Screen',
  'non so':
    'unknown',
  'Spegni schermo':
    'Screen off',
  'Accendi schermo':
    'Screen on',
  'I PC dietro gli schermi con CENSO Monitor: un programma a sé, non l’agente, che mostra un cruscotto in un browser a chiosco e prende ordini da qui. Un PC diventa un monitor solo con l’accoppiamento: al primo avvio CENSO Monitor mostra un codice sullo schermo, tu lo scrivi qui sotto.':
    'The PCs behind the screens running CENSO Monitor: a program of its own, not the agent, that shows a cockpit in a kiosk browser and takes orders from here. A PC becomes a monitor only by pairing: at its first start CENSO Monitor shows a code on the screen, you type it below.',
  'Accoppia un monitor':
    'Pair a monitor',
  'Codice sullo schermo':
    'Code on the screen',
  'il nome del cruscotto':
    'the cockpit\'s name',
  'Accoppio…':
    'Pairing…',
  'Accoppia':
    'Pair',
  'Installare CENSO Monitor':
    'Installing CENSO Monitor',
  'Sul PC del monitor, con l’utente che accende lo schermo: scarica il programma, poi':
    'On the monitor\'s PC, as the user who turns the screen on: download the program, then',
  'La prima riga lo fa partire da solo a ogni accesso; la seconda lo avvia adesso: sullo schermo compare il codice. Serve un browser (Chrome, Chromium, Edge o Firefox).':
    'The first line makes it start on its own at every logon; the second starts it now: the code appears on the screen. A browser is needed (Chrome, Chromium, Edge or Firefox).',
  'Scarica:':
    'Download:',
  'I binari di CENSO Monitor non sono in questa installazione: serve l’immagine del Control Plane che li costruisce.':
    'The CENSO Monitor binaries are not in this installation: the Control Plane image that builds them is needed.',
  'Nessun monitor accoppiato.':
    'No paired monitor.',
  'Installa CENSO Monitor sul PC dello schermo e scrivi qui il codice che compare.':
    'Install CENSO Monitor on the screen\'s PC and type here the code that appears.',
  'monitor':
    'monitors',
  'Questo schermo aspetta di essere accoppiato':
    'This screen is waiting to be paired',
  'Codice di accoppiamento':
    'Pairing code',
  'In CENSO, Eagle → Sinottici pubblicati → «Accoppia un monitor»: scrivi questo codice, dai un nome al monitor e scegli il cruscotto. Il codice vale quindici minuti.':
    'In CENSO, Eagle → Published wall displays → “Pair a monitor”: type this code, name the monitor and choose the cockpit. The code is valid for fifteen minutes.',
  'CENSO Monitor sta chiedendo un codice al Control Plane: se non compare, controlla l’indirizzo del server e la rete.':
    'CENSO Monitor is asking the Control Plane for a code: if none appears, check the server address and the network.',
  'Macchina: {host}':
    'Machine: {host}',
  'I monitor a parete: ogni sinottico pubblicato è un cruscotto assegnato a un monitor. Con CENSO Monitor sul PC dello schermo lo accoppi con un codice e lo comandi da qui; in alternativa un link che si apre senza accesso. Il cruscotto si compone in «Cruscotto» e si manda a parete con «Mostra a parete».':
    'The wall monitors: every published wall display is a cockpit assigned to a monitor. With CENSO Monitor on the screen\'s PC you pair it with a code and command it from here; otherwise a link that opens with no login. The cockpit is composed in “Cockpit” and sent to the wall with “Show on the wall”.',
  'Per comandare lo schermo da remoto c’è CENSO Monitor: un programma a sé (non l’agente), da installare solo sul PC del monitor con l’utente che accende lo schermo («censo-monitor --server … --install»). Al primo avvio mostra un codice a sei cifre: in Eagle → Sinottici pubblicati → «Accoppia un monitor» lo scrivi con un nome e il cruscotto, e da quel momento vedi lo stato del monitor (in linea, browser, schermo) e gli mandi i comandi — cruscotto, ricarica, schermo acceso o spento, riavvio del PC, link nuovo, scollega. Niente diventa un monitor senza il codice confermato dalla console.':
    'To command the screen remotely there is CENSO Monitor: a program of its own (not the agent), to install only on the monitor\'s PC as the user who turns the screen on (“censo-monitor --server … --install”). At its first start it shows a six-digit code: in Eagle → Published wall displays → “Pair a monitor” you type it with a name and the cockpit, and from then on you see the monitor\'s state (online, browser, screen) and send it commands — cockpit, reload, screen on or off, PC reboot, new link, unpair. Nothing becomes a monitor without the code confirmed from the console.',
  'Accendi e spegni lo schermo a orario':
    'Turn the screen on and off on a schedule',
  'Accensione':
    'Switch-on',
  'Spegnimento':
    'Switch-off',
  'Aggiorna CENSO Monitor':
    'Update CENSO Monitor',
  'Aggiorna CENSO Monitor da solo quando il Control Plane ne ha una versione nuova':
    'Update CENSO Monitor by itself when the Control Plane has a newer version',
  'Aggiorna il sistema':
    'Update the system',
  'Cosa mostra':
    'What it shows',
  'Fuso orario (es. Europe/Rome)':
    'Time zone (e.g. Europe/Rome)',
  'Il monitor dice':
    'The monitor reports',
  'Impostazioni…':
    'Settings…',
  'Le voci spente non sono disponibili su questo monitor (lo dice lui, con le sue capacità). Gli orari seguono l’orologio del PC del monitor.':
    'Greyed-out fields are not available on this monitor (it says so itself, through its capabilities). Times follow the clock of the monitor’s PC.',
  'Log del programma':
    'Program log',
  'Luminosità (0–100, vuoto = non toccare)':
    'Brightness (0–100, empty = leave alone)',
  'Nessuno screenshot: chiedilo dal menu «⋯».':
    'No screenshot yet: ask for one from the “⋯” menu.',
  'RAM libera':
    'free RAM',
  'Riavvia il browser':
    'Restart the browser',
  'Riavvio notturno del PC (facoltativo)':
    'Nightly PC reboot (optional)',
  'Ricarica il cruscotto ogni (ore, 0 = mai)':
    'Reload the dashboard every (hours, 0 = never)',
  'Schermo e risparmio energetico':
    'Screen and power saving',
  'Screenshot':
    'Screenshot',
  'Screenshot del monitor':
    'Screenshot of the monitor',
  'Spegnere il PC del monitor? Per riaccenderlo serve qualcuno sul posto (o Wake-on-LAN).':
    'Shut down the monitor’s PC? Someone on site (or Wake-on-LAN) is needed to turn it back on.',
  'Spegni il PC':
    'Shut down the PC',
  'Ultimo screenshot: {q}':
    'Last screenshot: {q}',
  'Zoom (0,5–3)':
    'Zoom (0.5–3)',
  'acceso da {d}':
    'up for {d}',
  'aggiornato {q}':
    'updated {q}',
  'disco libero':
    'free disk',
  'mostra':
    'show',
  '{g} g {h} h':
    '{g} d {h} h',
  '{m} min':
    '{m} min',
  'Un PC dedicato: CENSO Monitor OS':
    'A dedicated PC: CENSO Monitor OS',
  'Per un PC che fa solo il monitor c’è la ISO installabile (x86): scrivila su una chiavetta (dd su Linux e macOS, Rufus in modalità DD su Windows), avvia il PC dalla chiavetta e segui l’installazione, che chiede la rete (cablata o Wi‑Fi) e l’indirizzo di questo Control Plane; al primo avvio lo schermo mostra il codice da scrivere qui accanto. Programma e sistema si aggiornano da soli e la riga del monitor ha tutte le voci, compreso «Aggiorna il sistema».':
    'For a PC that is only a monitor there is the installable ISO (x86): write it to a USB stick (dd on Linux and macOS, Rufus in DD mode on Windows), boot the PC from the stick and follow the installer, which asks for the network (wired or Wi‑Fi) and the address of this Control Plane; at first boot the screen shows the code to type here. Program and system update by themselves and the monitor’s row has every option, including “Update the system”.',
  'Scarica la ISO di installazione':
    'Download the installation ISO',
  '{n} MB':
    '{n} MB',
  'servita da questa installazione':
    'served by this installation',
  'CENSO Monitor OS, la ISO dei monitor':
    'CENSO Monitor OS, the monitors’ ISO',
  'Sorgente: l’ultima release del repository {repo}{token}.':
    'Source: the latest release of the {repo} repository{token}.',
  'con token':
    'with token',
  'Sorgente: l’indirizzo {url}.':
    'Source: the address {url}.',
  'Ultimo controllo {q}: {esito}.':
    'Last check {q}: {esito}.',
  'scaricata':
    'downloaded',
  'già aggiornata':
    'already up to date',
  'Scarica adesso':
    'Download now',
  'Per un PC che fa solo il monitor c’è CENSO Monitor OS, la ISO installabile (x86): nel riquadro «Installare CENSO Monitor» trovi il link con nome, dimensione e impronta SHA‑256; la scrivi su una chiavetta, avvii il PC da lì, l’installazione chiede rete (cablata o Wi‑Fi) e indirizzo del Control Plane, e al primo avvio lo schermo mostra il codice. La ISO è unica per tutti e sta sul sito di DiscoveryGuard: chi gestisce la piattaforma ne scrive l’indirizzo in Piattaforma → CENSO Monitor OS, e il Control Plane può anche tenerne una copia e servirla lui, verificata con l’impronta pubblicata; se non è ancora collegata, il riquadro lo dice.':
    'For a PC that is only a monitor there is CENSO Monitor OS, the installable ISO (x86): the “Installing CENSO Monitor” box has the link with name, size and SHA‑256 digest; you write it to a USB stick, boot the PC from it, the installer asks for the network (wired or Wi‑Fi) and the Control Plane address, and at first boot the screen shows the code. The ISO is the same for everyone and lives on the DiscoveryGuard site: whoever runs the platform writes its address under Platform → CENSO Monitor OS, and the Control Plane can also keep a copy and serve it itself, verified against the published digest; if it is not linked yet, the box says so.',
  'La ISO non è ancora collegata: chi gestisce la piattaforma ne scrive l’indirizzo in Piattaforma → CENSO Monitor OS, e il link compare qui.':
    'The ISO is not linked yet: whoever runs the platform writes its address under Platform → CENSO Monitor OS, and the link appears here.',
  'L’immagine installabile, unica per tutti, per un PC che fa solo il monitor a parete: chi la installa scrive l’indirizzo del Control Plane e poi accoppia lo schermo con il codice. Sta sul sito: scrivi qui il suo indirizzo e ogni tenant la trova in Eagle → Sinottici pubblicati → «Installare CENSO Monitor», con l’impronta SHA‑256 accanto.':
    'The installable image, the same for everyone, for a PC that is only a wall monitor: whoever installs it types the Control Plane address and then pairs the screen with the code. It lives on the site: write its address here and every tenant finds it under Eagle → Published wall views → “Installing CENSO Monitor”, with the SHA‑256 digest beside it.',
  'Indirizzo salvato.':
    'Address saved.',
  'Indirizzo pubblico della ISO (https://…/file.iso)':
    'Public address of the ISO (https://…/file.iso)',
  'Impronta SHA-256 (se accanto al file non c’è il .sha256)':
    'SHA-256 digest (when there is no .sha256 beside the file)',
  'Valore preso dall’ambiente (CENSO_MONITOR_ISO_URL): salvandolo qui vince quello scritto qui.':
    'Value taken from the environment (CENSO_MONITOR_ISO_URL): once saved here, what is written here wins.',
  'Copia su questa installazione (facoltativa)':
    'Copy on this installation (optional)',
  'Il Control Plane può tenere una copia della ISO e servirla lui ai tenant, utile dove i monitor non raggiungono il sito: la scarica dall’indirizzo qui sopra e la accetta solo se l’impronta corrisponde a quella pubblicata. Con CENSO_MONITOR_ISO_AUTO=true la rinnova da sola ogni giorno.':
    'The Control Plane can keep a copy of the ISO and serve it to the tenants itself, useful where the monitors cannot reach the site: it downloads it from the address above and accepts it only if the digest matches the published one. With CENSO_MONITOR_ISO_AUTO=true it renews it by itself every day.',
  'Copia presente:':
    'Copy present:',
  'Nessuna copia su questa installazione: i tenant scaricano dal sito.':
    'No copy on this installation: the tenants download from the site.',
  'Nessuna sorgente: serve l’indirizzo qui sopra (o un repository GitHub da ambiente).':
    'No source: the address above is needed (or a GitHub repository from the environment).',
  'Rinnovo giornaliero acceso.':
    'Daily renewal on.',
  'Rinnovo giornaliero spento.':
    'Daily renewal off.',
  'Download avviato: questa pagina si aggiorna da sola.':
    'Download started: this page refreshes by itself.',
  'Senza Internet: copia la ISO e il suo file .sha256 in {folder} sul volume dei firmware del Control Plane.':
    'Without Internet: copy the ISO and its .sha256 file into {folder} on the Control Plane’s firmware volume.',
  'Tema del sinottico':
    'Wall theme',
  'come il browser':
    'as the browser',
  'Annulla i comandi in attesa':
    'Cancel the pending commands',
  'Scala dell’asse Y':
    'Y axis scale',
  'automatica, dallo zero (le percentuali fino a 100)':
    'automatic, from zero (percentages up to 100)',
  'adattata ai valori, anche senza lo zero':
    'fitted to the values, even without zero',
  'fissa: i limiti qui sotto':
    'fixed: the limits below',
  'Limite inferiore (scala fissa)':
    'Lower limit (fixed scale)',
  'Limite superiore (scala fissa)':
    'Upper limit (fixed scale)',
  'Nell’unità della misura; vuoto = zero.':
    'In the measure’s unit; empty = zero.',
  'Nell’unità della misura; vuoto = dal valore più alto.':
    'In the measure’s unit; empty = from the highest value.',
  'In «Titolo e opzioni» dei grafici («Grafico di un parametro», «Grafico personalizzato», «Latenza verso un indirizzo») si sceglie la scala dell’asse Y: automatica dallo zero (le percentuali fino a 100), adattata ai valori anche senza lo zero — una CPU al 2–5 % o un ping a 10–13 ms smettono di essere una linea piatta — oppure fissa con i limiti che scrivi, nell’unità della misura; i valori fuori dai limiti si fermano al bordo.':
    'In the charts’ “Title and options” (“Chart of a parameter”, “Custom chart”, “Latency to an address”) you choose the Y axis scale: automatic from zero (percentages up to 100), fitted to the values even without zero — a CPU at 2–5 % or a ping at 10–13 ms stop being a flat line — or fixed with the limits you write, in the measure’s unit; values beyond the limits stop at the edge.',
  'Risoluzione dello schermo':
    'Screen resolution',
  'quella del pannello':
    'the panel’s own',
  'Solo i CI con i tag':
    'Only the CIs with the tags',
  'chiave:valore oppure solo chiave (qualunque valore); più tag separati da virgola, tutti richiesti. Vuoto = tutti i CI.':
    'key:value or just key (any value); several tags separated by commas, all required. Empty = every CI.',
  'il valore, nel colore dello stato':
    'the value, in the colour of the status',
  'lo stato, con il valore sotto':
    'the status, with the value under it',
  'Il grafico dell’ultima ora sotto il valore':
    'The last hour’s chart under the value',
  'Nessun CI con questi tag.':
    'No CI with these tags.',
  'Valore di un parametro':
    'Value of a parameter',
  'letto {q}':
    'read {q}',
  'da {q}':
    'since {q}',
  'Scegli CI e parametro in «Titolo e opzioni».':
    'Choose the CI and the parameter in “Title and options”.',
  'Parametro non più presente sul CI.':
    'Parameter no longer present on the CI.',
  'Il valore attuale di un parametro di un CI, grande e nel colore dello stato, o solo lo stato; sotto, l’ultima ora.':
    'The current value of a CI’s parameter, big and in the colour of its status, or just the status; the last hour under it.',
  'Sempre nella parete dei CI, «Solo i CI con i tag» restringe le tessere ai CI che portano certi tag: «sede:milano» per quel tag, «sede» per qualunque valore, più tag separati da virgola e tutti richiesti; il conteggio e le pagine seguono il filtro. Il widget «Valore di un parametro» mostra un parametro di un CI a tua scelta grande e nel colore dello stato — o solo lo stato con il valore sotto — con le soglie e l’ultima ora; è il riquadro da mettere a parete per un numero che conta (la temperatura di una sala, il carico di un UPS, la latenza di una WAN).':
    'Still in the CI wall, “Only the CIs with the tags” narrows the tiles to the CIs carrying certain tags: “sede:milano” for that tag, “sede” for any value, several tags separated by commas and all required; the count and the pages follow the filter. The “Value of a parameter” widget shows a parameter of a CI of your choice, big and in the colour of its status — or just the status with the value under it — with the thresholds and the last hour; it is the box to put on the wall for a number that matters (a room’s temperature, a UPS load, a WAN’s latency).',
  'Chi è':
    'Who it is',
  'Dettaglio (dov’è, a cosa serve: compare sotto il nome)':
    'Detail (where it is, what it is for: shown under the name)',
  'es. Rianimazione, parete nord, 55 pollici':
    'e.g. ICU, north wall, 55 inches',
  'Commento (note per chi se ne occupa: non arriva al monitor)':
    'Comment (notes for the people who look after it: never sent to the monitor)',
  'Commento':
    'Comment',
  'Dettaglio (dov’è, a cosa serve: facoltativo)':
    'Detail (where it is, what it is for: optional)',
  'Ogni monitor ha anche un dettaglio e un commento, per le persone e non per il monitor: il dettaglio (dov’è, a cosa serve, che schermo è) sta sotto il nome nell’elenco e si può scrivere già accoppiando; il commento (chi chiamare, dov’è il PC, cosa è stato fatto) sta nelle «Impostazioni…», sezione «Chi è», dove si cambia anche il nome, e nella colonna nascosta «Commento» dell’elenco.':
    'Every monitor also has a detail and a comment, for the people and not for the monitor: the detail (where it is, what it is for, which screen it is) sits under the name in the list and can be written while pairing; the comment (whom to call, where the PC is, what was done) sits in «Settings…», section «Who it is», where the name can be changed too, and in the hidden «Comment» column of the list.',
  'Testata del sinottico: marchio, titolo, numeri e orologio':
    'Wall header: brand, title, numbers and clock',
  'scala {p} %':
    'scale {p} %',
  'La griglia si disegna alla misura di quel monitor, in scala: caratteri e tessere come li vedrai a parete.':
    'The grid is drawn at that monitor\'s size, scaled down: fonts and tiles as you will see them on the wall.',
  'Schermo di riferimento':
    'Reference screen',
  'nessuno (questa finestra)':
    'none (this window)',
  'Come un monitor accoppiato':
    'Like a paired monitor',
  'altro…':
    'other…',
  'Larghezza in pixel':
    'Width in pixels',
  'Altezza in pixel':
    'Height in pixels',
  'verticale':
    'portrait',
  'zoom del monitor':
    'monitor zoom',
  'disegnato per {s}':
    'designed for {s}',
  'il monitor mostra {s}':
    'the monitor shows {s}',
  '«Schermo di riferimento», nella barra di modifica, dice per quale monitor stai disegnando: uno degli schermi usuali (1024×768, Full HD, 4K…), uno dei monitor accoppiati (con la sua risoluzione, lo zoom e la rotazione), o una misura tua, anche verticale e con lo zoom del monitor. La griglia si disegna a quella misura e si rimpicciolisce per stare nella pagina, con la testata del sinottico e la riga «fine dello schermo»: caratteri e tessere sono quelli che vedrai a parete, e un 4K senza zoom mostra subito i caratteri piccoli. Il riferimento resta salvato con la dashboard, e in «Sinottici pubblicati» la riga del monitor avvisa se mostra un cruscotto disegnato per un’altra misura.':
    '«Reference screen», in the editing bar, says which monitor you are designing for: one of the usual screens (1024×768, Full HD, 4K…), one of the paired monitors (with its resolution, zoom and rotation), or a size of your own, portrait too and with the monitor\'s zoom. The grid is drawn at that size and scaled down to fit the page, with the wall header and the «end of the screen» line: fonts and tiles are the ones you will see on the wall, and a 4K without zoom shows its small characters right away. The reference is saved with the dashboard, and in «Published walls» the monitor\'s row warns when it shows a cockpit designed for another size.',
  'Indirizzo del monitor':
    'Monitor address',
  'Vale da':
    'Valid from',
  'come il Control Plane lo vede: dietro un NAT è l’indirizzo pubblico del sito':
    'as the Control Plane sees it: behind a NAT it is the site\'s public address',
  'qualsiasi indirizzo, fino al prossimo battito del monitor':
    'any address, until the monitor\'s next heartbeat',
  'Rinnovo':
    'Renewal',
  'da solo, prima del {d}':
    'by itself, before {d}',
  'Nessun link creato a mano.':
    'No hand-made link.',
  'I monitor con CENSO Monitor hanno il loro link, qui sotto. «Nuovo link» serve a uno schermo senza il programma.':
    'Monitors running CENSO Monitor have their own link, below. «New link» is for a screen without the program.',
  '{n} link tenuti dai monitor accoppiati':
    '{n} links held by the paired monitors',
  'Li conia e li rinnova il Control Plane, uno per monitor, e non si modificano qui: valgono solo dall’indirizzo da cui il monitor batte (lo seguono se cambia) e si rifanno da soli prima della scadenza. Per cambiarne uno usa «Link nuovo» sulla riga del monitor; per fermarlo, «Scollega».':
    'The Control Plane mints and renews them, one per monitor, and they are not edited here: they are valid only from the address the monitor heartbeats from (and follow it when it changes) and are minted anew by themselves before they expire. To change one use «New link» on the monitor\'s row; to stop it, «Unpair».',
  'Link del sinottico':
    'Wall link',
  'mai letto':
    'never read',
  'vale da {ips}':
    'valid from {ips}',
  'si rinnova prima del {d}':
    'renewed before {d}',
  'il sinottico non legge dal suo link da {q}: se lo schermo dice che il link non vale, «Link nuovo»':
    'the wall has not read through its link for {q}: if the screen says the link is not valid, «New link»',
  'I monitor con CENSO Monitor non hanno bisogno di questi link: il loro lo conia il Control Plane all’accoppiamento e lo rinnova da solo prima della scadenza; vale soltanto dall’indirizzo da cui il monitor batte (dietro un NAT è l’indirizzo pubblico del sito, uguale per tutti i monitor di quella sede) e lo segue se cambia. Nella scheda stanno in un elenco a parte, in sola lettura, con l’indirizzo vero del monitor accanto a quello visto dal Control Plane; per rifarne uno c’è «Link nuovo» sulla riga del monitor. Se il browser del monitor gira ma da otto minuti nessuna lettura passa dal suo link, la riga lo dice: lo schermo è probabilmente su «link non valido», e «Link nuovo» lo rimette a posto; il chiosco riprova comunque da solo ogni minuto.':
    'Monitors running CENSO Monitor do not need these links: theirs is minted by the Control Plane at pairing and renewed by itself before it expires; it is valid only from the address the monitor heartbeats from (behind a NAT it is the site\'s public address, the same for every monitor of that site) and follows it when it changes. In the tab they sit in a list of their own, read-only, with the monitor\'s own address next to the one the Control Plane sees; to mint a new one there is «New link» on the monitor\'s row. If the monitor\'s browser runs but for eight minutes nothing has been read through its link, the row says so: the screen is probably on «link not valid», and «New link» puts it right; the kiosk retries by itself every minute anyway.',
  'Aprire un cruscotto nuovo e vuoto? Le modifiche non salvate di questo vanno perse.':
    'Open a new, empty cockpit? The unsaved changes of this one are lost.',
  'Sezione 1':
    'Section 1',
  'Un cruscotto nuovo e vuoto, da comporre dal catalogo':
    'A new, empty cockpit to compose from the catalogue',
  'Nuovo':
    'New',
  'Eagle → Gestione cruscotti elenca tutti i cruscotti (e le dashboard della home, per chi le vede): di chi sono, sezioni e widget, chi li ha modificati, e per ognuno «Apri» nell’editor, «A parete» (proprio quella riga a tutto schermo) e nel menu «⋯» «Duplica», «Rinomina», «Condividi con il tenant» o «Rendi mia», «Elimina». Dal cruscotto, «Mostra a parete» mette a parete il cruscotto che hai davanti, dalla sezione aperta, anche se non è ancora salvato. «Nuovo», accanto a «Personalizza» (e nella barra in modifica), apre un cruscotto vuoto con una sezione sola e il catalogo già aperto: gli dai un nome e lo componi da zero, senza toccare quello che avevi davanti; esiste solo quando lo salvi. Le misure dei widget sono frazioni della riga: ¼, ⅓, ½, ⅔, ¾, riga intera, più «alto»; si trascinano per disporli. In «Come stanno le cose» c’è anche che cosa è monitorato per classe (169 switch, 760 access point…), ogni pillola del colore dello stato peggiore della classe.':
    'Eagle → Dashboard management lists all cockpits (and the home dashboards, for those who see them): whose they are, sections and widgets, who changed them, and for each “Open” in the editor, “On the wall” (exactly that row full screen) and in the “⋯” menu “Duplicate”, “Rename”, “Share with the tenant” or “Make it mine”, “Delete”. From the cockpit, “Show on the wall” puts on the wall the cockpit in front of you, from the open section, even if it is not saved yet. «New», next to «Customise» (and in the editing bar), opens an empty cockpit with a single section and the catalogue already open: you name it and compose it from scratch, without touching the one you had in front of you; it exists only once you save it. Widget sizes are fractions of the row: ¼, ⅓, ½, ⅔, ¾, whole row, plus “tall”; drag them to arrange. “How things are” also says what is monitored by class (169 switches, 760 access points…), each pill in the colour of the worst status in the class.',
  'Un cruscotto è la pagina di Eagle con i widget che scegli, anche su una matrice libera. «A parete» mostra a tutto schermo proprio quella riga; «Condividi con il tenant» la rende quella che vedono tutti e che i monitor mostrano se non ne hanno uno assegnato. Un cruscotto nuovo si crea dal cruscotto con «Nuovo» (vuoto, da comporre) o con «Personalizza» e «Salva come mio» (una copia di quello aperto); i monitor si assegnano in «Sinottici pubblicati».':
    'A cockpit is the Eagle page with the widgets you choose, also on a free matrix. “On the wall” shows exactly that row full screen; “Share with the tenant” makes it the one everyone sees and the monitors show when none is assigned to them. A new cockpit is created from the cockpit with «New» (empty, to compose) or with «Customise» and «Save as mine» (a copy of the one open); monitors are assigned in «Published walls».',
  'Screenshot (il monitor non sa farlo)':
    'Screenshot (the monitor cannot)',
  'Questo monitor non dichiara la capacità «screenshot»: non ha scrot e la sua versione di CENSO Monitor non legge lo schermo dal server X. «Aggiorna CENSO Monitor» risolve: la versione corrente lo fa senza strumenti.':
    'This monitor does not declare the «screenshot» capability: it has no scrot and its version of CENSO Monitor does not read the screen from the X server. «Update CENSO Monitor» fixes it: the current version does it with no tool.',
  'Da accoppiato il monitor si governa per intero da «Impostazioni…» sulla sua riga: cosa mostra, lo schermo a orario (accensione, spegnimento, giorni della settimana), il riavvio notturno del PC, la ricarica periodica del cruscotto, il fuso orario, la risoluzione dello schermo (scelta fra quelle che il monitor dichiara), la rotazione, lo zoom, la luminosità, il tema chiaro o scuro del sinottico e l’aggiornamento automatico del programma (scarica il binario nuovo dal Control Plane e lo sostituisce solo se l’impronta SHA‑256 corrisponde). Ogni monitor dichiara le sue capacità e le voci che non sa fare restano spente: su CENSO Monitor OS c’è tutto, compreso «Aggiorna il sistema». Nel menu «⋯» ci sono anche Riavvia il browser, Screenshot (l’immagine compare sotto la riga, solo l’ultima; il programma la prende con scrot se c’è, altrimenti legge lo schermo direttamente dal server X, senza pacchetti in più), Log del programma, Aggiorna CENSO Monitor, Spegni il PC e, quando ce ne sono, Annulla i comandi in attesa; la riga riporta rete (indirizzo, Wi‑Fi e segnale), sistema (versione, temperatura, acceso da quanto) e versione del programma. Gli orari seguono l’orologio del PC del monitor.':
    'Once paired, the monitor is governed entirely from “Settings…” on its row: what it shows, the screen schedule (switch-on, switch-off, days of the week), the nightly PC reboot, the periodic dashboard reload, the time zone, the screen resolution (chosen among those the monitor declares), rotation, zoom, brightness, the wall’s light or dark theme and the program’s automatic update (it downloads the new binary from the Control Plane and replaces itself only if the SHA‑256 digest matches). Every monitor declares its capabilities and the fields it cannot handle stay greyed out: on CENSO Monitor OS everything is there, including “Update the system”. The “⋯” menu also has Restart the browser, Screenshot (the image appears under the row, only the last one; the program takes it with scrot when present, otherwise it reads the screen directly from the X server, with no extra package), Program log, Update CENSO Monitor, Shut down the PC and, when there are any, Cancel the pending commands; the row reports network (address, Wi‑Fi and signal), system (version, temperature, uptime) and the program version. Times follow the clock of the monitor’s PC.',
  'In attesa del dispositivo…':
    'Waiting for the device…',
  'Richiedi di nuovo':
    'Ask again',
  'Richiedi i log':
    'Ask for the log',
  'Filtra le righe…':
    'Filter the lines…',
  'Filtra le righe del log':
    'Filter the log lines',
  'Richiesto {q}: il dispositivo risponde al prossimo contatto.':
    'Asked {q}: the device answers at its next contact.',
  'Richiesto {q}, nessuna risposta: il dispositivo è spento, o la sua versione non conosce la richiesta.':
    'Asked {q}, no answer: the device is off, or its version does not know the request.',
  'Ricevuto il {d} · {n} kB{t}{s}':
    'Received on {d} · {n} kB{t}{s}',
  'solo la coda':
    'tail only',
  'Nessun log ricevuto finora.':
    'No log received so far.',
  '{n} righe su {m}':
    '{n} lines of {m}',
  'La coda del registro dell’agente, letta dalla macchina al prossimo keep-alive: si legge qui e si scarica.':
    'The tail of the agent\'s log, read from the machine at its next keep-alive: read here, downloadable.',
  'Log dell’agente «{n}»':
    'Log of agent «{n}»',
  'Su richiesta: l’agente lo consegna al prossimo keep-alive, entro un minuto.':
    'On request: the agent delivers it at its next keep-alive, within a minute.',
  'Log della sonda':
    'Probe log',
  'Su richiesta: la sonda riceve la richiesta con il prossimo heartbeat e consegna la coda del suo registro (il journal dell’unità quando può leggerlo, altrimenti le ultime righe tenute in memoria dal processo). Si legge qui, si filtra e si scarica.':
    'On request: the probe gets the request with its next heartbeat and delivers the tail of its log (the unit\'s journal when it can read it, otherwise the last lines the process keeps in memory). Read it here, filter it, download it.',
  'L’agente è residente: resta acceso e si fa sentire ogni minuto, così «Ultimo contatto» dice subito se è vivo; l’inventario lo fa all’intervallo scelto in console. «Inventario adesso» lo fa raccogliere entro un minuto. «Log» chiede alla macchina la coda del registro dell’agente: arriva al prossimo keep-alive, si legge nella finestra con il filtro per riga e si scarica come file; una istantanea per agente, la richiesta seguente la sostituisce. Gli agenti installati prima diventano residenti da soli con l’aggiornamento automatico: non c’è niente da reinstallare.':
    'The agent is resident: it stays running and checks in every minute, so “Last contact” tells you right away whether it is alive; it takes the inventory at the interval chosen in the console. “Inventory now” makes it collect within a minute. «Log» asks the machine for the tail of the agent\'s log: it arrives at the next keep-alive, you read it in the window with the line filter and download it as a file; one snapshot per agent, the next request replaces it. Agents installed earlier become resident by themselves with automatic updates: there is nothing to reinstall.',
  'Nella scheda della sonda, «Log della sonda» chiede alla sonda la coda del suo registro (il journal dell’unità quando può leggerlo, altrimenti le ultime righe tenute in memoria): arriva con il prossimo heartbeat, si legge con il filtro e si scarica. Se la sonda chiede di aggiornare l’aggiornatore, il comando è nella sua scheda (Manutenzione della sonda); su una Censo Appliance, che non ha shell, si incolla nel menu dell’appliance in «CENSO probe» → «Enroll / reinstall the probe».':
    'In the probe\'s page, «Probe log» asks the probe for the tail of its log (the unit\'s journal when it can read it, otherwise the last lines kept in memory): it arrives with the next heartbeat, you read it with the filter and download it. If the probe asks for its updater to be updated, the command is on its page (Probe maintenance); on a Censo Appliance, which has no shell, it is pasted into the appliance menu under «CENSO probe» → «Enroll / reinstall the probe».',
  '(non privilegiato)':
    '(non-privileged)',
  'Accessi delle ultime 24 ore per ora':
    'Accesses of the last 24 hours by hour',
  'Accessi falliti (24 ore)':
    'Failed accesses (24 hours)',
  'Accessi privilegiati (24 ore)':
    'Privileged accesses (24 hours)',
  'Accesso fallito':
    'Failed access',
  'Account condiviso: postazioni diverse nello stesso momento':
    'Shared account: different workstations at the same time',
  'Account non privilegiato: con gli utenti non privilegiati spenti si conserva senza il nome.':
    'Non-privileged account: with the non-privileged users off it is kept without the name.',
  'Apparati di rete':
    'Network devices',
  'Austria':
    'Austria',
  'Azure':
    'Azure',
  'Base giuridica (obbligatoria)':
    'Legal basis (required)',
  'Belgio':
    'Belgium',
  'CENSO ACCESS':
    'CENSO ACCESS',
  'Carica altri':
    'Load more',
  'Catena di integrità':
    'Integrity chain',
  'Cerca nel registro':
    'Search the register',
  'Chi ha accesso a quest’area':
    'Who has access to this area',
  'Chi ha consultato il registro':
    'Who consulted the register',
  'Chi riceve i rilevamenti per posta (un indirizzo per riga)':
    'Who gets the detections by mail (one address per line)',
  'Chi è amministratore di che cosa, da quando, e le variazioni.':
    'Who is administrator of what, since when, and the changes.',
  'Ciò che le correlazioni hanno trovato: fuori orario, forza bruta, movimento laterale, nuovi privilegi.':
    'What the correlations found: off hours, brute force, lateral movement, new privileges.',
  'Collegato':
    'Linked',
  'Come':
    'How',
  'Con l’account':
    'With the account',
  'Conservazione':
    'Retention',
  'Credenziali esplicite':
    'Explicit credentials',
  'Cronologia (30 giorni)':
    'Timeline (30 days)',
  'Da dove arrivano gli eventi e come stanno le fonti.':
    'Where the events come from and how the sources are doing.',
  'Dichiarata da {chi} il {quando}: «{base}»':
    'Declared by {chi} on {quando}: «{base}»',
  'Domain controller':
    'Domain controller',
  'Entra ID':
    'Entra ID',
  'Eventi consegnati':
    'Events delivered',
  'Eventi conservati':
    'Events kept',
  'Eventi nel registro':
    'Events in the register',
  'Festività del Paese':
    'Country\'s public holidays',
  'Fonti del registro':
    'Sources of the register',
  'Fonti silenziose':
    'Silent sources',
  'Forza bruta: in minuti':
    'Brute force: within minutes',
  'Forza bruta: tentativi falliti':
    'Brute force: failed attempts',
  'Francia':
    'France',
  'Fuori da queste fasce, nelle festività del Paese e nei giorni aggiunti, un accesso è «fuori orario». Le fasce si scrivono come 08:00-13:00, separate da virgola; un giorno vuoto è tutto fuori orario. I reperibili si segnano sulla scheda della persona.':
    'Outside these bands, on the country\'s public holidays and on the added days, an access is «off hours». Bands are written as 08:00-13:00, separated by commas; an empty day is all off hours. People on call are marked on the person\'s page.',
  'Germania':
    'Germany',
  'Giorni aggiunti (AAAA-MM-GG, uno per riga; con «+» davanti un giorno lavorativo, es. +2026-12-24)':
    'Added days (YYYY-MM-DD, one per line; with «+» in front a working day, e.g. +2026-12-24)',
  'Giorni di conservazione (minimo {n})':
    'Days of retention (minimum {n})',
  'Gli eventi arrivano dalle fonti accese (agenti, domain controller, sonde, Entra ID). Senza la dichiarazione per gli utenti non privilegiati si registrano solo gli accessi privilegiati e i tentativi falliti.':
    'Events come from the sources that are on (agents, domain controllers, probes, Entra ID). Without the declaration for non-privileged users only privileged accesses and failed attempts are recorded.',
  'Gli eventi più vecchi escono ogni notte, lasciando un’ancora nella catena di integrità. Sotto i sei mesi non si scende: è il minimo del Garante per gli amministratori di sistema.':
    'Older events leave every night, leaving an anchor in the integrity chain. It never goes below six months: the minimum of the Italian Garante for system administrators.',
  'Governa CENSO ACCESS: fonti, orari di lavoro, conservazione, rilevamenti e report del registro degli accessi; non vede il resto della CMDB.':
    'Governs CENSO ACCESS: sources, working hours, retention, detections and reports of the register of the accesses; does not see the rest of the CMDB.',
  'Gruppi aggiunti':
    'Added groups',
  'Gruppi e ruoli privilegiati':
    'Privileged groups and roles',
  'I report per il Garante, NIS2, DORA, PCI DSS, ISO 27001.':
    'The reports for the Garante, NIS2, DORA, PCI DSS, ISO 27001.',
  'I report per quadro normativo arrivano con una delle prossime consegne di CENSO ACCESS.':
    'The reports per regulatory framework come with one of the next deliveries of CENSO ACCESS.',
  'Il registro':
    'The register',
  'Il registro degli accessi di amministratori e utenti, legato all’inventario: inalterabile, conservato, consultabile solo da chi ne ha il ruolo.':
    'The register of the accesses of administrators and users, tied to the inventory: tamper-proof, retained, readable only by those who hold the role.',
  'Il registro degli accessi di amministratori e utenti: inalterabile, legato all’inventario, consultabile solo da chi ne ha il ruolo.':
    'The register of the accesses of administrators and users: tamper-proof, tied to the inventory, readable only by those who hold the role.',
  'Il registro: chi è entrato in quale sistema, quando, da dove, con quali privilegi.':
    'The register: who entered which system, when, from where, with which privileges.',
  'Impostazioni del registro':
    'Register settings',
  'In preparazione.':
    'Being prepared.',
  'Integrità':
    'Integrity',
  'Irlanda':
    'Ireland',
  'Italia':
    'Italy',
  'Le fonti si accendono con gli agenti aggiornati (Windows, Linux, domain controller), con la funzione syslog di una sonda per apparati di rete e Wi‑Fi, e con una credenziale Azure per Entra ID.':
    'Sources switch on with updated agents (Windows, Linux, domain controllers), with the syslog function of a probe for network devices and Wi‑Fi, and with an Azure credential for Entra ID.',
  'Le persone compaiono con i primi eventi delle fonti e con i privilegi letti dall’inventario.':
    'People appear with the first events of the sources and with the privileges read from the inventory.',
  'Le persone dietro gli account: una riga per persona, unita fra dominio, account locali, Linux, Entra ID e apparati.':
    'The people behind the accounts: one row per person, joined across domain, local accounts, Linux, Entra ID and devices.',
  'L’integrità della catena, chi ha accesso all’area e chi ha consultato il registro.':
    'The integrity of the chain, who has access to the area and who consulted the register.',
  'Modifica della configurazione':
    'Configuration change',
  'Modifica della politica di audit':
    'Audit policy change',
  'Modifica di un account':
    'Account change',
  'Modifica di un gruppo':
    'Group change',
  'Modifica di un ruolo':
    'Role change',
  'Modifiche di utenti e ruoli (dal registro di audit)':
    'Changes of users and roles (from the audit log)',
  'Movimento laterale: in minuti':
    'Lateral movement: within minutes',
  'Movimento laterale: sistemi diversi':
    'Lateral movement: different systems',
  'N.':
    'No.',
  'Nessun evento con questi filtri.':
    'No event with these filters.',
  'Nessun evento nelle ultime 24 ore.':
    'No event in the last 24 hours.',
  'Nessuna fonte ha ancora consegnato eventi.':
    'No source has delivered events yet.',
  'Nessuna persona nel registro, finora.':
    'Nobody in the register, so far.',
  'Ogni agente, domain controller, sonda e credenziale di Entra ID che consegna eventi compare qui, con l’ultima consegna e gli eventi portati. Una fonte silenziosa da più di due ore è segnalata nella panoramica: un registro con un buco non dimostra niente per quel periodo.':
    'Every agent, domain controller, probe and Entra ID credential that delivers events appears here, with the last delivery and the events it brought. A source silent for more than two hours is flagged in the overview: a register with a gap proves nothing for that period.',
  'Orario di lavoro':
    'Working hours',
  'Orario di lavoro, conservazione, utenti non privilegiati, gruppi privilegiati, soglie dei rilevamenti.':
    'Working hours, retention, non-privileged users, privileged groups, detection thresholds.',
  'Paesi Bassi':
    'Netherlands',
  'Password spray: account diversi':
    'Password spray: different accounts',
  'Password spray: in minuti':
    'Password spray: within minutes',
  'Persone':
    'People',
  'Persone con privilegi':
    'People with privileges',
  'Persone e account':
    'People and accounts',
  'Portogallo':
    'Portugal',
  'Primo accesso a un sistema: giorni di storia':
    'First access to a system: days of history',
  'Privilegi':
    'Privileges',
  'Privilegiati fuori orario (24 ore)':
    'Privileged off hours (24 hours)',
  'Privilegiato dormiente: giorni senza accessi':
    'Dormant privileged account: days without accesses',
  'Problema':
    'Problem',
  'Registra anche gli accessi degli utenti non privilegiati':
    'Also record the accesses of non-privileged users',
  'Registro cancellato':
    'Log cleared',
  'Registro degli accessi':
    'Register of the accesses',
  'Registro dell’area':
    'Area register',
  'Regno Unito':
    'United Kingdom',
  'Reperibile':
    'On call',
  'Reperibile: i suoi accessi fuori orario non sono un rilevamento':
    'On call: their off-hours accesses are not a detection',
  'Responsabile degli accessi':
    'Access officer',
  'Revisore degli accessi':
    'Access reviewer',
  'Rilevamenti':
    'Detections',
  'Rilevamenti aperti':
    'Open detections',
  'SID di Windows':
    'Windows SID',
  'Salvato: la modifica è nel registro.':
    'Saved: the change is in the register.',
  'Separare «{k}» in una nuova persona? Gli eventi di quell’account la seguiranno.':
    'Split «{k}» into a new person? That account\'s events will follow it.',
  'Sistemi raggiunti (30 giorni)':
    'Systems reached (30 days)',
  'Sola lettura: le impostazioni le cambia il Responsabile degli accessi.':
    'Read-only: the settings are changed by the Access officer.',
  'Spagna':
    'Spain',
  'Spenti, il registro tiene gli accessi degli account privilegiati, le modifiche amministrative e i tentativi falliti di tutti; quelli degli account non privilegiati senza il nome. Accenderli significa registrare l’attività dei dipendenti: serve una base giuridica (in Italia l’accordo sindacale o l’autorizzazione dell’Ispettorato, art. 4 dello Statuto dei lavoratori), che resta scritta qui con chi l’ha dichiarata.':
    'When off, the register keeps the accesses of privileged accounts, administrative changes and everyone\'s failed attempts; those of non-privileged accounts without the name. Switching them on means recording employees\' activity: a legal basis is needed (in Italy the union agreement or the Labour Inspectorate\'s authorisation, art. 4 of the Workers\' Statute), which stays written here with who declared it.',
  'Stati Uniti':
    'United States',
  'Su che cosa':
    'On what',
  'Successo dopo fallimenti: in minuti':
    'Success after failures: within minutes',
  'Successo dopo fallimenti: quanti fallimenti':
    'Success after failures: how many failures',
  'Svizzera':
    'Switzerland',
  'Tipo di chiave':
    'Key type',
  'Tutti i suoi eventi':
    'All their events',
  'UPN':
    'UPN',
  'Ultima modifica di {chi} il {quando}':
    'Last changed by {chi} on {quando}',
  'Ultima verifica':
    'Last verification',
  'Ultimo accesso privilegiato':
    'Last privileged access',
  'Ultimo evento':
    'Last event',
  'Una riga per persona: gli account con cui compare nelle fonti (dominio, account locali, Linux, Entra ID, apparati) sono uniti dalle loro chiavi — SID, UPN, nome di dominio — e, per i nomi semplici, dal nome stesso. Un’unione sbagliata si separa dalla scheda della persona; due righe che sono la stessa persona si uniscono da lì.':
    'One row per person: the accounts it appears with in the sources (domain, local accounts, Linux, Entra ID, devices) are joined by their keys — SID, UPN, domain name — and, for plain names, by the name itself. A wrong join is split from the person\'s page; two rows that are the same person are merged from there.',
  'Unire questa persona a quella scelta? Account, privilegi e rilevamenti passano a lei.':
    'Merge this person into the chosen one? Accounts, privileges and detections move to it.',
  'Unisci a un’altra persona':
    'Merge into another person',
  'Uso di privilegi':
    'Use of privileges',
  'Utenti non privilegiati':
    'Non-privileged users',
  'Variazione di un privilegio':
    'Privilege change',
  'Verifica adesso':
    'Verify now',
  'Verifico…':
    'Verifying…',
  'Wi‑Fi':
    'Wi‑Fi',
  'account condiviso':
    'shared account',
  'account di dominio':
    'domain account',
  'account di servizio':
    'service account',
  'account locale':
    'local account',
  'account predefinito':
    'built-in account',
  'account, sistema, indirizzo…':
    'account, system, address…',
  'attività pianificata':
    'scheduled task',
  'conservazione':
    'retention',
  'conservazione {n} giorni':
    'retention {n} days',
  'console':
    'console',
  'consulta':
    'reads',
  'consultazioni':
    'consultations',
  'credenziali in cache':
    'cached credentials',
  'da Entra ID':
    'from Entra ID',
  'dagli eventi':
    'from the events',
  'dall’inventario':
    'from the inventory',
  'desktop remoto':
    'remote desktop',
  'di rete':
    'network',
  'domenica':
    'Sunday',
  'es. Accordo sindacale del 12/09/2026, art. 4 L. 300/1970':
    'e.g. Union agreement of 12/09/2026, art. 4 L. 300/1970',
  'es. GG-Server-Admin':
    'e.g. GG-Server-Admin',
  'esportazione':
    'export',
  'fuori orario':
    'off hours',
  'giovedì':
    'Thursday',
  'governa':
    'governs',
  'idoneo':
    'eligible',
  'impostazioni':
    'settings',
  'in orario e fuori orario':
    'in and off hours',
  'integra ({n} eventi)':
    'intact ({n} events)',
  'integra · {n} eventi controllati':
    'intact · {n} events checked',
  'interattivo':
    'interactive',
  'lettura':
    'read',
  'lunedì':
    'Monday',
  'martedì':
    'Tuesday',
  'mercoledì':
    'Wednesday',
  'non registrati: solo i tentativi falliti, senza il nome':
    'not recorded: only failed attempts, without the name',
  'oggetto di Entra ID':
    'Entra ID object',
  'persona':
    'person',
  'persone':
    'people',
  'privilegi':
    'privileges',
  'privilegiata':
    'privileged',
  'privilegiati e non':
    'privileged and not',
  'privilegiato':
    'privileged',
  'registrati, sulla base giuridica dichiarata':
    'recorded, on the declared legal basis',
  'reperibile':
    'on call',
  'ricerca':
    'search',
  'rilevamenti':
    'detections',
  'riusciti e falliti':
    'successful and failed',
  'rotta all’evento {seq}: {motivo}':
    'broken at event {seq}: {motivo}',
  'sabato':
    'Saturday',
  'sblocco':
    'unlock',
  'senza MFA':
    'without MFA',
  'separa':
    'split',
  'solo chi ha privilegi':
    'only who has privileges',
  'solo falliti':
    'failed only',
  'solo fuori orario':
    'off hours only',
  'solo in orario':
    'in hours only',
  'solo non privilegiati':
    'non-privileged only',
  'solo privilegiati':
    'privileged only',
  'solo riusciti':
    'successful only',
  'sonda (syslog)':
    'probe (syslog)',
  'su {n} persone nel registro':
    'out of {n} people in the register',
  'sudo':
    'sudo',
  'togli il filtro su persona o sistema':
    'remove the filter on person or system',
  'tutte le fonti':
    'all sources',
  'tutto il registro':
    'the whole register',
  'ultimi 90 giorni':
    'last 90 days',
  'ultimo evento {q}':
    'last event {q}',
  'venerdì':
    'Friday',
  'verifica':
    'verify',
  '{n} eventi rimossi fino al {seq}':
    '{n} events removed up to {seq}',
  '{n} privilegi':
    '{n} privileges',
  '{n} riusciti':
    '{n} successful',
  'Consulta in sola lettura il registro degli accessi, ne verifica l’integrità e produce i report (DPO, auditor); non vede il resto della CMDB.':
    'Reads the register of the accesses, verifies its integrity and produces the reports (DPO, auditor); does not see the rest of the CMDB.',
  'Accessi: il registro, dal più recente, con i filtri per periodo, privilegiati, fuori orario, esito, fonte e testo; «Carica altri» chiede la pagina dopo, «Esporta CSV» scarica gli eventi filtrati con l’impronta di ciascuno, per verificarli contro il registro.':
    'Accesses: the register, newest first, with filters for period, privileged, off hours, outcome, source and text; «Load more» asks for the next page, «Export CSV» downloads the filtered events with each one\'s fingerprint, to check them against the register.',
  'Chi lo apre':
    'Who opens it',
  'Fonti: agenti, domain controller, sonde e credenziali di Entra ID che consegnano eventi, con l’ultima consegna; una fonte silenziosa è un buco nel registro e la panoramica lo dice.':
    'Sources: agents, domain controllers, probes and Entra ID credentials that deliver events, with the last delivery; a silent source is a gap in the register and the overview says so.',
  'Il database rifiuta ogni modifica e ogni cancellazione degli eventi, tranne la conservazione e l’eliminazione del tenant. Ogni evento porta l’impronta del precedente: «Verifica adesso» ricalcola la catena e dice da quale evento, se mai, si rompe.':
    'The database refuses every change and every deletion of the events, except retention and the tenant\'s deletion. Every event carries the fingerprint of the previous one: «Verify now» recomputes the chain and says from which event, if ever, it breaks.',
  'In «Registro dell’area» si vede chi ha un ruolo che apre l’area, le modifiche di utenti e ruoli dal registro di audit e chi ha consultato il registro: ogni lettura, ricerca, esportazione e verifica resta scritta.':
    'In «Area register» you see who has a role that opens the area, the changes of users and roles from the audit log and who consulted the register: every read, search, export and verification stays written.',
  'Inalterabile e conservato':
    'Tamper-proof and retained',
  'La conservazione si sceglie in «Impostazioni» e non scende sotto i sei mesi, il minimo del Garante per gli amministratori di sistema. Ogni notte escono gli eventi più vecchi, lasciando un’ancora da cui la verifica riparte.':
    'Retention is chosen in «Settings» and never goes below six months, the Italian Garante\'s minimum for system administrators. Every night the oldest events leave, leaving an anchor the verification starts again from.',
  'Lo aprono i ruoli «Responsabile degli accessi» (fonti, orari, conservazione, rilevamenti, report) e «Revisore degli accessi» (sola lettura, verifica e report: il DPO, l’auditor), o un ruolo personalizzato che nomina l’area. Chi ha uno di questi ruoli e non vede la CMDB entra direttamente nell’area.':
    'The roles «Access officer» (sources, hours, retention, detections, reports) and «Access reviewer» (read-only, verification and reports: the DPO, the auditor) open it, or a custom role that names the area. Whoever has one of these roles and does not see the CMDB lands straight in the area.',
  'Ogni cambio delle impostazioni e delle persone (unioni, separazioni, reperibilità) è a sua volta un evento del registro, con chi l’ha fatto.':
    'Every change of the settings and of the people (merges, splits, on call) is itself an event of the register, with who made it.',
  'Panoramica: i numeri delle ultime 24 ore (accessi privilegiati, privilegiati fuori orario, falliti), i rilevamenti aperti, le persone con privilegi, le fonti silenziose; ogni numero apre l’elenco filtrato. Sotto, il grafico per ora e lo stato della catena.':
    'Overview: the numbers of the last 24 hours (privileged accesses, privileged off hours, failed), the open detections, the people with privileges, the silent sources; every number opens the filtered list. Below, the chart by hour and the state of the chain.',
  'Per difetto si registrano gli accessi degli account privilegiati, le modifiche amministrative e i tentativi falliti; quelli degli account non privilegiati senza il nome, e senza creare una scheda per la persona. Registrare anche gli utenti significa registrare l’attività dei dipendenti: si accende solo scrivendo la base giuridica (in Italia l’accordo sindacale o l’autorizzazione dell’Ispettorato), che resta con chi l’ha dichiarata.':
    'By default the accesses of privileged accounts, the administrative changes and the failed attempts are recorded; those of non-privileged accounts without the name, and without creating a record for the person. Recording the users too means recording the employees\' activity: it is switched on only by writing the legal basis (in Italy the union agreement or the Labour Inspectorate\'s authorisation), which stays with who declared it.',
  'Persone: una riga per persona, con gli account uniti per SID, UPN, nome di dominio o, per i nomi semplici, per nome. La scheda mostra come sono stati collegati, i sistemi raggiunti nei 30 giorni con gli accessi fuori orario e falliti, privilegi, rilevamenti e cronologia; da lì si separa un account unito per errore o si uniscono due righe che sono la stessa persona.':
    'People: one row per person, with the accounts joined by SID, UPN, domain name or, for plain names, by name. The page shows how they were linked, the systems reached in 30 days with the off-hours and failed accesses, privileges, detections and timeline; from there an account joined by mistake is split or two rows that are the same person are merged.',
  'Utenti non privilegiati e fuori orario':
    'Non-privileged users and off hours',
  'il registro degli accessi di amministratori e utenti, con la licenza CENSO ACCESS. Chi è entrato in quale sistema, quando, da dove, con quali privilegi: un evento per accesso, legato al CI del sistema e alla persona, che è una sola anche quando compare con nomi diversi nel dominio, negli account locali, su Linux, in Entra ID e sugli apparati.':
    'the register of the accesses of administrators and users, with the CENSO ACCESS licence. Who entered which system, when, from where, with which privileges: one event per access, tied to the system\'s CI and to the person, who is one even when they appear with different names in the domain, in local accounts, on Linux, in Entra ID and on devices.',
  '«Fuori orario» vuol dire fuori dalle fasce di ogni giorno della settimana, nelle festività nazionali del Paese scelto e nei giorni aggiunti a mano (con «+» davanti, un giorno che si lavora comunque). Una persona reperibile si segna sulla sua scheda.':
    '«Off hours» means outside the bands of each weekday, on the national public holidays of the chosen country and on the days added by hand (with «+» in front, a day that is worked anyway). A person on call is marked on their page.',
  'Account o gruppo':
    'Account or group',
  'Amministratori di sistema':
    'System administrators',
  'Fino a':
    'Until',
  'Gli amministratori locali arrivano dall’inventario dei sistemi (SSH, WinRM, agente) entro un’ora; i gruppi di dominio dai domain controller, i ruoli da Entra ID.':
    'Local administrators come from the systems\' inventory (SSH, WinRM, agent) within an hour; domain groups from the domain controllers, roles from Entra ID.',
  'Nessun amministratore, finora.':
    'No administrator, so far.',
  'Nessun privilegio letto finora.':
    'No privilege read so far.',
  'Persona':
    'Person',
  'Privilegi per sistema':
    'Privileges by system',
  'Sistema o ambito':
    'System or scope',
  'Sistemi':
    'Systems',
  'Un gruppo: i suoi membri arrivano dai domain controller.':
    'A group: its members come from the domain controllers.',
  'Una riga per persona con almeno un privilegio attivo: su quanti sistemi, con quali tipi di privilegio, da quando, l’ultimo accesso privilegiato. «Dormiente» vuol dire nessun accesso privilegiato oltre la soglia delle impostazioni: un privilegio che nessuno usa è un privilegio da togliere.':
    'One row per person with at least one active privilege: on how many systems, with which kinds of privilege, since when, the last privileged access. «Dormant» means no privileged access beyond the settings\' threshold: a privilege nobody uses is a privilege to take away.',
  'amministratore di apparato':
    'device administrator',
  'amministratore locale':
    'local administrator',
  'anche quelli cessati':
    'ended ones too',
  'domain controller':
    'domain controller',
  'dormiente':
    'dormant',
  'gruppo':
    'group',
  'gruppo privilegiato di dominio':
    'privileged domain group',
  'inventario':
    'inventory',
  'ruolo di Entra ID':
    'Entra ID role',
  'solo idoneo':
    'eligible only',
  'syslog':
    'syslog',
  'Privilegi: l’elenco degli amministratori di sistema chiesto dal Garante, una riga per persona con i sistemi che amministra, da quando e l’ultimo accesso privilegiato («dormiente» se non lo usa da troppo), e sotto i privilegi sistema per sistema. Gli amministratori locali arrivano dall’inventario ogni ora; la prima lettura di un sistema fa da base, poi ogni amministratore aggiunto o tolto è un evento del registro.':
    'Privileges: the list of system administrators the Garante asks for, one row per person with the systems they administer, since when and the last privileged access («dormant» if unused for too long), and below the privileges system by system. Local administrators come from the inventory every hour; the first reading of a system is the baseline, then every administrator added or removed is an event of the register.',
  'journald':
    'journald',
  'registro Security':
    'Security log',
  'registro Security (domain controller)':
    'Security log (domain controller)',
  'in coda sull’agente: {n}':
    'queued on the agent: {n}',
  'scartati per coda piena: {n}':
    'dropped, queue full: {n}',
  'attiva · sospendi':
    'active · suspend',
  'sospesa · riprendi':
    'suspended · resume',
  'Gli agenti leggono gli accessi delle loro macchine (Windows, Linux, domain controller)':
    'Agents read the accesses of their machines (Windows, Linux, domain controllers)',
  'Con gli agenti aggiornati e questa voce accesa, ogni agente legge il registro Security (Windows) o journald e auth.log (Linux) e consegna gli accessi col suo keep-alive; un domain controller anche i tentativi Kerberos e NTLM falliti e i membri dei gruppi privilegiati di dominio. Una singola fonte si sospende dalla vista Fonti.':
    'With updated agents and this option on, every agent reads the Security log (Windows) or journald and auth.log (Linux) and delivers the accesses with its keep-alive; a domain controller also the failed Kerberos and NTLM attempts and the members of the privileged domain groups. A single source is suspended from the Sources view.',
  'Gli accessi dagli agenti':
    'Accesses from the agents',
  'Con la licenza e gli agenti aggiornati, ogni agente residente legge gli accessi della sua macchina e li consegna col keep-alive, sullo stesso canale sigillato dell’inventario (anche attraverso una sonda). Si spegne per tutti in «Impostazioni», per un solo agente sospendendo la sua fonte in «Fonti».':
    'With the licence and updated agents, every resident agent reads the accesses of its machine and delivers them with the keep-alive, on the same sealed channel as the inventory (also through a probe). It is switched off for everyone in «Settings», for a single agent by suspending its source in «Sources».',
  'Windows: dal registro Security gli accessi interattivi, da desktop remoto e quelli con privilegi speciali, l’uso di credenziali esplicite, gli accessi falliti, la gestione di account e gruppi, il registro cancellato e la politica di audit cambiata. Gli accessi di servizio, quelli delle macchine e, con gli utenti spenti, gli accessi di rete non privilegiati restano sulla macchina.':
    'Windows: from the Security log the interactive and remote desktop logons and those with special privileges, the use of explicit credentials, failed logons, account and group management, the cleared log and the changed audit policy. Service logons, machine accounts and, with the users off, non-privileged network logons stay on the machine.',
  'Domain controller: in più i tentativi Kerberos e NTLM falliti, i ticket degli account privilegiati noti e, ogni ora, i membri dei gruppi privilegiati di dominio (Domain Admins, Enterprise Admins, Schema Admins, Administrators e gli operatori), riconosciuti dal SID e quindi in qualunque lingua. Ogni membro aggiunto o tolto è un evento del registro.':
    'Domain controller: in addition the failed Kerberos and NTLM attempts, the tickets of the known privileged accounts and, every hour, the members of the privileged domain groups (Domain Admins, Enterprise Admins, Schema Admins, Administrators and the operators), recognised by their SID and therefore in any language. Every member added or removed is an event of the register.',
  'Linux: da journald, o da auth.log e secure dove journald non c’è, gli accessi SSH riusciti e falliti, sudo, su e gli accessi da console. Di sudo si registra l’utente di destinazione, mai il comando.':
    'Linux: from journald, or from auth.log and secure where journald is missing, successful and failed SSH logons, sudo, su and console logons. Of sudo the target user is recorded, never the command.',
  'Un evento esce dalla coda dell’agente solo quando il Control Plane lo ha scritto nel registro: con il Control Plane irraggiungibile o un errore, gli eventi aspettano sull’agente e ripartono dal punto in cui erano anche dopo un riavvio.':
    'An event leaves the agent’s queue only once the Control Plane has written it in the register: with the Control Plane unreachable or an error, the events wait on the agent and resume from where they were, even after a restart.',
  'Gli amministratori del tenant e della piattaforma, con lettura e scrittura. Un ruolo personalizzato che vede «tutto», un operatore o un lettore senza ruolo personalizzato non la aprono: l’area va concessa per nome.':
    'The tenant’s and the platform’s administrators, reading and writing. A custom role that sees «everything», an operator or a reader without a custom role do not open it: the area must be granted by name.',
  'Amministratore del tenant':
    'Tenant administrator',
  'Sigilli':
    'Seals',
  'Ogni ora la testa della catena è firmata con la chiave del tenant, che non sta nel database: chi avesse solo il database e riscrivesse eventi e impronte non potrebbe rifare la firma, e «Verifica adesso» lo direbbe. Una volta al giorno, se è impostata una TSA, il sigillo riceve la marca temporale RFC 3161: una data certa data da terzi.':
    'Every hour the head of the chain is signed with the tenant’s key, which is not in the database: whoever had only the database and rewrote events and hashes could not redo the signature, and «Verify now» would say so. Once a day, when a TSA is set, the seal gets an RFC 3161 time stamp: a certain date given by a third party.',
  'Chiave del tenant':
    'Tenant key',
  'versione {n}':
    'version {n}',
  'Chiave pubblica':
    'Public key',
  'Ultimo sigillo':
    'Last seal',
  'fino all’evento {seq}':
    'up to event {seq}',
  'Marca temporale':
    'Time stamp',
  'nessuna TSA impostata (Impostazioni)':
    'no TSA set (Settings)',
  'alla prossima occasione':
    'at the next opportunity',
  'Sigilla adesso':
    'Seal now',
  'Esporta i sigilli':
    'Export the seals',
  'I sigilli già fatti restano verificabili con la loro versione; da adesso si firma con la nuova.':
    'The seals already made stay verifiable with their version; from now on the new one signs.',
  'sigilli':
    'seals',
  'Fino all’evento':
    'Up to event',
  'non ottenuta':
    'not obtained',
  'Copia sulla sonda d’archivio':
    'Copy on the archive probe',
  'Ogni giorno la parte nuova del registro va alla sonda d’archivio del tenant come segmento firmato: la sonda controlla firma e impronta, lo conserva in sola lettura (immutabile dove il file system lo permette) fino alla fine della conservazione, e ogni giorno lo rilegge. Un registro cancellato o riscritto nel Control Plane resta lì, e la rilettura lo dice.':
    'Every day the new part of the register goes to the tenant’s archive probe as a signed segment: the probe checks signature and hash, keeps it read-only (immutable where the file system allows it) until the end of retention, and re-reads it every day. A register deleted or rewritten in the Control Plane is still there, and the re-reading says so.',
  'Nessuna sonda d’archivio.':
    'No archive probe.',
  'Si sceglie in Impostazioni → Copia d’archivio, fra le sonde del tenant.':
    'Chosen in Settings → Archive copy, among the tenant’s probes.',
  'Segmenti conservati':
    'Segments kept',
  'Ultima rilettura':
    'Last re-reading',
  '{n} segmenti con problemi':
    '{n} segments with problems',
  '{n} segmenti non presi dalla sonda prima della fine della conservazione':
    '{n} segments not taken by the probe before the end of retention',
  'segmenti':
    'segments',
  'Tagliato':
    'Cut',
  'Sulla sonda':
    'On the probe',
  'perso':
    'lost',
  'Rilettura':
    'Re-reading',
  'integro':
    'intact',
  'problema':
    'problem',
  'Facoltativa. Una volta al giorno il sigillo del registro riceve una marca temporale RFC 3161 dall’autorità indicata qui: una data certa data da terzi. Per l’opponibilità in Italia serve una TSA qualificata eIDAS (Aruba, InfoCert, Namirial…), con le sue credenziali; per provare basta un servizio pubblico come https://freetsa.org/tsr. La chiamata parte dal Control Plane.':
    'Optional. Once a day the register’s seal gets an RFC 3161 time stamp from the authority given here: a certain date given by a third party. For legal standing in Italy a qualified eIDAS TSA is needed (Aruba, InfoCert, Namirial…), with its credentials; to try it a public service such as https://freetsa.org/tsr is enough. The call leaves from the Control Plane.',
  'Indirizzo della TSA':
    'TSA address',
  'Utente (se la TSA lo chiede)':
    'User (if the TSA asks for it)',
  'Password (impostata: scrivi per cambiarla)':
    'Password (set: type to change it)',
  'Prova la TSA (impostazioni salvate)':
    'Try the TSA (saved settings)',
  'Marca ottenuta: {quando}, numero {serie}, firmata da {chi}{verificata}':
    'Time stamp obtained: {quando}, number {serie}, signed by {chi}{verificata}',
  ' (firma da verificare con openssl)':
    ' (signature to verify with openssl)',
  'Copia d’archivio':
    'Archive copy',
  'Una sonda del tenant tiene una copia del registro: ogni giorno riceve la parte nuova firmata con la chiave del tenant, la controlla, la conserva in sola lettura (immutabile dove il file system lo permette) fino alla fine della conservazione e la rilegge. È un testimone che il database del Control Plane non controlla. Meglio una sonda dedicata, in un luogo diverso dal Control Plane.':
    'A probe of the tenant keeps a copy of the register: every day it receives the new part signed with the tenant’s key, checks it, keeps it read-only (immutable where the file system allows it) until the end of retention and re-reads it. It is a witness the Control Plane’s database does not control. A dedicated probe is better, in a place other than the Control Plane.',
  'Sonda d’archivio':
    'Archive probe',
  '{n} sigilli validi, {m} con marca temporale':
    '{n} valid seals, {m} time-stamped',
  'Ogni evento porta l’impronta SHA-256 del precedente: togliere, cambiare o inserire un evento rompe la catena da quel punto in poi. Il database rifiuta modifiche, svuotamenti e cancellazioni prima della fine della conservazione. La verifica ricalcola tutta la catena, controlla i sigilli e chiede alla sonda d’archivio di rileggere la sua copia.':
    'Every event carries the SHA-256 hash of the previous one: removing, changing or inserting an event breaks the chain from that point on. The database refuses changes, truncations and deletions before the end of retention. The verification recomputes the whole chain, checks the seals and asks the archive probe to re-read its copy.',
  'Il registro lo aprono gli amministratori del tenant e della piattaforma e i ruoli che lo nominano; un ruolo che vede «tutto» no. Chi gestisce gli utenti può assegnarsi uno di questi ruoli: per questo l’elenco e le modifiche dei ruoli sono qui, sotto gli occhi del revisore.':
    'The register is opened by the tenant’s and the platform’s administrators and by the roles that name it; a role that sees «everything» does not. Whoever manages users can give themselves one of these roles: that is why the list and the role changes are here, in front of the reviewer.',
  'Sigilli: ogni ora la testa della catena è firmata con la chiave del tenant, che non sta nel database. Chi riscrivesse eventi e impronte direttamente nel database non potrebbe rifare la firma, e la verifica lo direbbe; così anche per un sigillo falso o mancante. In «Registro dell’area» ci sono l’impronta della chiave, «Sigilla adesso», «Ruota la chiave» ed «Esporta i sigilli» per verificarli fuori da CENSO.':
    'Seals: every hour the head of the chain is signed with the tenant’s key, which is not in the database. Whoever rewrote events and hashes directly in the database could not redo the signature, and the verification would say so; the same for a forged or missing seal. «Register of the area» shows the key’s fingerprint, «Seal now», «Rotate the key» and «Export the seals» to verify them outside CENSO.',
  'Marca temporale (facoltativa): con l’indirizzo di una TSA in «Impostazioni», una volta al giorno il sigillo riceve una marca RFC 3161, una data certa data da terzi. Per l’opponibilità in Italia va scelta una TSA qualificata eIDAS; «Prova la TSA» verifica le impostazioni.':
    'Time stamp (optional): with a TSA’s address in «Settings», once a day the seal gets an RFC 3161 time stamp, a certain date given by a third party. For legal standing in Italy choose a qualified eIDAS TSA; «Try the TSA» checks the settings.',
  'Copia d’archivio (facoltativa): una sonda del tenant, meglio dedicata e lontana dal Control Plane, riceve ogni giorno la parte nuova del registro firmata, la conserva in sola lettura e immutabile fino alla fine della conservazione e la rilegge ogni giorno e a ogni «Verifica adesso». Un registro cancellato nel Control Plane resta sulla sonda.':
    'Archive copy (optional): a probe of the tenant, preferably dedicated and away from the Control Plane, receives every day the new part of the register signed, keeps it read-only and immutable until the end of retention and re-reads it every day and at every «Verify now». A register deleted in the Control Plane stays on the probe.',
  'Nell’installazione, con la password del ruolo applicativo del database (CENSO_DB_APP_PASSWORD) il Control Plane non è più proprietario delle tabelle del registro: nemmeno chi lo controllasse potrebbe modificarle o spegnerne la protezione.':
    'In the installation, with the password of the database’s application role (CENSO_DB_APP_PASSWORD) the Control Plane no longer owns the register’s tables: not even whoever controlled it could change them or switch their protection off.',
  'CENSO li riconosce da solo: i gruppi privilegiati per difetto (riconosciuti dal SID, in ogni lingua), i gruppi di dominio che trova fra gli amministratori locali delle macchine, i gruppi annidati in un gruppo privilegiato e, su Linux, i gruppi che sudoers abilita a tutto. I loro membri li legge l’agente del domain controller. Qui sotto si aggiungono solo quelli che CENSO non può vedere: uno per riga, senza il dominio.':
    'CENSO recognises them by itself: the default privileged groups (recognised by SID, in any language), the domain groups it finds among the machines’ local administrators, the groups nested in a privileged group and, on Linux, the groups sudoers allows everything. Their members are read by the domain controller’s agent. Below, add only those CENSO cannot see: one per line, without the domain.',
  'dentro {gruppo}':
    'inside {gruppo}',
  'Membri':
    'Members',
  'Nessun domain controller l’ha ancora descritto: serve l’agente aggiornato su un domain controller.':
    'No domain controller has described it yet: an updated agent on a domain controller is needed.',
  'da leggere':
    'to be read',
  'Amministra':
    'Administers',
  '{n} sistemi':
    '{n} systems',
  'per difetto (dal SID)':
    'by default (from the SID)',
  'aggiunto qui':
    'added here',
  'fra gli amministratori locali':
    'among the local administrators',
  'annidato':
    'nested',
  'attraverso: {gruppi}':
    'through: {gruppi}',
  '{n} tramite gruppo':
    '{n} through a group',
  'I gruppi privilegiati li riconosce CENSO: quelli per difetto dal SID, i gruppi di dominio trovati fra gli amministratori locali delle macchine (il domain controller dice che sono gruppi e ne legge i membri), i gruppi annidati in un gruppo privilegiato, e su Linux i gruppi e gli utenti che sudoers abilita a tutto. Chi è in un gruppo che amministra una macchina risulta amministratore di quella macchina; «Impostazioni» mostra l’elenco dei gruppi riconosciuti e come.':
    'CENSO recognises the privileged groups: the default ones by SID, the domain groups found among the machines’ local administrators (the domain controller says they are groups and reads their members), the groups nested in a privileged group, and on Linux the groups and users sudoers allows everything. Whoever is in a group that administers a machine counts as an administrator of that machine; «Settings» lists the recognised groups and how.',
  'Accesso privilegiato fuori orario':
    'Privileged access off hours',
  'Forza bruta':
    'Brute force',
  'Password spray':
    'Password spray',
  'Accesso riuscito dopo una serie di fallimenti':
    'Access succeeded after a series of failures',
  'Movimento laterale':
    'Lateral movement',
  'Account condiviso':
    'Shared account',
  'Primo accesso a un sistema':
    'First access to a system',
  'Account privilegiato dormiente di nuovo in uso':
    'Dormant privileged account used again',
  'Account privilegiato dormiente':
    'Dormant privileged account',
  'Accesso da un Paese nuovo':
    'Access from a new country',
  'Accesso amministrativo senza MFA':
    'Administrative access without MFA',
  'Nuovo privilegio':
    'New privilege',
  'Account creato e reso privilegiato':
    'Account created and made privileged',
  'Registro di sicurezza cancellato':
    'Security log cleared',
  'Politica di audit modificata':
    'Audit policy changed',
  'Tentativi su un account disabilitato':
    'Attempts on a disabled account',
  'preso in carico':
    'taken in charge',
  'Rilevamento':
    'Detection',
  '{n} eventi':
    '{n} events',
  'Nota (resta nel registro)':
    'Note (stays in the register)',
  'Prendi in carico':
    'Take in charge',
  'Chiudi il rilevamento':
    'Close the detection',
  'Riapri':
    'Reopen',
  'Gli eventi':
    'The events',
  'Nessun evento: è proprio l’assenza di accessi a contare.':
    'No events: the absence of accesses is precisely what counts.',
  'Gli eventi sono usciti dalla conservazione.':
    'The events left the retention.',
  '{n} eventi non ci sono più: usciti dalla conservazione.':
    '{n} events are no longer there: past the retention.',
  'Le correlazioni leggono il registro in ordine e confrontano ogni evento con quelli appena prima e con la storia dello stesso account, con le soglie delle Impostazioni. Un rilevamento è uno per regola e soggetto (l’account, il giorno): cresce finché il fatto continua e si riapre se torna dopo essere stato chiuso. I nuovi, di gravità media o più, vanno per posta ai destinatari delle Impostazioni.':
    'The correlations read the register in order and compare every event with those just before it and with the history of the same account, with the thresholds of the Settings. A detection is one per rule and subject (the account, the day): it grows while the fact goes on and reopens if it comes back after being closed. The new ones, of medium severity or more, go by mail to the recipients in the Settings.',
  'di una persona':
    'of one person',
  'togli il filtro':
    'remove the filter',
  'Nessun rilevamento aperto.':
    'No open detection.',
  'Nessun rilevamento.':
    'No detection.',
  'Le correlazioni girano da sole sugli eventi che arrivano dalle fonti.':
    'The correlations run by themselves on the events arriving from the sources.',
  'Rilevamenti: ciò che le correlazioni trovano da sole sul registro — accesso privilegiato fuori orario, forza bruta, password spray, accesso riuscito dopo una serie di fallimenti, movimento laterale, account condiviso, primo accesso a un sistema, account privilegiati dormienti o di nuovo in uso, Paese nuovo, accesso amministrativo senza MFA, nuovi privilegi, account creato e reso privilegiato, registro cancellato, politica di audit modificata, tentativi su account disabilitati. Ognuno si apre con gli eventi che lo provano, si prende in carico o si chiude con una nota (che resta nel registro) e si riapre da solo se il fatto torna. I nuovi di gravità media o più arrivano per posta ai destinatari delle Impostazioni, e solo a loro.':
    'Detections: what the correlations find by themselves on the register — privileged access off hours, brute force, password spray, access succeeding after a series of failures, lateral movement, shared account, first access to a system, privileged accounts dormant or used again, new country, administrative access without MFA, new privileges, account created and made privileged, log cleared, audit policy changed, attempts on disabled accounts. Each opens with the events that prove it, is taken in charge or closed with a note (which stays in the register) and reopens by itself if the fact comes back. The new ones of medium severity or more arrive by mail to the recipients in the Settings, and only to them.',
  'Con gli utenti non privilegiati spenti, un tentativo fallito non ha il nome ma uno pseudonimo che CENSO calcola con un segreto fuori dal database: la forza bruta su un account e il password spray su molti si vedono lo stesso, senza nominare nessuno.':
    'With the non-privileged users off, a failed attempt has no name but a pseudonym CENSO computes with a secret outside the database: the brute force on one account and the password spray over many are still seen, without naming anyone.',
  'abilitata':
    'enabled',
  'cancellata':
    'deleted',
  'modificata':
    'changed',
  'password reimpostata':
    'password reset',
  'password cambiata dal titolare':
    'password changed by its owner',
  'bloccata per tentativi':
    'locked out after attempts',
  'sbloccata':
    'unlocked',
  'aggiunta a un gruppo':
    'added to a group',
  'tolta da un gruppo':
    'removed from a group',
  'gruppo creato':
    'group created',
  'gruppo cancellato':
    'group deleted',
  'privilegio concesso':
    'privilege granted',
  'privilegio tolto':
    'privilege revoked',
  'Gestione utenze':
    'Account management',
  'Chi ha creato, modificato, abilitato, disabilitato, sbloccato o cancellato un’utenza, chi ha reimpostato una password, chi ha aggiunto o tolto qualcuno da un gruppo e chi ha concesso o tolto un privilegio: su Windows e sui domain controller dal registro Security, su Linux da useradd, usermod, userdel, passwd e gpasswd con l’utente del login originale anche dietro sudo, e dall’inventario. Chi l’ha fatto è sempre registrato, anche con gli utenti non privilegiati spenti: gestire le utenze è un atto da amministratore.':
    'Who created, changed, enabled, disabled, unlocked or deleted an account, who reset a password, who added someone to or removed them from a group and who granted or revoked a privilege: on Windows and the domain controllers from the Security log, on Linux from useradd, usermod, userdel, passwd and gpasswd with the user of the original login even behind sudo, and from the inventory. Who did it is always recorded, even with the non-privileged users off: managing accounts is an administrator’s act.',
  'utenza, gruppo, chi l’ha fatto, sistema…':
    'account, group, who did it, system…',
  'Nessuna modifica di utenze nel periodo.':
    'No account changes in the period.',
  'Le modifiche arrivano dagli agenti (Windows, Linux, domain controller) e dall’inventario.':
    'The changes come from the agents (Windows, Linux, domain controllers) and from the inventory.',
  'Chi l’ha fatto':
    'Who did it',
  'CENSO (inventario)':
    'CENSO (inventory)',
  'rilevato da CENSO':
    'detected by CENSO',
  'Utenza':
    'Account',
  'Gruppo o ambito':
    'Group or scope',
  'Lo stato lo dice il sistema dell’account: il domain controller per il dominio, la macchina per gli account locali (agente aggiornato).':
    'The state comes from the account’s system: the domain controller for the domain, the machine for local accounts (updated agent).',
  'Ultimo accesso':
    'Last logon',
  'Password che non scade':
    'Password never expires',
  'scade':
    'expires',
  'Ultimo cambio password':
    'Last password change',
  'IP del sistema':
    'System IP',
  'Chi crea, modifica, abilita, disabilita e cancella le utenze, chi cambia le password e i gruppi.':
    'Who creates, changes, enables, disables and deletes accounts, who changes passwords and groups.',
  'Gestione utenze: chi ha creato, modificato, abilitato, disabilitato, sbloccato o cancellato un’utenza, chi ha reimpostato una password, chi ha messo o tolto qualcuno da un gruppo e chi ha concesso o tolto un privilegio, con quando e dove; si cerca per utenza, gruppo o autore. Su Linux chi l’ha fatto è l’utente del login originale, anche dietro sudo.':
    'Account management: who created, changed, enabled, disabled, unlocked or deleted an account, who reset a password, who put someone into or removed them from a group and who granted or revoked a privilege, with when and where; searchable by account, group or author. On Linux who did it is the user of the original login, even behind sudo.',
  'Persone e Privilegi mostrano anche lo stato degli account come lo dice il loro sistema: attivo o disattivato (o bloccato), ultimo accesso, password che non scade, ultimo cambio della password. Per gli account di dominio lo legge il domain controller, per quelli locali ogni macchina.':
    'People and Privileges also show the accounts’ state as their system tells it: active or disabled (or locked), last logon, password that never expires, last password change. The domain controller reads it for domain accounts, each machine for its local ones.',
  'Entra ID e Azure:':
    'Entra ID and Azure:',
  'serve una credenziale Microsoft Azure (Credenziali), con i permessi descritti nella sua guida':
    'a Microsoft Azure credential is needed (Credentials), with the permissions described in its guide',
  'tutte le credenziali Azure sono già fonti':
    'all Azure credentials are already sources',
  'scegli la credenziale Azure':
    'choose the Azure credential',
  'Aggiungi Entra ID':
    'Add Entra ID',
  'Microsoft Graph':
    'Microsoft Graph',
  'Microsoft Graph e activity log di Azure':
    'Microsoft Graph and Azure activity log',
  'Entra ID e Azure: in «Fonti» si aggiunge una credenziale Microsoft Azure (con i permessi della sua guida, sezione C). Ogni cinque minuti CENSO legge gli accessi di Entra ID (con P1 o P2: app, IP, Paese, MFA, esito), l’audit della directory (utenti creati, disabilitati, password reimpostate, membri di gruppi e ruoli), i ruoli di amministrazione con quelli idonei di PIM e lo stato di quegli account, e l’activity log della sottoscrizione (assegnazioni di ruolo di Azure). Un account sincronizzato da Active Directory si unisce alla stessa persona.':
    'Entra ID and Azure: in «Sources» add a Microsoft Azure credential (with the permissions of its guide, section C). Every five minutes CENSO reads the Entra ID sign-ins (with P1 or P2: app, IP, country, MFA, outcome), the directory audit (users created, disabled, passwords reset, members of groups and roles), the administrative roles with the eligible ones of PIM and the state of those accounts, and the subscription’s activity log (Azure role assignments). An account synchronised from Active Directory joins the same person.',
  'C) Per CENSO ACCESS (accessi, audit e ruoli di Entra ID)':
    'C) For CENSO ACCESS (Entra ID sign-ins, audit and roles)',
  'Autorizzazioni API → Aggiungi → Microsoft Graph → Autorizzazioni dell’applicazione: «AuditLog.Read.All», «Directory.Read.All» e, per i ruoli idonei di PIM, «RoleManagement.Read.Directory»; poi «Concedi il consenso amministratore».':
    'API permissions → Add → Microsoft Graph → Application permissions: «AuditLog.Read.All», «Directory.Read.All» and, for the eligible PIM roles, «RoleManagement.Read.Directory»; then «Grant admin consent».',
  'Gli accessi (sign-in) di Entra ID si leggono solo con una licenza Entra ID P1 o P2 nel tenant: senza, CENSO legge comunque audit della directory, ruoli e activity log, e la fonte lo dice.':
    'The Entra ID sign-ins can be read only with an Entra ID P1 or P2 licence in the tenant: without it, CENSO still reads the directory audit, roles and activity log, and the source says so.',
  'L’activity log della sottoscrizione (assegnazioni di ruolo di Azure) usa lo stesso ruolo «Reader» della sezione B.':
    'The subscription’s activity log (Azure role assignments) uses the same «Reader» role as section B.',
  'In CENSO ACCESS → Fonti, «Aggiungi Entra ID» con questa credenziale: la lettura parte da sola ogni cinque minuti.':
    'In CENSO ACCESS → Sources, «Add Entra ID» with this credential: the reading starts by itself every five minutes.',
  'Syslog delle sonde':
    'The probes’ syslog',
  'Una sonda con la funzione «Syslog per CENSO ACCESS» riceve il syslog di apparati di rete, firewall, controller Wi‑Fi e server RADIUS e ne conserva tutto il grezzo, in segmenti orari compressi, in sola lettura e immutabili, con l’impronta registrata qui e riletta ogni giorno. Al registro arrivano solo gli accessi riconosciuti (Cisco, Huawei, FortiGate, Palo Alto, Juniper, Aruba, WatchGuard, FreeRADIUS, Cisco ISE). Il resto si cerca qui: la sonda guarda nei suoi segmenti e manda solo le righe trovate.':
    'A probe with the «Syslog for CENSO ACCESS» function receives the syslog of network devices, firewalls, Wi‑Fi controllers and RADIUS servers and keeps all of the raw, in hourly compressed, read-only and immutable segments, with the hash registered here and re-read every day. Only the recognised accesses reach the register (Cisco, Huawei, FortiGate, Palo Alto, Juniper, Aruba, WatchGuard, FreeRADIUS, Cisco ISE). The rest is searched here: the probe looks in its segments and sends only the lines found.',
  'Nessuna sonda riceve il syslog.':
    'No probe receives syslog.',
  'Si abilita la funzione «Syslog per CENSO ACCESS» nella scheda della sonda, poi si puntano gli apparati alla sonda (porta 514, UDP o TCP).':
    'Enable the «Syslog for CENSO ACCESS» function on the probe’s page, then point the devices at the probe (port 514, UDP or TCP).',
  'Ricezione':
    'Receiving',
  '{n} scartate':
    '{n} dropped',
  'Grezzo conservato':
    'Raw kept',
  '{n} segmenti':
    '{n} segments',
  'Cerca nel grezzo':
    'Search the raw',
  'es. admin, LOGIN_FAILED, 203.0.113.9':
    'e.g. admin, LOGIN_FAILED, 203.0.113.9',
  'Apparato (indirizzo)':
    'Device (address)',
  'La sonda sta cercando: risponde al prossimo contatto (entro un minuto).':
    'The probe is searching: it answers at its next contact (within a minute).',
  'La sonda non ha risposto entro mezz’ora: è online?':
    'The probe did not answer within half an hour: is it online?',
  'Nessuna riga trovata.':
    'No line found.',
  'Mostrate le prime righe: restringi il periodo o il testo per vederle tutte.':
    'Showing the first lines: narrow the period or the text to see them all.',
  'Le sonde con la funzione «Syslog per CENSO ACCESS» ricevono il syslog degli apparati':
    'The probes with the «Syslog for CENSO ACCESS» function receive the devices’ syslog',
  'Porta syslog (UDP e TCP)':
    'Syslog port (UDP and TCP)',
  'Apparati ammessi (indirizzi o reti, uno per riga; vuoto = tutti)':
    'Allowed devices (addresses or networks, one per line; empty = all)',
  'Syslog per CENSO ACCESS':
    'Syslog for CENSO ACCESS',
  'La sonda riceve il syslog degli apparati di rete, dei firewall, dei controller Wi‑Fi e dei server RADIUS (UDP e TCP, porta 514), conserva tutto il grezzo in segmenti compressi e immutabili con l’impronta registrata nel Control Plane, e manda al registro degli accessi solo gli accessi che riconosce. Apre una porta in ascolto. Richiede CENSO ACCESS.':
    'The probe receives the syslog of network devices, firewalls, Wi‑Fi controllers and RADIUS servers (UDP and TCP, port 514), keeps all the raw in compressed, immutable segments with the hash registered in the Control Plane, and sends to the register of the accesses only the accesses it recognises. Opens a listening port. Requires CENSO ACCESS.',
  'Syslog di apparati e Wi‑Fi: una sonda con la funzione «Syslog per CENSO ACCESS» riceve il syslog di switch, router, firewall, controller Wi‑Fi e server RADIUS, ne conserva tutto il grezzo (segmenti orari compressi, immutabili, con l’impronta registrata e riletta ogni giorno, per tutta la conservazione) e manda al registro solo gli accessi riconosciuti: amministrazione degli apparati, VPN, Wi‑Fi. Chi amministra un apparato compare fra i privilegi; il resto del grezzo si cerca da «Fonti», e ogni ricerca resta nel registro delle viste.':
    'Syslog of devices and Wi‑Fi: a probe with the «Syslog for CENSO ACCESS» function receives the syslog of switches, routers, firewalls, Wi‑Fi controllers and RADIUS servers, keeps all the raw (hourly compressed, immutable segments, with the hash registered and re-read every day, for the whole retention) and sends to the register only the recognised accesses: device administration, VPN, Wi‑Fi. Who administers a device appears among the privileges; the rest of the raw is searched from «Sources», and every search stays in the register of the views.',
  'Evento di prova affidato alla sonda: parte al prossimo contatto.':
    'Test event handed to the probe: it leaves at the next contact.',
  'Evento di prova inviato.':
    'Test event sent.',
  'Non inviato.':
    'Not sent.',
  'Inoltro a un SIEM':
    'Forwarding to a SIEM',
  'Il registro (e, se vuoi, i rilevamenti) va al SIEM del tenant, in ordine e senza buchi: un lotto si considera consegnato solo dopo l’invio, quindi un SIEM che non risponde non perde nulla. Parte dal Control Plane se il SIEM è raggiungibile da Internet, da una sonda se sta nella rete del cliente. Ciò che il registro non nomina (utenti non privilegiati spenti) non viene nominato nemmeno al SIEM.':
    'The register (and, if you want, the detections) goes to the tenant’s SIEM, in order and without holes: a batch counts as delivered only once sent, so a SIEM that does not answer loses nothing. It leaves from the Control Plane if the SIEM is reachable from the Internet, from a probe if it is inside the customer’s network. What the register does not name (non-privileged users off) is not named to the SIEM either.',
  'Inoltra gli eventi al SIEM':
    'Forward the events to the SIEM',
  'dal Control Plane':
    'from the Control Plane',
  'da una sonda':
    'from a probe',
  'scegli la sonda':
    'choose the probe',
  'syslog su TCP':
    'syslog over TCP',
  'syslog su TLS':
    'syslog over TLS',
  'syslog su UDP':
    'syslog over UDP',
  'Indirizzo del collettore':
    'Collector address',
  'webhook JSON':
    'JSON webhook',
  'Intestazione del token':
    'Token header',
  'Token (impostato: scrivi per cambiarlo)':
    'Token (set: type to change it)',
  'Indirizzo del SIEM':
    'SIEM address',
  'Cosa inoltrare':
    'What to forward',
  'tutti gli eventi':
    'all events',
  'solo i privilegiati':
    'privileged only',
  'privilegiati e falliti':
    'privileged and failures',
  'anche i rilevamenti':
    'the detections too',
  'verifica il certificato':
    'verify the certificate',
  'Inviati {n} messaggi':
    '{n} messages sent',
  'ultimo invio {quando}':
    'last sent {quando}',
  '{n} eventi da inviare':
    '{n} events to send',
  'Invia un evento di prova':
    'Send a test event',
  'Inoltro a un SIEM: in Impostazioni si sceglie da dove (Control Plane o una sonda della rete), come (syslog su UDP, TCP o TLS, oppure HTTPS verso Splunk HEC o un webhook), in che formato (CEF o JSON) e cosa (tutto, solo i privilegiati, privilegiati e falliti, anche i rilevamenti). Gli eventi partono in ordine e un lotto conta come inviato solo dopo l’invio: un SIEM fermo non perde nulla, e «Invia un evento di prova» verifica la strada.':
    'Forwarding to a SIEM: in Settings choose from where (Control Plane or a probe of the network), how (syslog over UDP, TCP or TLS, or HTTPS to Splunk HEC or a webhook), in which format (CEF or JSON) and what (everything, privileged only, privileged and failures, the detections too). The events leave in order and a batch counts as sent only once sent: a SIEM that is down loses nothing, and «Send a test event» checks the way.',
  'Splunk HEC':
    'Splunk HEC',
  'siem.esempio.local':
    'siem.example.local',
  'Per la continuità si aggiungono una seconda copia e una terza per il disaster recovery, su sonde diverse e meglio in sedi diverse: ognuna tiene tutto il registro per conto suo, lo rilegge e ne risponde. Una sonda aggiunta dopo riceve tutto ciò che il registro conserva ancora.':
    'For continuity add a second copy and a third one for disaster recovery, on different probes and better in different sites: each keeps the whole register on its own, re-reads it and answers for it. A probe added later receives everything the register still keeps.',
  'Seconda copia':
    'Second copy',
  'Terza copia (disaster recovery)':
    'Third copy (disaster recovery)',
  'seconda copia':
    'second copy',
  'disaster recovery':
    'disaster recovery',
  '{n} persi':
    '{n} lost',
  'Rilevamenti con un ID: ogni rilevamento ha un numero progressivo (RIL-000123), lo stesso nell’elenco, nella sua scheda, nella mail e nel SIEM, per citarlo senza ambiguità.':
    'Detections with an ID: every detection has a progressive number (RIL-000123), the same in the list, on its page, in the mail and in the SIEM, to quote it without ambiguity.',
  'Gli accessi arrivano al registro pochi secondi dopo essere avvenuti: l’agente li legge ogni cinque secondi e li manda subito, la sonda syslog li consegna ogni tre. L’ora registrata è quella dell’accesso, scritta dal sistema o dall’apparato; l’ora di arrivo resta a parte.':
    'The accesses reach the register a few seconds after they happened: the agent reads them every five seconds and sends them at once, the syslog probe delivers them every three. The time recorded is the access’s own, written by the system or the device; the arrival time is kept apart.',
  'Copia del syslog grezzo su altre sonde':
    'Copy of the raw syslog on other probes',
  'nessuna: il grezzo resta sulla sonda che lo riceve':
    'none: the raw stays on the probe that receives it',
  'CENSO copia ogni segmento sulle sonde d’archivio (seconda copia, DR)':
    'CENSO copies every segment to the archive probes (second copy, DR)',
  'gli apparati mandano a più sonde; CENSO registra ogni messaggio una volta':
    'the devices send to several probes; CENSO records each message once',
  'La sonda che riceve manda ogni segmento chiuso (un’ora di grezzo, compresso) al Control Plane, che lo tiene solo finché le sonde d’archivio diverse da lei non l’hanno conservato e verificato. Traffico in più verso il Control Plane: il grezzo compresso di ogni ora, una volta.':
    'The receiving probe sends every closed segment (an hour of raw, compressed) to the Control Plane, which keeps it only until the archive probes other than it have stored and verified it. Extra traffic to the Control Plane: each hour’s compressed raw, once.',
  'Si configurano gli apparati per mandare il syslog a due o tre sonde con la funzione «Syslog per CENSO ACCESS»: ognuna conserva il suo grezzo, e il registro prende ogni messaggio una volta sola, riconoscendolo dall’impronta del testo originale. Nessun traffico in più verso il Control Plane.':
    'Configure the devices to send their syslog to two or three probes with the «Syslog for CENSO ACCESS» function: each keeps its own raw, and the register takes each message only once, recognising it by the hash of the original text. No extra traffic to the Control Plane.',
  'Copie':
    'Copies',
  'Il syslog grezzo anche su altre sonde, a scelta: CENSO copia ogni segmento chiuso sulle sonde d’archivio (seconda copia, DR), che lo verificano e lo conservano immutabile; oppure gli apparati mandano il syslog a più sonde e il registro prende ogni messaggio una volta sola.':
    'The raw syslog on other probes too, by choice: CENSO copies every closed segment to the archive probes (second copy, DR), which verify it and keep it immutable; or the devices send their syslog to several probes and the register takes each message only once.',
  'Nella mail di ogni rilevamento: Utente, IP sorgente e Sistema. L’IP sorgente c’è sempre: l’indirizzo di provenienza, il nome della postazione se la fonte non dà l’indirizzo, «accesso locale» per console, sudo e su, oppure «non indicato dalla fonte».':
    'In each detection’s mail: User, Source IP and System. The source IP is always there: the address it came from, the workstation’s name when the source gives no address, «local access» for console, sudo and su, or «not given by the source».',
  'AWX / Automation Controller confronta la CMDB con gli host gestiti da AWX ed evidenzia i CI non automatizzati, gli host non censiti e l’inventario dinamico che sincronizza gli host dalla piattaforma ad AWX.':
    'AWX / Automation Controller compares the CMDB with the hosts managed by AWX and highlights the CIs not automated, the hosts not inventoried and the dynamic inventory that syncs the hosts from the platform to AWX.',
  'Inventario dinamico':
    'Dynamic inventory',
  'Nessun AWX collegato':
    'No AWX connected',
  'Aggiungi una connessione AWX (token, da una sonda o dal Control Plane) nella sezione Connessioni: il confronto legge gli host di AWX in tempo reale.':
    'Add an AWX connection (token, from a probe or from the Control Plane) in the Connections section: the comparison reads the AWX hosts live.',
  'Gli host si abbinano per nome, nome breve o ansible_host. Classi e tag restringono la CMDB a ciò che AWX dovrebbe avere.':
    'Hosts are matched by name, short name or ansible_host. Classes and tags narrow the CMDB to what AWX should have.',
  'Host in CENSO':
    'Hosts in CENSO',
  'Abbinati':
    'Matched',
  'In CENSO, non in AWX':
    'In CENSO, not in AWX',
  'In AWX, sconosciuti a CENSO':
    'In AWX, unknown to CENSO',
  'Inventario: {inv} · letto alle {ora}':
    'Inventory: {inv} · read at {ora}',
  'tutti gli host':
    'all hosts',
  'Abbinati ({n})':
    'Matched ({n})',
  'Abilitato in AWX':
    'Enabled in AWX',
  'Crea una chiave API con permessi di lettura per l’integrazione Ansible AWX nella sezione Amministrazione → Chiavi API.':
    'Create an API key with read permission for the Ansible AWX integration in Administration → API keys.',
  'Scarica lo script dell’inventario dinamico e pubblicalo nel repository Git utilizzato dal progetto AWX.':
    'Download the dynamic inventory script and publish it in the Git repository used by the AWX project.',
  'Configura in AWX una sorgente d’inventario «Sourced from a Project», associandola allo script e impostando l’indirizzo della piattaforma e la chiave API nelle variabili d’ambiente.':
    'In AWX, configure an inventory source «Sourced from a Project», linking it to the script and setting the platform address and the API key in the environment variables.',
  'Variabili d’ambiente della sorgente in AWX':
    'Environment variables of the source in AWX',
  'una sezione a sé nel menu Integrazioni, con tre schede: Confronto, Inventario dinamico e Connessioni. AWX può prendere i suoi host dalla CMDB: una chiave API del tenant con «Ansible AWX» in lettura, lo script d’inventario scaricato da «Inventario dinamico» nel progetto di AWX (la scheda scrive anche le variabili d’ambiente della sorgente, con i filtri scelti), e una sorgente «da progetto» — gli host arrivano con l’indirizzo, le proprietà (classe, OS, modello, seriale, tag, business service) e i gruppi presi da ciò che CENSO sa: i tag diventano i gruppi dei playbook. Con una credenziale AWX (token, da una sonda o dal Control Plane) «Confronta» legge gli host di AWX e dice quali macchine della CMDB non ci sono — nessun playbook le toccherà — e quali host di AWX CENSO non conosce, oltre agli abbinati; classi e tag restringono il confronto a ciò che AWX dovrebbe avere.':
    'a section of its own in the Integrations menu, with three tabs: Comparison, Dynamic inventory and Connections. AWX can take its hosts from the CMDB: a tenant API key with «Ansible AWX» read access, the inventory script downloaded from «Dynamic inventory» into the AWX project (the tab also writes the source’s environment variables, with the chosen filters), and a «from project» source — the hosts arrive with their address, their properties (class, OS, model, serial, tags, business service) and groups taken from what CENSO knows: the tags become the playbooks’ groups. With an AWX credential (token, from a probe or from the Control Plane) «Compare» reads the AWX hosts and says which CMDB machines are missing — no playbook will touch them — and which AWX hosts CENSO does not know, besides the matched ones; classes and tags narrow the comparison to what AWX should have.',
  'Annullare i comandi in attesa per questo monitor? Andranno dati di nuovo.':
    'Cancel the pending commands for this monitor? They will have to be given again.',
  'Cancellare il record {tipo} «{nome}» dal DNS? La modifica va subito sul server DNS e non si può annullare.':
    'Delete the {tipo} record «{nome}» from DNS? The change goes to the DNS server at once and cannot be undone.',
  'Eliminare il gruppo di credenziali «{nome}»? Le credenziali restano; i job che usano il gruppo non lo troveranno più. Non si può annullare.':
    'Delete the credential group «{nome}»? The credentials stay; the jobs using the group will no longer find it. This cannot be undone.',
  'Eliminare la vista «{nome}»? Non si può annullare.':
    'Delete the view «{nome}»? This cannot be undone.',
  'Eliminare l’utente {email} da questo tenant? Perde subito l’accesso. Non si può annullare.':
    'Delete the user {email} from this tenant? They lose access at once. This cannot be undone.',
  'Eliminare questa applicazione e il suo SBOM? Non si può annullare.':
    'Delete this application and its SBOM? This cannot be undone.',
  'Eliminare questa credenziale? Si toglie anche dai gruppi, e i job e le sorgenti che la usano smettono di funzionare. Il segreto non si recupera: andrà riscritto.':
    'Delete this credential? It is also removed from the groups, and the jobs and sources using it stop working. The secret cannot be recovered: it will have to be written again.',
  'Eliminare questa mappatura del connettore? La sincronizzazione si ferma. Non si può annullare.':
    'Delete this connector mapping? The synchronisation stops. This cannot be undone.',
  'Eliminare questa pianificazione? I job non partiranno più da soli. Non si può annullare.':
    'Delete this schedule? The jobs will no longer start on their own. This cannot be undone.',
  'Eliminare questa regola di classificazione? Non si può annullare.':
    'Delete this classification rule? This cannot be undone.',
  'Eliminare questo servizio IA dal catalogo? Non si può annullare.':
    'Delete this AI service from the catalogue? This cannot be undone.',
  'Eliminare questo SKU? Non si può annullare.':
    'Delete this SKU? This cannot be undone.',
  'Revocare l’agente «{nome}»? Smette subito di consegnare: ciò che manderà verrà rifiutato finché non lo riattivi.':
    'Revoke the agent «{nome}»? It stops delivering at once: whatever it sends will be refused until you reactivate it.',
  'Revocare questo agente esca? Smette subito di comunicare con CENSO; per riaverlo va installato di nuovo con una configurazione nuova. Non si può annullare.':
    'Revoke this decoy agent? It stops talking to CENSO at once; to have it back it must be installed again with a new configuration. This cannot be undone.',
  'Ruotare adesso la chiave di ripristino? La chiave di prima smette di funzionare appena l’agente la sostituisce. Non si può annullare.':
    'Rotate the recovery key now? The previous key stops working as soon as the agent replaces it. This cannot be undone.',
  'Spegnere il monitoraggio di questo CI? Non si può annullare: i parametri andranno riaccesi.':
    'Switch off the monitoring of this CI? This cannot be undone: the parameters will have to be switched on again.',
  'Togliere {email} dal tenant «{tenant}»? Perde subito l’accesso a quel tenant.':
    'Remove {email} from the tenant «{tenant}»? They lose access to that tenant at once.',
  'Togliere {n} utenti da questo tenant? Perdono subito l’accesso.':
    'Remove {n} users from this tenant? They lose access at once.',
  'Togliere il cespite dalla copertura di questo contratto? Non si può annullare.':
    'Remove the asset from this contract’s coverage? This cannot be undone.',
  'Togliere il profilo di monitoraggio «{nome}»? Non si può annullare.':
    'Remove the monitoring profile «{nome}»? This cannot be undone.',
  'Togliere l’indirizzo dedicato {host} e i suoi alias? Il tenant torna sull’indirizzo della piattaforma.':
    'Remove the dedicated address {host} and its aliases? The tenant goes back to the platform address.',
  'Togliere questa regola di posizione? Non si può annullare.':
    'Remove this location rule? This cannot be undone.',
  'Togliere questa sorveglianza? Smette subito di avvisare. Non si può annullare.':
    'Remove this watch? It stops alerting at once. This cannot be undone.',
  'Togliere questo elemento dalla Business App? Non si può annullare.':
    'Remove this element from the Business App? This cannot be undone.',
  'Togliere questo indirizzo dal CI? Non si può annullare: andrà aggiunto di nuovo.':
    'Remove this address from the CI? This cannot be undone: it will have to be added again.',
  'Togliere questo nome dal CI? Non si può annullare: andrà aggiunto di nuovo.':
    'Remove this name from the CI? This cannot be undone: it will have to be added again.',
  'Togliere questo parametro dal monitoraggio del CI? Il suo storico non si legge più. Non si può annullare.':
    'Remove this parameter from the CI’s monitoring? Its history can no longer be read. This cannot be undone.',
  'Ogni azione che non si può annullare chiede conferma prima di partire: revocare un agente o una sonda, eliminare utenti, credenziali, pianificazioni, regole o viste, togliere un record dal DNS, ruotare una chiave. Il messaggio dice cosa succede e se si potrà tornare indietro.':
    'Every action that cannot be undone asks for confirmation before it starts: revoking an agent or a probe, deleting users, credentials, schedules, rules or views, removing a DNS record, rotating a key. The message says what happens and whether it can be undone.',
  'per esempio: account del servizio SQL Server, gestito dal team database':
    'for example: SQL Server service account, managed by the database team',
  'Annulla le modifiche':
    'Discard the changes',
  'Modifiche non salvate.':
    'Unsaved changes.',
  'Le modifiche a tipo, reperibilità e nota si salvano con «Salva»; la nota compare sotto il nome della persona.':
    'Changes to type, on-call and note are saved with «Save»; the note appears under the person’s name.',
  'Applica alle selezionate':
    'Apply to the selected',
  'La prende la prima sonda libera che sa farla: con più sedi può essere quella di un’altra rete. Scegli la sonda o il gruppo della sede.':
    'The first free probe that can run it takes it: with several sites it may be one from another network. Choose the site’s probe or group.',
  'Ogni esecuzione la fa solo questa sonda; se non è disponibile il job aspetta. È anche la sonda che poi legge l’apparato nel monitoraggio, perché è quella che le sue ACL lasciano passare.':
    'Every run is done by this probe only; if it is not available the job waits. It is also the probe that then reads the device in monitoring, because it is the one its ACLs let through.',
  'Ogni esecuzione la fa una sonda di questo gruppo.':
    'Every run is done by a probe of this group.',
  'Questo CI si legge con una credenziale (SNMP, SSH, WinRM): i parametri li legge «{s}», la sonda del job di discovery che l’ha letto con quella credenziale, perché è quella che l’apparato lascia passare; le sonde scelte qui leggono ping e porte, e l’oggetto risulta giù solo se non risponde a nessuna. Per cambiarla, fai girare la discovery con credenziali dalla sonda giusta (in Pianificazioni, «Esegui su»).':
    'This CI is read with a credential (SNMP, SSH, WinRM): its parameters are read by «{s}», the probe of the discovery job that read it with that credential, because it is the one the device lets through; the probes chosen here read ping and ports, and the object is down only if it answers none. To change it, run the credentialed discovery from the right probe (in Schedules, «Run on»).',
  'Un oggetto letto con una credenziale (SNMP, SSH, WinRM) fa eccezione: i suoi parametri li legge sempre la sonda del job di discovery, quella che l’apparato lascia passare dalle sue ACL, qualunque sia l’ordine delle sonde scelte e il loro carico; le altre leggono ping e porte. La sezione lo dice: «principale: la sonda che legge con la credenziale».':
    'An object read with a credential (SNMP, SSH, WinRM) is the exception: its parameters are always read by the probe of the discovery job, the one the device’s ACLs let through, whatever the order of the chosen probes and their load; the others read ping and ports. The section says so: «principal: the probe that reads with the credential».',
  'lascia com’è':
    'leave as it is',
  'principale: la sonda che legge con la credenziale':
    'principal: the probe that reads with the credential',
  'qualunque sonda idonea':
    'any eligible probe',
  'scelta attuale (non idonea per questo tipo)':
    'current choice (not eligible for this kind)',
  '{n} pianificazioni aggiornate.':
    '{n} schedules updated.',
  '{n} pianificazioni aggiornate; lasciate com’erano perché la sonda non sa eseguirle: {nomi}.':
    '{n} schedules updated; left as they were because the probe cannot run them: {nomi}.',
  '{n} pianificazioni selezionate':
    '{n} schedules selected',
  '«Esegui su» sceglie la sonda o il gruppo di sonde che la esegue: con più sedi, quella della rete da scansionare. Selezionando più pianificazioni si cambia insieme, per tutte, la sonda o il gruppo e lo stato.':
    '«Run on» chooses the probe or the group of probes that runs it: with several sites, the one of the network to scan. Selecting several schedules changes the probe or group and the state for all of them at once.',
  'È la sonda del job che ha letto l’apparato con la credenziale, non l’ultima che l’ha visto: un ping sweep da un’altra sede non la sposta.':
    'It is the probe of the job that read the device with the credential, not the last one that saw it: a ping sweep from another site does not move it.',
  'Aggiorna l’agente: le versioni nuove ripiegano da sole su una query più semplice quando il domain controller rifiuta quella di sempre.':
    'Update the agent: new versions fall back on their own to a simpler query when the domain controller refuses the usual one.',
  'Installazione sostituita: un’altra fonte con lo stesso nome consegna, questa è ferma. Se l’agente è stato reinstallato, sospendila.':
    'Replaced installation: another source with the same name delivers, this one is stopped. If the agent was reinstalled, suspend it.',
  'Registro Security grande: con l’agente aggiornato gli eventi si consegnano in più giri, senza errore.':
    'Large Security log: with the updated agent the events are delivered over several rounds, without an error.',
  'installazione sostituita':
    'replaced installation',
  'non più presente':
    'no longer present',
  'In «Fonti» la colonna Agente dice la versione dell’agente di ogni fonte; una riga ferma accanto a un’altra con lo stesso nome che consegna è un’installazione sostituita (agente reinstallato) e si può sospendere. Su un domain controller con un registro Security grande gli eventi arrivano in più giri, senza errore; se il domain controller rifiuta la query, l’agente aggiornato ripiega da solo su una più semplice.':
    'In «Sources» the Agent column gives the version of each source’s agent; a stopped row next to another with the same name that delivers is a replaced installation (reinstalled agent) and can be suspended. On a domain controller with a large Security log the events arrive over several rounds, without an error; if the domain controller refuses the query, the updated agent falls back on its own to a simpler one.',
  '180–365 giorni':
    '180–365 days',
  '30–90 giorni':
    '30–90 days',
  '90–180 giorni':
    '90–180 days',
  'Chiedi al responsabile di ciascuna se serve ancora; senza risposta, disattivala.':
    'Ask the owner of each one whether it is still needed; with no answer, disable it.',
  'Chiedi il cambio password a queste utenze.':
    'Ask these accounts to change their password.',
  'Come migliorare':
    'How to improve',
  'Con MFA e password lunghe la scadenza può essere lunga, ma non assente: imposta un massimo nella politica del dominio.':
    'With MFA and long passwords the expiry can be long, but not absent: set a maximum in the domain policy.',
  'Controlla che le password non compaiano in elenchi di credenziali trafugate (per esempio con la protezione password di Entra ID).':
    'Check that the passwords do not appear in lists of leaked credentials (for instance with Entra ID password protection).',
  'Da dove arrivano le utenze':
    'Where the accounts come from',
  'Disattiva subito, cancella dopo un periodo di attesa (per esempio 30 giorni): così un errore si recupera.':
    'Disable at once, delete after a waiting period (for instance 30 days): that way a mistake can be undone.',
  'Disattivala finché non serve; crea le utenze quando la persona arriva.':
    'Disable it until it is needed; create accounts when the person arrives.',
  'Disattivate ma privilegiate':
    'Disabled but privileged',
  'Dove proprio deve restare, password lunga (almeno 25 caratteri), accesso limitato ai sistemi che servono e nessun privilegio in più.':
    'Where it really must stay, a long password (at least 25 characters), access limited to the systems needed and no extra privilege.',
  'Età della password delle utenze attive':
    'Password age of the enabled accounts',
  'Gruppi privilegiati':
    'Privileged groups',
  'Guarda gli accessi falliti dell’utenza e i Rilevamenti (forza bruta, password spray) prima di sbloccarla.':
    'Look at the account’s failed logons and at the Detections (brute force, password spray) before unlocking it.',
  'Imposta «cambio password al primo accesso» per le utenze nuove.':
    'Set «change password at first logon» for new accounts.',
  'Inattiva da':
    'Idle for',
  'Le utenze nei gruppi che amministrano il dominio o le macchine, anche attraverso un gruppo annidato: un problema qui pesa il doppio. Tienile poche, nominali, con MFA, e separate dalle utenze di tutti i giorni.':
    'The accounts in the groups that administer the domain or the machines, also through a nested group: a problem here weighs double. Keep them few, named, with MFA, and separate from everyday accounts.',
  'Leggo le utenze…':
    'Reading the accounts…',
  'Letto alle {ora}.':
    'Read at {ora}.',
  'Lo stato delle utenze lo mandano gli agenti aggiornati: il domain controller per il dominio, ogni macchina per i suoi amministratori locali, Entra ID per i ruoli.':
    'The accounts’ state is sent by the updated agents: the domain controller for the domain, each machine for its local administrators, Entra ID for the roles.',
  'Mai usate':
    'Never used',
  'Metti la disattivazione nella procedura di uscita del personale e di fine contratto dei fornitori.':
    'Put the disabling in the leaver procedure for staff and in the end of contract of suppliers.',
  'Nessun gruppo descritto. I gruppi delle utenze di dominio li manda l’agente del domain controller (aggiornato): per tutte le utenze con gli utenti non privilegiati accesi, altrimenti per le privilegiate.':
    'No group described. The groups of the domain accounts are sent by the (updated) domain controller agent: for every account with the non-privileged users on, otherwise for the privileged ones.',
  'Nessun problema: ottimo.':
    'No problem: excellent.',
  'Nessuna utenza descritta dai sistemi.':
    'No account described by the systems.',
  'Nessuna utenza.':
    'No account.',
  'Nota: l’ultimo accesso è quello visto dal domain controller su cui gira l’agente, incrociato con il registro di CENSO.':
    'Note: the last logon is the one seen by the domain controller where the agent runs, crossed with CENSO’s register.',
  'Oltre {n} giorni senza accessi un’utenza attiva è uno zombie (soglia delle Impostazioni).':
    'Beyond {n} days without logons an enabled account is a zombie (threshold in the Settings).',
  'Password vecchie':
    'Old passwords',
  'Per i servizi usa account di servizio gestiti (gMSA) invece di utenze personali lasciate attive.':
    'For services use managed service accounts (gMSA) instead of personal accounts left enabled.',
  'Per i servizi usa gMSA (password ruotata da Active Directory) o una rotazione programmata con un gestore di segreti.':
    'For services use gMSA (password rotated by Active Directory) or a scheduled rotation with a secrets manager.',
  'Per le persone togli «La password non scade mai» e lascia lavorare la politica delle password del dominio.':
    'For people remove «Password never expires» and let the domain password policy work.',
  'Perché conta':
    'Why it matters',
  'Postura delle utenze':
    'Account posture',
  'Privilegiate ({n})':
    'Privileged ({n})',
  'Privilegiate a rischio':
    'Privileged at risk',
  'Privilegiato':
    'Privileged',
  'Problemi':
    'Problems',
  'Problemi trovati':
    'Problems found',
  'Quando disattivi un’utenza, toglila anche dai gruppi privilegiati (Domain Admins, Administrators, gli Operators).':
    'When you disable an account, also remove it from the privileged groups (Domain Admins, Administrators, the Operators).',
  'Quanto sono in ordine le utenze: zombie, mai usate, password che non scadono o vecchie, privilegiate a rischio, e come migliorare.':
    'How tidy the accounts are: zombies, never used, passwords that never expire or are old, privileged at risk, and how to improve.',
  'Rivedi i membri dei gruppi privilegiati almeno ogni trimestre: CENSO ACCESS li elenca in «Privilegi».':
    'Review the members of the privileged groups at least every quarter: CENSO ACCESS lists them in «Privileges».',
  'Se è un account di servizio, documentalo come tale nella scheda della persona (Tipo: account di servizio).':
    'If it is a service account, record it as such on the person’s page (Type: service account).',
  'Se è un servizio, aggiorna la password dove è salvata.':
    'If it is a service, update the password where it is stored.',
  'Stato delle utenze':
    'State of the accounts',
  'Tutte le utenze':
    'All accounts',
  'Tutte le utenze ({n})':
    'All accounts ({n})',
  'Ultimo accesso delle utenze attive':
    'Last logon of the enabled accounts',
  'Una password che non scade resta valida per anni: se è stata vista, scritta o riusata altrove, vale ancora. È il primo bersaglio di chi ha rubato un elenco di credenziali.':
    'A password that never expires stays valid for years: if it was seen, written down or reused elsewhere, it still works. It is the first target of whoever stole a list of credentials.',
  'Una password invariata da oltre {n} giorni è probabilmente nota a più persone di quante dovrebbero, o riusata altrove.':
    'A password unchanged for over {n} days is probably known to more people than it should be, or reused elsewhere.',
  'Un’utenza attiva che nessuno usa da oltre {n} giorni è una porta lasciata aperta: se la sua password è debole, riusata o rubata, nessuno se ne accorge, perché nessuno si aspetta di vederla entrare. Sono spesso ex dipendenti, consulenti a fine incarico, account di prova.':
    'An enabled account nobody has used for over {n} days is a door left open: if its password is weak, reused or stolen, nobody notices, because nobody expects to see it log on. They are often former employees, consultants whose engagement ended, test accounts.',
  'Un’utenza attiva che non ha mai fatto un accesso è stata creata in anticipo o è rimasta da una prova: ha spesso la password iniziale, nota a chi l’ha creata.':
    'An enabled account that never logged on was created in advance or left over from a test: it often still has the initial password, known to whoever created it.',
  'Un’utenza bloccata ha superato i tentativi di password sbagliata: una password vecchia rimasta in un servizio o su un telefono, oppure qualcuno che prova a indovinarla.':
    'A locked account went past the wrong-password attempts: an old password left in a service or on a phone, or someone trying to guess it.',
  'Un’utenza disattivata ma ancora in un gruppo privilegiato torna amministratore nell’istante in cui qualcuno la riattiva, per errore o di proposito.':
    'A disabled account still in a privileged group is an administrator again the moment someone enables it, by mistake or on purpose.',
  'Utenti non privilegiati spenti: qui ci sono solo le utenze privilegiate. Accendili nelle Impostazioni per avere tutte le utenze del dominio e trovare gli account zombie.':
    'Non-privileged users off: only the privileged accounts are here. Turn them on in the Settings to get every account of the domain and find the zombie accounts.',
  'Utenze attive':
    'Enabled accounts',
  'Utenze con questo problema':
    'Accounts with this problem',
  'Utenze privilegiate':
    'Privileged accounts',
  'Utenze zombie':
    'Zombie accounts',
  'account locali':
    'local accounts',
  'attive con problemi':
    'enabled with problems',
  'attive senza problemi':
    'enabled without problems',
  'disattivate':
    'disabled',
  'dominio (domain controller)':
    'domain (domain controller)',
  'meno di 30 giorni':
    'less than 30 days',
  'meno di 90 giorni':
    'less than 90 days',
  'non nota':
    'unknown',
  'oltre un anno':
    'over a year',
  'su {n} privilegiate attive':
    'of {n} enabled privileged',
  'su {n} · {d} disattivate':
    'of {n} · {d} disabled',
  'tramite un altro gruppo':
    'through another group',
  'utenze':
    'accounts',
  'utenze attive senza problemi, le privilegiate contano doppio':
    'enabled accounts without problems, privileged ones count double',
  '{n} utenze':
    '{n} accounts',
  '{n} utenze attive hanno una password che non scade.':
    '{n} enabled accounts have a password that never expires.',
  'Postura delle utenze: un punteggio (le utenze attive senza problemi, le privilegiate contano doppio), i grafici di stato, ultimo accesso ed età della password, e una scheda per ogni problema — utenze zombie (attive ma senza accessi oltre la soglia delle Impostazioni), mai usate, password che non scade, password vecchie, disattivate ma ancora in un gruppo privilegiato, bloccate — con perché conta, come migliorare e l’elenco delle utenze da sistemare. Con gli utenti non privilegiati accesi l’agente del domain controller manda ogni ora tutte le utenze del dominio con i loro gruppi, altrimenti solo le privilegiate.':
    'Account posture: a score (the enabled accounts without problems, privileged ones count double), the charts of state, last logon and password age, and a tab for each problem — zombie accounts (enabled but without logons beyond the threshold in the Settings), never used, password that never expires, old passwords, disabled but still in a privileged group, locked — with why it matters, how to improve and the list of accounts to fix. With the non-privileged users on, the domain controller agent sends every hour all the domain’s accounts with their groups, otherwise only the privileged ones.',
  'Nella scheda di una persona la sezione Gruppi elenca i gruppi delle sue utenze, diretti o tramite un altro gruppo, con quelli privilegiati in evidenza.':
    'On a person’s page the Groups section lists the groups of their accounts, direct or through another group, with the privileged ones highlighted.',
  'Non compreso nella licenza':
    'Not included in the licence',
  'non compreso nella licenza':
    'not included in the licence',
  '{sistema} — non compreso nella licenza':
    '{sistema} — not included in the licence',
  'Pacchetti nei container ({n})':
    'Packages in containers ({n})',
  'Pacchetti vulnerabili dentro le immagini dei container che girano su questa macchina: il gestore dei pacchetti della macchina non li tocca. Si correggono ricostruendo l’immagine da una base aggiornata e ridistribuendo i container.':
    'Vulnerable packages inside the images of the containers running on this machine: the machine’s package manager does not touch them. They are fixed by rebuilding the image from an updated base and redeploying the containers.',
  'nel container':
    'in the container',
  'corretto in {v}':
    'fixed in {v}',
  'Nella scheda di una macchina i «Pacchetti nei container» sono a parte: vulnerabilità vere, ma dentro le immagini dei container che ci girano; il gestore dei pacchetti della macchina non le tocca, si correggono ricostruendo l’immagine. Non sono fra le patch di sistema né nei job.':
    'On a machine’s page the «Packages in containers» are kept apart: real vulnerabilities, but inside the images of the containers running there; the machine’s package manager does not touch them, they are fixed by rebuilding the image. They are not among the system patches nor in the jobs.',
  'Automazione':
    'Automation',
  'Credenziali e sonda':
    'Credentials and probe',
  'EDR/XDR':
    'EDR/XDR',
  'IPAM':
    'IPAM',
  'SIEM':
    'SIEM',
  'la prima idonea':
    'the first eligible one',
  'sorgenti':
    'sources',
  'dalla sede del cespite':
    'from the asset’s location',
  'dalla sede dei suoi elettromedicali':
    'from the location of its medical devices',
  'dall’Institution DICOM dei suoi elettromedicali':
    'from the DICOM Institution of its medical devices',
  'dall’HL7 facility che dichiara':
    'from the HL7 facility it declares',
  'La colonna Struttura dei sistemi clinici dice il presidio: la sede del cespite del sistema (la sede di primo livello, non il reparto), altrimenti la sede in cui sono registrati i suoi elettromedicali, l’Institution DICOM che dichiarano sul filo o l’HL7 facility che il sistema dichiara. Passandoci sopra si legge da dove viene.':
    'The Site column of the clinical systems gives the hospital: the location of the system’s asset (the top-level site, not the ward), otherwise the site its medical devices are recorded in, the DICOM Institution they declare on the wire or the HL7 facility the system declares. Hovering over it shows where it comes from.',
  'Aggiornare CensoOS adesso su {n} appliance? Ognuna si riavvia: la cattura si ferma per qualche minuto.':
    'Update CensoOS now on {n} appliances? Each one restarts: capture stops for a few minutes.',
  'Aggiornare CensoOS su {n} appliance nella prossima finestra di manutenzione? Ognuna si riavvierà.':
    'Update CensoOS on {n} appliances in the next maintenance window? Each one will restart.',
  'Riavviare adesso {n} appliance? La cattura si ferma per qualche minuto.':
    'Restart {n} appliances now? Capture stops for a few minutes.',
  'Riavviare {n} appliance nella prossima finestra di manutenzione?':
    'Restart {n} appliances in the next maintenance window?',
  'Richiesta inviata: le appliance la ricevono al prossimo battito.':
    'Request sent: the appliances receive it with their next heartbeat.',
  'Richiesta programmata nella finestra di manutenzione.':
    'Request scheduled in the maintenance window.',
  'Annullare la richiesta per {nome}?':
    'Cancel the request for {nome}?',
  'Richiesta annullata.':
    'Request cancelled.',
  'CensoOS':
    'CensoOS',
  'aggiornata':
    'up to date',
  'Richiesta / esito':
    'Request / outcome',
  'Automatico':
    'Automatic',
  'CensoOS delle appliance':
    'CensoOS on the appliances',
  'Il sistema delle appliance fisiche: si aggiorna da qui (solo pacchetti firmati, con ritorno alla versione precedente se la sonda non torna), subito o nella finestra di manutenzione. Con l’automatico acceso lo fa il Control Plane da solo nella finestra, una sonda per gruppo alla volta, e si ferma dopo due fallimenti sulla stessa versione.':
    'The system of the physical appliances: it is updated from here (signed packages only, back to the previous version if the probe does not come back), now or in the maintenance window. With the automatic mode on, the Control Plane does it by itself in the window, one probe per group at a time, and stops after two failures on the same version.',
  'Non inviata a:':
    'Not sent to:',
  'Aggiornamenti automatici fermi: CensoOS {v} è fallito due volte in una settimana. Guarda l’esito sulle appliance; da qui si può ancora chiedere a mano.':
    'Automatic updates stopped: CensoOS {v} failed twice in a week. Check the outcome on the appliances; it can still be requested by hand from here.',
  'Versione disponibile':
    'Available version',
  'Richieste in corso':
    'Requests under way',
  'Nessun aggiornamento di CensoOS su questa piattaforma.':
    'No CensoOS update on this platform.',
  'Finestra di manutenzione aperta fino alle {fine}.':
    'Maintenance window open until {fine}.',
  'Prossima finestra di manutenzione: {inizio} – {fine}.':
    'Next maintenance window: {inizio} – {fine}.',
  'Finestra e automatico':
    'Window and automatic mode',
  '{n} appliance con CensoOS 0.1.0: la prima volta si aggiornano dal loro menu (Updates → Update CensoOS), poi da qui.':
    '{n} appliances with CensoOS 0.1.0: the first time they are updated from their menu (Updates → Update CensoOS), then from here.',
  '{n} appliance selezionate':
    '{n} appliances selected',
  'Aggiorna nella finestra':
    'Update in the window',
  'Aggiorna adesso':
    'Update now',
  'Riavvia nella finestra':
    'Restart in the window',
  'Riavvia adesso':
    'Restart now',
  'Automatico acceso.':
    'Automatic mode on.',
  'Automatico: accendi':
    'Automatic: turn on',
  'Automatico spento.':
    'Automatic mode off.',
  'Automatico: spegni':
    'Automatic: turn off',
  'appliance':
    'appliances',
  '{v} disponibile':
    '{v} available',
  'dal menu dell’appliance, la prima volta':
    'from the appliance’s menu, the first time',
  'dalle {ora}':
    'from {ora}',
  'annulla':
    'cancel',
  'riavvio in corso':
    'restarting',
  'verifica della sonda':
    'checking the probe',
  'tornato alla versione precedente':
    'back to the previous version',
  'senza esito':
    'no outcome',
  'non è un’appliance CensoOS':
    'not a CensoOS appliance',
  'CensoOS 0.1.0: aggiornala una volta dal suo menu':
    'CensoOS 0.1.0: update it once from its menu',
  'sonda disattivata o revocata':
    'probe disabled or revoked',
  'c’è già una richiesta':
    'a request is already there',
  'nessun aggiornamento sulla piattaforma':
    'no update on the platform',
  'sonda non trovata':
    'probe not found',
  'Finestra di manutenzione':
    'Maintenance window',
  'Appliance insieme, al più':
    'Appliances at once, at most',
  'Con l’automatico, riavvia anche quando Debian lo chiede (kernel nuovo)':
    'With the automatic mode, also restart when Debian asks for it (new kernel)',
  'Una finestra può attraversare la mezzanotte (es. 22:00–02:00). Una richiesta che la sonda non prende nella finestra passa alla successiva.':
    'A window can cross midnight (e.g. 22:00–02:00). A request the probe does not take in the window moves to the next one.',
  'Non inviata:':
    'Not sent:',
  'Aggiornare CensoOS su {nome}{quando}? L’appliance si riavvia.':
    'Update CensoOS on {nome}{quando}? The appliance restarts.',
  'adesso':
    'now',
  'nella prossima finestra di manutenzione':
    'in the next maintenance window',
  'Riavviare {nome}{quando}? La cattura si ferma per qualche minuto.':
    'Restart {nome}{quando}? Capture stops for a few minutes.',
  'Richiesta inviata.':
    'Request sent.',
  'Disponibile':
    'Available',
  'Automatico nella finestra':
    'Automatic in the window',
  'Questa appliance ha CensoOS 0.1.0: la prima volta si aggiorna dal suo menu (Updates → Update CensoOS); da lì in poi da qui.':
    'This appliance has CensoOS 0.1.0: the first time it is updated from its menu (Updates → Update CensoOS); from then on from here.',
  'manifesto aggiornato':
    'manifest updated',
  'CensoOS, gli aggiornamenti delle appliance':
    'CensoOS, the appliances’ updates',
  'Il sistema delle appliance fisiche CENSO: questa installazione tiene una copia dell’ultima release (pacchetto, manifesto e firma) e la serve alle appliance sul loro canale. La firma la verifica l’appliance; qui si controlla che pacchetto e manifesto combacino. I tenant le aggiornano da Sonde → «CensoOS delle appliance», anche da sole nella finestra di manutenzione.':
    'The system of the physical CENSO appliances: this installation keeps a copy of the latest release (package, manifest and signature) and serves it to the appliances on their channel. The appliance verifies the signature; here the package and the manifest are checked to match. The tenants update them from Probes → «CensoOS on the appliances», also by themselves in the maintenance window.',
  'Versione servita:':
    'Version served:',
  'Nessun aggiornamento servito alle appliance:':
    'No update served to the appliances:',
  'senza token':
    'without token',
  'Nessuna sorgente: imposta CENSO_CENSOOS_UPDATE_REPO (e il token se il repository è privato).':
    'No source: set CENSO_CENSOOS_UPDATE_REPO (and the token if the repository is private).',
  'Rinnovo giornaliero spento (CENSO_CENSOOS_UPDATE_AUTO=true per accenderlo).':
    'Daily refresh off (CENSO_CENSOOS_UPDATE_AUTO=true to turn it on).',
  'Senza Internet: copia i tre file di una release (censoos_*_all.deb, censoos-update.txt e censoos-update.txt.sig) in {folder} sul volume dei firmware del Control Plane.':
    'Without Internet: copy the three files of a release (censoos_*_all.deb, censoos-update.txt and censoos-update.txt.sig) into {folder} on the Control Plane’s firmware volume.',
  'CensoOS delle appliance: aggiornamenti e riavvii':
    'CensoOS on the appliances: updates and restarts',
  'Sulle appliance fisiche (Censo Appliance) oltre alla sonda c’è il loro sistema, CensoOS. In Sonde, il riquadro «CensoOS delle appliance» mostra per ognuna la versione installata, quella disponibile sulla piattaforma, se Debian chiede un riavvio (un kernel nuovo) e l’esito dell’ultima richiesta.':
    'On the physical appliances (Censo Appliance), besides the probe there is their system, CensoOS. In Probes, the «CensoOS on the appliances» box shows for each one the installed version, the one available on the platform, whether Debian asks for a restart (a new kernel) and the outcome of the last request.',
  'Si selezionano le appliance e si sceglie «Aggiorna nella finestra», «Aggiorna adesso», «Riavvia nella finestra» o «Riavvia adesso», con una conferma: la richiesta arriva all’appliance con il battito della sonda, l’appliance installa solo pacchetti firmati, si riavvia e controlla che la sonda torni attiva; se dopo un aggiornamento la sonda non torna, rimette da sola la versione precedente. La riga segue le fasi (in attesa, in corso, riavvio, verifica) fino all’esito; una richiesta non ancora presa si annulla.':
    'Select the appliances and choose «Update in the window», «Update now», «Restart in the window» or «Restart now», with a confirmation: the request reaches the appliance with the probe’s heartbeat, the appliance installs signed packages only, restarts and checks that the probe comes back; if after an update the probe does not come back, it restores the previous version by itself. The row follows the phases (waiting, under way, restart, check) up to the outcome; a request not taken yet can be cancelled.',
  '«Finestra e automatico» imposta la finestra di manutenzione del tenant (giorni, ore, fuso; può attraversare la mezzanotte) e quante appliance insieme al più. Con «Automatico: accendi» sulle appliance scelte il Control Plane le aggiorna da solo nella finestra quando la piattaforma ha una versione nuova, e le riavvia quando Debian lo chiede: una sonda per gruppo alla volta, così una sede non resta mai senza sonde. Due fallimenti sulla stessa versione in una settimana fermano gli aggiornamenti automatici e il riquadro lo dice; da qui si può ancora chiedere a mano.':
    '«Window and automatic mode» sets the tenant’s maintenance window (days, hours, time zone; it can cross midnight) and how many appliances at once at most. With «Automatic: turn on» on the chosen appliances, the Control Plane updates them by itself in the window when the platform has a new version, and restarts them when Debian asks: one probe per group at a time, so a site is never left without probes. Two failures on the same version in a week stop the automatic updates and the box says so; from here it can still be requested by hand.',
  'Le appliance con CensoOS 0.1.0 non sanno ancora ricevere le richieste: la prima volta si aggiornano dal loro menu (Updates → Update CensoOS), poi da qui. Gli stessi comandi sono nella scheda della sonda, riquadro «CensoOS». La copia degli aggiornamenti la gestisce chi amministra la piattaforma, in Piattaforma → «CensoOS, gli aggiornamenti delle appliance».':
    'The appliances with CensoOS 0.1.0 cannot take requests yet: the first time they are updated from their menu (Updates → Update CensoOS), then from here. The same commands are on the probe’s page, «CensoOS» box. The copy of the updates is managed by whoever runs the platform, in Platform → «CensoOS, the appliances’ updates».',
  'Ciclo di vita delle patch':
    'Patch lifecycle',
  'Ogni patch mancante su ogni macchina, dalla prima volta che manca a quando è corretta: una patch è corretta quando resta assente per 12 ore (una scansione senza credenziali non conta), datata da quando è sparita; se torna, si riapre. Da qui il tempo medio di correzione (MTTR) e le nuove contro le corrette.':
    'Every missing patch on every machine, from the first time it is missing to when it is fixed: a patch is fixed when it stays absent for 12 hours (a scan without credentials does not count), dated from when it disappeared; if it comes back, it reopens. Hence the mean time to remediate (MTTR) and new against fixed.',
  'Nuove':
    'New',
  'Corrette':
    'Fixed',
  'Riaperte':
    'Reopened',
  'Aperte ora':
    'Open now',
  'MTTR':
    'MTTR',
  'mediana {n} giorni':
    'median {n} days',
  'Nuove e corrette, per settimana':
    'New and fixed, by week',
  'nuove':
    'new',
  'riaperte':
    'reopened',
  'corrette':
    'fixed',
  'Tempo di correzione per gravità':
    'Time to remediate by severity',
  'Media (giorni)':
    'Mean (days)',
  'Mediana (giorni)':
    'Median (days)',
  'Entro lo SLA':
    'Within SLA',
  'Aperte per età (giorni)':
    'Open by age (days)',
  'Aperte':
    'Open',
  'Corrette nel periodo':
    'Fixed in the period',
  'Nessuna patch mancante aperta.':
    'No open missing patch.',
  'Nessuna correzione nel periodo.':
    'No fix in the period.',
  'Aperta dal':
    'Open since',
  'Corretta il':
    'Fixed on',
  'Riaperture':
    'Reopenings',
  'corretta da CENSO':
    'fixed by CENSO',
  'corretta':
    'fixed',
  'macchina eliminata':
    'machine deleted',
  'assente, in conferma':
    'absent, being confirmed',
  'riaperta':
    'reopened',
  'rilevata':
    'detected',
  'Giorni aperta':
    'Days open',
  'Installata da':
    'Installed by',
  'intervento di CENSO':
    'CENSO intervention',
  'richiesto da {chi}':
    'requested by {chi}',
  'approvato da {chi} il {quando}':
    'approved by {chi} on {quando}',
  'installato il {quando}':
    'installed on {quando}',
  'Nessuna patch mancante registrata su questa macchina.':
    'No missing patch recorded on this machine.',
  'Storia delle patch':
    'Patch history',
  'Ogni patch che è mancata su questa macchina: da quando, quando è stata corretta, se è tornata.':
    'Every patch that was missing on this machine: since when, when it was fixed, whether it came back.',
  '«Ciclo di vita»: ogni patch mancante su ogni macchina dalla prima volta che manca a quando è corretta — nuove, corrette e riaperte nel periodo, il tempo medio e mediano di correzione (MTTR) per gravità con la quota entro lo SLA, le nuove contro le corrette settimana per settimana, le aperte per età. Una patch è corretta quando resta assente per 12 ore (così una scansione senza credenziali non conta come correzione), con la data di quando è sparita; se torna si riapre. Un clic su una patch apre la sua cronologia e, se l’ha installata CENSO, l’intervento con chi l’ha chiesto e approvato. Nella scheda della macchina, sezione Patching, «Storia delle patch» mostra la stessa storia per quella macchina.':
    '«Lifecycle»: every missing patch on every machine from the first time it is missing to when it is fixed — new, fixed and reopened in the period, the mean and median time to remediate (MTTR) by severity with the share within the SLA, new against fixed week by week, the open ones by age. A patch is fixed when it stays absent for 12 hours (so a scan without credentials does not count as a fix), dated from when it disappeared; if it comes back it reopens. A click on a patch opens its history and, if CENSO installed it, the intervention with who requested and approved it. On the machine’s page, Patching section, «Patch history» shows the same history for that machine.',
  'Politica SLA delle patch':
    'Patch SLA policy',
  'Entro quanti giorni una patch mancante va installata. Parco, catalogo, conformità per ambiente, report e ciclo di vita leggono questa politica: cambiarla cambia subito tutte le viste, anche la storia (l’età di una patch non cambia, la sua scadenza sì).':
    'Within how many days a missing patch must be installed. Fleet, catalogue, compliance by environment, reports and lifecycle read this policy: changing it changes every view at once, history included (a patch’s age does not change, its deadline does).',
  'Politica salvata.':
    'Policy saved.',
  'Patch sfruttate (CISA KEV), al più':
    'Exploited patches (CISA KEV), at most',
  'nessuna regola':
    'no rule',
  'Per ambiente (tag «{chiave}»)':
    'By environment (tag «{chiave}»)',
  'Solo i giorni che cambiano: un campo vuoto vale come sopra.':
    'Only the days that differ: an empty field counts as above.',
  'Aggiungi un ambiente':
    'Add an environment',
  'Sola lettura: la politica la cambia l’amministratore del tenant.':
    'Read only: the tenant administrator changes the policy.',
  'Politica SLA':
    'SLA policy',
  'Gli ambienti e le patch sfruttate possono avere tempi più stretti: si impostano in «Politica SLA».':
    'Environments and exploited patches can have shorter deadlines: they are set in «SLA policy».',
  '{p}° percentile':
    '{p}th percentile',
  'Rischio {n} su 100:':
    'Risk {n} out of 100:',
  'gravità {s}':
    'severity {s}',
  'sfruttata ora (CISA KEV)':
    'exploited now (CISA KEV)',
  'probabilità di sfruttamento {d}':
    'probability of exploitation {d}',
  'macchina di un servizio {c}':
    'machine of a {c} service',
  'in produzione':
    'in production',
  'esposta a Internet':
    'exposed to the Internet',
  'oltre lo SLA':
    'past the SLA',
  'EPSS, la probabilità di sfruttamento':
    'EPSS, the probability of exploitation',
  'Per ogni CVE la probabilità che venga sfruttata nei prossimi 30 giorni (FIRST): il Control Plane scarica il file una volta al giorno per tutti i tenant, e il rischio delle patch la usa insieme a gravità, CISA KEV e importanza della macchina.':
    'For each CVE, the probability that it is exploited in the next 30 days (FIRST): the Control Plane downloads the file once a day for every tenant, and the patch risk uses it together with severity, CISA KEV and how much the machine matters.',
  '{n} CVE con il punteggio del {giorno} (modello {m}), scaricato il {quando}.':
    '{n} CVEs with the score of {giorno} (model {m}), downloaded on {quando}.',
  'EPSS non ancora scaricato: il rischio delle patch non usa la probabilità di sfruttamento.':
    'EPSS not downloaded yet: the patch risk does not use the probability of exploitation.',
  'Ultimo tentativo non riuscito ({quando}): {errore}':
    'Last attempt failed ({quando}): {errore}',
  'Sorgente: {url}':
    'Source: {url}',
  'Scaricamento spento (CENSO_EPSS_URL vuoto).':
    'Download off (CENSO_EPSS_URL empty).',
  'Approvare «{nome}»? Da ora i suoi cicli partono da soli e gli interventi risultano approvati a tuo nome, citando la politica.':
    'Approve «{nome}»? From now on its cycles start by themselves and the interventions are approved in your name, citing the policy.',
  'Politica approvata.':
    'Policy approved.',
  'Avviare adesso un ciclo di «{nome}»? Parte la prima ondata.':
    'Start a cycle of «{nome}» now? The first ring starts.',
  'Ciclo avviato.':
    'Cycle started.',
  'Fermare il ciclo di «{nome}»? L’ondata in corso si annulla dove non è ancora partita, le successive non partono.':
    'Stop the cycle of «{nome}»? The ring under way is canceled where it has not started, the following ones do not start.',
  'Ciclo fermato.':
    'Cycle stopped.',
  'Eliminare la politica «{nome}» e la storia dei suoi cicli? Gli interventi già creati restano.':
    'Delete the policy «{nome}» and the history of its cycles? The interventions already created stay.',
  'Politica eliminata.':
    'Policy deleted.',
  'Politica':
    'Policy',
  'Ondate':
    'Rings',
  'attesa {n} giorni':
    'wait {n} days',
  'soglia {n} %':
    'threshold {n} %',
  'Prossimo ciclo':
    'Next cycle',
  'Approvazione':
    'Approval',
  'approvata':
    'approved',
  'da approvare':
    'to approve',
  'approvata da {chi} il {quando}':
    'approved by {chi} on {quando}',
  'cambiata: da riapprovare':
    'changed: approve again',
  'Ultimo ciclo':
    'Last cycle',
  'Ferma il ciclo':
    'Stop the cycle',
  'Politiche di rilascio':
    'Rollout policies',
  'Nuova politica':
    'New policy',
  'Cosa installare, su quali macchine e in che ordine (ondate: pilota, poi collaudo, poi produzione), e quando (a ogni Patch Tuesday, ogni settimana, ogni mese). Un amministratore la approva una volta; da lì ogni ciclo crea un intervento per ondata, approvato a suo nome, e l’ondata successiva parte solo se la precedente è riuscita sopra la sua soglia, dopo i giorni di attesa. Se la politica cambia, va riapprovata.':
    'What to install, on which machines and in which order (rings: pilot, then test, then production), and when (every Patch Tuesday, every week, every month). An administrator approves it once; from then on each cycle creates one intervention per ring, approved in their name, and the next ring starts only if the previous one succeeded above its threshold, after the waiting days. If the policy changes, it must be approved again.',
  'Nessuna politica di rilascio.':
    'No rollout policy.',
  '«Nuova politica» per il primo rilascio automatico: per esempio gli aggiornamenti di sicurezza due giorni dopo il Patch Tuesday, prima ai pilota.':
    '«New policy» for the first automatic rollout: for example the security updates two days after Patch Tuesday, first to the pilots.',
  'politiche':
    'policies',
  'aggiornamenti di sicurezza':
    'security updates',
  'tutti gli aggiornamenti':
    'all updates',
  'programmi di terze parti':
    'third-party programs',
  'patch {sev} o più gravi{kev}{rischio}':
    '{sev} or more severe patches{kev}{rischio}',
  'e tutte le sfruttate (KEV)':
    'and every exploited one (KEV)',
  'rischio ≥ {n}':
    'risk ≥ {n}',
  'ogni {g} alle {ora}':
    'every {g} at {ora}',
  'il giorno {d} di ogni mese alle {ora}':
    'on day {d} of every month at {ora}',
  '{n} giorni dopo il Patch Tuesday, alle {ora}':
    '{n} days after Patch Tuesday, at {ora}',
  'il Patch Tuesday, alle {ora}':
    'on Patch Tuesday, at {ora}',
  'concluso':
    'completed',
  'fermato':
    'stopped',
  'niente da installare':
    'nothing to install',
  'Nessun ciclo ancora.':
    'No cycle yet.',
  '{n} macchine':
    '{n} machines',
  'riuscita {n} %':
    'success {n} %',
  '{n} fallite':
    '{n} failed',
  '{n} bloccate':
    '{n} blocked',
  'Salvata. Se è cambiato cosa si installa, dove o quando, va riapprovata.':
    'Saved. If what is installed, where or when changed, it must be approved again.',
  'Salvata: ora va approvata.':
    'Saved: now it must be approved.',
  'Politica di rilascio':
    'Rollout policy',
  'Nuova politica di rilascio':
    'New rollout policy',
  'Da installare':
    'To install',
  'le patch che rispondono ai criteri':
    'the patches matching the criteria',
  'Gravità minima':
    'Minimum severity',
  'Rischio minimo (0–100)':
    'Minimum risk (0–100)',
  'Anche le sfruttate (CISA KEV), qualunque gravità':
    'Also the exploited ones (CISA KEV), whatever the severity',
  'Tipi di patch':
    'Patch types',
  'KB di Windows':
    'Windows KBs',
  'pacchetti del sistema':
    'system packages',
  'programmi installati':
    'installed programs',
  'Dove, in che ordine':
    'Where, in which order',
  'Ogni ondata prende le macchine con il suo tag. L’ondata successiva parte dopo i giorni di attesa, solo se questa è riuscita almeno per la soglia (le macchine bloccate — senza agente, senza tag d’ambiente — non contano).':
    'Each ring takes the machines with its tag. The next ring starts after the waiting days, only if this one succeeded at least for the threshold (blocked machines — no agent, no environment tag — do not count).',
  'Ondata':
    'Ring',
  'Tag delle macchine':
    'Machines’ tag',
  'Attesa (giorni)':
    'Wait (days)',
  'Attesa dopo la precedente (giorni)':
    'Wait after the previous (days)',
  'Soglia di riuscita (%)':
    'Success threshold (%)',
  'Ondata {n}':
    'Ring {n}',
  'Aggiungi un’ondata':
    'Add a ring',
  'Cadenza':
    'Cadence',
  'dopo il Patch Tuesday (secondo martedì del mese)':
    'after Patch Tuesday (second Tuesday of the month)',
  'ogni settimana':
    'every week',
  'ogni mese':
    'every month',
  'Giorni dopo':
    'Days after',
  'Giorno del mese':
    'Day of the month',
  'nessuna: si installa all’avvio dell’ondata':
    'none: installed when the ring starts',
  'Riavvia le macchine se serve':
    'Restart the machines if needed',
  'Preavviso (minuti)':
    'Notice (minutes)',
  'Accesa':
    'On',
  'Politiche':
    'Policies',
  '«Politiche»: i rilasci automatici. Una politica dice cosa installare (aggiornamenti di sicurezza, tutti, programmi di terze parti, oppure le patch con almeno una gravità, le sfruttate, un rischio minimo), su quali macchine e in che ordine — le ondate, una per tag: pilota, poi collaudo, poi produzione, con i giorni di attesa e la soglia di riuscita di ognuna — e quando: qualche giorno dopo il Patch Tuesday, ogni settimana o ogni mese, nella finestra di manutenzione scelta. Un amministratore la approva una volta: da lì ogni ciclo crea un intervento per ondata, approvato a suo nome citando la politica, e l’ondata successiva parte solo se la precedente è riuscita almeno per la soglia; se no il ciclo si ferma e dice perché. Cambiare cosa, dove o quando toglie l’approvazione. «Avvia adesso» lancia un ciclo senza aspettare, «Ferma il ciclo» lo interrompe; aprendo la riga si vede la storia dei cicli con gli interventi di ogni ondata.':
    '«Policies»: the automatic rollouts. A policy says what to install (security updates, all, third-party programs, or the patches with at least a severity, the exploited ones, a minimum risk), on which machines and in which order — the rings, one per tag: pilot, then test, then production, each with its waiting days and success threshold — and when: a few days after Patch Tuesday, every week or every month, in the chosen maintenance window. An administrator approves it once: from then on each cycle creates one intervention per ring, approved in their name citing the policy, and the next ring starts only if the previous one succeeded at least for the threshold; otherwise the cycle stops and says why. Changing what, where or when takes the approval away. «Start now» launches a cycle without waiting, «Stop the cycle» interrupts it; opening the row shows the history of the cycles with the interventions of each ring.',
  'controllo compensativo':
    'compensating control',
  'non correggibile':
    'not fixable',
  'attiva, scade fra {n} giorni':
    'active, expires in {n} days',
  'rifiutata':
    'rejected',
  'Revocare l’eccezione su {p}? La patch torna subito fra quelle da installare.':
    'Revoke the exception on {p}? The patch is back at once among those to install.',
  'Eccezione revocata.':
    'Exception revoked.',
  'Chiedere il rinnovo per {n} giorni dell’eccezione su {p}? Un amministratore dovrà approvarlo.':
    'Request the renewal for {n} days of the exception on {p}? An administrator will have to approve it.',
  'Rinnovo richiesto.':
    'Renewal requested.',
  'Eccezioni':
    'Exceptions',
  'Una patch che non si installa per una ragione — rischio accettato, controllo compensativo, falso positivo, non correggibile — su tutte le macchine o su alcune, fino a una scadenza. La chiede chiunque possa scrivere nel Patching (dal catalogo, «Chiedi un’eccezione»), la approva o la rifiuta un amministratore diverso da chi l’ha chiesta. Finché vale, la patch su quelle macchine non conta negli SLA, nella conformità, nelle priorità e nelle politiche; 14 giorni prima della scadenza parte una mail a chi l’ha chiesta e approvata.':
    'A patch that is not installed for a reason — risk accepted, compensating control, false positive, not fixable — on every machine or on some, until an expiry. Anyone who can write in Patching requests it (from the catalogue, «Request an exception»), an administrator other than the requester approves or rejects it. While it holds, the patch on those machines does not count in the SLA, the compliance, the priorities and the policies; 14 days before the expiry a mail goes to whoever requested and approved it.',
  'Attive':
    'Active',
  'Impostazione salvata.':
    'Setting saved.',
  'Chi chiede può anche approvare (senza quattro occhi)':
    'The requester can also approve (no four eyes)',
  'Confermi l’approvazione? Fino al {d} la patch non conterà su quelle macchine.':
    'Confirm the approval? Until {d} the patch will not count on those machines.',
  'Confermi il rifiuto?':
    'Confirm the rejection?',
  'Eccezione approvata.':
    'Exception approved.',
  'Eccezione rifiutata.':
    'Exception rejected.',
  'Approva l’eccezione su {p}':
    'Approve the exception on {p}',
  'Rifiuta l’eccezione su {p}':
    'Reject the exception on {p}',
  'Nota (facoltativa)':
    'Note (optional)',
  'Perché la rifiuti':
    'Why you reject it',
  'Nessuna richiesta da decidere.':
    'No request to decide.',
  'Nessuna eccezione.':
    'No exception.',
  'eccezioni':
    'exceptions',
  'Chiedi un’eccezione':
    'Request an exception',
  'Eccezione su {p}':
    'Exception on {p}',
  'Durata (giorni, al più 365)':
    'Duration (days, at most 365)',
  'es. il fornitore del PACS non certifica ancora la versione nuova; la porta è chiusa dal firewall':
    'e.g. the PACS vendor does not certify the new version yet; the port is closed by the firewall',
  'Su tutte le macchine a cui manca (anche quelle che si aggiungeranno)':
    'On every machine missing it (also those that will be added)',
  'La richiesta va approvata da un amministratore diverso da te; finché non è approvata la patch conta come prima.':
    'The request must be approved by an administrator other than you; until it is approved the patch counts as before.',
  'Chiedi l’eccezione':
    'Request the exception',
  'Eccezione chiesta: la trovi in «Eccezioni», da approvare.':
    'Exception requested: you find it in «Exceptions», to approve.',
  'rischio accettato, controllo compensativo, falso positivo: va approvata da un amministratore':
    'risk accepted, compensating control, false positive: an administrator approves it',
  '«Eccezioni»: una patch che non si installa per una ragione — rischio accettato, controllo compensativo, falso positivo, non correggibile —, su tutte le macchine o solo su alcune, fino a una scadenza (al più un anno). La chiede chi scrive nel Patching, dalla riga della patch nel catalogo («Chiedi un’eccezione»), con il motivo; la approva o la rifiuta un amministratore diverso da chi l’ha chiesta (i quattro occhi si possono spegnere per il tenant). Finché vale, quella patch su quelle macchine non conta negli SLA, nella conformità, nelle priorità e nelle politiche, e i report la elencano fra i rischi accettati con chi l’ha chiesta e approvata. Quattordici giorni prima della scadenza arriva una mail; alla scadenza la patch torna ovunque. Un’eccezione si rinnova (una richiesta nuova da approvare) o si revoca.':
    '«Exceptions»: a patch that is not installed for a reason — risk accepted, compensating control, false positive, not fixable —, on every machine or only on some, until an expiry (one year at most). Whoever writes in Patching requests it, from the patch’s row in the catalogue («Request an exception»), with the reason; an administrator other than the requester approves or rejects it (the four eyes can be switched off for the tenant). While it holds, that patch on those machines does not count in the SLA, the compliance, the priorities and the policies, and the reports list it among the accepted risks with who requested and approved it. Fourteen days before the expiry a mail arrives; at the expiry the patch is back everywhere. An exception is renewed (a new request to approve) or revoked.',
  'superata da {kb}':
    'superseded by {kb}',
  'superata da una cumulativa più nuova':
    'superseded by a newer cumulative update',
  'KB cumulative sostituite da una più nuova: non contano come corrette':
    'cumulative KBs replaced by a newer one: they do not count as fixed',
  'Le KB di Windows sono cumulative: quando ne esce una nuova e la macchina è ancora indietro, quella del mese prima risulta «superata», non corretta, e il tempo di correzione continua a contare sulla nuova.':
    'Windows KBs are cumulative: when a new one comes out and the machine is still behind, the previous month’s one is «superseded», not fixed, and the time to remediate keeps counting on the new one.',
  'Aprire un ticket per {p} su {n} macchine nel sistema di gestione?':
    'Open a ticket for {p} on {n} machines in the service management system?',
  'Ticket aperto: {n}.':
    'Ticket opened: {n}.',
  'Ticket aperto:':
    'Ticket open:',
  'Apri un ticket':
    'Open a ticket',
  'chiuso da CENSO':
    'closed by CENSO',
  'non aperto':
    'not opened',
  'Ticket':
    'Tickets',
  'Aperto':
    'Opened',
  'dalla regola':
    'by the rule',
  'Chiuso':
    'Closed',
  'Ticket sul sistema di gestione':
    'Tickets on the service management system',
  'Un ticket per patch su ServiceNow o Jira, con il connettore che il tenant ha già (Integrazioni → Connettori): cosa installare, su quali macchine, perché (CVE, rischio, EPSS) ed entro quando (lo SLA), con il collegamento alla console. Si apre dalla riga della patch nel catalogo o lo apre la regola qui sotto; CENSO lo chiude da solo quando la patch non manca più su nessuna delle macchine del ticket.':
    'One ticket per patch on ServiceNow or Jira, with the connector the tenant already has (Integrations → Connectors): what to install, on which machines, why (CVE, risk, EPSS) and by when (the SLA), with the link to the console. It is opened from the patch’s row in the catalogue or by the rule below; CENSO closes it by itself when the patch is no longer missing on any of the ticket’s machines.',
  'Connettore':
    'Connector',
  'nessuno: niente ticket':
    'none: no tickets',
  'Campi in più (uno per riga, campo=valore)':
    'Extra fields (one per line, field=value)',
  'Progetto (chiave)':
    'Project (key)',
  'Tipo di issue':
    'Issue type',
  'Etichette (virgola)':
    'Labels (comma)',
  'Priorità dalla gravità (Highest, High, Medium, Low)':
    'Priority from the severity (Highest, High, Medium, Low)',
  'Regola: apri da solo':
    'Rule: open by itself',
  'Apri un ticket per ogni patch che risponde alla regola (al più 20 per giro)':
    'Open a ticket for every patch matching the rule (at most 20 per round)',
  'anche le sfruttate (KEV)':
    'also the exploited ones (KEV)',
  'solo quelle già oltre lo SLA':
    'only those already past the SLA',
  'Ticket aperti da CENSO':
    'Tickets opened by CENSO',
  'Nessun ticket ancora.':
    'No ticket yet.',
  'ticket':
    'tickets',
  '«Ticket»: ogni patch può diventare un ticket su ServiceNow (un incident, o la tabella che scegli) o su Jira (una issue nel progetto scelto), con il connettore che il tenant ha già: dice cosa installare, su quali macchine, quanto è grave, entro quando, e porta alla console. Si apre dalla riga della patch nel catalogo («Apri un ticket») o lo apre da sé la regola (per esempio tutte le critiche e le sfruttate); quando la patch non manca più su nessuna di quelle macchine, CENSO chiude il ticket con una nota.':
    '«Tickets»: every patch can become a ticket on ServiceNow (an incident, or the table you choose) or on Jira (an issue in the chosen project), with the connector the tenant already has: it says what to install, on which machines, how severe, by when, and leads to the console. It is opened from the patch’s row in the catalogue («Open a ticket») or the rule opens it by itself (for example all the critical and exploited ones); when the patch is no longer missing on any of those machines, CENSO closes the ticket with a note.',
  'credenziali':
    'credentials',
  'lettura vecchia':
    'stale reading',
  'solo dalla rete':
    'network only',
  'sistema non riconosciuto':
    'system not recognised',
  'rifai la scansione con le credenziali o controlla l’agente: senza una lettura recente le patch mancanti non sono aggiornate':
    'scan again with credentials or check the agent: without a recent reading the missing patches are not up to date',
  'installa l’agente CENSO o dai al job una credenziale SSH/WinRM: dalla rete non si vedono le patch':
    'install the CENSO agent or give the job an SSH/WinRM credential: patches cannot be seen from the network',
  'il sistema non risulta Windows né Linux: una scansione con credenziali lo riconosce':
    'the system is neither Windows nor Linux: a scan with credentials recognises it',
  'Letta dall’interno':
    'Read from inside',
  'Un report delle patch vale quanto le macchine che vede: un server letto solo dalla rete non ha patch mancanti perché nessuno ha guardato dentro, non perché è aggiornato. Qui ogni computer del CMDB con la sua lettura: con l’agente, con credenziali negli ultimi {n} giorni, una lettura vecchia, solo dalla rete, sistema non riconosciuto.':
    'A patch report is only as good as the machines it sees: a server read only from the network has no missing patches because nobody looked inside, not because it is up to date. Here every computer of the CMDB with its reading: with the agent, with credentials in the last {n} days, a stale reading, network only, system not recognised.',
  'Coperte':
    'Covered',
  '{c} di {n} computer':
    '{c} of {n} computers',
  'Con l’agente':
    'With the agent',
  'Lettura vecchia':
    'Stale reading',
  'Solo dalla rete':
    'Network only',
  'Sistema non riconosciuto':
    'System not recognised',
  'Coperti':
    'Covered',
  'senza ambiente':
    'no environment',
  'Computer e lettura':
    'Computers and reading',
  '«Copertura»: quali computer CENSO sta davvero valutando. Un server letto solo dalla rete non ha patch mancanti perché nessuno ha guardato dentro, non perché è aggiornato: qui ogni computer ha la sua lettura (con l’agente, con credenziali di recente, una lettura vecchia, solo dalla rete, sistema non riconosciuto), la percentuale coperta per ambiente e, riga per riga, che cosa fare.':
    '«Coverage»: which computers CENSO is really assessing. A server read only from the network has no missing patches because nobody looked inside, not because it is up to date: here every computer has its reading (with the agent, with recent credentials, a stale reading, network only, system not recognised), the share covered by environment and, row by row, what to do.',
  'Copertura del patching':
    'Patching coverage',
  'computer letti dall’interno':
    'computers read from inside',
  '{n} solo dalla rete, {v} con la lettura vecchia':
    '{n} network only, {v} with a stale reading',
  'Entro lo SLA, per ambiente':
    'Within the SLA, by environment',
  '{n} corrette':
    '{n} fixed',
  'Tempo medio di correzione':
    'Mean time to remediate',
  'Patch nuove e corrette':
    'New and fixed patches',
  'rischio {r} × {n} macchine':
    'risk {r} × {n} machines',
  'Le patch che tolgono più rischio':
    'The patches that take away most risk',
  'Eccezioni sulle patch':
    'Exceptions on patches',
  'da decidere':
    'to decide',
  '{n} attive, {s} scadono entro 14 giorni':
    '{n} active, {s} expire within 14 days',
  'Quanti computer CENSO legge dall’interno, e quanti solo dalla rete.':
    'How many computers CENSO reads from inside, and how many only from the network.',
  'La quota di macchine con le patch entro i tempi, ambiente per ambiente.':
    'The share of machines with their patches on time, environment by environment.',
  'In quanti giorni si correggono le patch, per gravità.':
    'In how many days patches are fixed, by severity.',
  'Settimana per settimana, quante patch mancanti nascono e quante si chiudono.':
    'Week by week, how many missing patches appear and how many close.',
  'Le dieci patch che, installate, tolgono più rischio dal parco.':
    'The ten patches that, once installed, take the most risk away from the fleet.',
  'Le richieste da decidere e quelle in scadenza.':
    'The requests to decide and those expiring.',
  'Nella dashboard della home e nei cruscotti, «Personalizza» → gruppo «Patching» aggiunge i widget del patching: copertura, entro lo SLA per ambiente, tempo medio di correzione, nuove e corrette per settimana, le patch che tolgono più rischio, le eccezioni da decidere.':
    'In the home dashboard and in the cockpits, «Customise» → group «Patching» adds the patching widgets: coverage, within the SLA by environment, mean time to remediate, new and fixed by week, the patches that take away most risk, the exceptions to decide.',
  'Sintesi direzionale':
    'Executive summary',
  'Report completo':
    'Full report',
  'Ai responsabili delle macchine':
    'To the machines’ owners',
  'Mandare adesso «{nome}» ai suoi destinatari?':
    'Send «{nome}» to its recipients now?',
  'Inviato: {esito}':
    'Sent: {esito}',
  'Eliminare il report pianificato «{nome}»?':
    'Delete the scheduled report «{nome}»?',
  'Report pianificato eliminato.':
    'Scheduled report deleted.',
  'A chi':
    'To whom',
  'ai responsabili, riepilogo a {n}':
    'to the owners, summary to {n}',
  'Prossimo invio':
    'Next send',
  'Invia adesso':
    'Send now',
  'Report pianificati':
    'Scheduled reports',
  'Nuovo report pianificato':
    'New scheduled report',
  'Un report che parte da solo per mail, ogni settimana, ogni mese o dopo il Patch Tuesday: la sintesi direzionale, il report completo, oppure una mail per ogni responsabile delle macchine (il proprietario dell’asset, se è un indirizzo e-mail) con le patch che mancano sulle sue, le più gravi prima; i destinatari ricevono chi ha avuto cosa e le macchine senza responsabile.':
    'A report that goes by mail by itself, every week, every month or after Patch Tuesday: the executive summary, the full report, or one mail per machine owner (the asset’s owner, when it is an e-mail address) with the patches missing on theirs, the most severe first; the recipients get who received what and the machines without an owner.',
  'Nessun report pianificato.':
    'No scheduled report.',
  'Report pianificato':
    'Scheduled report',
  'Periodo (giorni)':
    'Period (days)',
  'A chi mandare il riepilogo (e-mail, separate da virgola)':
    'Who gets the summary (e-mails, comma-separated)',
  'Destinatari (e-mail, separate da virgola)':
    'Recipients (e-mails, comma-separated)',
  'Acceso':
    'On',
  '«Report pianificati» (sezione Report): un report che parte da solo per mail ogni settimana, ogni mese o dopo il Patch Tuesday — la sintesi direzionale, il report completo, oppure una mail a ogni responsabile delle macchine (il proprietario dell’asset, se è un indirizzo e-mail) con le patch che mancano sulle sue; chi riceve il riepilogo vede chi ha avuto cosa e le macchine senza responsabile. «Invia adesso» lo manda subito, per vedere com’è.':
    '«Scheduled reports» (Reports section): a report that goes by mail by itself every week, every month or after Patch Tuesday — the executive summary, the full report, or a mail to each machine owner (the asset’s owner, when it is an e-mail address) with the patches missing on theirs; whoever gets the summary sees who received what and the machines without an owner. «Send now» sends it at once, to see how it looks.',
  'aperta, con eccezione':
    'open, with exception',
  'superata':
    'superseded',
  'Rilevata il':
    'Detected on',
  '{n} di {s} giorni':
    '{n} of {s} days',
  'approvata da {chi}':
    'approved by {chi}',
  'verifica: {v}':
    'check: {v}',
  'Eccezione':
    'Exception',
  'Evidenza per l’audit':
    'Audit evidence',
  'Per una CVE (CVE-2026-1234) o una patch (kb:KB5031234, pkg:openssl): ogni macchina che ne è stata colpita, quando è stata trovata, chi ha chiesto e approvato l’installazione, quando è stata installata e verificata, l’eccezione e il ticket se c’erano, e la cronologia. È la prova da dare a un revisore (NIS2, DORA), in Excel o PDF.':
    'For a CVE (CVE-2026-1234) or a patch (kb:KB5031234, pkg:openssl): every machine it hit, when it was found, who requested and approved the installation, when it was installed and verified, the exception and the ticket if any, and the history. It is the proof to give an auditor (NIS2, DORA), in Excel or PDF.',
  'CVE o patch':
    'CVE or patch',
  'Nessuna macchina risulta aver avuto questa vulnerabilità o questa patch mancante.':
    'No machine appears to have had this vulnerability or this missing patch.',
  '«Evidenza per l’audit» (sezione Report): scrivi una CVE o una patch e hai, per ogni macchina colpita, quando è stata trovata, chi ha chiesto e approvato l’installazione, quando è stata installata e verificata, l’eccezione e il ticket se c’erano, con la cronologia; si scarica in Excel o PDF per il revisore (NIS2, DORA).':
    '«Audit evidence» (Reports section): type a CVE or a patch and get, for every machine hit, when it was found, who requested and approved the installation, when it was installed and verified, the exception and the ticket if any, with the history; it downloads as Excel or PDF for the auditor (NIS2, DORA).',
  'Cicli delle politiche':
    'Policy cycles',
  'Cicli delle politiche di rilascio':
    'Rollout policy cycles',
  'Ciclo di vita e governo':
    'Lifecycle and governance',
  'Eccezioni attive':
    'Active exceptions',
  'Iniziato':
    'Started',
  'Le patch di chi è visto solo dalla rete o ha una lettura più vecchia di {n} giorni non sono valutate.':
    'The patches of machines seen only from the network, or read more than {n} days ago, are not assessed.',
  'Mediana':
    'Median',
  'Nessun ciclo nel periodo.':
    'No cycle in the period.',
  'Nessun computer.':
    'No computer.',
  'Nessuna patch mancante nuova o corretta nel periodo.':
    'No missing patch new or fixed in the period.',
  'Patch mancanti corrette':
    'Missing patches fixed',
  'Patch mancanti nuove':
    'New missing patches',
  'Patch mancanti nuove e corrette, settimana per settimana':
    'Missing patches new and fixed, week by week',
  'Rischio × macchine':
    'Risk × machines',
  'con credenziali':
    'with credentials',
  'con l’agente':
    'with the agent',
  '{n} da decidere, {s} in scadenza':
    '{n} to decide, {s} expiring',
  '{n} fermati, {t} ticket aperti':
    '{n} stopped, {t} tickets opened',
  '{n} su {m} computer letti dall’interno':
    '{n} of {m} computers read from inside',
  '{n} tornate':
    '{n} came back',
  '{n}% entro lo SLA':
    '{n}% within the SLA',
  'Report e sintesi portano anche il ciclo di vita e il governo del periodo: patch mancanti nuove e corrette settimana per settimana, il tempo medio di correzione per gravità con la quota entro lo SLA, la copertura (quanti computer CENSO legge davvero dall’interno), le dieci patch che tolgono più rischio, le eccezioni attive, da decidere e in scadenza, i cicli delle politiche di rilascio con le loro ondate e i ticket aperti; nel report completo anche la copertura macchina per macchina.':
    'Reports and summaries also carry the lifecycle and the governance of the period: missing patches new and fixed week by week, the mean time to remediate by severity with the share within the SLA, the coverage (how many computers CENSO really reads from inside), the ten patches that take away most risk, the exceptions active, to decide and expiring, the rollout policies\' cycles with their rings and the tickets opened; in the full report also the coverage machine by machine.',
  'Andamento del rischio':
    'Risk trend',
  'Il rischio totale del parco giorno per giorno: scende quando le patch che contano vengono installate.':
    'The fleet\'s total risk day by day: it goes down when the patches that matter are installed.',
  'Rischio totale':
    'Total risk',
  'il rischio di ogni patch mancante su ogni macchina, sommato':
    'the risk of every missing patch on every machine, summed',
  'rischio totale':
    'total risk',
  'tutto il parco':
    'the whole fleet',
  '{n} macchine a rischio urgente':
    '{n} machines at urgent risk',
  '{p}% nel periodo, {n} macchine a rischio urgente':
    '{p}% in the period, {n} machines at urgent risk',
  'Il selettore «Ambiente» fa il report di un ambiente solo (produzione, collaudo… o le macchine senza ambiente): numeri, grafici, tabelle e file scaricati riguardano solo le sue macchine, e lo stesso vale per un report pianificato. Il «Rischio totale» somma il rischio di ogni patch mancante su ogni macchina: CENSO lo fotografa ogni giorno, così il report mostra quanto è sceso o salito nel periodo e il suo andamento, che è anche un widget della dashboard («Andamento del rischio»).':
    'The «Environment» selector makes the report of one environment only (production, test… or the machines without an environment): numbers, charts, tables and downloaded files cover only its machines, and the same holds for a scheduled report. The «Total risk» adds up the risk of every missing patch on every machine: CENSO takes a snapshot of it every day, so the report shows how much it went down or up in the period and its trend, which is also a dashboard widget («Risk trend»).',
  'Collegamento per la nuova password mandato a {email}.':
    'Link for the new password sent to {email}.',
  'Invito rimandato a {email}.':
    'Invitation sent again to {email}.',
  'Link password':
    'Password link',
  'link password':
    'password link',
  'Mandare a {email} un collegamento per scegliere la password? Se ne ha già una, il collegamento vale un’ora e quella attuale resta valida finché non la cambia.':
    'Send {email} a link to choose the password? If they already have one, the link is valid for one hour and the current password stays valid until they change it.',
  'Password cambiata: entra con quella nuova. Le sessioni aperte prima non vengono rinnovate.':
    'Password changed: sign in with the new one. The sessions opened before are not renewed.',
  'Scegli la nuova password':
    'Choose the new password',
  'password impostata dall’invito':
    'password set from the invitation',
  'password reimpostata dal collegamento':
    'password reset from the link',
  'Chi ha questo link può scegliere la password di {email}: consegnalo solo a questa persona, per un canale sicuro. Lo vedi solo adesso; i link precedenti non valgono più. La generazione è nel registro degli accessi.':
    'Whoever has this link can choose the password of {email}: hand it only to this person, through a safe channel. You see it only now; the previous links no longer work. Generating it is in the access log.',
  'Genera e mostra il link':
    'Generate and show the link',
  'Generare un nuovo link per {email}? Chi lo ha può scegliere la password al suo posto: consegnalo solo a questa persona. I link mandati prima smettono di valere.':
    'Generate a new link for {email}? Whoever has it can choose the password in their place: hand it only to this person. The links sent before stop working.',
  'Il link si può mostrare solo per le utenze di questo tenant: questa persona appartiene a un altro tenant, e chi ha il link avrebbe anche quell’accesso. Si può solo mandarlo per mail.':
    'The link can be shown only for accounts of this tenant: this person belongs to another tenant, and whoever has the link would have that access too. It can only be mailed.',
  'Invito':
    'Invitation',
  'Link password — {email}':
    'Password link — {email}',
  'Manda per mail':
    'Send by email',
  'Un collegamento per scegliere la password: a chi non l’ha mai scelta rimanda l’invito, agli altri un link valido un’ora. Si usa una volta sola, e chi amministra non vede né sceglie la password.':
    'A link to choose the password: whoever never chose one gets the invitation again, the others a link valid for one hour. It works once, and the administrator neither sees nor chooses the password.',
  'Un collegamento per scegliere la password: per mail o da consegnare':
    'A link to choose the password: by email or to hand over',
  'Vale fino al {quando}, una volta sola.':
    'Valid until {quando}, once.',
  'link password mostrato a un amministratore':
    'password link shown to an administrator',
  'Password da reimpostare: in Utenti, «Link password» prepara per la persona un collegamento per scegliere una password nuova, valido un’ora e una volta sola (o le rimanda l’invito, se non ha mai scelto una password); l’amministratore non vede né sceglie la password. Il collegamento si manda per mail oppure, per chi non riceve la posta, si genera e si mostra nella finestra con «copia», da consegnare per un altro canale: si vede una volta sola, annulla i link precedenti e va nel registro degli accessi con chi l’ha generato. Chi ha il link può scegliere la password al posto della persona, per questo un amministratore di tenant può mostrarlo solo per le utenze del suo tenant. Non c’è un «password dimenticata» sulla pagina di accesso: la persona lo chiede all’amministratore. Usato il collegamento, arriva una mail di conferma con ora e indirizzo, e le sessioni aperte prima non vengono rinnovate. Chi entra con il single sign-on la cambia nel sistema dell’organizzazione.':
    'Passwords to reset: in Users, «Password link» prepares for the person a link to choose a new password, valid for one hour and once (or sends the invitation again, if they never chose a password); the administrator neither sees nor chooses the password. The link is sent by email or, for whoever does not receive mail, generated and shown in the window with «copy», to hand over by another channel: it is seen once, cancels the previous links and goes to the access log with who generated it. Whoever has the link can choose the password in the person’s place, which is why a tenant administrator can show it only for the accounts of their tenant. There is no «forgot password» on the sign-in page: the person asks the administrator. Once the link is used, a confirmation email arrives with the time and the address, and the sessions opened before are not renewed. Whoever signs in with single sign-on changes it in the organisation’s system.',
  '+ un altro tag…':
    '+ another tag…',
  'Aggiungi un tag al filtro':
    'Add a tag to the filter',
  'Come combinare i tag':
    'How to combine the tags',
  'I CI che hanno almeno uno di questi tag':
    'The CIs that have at least one of these tags',
  'Solo i CI che hanno tutti questi tag':
    'Only the CIs that have all these tags',
  'almeno uno':
    'at least one',
  'togli i tag':
    'remove the tags',
  'Il filtro «Tag» accetta più tag: scegli il primo, poi «+ un altro tag…». I tag scelti compaiono accanto al titolo dell’elenco, ognuno con la × per toglierlo, e da due tag in su lì scegli se vuoi i CI che li hanno tutti o almeno uno (per esempio produzione o collaudo). Conteggio, «seleziona tutti quelli del filtro» ed export seguono la stessa scelta, e il filtro sta nell’indirizzo della pagina, quindi si può passare a un collega.':
    'The «Tag» filter takes more tags: choose the first, then «+ another tag…». The chosen tags appear next to the list title, each with the × to remove it, and from two tags on you choose there whether you want the CIs that have all of them or at least one (for example production or test). Count, «select all those of the filter» and export follow the same choice, and the filter is in the page address, so it can be passed to a colleague.',
  'Modifica il nome {fqdn}':
    'Edit the name {fqdn}',
  'Modifica l’indirizzo {ip}':
    'Edit the address {ip}',
  'Ricavata dalla tabella di routing':
    'Deduced from the routing table',
  'Scritta a mano':
    'Written by hand',
  'Vuoto: quella ricavata dalla tabella di routing dell’apparato, se c’è.':
    'Empty: the one deduced from the device’s routing table, if any.',
  'Indirizzi aggiuntivi e FQDN aggiuntivi: VIP, alias e nomi in più dell’oggetto. «Modifica» su ogni riga cambia FQDN, funzione e note, e per gli indirizzi anche la VRF/VPN-instance; l’IP di un indirizzo resta quello, perché un IP diverso è un altro indirizzo da aggiungere. La VRF, come nelle interfacce di rete, CENSO la ricava dalla tabella di routing dell’apparato; scritta a mano vale quella, e lasciandola vuota torna quella ricavata. L’FQDN di un indirizzo serve anche a riconoscerlo: cambiarlo sposta il riconoscimento sul nome nuovo, e un nome che appartiene già a un altro CI viene rifiutato.':
    'Additional addresses and additional FQDNs: VIPs, aliases and extra names of the object. «Edit» on each row changes FQDN, function and notes, and for addresses also the VRF/VPN-instance; the IP of an address stays the same, because a different IP is another address to add. The VRF, as in the network interfaces, CENSO deduces from the device’s routing table; written by hand that one counts, and leaving it empty brings back the deduced one. The FQDN of an address also serves to recognise it: changing it moves the recognition to the new name, and a name that already belongs to another CI is refused.',
  'L’integrazione supporta due funzionalità indipendenti. Confronto inventario: acquisisce gli host gestiti da AWX e li confronta con la CMDB, evidenziando i sistemi presenti in inventario ma non gestiti da AWX e gli host presenti in AWX ma non censiti; questa credenziale serve a questo. Inventario dinamico: consente ad AWX di utilizzare la CMDB come sorgente d’inventario; i tag vengono pubblicati come gruppi AWX e gli host vengono sincronizzati automaticamente.':
    'The integration supports two independent functions. Inventory comparison: it reads the hosts managed by AWX and compares them with the CMDB, highlighting the systems in the inventory but not managed by AWX and the hosts in AWX but not inventoried; this credential is for this one. Dynamic inventory: it lets AWX use the CMDB as its inventory source; the tags are published as AWX groups and the hosts are synchronised automatically.',
  'A) Token API (per il confronto inventario)':
    'A) API token (for the inventory comparison)',
  'In AWX genera un token API dall’utenza che verrà utilizzata per la connessione (Users → utente → Tokens → Add) con ambito «Read». È consigliato un account dedicato con ruolo «Auditor» o con permessi di sola lettura sugli inventari da confrontare.':
    'In AWX, generate an API token from the account that will be used for the connection (Users → user → Tokens → Add) with scope «Read». A dedicated account with the «Auditor» role or read-only permissions on the inventories to compare is recommended.',
  'In Automation Controller la procedura è identica. L’integrazione rileva automaticamente l’endpoint API corretto (/api/controller/v2), senza configurazioni aggiuntive.':
    'In Automation Controller the procedure is the same. The integration finds the right API endpoint automatically (/api/controller/v2), with no further configuration.',
  'B) Origine della connessione':
    'B) Where the connection starts from',
  'Sonda o Control Plane: la connessione verso AWX / Automation Controller viene normalmente eseguita da una sonda che raggiunge l’istanza AWX e ha la capability «Ansible AWX» abilitata. Usa il Control Plane solo se AWX è raggiungibile direttamente da Internet.':
    'Probe or Control Plane: the connection to AWX / Automation Controller is normally made by a probe that reaches the AWX instance and has the «Ansible AWX» capability enabled. Use the Control Plane only if AWX is reachable directly from the Internet.',
  'C) Inventario dinamico (AWX ← CMDB)':
    'C) Dynamic inventory (AWX ← CMDB)',
  'Configura una chiave API con permessi di lettura per il modulo «Ansible AWX» (Impostazioni → Chiavi API).':
    'Configure an API key with read permission for the «Ansible AWX» module (Settings → API keys).',
  'In AWX / Automation Controller crea un Credential Type personalizzato che esponga l’URL della piattaforma e la chiave API come variabili d’ambiente (CENSO_URL e CENSO_API_KEY).':
    'In AWX / Automation Controller create a custom Credential Type that exposes the platform URL and the API key as environment variables (CENSO_URL and CENSO_API_KEY).',
  'Configura una sorgente d’inventario «Sourced from a Project» con lo script di inventario dinamico censo_inventory.py (si scarica dalla scheda AWX in Sorgenti dati).':
    'Configure an inventory source «Sourced from a Project» with the dynamic inventory script censo_inventory.py (download it from the AWX tab in Data sources).',
  'Sincronizza l’inventario: i CI vengono importati come host e i tag diventano gruppi di inventario AWX.':
    'Sync the inventory: the CIs are imported as hosts and the tags become AWX inventory groups.',
  'Il sistema acquisisce tramite API sezioni, subnet e indirizzi IP da phpIPAM. L’integrazione opera in sola lettura per impostazione predefinita; le operazioni di aggiornamento verso phpIPAM sono abilitate solo attivando l’opzione «Consenti la scrittura».':
    'The system reads sections, subnets and IP addresses from phpIPAM through its API. The integration is read-only by default; updates towards phpIPAM are enabled only by turning on the «Allow writing» option.',
  'A) API phpIPAM':
    'A) phpIPAM API',
  'In Administration → phpIPAM settings abilita l’opzione «API».':
    'In Administration → phpIPAM settings, enable the «API» option.',
  'In Administration → API crea una API key specificando: un App ID dedicato (per esempio «censo»); permessi «Read» (oppure «Read / Write» se è prevista la scrittura verso phpIPAM); modalità di autenticazione «SSL with App code token» (consigliata) oppure «SSL with User token».':
    'In Administration → API, create an API key specifying: a dedicated App ID (for example «censo»); «Read» permissions (or «Read / Write» if writing to phpIPAM is planned); authentication mode «SSL with App code token» (recommended) or «SSL with User token».',
  'Connettività HTTPS: la sonda deve raggiungere l’istanza phpIPAM in HTTPS. phpIPAM rifiuta le richieste API con token di autenticazione su connessioni non cifrate.':
    'HTTPS connectivity: the probe must reach the phpIPAM instance over HTTPS. phpIPAM refuses API requests carrying authentication tokens over unencrypted connections.',
  'Le operazioni di scrittura possono essere eseguite dagli utenti autorizzati sul modulo (operatori e amministratori; nei ruoli personalizzati chi ha «Scrittura» sul modulo) e vengono registrate nell’audit, con il dettaglio dell’oggetto modificato e dei valori precedenti e successivi.':
    'Write operations can be carried out by the users authorised on the module (operators and administrators; in custom roles whoever has «Write» on the module) and are recorded in the audit, with the details of the object changed and of the values before and after.',
  'La modalità di accesso è configurata per singola connessione: si può abilitare la scrittura su alcune connessioni e mantenere le altre in sola lettura.':
    'The access mode is set per connection: writing can be enabled on some connections and the others kept read-only.',
  'Con «Consenti la scrittura» attivo, il confronto di una subnet nella pagina IPAM può aggiornare phpIPAM con i dati della CMDB: registra gli indirizzi rilevati dalla discovery, ne aggiorna il nome in base al CI associato e rimuove gli indirizzi documentati che non risultano più presenti nell’inventario.':
    'With «Allow writing» on, comparing a subnet in the IPAM page can update phpIPAM with the CMDB data: it records the addresses found by discovery, updates their name from the associated CI and removes the documented addresses no longer present in the inventory.',
  'Per usare la scrittura, l’applicazione configurata in phpIPAM deve avere i permessi «Read / Write»; se si usa l’autenticazione con utente e password, l’utenza deve avere anche i permessi di scrittura sulle sezioni interessate.':
    'To use writing, the application configured in phpIPAM must have «Read / Write» permissions; with username and password authentication, the account must also have write permission on the sections involved.',
  'La modalità di accesso è configurata per singola connessione: si può abilitare la scrittura su alcune istanze di phpIPAM e mantenere le altre in sola lettura.':
    'The access mode is set per connection: writing can be enabled on some phpIPAM instances and the others kept read-only.',
  'Credenziali di accesso alla console Veeam con un account di dominio («DOMINIO\\utente») o locale («SERVER\\utente»). Usa un’utenza dedicata con privilegi di sola lettura (ruolo Veeam Backup Viewer o Operator). A ogni sincronizzazione la sonda esegue l’autenticazione OAuth 2.0 e usa un token temporaneo rilasciato dalla piattaforma.':
    'Access credentials for the Veeam console with a domain («DOMAIN\\user») or local («SERVER\\user») account. Use a dedicated account with read-only privileges (Veeam Backup Viewer or Operator role). At each sync the probe performs OAuth 2.0 authentication and uses a temporary token issued by the platform.',
  'Con la modalità «SSL with App code token», il valore App Code generato da phpIPAM viene inviato nell’header HTTP `token` di ogni richiesta API. L’autenticazione è associata all’applicazione e non a una specifica utenza; l’accesso si revoca in qualsiasi momento disabilitando o rimuovendo l’API application in phpIPAM.':
    'With the «SSL with App code token» mode, the App Code generated by phpIPAM is sent in the HTTP `token` header of every API request. Authentication is tied to the application, not to a specific user; access can be revoked at any time by disabling or removing the API application in phpIPAM.',
  'Sincronizza l’inventario: gli host vengono importati automaticamente dalla CMDB e i tag vengono esposti come gruppi AWX.':
    'Sync the inventory: the hosts are imported automatically from the CMDB and the tags are exposed as AWX groups.',
  'Flussi di traffico rilevati':
    'Detected traffic flows',
}
